<template>
    <div class="flow-main-container">
        <v-container fluid class="flow-container-fluid">
            <v-card class="card2">
                <v-btn v-if="!flowToolEditorDialog" class="floating-back-btn" @click="goBack" variant="elevated" color="white">
                    <v-icon color="primary" size="20">mdi-arrow-left</v-icon>
                    <span class="back-text">Back</span>
                </v-btn>
                    <!-- Undo/Redo buttons -->
                    <div class="undo-redo-container mr-2" v-if="!isViewMode && !flowToolEditorDialog && !isDebugMode">
                        <v-tooltip location="bottom">
                            <template v-slot:activator="{ props }">
                                <v-btn v-bind="props"
                                       @click="undo" 
                                       :disabled="!canUndo" 
                                       variant="outlined" 
                                       size="small" 
                                       color="primary"
                                       class="undo-btn mr-1">
                                    <v-icon size="16">mdi-undo</v-icon>
                                </v-btn>
                            </template>
                            <span>
                                Undo {{ historyIndex >= 0 ? `(${history[historyIndex]?.action || 'Unknown'})` : '(None)' }}
                                <br><small><kbd>Ctrl</kbd> + <kbd>Z</kbd></small>
                            </span>
                        </v-tooltip>
                        <v-tooltip location="bottom">
                            <template v-slot:activator="{ props }">
                                <v-btn v-bind="props"
                                       @click="redo" 
                                       :disabled="!canRedo" 
                                       variant="outlined" 
                                       size="small" 
                                       color="primary"
                                       class="redo-btn">
                                    <v-icon size="16">mdi-redo</v-icon>
                                </v-btn>
                            </template>
                            <span>
                                Redo {{ historyIndex + 1 < history.length ? `(${history[historyIndex + 1]?.action || 'Unknown'})` : '(None)' }}
                                <br><small><kbd>Ctrl</kbd> + <kbd>Y</kbd></small>
                            </span>
                        </v-tooltip>
                    </div>
                <!-- Flow Toolbar -->
                <div class="flow-toolbar" v-if="!flowToolEditorDialog">
                    <div class="toolbar-left">
                        <div class="flow-info">
                            <v-text-field v-model="flowName" label="Flow Name" variant="outlined" density="compact"
                                hide-details="auto" class="modern-input flow-name-input" :readonly="isViewMode || isDebugMode"
                                :disabled="isDebugMode"
                                @focus="handleFlowNameFocus" />
                            <div v-if="!isViewMode" class="flow-actions">
                                <!-- Additional Actions -->
                                <v-menu offset-y v-if="!isDebugMode">
                                    <template v-slot:activator="{ props }">
                                        <v-btn v-bind="props" variant="text" icon size="small" class="more-actions-btn">
                                            <v-icon>mdi-dots-vertical</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-list density="compact" class="menu-list">
                                        <v-list-item @click="exportFlow" :disabled="!tempNode.length">
                                            <template v-slot:prepend>
                                                <v-icon>mdi-export</v-icon>
                                            </template>
                                            <v-list-item-title>Export Flow</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="importFlow">
                                            <template v-slot:prepend>
                                                <v-icon>mdi-import</v-icon>
                                            </template>
                                            <v-list-item-title>Import Flow</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="navigateToCompare" :disabled="!flowId">
                                            <template v-slot:prepend>
                                                <v-icon color="info">mdi-compare</v-icon>
                                            </template>
                                            <v-list-item-title>Compare with...</v-list-item-title>
                                        </v-list-item>
                                        <div class="menu-divider"></div>
                                        <v-list-item @click="handleDuplicateSelectedNodes" 
                                                   :disabled="!selectedNodes.length" 
                                                   class="text-primary">
                                            <template v-slot:prepend>
                                                <v-icon color="primary">mdi-content-duplicate</v-icon>
                                            </template>
                                            <v-list-item-title>Duplicate Selected ({{ selectedNodes.length }})</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="handleCopySelectedNodes" 
                                                   :disabled="!selectedNodes.length" 
                                                   class="text-info">
                                            <template v-slot:prepend>
                                                <v-icon color="info">mdi-content-copy</v-icon>
                                            </template>
                                            <v-list-item-title>Copy Selected ({{ selectedNodes.length }})</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="handlePasteNodes()"
                                                   :disabled="isViewMode || isDebugMode"
                                                   class="text-info">
                                            <template v-slot:prepend>
                                                <v-icon color="info">mdi-content-paste</v-icon>
                                            </template>
                                            <v-list-item-title>Paste</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="handleDeleteSelectedNodes" 
                                                   :disabled="!selectedNodes.length" 
                                                   class="text-error">
                                            <template v-slot:prepend>
                                                <v-icon color="error">mdi-delete</v-icon>
                                            </template>
                                            <v-list-item-title>Delete Selected ({{ selectedNodes.length }})</v-list-item-title>
                                        </v-list-item>
                                        <v-list-item @click="clearFlow" :disabled="!tempNode.length" class="text-error">
                                            <template v-slot:prepend>
                                                <v-icon color="error">mdi-delete-sweep</v-icon>
                                            </template>
                                            <v-list-item-title>Clear Flow</v-list-item-title>
                                        </v-list-item>
                                    </v-list>
                                </v-menu>
                                <!-- Save Button -->
                                <div v-if="!isDebugMode">
                                    <v-btn @click="openSaveDialog" :disabled="!tempNode.length" color="success"
                                        class="save-btn" size="small">
                                        <v-icon size="16">mdi-content-save</v-icon>
                                        <span class="btn-text">Save</span>
                                    </v-btn>
                                </div>
                                <!-- Debug Mode Toggle -->
                                <div class="ml-2">
                                    <v-btn @click="toggleDebugMode" :disabled="!tempNode.length || isViewMode"
                                        :color="isDebugMode ? 'warning' : 'default'"
                                        :variant="isDebugMode ? 'flat' : 'outlined'"
                                        class="debug-toggle-btn" size="small">
                                        <v-icon size="16">{{ isDebugMode ? 'mdi-bug-check' : 'mdi-bug-play-outline' }}</v-icon>
                                        <span class="btn-text">{{ isDebugMode ? 'Exit Debug' : 'Debug' }}</span>
                                    </v-btn>
                                </div>
                            </div>
                            <div v-else class="ml-4">
                                <v-btn variant="text" size="small" class="view-mode-btn" readonly>
                                    <v-icon size="16">mdi-eye</v-icon>
                                    <span class="btn-text">View Mode</span>
                                </v-btn>
                            </div>
                        </div>
                    </div>
                    <!-- <div class="toolbar-actions">
                        <div>
                            <span class="flow-status" v-if="hasUnsavedChanges">
                                <v-icon size="small" color="warning">mdi-circle</v-icon>
                                Unsaved changes
                            </span>
                            <span class="flow-status saved" v-else>
                                <v-icon size="small" color="green">mdi-check-circle</v-icon>
                                Saved
                            </span>
                        </div>
                        <v-btn @click="toggleLockStatus" :color="interactiveControls ? 'success' : 'error'"
                            :variant="interactiveControls ? 'outlined' : 'elevated'" size="small" class="lock-btn">
                            <v-icon size="16">{{ interactiveControls ? 'mdi-lock-open' : 'mdi-lock' }}</v-icon>
                            <span class="btn-text">{{ interactiveControls ? 'Unlocked' : 'Locked' }}</span>
                        </v-btn>
                    </div> -->
                </div>
                <!-- Vue Flow Container -->
                <div class="flow-container" :class="{ 'flow-tool-editing': flowToolEditorDialog, 'flow-debug-active': isDebugMode }"
                     @drop="onDrop" @dragover.prevent @dragenter.prevent
                     @wheel.capture="onFlowWheelCapture" @mousemove="trackCanvasMouse"
                     @keydown="handleKeyDown" @click="focusFlowContainer" tabindex="0" ref="flowContainer">
                    <VueFlow ref="vueFlow" :nodes="tempNode" v-model:edges="tempEdge" :default-viewport="{ zoom: 1.2 }"
                        :snap-to-grid="false" :min-zoom="0.2" :max-zoom="2" class="basic-flow" :pan-on-drag="[2]"
                        :pan-on-scroll="panOnScrollActive" :pan-on-scroll-mode="'free'" :zoom-on-pinch="true"
                        :selection-key-code="true" :multi-selection-key-code="true" :only-render-visible-elements="true"
                        :nodes-connectable="!isDebugMode"
                        :delete-key-code="isDebugMode ? null : ['Backspace', 'Delete']"
                        @nodes-change="onNodesChange" @edges-change="onEdgesChange" @node-selection-change="onSelectionChange" 
                        @connect="onConnect" @edge-mouse-enter="onEdgeMouseEnter" @edge-mouse-leave="onEdgeMouseLeave"
                        @edge-click="onEdgeClick"
                        @node-click="onNodeClick" @node-drag-start="onNodeDragStart" @node-drag-stop="onNodeDragStop"
                        @node-mouse-enter="onNodeMouseEnter" @node-mouse-leave="onNodeMouseLeave"
                        @pane-click="onPaneClick"
                        :fitViewOnInit="true">

                        <template #node-flow-node="props">
                            <FlowNode :id="props.id" :data="props.data" :key="props.id"
                                @delete-node="handleDeleteNode" @update:data="handleNodeDataUpdate"
                                @remove-outgoing-edge="handleRemoveOutgoingEdge"
                                @set-next="onDebugSetNext" />
                        </template>

                        <template #edge-flow-edge="buttonEdgeProps">
                            <FlowEdge v-bind="buttonEdgeProps" :id="buttonEdgeProps.id"
                                :source-x="buttonEdgeProps.sourceX" :source-y="buttonEdgeProps.sourceY"
                                :target-x="buttonEdgeProps.targetX" :target-y="buttonEdgeProps.targetY"
                                :source-position="buttonEdgeProps.sourcePosition"
                                :target-position="buttonEdgeProps.targetPosition"
                                :marker-end="buttonEdgeProps.markerEnd" :style="buttonEdgeProps.style" />
                        </template>

                        <Background patternColor="rgba(0, 0, 0, 0.4)" :gap="16" />
                        <Controls :show-interactive="false" v-if="!lockStatus" />
                        
                        <!-- Selection Info -->
                        <div v-if="selectedNodes.length > 0 && !isViewMode && !flowToolEditorDialog" class="selection-info">
                            <v-chip color="#0080ff7a" variant="elevated" size="small" class="ml-2">
                                <v-icon size="16" class="mr-1">mdi-cursor-default-click</v-icon>
                                {{ selectedNodes.length }} node{{ selectedNodes.length > 1 ? 's' : '' }} selected
                            </v-chip>
                            <v-chip color="primary" variant="text" size="small">
                                <v-icon size="16" class="mr-1">mdi-content-duplicate</v-icon>
                                Ctrl+D duplicate
                            </v-chip>
                            <v-chip color="error" variant="text" size="small">
                                <v-icon size="16" class="mr-1">mdi-delete</v-icon>
                                Del to remove
                            </v-chip>
                        </div>
                    </VueFlow>

                    <!-- Debug Mode Components -->
                    <DebugControlBar
                        :visible="isDebugMode"
                        :status="debugStatus"
                        :paused-node-name="debugPausedNodeName"
                        :breakpoint-count="debugBreakpoints.size"
                        :step-count="debugStepCount"
                        :total-duration="debugTotalDuration"
                        :error-message="debugError"
                        :last-reply="debugLastBotReply"
                        :streaming="debugStreaming"
                        :set-next-target-name="debugSetNextTargetName"
                        :keep-memory="debugKeepMemory"
                        :voice-mode="debugVoiceMode"
                        :voice-lang="debugVoiceLang"
                        :proactive-message="debugProactiveMessage"
                        :debug-user-id="debugUserId"
                        :debug-cif="debugCif"
                        :use-cif="debugUseCif"
                        :access-level="debugAccessLevel"
                        v-model:message="debugMessage"
                        @update:voice-mode="debugVoiceMode = $event"
                        @update:voice-lang="debugVoiceLang = $event"
                        @update:proactive-message="debugProactiveMessage = $event"
                        @update:debugCif="debugCif = $event"
                        @update:useCif="debugUseCif = $event"
                        @update:access-level="debugAccessLevel = $event"
                        @send="startDebugExecution"
                        @resume="resumeDebugExecution"
                        @stop="stopDebugExecution"
                        @cancel-set-next="onDebugCancelSetNext"
                        @update:keep-memory="onDebugKeepMemoryChange"
                    />
                    <DebugInspector
                        :visible="isDebugMode"
                        :steps="debugSteps"
                        :current-node-id="debugCurrentNodeId"
                        :current-variables="debugVariables"
                        :changed-variables="debugChangedVariables"
                        :status="debugStatus"
                        :debug-cif="debugCif"
                        @close="isDebugMode && toggleDebugMode()"
                        @focus-node="focusOnNode"
                        @patch-variable="onDebugPatchVariable"
                        @set-next="onDebugSetNext"
                    />

                    <!-- Flow Tool Editor Header Bar (shows when editing internal flow) -->
                    <transition name="flow-tool-header">
                        <div v-if="flowToolEditorDialog" class="flow-tool-editor-header">
                            <div class="d-flex align-center">
                                <v-btn icon size="small" variant="text" @click="closeFlowToolEditor" class="mr-2">
                                    <v-icon color="white">mdi-arrow-left</v-icon>
                                </v-btn>
                                <v-icon class="mr-2" color="white">mdi-sitemap</v-icon>
                                <span class="text-subtitle-1 font-weight-medium">
                                    Editing: {{ editingFlowToolNode?.data?.flowToolName || 'Flow Tool' }}
                                </span>
                            </div>
                            <div class="d-flex align-center ga-2">
                                <v-chip v-if="!isInternalFlowComplete()" 
                                    color="warning" variant="elevated" size="small">
                                    <v-icon size="14" class="mr-1">mdi-alert</v-icon>
                                    Incomplete
                                </v-chip>
                                <v-chip v-else color="success" variant="elevated" size="small">
                                    <v-icon size="14" class="mr-1">mdi-check</v-icon>
                                    Valid
                                </v-chip>
                                <v-btn color="white" variant="elevated" size="small" 
                                    @click="saveFlowToolInternalFlow">
                                    <v-icon size="16" class="mr-1">mdi-content-save</v-icon>
                                    Save & Exit
                                </v-btn>
                                <v-btn color="white" variant="outlined" size="small" 
                                    @click="closeFlowToolEditor">
                                    Cancel
                                </v-btn>
                            </div>
                        </div>
                    </transition>

                    <!-- Help overlay for internal flow (shows when editing and few nodes) -->
                    <div v-if="flowToolEditorDialog && tempNode.length <= 2" class="flow-tool-help-center">
                        <v-alert type="info" variant="tonal" density="compact" class="help-alert">
                            <div class="text-caption">
                                <strong>Internal Flow:</strong> Drag nodes from panel → Connect <strong>Flow Input</strong> → your logic → <strong>Flow Output</strong>
                            </div>
                        </v-alert>
                    </div>

                    <!-- Node Search Panel (Ctrl+F) -->
                    <transition name="node-search-slide">
                        <div v-if="nodeSearchOpen" class="node-search-panel" @click.stop @keydown.stop>
                            <div class="node-search-inner">
                                <v-icon size="15" class="node-search-icon-left">mdi-magnify</v-icon>
                                <input
                                    ref="nodeSearchInput"
                                    v-model="nodeSearchQuery"
                                    @input="performNodeSearch"
                                    @keydown.enter.prevent="navigateSearchResult(1)"
                                    @keydown.shift.enter.prevent="navigateSearchResult(-1)"
                                    @keydown.esc.prevent="closeNodeSearch"
                                    @keydown.up.prevent="navigateSearchResult(-1)"
                                    @keydown.down.prevent="navigateSearchResult(1)"
                                    placeholder="Search nodes..."
                                    class="node-search-input"
                                    autocomplete="off"
                                    spellcheck="false"
                                />
                                <span v-if="nodeSearchQuery" class="node-search-count" :class="{ 'no-results': !nodeSearchResults.length }">
                                    {{ nodeSearchResults.length ? `${nodeSearchCurrentIndex + 1} / ${nodeSearchResults.length}` : 'No results' }}
                                </span>
                                <button :disabled="!nodeSearchResults.length" @click="navigateSearchResult(-1)" class="node-search-nav-btn" title="Previous (Shift+Enter)">
                                    <v-icon size="13">mdi-chevron-up</v-icon>
                                </button>
                                <button :disabled="!nodeSearchResults.length" @click="navigateSearchResult(1)" class="node-search-nav-btn" title="Next (Enter)">
                                    <v-icon size="13">mdi-chevron-down</v-icon>
                                </button>
                                <button @click="closeNodeSearch" class="node-search-nav-btn node-search-close-btn" title="Close (Esc)">
                                    <v-icon size="13">mdi-close</v-icon>
                                </button>
                            </div>
                        </div>
                    </transition>
                </div>
            </v-card>
        </v-container>

        <!-- Floating Node Panel (switch excludeTypes based on mode) -->
        <NodePanel v-if="!isViewMode && !isDebugMode" 
            :exclude-types="flowToolEditorDialog ? internalFlowExcludedTypes : []"
            @add-node="flowToolEditorDialog ? addInternalFlowNodeDirect($event) : addNewNode($event)" 
            class="node-panel" />
        <!-- Floating Node Inspector Panel -->
        <div v-if="selectedNode && selectedNodes.length == 1" class="floating-inspector"
            :class="{ 'inspector-expanded': inspectorExpanded }">
            <!-- Inspector Header -->
            <div class="inspector-header">
                <div class="inspector-title">
                    <v-icon class="mr-2" color="white">mdi-cog</v-icon>
                    <span class="text-h6">Node Properties</span>
                </div>
                <div class="inspector-controls">
                    <v-btn icon size="small" @click="inspectorExpanded = !inspectorExpanded" class="mr-1">
                        <v-icon color="white">{{ inspectorExpanded ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
                    </v-btn>
                    <v-btn icon size="small" @click="closeInspector">
                        <v-icon color="white">mdi-close</v-icon>
                    </v-btn>
                </div>
            </div>
            <!-- Inspector Content -->
            <div class="inspector-content">
                <v-form ref="inspectorForm">
                    
                    <!-- invisible dummy fields to trap browser autofill (Edge/Chrome) -->
                    <div style="position:absolute; left:-9999px; opacity:0; height:0; overflow:hidden;">
                        <input type="text" name="prevent_autofill_username" autocomplete="username" tabindex="-1" />
                        <input type="password" name="prevent_autofill_password" autocomplete="current-password" tabindex="-1" />
                    </div>

                    <!-- Node Type Badge -->
                    <div class="node-type-badge mb-4">
                        <v-chip :color="getNodeTypeColor(selectedNode.data.type)" variant="elevated" size="large"
                            class="text-subtitle-1 font-weight-bold header-chip">
                            <v-icon class="mr-2" size="20">{{ getNodeTypeIcon(selectedNode.data.type) }}</v-icon>
                            {{ getNodeTypeLabel(selectedNode.data.type) }}
                        </v-chip>
                    </div>

                    <!-- Common Properties -->
                    <!-- <div class="form-section">
                        <h6 class="section-title">General</h6>
                        <v-text-field v-model="nodeInspector.label" label="Node Label" variant="outlined" disabled
                            density="compact" hide-details="auto" :rules="[v => !!v || 'Label is required']"
                            @update:model-value="updateNodeProperty('label', $event)" class="modern-input" />
                    </div> -->
                    <!-- Node Type Specific Properties -->
                    <div v-if="selectedNode.data.type === 'messageInput'" class="form-section">
                        <h6 class="section-title">Chat AI Model Configuration</h6>
                        <v-autocomplete v-model="nodeInspector.selectedModel" :items="uniqueAiModelOptions"
                            item-title="searchableText" item-value="value" label="Flow AI Model" variant="outlined"
                            density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('selectedModel', $event)" class="modern-input mb-3"
                            :clearable="!isViewMode" :readonly="isViewMode" chips multiple
                            :custom-filter="(value, query, item) => {
                                if (!query) return true;
                                const searchQuery = query.toLowerCase();
                                const itemValue = item?.raw?.value?.toString().toLowerCase() || '';
                                const itemName = item?.raw?.name?.toLowerCase() || '';
                                return itemValue.includes(searchQuery) || itemName.includes(searchQuery);
                            }">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="model-option"
                                    :title="item.raw.value + ': ' + item.raw.name">
                                    <template v-slot:prepend>
                                        <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                            <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                        </div>
                                    </template>
                                </v-list-item>
                            </template>
                            <template v-slot:chip="{ props, item }">
                                <v-chip v-bind="props" :text="item.raw.value + ': ' + item.raw.name"></v-chip>
                            </template>
                        </v-autocomplete>
                    </div>

                    <div v-if="selectedNode.data.type === 'model'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-robot" size="24" color="primary" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-primary mb-0">AI Model Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Configure your AI model settings and
                                parameters</p>
                        </div>

                        <!-- Action Buttons -->
                        <div class="action-buttons-container mb-4">
                            <!-- Load from Model Button (First Row) -->
                            <div class="load-model-button-wrapper mb-3">
                                <v-btn @click="openLoadFromModelDialog" color="success" variant="tonal" size="small"
                                    prepend-icon="mdi-download" :disabled="isViewMode" class="load-model-btn">
                                    Load from Model
                                </v-btn>
                            </div>

                            <!-- Use Base Model Prompt Toggle (Second Row) -->
                            <div class="base-prompt-toggle-section">
                                <v-card variant="outlined" class="pa-3" color="green-lighten-5" style="border-radius: 12px;">
                                    <div class="d-flex align-center">
                                        <v-avatar size="24"
                                            color="green"
                                            class="me-3">
                                            <v-icon
                                                color="white"
                                                size="12">mdi-text-box-check</v-icon>
                                        </v-avatar>
                                        <div class="flex-grow-1 me-3">
                                            <div class="text-subtitle-2"><span style="color:#4CAF50;font-weight: 500;"> Use Base Model Prompt</span></div>
                                            <div class="text-caption text-medium-emphasis">Combine with the default model prompt</div>
                                        </div>
                                        <v-switch v-model="nodeInspector.useBaseModelPrompt" color="green"
                                            hide-details density="compact"
                                            @update:model-value="updateNodeProperty('useBaseModelPrompt', $event)"
                                            :readonly="isViewMode" inset />
                                    </div>
                                </v-card>
                            </div>
                        </div>

                        <!-- Custom Label -->
                        <div class="config-section mb-6">
                            <h6 class="section-label mb-3">Node Label</h6>
                            <v-row>
                                <v-col cols="12">
                                    <v-text-field v-model="nodeInspector.customLabel"
                                        label="Custom Label" variant="outlined"
                                        density="comfortable" hide-details="auto"
                                        prepend-inner-icon="mdi-tag-outline"
                                        placeholder="AI Model"
                                        :maxlength="30"
                                        counter="30"
                                        @update:model-value="updateNodeProperty('customLabel', $event)"
                                        class="config-input" :readonly="isViewMode"
                                        hint="Override the node title shown on the canvas" persistent-hint />
                                </v-col>
                            </v-row>
                        </div>
                        <!-- Model Configuration Panels -->
                        <v-expansion-panels v-model="modelConfigPanels" multiple class="config-panels elevation-0"
                            variant="accordion">
                            <!-- Basic Information Panel -->
                            <v-expansion-panel value="basic" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Basic Information</div>
                                            <div class="panel-subtitle">Provider, model, and basic settings</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <!-- Provider and Model -->
                                        <div class="config-section mb-6">
                                            <h6 class="section-label mb-3">Service Configuration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.provider"
                                                        :items="listDDProvider" item-title="providerName"
                                                        item-value="providerName" label="Provider*" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-cloud-outline"
                                                        @update:model-value="updateNodeProperty('provider', $event); changeProvider($event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select AI service provider" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.model" :items="DDModelShow"
                                                        item-title="modelDisplay" item-value="modelName" label="Model*"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-brain"
                                                        @update:model-value="updateNodeProperty('model', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :disabled="!nodeInspector.provider"
                                                        hint="Select specific model version" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <!-- Behavior Settings -->
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Behavior Settings</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.gender"
                                                        :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                        item-title="name" item-value="id" label="Gender"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-account"
                                                        @update:model-value="updateNodeProperty('gender', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Voice/persona gender preference" persistent-hint
                                                        :model-value="nodeInspector.gender || 'male'" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.typingSpeed"
                                                        label="Typing Speed" type="number" min="0" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-keyboard-outline"
                                                        @update:model-value="updateNodeProperty('typingSpeed', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Characters per second (0 = instant)" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.maxInputLength"
                                                        label="Max Input Length" type="number" min="0" max="200000"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-outline"
                                                        @update:model-value="updateNodeProperty('maxInputLength', $event)"
                                                        @keypress="maxNumberpositive($event)"
                                                        @focusin="validateAndCorrectValue($event, 1, 200000)"
                                                        @focusout="validateAndCorrectValue($event, 1, 200000)"
                                                        @keydown.enter="validateAndCorrectValue($event, 1, 200000)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Maximum characters allowed in user input"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.nodeTimeout"
                                                        label="Request Timeout (seconds)" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('nodeTimeout', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="0 = use global timeout setting" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.nodeMaxRetry"
                                                        label="Max Retry" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-refresh"
                                                        @update:model-value="updateNodeProperty('nodeMaxRetry', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="0 = use global max retry setting" persistent-hint />
                                                </v-col>
                                            </v-row>

                                            <!-- Bypass on Error Toggle -->
                                            <div class="switch-option-compact mt-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.bypassOnError }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.bypassOnError ? 'warning' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.bypassOnError ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-transfer-right</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Bypass on Error</div>
                                                            <div class="switch-description">Pass input through as output when LLM errors or times out (no error message)</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.bypassOnError" color="warning"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('bypassOnError', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Response Options Panel -->
                            <v-expansion-panel value="response" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Response Options</div>
                                            <div class="panel-subtitle">Configure how responses are generated</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <!-- Stream Response -->
                                        <div class="switch-option-compact mb-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.streamResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.streamResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.streamResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-text-box-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">Stream Response</div>
                                                        <div class="switch-description">Progressive text generation
                                                        </div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.streamResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('streamResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>

                                        <!-- Json Response -->
                                        <div class="switch-option-compact mt-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.jsonResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.jsonResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.jsonResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-code-json</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">JSON Response</div>
                                                        <div class="switch-description">Return structured JSON format
                                                        </div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.jsonResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('jsonResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Memory Settings Panel -->
                            <v-expansion-panel value="memory" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                            <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Memory Settings</div>
                                            <div class="panel-subtitle">Configure conversation memory and context</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Conversation Memory</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.memoryTurn"
                                                        label="Turn Count" type="number" min="0" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-history"
                                                        @update:model-value="updateNodeProperty('memoryTurn', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Number of conversation turns to remember"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.memoryDuration"
                                                        label="Duration (minutes)" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('memoryDuration', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="How long to keep memory active"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Memory Types</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.shortTermMemory"
                                                        label="Short-Term Memory" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-memory"
                                                        @update:model-value="updateNodeProperty('shortTermMemory', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Recent context window size"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col>
                                                    <!-- Long-Term Memory Switch -->
                                                    <div class="switch-option-compact mb-4">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.longTermMemory }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.longTermMemory ? 'success' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.longTermMemory ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-database</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">Long-Term Memory</div>
                                                                    <div class="switch-description">Persistent storage
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <v-switch v-model="nodeInspector.longTermMemory"
                                                                color="success" hide-details density="compact"
                                                                @update:model-value="updateNodeProperty('longTermMemory', $event)"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>

                                        </div>

                                        <!-- Multi-turn Input Configuration -->
                                        <!-- <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Multi-turn Input Configuration</h6>
                                            <v-row>
                                                <v-col>
                                                    <div class="switch-option-compact mb-3">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.isChainMassage }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.isChainMassage ? 'indigo' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.isChainMassage ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-chat-processing</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">Enable Multi-Turn Input
                                                                    </div>
                                                                    <div class="switch-description">Allow continuous
                                                                        conversation</div>
                                                                </div>
                                                            </div>
                                                            <v-switch v-model="nodeInspector.isChainMassage"
                                                                color="indigo" hide-details density="compact"
                                                                @update:model-value="updateNodeProperty('isChainMassage', $event)"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.lenChainMassage"
                                                        label="Sequence" type="number" min="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-numeric"
                                                        @update:model-value="updateNodeProperty('lenChainMassage', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :disabled="!nodeInspector.isChainMassage"
                                                        hint="Number of conversation turns in sequence" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.timeSecondChainMassage"
                                                        label="Duration (seconds)" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer"
                                                        @update:model-value="updateNodeProperty('timeSecondChainMassage', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :disabled="!nodeInspector.isChainMassage"
                                                        hint="Time duration for multi-turn session" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                        </div> -->


                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Cache Settings Panel -->
                            <v-expansion-panel value="cache" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                            <v-icon icon="mdi-cached" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Cache Settings</div>
                                            <div class="panel-subtitle">Configure response caching for performance</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Response Cache</h6>
                                            
                                            <!-- Enable Cache Switch -->
                                            <div class="switch-option-compact mb-4">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableCache }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableCache ? 'warning' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableCache ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-lightning-bolt</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Enable Cache</div>
                                                            <div class="switch-description">Cache responses to reduce LLM calls</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableCache"
                                                        color="warning" hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableCache', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>

                                            <!-- Cache TTL -->
                                            <v-row v-if="nodeInspector.enableCache">
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.cacheTTLMinutes"
                                                        label="Cache TTL (minutes)" type="number" min="1" max="10080"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('cacheTTLMinutes', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="How long to keep cached responses (1-10080 min)"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>

                                            <!-- Cache Scope -->
                                            <v-row v-if="nodeInspector.enableCache">
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.cacheScope"
                                                        :items="cacheScopeOptions"
                                                        item-title="label"
                                                        item-value="value"
                                                        label="Cache Scope"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-account-group-outline"
                                                        @update:model-value="updateNodeProperty('cacheScope', $event)"
                                                        class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Who can use cached responses"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3" v-if="nodeInspector.enableCache">
                                            <h6 class="section-label mb-3">Semantic Cache</h6>
                                            
                                            <!-- Enable Semantic Cache Switch -->
                                            <div class="switch-option-compact mb-4">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableSemanticCache }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableSemanticCache ? 'deep-purple' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableSemanticCache ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-brain</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Semantic Match</div>
                                                            <div class="switch-description">Match similar questions using AI</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableSemanticCache"
                                                        color="deep-purple" hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableSemanticCache', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>

                                            <!-- Similarity Threshold Slider -->
                                            <v-row v-if="nodeInspector.enableSemanticCache">
                                                <v-col cols="12">
                                                    <div class="slider-label mb-2">
                                                        <span>Similarity Threshold</span>
                                                        <span class="text-primary font-weight-bold">{{ (nodeInspector.similarityThreshold * 100).toFixed(0) }}%</span>
                                                    </div>
                                                    <v-slider
                                                        v-model="nodeInspector.similarityThreshold"
                                                        min="0.50"
                                                        max="1.00"
                                                        step="0.01"
                                                        color="deep-purple"
                                                        track-color="grey-lighten-2"
                                                        thumb-label
                                                        hide-details
                                                        @update:model-value="updateNodeProperty('similarityThreshold', $event)"
                                                        :readonly="isViewMode">
                                                        <template v-slot:thumb-label="{ modelValue }">
                                                            {{ (modelValue * 100).toFixed(0) }}%
                                                        </template>
                                                    </v-slider>
                                                    <div class="text-caption text-grey mt-1">
                                                        Higher = more strict (exact match), Lower = more flexible
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <!-- Cache Info Alert -->
                                        <v-alert v-if="nodeInspector.enableCache" 
                                            type="info" 
                                            variant="tonal" 
                                            density="compact"
                                            class="mt-3">
                                            <template v-slot:prepend>
                                                <v-icon>mdi-information-outline</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                Cache will skip when streaming is enabled or when data integrations are used.
                                            </div>
                                        </v-alert>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Parameters Panel -->
                            <v-expansion-panel value="parameters" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                            <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">AI Parameters</div>
                                            <div class="panel-subtitle">Fine-tune model behavior and output</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Core Parameters</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.temperature"
                                                        label="Temperature*" type="number" step="0.1" min="0" max="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-thermometer"
                                                        @update:model-value="updateNodeProperty('temperature', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Creativity level (0 = focused, 1 = creative)"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.topP" label="Top P"
                                                        type="number" step="0.1" min="0" max="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-filter-variant"
                                                        @update:model-value="updateNodeProperty('topP', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Token selection probability threshold" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Penalty Settings</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.frequencyPenalty"
                                                        label="Frequency Penalty" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-repeat-off"
                                                        @update:model-value="updateNodeProperty('frequencyPenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Reduce repetition of frequent tokens" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.presencePenalty"
                                                        label="Presence Penalty" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto"
                                                        prepend-inner-icon="mdi-minus-circle-outline"
                                                        @update:model-value="updateNodeProperty('presencePenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Reduce repetition of any tokens"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Output Control</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.maxTokens"
                                                        label="Max Tokens Response" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-check-outline"
                                                        @update:model-value="updateNodeProperty('maxTokens', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Maximum response length in tokens"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.stopSequence"
                                                        label="Stop Sequence" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-stop-circle-outline"
                                                        @update:model-value="updateNodeProperty('stopSequence', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Text sequence to stop generation" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Line Configuration Panel -->
                            <v-expansion-panel value="line" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #4CAF50, #388E3C);">
                                            <v-icon icon="mdi-message" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Line Configuration</div>
                                            <div class="panel-subtitle">LINE messaging platform settings</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Display Options</h6>

                                            <!-- Show Name -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.showLineName }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.showLineName ? 'info' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.showLineName ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-account-circle</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Show Name</div>
                                                            <div class="switch-description">Display user name</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.showLineName" color="info"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('showLineName', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>
                                            <!-- Loading Indicator -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.loadingBar }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.loadingBar ? 'success' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.loadingBar ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-progress-clock</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Loading Indicator</div>
                                                            <div class="switch-description">Show typing animation</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.loadingBar" color="success"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('loadingBar', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>

                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Loading & Duration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.loadingBarDuration"
                                                        :items="[10, 20, 30, 40, 50, 60]"
                                                        label="Loading Duration (seconds)" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-sand"
                                                        @update:model-value="updateNodeProperty('loadingBarDuration', $event)"
                                                        class="config-input" :disabled="!nodeInspector.loadingBar"
                                                        :readonly="isViewMode"
                                                        hint="Duration for loading animation display" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Monitoring</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.monitorLineUserId"
                                                        label="Monitor Line User ID" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-monitor-eye"
                                                        @update:model-value="updateNodeProperty('monitorLineUserId', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="LINE user ID for monitoring notifications"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.monitorSocialID"
                                                        :items="listMonitorLineName" label="Monitor Line Name"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-chat" item-title="name" item-value="id"
                                                        @update:model-value="updateNodeProperty('monitorSocialID', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select monitor line name" persistent-hint clearable />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Content Filtering</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-toolbar-title style="font-size: 16px">
                                                        Forbidden word
                                                    </v-toolbar-title>
                                                </v-col>
                                            </v-row>
                                            <div class="pr-1" style="
                                                    border: #dfe0e2 1px solid;
                                                    background: #fff;
                                                    margin-top: 10px;
                                                    border-radius: 8px;
                                                ">
                                                <v-row v-for="(item, index) in nodeInspector.forbiddenWords || ['']"
                                                    :key="index" style="margin: 0px">
                                                    <v-col cols="12" style="padding: 0px">
                                                        <div style="display: flex; align-items: center">
                                                            <div style="
                                                                    height: 50px;
                                                                    background: #f8f8f8;
                                                                    padding: 14px 20px 0 20px;
                                                                ">
                                                                {{ index + 1 }}
                                                            </div>
                                                            <v-textarea variant="solo" style="
                                                                    overflow: hidden;
                                                                    overflow-wrap: break-word;
                                                                    resize: none;
                                                                    height: 50px;
                                                                    width: 98%;
                                                                    border-radius: 0px;
                                                                    font-family: Fira code, Fira Mono, Consolas,
                                                                        Menlo, Courier, monospace;
                                                                    font-size: 14px;
                                                                "
                                                                :model-value="(nodeInspector.forbiddenWords && nodeInspector.forbiddenWords[index]) || ''"
                                                                placeholder="Enter word or phrase"
                                                                @keypress="isEnterForbidden($event, index)"
                                                                @keyup.delete="checkDeleteForbiddenWord(index, (nodeInspector.forbiddenWords && nodeInspector.forbiddenWords[index]) || '')"
                                                                @update:model-value="updateForbiddenWordProperty(index, $event)"
                                                                :readonly="isViewMode" />
                                                            <v-btn icon="mdi-attachment" variant="text"
                                                                v-show="(nodeInspector.forbiddenWords || []).length > 1 && index + 1 < (nodeInspector.forbiddenWords || []).length"
                                                                @click="removeForbiddenWordDirect(index)"
                                                                :disabled="isViewMode">
                                                                <v-tooltip location="top" :open-delay="500">
                                                                    <template v-slot:activator="{ props }">
                                                                        <v-icon color="error" style="cursor: pointer"
                                                                            v-bind="props">
                                                                            mdi-delete-forever
                                                                        </v-icon>
                                                                    </template>
                                                                    <span>delete</span>
                                                                </v-tooltip>
                                                            </v-btn>
                                                        </div>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- System Prompt Panel -->
                            <v-expansion-panel value="prompt" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                            <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">System Prompt</div>
                                            <div class="panel-subtitle">Define AI behavior and personality</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="prompt-section">
                                            <!-- Time Context Section -->
                                            <div class="config-section mt-6">
                                                <h6 class="section-label mb-3">Time Context</h6>
                                                
                                                <!-- Time Context Switch -->
                                                <div class="switch-option-compact mb-3">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.enableTime }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.enableTime ? 'success' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.enableTime ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-clock-outline</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">Time Context</div>
                                                                <div class="switch-description">Enable time awareness for AI responses</div>
                                                            </div>
                                                        </div>
                                                        <v-switch v-model="nodeInspector.enableTime" color="success"
                                                            hide-details density="compact"
                                                            @update:model-value="updateNodeProperty('enableTime', $event)"
                                                            :readonly="isViewMode" inset />
                                                    </div>
                                                </div>

                                                <!-- Time Zone Dropdown (shown when enableTime is true) -->
                                                <div v-if="nodeInspector.enableTime" class="mt-3">
                                                    <v-select v-model="nodeInspector.timeZone" :items="DDTimeZone"
                                                        item-title="name" item-value="value" label="Time Zone"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-earth"
                                                        @update:model-value="updateNodeProperty('timeZone', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select your timezone for time-aware responses" persistent-hint
                                                        placeholder="Select your timezone">
                                                        <template v-slot:selection="{ item }">
                                                            <div class="d-flex align-center">
                                                                <v-icon small color="primary"
                                                                    class="me-2">mdi-map-marker</v-icon>
                                                                <span class="font-weight-medium">{{ item.raw.name }}</span>
                                                                <v-chip size="x-small" color="grey-darken-1"
                                                                    text-color="white" class="ml-2">
                                                                    {{ item.raw.value.split('/')[1].replace('_', ' ') }}
                                                                </v-chip>
                                                            </div>
                                                        </template>
                                                    </v-select>
                                                </div>
                                            </div>
                                            <!-- TransactionID Context Section -->
                                            <div class="config-section mt-6">
                                                <h6 class="section-label mb-3">TransactionID Context</h6>
                                                
                                                <!-- TransactionID Context Switch -->
                                                <div class="switch-option-compact mb-3">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.enableTransactionId }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.enableTransactionId ? 'info' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.enableTransactionId ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-identifier</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">Add TransactionID to Prompt</div>
                                                                <div class="switch-description">Include transaction identifier in AI context</div>
                                                            </div>
                                                        </div>
                                                        <v-switch v-model="nodeInspector.enableTransactionId" color="info"
                                                            hide-details density="compact"
                                                            @update:model-value="updateNodeProperty('enableTransactionId', $event)"
                                                            :readonly="isViewMode" inset />
                                                    </div>
                                                </div>
                                                
                                            </div>

                                            <!-- Prompt Replacement Flags Section -->
                                            <div class="config-section mt-6">
                                                <h6 class="section-label mb-3">Prompt Replacement Flags</h6>
                                                
                                                <!-- InputMessage Replace Flag -->
                                                <div class="switch-option-compact mb-3">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.enableInputMessageReplace }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.enableInputMessageReplace ? 'purple' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.enableInputMessageReplace ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-message-text-outline</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">InputMessage replacing</div>
                                                                <div class="switch-description">Replace tag with input value in prompt</div>
                                                            </div>
                                                        </div>
                                                        <v-switch v-model="nodeInspector.enableInputMessageReplace" color="purple"
                                                            hide-details density="compact"
                                                            @update:model-value="updateNodeProperty('enableInputMessageReplace', $event)"
                                                            :readonly="isViewMode" inset />
                                                    </div>
                                                    <!-- Code block with copy button -->
                                                    <div v-if="nodeInspector.enableInputMessageReplace" class="code-block-wrapper mt-2 mx-3">
                                                        <div class="code-block d-flex align-center justify-space-between">
                                                            <code class="code-text">{{InputMessageTagText}}</code>
                                                            <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                                @click="copyToClipboard(InputMessageTagText)"
                                                                class="copy-btn">
                                                                <v-icon size="14">mdi-content-copy</v-icon>
                                                                <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                            </v-btn>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- VoicePrompt Replace Flag -->
                                                <div class="switch-option-compact mb-3">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.enableVoicePromptReplace }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.enableVoicePromptReplace ? 'deep-orange' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.enableVoicePromptReplace ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-microphone-outline</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">VoicePrompt replacing</div>
                                                                <div class="switch-description">Replace tag with TTS prompt in prompt</div>
                                                            </div>
                                                        </div>
                                                        <v-switch v-model="nodeInspector.enableVoicePromptReplace" color="deep-orange"
                                                            hide-details density="compact"
                                                            @update:model-value="updateNodeProperty('enableVoicePromptReplace', $event)"
                                                            :readonly="isViewMode" inset />
                                                    </div>
                                                    <!-- Code block with copy button -->
                                                    <div v-if="nodeInspector.enableVoicePromptReplace" class="code-block-wrapper mt-2 mx-3">
                                                        <div class="code-block d-flex align-center justify-space-between">
                                                            <code class="code-text">{{VoicePromptTagText}}</code>
                                                            <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                                @click="copyToClipboard(VoicePromptTagText)"
                                                                class="copy-btn">
                                                                <v-icon size="14">mdi-content-copy</v-icon>
                                                                <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                            </v-btn>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Language Replace Flag -->
                                                <div class="switch-option-compact mb-3">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.enableLanguageReplace }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.enableLanguageReplace ? 'teal' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.enableLanguageReplace ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-translate</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">Language replacing</div>
                                                                <div class="switch-description">Replace tag with detected language in prompt</div>
                                                            </div>
                                                        </div>
                                                        <v-switch v-model="nodeInspector.enableLanguageReplace" color="teal"
                                                            hide-details density="compact"
                                                            @update:model-value="updateNodeProperty('enableLanguageReplace', $event)"
                                                            :readonly="isViewMode" inset />
                                                    </div>
                                                    <!-- Code block with copy button -->
                                                    <div v-if="nodeInspector.enableLanguageReplace" class="code-block-wrapper mt-2 mx-3">
                                                        <div class="code-block d-flex align-center justify-space-between">
                                                            <code class="code-text">{{LanguageTagText}}</code>
                                                            <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                                @click="copyToClipboard(LanguageTagText)"
                                                                class="copy-btn">
                                                                <v-icon size="14">mdi-content-copy</v-icon>
                                                                <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                            </v-btn>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                            </div>

                                            <h3 class="text-h6 d-flex align-center mb-3" style="font-size: 16px !important;">
                                                <v-icon icon="mdi-pencil-outline" size="small"
                                                    class="me-2"></v-icon>
                                                Configure system message for your AI
                                            </h3>

                                            <!-- Prompt Template Header -->
                                            <div class="mb-2" style="display:flex;flex-direction: column;align-items: flex-end;">
                                                <div style="display: flex; align-items: center;">
                                                    <v-tooltip location="top">
                                                        <template v-slot:activator="{ props }">
                                                            <v-icon icon="mdi-information-outline" size="small"
                                                                color="grey" class="me-3" v-bind="props"></v-icon>
                                                        </template>
                                                        <span>Configure additional prompts and templates</span>
                                                    </v-tooltip>
                                                    <v-btn
                                                        v-if="DDGroupPromptTemplate && DDGroupPromptTemplate.length > 0"
                                                        variant="outlined" color="primary"
                                                        prepend-icon="mdi-playlist-plus" @click="showPromptTemplate"
                                                        class="text-none font-weight-medium" size="small">
                                                        Show Templates
                                                    </v-btn>
                                                </div>
                                            </div>

                                            <!-- Main editor and templates layout -->
                                            <v-row>
                                                <!-- Editor area (full width) -->
                                                <v-col cols="12" class="prompt-editor-col">
                                                    <v-card variant="outlined" class="prompt-editor-card">
                                                        <v-card-text class="px-2 py-1">
                                                            <v-textarea v-model="nodeInspector.systemPrompt" label=""
                                                                variant="plain" counter auto-grow rows="25"
                                                                @update:model-value="updateNodeProperty('systemPrompt', $event)"
                                                                :readonly="isViewMode"
                                                                placeholder="Enter your system message here..."
                                                                class="prompt-textarea"
                                                                @contextmenu="onContextMenu"></v-textarea>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-col>
                                            </v-row>

                                            <!-- Context menu for prompt templates -->
                                            <v-menu v-model="showMenu" :activator="{ x, y }" location="bottom">
                                                <v-card min-width="250" elevation="3">
                                                    <v-list density="compact" class="py-0">
                                                        <v-list-subheader class="d-flex bg-grey-lighten-4 py-2">
                                                            <v-icon icon="mdi-text-box-plus-outline" class="me-2"
                                                                size="small"></v-icon>
                                                            Insert Template
                                                        </v-list-subheader>
                                                        <v-divider :thickness="1"
                                                            class="border-opacity-100"></v-divider>
                                                        <v-list-item v-for="(item, index) in filteredPromptTemplates"
                                                            :key="index"
                                                            @click="setSelectPromptTemplate(item); showMenu = false;"
                                                            :value="item.name" class="template-list-item">
                                                            <template v-slot:prepend>
                                                                <v-icon size="small"
                                                                    icon="mdi-text-box-outline"></v-icon>
                                                            </template>
                                                            <template v-slot:title>
                                                                <span>{{ item.name }}</span>
                                                            </template>
                                                        </v-list-item>
                                                    </v-list>
                                                </v-card>
                                            </v-menu>

                                            <!-- Dialog for showing all templates -->
                                            <v-dialog v-model="dialogShowAll" max-width="550" scrollable>
                                                <v-card>
                                                    <v-card-title class="bg-primary text-white d-flex align-center">
                                                        <v-icon icon="mdi-text-box-multiple-outline"
                                                            class="me-2"></v-icon>
                                                        Prompt Templates
                                                    </v-card-title>
                                                    <v-divider :thickness="1" class="border-opacity-100"></v-divider>
                                                    <v-card-text style="max-height: 500px" class="pt-4">
                                                        <v-text-field v-model="templateSearchQuery"
                                                            label="Search templates" prepend-inner-icon="mdi-magnify"
                                                            variant="outlined" density="compact" hide-details
                                                            class="mb-3" clearable></v-text-field>

                                                        <v-list density="compact" class="template-list-all pa-0">
                                                            <v-list-item v-for="(item, i) in searchedTemplates" :key="i"
                                                                @click="setSelectPromptTemplate(item)"
                                                                class="template-list-item" :ripple="true">
                                                                <template v-slot:prepend>
                                                                    <v-icon size="small" color="primary"
                                                                        icon="mdi-text-box-outline"></v-icon>
                                                                </template>
                                                                <template v-slot:title>
                                                                    <span>{{ item.name }}</span>
                                                                </template>
                                                                <template v-slot:subtitle>
                                                                    <span class="text-caption text-truncate">
                                                                        {{ truncatePrompt(item.prompt) }}
                                                                    </span>
                                                                </template>
                                                            </v-list-item>
                                                        </v-list>
                                                    </v-card-text>
                                                    <v-card-actions>
                                                        <v-spacer></v-spacer>
                                                        <v-btn color="blue darken-1" variant="text"
                                                            @click="dialogShowAll = false">
                                                            Close
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-card>
                                            </v-dialog>

                                            <!-- Main Prompt Template Dialog -->
                                            <v-dialog v-model="promptTemplateDialog" max-width="800" scrollable>
                                                <v-card>
                                                    <v-card-title class="bg-primary text-white d-flex align-center">
                                                        <v-icon icon="mdi-text-box-multiple-outline" class="me-2"></v-icon>
                                                        Prompt Templates
                                                        <v-spacer></v-spacer>
                                                        <v-btn icon="mdi-close" variant="text" color="white" size="small" 
                                                            @click="promptTemplateDialog = false"></v-btn>
                                                    </v-card-title>
                                                    
                                                    <v-card-text class="pa-4">
                                                        <!-- Template Group Selection -->
                                                        <v-autocomplete 
                                                            label="Select Template Group" 
                                                            :items="DDGroupPromptTemplate" 
                                                            item-title="name"
                                                            item-value="id" 
                                                            density="compact" 
                                                            clearable
                                                            variant="outlined" 
                                                            v-model="groupPromptTemplate"
                                                            prepend-inner-icon="mdi-folder-outline" 
                                                            hide-details
                                                            class="mb-4">
                                                        </v-autocomplete>

                                                        <!-- Search Template -->
                                                        <v-text-field 
                                                            v-model="templateSearchQuery"
                                                            label="Search templates" 
                                                            prepend-inner-icon="mdi-magnify"
                                                            variant="outlined" 
                                                            density="compact" 
                                                            hide-details
                                                            class="mb-4" 
                                                            clearable>
                                                        </v-text-field>

                                                        <!-- Templates List -->
                                                        <div style="max-height: 400px; overflow-y: auto;">
                                                            <div v-if="searchedTemplates.length === 0" 
                                                                class="pa-4 text-center text-grey-darken-1">
                                                                <v-icon icon="mdi-playlist-remove" size="large" 
                                                                    class="d-block mx-auto mb-2"></v-icon>
                                                                <span>No templates found</span>
                                                            </div>

                                                            <v-list v-else density="compact" class="pa-0">
                                                                <v-list-item 
                                                                    v-for="(item, i) in searchedTemplates" 
                                                                    :key="i"
                                                                    @click="setSelectPromptTemplate(item)"
                                                                    class="template-list-item ma-1" 
                                                                    :ripple="true"
                                                                    rounded>
                                                                    <template v-slot:prepend>
                                                                        <v-icon size="small" color="primary" 
                                                                            icon="mdi-text-box-outline"></v-icon>
                                                                    </template>
                                                                    <template v-slot:title>
                                                                        <span class="font-weight-medium">{{ item.name }}</span>
                                                                    </template>
                                                                    <template v-slot:subtitle>
                                                                        <span class="text-caption text-grey">
                                                                            {{ truncatePrompt(item.prompt) }}
                                                                        </span>
                                                                    </template>
                                                                    <template v-slot:append>
                                                                        <v-btn icon="mdi-plus" size="small" variant="text" 
                                                                            color="primary" @click.stop="setSelectPromptTemplate(item)">
                                                                        </v-btn>
                                                                    </template>
                                                                </v-list-item>
                                                            </v-list>
                                                        </div>
                                                    </v-card-text>
                                                    
                                                    <v-card-actions>
                                                        <v-spacer></v-spacer>
                                                        <v-btn color="primary" variant="text" @click="promptTemplateDialog = false">
                                                            Close
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-card>
                                            </v-dialog>

                                            <div class="prompt-tips mt-4">
                                                <v-card class="tips-card" color="blue-grey-lighten-5" flat>
                                                    <v-card-text class="pa-4">
                                                        <div class="d-flex align-start">
                                                            <v-icon color="info" size="20"
                                                                class="me-2 mt-1">mdi-lightbulb-outline</v-icon>
                                                            <div>
                                                                <div class="text-subtitle-2 font-weight-medium mb-2">
                                                                    Tips for
                                                                    better prompts:</div>
                                                                <ul class="text-body-2 text-medium-emphasis">
                                                                    <li>Be specific about the AI's role and expertise
                                                                    </li>
                                                                    <li>Define the tone and communication style</li>
                                                                    <li>Include any restrictions or guidelines</li>
                                                                    <li>Specify how to handle unknown information</li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </v-card-text>
                                                </v-card>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>

                    </div>

                    <!-- Tax Planning Node Configuration -->
                    <div v-else-if="selectedNode.data.type === 'taxPlanning'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-clipboard-text-clock" size="24" color="green" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-green mb-0">Tax Planning Form Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Ask tax questions from the active
                                configuration and store answers as a Tax Form</p>
                        </div>

                        <!-- Node Label -->
                        <div class="config-section mb-6">
                            <h6 class="section-label mb-3">Node Label</h6>
                            <v-row>
                                <v-col cols="12">
                                    <v-text-field v-model="nodeInspector.taxPlanningName"
                                        label="Node Label" variant="outlined"
                                        density="comfortable" hide-details="auto"
                                        prepend-inner-icon="mdi-tag-outline"
                                        placeholder="Active config"
                                        @update:model-value="updateNodeProperty('taxPlanningName', $event)"
                                        class="config-input" :readonly="isViewMode"
                                        hint="Override the node subtitle shown on the canvas" persistent-hint />
                                </v-col>
                            </v-row>
                        </div>

                        <!-- LLM Settings (reuse same fields as AI Model) -->
                        <v-expansion-panels v-model="modelConfigPanels" multiple class="config-panels elevation-0"
                            variant="accordion">
                            <!-- Basic Information Panel -->
                            <v-expansion-panel value="basic" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #10b981, #059669);">
                                            <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">LLM · Basic Information</div>
                                            <div class="panel-subtitle">Provider and model</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-6">
                                            <h6 class="section-label mb-3">Service Configuration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.provider"
                                                        :items="listDDProvider" item-title="providerName"
                                                        item-value="providerName" label="Provider*" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-cloud-outline"
                                                        @update:model-value="updateNodeProperty('provider', $event); changeProvider($event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select AI service provider" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.model" :items="DDModelShow"
                                                        item-title="modelDisplay" item-value="modelName" label="Model*"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-brain"
                                                        @update:model-value="updateNodeProperty('model', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :disabled="!nodeInspector.provider"
                                                        hint="Select specific model version" persistent-hint required />
                                                </v-col>
                                            </v-row>

                                            <!-- Question Phrasing Model Override -->
                                            <div class="switch-option-compact mt-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.taxPlanningUseQuestionModel }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.taxPlanningUseQuestionModel ? 'indigo' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.taxPlanningUseQuestionModel ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-comment-question-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Use a Separate Model for Question Phrasing</div>
                                                            <div class="switch-description">Only affects how questions are asked to the user — answer extraction always uses the main model above</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.taxPlanningUseQuestionModel" color="indigo"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('taxPlanningUseQuestionModel', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>

                                            <div v-if="nodeInspector.taxPlanningUseQuestionModel" class="mt-3">
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-autocomplete v-model="nodeInspector.taxPlanningQuestionProvider"
                                                            :items="listDDProvider" item-title="providerName"
                                                            item-value="providerName" label="Question Provider*" variant="outlined"
                                                            density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-cloud-outline"
                                                            @update:model-value="updateTaxPlanningQuestionProviderChange($event)"
                                                            class="config-input" :readonly="isViewMode"
                                                            hint="Select AI service provider for question phrasing" persistent-hint />
                                                    </v-col>
                                                </v-row>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-autocomplete v-model="nodeInspector.taxPlanningQuestionModel"
                                                            :items="getModelsByProvider(nodeInspector.taxPlanningQuestionProvider)"
                                                            item-title="modelDisplay" item-value="modelName" label="Question Model*"
                                                            variant="outlined" density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-brain"
                                                            @update:model-value="updateTaxPlanningQuestionModelChange($event)"
                                                            class="config-input" :readonly="isViewMode"
                                                            :disabled="!nodeInspector.taxPlanningQuestionProvider"
                                                            hint="Select specific model version for question phrasing" persistent-hint />
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- System Prompt Panel -->
                            <v-expansion-panel value="prompt" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #8b5cf6, #6d28d9);">
                                            <v-icon icon="mdi-text-box-outline" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">LLM · System Prompts</div>
                                            <div class="panel-subtitle">Node-level guidance — separate for asking vs. extracting</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <!-- Time Context Section -->
                                        <div class="config-section mt-6">
                                            <h6 class="section-label mb-3">Time Context</h6>

                                            <!-- Time Context Switch -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableTime }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableTime ? 'success' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableTime ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-clock-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Time Context</div>
                                                            <div class="switch-description">Enable time awareness for AI responses</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableTime" color="success"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableTime', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>

                                            <!-- Time Zone Dropdown (shown when enableTime is true) -->
                                            <div v-if="nodeInspector.enableTime" class="mt-3">
                                                <v-select v-model="nodeInspector.timeZone" :items="DDTimeZone"
                                                    item-title="name" item-value="value" label="Time Zone"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-earth"
                                                    @update:model-value="updateNodeProperty('timeZone', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    hint="Select your timezone for time-aware responses" persistent-hint
                                                    placeholder="Select your timezone">
                                                    <template v-slot:selection="{ item }">
                                                        <div class="d-flex align-center">
                                                            <v-icon small color="primary"
                                                                class="me-2">mdi-map-marker</v-icon>
                                                            <span class="font-weight-medium">{{ item.raw.name }}</span>
                                                            <v-chip size="x-small" color="grey-darken-1"
                                                                text-color="white" class="ml-2">
                                                                {{ item.raw.value.split('/')[1].replace('_', ' ') }}
                                                            </v-chip>
                                                        </div>
                                                    </template>
                                                </v-select>
                                            </div>
                                        </div>
                                        <!-- TransactionID Context Section -->
                                        <div class="config-section mt-6">
                                            <h6 class="section-label mb-3">TransactionID Context</h6>

                                            <!-- TransactionID Context Switch -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableTransactionId }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableTransactionId ? 'info' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableTransactionId ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-identifier</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Add TransactionID to Prompt</div>
                                                            <div class="switch-description">Include transaction identifier in AI context</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableTransactionId" color="info"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableTransactionId', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>

                                        </div>

                                        <!-- Prompt Replacement Flags Section -->
                                        <div class="config-section mt-6">
                                            <h6 class="section-label mb-3">Prompt Replacement Flags</h6>

                                            <!-- InputMessage Replace Flag -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableInputMessageReplace }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableInputMessageReplace ? 'purple' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableInputMessageReplace ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-message-text-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">InputMessage replacing</div>
                                                            <div class="switch-description">Replace tag with input value in prompt</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableInputMessageReplace" color="purple"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableInputMessageReplace', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                                <!-- Code block with copy button -->
                                                <div v-if="nodeInspector.enableInputMessageReplace" class="code-block-wrapper mt-2 mx-3">
                                                    <div class="code-block d-flex align-center justify-space-between">
                                                        <code class="code-text">{{InputMessageTagText}}</code>
                                                        <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                            @click="copyToClipboard(InputMessageTagText)"
                                                            class="copy-btn">
                                                            <v-icon size="14">mdi-content-copy</v-icon>
                                                            <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                        </v-btn>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- VoicePrompt Replace Flag -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableVoicePromptReplace }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableVoicePromptReplace ? 'deep-orange' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableVoicePromptReplace ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-microphone-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">VoicePrompt replacing</div>
                                                            <div class="switch-description">Replace tag with TTS prompt in prompt</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableVoicePromptReplace" color="deep-orange"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableVoicePromptReplace', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                                <!-- Code block with copy button -->
                                                <div v-if="nodeInspector.enableVoicePromptReplace" class="code-block-wrapper mt-2 mx-3">
                                                    <div class="code-block d-flex align-center justify-space-between">
                                                        <code class="code-text">{{VoicePromptTagText}}</code>
                                                        <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                            @click="copyToClipboard(VoicePromptTagText)"
                                                            class="copy-btn">
                                                            <v-icon size="14">mdi-content-copy</v-icon>
                                                            <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                        </v-btn>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Language Replace Flag -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableLanguageReplace }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableLanguageReplace ? 'teal' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableLanguageReplace ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-translate</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Language replacing</div>
                                                            <div class="switch-description">Replace tag with detected language in prompt</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableLanguageReplace" color="teal"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableLanguageReplace', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                                <!-- Code block with copy button -->
                                                <div v-if="nodeInspector.enableLanguageReplace" class="code-block-wrapper mt-2 mx-3">
                                                    <div class="code-block d-flex align-center justify-space-between">
                                                        <code class="code-text">{{LanguageTagText}}</code>
                                                        <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                            @click="copyToClipboard(LanguageTagText)"
                                                            class="copy-btn">
                                                            <v-icon size="14">mdi-content-copy</v-icon>
                                                            <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                        </v-btn>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                        <div class="config-section mb-4">
                                            <h6 class="section-label mb-2">Question System Prompt</h6>
                                            <v-textarea v-model="nodeInspector.questionSystemPrompt"
                                                label="Question System Prompt" variant="outlined" counter auto-grow rows="6"
                                                @update:model-value="updateNodeProperty('questionSystemPrompt', $event)"
                                                :readonly="isViewMode"
                                                hint="Prepended at node level when the bot phrases each question (### Tax Node Prompt ###)"
                                                persistent-hint
                                                placeholder="Guidance used when ASKING a question..." />
                                        </div>
                                        <!-- 🆕 Use Base Model Prompt (node-level) — prepend the main-model base prompt when phrasing EVERY tax question -->
                                        <div class="config-section mb-4">
                                            <v-card variant="outlined" class="pa-3" color="green-lighten-5" style="border-radius: 12px;">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="24" color="green" class="me-3">
                                                        <v-icon color="white" size="12">mdi-text-box-check</v-icon>
                                                    </v-avatar>
                                                    <div class="flex-grow-1 me-3">
                                                        <div class="text-subtitle-2"><span style="color:#4CAF50;font-weight: 500;"> Use Base Model Prompt</span></div>
                                                        <div class="text-caption text-medium-emphasis">Combine with the default model prompt</div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.useBaseModelPrompt" color="green"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('useBaseModelPrompt', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </v-card>
                                        </div>
                                        <div class="config-section">
                                            <h6 class="section-label mb-2">Answer System Prompt</h6>
                                            <v-textarea v-model="nodeInspector.answerSystemPrompt"
                                                label="Answer System Prompt" variant="outlined" counter auto-grow rows="6"
                                                @update:model-value="updateNodeProperty('answerSystemPrompt', $event)"
                                                :readonly="isViewMode"
                                                hint="Prepended at node level when extracting the user's answer (### Tax Node Prompt ###)"
                                                persistent-hint
                                                placeholder="Guidance used when EXTRACTING the answer..." />
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- AI Parameters Panel -->
                            <v-expansion-panel value="parameters" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #3b82f6, #1d4ed8);">
                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">LLM · AI Parameters</div>
                                            <div class="panel-subtitle">Temperature, penalties, output</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Core Parameters</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.temperature"
                                                        label="Temperature*" type="number" step="0.1" min="0" max="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-thermometer"
                                                        @update:model-value="updateNodeProperty('temperature', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Creativity level (0 = focused, 1 = creative)"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.topP" label="Top P"
                                                        type="number" step="0.1" min="0" max="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-filter-variant"
                                                        @update:model-value="updateNodeProperty('topP', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Token selection probability threshold" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Penalty Settings</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.frequencyPenalty"
                                                        label="Frequency Penalty" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-repeat-off"
                                                        @update:model-value="updateNodeProperty('frequencyPenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Reduce repetition of frequent tokens" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.presencePenalty"
                                                        label="Presence Penalty" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto"
                                                        prepend-inner-icon="mdi-minus-circle-outline"
                                                        @update:model-value="updateNodeProperty('presencePenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Reduce repetition of any tokens"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Output Control</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.maxTokens"
                                                        label="Max Tokens Response" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-check-outline"
                                                        @update:model-value="updateNodeProperty('maxTokens', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Maximum response length in tokens"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.memoryTurn"
                                                        label="Memory Turn Count" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-history"
                                                        @update:model-value="updateNodeProperty('memoryTurn', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Number of conversation turns to remember"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <!-- Persona / voice gender (ครับ/ค่ะ) — same key the AI Model node uses -->
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Persona</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.gender"
                                                        :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                        item-title="name" item-value="id" label="Gender"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-account"
                                                        @update:model-value="updateNodeProperty('gender', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="เพศของบอทตอนพูด (ชาย = ครับ, หญิง = ค่ะ)" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Tax Settings Panel -->
                            <v-expansion-panel value="tax" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #f59e0b, #b45309);">
                                            <v-icon icon="mdi-clipboard-text-clock" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Tax Settings</div>
                                            <div class="panel-subtitle">Tax year resolution & form behavior</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Tax Year</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.taxPlanningYearMode"
                                                        :items="[{ id: 'active', name: 'Use active year (auto)' }, { id: 'pinned', name: 'Pinned year' }]"
                                                        item-title="name" item-value="id" label="Year mode"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-calendar-clock"
                                                        @update:model-value="updateNodeProperty('taxPlanningYearMode', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Resolve the active Tax Year automatically, or pin a specific year"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row v-if="nodeInspector.taxPlanningYearMode === 'pinned'">
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.taxPlanningYearId"
                                                        :items="taxPlanningYearOptions" item-title="label"
                                                        item-value="yearID" label="Tax Year" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-calendar"
                                                        @update:model-value="updateNodeProperty('taxPlanningYearId', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Pin a specific Tax Year (from Tax Configuration)"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Form Behavior</h6>
                                            <div class="switch-option-compact">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.taxPlanningAskWhenMultipleActive }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.taxPlanningAskWhenMultipleActive ? 'green' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.taxPlanningAskWhenMultipleActive ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-help-circle-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Ask when multiple years are active</div>
                                                            <div class="switch-description">Let the user pick the Tax Year when more than one is active</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.taxPlanningAskWhenMultipleActive" color="green"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('taxPlanningAskWhenMultipleActive', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>
                                        </div>
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Completion Message</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-textarea v-model="nodeInspector.taxPlanningCompletionMessage"
                                                        label="Completion message" variant="outlined"
                                                        density="comfortable" auto-grow rows="3" hide-details="auto"
                                                        @update:model-value="updateNodeProperty('taxPlanningCompletionMessage', $event)"
                                                        :readonly="isViewMode"
                                                        placeholder="Shown when the user finishes all tax question groups"
                                                        hint="Message sent when the Tax Form is completed" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </div>

                    <!-- Tax Planning Form V2 Node Configuration
                         config ทั้งหมด (LLM / System Prompt / พฤติกรรมฟอร์ม) ถูกย้ายไปเก็บที่
                         tax.TaxPlanningSettingsV2 แบบแยกเวอร์ชันแล้ว — node จึงเหลือแค่
                         Node Label + เลือกว่าจะใช้ "เวอร์ชันการตั้งค่า" ไหน -->
                    <div v-else-if="selectedNode.data.type === 'taxPlanningV2'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-clipboard-text-clock" size="24" color="teal" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-teal mb-0">Tax Planning Form V2 Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">
                                ถามคำถามภาษีตามชุดคำถาม V2 แล้วบันทึกคำตอบเป็น Tax Form V2 —
                                การตั้งค่าทั้งหมดอ่านจาก “เวอร์ชันการตั้งค่า” ที่เลือกด้านล่าง
                            </p>
                        </div>

                        <!-- Node Label -->
                        <div class="config-section mb-6">
                            <h6 class="section-label mb-3">Node Label</h6>
                            <v-row>
                                <v-col cols="12">
                                    <v-text-field v-model="nodeInspector.taxPlanningV2Name"
                                        label="Node Label" variant="outlined"
                                        density="comfortable" hide-details="auto"
                                        prepend-inner-icon="mdi-tag-outline"
                                        placeholder="Active config V2"
                                        data-testid="taxplanningv2-name"
                                        @update:model-value="updateNodeProperty('taxPlanningV2Name', $event)"
                                        class="config-input" :readonly="isViewMode"
                                        hint="Override the node subtitle shown on the canvas" persistent-hint />
                                </v-col>
                            </v-row>
                        </div>

                        <!-- Settings Version — node ไม่ได้เก็บ config เองแล้ว เลือกได้แค่ว่าจะใช้เวอร์ชันไหน -->
                        <div class="config-section mb-6">
                            <h6 class="section-label mb-3">เวอร์ชันการตั้งค่า (Settings Version)</h6>
                            <v-row>
                                <v-col cols="12">
                                    <v-autocomplete v-model="nodeInspector.taxPlanningV2SettingsVersionId"
                                        :items="taxPlanningV2SettingsVersionOptions" item-title="label"
                                        item-value="value" label="เวอร์ชันการตั้งค่าที่ใช้"
                                        variant="outlined" density="comfortable" hide-details="auto"
                                        prepend-inner-icon="mdi-source-branch"
                                        :loading="taxPlanningV2SettingsVersionsLoading"
                                        data-testid="taxplanningv2-settings-version"
                                        @update:model-value="updateNodeProperty('taxPlanningV2SettingsVersionId', $event ?? null)"
                                        class="config-input" :readonly="isViewMode"
                                        hint="เว้นว่างไว้ = ใช้เวอร์ชันที่ใช้งานอยู่ (อัตโนมัติ) · เลือกเวอร์ชันเจาะจงเมื่อต้องการปักหมุด node นี้ไว้กับเวอร์ชันเดียว"
                                        persistent-hint>
                                        <template #item="{ props: itemProps, item }">
                                            <v-list-item v-bind="itemProps" :subtitle="item.raw.subtitle">
                                                <template #append>
                                                    <v-chip v-if="item.raw.isActive" size="x-small" color="teal"
                                                        variant="flat">ใช้งานอยู่</v-chip>
                                                </template>
                                            </v-list-item>
                                        </template>
                                    </v-autocomplete>
                                </v-col>
                            </v-row>
                        </div>

                        <!-- สรุปว่ากำลังใช้เวอร์ชันไหน + ทางลัดไปหน้าตั้งค่า -->
                        <div class="config-section mb-6">
                            <v-card variant="outlined" class="pa-4" style="border-radius: 12px;">
                                <div class="d-flex align-start">
                                    <v-avatar size="32" color="teal" class="me-3">
                                        <v-icon color="white" size="18">mdi-cog-outline</v-icon>
                                    </v-avatar>
                                    <div class="flex-grow-1">
                                        <div class="text-subtitle-2 mb-1">การตั้งค่าของ node นี้อยู่ที่หน้า Tax Settings V2</div>
                                        <div class="text-caption text-medium-emphasis mb-3">
                                            ค่า LLM (provider / model / temperature / max tokens / memory / gender),
                                            System Prompt ของ node (ทั้งชั้นหลัก ชั้นตอนถาม และชั้นตอนสกัดคำตอบ)
                                            และพฤติกรรมฟอร์ม (โหมดปีภาษี / ปีที่ปักหมุด / ถามเมื่อมีหลายปี active /
                                            บันทึกคำตอบ / ข้อความเมื่อทำฟอร์มจบ)
                                            ถูกย้ายออกจาก node ไปเก็บเป็น “เวอร์ชันการตั้งค่า” แล้ว
                                            แก้ไขได้ที่หน้า Tax Configuration V2 → ปุ่ม SETTINGS
                                        </div>
                                        <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px;">
                                            <span class="text-caption text-medium-emphasis">กำลังใช้:</span>
                                            <v-chip size="small" color="teal" variant="tonal"
                                                data-testid="taxplanningv2-effective-version">
                                                {{ taxPlanningV2EffectiveVersionLabel }}
                                            </v-chip>
                                            <v-chip v-if="taxPlanningV2PinnedSettingsVersionId === null" size="small"
                                                color="grey" variant="tonal">อัตโนมัติ</v-chip>
                                            <v-chip v-else size="small" color="indigo" variant="tonal">ปักหมุดไว้</v-chip>
                                        </div>
                                        <v-alert v-if="taxPlanningV2PinnedVersionMissing" type="warning"
                                            variant="tonal" density="compact" class="mb-3"
                                            data-testid="taxplanningv2-version-missing">
                                            ไม่พบเวอร์ชันที่ปักหมุดไว้ (ID {{ taxPlanningV2PinnedSettingsVersionId }})
                                            — อาจถูกลบไปแล้ว ตอนรันจริงระบบจะย้อนไปใช้เวอร์ชันที่ใช้งานอยู่ให้อัตโนมัติ
                                        </v-alert>
                                        <v-btn size="small" variant="tonal" color="teal"
                                            prepend-icon="mdi-open-in-new"
                                            data-testid="taxplanningv2-open-settings"
                                            @click="openTaxPlanningV2Settings">
                                            เปิดหน้าตั้งค่า Tax Configuration V2
                                        </v-btn>
                                    </div>
                                </div>
                            </v-card>
                        </div>
                    </div>

                    <!-- Reformat TTS Node Configuration -->
                    <div v-else-if="selectedNode.data.type === 'reformatTTS'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-text-to-speech" size="24" color="#2196f3" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold" style="color: #2196f3;">Reformat TTS Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Step 1: Reformat text for TTS (conversational language)</p>
                        </div>

                        <!-- Model Configuration Panels -->
                        <v-expansion-panels v-model="modelConfigPanels" multiple class="config-panels elevation-0" variant="accordion">
                            <!-- Basic Information Panel -->
                            <v-expansion-panel value="basic" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Basic Information</div>
                                            <div class="panel-subtitle">Provider, model, and basic settings</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <!-- Provider and Model -->
                                        <div class="config-section mb-6">
                                            <h6 class="section-label mb-3">Service Configuration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.provider"
                                                        :items="listDDProvider" item-title="providerName"
                                                        item-value="providerName" label="Provider*" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-cloud-outline"
                                                        @update:model-value="updateNodeProperty('provider', $event); changeProvider($event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select AI service provider" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.model" :items="DDModelShow"
                                                        item-title="modelDisplay" item-value="modelName" label="Model*"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-brain"
                                                        @update:model-value="updateNodeProperty('model', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :disabled="!nodeInspector.provider"
                                                        hint="Select specific model version" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                           <v-row>
                                               <v-col cols="12">
                                                   <v-text-field v-model.number="nodeInspector.nodeTimeout"
                                                       label="Request Timeout (seconds)" type="number" min="0"
                                                       variant="outlined" density="comfortable" hide-details="auto"
                                                       prepend-inner-icon="mdi-timer-outline"
                                                       @update:model-value="updateNodeProperty('nodeTimeout', $event)"
                                                       @keypress="isNumberpositive($event)" class="config-input"
                                                       :readonly="isViewMode"
                                                       hint="0 = use global timeout setting" persistent-hint />
                                               </v-col>
                                           </v-row>
                                           <v-row>
                                               <v-col cols="12">
                                                   <v-text-field v-model.number="nodeInspector.nodeMaxRetry"
                                                       label="Max Retry" type="number" min="0"
                                                       variant="outlined" density="comfortable" hide-details="auto"
                                                       prepend-inner-icon="mdi-refresh"
                                                       @update:model-value="updateNodeProperty('nodeMaxRetry', $event)"
                                                       @keypress="isNumberpositive($event)" class="config-input"
                                                       :readonly="isViewMode"
                                                       hint="0 = use global max retry setting" persistent-hint />
                                               </v-col>
                                           </v-row>
                                       </div>
                                   </div>
                               </v-expansion-panel-text>
                           </v-expansion-panel>

                           <!-- AI Parameters Panel -->
                           <v-expansion-panel value="parameters" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                            <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">AI Parameters</div>
                                            <div class="panel-subtitle">Fine-tune model behavior</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-row>
                                            <v-col cols="12">
                                                <v-text-field v-model.number="nodeInspector.temperature"
                                                    label="Temperature" type="number" step="0.1" min="0" max="1"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-thermometer"
                                                    @update:model-value="updateNodeProperty('temperature', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    hint="Creativity level (0 = focused, 1 = creative)" persistent-hint />
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12">
                                                <v-text-field v-model.number="nodeInspector.maxTokens"
                                                    label="Max Tokens" type="number" min="1"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-text-box-check-outline"
                                                    @update:model-value="updateNodeProperty('maxTokens', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    hint="Maximum response length" persistent-hint />
                                            </v-col>
                                        </v-row>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- System Prompt Panel -->
                            <v-expansion-panel value="prompt" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                            <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Voice Reformat Prompt</div>
                                            <div class="panel-subtitle">Instructions for TTS reformatting</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-row>
                                            <v-col cols="12">
                                                <v-textarea v-model="nodeInspector.systemPrompt" label=""
                                                    variant="outlined" counter auto-grow rows="10"
                                                    @update:model-value="updateNodeProperty('systemPrompt', $event)"
                                                    :readonly="isViewMode"
                                                    placeholder="Enter voice reformat instructions...
Example: Reformat text for natural speech, convert numbers to words, add pauses."
                                                    class="prompt-textarea"></v-textarea>
                                            </v-col>
                                        </v-row>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Response Options Panel -->
                            <v-expansion-panel value="response" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Response Options</div>
                                            <div class="panel-subtitle">Configure how responses are generated</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <!-- Stream Response -->
                                        <div class="switch-option-compact mb-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.streamResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.streamResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.streamResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-text-box-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">Stream Response</div>
                                                        <div class="switch-description">Progressive text generation</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.streamResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('streamResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>

                                        <!-- JSON Response -->
                                        <div class="switch-option-compact mt-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.jsonResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.jsonResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.jsonResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-code-json</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">JSON Response</div>
                                                        <div class="switch-description">Return structured JSON format</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.jsonResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('jsonResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>

                                        <!-- Processing Mode (ReformatTTS) -->
                                        <div class="config-section mt-4">
                                            <h6 class="section-label mb-3">Processing Mode</h6>
                                            <v-select
                                                v-model="nodeInspector.processingMode"
                                                :items="[
                                                    { title: 'LLM (AI Processing)', value: 'LLM' },
                                                    { title: 'RuleBased (Fast)', value: 'RuleBased' }
                                                ]"
                                                item-title="title"
                                                item-value="value"
                                                label="Processing Mode"
                                                variant="outlined"
                                                density="comfortable"
                                                prepend-inner-icon="mdi-cog-transfer"
                                                @update:model-value="updateNodeProperty('processingMode', $event)"
                                                :readonly="isViewMode"
                                                hint="LLM = AI reformat (~1-2s), RuleBased = Fast rules (~0ms)"
                                                persistent-hint
                                            />
                                        </div>

                                        <!-- TransactionID Context Toggle -->
                                        <div class="switch-option-compact mb-3 mt-4">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.enableTransactionId }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.enableTransactionId ? 'info' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.enableTransactionId ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-identifier</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">Add TransactionID to Prompt</div>
                                                        <div class="switch-description">Include transaction identifier in AI context</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.enableTransactionId" color="info"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('enableTransactionId', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>

                                        <!-- Language Replace Toggle -->
                                        <div class="switch-option-compact mb-3 mt-4">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.enableLanguageReplace }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.enableLanguageReplace ? 'deep-purple' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.enableLanguageReplace ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-translate</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">Enable Language Replace</div>
                                                        <div class="switch-description">Replace {{Language}} tag in prompt with detected language</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.enableLanguageReplace" color="deep-purple"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('enableLanguageReplace', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Cache Settings Panel -->
                            <v-expansion-panel value="cache" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                            <v-icon icon="mdi-cached" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Cache Settings</div>
                                            <div class="panel-subtitle">Configure response caching for performance</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Response Cache</h6>

                                            <!-- Enable Cache Switch -->
                                            <div class="switch-option-compact mb-4">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableCache }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableCache ? 'warning' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableCache ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-lightning-bolt</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Enable Cache</div>
                                                            <div class="switch-description">Cache responses to reduce LLM calls</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableCache"
                                                        color="warning" hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableCache', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>

                                            <!-- Cache TTL -->
                                            <v-row v-if="nodeInspector.enableCache">
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.cacheTTLMinutes"
                                                        label="Cache TTL (minutes)" type="number" min="1" max="10080"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('cacheTTLMinutes', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="How long to keep cached responses (1-10080 min)"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>

                                            <h6 v-if="nodeInspector.enableCache" class="section-label mb-3 mt-4">Semantic Cache</h6>

                                            <!-- Enable Semantic Cache Switch -->
                                            <div v-if="nodeInspector.enableCache" class="switch-option-compact mb-4">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableSemanticCache }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableSemanticCache ? 'deep-purple' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableSemanticCache ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-brain</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Semantic Match</div>
                                                            <div class="switch-description">Match similar segments using BGE-M3 AI embeddings</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableSemanticCache"
                                                        color="deep-purple" hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableSemanticCache', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>

                                            <!-- Similarity Threshold Slider -->
                                            <v-row v-if="nodeInspector.enableCache && nodeInspector.enableSemanticCache">
                                                <v-col cols="12">
                                                    <div class="slider-label mb-2">
                                                        <span>Similarity Threshold</span>
                                                        <span class="text-primary font-weight-bold">{{ (nodeInspector.similarityThreshold * 100).toFixed(0) }}%</span>
                                                    </div>
                                                    <v-slider
                                                        v-model="nodeInspector.similarityThreshold"
                                                        min="0.50"
                                                        max="1.00"
                                                        step="0.01"
                                                        color="deep-purple"
                                                        track-color="grey-lighten-2"
                                                        thumb-label
                                                        hide-details
                                                        @update:model-value="updateNodeProperty('similarityThreshold', $event)"
                                                        :readonly="isViewMode">
                                                        <template v-slot:thumb-label="{ modelValue }">
                                                            {{ (modelValue * 100).toFixed(0) }}%
                                                        </template>
                                                    </v-slider>
                                                    <div class="text-caption text-grey mt-1">
                                                        Higher = more strict (exact match), Lower = more flexible
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </div>

                    <!-- SSML Format Node Configuration -->
                    <div v-else-if="selectedNode.data.type === 'ssmlFormat'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-xml" size="24" color="#ff9800" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold" style="color: #ff9800;">SSML Format Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Step 2: Add SSML break tags for TTS engine</p>
                        </div>

                        <!-- Model Configuration Panels -->
                        <v-expansion-panels v-model="modelConfigPanels" multiple class="config-panels elevation-0" variant="accordion">
                            <!-- Basic Information Panel -->
                            <v-expansion-panel value="basic" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Basic Information</div>
                                            <div class="panel-subtitle">Provider, model, and basic settings</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <!-- Provider and Model -->
                                        <div class="config-section mb-6">
                                            <h6 class="section-label mb-3">Service Configuration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.provider"
                                                        :items="listDDProvider" item-title="providerName"
                                                        item-value="providerName" label="Provider*" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-cloud-outline"
                                                        @update:model-value="updateNodeProperty('provider', $event); changeProvider($event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select AI service provider" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.model" :items="DDModelShow"
                                                        item-title="modelDisplay" item-value="modelName" label="Model*"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-brain"
                                                        @update:model-value="updateNodeProperty('model', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :disabled="!nodeInspector.provider"
                                                        hint="Select specific model version" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.nodeTimeout"
                                                        label="Request Timeout (seconds)" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('nodeTimeout', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="0 = use global timeout setting" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.nodeMaxRetry"
                                                        label="Max Retry" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-refresh"
                                                        @update:model-value="updateNodeProperty('nodeMaxRetry', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="0 = use global max retry setting" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- AI Parameters Panel -->
                            <v-expansion-panel value="parameters" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                            <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">AI Parameters</div>
                                            <div class="panel-subtitle">Fine-tune model behavior</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-row>
                                            <v-col cols="12">
                                                <v-text-field v-model.number="nodeInspector.temperature"
                                                    label="Temperature" type="number" step="0.1" min="0" max="1"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-thermometer"
                                                    @update:model-value="updateNodeProperty('temperature', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    hint="Creativity level (0 = focused, 1 = creative)" persistent-hint />
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12">
                                                <v-text-field v-model.number="nodeInspector.maxTokens"
                                                    label="Max Tokens" type="number" min="1"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-text-box-check-outline"
                                                    @update:model-value="updateNodeProperty('maxTokens', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    hint="Maximum response length" persistent-hint />
                                            </v-col>
                                        </v-row>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- System Prompt Panel -->
                            <v-expansion-panel value="prompt" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                            <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">SSML Format Prompt</div>
                                            <div class="panel-subtitle">Instructions for SSML formatting</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-row>
                                            <v-col cols="12">
                                                <v-textarea v-model="nodeInspector.systemPrompt" label=""
                                                    variant="outlined" counter auto-grow rows="10"
                                                    @update:model-value="updateNodeProperty('systemPrompt', $event)"
                                                    :readonly="isViewMode"
                                                    placeholder="Enter SSML formatting instructions...
Example: Add <break> tags after sentences, preserve links and buttons."
                                                    class="prompt-textarea"></v-textarea>
                                            </v-col>
                                        </v-row>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Response Options Panel -->
                            <v-expansion-panel value="response" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Response Options</div>
                                            <div class="panel-subtitle">Configure how responses are generated</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <!-- Stream Response -->
                                        <div class="switch-option-compact mb-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.streamResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.streamResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.streamResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-text-box-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">Stream Response</div>
                                                        <div class="switch-description">Progressive text generation</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.streamResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('streamResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>

                                        <!-- JSON Response -->
                                        <div class="switch-option-compact mt-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.jsonResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.jsonResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.jsonResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-code-json</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">JSON Response</div>
                                                        <div class="switch-description">Return structured JSON format</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.jsonResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('jsonResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>

                                        <!-- Processing Mode (SSMLFormat) -->
                                        <div class="config-section mt-4">
                                            <h6 class="section-label mb-3">Processing Mode</h6>
                                            <v-select
                                                v-model="nodeInspector.processingMode"
                                                :items="[
                                                    { title: 'RuleBased (Fast)', value: 'RuleBased' },
                                                    { title: 'LLM (AI Processing)', value: 'LLM' }
                                                ]"
                                                item-title="title"
                                                item-value="value"
                                                label="Processing Mode"
                                                variant="outlined"
                                                density="comfortable"
                                                prepend-inner-icon="mdi-cog-transfer"
                                                @update:model-value="updateNodeProperty('processingMode', $event)"
                                                :readonly="isViewMode"
                                                hint="RuleBased = Fast rules (~0ms), LLM = AI processing (~1-2s)"
                                                persistent-hint
                                            />
                                        </div>

                                        <!-- TransactionID Context Toggle -->
                                        <div class="switch-option-compact mb-3 mt-4">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.enableTransactionId }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.enableTransactionId ? 'info' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.enableTransactionId ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-identifier</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">Add TransactionID to Prompt</div>
                                                        <div class="switch-description">Include transaction identifier in AI context</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.enableTransactionId" color="info"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('enableTransactionId', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Cache Settings Panel -->
                            <v-expansion-panel value="cache" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                            <v-icon icon="mdi-cached" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Cache Settings</div>
                                            <div class="panel-subtitle">Configure response caching for performance</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Response Cache</h6>

                                            <!-- Enable Cache Switch -->
                                            <div class="switch-option-compact mb-4">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableCache }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableCache ? 'warning' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableCache ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-lightning-bolt</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Enable Cache</div>
                                                            <div class="switch-description">Cache responses to reduce LLM calls</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableCache"
                                                        color="warning" hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableCache', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>

                                            <!-- Cache TTL -->
                                            <v-row v-if="nodeInspector.enableCache">
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.cacheTTLMinutes"
                                                        label="Cache TTL (minutes)" type="number" min="1" max="10080"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('cacheTTLMinutes', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="How long to keep cached responses (1-10080 min)"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>

                                            <h6 v-if="nodeInspector.enableCache" class="section-label mb-3 mt-4">Semantic Cache</h6>

                                            <!-- Enable Semantic Cache Switch -->
                                            <div v-if="nodeInspector.enableCache" class="switch-option-compact mb-4">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableSemanticCache }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableSemanticCache ? 'deep-purple' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableSemanticCache ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-brain</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Semantic Match</div>
                                                            <div class="switch-description">Match similar segments using BGE-M3 AI embeddings</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableSemanticCache"
                                                        color="deep-purple" hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableSemanticCache', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>

                                            <!-- Similarity Threshold Slider -->
                                            <v-row v-if="nodeInspector.enableCache && nodeInspector.enableSemanticCache">
                                                <v-col cols="12">
                                                    <div class="slider-label mb-2">
                                                        <span>Similarity Threshold</span>
                                                        <span class="text-primary font-weight-bold">{{ (nodeInspector.similarityThreshold * 100).toFixed(0) }}%</span>
                                                    </div>
                                                    <v-slider
                                                        v-model="nodeInspector.similarityThreshold"
                                                        min="0.50"
                                                        max="1.00"
                                                        step="0.01"
                                                        color="deep-purple"
                                                        track-color="grey-lighten-2"
                                                        thumb-label
                                                        hide-details
                                                        @update:model-value="updateNodeProperty('similarityThreshold', $event)"
                                                        :readonly="isViewMode">
                                                        <template v-slot:thumb-label="{ modelValue }">
                                                            {{ (modelValue * 100).toFixed(0) }}%
                                                        </template>
                                                    </v-slider>
                                                    <div class="text-caption text-grey mt-1">
                                                        Higher = more strict (exact match), Lower = more flexible
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </div>

                    <!-- Query Rewriter Node Configuration -->
                    <div v-else-if="selectedNode.data.type === 'queryRewriter'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-message-text-clock-outline" size="24" color="#7c3aed" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold" style="color: #7c3aed;">Query Rewriter Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Merge context or summarize history with user message</p>
                        </div>

                        <!-- Model Configuration Panels -->
                        <v-expansion-panels v-model="modelConfigPanels" multiple class="config-panels elevation-0" variant="accordion">
                            <!-- Mode Selection Panel -->
                            <v-expansion-panel value="mode" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #7c3aed, #a855f7);">
                                            <v-icon icon="mdi-tune-variant" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Rewriter Mode</div>
                                            <div class="panel-subtitle">Select how to process user query</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-row>
                                            <v-col cols="12">
                                                <v-select
                                                    v-model="nodeInspector.queryRewriterMode"
                                                    :items="[
                                                        { title: 'Continue Question - Merge proactive message with user response', value: 'continueQuestion' },
                                                        { title: 'History Summary - Summarize chat history with latest question', value: 'historySummary' }
                                                    ]"
                                                    item-title="title"
                                                    item-value="value"
                                                    label="Rewriter Mode*"
                                                    variant="outlined"
                                                    density="comfortable"
                                                    prepend-inner-icon="mdi-swap-horizontal"
                                                    @update:model-value="updateNodeProperty('queryRewriterMode', $event)"
                                                    :readonly="isViewMode"
                                                    hint="Choose the query rewriting strategy"
                                                    persistent-hint
                                                />
                                            </v-col>
                                        </v-row>
                                        
                                        <!-- Mode Description -->
                                        <v-alert v-if="nodeInspector.queryRewriterMode === 'continueQuestion'" 
                                            type="info" variant="tonal" density="compact" class="mt-4">
                                            <strong>Continue Question Mode:</strong> When the bot asks a proactive question (ContinueQuestion), 
                                            this mode merges the bot's question with the user's response into a complete, standalone query.
                                        </v-alert>
                                        <v-alert v-else-if="nodeInspector.queryRewriterMode === 'historySummary'" 
                                            type="info" variant="tonal" density="compact" class="mt-4">
                                            <strong>History Summary Mode:</strong> Summarizes the conversation history and combines it 
                                            with the user's latest message to create context-aware query.
                                        </v-alert>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Basic Information Panel -->
                            <v-expansion-panel value="basic" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Basic Information</div>
                                            <div class="panel-subtitle">Provider and model settings</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <!-- Provider and Model -->
                                        <div class="config-section mb-6">
                                            <h6 class="section-label mb-3">Service Configuration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.queryRewriterProvider"
                                                        :items="listDDProvider" item-title="providerName"
                                                        item-value="providerName" label="Provider*" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-cloud-outline"
                                                        @update:model-value="updateNodeProperty('queryRewriterProvider', $event); changeProvider($event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select AI service provider" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.queryRewriterModel" :items="DDModelShow"
                                                        item-title="modelDisplay" item-value="modelName" label="Model*"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-brain"
                                                        @update:model-value="updateNodeProperty('queryRewriterModel', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :disabled="!nodeInspector.queryRewriterProvider"
                                                        hint="Select specific model version" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                            <v-row v-if="nodeInspector.queryRewriterApiVersion">
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.queryRewriterApiVersion"
                                                        label="API Version" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-api"
                                                        class="config-input" readonly
                                                        hint="Auto-detected from selected model" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.queryRewriterTimeout"
                                                        label="Request Timeout (seconds)" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('queryRewriterTimeout', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="0 = use global timeout setting" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.queryRewriterMaxRetry"
                                                        label="Max Retry" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-refresh"
                                                        @update:model-value="updateNodeProperty('queryRewriterMaxRetry', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="0 = use global max retry setting" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- AI Parameters Panel -->
                            <v-expansion-panel value="parameters" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                            <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">AI Parameters</div>
                                            <div class="panel-subtitle">Fine-tune model behavior</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-row>
                                            <v-col cols="6">
                                                <v-text-field v-model.number="nodeInspector.queryRewriterTemperature"
                                                    label="Temperature" type="number" step="0.1" min="0" max="2"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-thermometer"
                                                    @update:model-value="updateNodeProperty('queryRewriterTemperature', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    hint="0 = focused, 1 = creative" persistent-hint />
                                            </v-col>
                                            <v-col cols="6">
                                                <v-text-field v-model.number="nodeInspector.queryRewriterTopP"
                                                    label="Top P" type="number" step="0.1" min="0" max="1"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-chart-bell-curve"
                                                    @update:model-value="updateNodeProperty('queryRewriterTopP', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    hint="Nucleus sampling" persistent-hint />
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="12">
                                                <v-text-field v-model.number="nodeInspector.queryRewriterMaxTokens"
                                                    label="Max Tokens" type="number" min="1"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-text-box-check-outline"
                                                    @update:model-value="updateNodeProperty('queryRewriterMaxTokens', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    hint="Maximum response length" persistent-hint />
                                            </v-col>
                                        </v-row>
                                        <v-row>
                                            <v-col cols="6">
                                                <v-text-field v-model.number="nodeInspector.queryRewriterFrequencyPenalty"
                                                    label="Frequency Penalty" type="number" step="0.1" min="-2" max="2"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-repeat"
                                                    @update:model-value="updateNodeProperty('queryRewriterFrequencyPenalty', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    hint="Reduce repetition" persistent-hint />
                                            </v-col>
                                            <v-col cols="6">
                                                <v-text-field v-model.number="nodeInspector.queryRewriterPresencePenalty"
                                                    label="Presence Penalty" type="number" step="0.1" min="-2" max="2"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-new-box"
                                                    @update:model-value="updateNodeProperty('queryRewriterPresencePenalty', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    hint="Encourage new topics" persistent-hint />
                                            </v-col>
                                        </v-row>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Response Options Panel -->
                            <v-expansion-panel value="response" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Response Options</div>
                                            <div class="panel-subtitle">Configure how responses are generated</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <!-- Stream Response -->
                                        <div class="switch-option-compact mb-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.queryRewriterStreamResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.queryRewriterStreamResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.queryRewriterStreamResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-text-box-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">Stream Response</div>
                                                        <div class="switch-description">Progressive text generation
                                                        </div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.queryRewriterStreamResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('queryRewriterStreamResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>

                                        <!-- Json Response -->
                                        <div class="switch-option-compact mt-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.queryRewriterJsonResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.queryRewriterJsonResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.queryRewriterJsonResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-code-json</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">JSON Response</div>
                                                        <div class="switch-description">Return structured JSON format
                                                        </div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.queryRewriterJsonResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('queryRewriterJsonResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Memory Settings Panel (for historySummary mode) -->
                            <v-expansion-panel value="memory" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                            <v-icon icon="mdi-history" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Memory Settings</div>
                                            <div class="panel-subtitle">Configure history retrieval</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-row>
                                            <v-col cols="6">
                                                <v-text-field v-model.number="nodeInspector.queryRewriterMemoryTurn"
                                                    label="Memory Turns" type="number" min="1" max="50"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-message-processing"
                                                    @update:model-value="updateNodeProperty('queryRewriterMemoryTurn', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    hint="Number of conversation turns to include" persistent-hint />
                                            </v-col>
                                            <v-col cols="6">
                                                <v-text-field v-model.number="nodeInspector.queryRewriterMemoryDuration"
                                                    label="Memory Duration (min)" type="number" min="1"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-clock-outline"
                                                    @update:model-value="updateNodeProperty('queryRewriterMemoryDuration', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    hint="Time window for history (minutes)" persistent-hint />
                                            </v-col>
                                        </v-row>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- System Prompt Panel -->
                            <v-expansion-panel value="prompt" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                            <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">System Prompt</div>
                                            <div class="panel-subtitle">Instructions for query rewriting</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-row>
                                            <v-col cols="12">
                                                <!-- Language Replace Flag -->
                                                <div class="switch-option-compact mb-3">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.queryRewriterEnableLanguageReplace }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.queryRewriterEnableLanguageReplace ? 'teal' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.queryRewriterEnableLanguageReplace ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-translate</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">Language replacing</div>
                                                                <div class="switch-description">Replace tag with detected language in prompt</div>
                                                            </div>
                                                        </div>
                                                        <v-switch v-model="nodeInspector.queryRewriterEnableLanguageReplace" color="teal"
                                                            hide-details density="compact"
                                                            @update:model-value="updateNodeProperty('queryRewriterEnableLanguageReplace', $event)"
                                                            :readonly="isViewMode" inset />
                                                    </div>
                                                    <div v-if="nodeInspector.queryRewriterEnableLanguageReplace" class="code-block-wrapper mt-2 mx-3">
                                                        <div class="code-block d-flex align-center justify-space-between">
                                                            <code class="code-text">{{LanguageTagText}}</code>
                                                            <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                                @click="copyToClipboard(LanguageTagText)" class="copy-btn">
                                                                <v-icon size="14">mdi-content-copy</v-icon>
                                                                <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                            </v-btn>
                                                        </div>
                                                    </div>
                                                </div>
                                                <v-textarea v-model="nodeInspector.queryRewriterSystemPrompt" label=""
                                                    variant="outlined" counter auto-grow rows="10"
                                                    @update:model-value="updateNodeProperty('queryRewriterSystemPrompt', $event)"
                                                    :readonly="isViewMode"
                                                    :placeholder="nodeInspector.queryRewriterMode === 'continueQuestion' 
                                                        ? 'Enter instructions for merging bot question with user response...\n\nExample:\nYou are a query rewriter. Given a bot\'s question and user\'s answer, create a complete standalone query that captures the full context.'
                                                        : 'Enter instructions for summarizing history...\n\nExample:\nYou are a conversation summarizer. Given the chat history and latest user message, create a concise contextual query.'"
                                                    class="prompt-textarea"></v-textarea>
                                            </v-col>
                                        </v-row>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            
                        </v-expansion-panels>
                    </div>

                    <div v-else-if="selectedNode.data.type === 'intentAI'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-brain" size="24" color="primary" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-primary mb-0">Intent AI Model Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Configure your Intent AI model settings and
                                parameters</p>
                        </div>

                        <!-- Action Button -->
                        <div class="d-flex justify-end mb-4">
                            <v-btn @click="openLoadFromModelDialog" color="success" variant="tonal" size="small"
                                prepend-icon="mdi-download" :disabled="isViewMode" class="load-model-btn">
                                Load from Model
                            </v-btn>
                        </div>

                        <!-- Intent AI Model Configuration Panels -->
                        <v-expansion-panels v-model="intentaiModelConfigPanels" multiple class="config-panels elevation-0"
                            variant="accordion">
                            <!-- Basic Information Panel -->
                            <v-expansion-panel value="intentaibasic" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Basic Information</div>
                                            <div class="panel-subtitle">Provider, model, and basic settings</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <!-- Provider and Model -->
                                        <div class="config-section mb-6">
                                            <h6 class="section-label mb-3">Service Configuration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.intentaiProvider"
                                                        :items="listDDProvider" item-title="providerName"
                                                        item-value="providerName" label="Provider*" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-cloud-outline"
                                                        @update:model-value="updateNodeProperty('intentaiProvider', $event); changeProvider($event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select AI service provider" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.intentaiModel" :items="DDModelShow"
                                                        item-title="modelDisplay" item-value="modelName" label="Model*"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-brain"
                                                        @update:model-value="updateNodeProperty('intentaiModel', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :disabled="!nodeInspector.intentaiProvider"
                                                        hint="Select specific model version" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <!-- Behavior Settings -->
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Behavior Settings</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.intentaiGender"
                                                        :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                        item-title="name" item-value="id" label="Gender"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-account"
                                                        @update:model-value="updateNodeProperty('intentaiGender', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Voice/persona gender preference" persistent-hint
                                                        :model-value="nodeInspector.intentaiGender || 'male'" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiTypingSpeed"
                                                        label="Typing Speed" type="number" min="0" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-keyboard-outline"
                                                        @update:model-value="updateNodeProperty('intentaiTypingSpeed', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Characters per second (0 = instant)" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiMaxInputLength"
                                                        label="Max Input Length" type="number" min="0" max="200000"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-outline"
                                                        @update:model-value="updateNodeProperty('intentaiMaxInputLength', $event)"
                                                        @keypress="maxNumberpositive($event)"
                                                        @focusin="validateAndCorrectValue($event, 1, 200000)"
                                                        @focusout="validateAndCorrectValue($event, 1, 200000)"
                                                        @keydown.enter="validateAndCorrectValue($event, 1, 200000)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Maximum characters allowed in user input"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiTimeout"
                                                        label="Request Timeout (seconds)" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('intentaiTimeout', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="0 = use global timeout setting" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiMaxRetry"
                                                        label="Max Retry" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-refresh"
                                                        @update:model-value="updateNodeProperty('intentaiMaxRetry', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="0 = use global max retry setting" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Response Options Panel -->
                            <v-expansion-panel value="intentairesponse" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Response Options</div>
                                            <div class="panel-subtitle">Configure how responses are generated</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <!-- Stream Response -->
                                        <div class="switch-option-compact mb-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.intentaiStreamResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.intentaiStreamResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.intentaiStreamResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-text-box-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">Stream Response</div>
                                                        <div class="switch-description">Progressive text generation
                                                        </div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.intentaiStreamResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('intentaiStreamResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>

                                        <!-- JSON Response -->
                                        <div class="switch-option-compact mb-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.intentaiJsonResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.intentaiJsonResponse ? 'amber-darken-2' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.intentaiJsonResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-code-json</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">JSON Response</div>
                                                        <div class="switch-description">Return structured JSON format
                                                        </div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.intentaiJsonResponse" color="amber-darken-2"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('intentaiJsonResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Memory Settings Panel -->
                            <v-expansion-panel value="intentaimemory" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                            <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Memory Settings</div>
                                            <div class="panel-subtitle">Configure conversation memory and context</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Conversation Memory</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiMemoryTurn"
                                                        label="Turn Count" type="number" min="0" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-history"
                                                        @update:model-value="updateNodeProperty('intentaiMemoryTurn', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Number of conversation turns to remember"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiMemoryDuration"
                                                        label="Duration (minutes)" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('intentaiMemoryDuration', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="How long to keep memory active"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Memory Types</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiShortTermMemory"
                                                        label="Short-Term Memory" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-memory"
                                                        @update:model-value="updateNodeProperty('intentaiShortTermMemory', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Recent context window size"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col>
                                                    <!-- Long-Term Memory Switch -->
                                                    <div class="switch-option-compact mb-4">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.intentaiLongTermMemory }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.intentaiLongTermMemory ? 'success' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.intentaiLongTermMemory ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-database</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">Long-Term Memory</div>
                                                                    <div class="switch-description">Persistent storage
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <v-switch v-model="nodeInspector.intentaiLongTermMemory"
                                                                color="success" hide-details density="compact"
                                                                @update:model-value="updateNodeProperty('intentaiLongTermMemory', $event)"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>

                                        </div>

                                        <!-- Multi-turn Input Configuration -->
                                        <!-- <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Multi-turn Input Configuration</h6>
                                            <v-row>
                                                <v-col>
                                                    <div class="switch-option-compact mb-3">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.intentaiIsChainMassage }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.intentaiIsChainMassage ? 'indigo' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.intentaiIsChainMassage ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-chat-processing</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">Enable Multi-Turn Input
                                                                    </div>
                                                                    <div class="switch-description">Allow continuous
                                                                        conversation</div>
                                                                </div>
                                                            </div>
                                                            <v-switch v-model="nodeInspector.intentaiIsChainMassage"
                                                                color="indigo" hide-details density="compact"
                                                                @update:model-value="updateNodeProperty('intentaiIsChainMassage', $event)"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiLenChainMassage"
                                                        label="Sequence" type="number" min="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-numeric"
                                                        @update:model-value="updateNodeProperty('intentaiLenChainMassage', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :disabled="!nodeInspector.intentaiIsChainMassage"
                                                        hint="Number of conversation turns in sequence" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiTimeSecondChainMassage"
                                                        label="Duration (seconds)" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer"
                                                        @update:model-value="updateNodeProperty('intentaiTimeSecondChainMassage', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" :disabled="!nodeInspector.intentaiIsChainMassage"
                                                        hint="Time duration for multi-turn session" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                        </div> -->


                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Parameters Panel -->
                            <v-expansion-panel value="intentaiparameters" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                            <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">AI Parameters</div>
                                            <div class="panel-subtitle">Fine-tune model behavior and output</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Core Parameters</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiTemperature"
                                                        label="Temperature*" type="number" step="0.1" min="0" max="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-thermometer"
                                                        @update:model-value="updateNodeProperty('intentaiTemperature', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Creativity level (0 = focused, 1 = creative)"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiTopP" label="Top P"
                                                        type="number" step="0.1" min="0" max="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-filter-variant"
                                                        @update:model-value="updateNodeProperty('intentaiTopP', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Token selection probability threshold" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Penalty Settings</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiFrequencyPenalty"
                                                        label="Frequency Penalty" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-repeat-off"
                                                        @update:model-value="updateNodeProperty('intentaiFrequencyPenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Reduce repetition of frequent tokens" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiPresencePenalty"
                                                        label="Presence Penalty" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto"
                                                        prepend-inner-icon="mdi-minus-circle-outline"
                                                        @update:model-value="updateNodeProperty('intentaiPresencePenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Reduce repetition of any tokens"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Output Control</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.intentaiMaxTokens"
                                                        label="Max Tokens Response" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-check-outline"
                                                        @update:model-value="updateNodeProperty('intentaiMaxTokens', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Maximum response length in tokens"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.intentaiStopSequence"
                                                        label="Stop Sequence" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-stop-circle-outline"
                                                        @update:model-value="updateNodeProperty('intentaiStopSequence', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Text sequence to stop generation" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Line Configuration Panel -->
                            <v-expansion-panel value="intentailine" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #4CAF50, #388E3C);">
                                            <v-icon icon="mdi-message" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Line Configuration</div>
                                            <div class="panel-subtitle">LINE messaging platform settings</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Display Options</h6>

                                            <!-- Show Name -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.intentaiShowLineName }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.intentaiShowLineName ? 'info' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.intentaiShowLineName ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-account-circle</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Show Name</div>
                                                            <div class="switch-description">Display user name</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.intentaiShowLineName" color="info"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('intentaiShowLineName', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>
                                            <!-- Loading Indicator -->
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.intentaiLoadingBar }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.intentaiLoadingBar ? 'success' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.intentaiLoadingBar ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-progress-clock</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Loading Indicator</div>
                                                            <div class="switch-description">Show typing animation</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.intentaiLoadingBar" color="success"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('intentaiLoadingBar', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>

                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Loading & Duration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.intentaiLoadingBarDuration"
                                                        :items="[10, 20, 30, 40, 50, 60]"
                                                        label="Loading Duration (seconds)" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-sand"
                                                        @update:model-value="updateNodeProperty('intentaiLoadingBarDuration', $event)"
                                                        class="config-input" :disabled="!nodeInspector.intentaiLoadingBar"
                                                        :readonly="isViewMode"
                                                        hint="Duration for loading animation display" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Monitoring</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.intentaiMonitorLineUserId"
                                                        label="Monitor Line User ID" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-monitor-eye"
                                                        @update:model-value="updateNodeProperty('intentaiMonitorLineUserId', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="LINE user ID for monitoring notifications"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.intentaiMonitorSocialID"
                                                        :items="listMonitorLineName" label="Monitor Line Name"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-chat" item-title="name" item-value="id"
                                                        @update:model-value="updateNodeProperty('intentaiMonitorSocialID', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select monitor line name" persistent-hint clearable />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Content Filtering</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-toolbar-title style="font-size: 16px">
                                                        Forbidden word
                                                    </v-toolbar-title>
                                                </v-col>
                                            </v-row>
                                            <div class="pr-1" style="
                                                    border: #dfe0e2 1px solid;
                                                    background: #fff;
                                                    margin-top: 10px;
                                                    border-radius: 8px;
                                                ">
                                                <v-row v-for="(item, index) in nodeInspector.intentaiForbiddenWords || ['']"
                                                    :key="index" style="margin: 0px">
                                                    <v-col cols="12" style="padding: 0px">
                                                        <div style="display: flex; align-items: center">
                                                            <div style="
                                                                    height: 50px;
                                                                    background: #f8f8f8;
                                                                    padding: 14px 20px 0 20px;
                                                                ">
                                                                {{ index + 1 }}
                                                            </div>
                                                            <v-textarea variant="solo" style="
                                                                    overflow: hidden;
                                                                    overflow-wrap: break-word;
                                                                    resize: none;
                                                                    height: 50px;
                                                                    width: 98%;
                                                                    border-radius: 0px;
                                                                    font-family: Fira code, Fira Mono, Consolas,
                                                                        Menlo, Courier, monospace;
                                                                    font-size: 14px;
                                                                "
                                                                :model-value="(nodeInspector.intentaiForbiddenWords && nodeInspector.intentaiForbiddenWords[index]) || ''"
                                                                placeholder="Enter word or phrase"
                                                                @keypress="isEnterIntentaiForbidden($event, index)"
                                                                @keyup.delete="checkDeleteIntentaiForbiddenWord(index, (nodeInspector.intentaiForbiddenWords && nodeInspector.intentaiForbiddenWords[index]) || '')"
                                                                @update:model-value="updateIntentaiForbiddenWordProperty(index, $event)"
                                                                :readonly="isViewMode" />
                                                            <v-btn icon="mdi-attachment" variant="text"
                                                                v-show="(nodeInspector.intentaiForbiddenWords || []).length > 1 && index + 1 < (nodeInspector.intentaiForbiddenWords || []).length"
                                                                @click="removeIntentaiForbiddenWordDirect(index)"
                                                                :disabled="isViewMode">
                                                                <v-tooltip location="top" :open-delay="500">
                                                                    <template v-slot:activator="{ props }">
                                                                        <v-icon color="error" style="cursor: pointer"
                                                                            v-bind="props">
                                                                            mdi-delete-forever
                                                                        </v-icon>
                                                                    </template>
                                                                    <span>delete</span>
                                                                </v-tooltip>
                                                            </v-btn>
                                                        </div>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- System Prompt Panel -->
                            <v-expansion-panel value="intentaiprompt" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                            <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">System Prompt</div>
                                            <div class="panel-subtitle">Define AI behavior and personality</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="prompt-section">
                                             <div class="config-section mt-6">
                                                <h6 class="section-label mb-3">Time Context</h6>
                                                <div class="switch-option-compact mb-3">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.intentaiEnableTime }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.intentaiEnableTime ? 'success' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.intentaiEnableTime ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-clock-outline</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">Time Context</div>
                                                                <div class="switch-description">Enable time awareness for AI responses</div>
                                                            </div>
                                                        </div>
                                                        <v-switch v-model="nodeInspector.intentaiEnableTime" color="success"
                                                            hide-details density="compact"
                                                            @update:model-value="updateNodeProperty('intentaiEnableTime', $event)"
                                                            :readonly="isViewMode" inset />
                                                    </div>

                                                    <!-- Time Zone Dropdown (shown when enableTime is true) -->
                                                    <div v-if="nodeInspector.intentaiEnableTime" class="mt-3">
                                                        <v-select v-model="nodeInspector.intentaiTimeZone" :items="DDTimeZone"
                                                            item-title="name" item-value="value" label="Time Zone"
                                                            variant="outlined" density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-earth"
                                                            @update:model-value="updateNodeProperty('intentaiTimeZone', $event)"
                                                            class="config-input" :readonly="isViewMode"
                                                            hint="Select your timezone" persistent-hint
                                                            placeholder="Select your timezone">
                                                            <template v-slot:selection="{ item }">
                                                                <div class="d-flex align-center">
                                                                    <v-icon small color="primary"
                                                                        class="me-2">mdi-map-marker</v-icon>
                                                                    <span class="font-weight-medium">{{ item.raw.name }}</span>
                                                                    <v-chip size="x-small" color="grey-darken-1"
                                                                        text-color="white" class="ml-2">
                                                                        {{ item.raw.value.split('/')[1].replace('_', ' ') }}
                                                                    </v-chip>
                                                                </div>
                                                            </template>
                                                        </v-select>
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- TransactionID Context Section -->
                                            <div class="config-section mt-6">
                                                <h6 class="section-label mb-3">TransactionID Context</h6>
                                                
                                                <!-- TransactionID Context Switch -->
                                                <div class="switch-option-compact mb-3">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.intentaiEnableTransactionId }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.intentaiEnableTransactionId ? 'info' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.intentaiEnableTransactionId ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-identifier</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">Add TransactionID to Prompt</div>
                                                                <div class="switch-description">Include transaction identifier in AI context</div>
                                                            </div>
                                                        </div>
                                                        <v-switch v-model="nodeInspector.intentaiEnableTransactionId" color="info"
                                                            hide-details density="compact"
                                                            @update:model-value="updateNodeProperty('intentaiEnableTransactionId', $event)"
                                                            :readonly="isViewMode" inset />
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Prompt Template Header -->
                                            <div class="mb-2" style="display:flex;flex-direction: column;align-items: flex-end;">
                                                <div style="display: flex; align-items: center;">
                                                    <v-tooltip location="top">
                                                        <template v-slot:activator="{ props }">
                                                            <v-icon icon="mdi-information-outline" size="small"
                                                                color="grey" class="me-3" v-bind="props"></v-icon>
                                                        </template>
                                                        <span>Configure additional prompts and templates</span>
                                                    </v-tooltip>
                                                    <v-btn
                                                        v-if="DDGroupPromptTemplate && DDGroupPromptTemplate.length > 0"
                                                        variant="outlined" color="primary"
                                                        prepend-icon="mdi-playlist-plus" @click="showIntentaiPromptTemplate"
                                                        class="text-none font-weight-medium" size="small">
                                                        Show Templates
                                                    </v-btn>
                                                </div>
                                            </div>

                                            <!-- Main editor and templates layout -->
                                            <v-row>
                                                <!-- Editor area (full width) -->
                                                <v-col cols="12" class="prompt-editor-col">
                                                    <v-card variant="outlined" class="prompt-editor-card">
                                                        <v-card-text class="px-2 py-1">
                                                            <v-textarea v-model="nodeInspector.intentaiSystemPrompt" label=""
                                                                variant="plain" counter auto-grow rows="25"
                                                                @update:model-value="updateNodeProperty('intentaiSystemPrompt', $event)"
                                                                :readonly="isViewMode"
                                                                placeholder="Enter your system message here..."
                                                                class="prompt-textarea"
                                                                @contextmenu="onIntentaiContextMenu"></v-textarea>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-col>
                                            </v-row>

                                            <!-- Context menu for prompt templates -->
                                            <v-menu v-model="showIntentaiMenu" :activator="{ x, y }" location="bottom">
                                                <v-card min-width="250" elevation="3">
                                                    <v-list density="compact" class="py-0">
                                                        <v-list-subheader class="d-flex bg-grey-lighten-4 py-2">
                                                            <v-icon icon="mdi-text-box-plus-outline" class="me-2"
                                                                size="small"></v-icon>
                                                            Insert Template
                                                        </v-list-subheader>
                                                        <v-divider :thickness="1"
                                                            class="border-opacity-100"></v-divider>
                                                        <v-list-item v-for="(item, index) in filteredIntentaiPromptTemplates"
                                                            :key="index"
                                                            @click="setSelectIntentaiPromptTemplate(item); showIntentaiMenu = false;"
                                                            :value="item.name" class="template-list-item">
                                                            <template v-slot:prepend>
                                                                <v-icon size="small"
                                                                    icon="mdi-text-box-outline"></v-icon>
                                                            </template>
                                                            <template v-slot:title>
                                                                <span>{{ item.name }}</span>
                                                            </template>
                                                        </v-list-item>
                                                    </v-list>
                                                </v-card>
                                            </v-menu>

                                            <!-- Dialog for showing all templates -->
                                            <v-dialog v-model="dialogShowAllIntentai" max-width="550" scrollable>
                                                <v-card>
                                                    <v-card-title class="bg-primary text-white d-flex align-center">
                                                        <v-icon icon="mdi-text-box-multiple-outline"
                                                            class="me-2"></v-icon>
                                                        Prompt Templates
                                                    </v-card-title>
                                                    <v-divider :thickness="1" class="border-opacity-100"></v-divider>
                                                    <v-card-text style="max-height: 500px" class="pt-4">
                                                        <v-text-field v-model="intentaiTemplateSearchQuery"
                                                            label="Search templates" prepend-inner-icon="mdi-magnify"
                                                            variant="outlined" density="compact" hide-details
                                                            class="mb-3" clearable></v-text-field>

                                                        <v-list density="compact" class="template-list-all pa-0">
                                                            <v-list-item v-for="(item, i) in searchedIntentaiTemplates" :key="i"
                                                                @click="setSelectIntentaiPromptTemplate(item)"
                                                                class="template-list-item" :ripple="true">
                                                                <template v-slot:prepend>
                                                                    <v-icon size="small" color="primary"
                                                                        icon="mdi-text-box-outline"></v-icon>
                                                                </template>
                                                                <template v-slot:title>
                                                                    <span>{{ item.name }}</span>
                                                                </template>
                                                                <template v-slot:subtitle>
                                                                    <span class="text-caption text-truncate">
                                                                        {{ truncatePrompt(item.prompt) }}
                                                                    </span>
                                                                </template>
                                                            </v-list-item>
                                                        </v-list>
                                                    </v-card-text>
                                                    <v-card-actions>
                                                        <v-spacer></v-spacer>
                                                        <v-btn color="blue darken-1" variant="text"
                                                            @click="dialogShowAllIntentai = false">
                                                            Close
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-card>
                                            </v-dialog>

                                            <!-- Main Prompt Template Dialog -->
                                            <v-dialog v-model="intentaiPromptTemplateDialog" max-width="800" scrollable>
                                                <v-card>
                                                    <v-card-title class="bg-primary text-white d-flex align-center">
                                                        <v-icon icon="mdi-text-box-multiple-outline" class="me-2"></v-icon>
                                                        Prompt Templates
                                                        <v-spacer></v-spacer>
                                                        <v-btn icon="mdi-close" variant="text" color="white" size="small" 
                                                            @click="intentaiPromptTemplateDialog = false"></v-btn>
                                                    </v-card-title>
                                                    
                                                    <v-card-text class="pa-4">
                                                        <!-- Template Group Selection -->
                                                        <v-autocomplete 
                                                            label="Select Template Group" 
                                                            :items="DDGroupPromptTemplate" 
                                                            item-title="name"
                                                            item-value="id" 
                                                            density="compact" 
                                                            clearable
                                                            variant="outlined" 
                                                            v-model="intentaiGroupPromptTemplate"
                                                            prepend-inner-icon="mdi-folder-outline" 
                                                            hide-details
                                                            class="mb-4">
                                                        </v-autocomplete>

                                                        <!-- Search Template -->
                                                        <v-text-field 
                                                            v-model="intentaiTemplateSearchQuery"
                                                            label="Search templates" 
                                                            prepend-inner-icon="mdi-magnify"
                                                            variant="outlined" 
                                                            density="compact" 
                                                            hide-details
                                                            class="mb-4" 
                                                            clearable>
                                                        </v-text-field>

                                                        <!-- Templates List -->
                                                        <div style="max-height: 400px; overflow-y: auto;">
                                                            <div v-if="searchedIntentaiTemplates.length === 0" 
                                                                class="pa-4 text-center text-grey-darken-1">
                                                                <v-icon icon="mdi-playlist-remove" size="large" 
                                                                    class="d-block mx-auto mb-2"></v-icon>
                                                                <span>No templates found</span>
                                                            </div>

                                                            <v-list v-else density="compact" class="pa-0">
                                                                <v-list-item 
                                                                    v-for="(item, i) in searchedIntentaiTemplates" 
                                                                    :key="i"
                                                                    @click="setSelectIntentaiPromptTemplate(item)"
                                                                    class="template-list-item ma-1" 
                                                                    :ripple="true"
                                                                    rounded>
                                                                    <template v-slot:prepend>
                                                                        <v-icon size="small" color="primary" 
                                                                            icon="mdi-text-box-outline"></v-icon>
                                                                    </template>
                                                                    <template v-slot:title>
                                                                        <span class="font-weight-medium">{{ item.name }}</span>
                                                                    </template>
                                                                    <template v-slot:subtitle>
                                                                        <span class="text-caption text-grey">
                                                                            {{ truncatePrompt(item.prompt) }}
                                                                        </span>
                                                                    </template>
                                                                    <template v-slot:append>
                                                                        <v-btn icon="mdi-plus" size="small" variant="text" 
                                                                            color="primary" @click.stop="setSelectIntentaiPromptTemplate(item)">
                                                                        </v-btn>
                                                                    </template>
                                                                </v-list-item>
                                                            </v-list>
                                                        </div>
                                                    </v-card-text>
                                                    
                                                    <v-card-actions>
                                                        <v-spacer></v-spacer>
                                                        <v-btn color="primary" variant="text" @click="intentaiPromptTemplateDialog = false">
                                                            Close
                                                        </v-btn>
                                                    </v-card-actions>
                                                </v-card>
                                            </v-dialog>

                                            <div class="prompt-tips mt-4">
                                                <v-card class="tips-card" color="blue-grey-lighten-5" flat>
                                                    <v-card-text class="pa-4">
                                                        <div class="d-flex align-start">
                                                            <v-icon color="info" size="20"
                                                                class="me-2 mt-1">mdi-lightbulb-outline</v-icon>
                                                            <div>
                                                                <div class="text-subtitle-2 font-weight-medium mb-2">
                                                                    Tips for
                                                                    better prompts:</div>
                                                                <ul class="text-body-2 text-medium-emphasis">
                                                                    <li>Be specific about the AI's role and expertise
                                                                    </li>
                                                                    <li>Define the tone and communication style</li>
                                                                    <li>Include any restrictions or guidelines</li>
                                                                    <li>Specify how to handle unknown information</li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </v-card-text>
                                                </v-card>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>

                    </div>
                    <div v-else-if="selectedNode.data.type === 'intent'" class="form-section">
                        <h6 class="section-title">Intent Configuration</h6>
                        <v-text-field v-model="nodeInspector.intentGroupName" label="Intent handler Name"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('intentGroupName', $event)"
                            class="modern-input mb-4" :readonly="isViewMode" />

                        <!-- Intents Configuration -->
                        <div class="intents-section">
                            <div class="section-header mb-3">
                                <h6 class="section-title mb-0">Intents</h6>
                                <v-btn v-if="!isViewMode" size="small" color="primary" variant="tonal"
                                    @click="addInspectorIntent" class="add-btn">
                                    <v-icon left size="16">mdi-plus</v-icon>
                                    Add Intent
                                </v-btn>
                            </div>

                            <div v-for="(intent, intentIndex) in nodeInspector.intents" :key="intent.id"
                                class="intent-card mb-3">
                                <v-expansion-panels variant="accordion" class="intent-expansion">
                                    <v-expansion-panel>
                                        <v-expansion-panel-title>
                                            <div class="d-flex align-center w-100">
                                                <v-icon class="mr-2" color="primary" size="small">mdi-target</v-icon>
                                                <!-- Priority Number -->
                                                <span v-if="intent.priority || intent.priority === 0"
                                                    class="priority-number mr-2">
                                                    {{ intent.priority }}
                                                </span>
                                                <span class="font-weight-medium">
                                                    {{
                                                        intent.name || `Intent ${intentIndex
                                                        + 1}`
                                                    }}
                                                </span>
                                                <v-spacer></v-spacer>
                                                <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                    variant="text" @click.stop="removeInspectorIntent(intentIndex)"
                                                    :disabled="nodeInspector.intents.length <= 1" class="ml-2">
                                                    <v-icon size="16">mdi-delete</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <div class="intent-content pa-2">
                                                <!-- Intent Priority -->
                                                <v-text-field
                                                    v-model.number="nodeInspector.intents[intentIndex].priority"
                                                    label="Priority" type="number" variant="outlined" density="compact"
                                                    hide-details="auto" min="0" step="1" placeholder="Enter priority"
                                                    @update:model-value="updateIntentData()"
                                                    @keypress="isNumberpositive($event)" class="modern-input mb-3"
                                                    :readonly="isViewMode">
                                                    <template v-slot:prepend-inner>
                                                        <v-icon size="small" color="orange">mdi-priority-high</v-icon>
                                                    </template>
                                                    <template v-slot:append-inner>
                                                        <v-tooltip location="top" max-width="200px">
                                                            <template v-slot:activator="{ props }">
                                                                <v-icon size="small" color="grey-darken-1"
                                                                    v-bind="props">
                                                                    mdi-information-outline
                                                                </v-icon>
                                                            </template>
                                                            <span>Priority 0 is the highest importance</span>
                                                        </v-tooltip>
                                                    </template>
                                                </v-text-field>

                                                <!-- Intent Name -->
                                                <v-text-field v-model="nodeInspector.intents[intentIndex].name"
                                                    label="Intent Name" variant="outlined" density="compact"
                                                    hide-details="auto" @update:model-value="updateIntentData()"
                                                    class="modern-input mb-3" :readonly="isViewMode" />

                                                <div
                                                    style="display: flex; align-items: center; justify-content: flex-start;margin-top: -15px;">
                                                    <v-switch v-model="nodeInspector.intents[intentIndex].useMemory"
                                                        hide-details density="compact" color="primary" inset
                                                        style="transform: scale(0.9); margin-left: 0px; margin-top: 10px;"
                                                        @update:model-value="updateIntentData()" class="mb-3"
                                                        :readonly="isViewMode" />
                                                    <span style="font-size: 16px;margin-left: 8px;">
                                                        Use memory
                                                    </span>
                                                </div>

                                                <div class="custom-divider"></div>

                                                <!-- Base Prompt Section -->
                                                <div style="margin-bottom: 36px;">
                                                    <!-- Base Prompt Toggle -->
                                                    <div
                                                        style="display: flex;align-items: center;justify-content: flex-end;">
                                                        <span
                                                            style="font-weight: 500;font-size: 12px;margin-right: 8px;">
                                                            Edit Base Prompt
                                                        </span>
                                                        <v-switch
                                                            v-model="nodeInspector.intents[intentIndex].enableBasePrompt"
                                                            color="primary" density="compact" hide-details
                                                            :readonly="isViewMode"
                                                            @update:model-value="updateIntentData()" inset
                                                            style="transform: scale(0.8);">
                                                        </v-switch>
                                                    </div>

                                                    <!-- Base Prompt Textarea -->
                                                    <v-textarea v-model="nodeInspector.intents[intentIndex].basePrompt"
                                                        label="Base Prompt" variant="outlined" density="compact"
                                                        rows="4" auto-grow hide-details="auto"
                                                        @update:model-value="updateIntentData()" class="modern-input"
                                                        :readonly="isViewMode || !nodeInspector.intents[intentIndex].enableBasePrompt"
                                                        :disabled="!nodeInspector.intents[intentIndex].enableBasePrompt"
                                                        placeholder="Find the Intent from the message of users.&#10;#Condition for Response&#10;1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0" />
                                                </div>

                                                <!-- Intent Prompt -->
                                                <v-textarea v-model="nodeInspector.intents[intentIndex].prompt"
                                                    label="Intent Prompt" variant="outlined" density="compact" rows="2"
                                                    auto-grow hide-details="auto"
                                                    @update:model-value="updateIntentData()" class="modern-input mb-3"
                                                    :readonly="isViewMode" />

                                                <!-- Example Messages -->
                                                <div class="mb-3">
                                                    <div class="d-flex align-center mb-2">
                                                        <span class="text-subtitle-2 font-weight-medium">
                                                            Example Messages
                                                        </span>
                                                        <v-btn v-if="!isViewMode" size="x-small" color="primary"
                                                            variant="tonal"
                                                            @click="addInspectorIntentMessage(intentIndex)"
                                                            class="ml-auto">
                                                            <v-icon size="12">mdi-plus</v-icon>
                                                        </v-btn>
                                                    </div>
                                                    <div v-for="(message, messageIndex) in intent.messages"
                                                        :key="message.id" class="d-flex align-center mb-2">
                                                        <v-text-field
                                                            v-model="nodeInspector.intents[intentIndex].messages[messageIndex].text"
                                                            placeholder="Example user message..." variant="outlined"
                                                            density="compact" hide-details="auto"
                                                            @update:model-value="updateIntentData()"
                                                            class="modern-input flex-grow-1 mr-2"
                                                            :readonly="isViewMode" />
                                                        <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                            variant="text"
                                                            @click="removeInspectorIntentMessage(intentIndex, messageIndex)"
                                                            :disabled="intent.messages.length <= 1">
                                                            <v-icon size="14">mdi-close</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </div>

                                                <!-- Actions Section -->
                                                <div class="actions-section">
                                                    <!-- Main Action -->
                                                    <div class="mb-3">
                                                        <span class="text-subtitle-2 font-weight-medium">
                                                            Main
                                                            Action
                                                        </span>
                                                        <v-text-field
                                                            v-model="nodeInspector.intents[intentIndex].mainAction.label"
                                                            placeholder="Main action label..." variant="outlined"
                                                            density="compact" hide-details="auto"
                                                            @update:model-value="updateIntentData()"
                                                            class="modern-input mt-2" :readonly="isViewMode" />
                                                    </div>

                                                    <!-- Parallel Actions -->
                                                    <div>
                                                        <div class="d-flex align-center mb-2">
                                                            <span class="text-subtitle-2 font-weight-medium">
                                                                Parallel
                                                                Actions
                                                            </span>
                                                            <v-btn v-if="!isViewMode" size="x-small" color="primary"
                                                                variant="tonal"
                                                                @click="addInspectorParallelAction(intentIndex)"
                                                                class="ml-auto">
                                                                <v-icon size="12">mdi-plus</v-icon>
                                                            </v-btn>
                                                        </div>
                                                        <div v-for="(parallel, parallelIndex) in intent.parallelActions"
                                                            :key="parallel.id" class="d-flex align-center mb-2">
                                                            <v-text-field
                                                                v-model="nodeInspector.intents[intentIndex].parallelActions[parallelIndex].label"
                                                                placeholder="Parallel action label..."
                                                                variant="outlined" density="compact" hide-details="auto"
                                                                @update:model-value="updateIntentData()"
                                                                class="modern-input flex-grow-1 mr-2"
                                                                :readonly="isViewMode" />
                                                            <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                                variant="text"
                                                                @click="removeInspectorParallelAction(intentIndex, parallelIndex)">
                                                                <v-icon size="14">mdi-close</v-icon>
                                                            </v-btn>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>
                    </div>

                    <!-- Router Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'router'" class="form-section">
                        <h6 class="section-title">Router Configuration</h6>
                        
                        <!-- Router Name -->
                        <v-text-field v-model="nodeInspector.routerName" label="Router Name"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('routerName', $event)"
                            class="modern-input mb-4" :readonly="isViewMode" />

                        <!-- Router Prompt -->
                        <v-textarea v-model="nodeInspector.routerPrompt" label="Routing Prompt"
                            variant="outlined" density="compact" rows="6" auto-grow hide-details="auto"
                            @update:model-value="updateNodeProperty('routerPrompt', $event)"
                            class="modern-input mb-4" :readonly="isViewMode"
                            placeholder="Enter additional instructions for intent routing. The AI will return confidence scores for ALL intents as JSON object: { &quot;intents&quot;: [{ &quot;capability_intent&quot;: &quot;intent1&quot;, &quot;confidence&quot;: 0.95 }, ...] }" />

                        <!-- Confidence Threshold -->
                        <div class="slider-container mb-4">
                            <div class="d-flex justify-space-between align-center mb-2">
                                <span class="slider-label">Confidence Threshold</span>
                                <v-chip size="small" color="green" variant="tonal">
                                    {{ (nodeInspector.confidenceThreshold * 100).toFixed(0) }}%
                                </v-chip>
                            </div>
                            <v-slider v-model="nodeInspector.confidenceThreshold" :min="0" :max="1" :step="0.05"
                                color="green" track-color="grey-lighten-3" thumb-label
                                @update:model-value="updateNodeProperty('confidenceThreshold', $event)"
                                class="modern-slider" :readonly="isViewMode">
                                <template v-slot:thumb-label="{ modelValue }">
                                    {{ (modelValue * 100).toFixed(0) }}%
                                </template>
                            </v-slider>
                            <div class="text-caption text-grey-darken-1">
                                If confidence is below this threshold, route to fallback
                            </div>
                        </div>

                        <!-- Router Outputs Configuration -->
                        <div class="router-outputs-section">
                            <div class="section-header mb-3">
                                <h6 class="section-title mb-0">Router Outputs</h6>
                                <v-btn v-if="!isViewMode" size="small" color="success" variant="tonal"
                                    @click="addRouterOutput" class="add-btn">
                                    <v-icon left size="16">mdi-plus</v-icon>
                                    Add Output
                                </v-btn>
                            </div>

                            <div v-for="(output, outputIndex) in nodeInspector.routerOutputs" :key="output.id"
                                class="router-output-card mb-3">
                                <v-expansion-panels variant="accordion" class="router-output-expansion">
                                    <v-expansion-panel>
                                        <v-expansion-panel-title>
                                            <div class="d-flex align-center w-100">
                                                <v-icon class="mr-2" color="success" size="small">mdi-ray-start-arrow</v-icon>
                                                <span class="font-weight-medium">
                                                    {{ output.intentName || `Output ${outputIndex + 1}` }}
                                                </span>
                                                <v-spacer></v-spacer>
                                                <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                    variant="text" @click.stop="removeRouterOutput(outputIndex)"
                                                    :disabled="nodeInspector.routerOutputs.length <= 1" class="ml-2">
                                                    <v-icon size="16">mdi-delete</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <div class="router-output-content pa-2">
                                                <!-- Intent Name (capability_intent value to match) -->
                                                <v-text-field
                                                    v-model="nodeInspector.routerOutputs[outputIndex].intentName"
                                                    label="Intent Name (capability_intent)"
                                                    variant="outlined" density="compact" hide-details="auto"
                                                    @update:model-value="updateRouterData()"
                                                    class="modern-input mb-3" :readonly="isViewMode"
                                                    placeholder="e.g., product, support, billing" />
                                                
                                                <!-- Output Label (handle display) -->
                                                <v-text-field
                                                    v-model="nodeInspector.routerOutputs[outputIndex].label"
                                                    label="Output Label"
                                                    variant="outlined" density="compact" hide-details="auto"
                                                    @update:model-value="updateRouterData()"
                                                    class="modern-input" :readonly="isViewMode"
                                                    placeholder="Display label for the output handle" />
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>

                        <!-- Info Alert -->
                        <v-alert type="info" variant="tonal" density="compact" class="mt-4">
                            <template v-slot:prepend>
                                <v-icon size="small">mdi-information</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>AI Response Format:</strong><br>
                                Returns confidence scores for <strong>ALL</strong> intents:<br>
                                <code>{ "intents": [{ "capability_intent": "intent1", "confidence": 0.95 }, ...] }</code><br>
                                Selects highest confidence above threshold. Logs all intent scores.
                            </div>
                        </v-alert>
                    </div>

                    <!-- Advanced Router Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'advancedRouter'" class="form-section">
                        <h6 class="section-title">Advanced Router Configuration</h6>
                        
                        <!-- Router Name -->
                        <v-text-field v-model="nodeInspector.advancedRouterName" label="Advanced Router Name"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('advancedRouterName', $event)"
                            class="modern-input mb-4" :readonly="isViewMode" />

                        <!-- Router Prompt -->
                        <v-textarea v-model="nodeInspector.advancedRouterPrompt" label="Routing Prompt"
                            variant="outlined" density="compact" rows="4" auto-grow hide-details="auto"
                            @update:model-value="updateNodeProperty('advancedRouterPrompt', $event)"
                            class="modern-input mb-4" :readonly="isViewMode"
                            placeholder="Additional instructions for hierarchical intent routing..." />

                        <!-- Confidence Threshold -->
                        <div class="slider-container mb-4">
                            <div class="d-flex justify-space-between align-center mb-2">
                                <span class="slider-label">Confidence Threshold</span>
                                <v-chip size="small" color="deep-purple" variant="tonal">
                                    {{ (nodeInspector.advancedRouterConfidenceThreshold * 100).toFixed(0) }}%
                                </v-chip>
                            </div>
                            <v-slider v-model="nodeInspector.advancedRouterConfidenceThreshold" :min="0" :max="1" :step="0.05"
                                color="deep-purple" track-color="grey-lighten-3" thumb-label
                                @update:model-value="updateNodeProperty('advancedRouterConfidenceThreshold', $event)"
                                class="modern-slider" :readonly="isViewMode">
                                <template v-slot:thumb-label="{ modelValue }">
                                    {{ (modelValue * 100).toFixed(0) }}%
                                </template>
                            </v-slider>
                        </div>

                        <!-- Max Depth -->
                        <div class="slider-container mb-4">
                            <div class="d-flex justify-space-between align-center mb-2">
                                <span class="slider-label">Maximum Intent Depth</span>
                                <v-chip size="small" color="deep-purple" variant="tonal">
                                    {{ nodeInspector.advancedRouterMaxDepth }} levels
                                </v-chip>
                            </div>
                            <v-slider v-model="nodeInspector.advancedRouterMaxDepth" :min="1" :max="4" :step="1"
                                color="deep-purple" track-color="grey-lighten-3" thumb-label
                                @update:model-value="updateNodeProperty('advancedRouterMaxDepth', $event)"
                                class="modern-slider" :readonly="isViewMode" />
                            <div class="text-caption text-grey-darken-1">
                                How many levels deep the intent hierarchy can go (1-4)
                            </div>
                        </div>

                        <!-- Custom Response Schema (Google only) -->
                        <h6 class="section-title mt-6">Response Schema (Google Provider)</h6>
                        <div class="config-section mb-4">
                            <div class="switch-option-compact mb-4">
                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                    :class="{ 'switch-active-bg': nodeInspector.advancedRouterCustomSchemaEnabled }">
                                    <div class="d-flex align-center">
                                        <v-avatar size="28"
                                            :color="nodeInspector.advancedRouterCustomSchemaEnabled ? 'deep-purple' : 'grey-lighten-1'"
                                            class="me-3">
                                            <v-icon
                                                :color="nodeInspector.advancedRouterCustomSchemaEnabled ? 'white' : 'grey-darken-1'"
                                                size="16">mdi-code-json</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="switch-title">Enable Custom Response Schema</div>
                                            <div class="switch-description">Use node-defined schema for Google structured output</div>
                                        </div>
                                    </div>
                                    <v-switch v-model="nodeInspector.advancedRouterCustomSchemaEnabled"
                                        color="deep-purple" hide-details density="compact"
                                        @update:model-value="updateAdvancedRouterData()"
                                        :readonly="isViewMode" inset />
                                </div>
                            </div>

                            <v-alert v-if="nodeInspector.advancedRouterCustomSchemaEnabled" type="warning" variant="tonal" density="compact" class="mb-3">
                                <div class="text-caption">
                                    Custom schema is applied only when IntentAI provider is <strong>Google</strong>.
                                </div>
                            </v-alert>

                            <template v-if="nodeInspector.advancedRouterCustomSchemaEnabled">
                                <v-row>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="nodeInspector.advancedRouterIntentField"
                                            label="Intent Field"
                                            variant="outlined" density="compact" hide-details="auto"
                                            class="modern-input"
                                            :readonly="isViewMode"
                                            @update:model-value="updateAdvancedRouterData()" />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="nodeInspector.advancedRouterConfidenceField"
                                            label="Confidence Field"
                                            variant="outlined" density="compact" hide-details="auto"
                                            class="modern-input"
                                            :readonly="isViewMode"
                                            @update:model-value="updateAdvancedRouterData()" />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="nodeInspector.advancedRouterSubIntentsField"
                                            label="Sub-Intents Field"
                                            variant="outlined" density="compact" hide-details="auto"
                                            class="modern-input"
                                            :readonly="isViewMode"
                                            @update:model-value="updateAdvancedRouterData()" />
                                    </v-col>
                                </v-row>

                                <v-textarea
                                    v-model="nodeInspector.advancedRouterSchemaTemplateText"
                                    label="Custom Schema JSON"
                                    variant="outlined" density="compact"
                                    rows="10" auto-grow hide-details="auto"
                                    class="modern-input"
                                    :readonly="isViewMode"
                                    @update:model-value="updateAdvancedRouterData()"
                                    placeholder='{"type":"OBJECT","properties":{"intents":{"type":"ARRAY"}}}' />

                                <v-alert v-if="nodeInspector.advancedRouterSchemaError" type="error" variant="tonal" density="compact" class="mt-2">
                                    <div class="text-caption">{{ nodeInspector.advancedRouterSchemaError }}</div>
                                </v-alert>

                                <div class="d-flex justify-end mt-2">
                                    <v-btn v-if="!isViewMode" size="small" variant="text" color="deep-purple"
                                        @click="resetAdvancedRouterSchemaTemplate">
                                        <v-icon left size="14">mdi-restore</v-icon>
                                        Reset Default Schema
                                    </v-btn>
                                </div>
                            </template>
                        </div>

                        <!-- Hierarchical Router Outputs Configuration -->
                        <div class="adv-router-outputs-section">
                            <div class="section-header mb-3">
                                <h6 class="section-title mb-0">Intent Hierarchy (Level 1)</h6>
                                <v-btn v-if="!isViewMode" size="small" color="deep-purple" variant="tonal"
                                    @click="addAdvancedRouterOutput()" class="add-btn">
                                    <v-icon left size="16">mdi-plus</v-icon>
                                    Add Intent
                                </v-btn>
                            </div>

                            <!-- Recursive Intent Tree -->
                            <div v-for="(output, outputIndex) in nodeInspector.advancedRouterOutputs" :key="output.id"
                                class="adv-router-output-card mb-3">
                                <v-expansion-panels variant="accordion" class="adv-router-output-expansion">
                                    <v-expansion-panel>
                                        <v-expansion-panel-title>
                                            <div class="d-flex align-center w-100">
                                                <v-icon class="mr-2" color="deep-purple" size="small">mdi-folder-outline</v-icon>
                                                <span class="font-weight-medium">
                                                    {{ output.intentName || `Intent ${outputIndex + 1}` }}
                                                </span>
                                                <v-chip v-if="output.subIntents && output.subIntents.length > 0" 
                                                    size="x-small" color="deep-purple" variant="outlined" class="ml-2">
                                                    {{ output.subIntents.length }} sub-intents
                                                </v-chip>
                                                <v-chip v-else size="x-small" color="green" variant="outlined" class="ml-2">
                                                    Leaf (Output)
                                                </v-chip>
                                                <v-spacer></v-spacer>
                                                <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                    variant="text" @click.stop="removeAdvancedRouterOutput(outputIndex)"
                                                    :disabled="nodeInspector.advancedRouterOutputs.length <= 1" class="ml-2">
                                                    <v-icon size="16">mdi-delete</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <div class="adv-router-output-content pa-2">
                                                <!-- Intent Name -->
                                                <v-text-field
                                                    v-model="nodeInspector.advancedRouterOutputs[outputIndex].intentName"
                                                    label="Intent Name"
                                                    variant="outlined" density="compact" hide-details="auto"
                                                    @update:model-value="updateAdvancedRouterData()"
                                                    class="modern-input mb-3" :readonly="isViewMode"
                                                    placeholder="e.g., Product, Support, Billing" />
                                                <!-- Access Level (ขั้นต่ำ) — ค่าจริงของ intent = สูงสุดตามเส้นทาง folder ราก → intent -->
                                                <v-select
                                                    :items="accessLevelOptionsIncluding(output.accessLevel)" item-title="title" item-value="value"
                                                    label="Access Level (ขั้นต่ำ)"
                                                    variant="outlined" density="compact" hide-details="auto"
                                                    class="modern-input mb-1" :readonly="isViewMode"
                                                    :model-value="output.accessLevel || 'T0'"
                                                    @update:model-value="(v) => { output.accessLevel = v; updateAdvancedRouterData(); }" />
                                                <div class="text-caption text-grey mb-3">folder ที่ตั้งระดับไว้ คุม intent ลูกทั้งหมด (ลูกตั้งต่ำกว่าไม่มีผล)</div>
                                                
                                                <!-- Sub-Intents (Level 2) -->
                                                <div v-if="nodeInspector.advancedRouterMaxDepth >= 2" class="sub-intents-section mt-4">
                                                    <div class="d-flex align-center justify-space-between mb-2">
                                                        <span class="text-subtitle-2 text-grey-darken-1">
                                                            <v-icon size="16" class="mr-1">mdi-subdirectory-arrow-right</v-icon>
                                                            Sub-Intents (Level 2)
                                                        </span>
                                                        <v-btn v-if="!isViewMode" size="x-small" color="deep-purple" variant="text"
                                                            @click="addAdvancedRouterSubIntent(outputIndex)">
                                                            <v-icon left size="14">mdi-plus</v-icon>
                                                            Add Sub-Intent
                                                        </v-btn>
                                                    </div>
                                                    
                                                    <!-- Level 2 Sub-Intents -->
                                                    <div v-for="(subIntent, subIndex) in output.subIntents" :key="subIntent.id"
                                                        class="sub-intent-item ml-4 mb-2 pa-2 rounded border">
                                                        <div class="d-flex align-center mb-2">
                                                            <v-icon size="16" color="deep-purple-lighten-2" class="mr-2">mdi-circle-small</v-icon>
                                                            <v-text-field
                                                                v-model="subIntent.intentName"
                                                                label="Sub-Intent Name"
                                                                variant="outlined" density="compact" hide-details
                                                                @update:model-value="updateAdvancedRouterData()"
                                                                class="modern-input flex-grow-1" :readonly="isViewMode" />
                                                            <v-select
                                                                :items="accessLevelOptionsIncluding(subIntent.accessLevel)" item-title="title" item-value="value"
                                                                variant="outlined" density="compact" hide-details
                                                                class="ml-2 flex-shrink-0" style="max-width: 96px"
                                                                title="Access Level (ขั้นต่ำ)" :readonly="isViewMode"
                                                                :model-value="subIntent.accessLevel || 'T0'"
                                                                @update:model-value="(v) => { subIntent.accessLevel = v; updateAdvancedRouterData(); }">
                                                                <template v-slot:selection="{ item }">{{ item.raw.value }}</template>
                                                            </v-select>
                                                            <v-btn v-if="!isViewMode" icon size="x-small" color="error" variant="text"
                                                                @click="removeAdvancedRouterSubIntent(outputIndex, subIndex)" class="ml-2">
                                                                <v-icon size="14">mdi-close</v-icon>
                                                            </v-btn>
                                                        </div>
                                                        
                                                        <!-- Level 3 Sub-Intents -->
                                                        <div v-if="nodeInspector.advancedRouterMaxDepth >= 3" class="ml-6 mt-2">
                                                            <div class="d-flex align-center justify-space-between mb-1">
                                                                <span class="text-caption text-grey">Level 3 Intents</span>
                                                                <v-btn v-if="!isViewMode" size="x-small" variant="text" color="deep-purple"
                                                                    @click="addAdvancedRouterSubIntent(outputIndex, subIndex)">
                                                                    <v-icon size="12">mdi-plus</v-icon>
                                                                </v-btn>
                                                            </div>
                                                            <div v-for="(subSubIntent, subSubIndex) in subIntent.subIntents" :key="subSubIntent.id"
                                                                class="sub-sub-intent-item ml-2 mb-1 pa-1 rounded bg-grey-lighten-4">
                                                                <div class="d-flex align-center">
                                                                    <v-icon size="12" color="grey" class="mr-1">mdi-chevron-right</v-icon>
                                                                    <v-text-field
                                                                        v-model="subSubIntent.intentName"
                                                                        density="compact" hide-details variant="plain"
                                                                        @update:model-value="updateAdvancedRouterData()"
                                                                        class="flex-grow-1" :readonly="isViewMode"
                                                                        placeholder="Level 3 intent name" />
                                                                    <v-select
                                                                        :items="accessLevelOptionsIncluding(subSubIntent.accessLevel)" item-title="title" item-value="value"
                                                                        variant="outlined" density="compact" hide-details
                                                                        class="ml-2 flex-shrink-0" style="max-width: 96px"
                                                                        title="Access Level (ขั้นต่ำ)" :readonly="isViewMode"
                                                                        :model-value="subSubIntent.accessLevel || 'T0'"
                                                                        @update:model-value="(v) => { subSubIntent.accessLevel = v; updateAdvancedRouterData(); }">
                                                                        <template v-slot:selection="{ item }">{{ item.raw.value }}</template>
                                                                    </v-select>
                                                                    <v-btn v-if="!isViewMode" icon size="x-small" variant="text"
                                                                        @click="removeAdvancedRouterSubIntent(outputIndex, subIndex, subSubIndex)">
                                                                        <v-icon size="12">mdi-close</v-icon>
                                                                    </v-btn>
                                                                </div>
                                                                
                                                                <!-- Level 4 Sub-Intents -->
                                                                <div v-if="nodeInspector.advancedRouterMaxDepth >= 4" class="ml-4 mt-1">
                                                                    <div class="d-flex align-center">
                                                                        <span class="text-caption text-grey mr-2">L4:</span>
                                                                        <v-btn v-if="!isViewMode" size="x-small" variant="text"
                                                                            @click="addAdvancedRouterSubIntent(outputIndex, subIndex, subSubIndex)">
                                                                            <v-icon size="10">mdi-plus</v-icon>
                                                                        </v-btn>
                                                                    </div>
                                                                    <div v-for="(l4Intent, l4Index) in subSubIntent.subIntents" :key="l4Intent.id"
                                                                        class="d-flex align-center ml-2">
                                                                        <v-icon size="10" color="grey-lighten-1">mdi-minus</v-icon>
                                                                        <v-text-field
                                                                            v-model="l4Intent.intentName"
                                                                            density="compact" hide-details variant="plain"
                                                                            @update:model-value="updateAdvancedRouterData()"
                                                                            class="flex-grow-1" :readonly="isViewMode"
                                                                            placeholder="Level 4 intent" />
                                                                        <v-select
                                                                            :items="accessLevelOptionsIncluding(l4Intent.accessLevel)" item-title="title" item-value="value"
                                                                            variant="outlined" density="compact" hide-details
                                                                            class="ml-2 flex-shrink-0" style="max-width: 96px"
                                                                            title="Access Level (ขั้นต่ำ)" :readonly="isViewMode"
                                                                            :model-value="l4Intent.accessLevel || 'T0'"
                                                                            @update:model-value="(v) => { l4Intent.accessLevel = v; updateAdvancedRouterData(); }">
                                                                            <template v-slot:selection="{ item }">{{ item.raw.value }}</template>
                                                                        </v-select>
                                                                        <v-btn v-if="!isViewMode" icon size="x-small" variant="text"
                                                                            @click="removeAdvancedRouterSubIntent(outputIndex, subIndex, subSubIndex, l4Index)">
                                                                            <v-icon size="10">mdi-close</v-icon>
                                                                        </v-btn>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>

                        <!-- Cache Settings -->
                        <h6 class="section-title mt-6">Cache Settings</h6>
                        <div class="config-section mb-3">
                            <!-- Enable Cache Switch -->
                            <div class="switch-option-compact mb-4">
                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                    :class="{ 'switch-active-bg': nodeInspector.enableCache }">
                                    <div class="d-flex align-center">
                                        <v-avatar size="28"
                                            :color="nodeInspector.enableCache ? 'warning' : 'grey-lighten-1'"
                                            class="me-3">
                                            <v-icon
                                                :color="nodeInspector.enableCache ? 'white' : 'grey-darken-1'"
                                                size="16">mdi-lightning-bolt</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="switch-title">Enable Cache</div>
                                            <div class="switch-description">Cache intent routing results to reduce LLM calls</div>
                                        </div>
                                    </div>
                                    <v-switch v-model="nodeInspector.enableCache"
                                        color="warning" hide-details density="compact"
                                        @update:model-value="updateNodeProperty('enableCache', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                            </div>

                            <!-- Cache TTL -->
                            <v-row v-if="nodeInspector.enableCache">
                                <v-col cols="12">
                                    <v-text-field v-model.number="nodeInspector.cacheTTLMinutes"
                                        label="Cache TTL (minutes)" type="number" min="1" max="10080"
                                        variant="outlined" density="comfortable" hide-details="auto"
                                        prepend-inner-icon="mdi-timer-outline"
                                        @update:model-value="updateNodeProperty('cacheTTLMinutes', $event)"
                                        @keypress="isNumberpositive($event)" class="config-input"
                                        :readonly="isViewMode"
                                        hint="How long to keep cached routing results (1-10080 min)"
                                        persistent-hint />
                                </v-col>
                            </v-row>

                            <h6 v-if="nodeInspector.enableCache" class="section-label mb-3 mt-4">Semantic Cache</h6>

                            <!-- Enable Semantic Cache Switch -->
                            <div v-if="nodeInspector.enableCache" class="switch-option-compact mb-4">
                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                    :class="{ 'switch-active-bg': nodeInspector.enableSemanticCache }">
                                    <div class="d-flex align-center">
                                        <v-avatar size="28"
                                            :color="nodeInspector.enableSemanticCache ? 'deep-purple' : 'grey-lighten-1'"
                                            class="me-3">
                                            <v-icon
                                                :color="nodeInspector.enableSemanticCache ? 'white' : 'grey-darken-1'"
                                                size="16">mdi-brain</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="switch-title">Semantic Match</div>
                                            <div class="switch-description">Match similar questions using BGE-M3 AI embeddings</div>
                                        </div>
                                    </div>
                                    <v-switch v-model="nodeInspector.enableSemanticCache"
                                        color="deep-purple" hide-details density="compact"
                                        @update:model-value="updateNodeProperty('enableSemanticCache', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                            </div>

                            <!-- Similarity Threshold Slider -->
                            <v-row v-if="nodeInspector.enableCache && nodeInspector.enableSemanticCache">
                                <v-col cols="12">
                                    <div class="slider-label mb-2">
                                        <span>Similarity Threshold</span>
                                        <span class="text-primary font-weight-bold">{{ (nodeInspector.similarityThreshold * 100).toFixed(0) }}%</span>
                                    </div>
                                    <v-slider
                                        v-model="nodeInspector.similarityThreshold"
                                        min="0.50"
                                        max="1.00"
                                        step="0.01"
                                        color="deep-purple"
                                        track-color="grey-lighten-2"
                                        thumb-label
                                        hide-details
                                        @update:model-value="updateNodeProperty('similarityThreshold', $event)"
                                        :readonly="isViewMode">
                                        <template v-slot:thumb-label="{ modelValue }">
                                            {{ (modelValue * 100).toFixed(0) }}%
                                        </template>
                                    </v-slider>
                                    <div class="text-caption text-grey mt-1">
                                        Higher = more strict (exact match), Lower = more flexible
                                    </div>
                                </v-col>
                            </v-row>
                        </div>

                        <!-- Info Alert -->
                        <v-alert type="info" variant="tonal" density="compact" class="mt-4">
                            <template v-slot:prepend>
                                <v-icon size="small">mdi-information</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>Hierarchical Intent Detection:</strong><br>
                                Detects intents at multiple levels. Routes to <strong>deepest matching leaf</strong> intent.<br>
                                Example: User says "ประกันชีวิต" → Product > ประกันชีวิต<br>
                                Only leaf nodes (without sub-intents) have output handles.
                            </div>
                        </v-alert>
                    </div>

                    <!-- Dynamic Router Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'dynamicRouter'" class="form-section">
                        <h6 class="section-title">Dynamic Router Configuration</h6>
                        
                        <!-- Router Name -->
                        <v-text-field v-model="nodeInspector.dynamicRouterName" label="Dynamic Router Name"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('dynamicRouterName', $event)"
                            class="modern-input mb-4" :readonly="isViewMode" />

                        <!-- Router Prompt -->
                        <v-textarea v-model="nodeInspector.dynamicRouterPrompt" label="Routing Prompt (supports {{tagName}} tags)"
                            variant="outlined" density="compact" rows="4" auto-grow hide-details="auto"
                            @update:model-value="updateNodeProperty('dynamicRouterPrompt', $event)"
                            class="modern-input mb-4" :readonly="isViewMode"
                            placeholder="Use {{tagName}} to reference MetaDocument titles. They will be auto-resolved to active documents at runtime." />

                        <!-- Confidence Threshold -->
                        <div class="slider-container mb-4">
                            <div class="d-flex justify-space-between align-center mb-2">
                                <span class="slider-label">Confidence Threshold</span>
                                <v-chip size="small" color="teal" variant="tonal">
                                    {{ (nodeInspector.dynamicRouterConfidenceThreshold * 100).toFixed(0) }}%
                                </v-chip>
                            </div>
                            <v-slider v-model="nodeInspector.dynamicRouterConfidenceThreshold" :min="0" :max="1" :step="0.05"
                                color="teal" track-color="grey-lighten-3" thumb-label
                                @update:model-value="updateNodeProperty('dynamicRouterConfidenceThreshold', $event)"
                                class="modern-slider" :readonly="isViewMode">
                                <template v-slot:thumb-label="{ modelValue }">
                                    {{ (modelValue * 100).toFixed(0) }}%
                                </template>
                            </v-slider>
                        </div>

                        <!-- Max Depth -->
                        <div class="slider-container mb-4">
                            <div class="d-flex justify-space-between align-center mb-2">
                                <span class="slider-label">Maximum Intent Depth</span>
                                <v-chip size="small" color="teal" variant="tonal">
                                    {{ nodeInspector.dynamicRouterMaxDepth }} levels
                                </v-chip>
                            </div>
                            <v-slider v-model="nodeInspector.dynamicRouterMaxDepth" :min="1" :max="4" :step="1"
                                color="teal" track-color="grey-lighten-3" thumb-label
                                @update:model-value="updateNodeProperty('dynamicRouterMaxDepth', $event)"
                                class="modern-slider" :readonly="isViewMode" />
                            <div class="text-caption text-grey-darken-1">
                                How many levels deep the intent hierarchy can go (1-4)
                            </div>
                        </div>

                        <!-- Hierarchical Router Outputs Configuration -->
                        <div class="adv-router-outputs-section">
                            <div class="section-header mb-3">
                                <h6 class="section-title mb-0">Intent Hierarchy (Level 1)</h6>
                                <v-btn v-if="!isViewMode" size="small" color="teal" variant="tonal"
                                    @click="adddynamicRouterOutput()" class="add-btn">
                                    <v-icon left size="16">mdi-plus</v-icon>
                                    Add Intent
                                </v-btn>
                            </div>

                            <!-- Recursive Intent Tree -->
                            <div v-for="(output, outputIndex) in nodeInspector.dynamicRouterOutputs" :key="output.id"
                                class="adv-router-output-card mb-3">
                                <v-expansion-panels variant="accordion" class="adv-router-output-expansion">
                                    <v-expansion-panel>
                                        <v-expansion-panel-title>
                                            <div class="d-flex align-center w-100">
                                                <v-icon class="mr-2" color="teal" size="small">mdi-folder-outline</v-icon>
                                                <span class="font-weight-medium">
                                                    {{ output.intentName || `Intent ${outputIndex + 1}` }}
                                                </span>
                                                <v-chip v-if="output.subIntents && output.subIntents.length > 0" 
                                                    size="x-small" color="teal" variant="outlined" class="ml-2">
                                                    {{ output.subIntents.length }} sub-intents
                                                </v-chip>
                                                <v-chip v-else size="x-small" color="green" variant="outlined" class="ml-2">
                                                    Leaf (Output)
                                                </v-chip>
                                                <v-spacer></v-spacer>
                                                <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                    variant="text" @click.stop="removedynamicRouterOutput(outputIndex)"
                                                    :disabled="nodeInspector.dynamicRouterOutputs.length <= 1" class="ml-2">
                                                    <v-icon size="16">mdi-delete</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <div class="adv-router-output-content pa-2">
                                                <!-- Intent Name -->
                                                <v-text-field
                                                    v-model="nodeInspector.dynamicRouterOutputs[outputIndex].intentName"
                                                    label="Intent Name"
                                                    variant="outlined" density="compact" hide-details="auto"
                                                    @update:model-value="updatedynamicRouterData()"
                                                    class="modern-input mb-3" :readonly="isViewMode"
                                                    placeholder="e.g., Product, Support, Billing" />
                                                <!-- Access Level (ขั้นต่ำ) — ค่าจริงของ intent = สูงสุดตามเส้นทาง folder ราก → intent -->
                                                <v-select
                                                    :items="accessLevelOptionsIncluding(output.accessLevel)" item-title="title" item-value="value"
                                                    label="Access Level (ขั้นต่ำ)"
                                                    variant="outlined" density="compact" hide-details="auto"
                                                    class="modern-input mb-1" :readonly="isViewMode"
                                                    :model-value="output.accessLevel || 'T0'"
                                                    @update:model-value="(v) => { output.accessLevel = v; updatedynamicRouterData(); }" />
                                                <div class="text-caption text-grey mb-3">folder ที่ตั้งระดับไว้ คุม intent ลูกทั้งหมด (ลูกตั้งต่ำกว่าไม่มีผล)</div>
                                                
                                                <!-- Sub-Intents (Level 2) -->
                                                <div v-if="nodeInspector.dynamicRouterMaxDepth >= 2" class="sub-intents-section mt-4">
                                                    <div class="d-flex align-center justify-space-between mb-2">
                                                        <span class="text-subtitle-2 text-grey-darken-1">
                                                            <v-icon size="16" class="mr-1">mdi-subdirectory-arrow-right</v-icon>
                                                            Sub-Intents (Level 2)
                                                        </span>
                                                        <v-btn v-if="!isViewMode" size="x-small" color="teal" variant="text"
                                                            @click="adddynamicRouterSubIntent(outputIndex)">
                                                            <v-icon left size="14">mdi-plus</v-icon>
                                                            Add Sub-Intent
                                                        </v-btn>
                                                    </div>
                                                    
                                                    <!-- Level 2 Sub-Intents -->
                                                    <div v-for="(subIntent, subIndex) in output.subIntents" :key="subIntent.id"
                                                        class="sub-intent-item ml-4 mb-2 pa-2 rounded border">
                                                        <div class="d-flex align-center mb-2">
                                                            <v-icon size="16" color="teal-lighten-2" class="mr-2">mdi-circle-small</v-icon>
                                                            <v-text-field
                                                                v-model="subIntent.intentName"
                                                                label="Sub-Intent Name"
                                                                variant="outlined" density="compact" hide-details
                                                                @update:model-value="updatedynamicRouterData()"
                                                                class="modern-input flex-grow-1" :readonly="isViewMode" />
                                                            <v-select
                                                                :items="accessLevelOptionsIncluding(subIntent.accessLevel)" item-title="title" item-value="value"
                                                                variant="outlined" density="compact" hide-details
                                                                class="ml-2 flex-shrink-0" style="max-width: 96px"
                                                                title="Access Level (ขั้นต่ำ)" :readonly="isViewMode"
                                                                :model-value="subIntent.accessLevel || 'T0'"
                                                                @update:model-value="(v) => { subIntent.accessLevel = v; updatedynamicRouterData(); }">
                                                                <template v-slot:selection="{ item }">{{ item.raw.value }}</template>
                                                            </v-select>
                                                            <v-btn v-if="!isViewMode" icon size="x-small" color="error" variant="text"
                                                                @click="removedynamicRouterSubIntent(outputIndex, subIndex)" class="ml-2">
                                                                <v-icon size="14">mdi-close</v-icon>
                                                            </v-btn>
                                                        </div>
                                                        
                                                        <!-- Level 3 Sub-Intents -->
                                                        <div v-if="nodeInspector.dynamicRouterMaxDepth >= 3" class="ml-6 mt-2">
                                                            <div class="d-flex align-center justify-space-between mb-1">
                                                                <span class="text-caption text-grey">Level 3 Intents</span>
                                                                <v-btn v-if="!isViewMode" size="x-small" variant="text" color="teal"
                                                                    @click="adddynamicRouterSubIntent(outputIndex, subIndex)">
                                                                    <v-icon size="12">mdi-plus</v-icon>
                                                                </v-btn>
                                                            </div>
                                                            <div v-for="(subSubIntent, subSubIndex) in subIntent.subIntents" :key="subSubIntent.id"
                                                                class="sub-sub-intent-item ml-2 mb-1 pa-1 rounded bg-grey-lighten-4">
                                                                <div class="d-flex align-center">
                                                                    <v-icon size="12" color="grey" class="mr-1">mdi-chevron-right</v-icon>
                                                                    <v-text-field
                                                                        v-model="subSubIntent.intentName"
                                                                        density="compact" hide-details variant="plain"
                                                                        @update:model-value="updatedynamicRouterData()"
                                                                        class="flex-grow-1" :readonly="isViewMode"
                                                                        placeholder="Level 3 intent name" />
                                                                    <v-select
                                                                        :items="accessLevelOptionsIncluding(subSubIntent.accessLevel)" item-title="title" item-value="value"
                                                                        variant="outlined" density="compact" hide-details
                                                                        class="ml-2 flex-shrink-0" style="max-width: 96px"
                                                                        title="Access Level (ขั้นต่ำ)" :readonly="isViewMode"
                                                                        :model-value="subSubIntent.accessLevel || 'T0'"
                                                                        @update:model-value="(v) => { subSubIntent.accessLevel = v; updatedynamicRouterData(); }">
                                                                        <template v-slot:selection="{ item }">{{ item.raw.value }}</template>
                                                                    </v-select>
                                                                    <v-btn v-if="!isViewMode" icon size="x-small" variant="text"
                                                                        @click="removedynamicRouterSubIntent(outputIndex, subIndex, subSubIndex)">
                                                                        <v-icon size="12">mdi-close</v-icon>
                                                                    </v-btn>
                                                                </div>
                                                                
                                                                <!-- Level 4 Sub-Intents -->
                                                                <div v-if="nodeInspector.dynamicRouterMaxDepth >= 4" class="ml-4 mt-1">
                                                                    <div class="d-flex align-center">
                                                                        <span class="text-caption text-grey mr-2">L4:</span>
                                                                        <v-btn v-if="!isViewMode" size="x-small" variant="text"
                                                                            @click="adddynamicRouterSubIntent(outputIndex, subIndex, subSubIndex)">
                                                                            <v-icon size="10">mdi-plus</v-icon>
                                                                        </v-btn>
                                                                    </div>
                                                                    <div v-for="(l4Intent, l4Index) in subSubIntent.subIntents" :key="l4Intent.id"
                                                                        class="d-flex align-center ml-2">
                                                                        <v-icon size="10" color="grey-lighten-1">mdi-minus</v-icon>
                                                                        <v-text-field
                                                                            v-model="l4Intent.intentName"
                                                                            density="compact" hide-details variant="plain"
                                                                            @update:model-value="updatedynamicRouterData()"
                                                                            class="flex-grow-1" :readonly="isViewMode"
                                                                            placeholder="Level 4 intent" />
                                                                        <v-select
                                                                            :items="accessLevelOptionsIncluding(l4Intent.accessLevel)" item-title="title" item-value="value"
                                                                            variant="outlined" density="compact" hide-details
                                                                            class="ml-2 flex-shrink-0" style="max-width: 96px"
                                                                            title="Access Level (ขั้นต่ำ)" :readonly="isViewMode"
                                                                            :model-value="l4Intent.accessLevel || 'T0'"
                                                                            @update:model-value="(v) => { l4Intent.accessLevel = v; updatedynamicRouterData(); }">
                                                                            <template v-slot:selection="{ item }">{{ item.raw.value }}</template>
                                                                        </v-select>
                                                                        <v-btn v-if="!isViewMode" icon size="x-small" variant="text"
                                                                            @click="removedynamicRouterSubIntent(outputIndex, subIndex, subSubIndex, l4Index)">
                                                                            <v-icon size="10">mdi-close</v-icon>
                                                                        </v-btn>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>

                        <v-alert type="info" variant="tonal" density="compact" class="mb-3">
                            <template v-slot:text>
                            </template>
                            <div class="text-caption">
                                <strong>MetaDocument-Aware Routing:</strong><br>
                                Use <code>{<!-- -->{tagName}}</code> in the prompt to reference documents by title.<br>
                                Tags are resolved at runtime to active documents from <strong>SplitChunkHeadContent</strong>.<br>
                                Resolved documents are auto-attached to matched intents for downstream retrieval.
                            </div>
                        </v-alert>
                    </div>

                    <!-- Dynamic Knowledge Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'dynamicKnowledge'" class="form-section">
                        <h6 class="section-title">Dynamic Knowledge Configuration</h6>

                        <v-alert type="info" variant="tonal" density="compact" class="mb-3">
                            <div class="text-caption">
                                <strong>Auto-resolved Documents:</strong><br>
                                This node automatically retrieves documents from the connected <strong>Dynamic Router</strong> node.<br>
                                When Meta Community Graph is preferred, it is used across Level 1, Level 2, and Level 3 even if the router sends document references. When it is off, router document references are used directly when they are within the explicit document limit.
                            </div>
                        </v-alert>

                        <!-- Dynamic Knowledge Retrieval Mode -->
                        <h6 class="section-title mb-2">Retrieval Mode</h6>
                        <div class="text-caption text-grey-darken-1 mb-3">
                            Turn this on to prefer Meta Community Graph for every router level. Leave it off to use explicit router documents first.
                        </div>

                        <!-- Prefer Meta Community Graph -->
                        <div class="switch-option-compact mb-3">
                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                :class="{ 'switch-active-bg': nodeInspector.useMetaCommunityGraphSubCat }">
                                <div class="d-flex align-center switch-content-row">
                                    <v-avatar size="28"
                                        :color="nodeInspector.useMetaCommunityGraphSubCat ? 'deep-purple' : 'grey-lighten-1'"
                                        class="me-3">
                                        <v-icon
                                            :color="nodeInspector.useMetaCommunityGraphSubCat ? 'white' : 'grey-darken-1'"
                                            size="16">mdi-graph-outline</v-icon>
                                    </v-avatar>
                                    <div class="switch-copy-block">
                                        <div class="switch-title">Prefer Meta Community Graph</div>
                                        <div class="switch-description">Uses Meta Community Graph for Level 1, Level 2, and Level 3 before explicit router document references</div>
                                    </div>
                                </div>
                                <v-switch v-model="nodeInspector.useMetaCommunityGraphSubCat"
                                    color="deep-purple" hide-details density="compact"
                                    @update:model-value="updateNodeProperty('useMetaCommunityGraphSubCat', $event)"
                                    :readonly="isViewMode" inset />
                            </div>
                        </div>

                        <v-divider class="my-4"></v-divider>

                        <h6 class="section-title">Meta Document Retrieval</h6>

                        <v-text-field v-model="nodeInspector.maxExplicitDocs" type="number"
                            label="Max Explicit Documents"
                            variant="outlined" density="compact" hide-details :readonly="isViewMode" min="1"
                            step="1" @focus="$event.target.select()"
                            @blur="validateMaxExplicitDocs($event)"
                            @keydown.enter="validateMaxExplicitDocs($event)"
                            @update:model-value="updateNodeProperty('maxExplicitDocs', $event)"
                            class="modern-input mb-1" />
                        <div class="text-caption text-grey mb-3">When Meta Community Graph is not preferred, router document references are used only up to this limit</div>

                        <v-text-field v-model="nodeInspector.topNChunks" type="number"
                            label="Meta Document Top-N Chunks"
                            variant="outlined" density="compact" hide-details="auto" :readonly="isViewMode" min="1"
                            step="1" @focus="$event.target.select()"
                            @blur="validateAndFixTopNChunks($event)"
                            @keydown.enter="validateAndFixTopNChunks($event)"
                            class="modern-input mb-3"
                            hint="Controls how many chunks are retrieved when Dynamic Knowledge reads meta documents" />

                        <!-- Summary Memory (Embedding) -->
                        <h6 class="section-title">Summary Memory (Embedding)</h6>
                        <div class="text-caption text-grey mb-2">
                            When enabled, an LLM summarizes recent conversation turns + the latest message into the
                            search query so follow-up questions retrieve the right documents. Affects document search
                            only — the final answer is unaffected. Memory Turns = 0 embeds the latest message directly.
                        </div>
                        <v-switch v-model="nodeInspector.summaryMemoryEnabled" color="primary" hide-details
                            density="compact" inset :readonly="isViewMode" label="Enable Summary Memory"
                            @update:model-value="updateNodeProperty('summaryMemoryEnabled', $event)" class="mb-2" />
                        <template v-if="nodeInspector.summaryMemoryEnabled">
                            <v-autocomplete v-model="nodeInspector.summaryMemoryProvider" :items="listDDProvider"
                                item-title="providerName" item-value="providerName" label="Provider" variant="outlined"
                                density="compact" hide-details="auto" :readonly="isViewMode"
                                @update:model-value="updateNodeProperty('summaryMemoryProvider', $event); changeProvider($event)"
                                class="modern-input mb-3" />
                            <v-autocomplete v-model="nodeInspector.summaryMemoryModel" :items="DDModelShow"
                                item-title="modelDisplay" item-value="modelName" label="Model" variant="outlined"
                                density="compact" hide-details="auto" :readonly="isViewMode"
                                :disabled="!nodeInspector.summaryMemoryProvider"
                                @update:model-value="updateNodeProperty('summaryMemoryModel', $event)"
                                class="modern-input mb-3" />
                            <v-text-field v-if="nodeInspector.summaryMemoryApiVersion"
                                v-model="nodeInspector.summaryMemoryApiVersion" label="API Version" variant="outlined"
                                density="compact" hide-details="auto" readonly class="modern-input mb-3" />
                            <v-row>
                                <v-col cols="6">
                                    <v-text-field v-model.number="nodeInspector.summaryMemoryMemoryTurn" type="number"
                                        min="0" step="1" label="Memory Turns" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode" @focus="$event.target.select()"
                                        @update:model-value="updateNodeProperty('summaryMemoryMemoryTurn', $event)"
                                        class="modern-input" hint="0 = embed latest message only" />
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field v-model.number="nodeInspector.summaryMemoryMemoryDuration" type="number"
                                        min="1" step="1" label="Memory Duration (min)" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode" @focus="$event.target.select()"
                                        @update:model-value="updateNodeProperty('summaryMemoryMemoryDuration', $event)"
                                        class="modern-input" />
                                </v-col>
                            </v-row>
                            <v-row class="mt-1">
                                <v-col cols="6">
                                    <v-text-field v-model.number="nodeInspector.summaryMemoryTemperature" type="number"
                                        step="0.1" min="0" max="2" label="Temperature" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateNodeProperty('summaryMemoryTemperature', $event)"
                                        class="modern-input" />
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field v-model.number="nodeInspector.summaryMemoryTopP" type="number" step="0.1"
                                        min="0" max="1" label="Top P" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateNodeProperty('summaryMemoryTopP', $event)"
                                        class="modern-input" />
                                </v-col>
                            </v-row>
                            <v-text-field v-model.number="nodeInspector.summaryMemoryMaxTokens" type="number" min="1"
                                label="Max Tokens" variant="outlined" density="compact" hide-details="auto"
                                :readonly="isViewMode"
                                @update:model-value="updateNodeProperty('summaryMemoryMaxTokens', $event)"
                                class="modern-input mt-3" />
                            <v-row class="mt-1">
                                <v-col cols="6">
                                    <v-text-field v-model.number="nodeInspector.summaryMemoryFrequencyPenalty"
                                        type="number" step="0.1" min="-2" max="2" label="Frequency Penalty"
                                        variant="outlined" density="compact" hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateNodeProperty('summaryMemoryFrequencyPenalty', $event)"
                                        class="modern-input" />
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field v-model.number="nodeInspector.summaryMemoryPresencePenalty" type="number"
                                        step="0.1" min="-2" max="2" label="Presence Penalty" variant="outlined"
                                        density="compact" hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateNodeProperty('summaryMemoryPresencePenalty', $event)"
                                        class="modern-input" />
                                </v-col>
                            </v-row>
                            <v-textarea v-model="nodeInspector.summaryMemorySystemPrompt" label="System Prompt"
                                variant="outlined" density="compact" auto-grow rows="6" hide-details="auto"
                                :readonly="isViewMode"
                                @update:model-value="updateNodeProperty('summaryMemorySystemPrompt', $event)"
                                class="modern-input mt-3"
                                placeholder="You are a conversation summarizer. Given the chat history and the latest user message, write a concise standalone search query that captures the full context." />
                            <v-text-field v-model.number="nodeInspector.summaryMemoryTimeout" type="number" min="0"
                               label="LLM Timeout (seconds)" variant="outlined" density="compact" hide-details="auto"
                               :readonly="isViewMode" @keypress="isNumberpositive($event)"
                               @update:model-value="updateNodeProperty('summaryMemoryTimeout', $event)"
                               class="modern-input mt-3" hint="0 = use global timeout setting" />
                        </template>

                        <v-divider class="my-4"></v-divider>

                        <!-- Meta Community Graph RAG Section -->
                        <h6 class="section-title">
                            Meta Community Graph
                        </h6>
                        <div class="text-caption text-grey mb-2">
                            Select the graph used when Meta Community Graph is preferred or when explicit router documents cannot be used.
                        </div>

                        <!-- Meta Community Graph Selection -->
                        <v-autocomplete
                            v-model="nodeInspector.metaCommunityId"
                            :items="DDMetaCommunityGraph" :no-data-text="kccGraphsNoDataText"
                            item-title="communityName"
                            item-value="id"
                            label="Meta Community Graph"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            class="modern-input mb-3"
                            placeholder="Select meta community graph"
                            :clearable="!isViewMode"
                            :readonly="isViewMode"
                            @update:model-value="updateNodeProperty('metaCommunityId', $event)"
                        >
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="deep-purple">mdi-graph-outline</v-icon>
                                    </template>
                                    <template v-slot:append>
                                        <v-chip size="x-small" color="deep-purple" variant="tonal">
                                            {{ item.raw.groupCount }} groups
                                        </v-chip>
                                    </template>
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                            <template v-slot:selection="{ item }">
                                <v-chip color="deep-purple" variant="tonal" size="small">
                                    <v-icon start size="small">mdi-graph-outline</v-icon>
                                    {{ item.title }} ({{ item.raw.groupCount }} groups)
                                </v-chip>
                            </template>
                        </v-autocomplete>
                        <!-- App Knowledge Center (D2): graph ของ Center เท่านั้น / ดึงรายการไม่ได้ (โหมดปิดไม่แสดง) -->
                        <v-alert v-if="kccCenterMode" :type="kccGraphsUnavailable ? 'warning' : 'info'" variant="tonal"
                            density="compact" class="mb-3 text-caption">
                            {{ $t(kccGraphsUnavailable || 'kccGraphsCenterOnly') }}
                        </v-alert>

                        <!-- Meta Community Graph Parameters (always visible) -->
                        <div class="community-params mb-3">
                            <!-- Search Method Selection -->
                            <v-select
                                v-model="nodeInspector.metaCommunitySearchMethod"
                                :items="[
                                    { title: 'Top N', value: 'topn' },
                                    { title: 'Cosine Threshold', value: 'cosine' }
                                ]"
                                item-title="title"
                                item-value="value"
                                label="Search Method"
                                variant="outlined"
                                density="compact"
                                hide-details="auto"
                                :readonly="isViewMode"
                                @update:model-value="updateNodeProperty('metaCommunitySearchMethod', $event)"
                                class="modern-input mb-3"
                                hint="Method to retrieve chunks"
                            />

                            <!-- Top N Mode Parameters -->
                            <div v-if="nodeInspector.metaCommunitySearchMethod === 'topn'" class="d-flex gap-3">
                                <v-text-field
                                    v-model="nodeInspector.metaCommunityTopGroups"
                                    type="number"
                                    label="Top Groups"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    :readonly="isViewMode"
                                    min="1"
                                    max="100"
                                    step="1"
                                    @focus="$event.target.select()"
                                    @blur="validateMetaCommunityTopGroups($event)"
                                    class="modern-input"
                                    style="max-width: 120px; margin-right: 8px;"
                                />
                                <v-text-field
                                    v-model="nodeInspector.metaCommunityTopChunks"
                                    type="number"
                                    label="Top Chunks"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    :readonly="isViewMode"
                                    min="1"
                                    max="100"
                                    step="1"
                                    @focus="$event.target.select()"
                                    @blur="validateMetaCommunityTopChunks($event)"
                                    class="modern-input"
                                    style="max-width: 120px;"
                                />
                            </div>

                            <!-- Cosine Threshold Mode Parameters -->
                            <div v-else-if="nodeInspector.metaCommunitySearchMethod === 'cosine'">
                                <v-text-field
                                    v-model="nodeInspector.metaCommunityMinCosineScore"
                                    type="number"
                                    label="Min Cosine Score"
                                    variant="outlined"
                                    density="compact"
                                    hide-details="auto"
                                    :readonly="isViewMode"
                                    min="0"
                                    max="1"
                                    step="0.05"
                                    @focus="$event.target.select()"
                                    @blur="validateMetaCommunityMinCosineScore($event)"
                                    class="modern-input"
                                    style="max-width: 160px;"
                                    hint="Min score (0-1) to retrieve chunks"
                                />
                            </div>
                        </div>

                        <v-divider class="my-4"></v-divider>

                        <!-- LLM Timeout / Max Retry -->
                        <h6 class="section-title">Advanced Settings</h6>
                        <v-text-field
                            v-model.number="nodeInspector.nodeTimeout"
                            type="number"
                            min="0"
                            label="LLM Timeout (seconds)"
                            hint="0 = use global timeout setting"
                            persistent-hint
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            class="modern-input mb-3"
                            @update:model-value="updateNodeProperty('nodeTimeout', $event)"
                            :readonly="isViewMode"
                        />
                        <v-text-field
                            v-model.number="nodeInspector.nodeMaxRetry"
                            type="number"
                            min="0"
                            label="Max Retry"
                            hint="0 = use global max retry setting"
                            persistent-hint
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            class="modern-input"
                            @update:model-value="updateNodeProperty('nodeMaxRetry', $event)"
                            :readonly="isViewMode"
                        />

                    </div>

                    <!-- Smart Intent Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'smartIntent'" class="form-section">
                        <h6 class="section-title">Smart Intent Configuration</h6>
                        <v-text-field v-model="nodeInspector.smartIntentGroupName" label="Smart Intent Handler Name"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('smartIntentGroupName', $event)"
                            class="modern-input mb-4" :readonly="isViewMode" />

                        <!-- Smart Intents Configuration -->
                        <div class="smart-intents-section">
                            <div class="section-header mb-3">
                                <h6 class="section-title mb-0">Smart Intents</h6>
                                <div class="d-flex gap-2">
                                    <v-btn v-if="!isViewMode" size="small" color="primary" variant="tonal"
                                        @click="addInspectorSmartIntent" class="add-btn">
                                        <v-icon left size="16">mdi-plus</v-icon>
                                        Add Smart Intent
                                    </v-btn>
                                </div>
                            </div>

                            <div v-for="(smartIntent, smartIntentIndex) in nodeInspector.smartIntents" :key="smartIntent.id"
                                class="smart-intent-card mb-3">
                                <v-expansion-panels variant="accordion" class="smart-intent-expansion">
                                    <v-expansion-panel>
                                        <v-expansion-panel-title>
                                            <div class="d-flex align-center w-100">
                                                <v-icon class="mr-2" color="primary" size="small">mdi-brain-outline</v-icon>
                                                <!-- Priority Number -->
                                                <span v-if="smartIntent.priority || smartIntent.priority === 0"
                                                    class="priority-number mr-2">
                                                    {{ smartIntent.priority }}
                                                </span>
                                                <span class="font-weight-medium">
                                                    {{
                                                        smartIntent.name || `Smart Intent ${smartIntentIndex + 1}`
                                                    }}
                                                </span>
                                                <v-spacer></v-spacer>
                                                <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                    variant="text" @click.stop="removeInspectorSmartIntent(smartIntentIndex)"
                                                    :disabled="nodeInspector.smartIntents.length <= 1" class="ml-2">
                                                    <v-icon size="16">mdi-delete</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <div class="smart-intent-content pa-2">
                                                <!-- Smart Intent Priority -->
                                                <v-text-field
                                                    v-model.number="nodeInspector.smartIntents[smartIntentIndex].priority"
                                                    label="Priority" type="number" variant="outlined" density="compact"
                                                    hide-details="auto" min="0" step="1" placeholder="Enter priority"
                                                    @update:model-value="updateSmartIntentData()"
                                                    @keypress="isNumberpositive($event)" class="modern-input mb-3"
                                                    :readonly="isViewMode">
                                                    <template v-slot:prepend-inner>
                                                        <v-icon size="small" color="orange">mdi-priority-high</v-icon>
                                                    </template>
                                                    <template v-slot:append-inner>
                                                        <v-tooltip location="top" max-width="200px">
                                                            <template v-slot:activator="{ props }">
                                                                <v-icon size="small" color="grey-darken-1"
                                                                    v-bind="props">
                                                                    mdi-information-outline
                                                                </v-icon>
                                                            </template>
                                                            <span>Priority 0 is the highest importance</span>
                                                        </v-tooltip>
                                                    </template>
                                                </v-text-field>

                                                <!-- Smart Intent Name -->
                                                <v-text-field v-model="nodeInspector.smartIntents[smartIntentIndex].name"
                                                    label="Smart Intent Name" variant="outlined" density="compact"
                                                    hide-details="auto" @update:model-value="updateSmartIntentData()"
                                                    class="modern-input mb-3" :readonly="isViewMode" />

                                                <!-- Use Memory Toggle -->
                                                <div style="display: flex; align-items: center; justify-content: flex-start;margin-top: -15px;">
                                                    <v-switch v-model="nodeInspector.smartIntents[smartIntentIndex].useMemory"
                                                        hide-details density="compact" color="primary" inset
                                                        style="transform: scale(0.9); margin-left: 0px; margin-top: 10px;"
                                                        @update:model-value="updateSmartIntentData()" class="mb-3"
                                                        :readonly="isViewMode" />
                                                    <span style="font-size: 16px;margin-left: 8px;">
                                                        Use memory
                                                    </span>
                                                </div>

                                                <div class="custom-divider"></div>

                                                <!-- Base Prompt Section -->
                                                <div style="margin-bottom: 36px;">
                                                    <!-- Base Prompt Toggle -->
                                                    <div style="display: flex;align-items: center;justify-content: flex-end;">
                                                        <span style="font-weight: 500;font-size: 12px;margin-right: 8px;">
                                                            Edit Base Prompt
                                                        </span>
                                                        <v-switch
                                                            v-model="nodeInspector.smartIntents[smartIntentIndex].enableBasePrompt"
                                                            color="primary" density="compact" hide-details
                                                            :readonly="isViewMode"
                                                            @update:model-value="updateSmartIntentData()" inset
                                                            style="transform: scale(0.8);">
                                                        </v-switch>
                                                    </div>

                                                    <!-- Base Prompt Textarea -->
                                                    <v-textarea v-model="nodeInspector.smartIntents[smartIntentIndex].basePrompt"
                                                        label="Base Prompt" variant="outlined" density="compact"
                                                        rows="4" auto-grow hide-details="auto"
                                                        @update:model-value="updateSmartIntentData()" class="modern-input"
                                                        :readonly="isViewMode || !nodeInspector.smartIntents[smartIntentIndex].enableBasePrompt"
                                                        :disabled="!nodeInspector.smartIntents[smartIntentIndex].enableBasePrompt"
                                                        placeholder="Find the Intent from the message of users.&#10;#Condition for Response&#10;1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0" />
                                                </div>

                                                <!-- Smart Intent Prompt -->
                                                <v-textarea v-model="nodeInspector.smartIntents[smartIntentIndex].prompt"
                                                    label="Smart Intent Prompt" variant="outlined" density="compact" rows="2"
                                                    auto-grow hide-details="auto"
                                                    @update:model-value="updateSmartIntentData()" class="modern-input mb-3"
                                                    :readonly="isViewMode" />

                                                <!-- Example Messages -->
                                                <div class="mb-3">
                                                    <div class="d-flex align-center mb-2">
                                                        <span class="text-subtitle-2 font-weight-medium">
                                                            Example Messages
                                                        </span>
                                                        <v-btn v-if="!isViewMode" size="x-small" color="primary"
                                                            variant="tonal"
                                                            @click="addInspectorSmartIntentMessage(smartIntentIndex)"
                                                            class="ml-auto">
                                                            <v-icon size="12">mdi-plus</v-icon>
                                                        </v-btn>
                                                    </div>
                                                    <div v-for="(message, messageIndex) in smartIntent.messages"
                                                        :key="message.id" class="d-flex align-center mb-2">
                                                        <v-text-field
                                                            v-model="nodeInspector.smartIntents[smartIntentIndex].messages[messageIndex].text"
                                                            placeholder="Example user message..." variant="outlined"
                                                            density="compact" hide-details="auto"
                                                            @update:model-value="updateSmartIntentData()"
                                                            class="modern-input flex-grow-1 mr-2"
                                                            :readonly="isViewMode" />
                                                        <v-btn v-if="!isViewMode" icon size="x-small" color="error"
                                                            variant="text"
                                                            @click="removeInspectorSmartIntentMessage(smartIntentIndex, messageIndex)"
                                                            :disabled="smartIntent.messages.length <= 1">
                                                            <v-icon size="14">mdi-close</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </div>

                                                <!-- Main Action Section with AI Configuration -->
                                                <div class="main-action-section mb-4">
                                                    <div class="d-flex align-center justify-space-between mb-2">
                                                        <span class="text-subtitle-2 font-weight-medium">Action Label</span>
                                                    </div>
                                                    
                                                    <!-- Action Label -->
                                                    <v-text-field
                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.label"
                                                        placeholder="Main action label..." variant="outlined"
                                                        density="compact" hide-details="auto"
                                                        @update:model-value="updateSmartIntentData()"
                                                        class="modern-input mt-2 mb-3" :readonly="isViewMode" />

                                                    <!-- <div class="custom-divider my-3"></div> -->
                                                    <!-- AI Toggle -->
                                                    <div class="ai-toggle-section mb-3">
                                                        <div style="display: flex; align-items: center; justify-content: space-between;">
                                                            <span class="text-subtitle-2 font-weight-medium">
                                                                <v-icon size="16" class="mr-2" color="primary">mdi-robot</v-icon>
                                                                Use AI Model
                                                            </span>
                                                            <div style="display: flex; align-items: center; gap: 8px;">
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.useAI"
                                                                        color="primary" density="compact" hide-details inset
                                                                        @update:model-value="updateSmartIntentData()"
                                                                        :readonly="isViewMode"
                                                                        style="transform: scale(0.8);" />
                                                                </div>
                                                            </div>
                                                    </div>

                                                    <!-- AI Configuration (when enabled) -->
                                                    <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.useAI" 
                                                         class="ai-config-section">
                                                         <div style="display: flex; align-items: center; justify-content: flex-end; margin-bottom: 8px;width: 100%;">  
                                                            <v-btn class="mb-3" v-if="!isViewMode && nodeInspector.smartIntents[smartIntentIndex].mainAction.useAI" @click="openSmartIntentLoadFromModelDialog(smartIntentIndex)" 
                                                                color="success" variant="text" size="small"
                                                                style="background-color: #e8f5e9;"
                                                                prepend-icon="mdi-download">
                                                                Load Config
                                                            </v-btn>
                                                         </div>
                                                        
                                                        <!-- AI Configuration Expandable Section -->
                                                        <v-expansion-panels variant="accordion" class="ai-main-config-expansion">
                                                            <v-expansion-panel>
                                                                <v-expansion-panel-title class="ai-config-title">
                                                                    <div class="d-flex align-center">
                                                                        <v-icon icon="mdi-brain" size="20" color="primary" class="me-3"></v-icon>
                                                                        <div>
                                                                            <div class="text-subtitle-1 font-weight-medium">AI Model Configuration</div>
                                                                            <div class="text-caption text-medium-emphasis">Provider, parameters, and behavior settings</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text>
                                                                    <!-- AI Model Configuration Panels (exactly like AI Model Node) -->
                                                                    <v-expansion-panels multiple class="config-panels elevation-0" variant="accordion">
                                                            <!-- Basic Information Panel -->
                                                            <v-expansion-panel value="basic" class="panel-item mt-3">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4">
                                                                            <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">Basic Information</div>
                                                                            <div class="panel-subtitle">Provider, model, and basic settings</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content">
                                                                        <!-- Provider and Model -->
                                                                        <div class="config-section mb-6">
                                                                            <h6 class="section-label mb-3">Service Configuration</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-autocomplete 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.provider"
                                                                                        :items="listDDProvider" item-title="providerName" item-value="providerName"
                                                                                        label="Provider*" variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-cloud-outline"
                                                                                        @update:model-value="updateSmartIntentProviderChange(smartIntentIndex, $event)"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        hint="Select AI service provider" persistent-hint required />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-autocomplete 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.model"
                                                                                        :items="getModelsByProvider(nodeInspector.smartIntents[smartIntentIndex].mainAction.provider)"
                                                                                        item-title="modelDisplay" item-value="modelName" label="Model*"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-brain"
                                                                                        @update:model-value="updateSmartIntentModelChange(smartIntentIndex, $event)"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        :disabled="!nodeInspector.smartIntents[smartIntentIndex].mainAction.provider"
                                                                                        hint="Select specific model version" persistent-hint required />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <!-- Behavior Settings -->
                                                                        <div class="config-section">
                                                                            <h6 class="section-label mb-3">Behavior Settings</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-select 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.gender"
                                                                                        :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                                                        item-title="name" item-value="id" label="Gender"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-account"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        hint="Voice/persona gender preference" persistent-hint
                                                                                        :model-value="nodeInspector.smartIntents[smartIntentIndex].mainAction.gender || 'male'" />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.typingSpeed"
                                                                                        label="Typing Speed" type="number" min="0" variant="outlined"
                                                                                        density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-keyboard-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        hint="Characters per second (0 = instant)" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.maxInputLength"
                                                                                        label="Max Input Length" type="number" min="0" max="200000"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-text-box-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="maxNumberpositive($event)"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        hint="Maximum characters allowed in user input" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.nodeTimeout"
                                                                                        label="Request Timeout (seconds)" type="number" min="0"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-timer-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        hint="0 = use global timeout setting" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.nodeMaxRetry"
                                                                                        label="Max Retry" type="number" min="0"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-refresh"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        hint="0 = use global max retry setting" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- Response Options Panel -->
                                                            <v-expansion-panel value="response" class="panel-item">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">Response Options</div>
                                                                            <div class="panel-subtitle">Configure how responses are generated</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content pa-4">
                                                                        <!-- Stream Response -->
                                                                        <div class="switch-option-compact mb-3">
                                                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.streamResponse }">
                                                                                <div class="d-flex align-center">
                                                                                    <v-avatar size="28"
                                                                                        :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.streamResponse ? 'primary' : 'grey-lighten-1'"
                                                                                        class="me-3">
                                                                                        <v-icon
                                                                                            :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.streamResponse ? 'white' : 'grey-darken-1'"
                                                                                            size="16">mdi-text-box-outline</v-icon>
                                                                                    </v-avatar>
                                                                                    <div>
                                                                                        <div class="switch-title">Stream Response</div>
                                                                                        <div class="switch-description">Progressive text generation</div>
                                                                                    </div>
                                                                                </div>
                                                                                <v-switch 
                                                                                    v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.streamResponse" 
                                                                                    color="primary" hide-details density="compact"
                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                    :readonly="isViewMode" inset />
                                                                            </div>
                                                                        </div>

                                                                        <!-- JSON Response -->
                                                                        <div class="switch-option-compact mt-3">
                                                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.jsonResponse }">
                                                                                <div class="d-flex align-center">
                                                                                    <v-avatar size="28"
                                                                                        :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.jsonResponse ? 'primary' : 'grey-lighten-1'"
                                                                                        class="me-3">
                                                                                        <v-icon
                                                                                            :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.jsonResponse ? 'white' : 'grey-darken-1'"
                                                                                            size="16">mdi-code-json</v-icon>
                                                                                    </v-avatar>
                                                                                    <div>
                                                                                        <div class="switch-title">JSON Response</div>
                                                                                        <div class="switch-description">Return structured JSON format</div>
                                                                                    </div>
                                                                                </div>
                                                                                <v-switch 
                                                                                    v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.jsonResponse" 
                                                                                    color="primary" hide-details density="compact"
                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                    :readonly="isViewMode" inset />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- Parameters Panel -->
                                                            <v-expansion-panel value="parameters" class="panel-item">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                                                            <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">AI Parameters</div>
                                                                            <div class="panel-subtitle">Fine-tune model behavior and output</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content">
                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Core Parameters</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.temperature"
                                                                                        label="Temperature*" type="number" step="0.1" min="0" max="1"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-thermometer"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        hint="Creativity level (0 = focused, 1 = creative)" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.topP" 
                                                                                        label="Top P" type="number" step="0.1" min="0" max="1" 
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-filter-variant"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        hint="Token selection probability threshold" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Penalty Settings</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.frequencyPenalty"
                                                                                        label="Frequency Penalty" type="number" step="0.1" min="0" max="2" 
                                                                                        variant="outlined" density="comfortable" hide-details="auto" 
                                                                                        prepend-inner-icon="mdi-repeat-off"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        hint="Reduce repetition of frequent tokens" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.presencePenalty"
                                                                                        label="Presence Penalty" type="number" step="0.1" min="0" max="2" 
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-minus-circle-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" 
                                                                                        hint="Reduce repetition of any tokens" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <div class="config-section">
                                                                            <h6 class="section-label mb-3">Output Control</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.maxTokens"
                                                                                        label="Max Tokens Response" type="number" min="1"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-text-box-check-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" 
                                                                                        hint="Maximum response length in tokens" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.stopSequence"
                                                                                        label="Stop Sequence" variant="outlined" density="comfortable"
                                                                                        hide-details="auto" prepend-inner-icon="mdi-stop-circle-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        hint="Text sequence to stop generation" persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- Memory Settings Panel -->
                                                            <v-expansion-panel value="memory" class="panel-item">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4"
                                                                            style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                                                            <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">Memory Settings</div>
                                                                            <div class="panel-subtitle">Configure conversation memory and context</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content">
                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Conversation Memory</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.memoryTurn"
                                                                                        label="Turn Count" type="number" min="0" variant="outlined"
                                                                                        density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-history"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode"
                                                                                        hint="Number of conversation turns to remember"
                                                                                        persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.memoryDuration"
                                                                                        label="Duration (minutes)" type="number" min="0"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-timer-outline"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" hint="How long to keep memory active"
                                                                                        persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Memory Types</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.shortTermMemory"
                                                                                        label="Short-Term Memory" type="number" min="1"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-memory"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" hint="Recent context window size"
                                                                                        persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col>
                                                                                    <!-- Long-Term Memory Switch -->
                                                                                    <div class="switch-option-compact mb-4">
                                                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                            :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.longTermMemory }">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="28"
                                                                                                    :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.longTermMemory ? 'success' : 'grey-lighten-1'"
                                                                                                    class="me-3">
                                                                                                    <v-icon
                                                                                                        :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.longTermMemory ? 'white' : 'grey-darken-1'"
                                                                                                        size="16">mdi-database</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="switch-title">Long-Term Memory</div>
                                                                                                    <div class="switch-description">Persistent storage
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.longTermMemory"
                                                                                                color="success" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                    </div>
                                                                                </v-col>
                                                                            </v-row>

                                                                        </div>

                                                                        <!-- Multi-turn Input Configuration -->
                                                                        <!-- <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Multi-turn Input Configuration</h6>
                                                                            <v-row>
                                                                                <v-col>
                                                                                    <div class="switch-option-compact mb-3">
                                                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                            :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage }">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="28"
                                                                                                    :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage ? 'indigo' : 'grey-lighten-1'"
                                                                                                    class="me-3">
                                                                                                    <v-icon
                                                                                                        :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage ? 'white' : 'grey-darken-1'"
                                                                                                        size="16">mdi-chat-processing</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="switch-title">Enable Multi-Turn Input
                                                                                                    </div>
                                                                                                    <div class="switch-description">Allow continuous
                                                                                                        conversation</div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage"
                                                                                                color="indigo" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                    </div>
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.lenChainMassage"
                                                                                        label="Sequence" type="number" min="1" variant="outlined"
                                                                                        density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-numeric"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" :disabled="!nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage"
                                                                                        hint="Number of conversation turns in sequence" persistent-hint
                                                                                        clearable />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.timeSecondChainMassage"
                                                                                        label="Duration (seconds)" type="number" min="1"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-timer"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                                                        :readonly="isViewMode" :disabled="!nodeInspector.smartIntents[smartIntentIndex].mainAction.isChainMassage"
                                                                                        hint="Time duration for multi-turn session" persistent-hint
                                                                                        clearable />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div> -->


                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- Line Configuration Panel -->
                                                            <v-expansion-panel value="line" class="panel-item">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4"
                                                                            style="background: linear-gradient(135deg, #4CAF50, #388E3C);">
                                                                            <v-icon icon="mdi-message" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">Line Configuration</div>
                                                                            <div class="panel-subtitle">LINE messaging platform settings</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content pa-4">
                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Display Options</h6>

                                                                            <!-- Show Name -->
                                                                            <div class="switch-option-compact mb-3">
                                                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                    :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.showLineName }">
                                                                                    <div class="d-flex align-center">
                                                                                        <v-avatar size="28"
                                                                                            :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.showLineName ? 'info' : 'grey-lighten-1'"
                                                                                            class="me-3">
                                                                                            <v-icon
                                                                                                :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.showLineName ? 'white' : 'grey-darken-1'"
                                                                                                size="16">mdi-account-circle</v-icon>
                                                                                        </v-avatar>
                                                                                        <div>
                                                                                            <div class="switch-title">Show Name</div>
                                                                                            <div class="switch-description">Display user name</div>
                                                                                        </div>
                                                                                    </div>
                                                                                    <v-switch 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.showLineName" 
                                                                                        color="info" hide-details density="compact"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        :readonly="isViewMode" inset />
                                                                                </div>
                                                                            </div>
                                                                            <!-- Loading Indicator -->
                                                                            <div class="switch-option-compact mb-3">
                                                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                    :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBar }">
                                                                                    <div class="d-flex align-center">
                                                                                        <v-avatar size="28"
                                                                                            :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBar ? 'success' : 'grey-lighten-1'"
                                                                                            class="me-3">
                                                                                            <v-icon
                                                                                                :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBar ? 'white' : 'grey-darken-1'"
                                                                                                size="16">mdi-progress-clock</v-icon>
                                                                                        </v-avatar>
                                                                                        <div>
                                                                                            <div class="switch-title">Loading Indicator</div>
                                                                                            <div class="switch-description">Show typing animation</div>
                                                                                        </div>
                                                                                    </div>
                                                                                    <v-switch 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBar" 
                                                                                        color="success" hide-details density="compact"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        :readonly="isViewMode" inset />
                                                                                </div>
                                                                            </div>

                                                                        </div>

                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Loading & Duration</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-select 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBarDuration"
                                                                                        :items="[10, 20, 30, 40, 50, 60]"
                                                                                        label="Loading Duration (seconds)" variant="outlined"
                                                                                        density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-timer-sand"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :disabled="!nodeInspector.smartIntents[smartIntentIndex].mainAction.loadingBar"
                                                                                        :readonly="isViewMode"
                                                                                        hint="Duration for loading animation display" persistent-hint
                                                                                        clearable />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <div class="config-section mb-3">
                                                                            <h6 class="section-label mb-3">Monitoring</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-text-field 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.monitorLineUserId"
                                                                                        label="Monitor Line User ID" variant="outlined"
                                                                                        density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-monitor-eye"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        hint="LINE user ID for monitoring notifications"
                                                                                        persistent-hint />
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-autocomplete 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.monitorSocialID"
                                                                                        :items="listMonitorLineName" label="Monitor Line Name"
                                                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-chat" item-title="name" item-value="id"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        hint="Select monitor line name" persistent-hint clearable />
                                                                                </v-col>
                                                                            </v-row>
                                                                        </div>

                                                                        <div class="config-section">
                                                                            <h6 class="section-label mb-3">Content Filtering</h6>
                                                                            <v-row>
                                                                                <v-col cols="12">
                                                                                    <v-toolbar-title style="font-size: 16px">
                                                                                        Forbidden word
                                                                                    </v-toolbar-title>
                                                                                </v-col>
                                                                            </v-row>
                                                                            <div class="pr-1" style="
                                                                                    border: #dfe0e2 1px solid;
                                                                                    background: #fff;
                                                                                    margin-top: 10px;
                                                                                    border-radius: 8px;
                                                                                ">
                                                                                <v-row v-for="(item, forbiddenIndex) in (nodeInspector.smartIntents[smartIntentIndex].mainAction.forbiddenWords || [''])"
                                                                                    :key="forbiddenIndex" style="margin: 0px">
                                                                                    <v-col cols="12" style="padding: 0px">
                                                                                        <div style="display: flex; align-items: center">
                                                                                            <div style="
                                                                                                    height: 50px;
                                                                                                    background: #f8f8f8;
                                                                                                    padding: 14px 20px 0 20px;
                                                                                                ">
                                                                                                {{ forbiddenIndex + 1 }}
                                                                                            </div>
                                                                                            <v-textarea variant="solo" style="
                                                                                                    overflow: hidden;
                                                                                                    overflow-wrap: break-word;
                                                                                                    resize: none;
                                                                                                    height: 50px;
                                                                                                    width: 98%;
                                                                                                    border-radius: 0px;
                                                                                                    font-family: Fira code, Fira Mono, Consolas,
                                                                                                        Menlo, Courier, monospace;
                                                                                                    font-size: 14px;
                                                                                                "
                                                                                                :model-value="((nodeInspector.smartIntents[smartIntentIndex].mainAction.forbiddenWords || [])[forbiddenIndex]) || ''"
                                                                                                placeholder="Enter word or phrase"
                                                                                                @keypress="isEnterSmartIntentForbidden($event, smartIntentIndex, forbiddenIndex)"
                                                                                                @keyup.delete="checkDeleteSmartIntentForbiddenWord(smartIntentIndex, forbiddenIndex, ((nodeInspector.smartIntents[smartIntentIndex].mainAction.forbiddenWords || [])[forbiddenIndex]) || '')"
                                                                                                @update:model-value="updateSmartIntentForbiddenWordProperty(smartIntentIndex, forbiddenIndex, $event)"
                                                                                                :readonly="isViewMode" />
                                                                                            <v-btn icon="mdi-attachment" variant="text"
                                                                                                v-show="(nodeInspector.smartIntents[smartIntentIndex].mainAction.forbiddenWords || []).length > 1 && forbiddenIndex + 1 < (nodeInspector.smartIntents[smartIntentIndex].mainAction.forbiddenWords || []).length"
                                                                                                @click="removeSmartIntentForbiddenWordDirect(smartIntentIndex, forbiddenIndex)"
                                                                                                :disabled="isViewMode">
                                                                                                <v-tooltip location="top" :open-delay="500">
                                                                                                    <template v-slot:activator="{ props }">
                                                                                                        <v-icon color="error" style="cursor: pointer"
                                                                                                            v-bind="props">
                                                                                                            mdi-delete-forever
                                                                                                        </v-icon>
                                                                                                    </template>
                                                                                                    <span>delete</span>
                                                                                                </v-tooltip>
                                                                                            </v-btn>
                                                                                        </div>
                                                                                    </v-col>
                                                                                </v-row>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- System Prompt Panel -->
                                                            <v-expansion-panel value="prompt" class="panel-item mb-3">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4"
                                                                            style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                                                            <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">System Prompt</div>
                                                                            <div class="panel-subtitle">Define AI behavior and personality</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content">
                                                                        <div class="prompt-section">
                                                                            <!-- Time Context -->
                                                                            <div class="config-section mt-6">
                                                                                <h6 class="section-label mb-3">Time Context</h6>
                                                                                <div class="switch-option-compact mb-3">
                                                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                        :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTime }">
                                                                                        <div class="d-flex align-center">
                                                                                            <v-avatar size="28"
                                                                                                :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTime ? 'success' : 'grey-lighten-1'"
                                                                                                class="me-3">
                                                                                                <v-icon
                                                                                                    :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTime ? 'white' : 'grey-darken-1'"
                                                                                                    size="16">mdi-clock-outline</v-icon>
                                                                                            </v-avatar>
                                                                                            <div>
                                                                                                <div class="switch-title">Time Context</div>
                                                                                                <div class="switch-description">Enable time awareness for AI responses</div>
                                                                                            </div>
                                                                                        </div>
                                                                                        <v-switch 
                                                                                            v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTime" 
                                                                                            color="success" hide-details density="compact"
                                                                                            @update:model-value="updateSmartIntentData()"
                                                                                            :readonly="isViewMode" inset />
                                                                                    </div>
                                                                                </div>

                                                                                <!-- Time Zone Dropdown (shown when enableTime is true) -->
                                                                                <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTime" class="mt-3">
                                                                                    <v-select 
                                                                                        v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.timeZone" 
                                                                                        :items="DDTimeZone" item-title="name" item-value="value" 
                                                                                        label="Time Zone" variant="outlined" density="comfortable" hide-details="auto"
                                                                                        prepend-inner-icon="mdi-earth"
                                                                                        @update:model-value="updateSmartIntentData()"
                                                                                        class="config-input" :readonly="isViewMode"
                                                                                        hint="Select your timezone" persistent-hint
                                                                                        placeholder="Select your timezone">
                                                                                        <template v-slot:selection="{ item }">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-icon small color="primary" class="me-2">mdi-map-marker</v-icon>
                                                                                                <span class="font-weight-medium">{{ item.raw.name }}</span>
                                                                                                <v-chip size="x-small" color="grey-darken-1" text-color="white" class="ml-2">
                                                                                                    {{ item.raw.value && item.raw.value.includes('/') ? item.raw.value.split('/')[1].replace('_', ' ') : item.raw.value }}
                                                                                                </v-chip>
                                                                                            </div>
                                                                                        </template>
                                                                                    </v-select>
                                                                                </div>
                                                                            </div>

                                                                            <!-- TransactionID Context -->
                                                                            <div class="config-section mt-6">
                                                                                <h6 class="section-label mb-3">TransactionID Context</h6>
                                                                                
                                                                                <!-- TransactionID Context Switch -->
                                                                                <div class="switch-option-compact mb-3">
                                                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                        :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTransactionId }">
                                                                                        <div class="d-flex align-center">
                                                                                            <v-avatar size="28"
                                                                                                :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTransactionId ? 'info' : 'grey-lighten-1'"
                                                                                                class="me-3">
                                                                                                <v-icon
                                                                                                    :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTransactionId ? 'white' : 'grey-darken-1'"
                                                                                                    size="16">mdi-identifier</v-icon>
                                                                                            </v-avatar>
                                                                                            <div>
                                                                                                <div class="switch-title">Add TransactionID to Prompt</div>
                                                                                                <div class="switch-description">Include transaction identifier in AI context</div>
                                                                                            </div>
                                                                                        </div>
                                                                                        <v-switch 
                                                                                            v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableTransactionId" 
                                                                                            color="info" hide-details density="compact"
                                                                                            @update:model-value="updateSmartIntentData()"
                                                                                            :readonly="isViewMode" inset />
                                                                                    </div>
                                                                                </div>
                                                                            </div>

                                                                            <!-- Prompt Replacement Flags Section -->
                                                                            <div class="config-section mt-6">
                                                                                <h6 class="section-label mb-3">Prompt Replacement Flags</h6>
                                                                                
                                                                                <!-- InputMessage Replace Flag -->
                                                                                <div class="switch-option-compact mb-3">
                                                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                        :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.enableInputMessageReplace }">
                                                                                        <div class="d-flex align-center">
                                                                                            <v-avatar size="28"
                                                                                                :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableInputMessageReplace ? 'purple' : 'grey-lighten-1'"
                                                                                                class="me-3">
                                                                                                <v-icon
                                                                                                    :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableInputMessageReplace ? 'white' : 'grey-darken-1'"
                                                                                                    size="16">mdi-message-text-outline</v-icon>
                                                                                            </v-avatar>
                                                                                            <div>
                                                                                                <div class="switch-title">InputMessage replacing</div>
                                                                                                <div class="switch-description">Replace tag with input value in prompt</div>
                                                                                            </div>
                                                                                        </div>
                                                                                        <v-switch 
                                                                                            v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableInputMessageReplace" 
                                                                                            color="purple" hide-details density="compact"
                                                                                            @update:model-value="updateSmartIntentData()"
                                                                                            :readonly="isViewMode" inset />
                                                                                    </div>
                                                                                    <!-- Code block with copy button -->
                                                                                    <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableInputMessageReplace" class="code-block-wrapper mt-2 mx-3">
                                                                                        <div class="code-block d-flex align-center justify-space-between">
                                                                                            <code class="code-text">{{InputMessageTagText}}</code>
                                                                                            <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                                                                @click="copyToClipboard(InputMessageTagText)"
                                                                                                class="copy-btn">
                                                                                                <v-icon size="14">mdi-content-copy</v-icon>
                                                                                                <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                                                            </v-btn>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <!-- VoicePrompt Replace Flag -->
                                                                                <div class="switch-option-compact mb-3">
                                                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                        :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.enableVoicePromptReplace }">
                                                                                        <div class="d-flex align-center">
                                                                                            <v-avatar size="28"
                                                                                                :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableVoicePromptReplace ? 'deep-orange' : 'grey-lighten-1'"
                                                                                                class="me-3">
                                                                                                <v-icon
                                                                                                    :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableVoicePromptReplace ? 'white' : 'grey-darken-1'"
                                                                                                    size="16">mdi-microphone-outline</v-icon>
                                                                                            </v-avatar>
                                                                                            <div>
                                                                                                <div class="switch-title">VoicePrompt replacing</div>
                                                                                                <div class="switch-description">Replace tag with TTS prompt in prompt</div>
                                                                                            </div>
                                                                                        </div>
                                                                                        <v-switch 
                                                                                            v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableVoicePromptReplace" 
                                                                                            color="deep-orange" hide-details density="compact"
                                                                                            @update:model-value="updateSmartIntentData()"
                                                                                            :readonly="isViewMode" inset />
                                                                                    </div>
                                                                                    <!-- Code block with copy button -->
                                                                                    <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableVoicePromptReplace" class="code-block-wrapper mt-2 mx-3">
                                                                                        <div class="code-block d-flex align-center justify-space-between">
                                                                                            <code class="code-text">{{VoicePromptTagText}}</code>
                                                                                            <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                                                                @click="copyToClipboard(VoicePromptTagText)"
                                                                                                class="copy-btn">
                                                                                                <v-icon size="14">mdi-content-copy</v-icon>
                                                                                                <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                                                            </v-btn>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <!-- Language Replace Flag -->
                                                                                <div class="switch-option-compact mb-3">
                                                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                                        :class="{ 'switch-active-bg': nodeInspector.smartIntents[smartIntentIndex].mainAction.enableLanguageReplace }">
                                                                                        <div class="d-flex align-center">
                                                                                            <v-avatar size="28"
                                                                                                :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableLanguageReplace ? 'teal' : 'grey-lighten-1'"
                                                                                                class="me-3">
                                                                                                <v-icon
                                                                                                    :color="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableLanguageReplace ? 'white' : 'grey-darken-1'"
                                                                                                    size="16">mdi-translate</v-icon>
                                                                                            </v-avatar>
                                                                                            <div>
                                                                                                <div class="switch-title">Language replacing</div>
                                                                                                <div class="switch-description">Replace tag with detected language in prompt</div>
                                                                                            </div>
                                                                                        </div>
                                                                                        <v-switch 
                                                                                            v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableLanguageReplace" 
                                                                                            color="teal" hide-details density="compact"
                                                                                            @update:model-value="updateSmartIntentData()"
                                                                                            :readonly="isViewMode" inset />
                                                                                    </div>
                                                                                    <!-- Code block with copy button -->
                                                                                    <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableLanguageReplace" class="code-block-wrapper mt-2 mx-3">
                                                                                        <div class="code-block d-flex align-center justify-space-between">
                                                                                            <code class="code-text">{{LanguageTagText}}</code>
                                                                                            <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                                                                @click="copyToClipboard(LanguageTagText)"
                                                                                                class="copy-btn">
                                                                                                <v-icon size="14">mdi-content-copy</v-icon>
                                                                                                <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                                                            </v-btn>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>

                                                                            <h3 class="text-h6 d-flex align-center mb-3" style="font-size: 16px !important;">
                                                                                <v-icon icon="mdi-pencil-outline" size="small"
                                                                                    class="me-2"></v-icon>
                                                                                Configure system message for your AI
                                                                            </h3>

                                                                            <!-- Prompt Template Header -->
                                                                            <div class="mb-2" style="display:flex;flex-direction: column;align-items: flex-end;">
                                                                                <div style="display: flex; align-items: center;">
                                                                                    <v-tooltip location="top">
                                                                                        <template v-slot:activator="{ props }">
                                                                                            <v-icon icon="mdi-information-outline" size="small"
                                                                                                color="grey" class="me-3" v-bind="props"></v-icon>
                                                                                        </template>
                                                                                        <span>Configure additional prompts and templates</span>
                                                                                    </v-tooltip>
                                                                                    <v-btn
                                                                                        v-if="DDGroupPromptTemplate && DDGroupPromptTemplate.length > 0"
                                                                                        variant="outlined" color="primary"
                                                                                        prepend-icon="mdi-playlist-plus" @click="showSmartIntentPromptTemplate(smartIntentIndex)"
                                                                                        class="text-none font-weight-medium" size="small">
                                                                                        Show Templates
                                                                                    </v-btn>
                                                                                </div>
                                                                            </div>

                                                                            <!-- Main editor and templates layout -->
                                                                            <v-row>
                                                                                <!-- Editor area (full width) -->
                                                                                <v-col cols="12" class="prompt-editor-col">
                                                                                    <v-card variant="outlined" class="prompt-editor-card">
                                                                                        <v-card-text class="px-2 py-1">
                                                                                            <v-textarea 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.systemPrompt" 
                                                                                                label="" variant="plain" counter auto-grow rows="25"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode"
                                                                                                placeholder="Enter your system message here..."
                                                                                                class="prompt-textarea"
                                                                                                @contextmenu="onSmartIntentContextMenu(smartIntentIndex, $event)"></v-textarea>
                                                                                        </v-card-text>
                                                                                    </v-card>
                                                                                </v-col>
                                                                            </v-row>

                                                                            <div class="prompt-tips mt-4">
                                                                                <v-card class="tips-card" color="blue-grey-lighten-5" flat>
                                                                                    <v-card-text class="pa-4">
                                                                                        <div class="d-flex align-start">
                                                                                            <v-icon color="info" size="20"
                                                                                                class="me-2 mt-1">mdi-lightbulb-outline</v-icon>
                                                                                            <div>
                                                                                                <div class="text-subtitle-2 font-weight-medium mb-2">
                                                                                                    Tips for
                                                                                                    better prompts:</div>
                                                                                                <ul class="text-body-2 text-medium-emphasis">
                                                                                                    <li>Be specific about the AI's role and expertise
                                                                                                    </li>
                                                                                                    <li>Define the tone and communication style</li>
                                                                                                    <li>Include any restrictions or guidelines</li>
                                                                                                    <li>Specify how to handle unknown information</li>
                                                                                                </ul>
                                                                                            </div>
                                                                                        </div>
                                                                                    </v-card-text>
                                                                                </v-card>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>

                                                            <!-- Knowledge Panel -->
                                                            <v-expansion-panel value="knowledge" class="panel-item mb-3">
                                                                <v-expansion-panel-title class="panel-title-custom">
                                                                    <div class="d-flex align-center py-3">
                                                                        <div class="panel-icon-wrapper me-4"
                                                                            style="background: linear-gradient(135deg, #FF6F00, #E65100);">
                                                                            <v-icon icon="mdi-database" size="20" color="white"></v-icon>
                                                                        </div>
                                                                        <div>
                                                                            <div class="panel-title">Knowledge Sources</div>
                                                                            <div class="panel-subtitle">Configure knowledge integration for context</div>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-title>
                                                                <v-expansion-panel-text class="panel-content-wrapper">
                                                                    <div class="panel-content">
                                                                        <!-- Knowledge Sources Selection -->
                                                                        <div class="config-section mb-4">
                                                                            <h6 class="section-label mb-3">Enable Knowledge Sources</h6>
                                                                            <div class="knowledge-sources-grid">
                                                                                <div class="knowledge-source-card mb-3">
                                                                                    <div class="knowledge-source-frame rounded-lg border">
                                                                                        <div class="knowledge-source-title">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="32" color="blue" class="me-3">
                                                                                                    <v-icon color="white" size="18">mdi-file-document</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="text-subtitle-2 font-weight-medium">Documents</div>
                                                                                                    <div class="text-caption text-medium-emphasis">Vector-based document search</div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableDocuments"
                                                                                                color="blue" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                        <!-- Documents Configuration -->
                                                                                        <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableDocuments" 
                                                                                            class="config-section my-4 knowledge-source-config">
                                                                                            <div class="d-flex align-center mb-3">
                                                                                                <h6 class="section-label mb-0">Documents Configuration</h6>
                                                                                            </div>
                                                                                            <!-- Document Selection -->
                                                                                            <v-autocomplete 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.document" 
                                                                                                :items="DDVector" :no-data-text="kccDocsNoDataText" item-title="displayName" item-value="name" 
                                                                                                label="Document" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" chips multiple 
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input mb-3"
                                                                                                placeholder="Select documents" :clearable="!isViewMode" :readonly="isViewMode">
                                                                                                <template v-slot:chip="{ props, item }">
                                                                                                    <v-chip v-bind="props" :color="isDocumentMissing(item.value) ? 'error' : undefined" :variant="isDocumentMissing(item.value) ? 'flat' : 'tonal'">
                                                                                                        <v-icon v-if="isDocumentMissing(item.value)" icon="mdi-alert-circle" size="16" class="me-1" />
                                                                                                        {{ isDocumentMissing(item.value) ? item.value : item.title }}
                                                                                                    </v-chip>
                                                                                                </template>
                                                                                                <template v-slot:item="{ props, item }">
                                                                                                    <v-list-item v-bind="props" class="document-option">
                                                                                                        <template v-slot:prepend>
                                                                                                            <v-icon size="20" color="primary">mdi-file-document</v-icon>
                                                                                                        </template>
                                                                                                        <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                                                    </v-list-item>
                                                                                                </template>
                                                                                            </v-autocomplete>
                                                                                            
                                                                                            <!-- Top-N Chunks -->
                                                                                            <v-text-field 
                                                                                                v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.topNChunks" 
                                                                                                type="number" label="Top-N Chunks" variant="outlined" 
                                                                                                density="comfortable" hide-details="auto" :readonly="isViewMode" 
                                                                                                min="1" step="1" 
                                                                                                @focus="$event.target.select()"
                                                                                                @blur="validateAndFixSmartIntentTopNChunks($event, smartIntentIndex)"
                                                                                                @keydown.enter="validateAndFixSmartIntentTopNChunks($event, smartIntentIndex)"
                                                                                                class="config-input mb-3" 
                                                                                                hint="Number of document chunks to retrieve" persistent-hint />
                                                                                            
                                                                                            <!-- Cosine Similarity -->
                                                                                            <!-- <v-text-field 
                                                                                                v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.cosineSimilarity" 
                                                                                                type="number" label="Cosine Similarity" variant="outlined" 
                                                                                                density="comfortable" hide-details="auto" :readonly="isViewMode" 
                                                                                                min="0.0001" max="1" step="0.0001"
                                                                                                @focus="$event.target.select()"
                                                                                                @blur="validateAndFixSmartIntentCosineSimilarity($event, smartIntentIndex)"
                                                                                                @keydown.enter="validateAndFixSmartIntentCosineSimilarity($event, smartIntentIndex)"
                                                                                                class="config-input" 
                                                                                                hint="Similarity threshold for document matching" persistent-hint /> -->
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <div class="knowledge-source-card mb-3">
                                                                                    <div class="knowledge-source-frame rounded-lg border">
                                                                                        <div class="knowledge-source-title">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="32" color="green" class="me-3">
                                                                                                    <v-icon color="white" size="18">mdi-database</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="text-subtitle-2 font-weight-medium">Database Integration</div>
                                                                                                    <div class="text-caption text-medium-emphasis">SQL database queries</div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableDatabaseIntegration"
                                                                                                color="green" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                        <!-- Database Integration Configuration -->
                                                                                        <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableDatabaseIntegration" 
                                                                                            class="config-section my-4 knowledge-source-config">
                                                                                            <div class="d-flex align-center mb-3">
                                                                                                <h6 class="section-label mb-0">Database Integration Configuration</h6>
                                                                                            </div>
                                                                                            
                                                                                            <!-- Table Name Selection -->
                                                                                            <v-autocomplete 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.tableName" 
                                                                                                :items="listDataIntegration" item-title="dataName" item-value="id" 
                                                                                                label="Table Name" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" @update:model-value="updateSmartIntentData()" 
                                                                                                class="config-input mb-3" placeholder="Select database table" 
                                                                                                :clearable="!isViewMode" :readonly="isViewMode">
                                                                                                <template v-slot:item="{ props, item }">
                                                                                                    <v-list-item v-bind="props" class="table-option">
                                                                                                        <template v-slot:prepend>
                                                                                                            <v-icon size="20" color="primary">mdi-table</v-icon>
                                                                                                        </template>
                                                                                                        <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                                                                                                        <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                                                    </v-list-item>
                                                                                                </template>
                                                                                            </v-autocomplete>

                                                                                            <!-- SQL Extraction Model Selection -->
                                                                                            <v-autocomplete 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.sqlExtractionModel" 
                                                                                                :items="listSqlExtract" item-title="searchableText" item-value="modelDisplayID" 
                                                                                                label="SQL Extraction Model" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" :clearable="!isViewMode" :readonly="isViewMode"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input mb-3" placeholder="Select AI model for SQL generation">
                                                                                                <template v-slot:item="{ props, item }">
                                                                                                    <v-list-item v-bind="props" class="model-option">
                                                                                                        <template v-slot:prepend>
                                                                                                            <div class="model-icon-small mr-2" :style="{ backgroundColor: item.raw.color }">
                                                                                                                <v-icon size="14" color="green">mdi-robot</v-icon>
                                                                                                            </div>
                                                                                                        </template>
                                                                                                        <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                                                    </v-list-item>
                                                                                                </template>
                                                                                            </v-autocomplete>

                                                                                            <!-- Prompt for No Data -->
                                                                                            <v-textarea 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.promptSqlNoData" 
                                                                                                label="Prompt for No Data" variant="outlined" density="comfortable" 
                                                                                                rows="3" auto-grow hide-details="auto"
                                                                                                placeholder="Enter the message to display when no data is found in the database..."
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input" :readonly="isViewMode" 
                                                                                                hint="Message shown when database query returns no results" persistent-hint />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <div class="knowledge-source-card mb-3">
                                                                                    <div class="knowledge-source-frame rounded-lg border">
                                                                                        <div class="knowledge-source-title">
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="32" color="purple" class="me-3">
                                                                                                    <v-icon color="white" size="18">mdi-package-variant</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="text-subtitle-2 font-weight-medium">Products</div>
                                                                                                    <div class="text-caption text-medium-emphasis">Product catalog search</div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableProducts"
                                                                                                color="purple" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                        <!-- Products Configuration -->
                                                                                        <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableProducts" 
                                                                                            class="config-section my-4 knowledge-source-config">
                                                                                            <div class="d-flex align-center mb-3">
                                                                                                <h6 class="section-label mb-0">Products Configuration</h6>
                                                                                            </div>
                                                                                            
                                                                                            <!-- Product Group List Selection (hidden - use Document Filter Title/SubTitle instead) -->
                                                                                            <div style="display: none;">
                                                                                            <v-autocomplete
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.productGroupList"
                                                                                                :items="DDProductGroup" item-title="name" item-value="id"
                                                                                                label="Product Group List" variant="outlined" density="comfortable"
                                                                                                hide-details="auto" chips multiple return-object
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input mb-3" placeholder="Select product group"
                                                                                                :clearable="!isViewMode" :readonly="isViewMode">
                                                                                                <template v-slot:item="{ props, item }">
                                                                                                    <v-list-item v-bind="props" class="product-group-option">
                                                                                                        <template v-slot:prepend>
                                                                                                            <v-icon size="20" color="primary">mdi-package-variant</v-icon>
                                                                                                        </template>
                                                                                                        <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                                                    </v-list-item>
                                                                                                </template>
                                                                                            </v-autocomplete>
                                                                                            </div>

                                                                                            <!-- Top-N Chunks -->
                                                                                            <v-text-field 
                                                                                                v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.productTopNChunks" 
                                                                                                type="number" label="Top-N Chunks" variant="outlined" 
                                                                                                density="comfortable" hide-details="auto" min="1" step="1"
                                                                                                @focus="$event.target.select()"
                                                                                                @blur="validateAndFixSmartIntentProductTopNChunks($event, smartIntentIndex)"
                                                                                                @keydown.enter="validateAndFixSmartIntentProductTopNChunks($event, smartIntentIndex)"
                                                                                                class="config-input mb-3" placeholder="Enter number of chunks" 
                                                                                                :readonly="isViewMode" 
                                                                                                hint="Number of product chunks to retrieve" persistent-hint />

                                                                                            <!-- Cosine Similarity -->
                                                                                            <v-text-field 
                                                                                                v-model.number="nodeInspector.smartIntents[smartIntentIndex].mainAction.productCosineSimilarity"
                                                                                                type="number" label="Cosine Similarity" variant="outlined" 
                                                                                                density="comfortable" hide-details="auto" min="0.0001" max="1" step="0.0001"
                                                                                                @focus="$event.target.select()"
                                                                                                @blur="validateAndFixSmartIntentProductCosineSimilarity($event, smartIntentIndex)"
                                                                                                @keydown.enter="validateAndFixSmartIntentProductCosineSimilarity($event, smartIntentIndex)"
                                                                                                class="config-input" placeholder="Enter similarity threshold (0.0001 - 1.0)"
                                                                                                :readonly="isViewMode" 
                                                                                                hint="Similarity threshold for product matching" persistent-hint />
                                                                                        </div>
                                                                                    </div>
                                                                                </div>

                                                                                <div class="knowledge-source-card mb-3">
                                                                                    <div class="knowledge-source-frame rounded-lg border">
                                                                                        <div class="knowledge-source-title">    
                                                                                            <div class="d-flex align-center">
                                                                                                <v-avatar size="32" color="orange" class="me-3">
                                                                                                    <v-icon color="white" size="18">mdi-rocket-launch</v-icon>
                                                                                                </v-avatar>
                                                                                                <div>
                                                                                                    <div class="text-subtitle-2 font-weight-medium">API Knowledge</div>
                                                                                                    <div class="text-caption text-medium-emphasis">External API integration</div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <v-switch 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableApiKnowledge"
                                                                                                color="orange" hide-details density="compact"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                :readonly="isViewMode" inset />
                                                                                        </div>
                                                                                        <!-- API Knowledge Configuration -->
                                                                                        <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.enableApiKnowledge" 
                                                                                            class="config-section my-4 knowledge-source-config">
                                                                                            <div class="d-flex align-center mb-3">
                                                                                                <h6 class="section-label mb-0">API Knowledge Configuration</h6>
                                                                                            </div>
                                                                                            
                                                                                            <!-- API URL -->
                                                                                            <v-text-field 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.knowledgeApiUrl" 
                                                                                                label="API URL" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" placeholder="https://api.example.com/knowledge"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input mb-3" :readonly="isViewMode">
                                                                                                <template v-slot:prepend-inner>
                                                                                                    <v-icon size="20" color="primary">mdi-link</v-icon>
                                                                                                </template>
                                                                                            </v-text-field>

                                                                                            <!-- HTTP Method -->
                                                                                            <v-select 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.knowledgeHttpMethod" 
                                                                                                :items="['GET', 'POST']" label="HTTP Method" variant="outlined" 
                                                                                                density="comfortable" hide-details="auto"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input mb-3" :readonly="isViewMode">
                                                                                                <template v-slot:prepend-inner>
                                                                                                    <v-icon size="20" color="primary">mdi-code-brackets</v-icon>
                                                                                                </template>
                                                                                            </v-select>

                                                                                            <!-- Request Headers -->
                                                                                            <div class="headers-section mb-3">
                                                                                                <div class="section-header mb-2">
                                                                                                    <span class="section-title text-subtitle-2 font-weight-medium">Request Headers</span>
                                                                                                    <v-btn v-if="!isViewMode" @click="addSmartIntentKnowledgeAPIHeader(smartIntentIndex)" size="small" color="primary"
                                                                                                        variant="tonal" class="add-btn">
                                                                                                        <v-icon size="16">mdi-plus</v-icon>
                                                                                                        Add Header
                                                                                                    </v-btn>
                                                                                                </div>
                                                                                                <div v-if="nodeInspector.smartIntents[smartIntentIndex].mainAction.listHeaders" class="headers-list">
                                                                                                    <div v-for="(header, headerIndex) in nodeInspector.smartIntents[smartIntentIndex].mainAction.listHeaders" :key="headerIndex"
                                                                                                        class="header-row mb-2">
                                                                                                        <v-row no-gutters>
                                                                                                            <v-col cols="5">
                                                                                                                <v-text-field
                                                                                                                    v-model="header.key"
                                                                                                                    label="Header Name"
                                                                                                                    variant="outlined"
                                                                                                                    density="compact"
                                                                                                                    hide-details="auto"
                                                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                                                    class="header-input"
                                                                                                                    :readonly="isViewMode"
                                                                                                                    placeholder="Content-Type" />
                                                                                                            </v-col>
                                                                                                            <v-col cols="6" class="pl-2">
                                                                                                                <v-text-field
                                                                                                                    v-model="header.value"
                                                                                                                    label="Header Value"
                                                                                                                    variant="outlined"
                                                                                                                    density="compact"
                                                                                                                    hide-details="auto"
                                                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                                                    class="header-input"
                                                                                                                    :readonly="isViewMode"
                                                                                                                    placeholder="application/json" />
                                                                                                            </v-col>
                                                                                                            <v-col cols="1" class="pl-2 d-flex align-center">
                                                                                                                <v-btn
                                                                                                                    v-if="!isViewMode"
                                                                                                                    @click="removeSmartIntentKnowledgeAPIHeader(smartIntentIndex, headerIndex)"
                                                                                                                    icon
                                                                                                                    size="small"
                                                                                                                    variant="text"
                                                                                                                    color="error">
                                                                                                                    <v-icon size="16">mdi-delete</v-icon>
                                                                                                                </v-btn>
                                                                                                            </v-col>
                                                                                                        </v-row>
                                                                                                    </div>
                                                                                                </div>
                                                                                                <div v-else class="text-caption text-medium-emphasis">
                                                                                                    No headers configured
                                                                                                </div>
                                                                                            </div>

                                                                                            <!-- Request Body (for POST requests) -->
                                                                                            <div class="mb-3">
                                                                                                <div class="section-header mb-2">
                                                                                                    <span class="section-title text-subtitle-2 font-weight-medium">Request Body</span>
                                                                                                </div>
                                                                                                <v-textarea
                                                                                                    v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.knowledgeRequestBody"
                                                                                                    label="Request Body (JSON)"
                                                                                                    variant="outlined"
                                                                                                    density="comfortable"
                                                                                                    rows="4"
                                                                                                    auto-grow
                                                                                                    hide-details="auto"
                                                                                                    @update:model-value="updateSmartIntentData()"
                                                                                                    class="config-input"
                                                                                                    :readonly="isViewMode"
                                                                                                    placeholder='{"key": "value"}'
                                                                                                    hint="JSON payload for API request (use {{ variables }} for dynamic values)" 
                                                                                                    persistent-hint>
                                                                                                    <template v-slot:prepend-inner>
                                                                                                        <v-icon size="20" color="primary">mdi-code-json</v-icon>
                                                                                                    </template>
                                                                                                    <template v-slot:append-inner>
                                                                                                        <v-tooltip location="top">
                                                                                                            <template v-slot:activator="{ props }">
                                                                                                                <v-icon v-bind="props" size="small" color="grey-darken-1">
                                                                                                                    mdi-information-outline
                                                                                                                </v-icon>
                                                                                                            </template>
                                                                                                            <span>JSON payload for API request (use {{ variables }} for dynamic values)</span>
                                                                                                        </v-tooltip>
                                                                                                    </template>
                                                                                                </v-textarea>
                                                                                            </div>

                                                                                            <!-- Response Data Path -->
                                                                                            <v-text-field 
                                                                                                v-model="nodeInspector.smartIntents[smartIntentIndex].mainAction.knowledgeResponseDataPath" 
                                                                                                label="Response Data Path" variant="outlined" density="comfortable" 
                                                                                                hide-details="auto" placeholder="data.knowledge"
                                                                                                @update:model-value="updateSmartIntentData()"
                                                                                                class="config-input" :readonly="isViewMode">
                                                                                                <template v-slot:prepend-inner>
                                                                                                    <v-icon size="20" color="primary">mdi-code-json</v-icon>
                                                                                                </template>
                                                                                                <template v-slot:append-inner>
                                                                                                    <v-tooltip location="top">
                                                                                                        <template v-slot:activator="{ props }">
                                                                                                            <v-icon v-bind="props" size="small" color="grey-darken-1">
                                                                                                                mdi-information-outline
                                                                                                            </v-icon>
                                                                                                        </template>
                                                                                                        <span>JSON path to extract knowledge data from API response</span>
                                                                                                    </v-tooltip>
                                                                                                </template>
                                                                                            </v-text-field>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                        <!-- Knowledge Integration Tips -->
                                                                        <div class="knowledge-tips mt-4">
                                                                            <v-card class="tips-card" color="orange-lighten-5" flat>
                                                                                <v-card-text class="pa-4">
                                                                                    <div class="d-flex align-start">
                                                                                        <v-icon color="orange-darken-1" size="20"
                                                                                            class="me-2 mt-1">mdi-lightbulb-outline</v-icon>
                                                                                        <div>
                                                                                            <div class="text-subtitle-2 font-weight-medium mb-2">
                                                                                                Knowledge Integration Tips:</div>
                                                                                            <ul class="text-body-2 text-medium-emphasis">
                                                                                                <li>You can enable multiple knowledge sources simultaneously</li>
                                                                                                <li>Configure similarity thresholds for better content matching</li>
                                                                                                <li>Use multiple documents or product groups for broader coverage</li>
                                                                                                <li>Test API endpoints before deploying in production</li>
                                                                                                <li>Knowledge sources will be queried in order and combined for context</li>
                                                                                            </ul>
                                                                                        </div>
                                                                                    </div>
                                                                                </v-card-text>
                                                                            </v-card>
                                                                        </div>
                                                                    </div>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>
                                                        </v-expansion-panels>
                                                                </v-expansion-panel-text>
                                                            </v-expansion-panel>
                                                        </v-expansion-panels>
                                                    </div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>

                        <!-- Smart Intent Fallback Configuration -->
                        <div class="smart-intent-fallback-section mt-4">
                            <div class="d-flex align-center justify-space-between mb-3">
                                <h6 class="section-title mb-0">Fallback Configuration</h6>
                            </div>
                            
                            <!-- Fallback AI Toggle -->
                            <div class="ai-toggle-section mb-3">
                                <div style="display: flex; align-items: center; justify-content: space-between;">
                                    <span class="text-subtitle-2 font-weight-medium">
                                        <v-icon size="16" class="mr-2" color="orange">mdi-robot</v-icon>
                                        Use AI Model for Fallback
                                    </span>
                                    <v-switch 
                                        v-model="nodeInspector.smartIntentFallback.useAI"
                                        color="orange" density="compact" hide-details inset
                                        @update:model-value="updateSmartIntentFallbackData()"
                                        :readonly="isViewMode"
                                        style="transform: scale(0.8);" />
                                </div>
                            </div>

                            <!-- Fallback AI Configuration (when enabled) -->
                            <div v-if="nodeInspector.smartIntentFallback && nodeInspector.smartIntentFallback.useAI" 
                                 class="ai-config-section">
                                 <div style="display: flex; align-items: center; justify-content: flex-end; gap: 8px;width: 100%;">
                                    <v-btn v-if="!isViewMode && nodeInspector.smartIntentFallback.useAI" 
                                        @click="openSmartIntentLoadFromModelDialog('fallback')" 
                                        color="success" variant="text" size="small"
                                        style="background-color: #e8f5e9;"
                                        prepend-icon="mdi-download">
                                        Load Config
                                    </v-btn>
                                 </div>
                                 
                                <v-divider class="mb-3"></v-divider>
                                
                                <!-- Fallback AI Configuration Expandable Section -->
                                <v-expansion-panels variant="accordion" class="fallback-ai-config-expansion">
                                    <v-expansion-panel>
                                        <v-expansion-panel-title class="fallback-ai-config-title">
                                            <div class="d-flex align-center">
                                                <v-icon icon="mdi-backup-restore" size="20" color="orange" class="me-3"></v-icon>
                                                <div>
                                                    <div class="text-subtitle-1 font-weight-medium">Fallback AI Configuration</div>
                                                    <div class="text-caption text-medium-emphasis">Backup AI settings when primary intent fails</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <!-- Model Configuration Panels (exactly like AI Model Node) -->
                                            <v-expansion-panels multiple class="config-panels elevation-0" variant="accordion">
                                    <!-- Basic Information Panel -->
                                    <v-expansion-panel value="basic" class="panel-item mt-3">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4">
                                                    <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">Basic Information</div>
                                                    <div class="panel-subtitle">Provider, model, and basic settings</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content">
                                                <!-- Provider and Model -->
                                                <div class="config-section mb-6">
                                                    <h6 class="section-label mb-3">Service Configuration</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-autocomplete 
                                                                v-model="nodeInspector.smartIntentFallback.provider"
                                                                :items="listDDProvider" item-title="providerName" item-value="providerName"
                                                                label="Provider*" variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-cloud-outline"
                                                                @update:model-value="updateSmartIntentFallbackProviderChange($event)"
                                                                class="config-input" :readonly="isViewMode"
                                                                hint="Select AI service provider" persistent-hint required />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-autocomplete 
                                                                v-model="nodeInspector.smartIntentFallback.model"
                                                                :items="getModelsByProvider(nodeInspector.smartIntentFallback.provider)"
                                                                item-title="modelDisplay" item-value="modelName"
                                                                label="Model*" variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-brain"
                                                                @update:model-value="updateSmartIntentFallbackModelChange($event)"
                                                                class="config-input" :readonly="isViewMode"
                                                                :disabled="!nodeInspector.smartIntentFallback.provider"
                                                                hint="Select specific model version" persistent-hint required />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <!-- Behavior Settings -->
                                                <div class="config-section">
                                                    <h6 class="section-label mb-3">Behavior Settings</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-select 
                                                                v-model="nodeInspector.smartIntentFallback.gender"
                                                                :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                                item-title="name" item-value="id" label="Gender"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-account"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :readonly="isViewMode"
                                                                hint="Voice/persona gender preference" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.typingSpeed"
                                                                label="Typing Speed" type="number" min="0" variant="outlined"
                                                                density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-keyboard-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                hint="Characters per second (0 = instant)" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.maxInputLength"
                                                                label="Max Input Length" type="number" min="0" max="200000"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-text-box-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="maxNumberpositive($event)"
                                                                class="config-input" :readonly="isViewMode"
                                                                hint="Maximum characters allowed in user input" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field
                                                                v-model.number="nodeInspector.smartIntentFallback.nodeTimeout"
                                                                label="Request Timeout (seconds)" type="number" min="0"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-timer-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                hint="0 = use global timeout setting" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field v-model.number="nodeInspector.smartIntentFallback.nodeMaxRetry"
                                                                label="Max Retry" type="number" min="0"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-refresh"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                hint="0 = use global max retry setting" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- Response Options Panel -->
                                    <v-expansion-panel value="response" class="panel-item">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                                    <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">Response Options</div>
                                                    <div class="panel-subtitle">Configure how responses are generated</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content pa-4">
                                                <!-- Stream Response -->
                                                <div class="switch-option-compact mb-3">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.streamingEnabled }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.smartIntentFallback.streamingEnabled ? 'primary' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.smartIntentFallback.streamingEnabled ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-text-box-outline</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">Stream Response</div>
                                                                <div class="switch-description">Progressive text generation</div>
                                                            </div>
                                                        </div>
                                                        <v-switch 
                                                            v-model="nodeInspector.smartIntentFallback.streamingEnabled" 
                                                            color="primary" hide-details density="compact"
                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                            :readonly="isViewMode" inset />
                                                    </div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>                                    <!-- Memory Settings Panel -->
                                    <v-expansion-panel value="memory" class="panel-item">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                                    <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">Memory Settings</div>
                                                    <div class="panel-subtitle">Configure conversation memory and context</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content">
                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Conversation Memory</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.memoryTurn"
                                                                label="Turn Count" type="number" min="0" variant="outlined"
                                                                density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-history"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                hint="Number of conversation turns to remember" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.memoryDuration"
                                                                label="Duration (minutes)" type="number" min="0"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-timer-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" hint="How long to keep memory active"
                                                                persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Memory Types</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.shortTermMemory"
                                                                label="Short-Term Memory" type="number" min="1"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-memory"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" hint="Recent context window size"
                                                                persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col>
                                                            <!-- Long-Term Memory Switch -->
                                                            <div class="switch-option-compact mb-4">
                                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                    :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.longTermMemory }">
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="28"
                                                                            :color="nodeInspector.smartIntentFallback.longTermMemory ? 'success' : 'grey-lighten-1'"
                                                                            class="me-3">
                                                                            <v-icon
                                                                                :color="nodeInspector.smartIntentFallback.longTermMemory ? 'white' : 'grey-darken-1'"
                                                                                size="16">mdi-database</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="switch-title">Long-Term Memory</div>
                                                                            <div class="switch-description">Persistent storage</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.longTermMemory"
                                                                        color="success" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                            </div>
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <!-- Multi-turn Input Configuration -->
                                                <!-- <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Multi-turn Input Configuration</h6>
                                                    <v-row>
                                                        <v-col>
                                                            <div class="switch-option-compact mb-3">
                                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                    :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.isChainMassage }">
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="28"
                                                                            :color="nodeInspector.smartIntentFallback.isChainMassage ? 'indigo' : 'grey-lighten-1'"
                                                                            class="me-3">
                                                                            <v-icon
                                                                                :color="nodeInspector.smartIntentFallback.isChainMassage ? 'white' : 'grey-darken-1'"
                                                                                size="16">mdi-chat-processing</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="switch-title">Enable Multi-Turn Input</div>
                                                                            <div class="switch-description">Allow continuous conversation</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.isChainMassage"
                                                                        color="indigo" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                            </div>
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.lenChainMassage"
                                                                label="Sequence" type="number" min="1" variant="outlined"
                                                                density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-numeric"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" :disabled="!nodeInspector.smartIntentFallback.isChainMassage"
                                                                hint="Number of conversation turns in sequence" persistent-hint
                                                                clearable />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.timeSecondChainMassage"
                                                                label="Duration (seconds)" type="number" min="1"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-timer"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" :disabled="!nodeInspector.smartIntentFallback.isChainMassage"
                                                                hint="Time duration for multi-turn session" persistent-hint
                                                                clearable />
                                                        </v-col>
                                                    </v-row>
                                                </div> -->
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- Parameters Panel -->
                                    <v-expansion-panel value="parameters" class="panel-item">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                                    <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">AI Parameters</div>
                                                    <div class="panel-subtitle">Fine-tune model behavior and output</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content">
                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Core Parameters</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.temperature"
                                                                label="Temperature*" type="number" step="0.1" min="0" max="1"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-thermometer"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                hint="Creativity level (0 = focused, 1 = creative)" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.topP" 
                                                                label="Top P" type="number" step="0.1" min="0" max="1" 
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-filter-variant"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                hint="Token selection probability threshold" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Penalty Settings</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.frequencyPenalty"
                                                                label="Frequency Penalty" type="number" step="0.1" min="0" max="2" 
                                                                variant="outlined" density="comfortable" hide-details="auto" 
                                                                prepend-inner-icon="mdi-repeat-off"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode"
                                                                hint="Reduce repetition of frequent tokens" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.presencePenalty"
                                                                label="Presence Penalty" type="number" step="0.1" min="0" max="2" 
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-minus-circle-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" hint="Reduce repetition of any tokens"
                                                                persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <div class="config-section">
                                                    <h6 class="section-label mb-3">Output Control</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model.number="nodeInspector.smartIntentFallback.maxTokens"
                                                                label="Max Tokens Response" type="number" min="1"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-text-box-check-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                @keypress="isNumberpositive($event)" class="config-input"
                                                                :readonly="isViewMode" hint="Maximum response length in tokens"
                                                                persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model="nodeInspector.smartIntentFallback.stopSequence"
                                                                label="Stop Sequence" variant="outlined" density="comfortable"
                                                                hide-details="auto" prepend-inner-icon="mdi-stop-circle-outline"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :readonly="isViewMode"
                                                                hint="Text sequence to stop generation" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- Line Configuration Panel -->
                                    <v-expansion-panel value="line" class="panel-item">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #4CAF50, #388E3C);">
                                                    <v-icon icon="mdi-message" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">Line Configuration</div>
                                                    <div class="panel-subtitle">LINE messaging platform settings</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content pa-4">
                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Display Options</h6>

                                                    <!-- Show Name -->
                                                    <div class="switch-option-compact mb-3">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.showLineName }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.smartIntentFallback.showLineName ? 'info' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.smartIntentFallback.showLineName ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-account-circle</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">Show Name</div>
                                                                    <div class="switch-description">Display user name</div>
                                                                </div>
                                                            </div>
                                                            <v-switch 
                                                                v-model="nodeInspector.smartIntentFallback.showLineName" 
                                                                color="info" hide-details density="compact"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>

                                                    <!-- Loading Indicator -->
                                                    <div class="switch-option-compact mb-3">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.loadingBar }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.smartIntentFallback.loadingBar ? 'success' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.smartIntentFallback.loadingBar ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-progress-clock</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">Loading Indicator</div>
                                                                    <div class="switch-description">Show typing animation</div>
                                                                </div>
                                                            </div>
                                                            <v-switch 
                                                                v-model="nodeInspector.smartIntentFallback.loadingBar" 
                                                                color="success" hide-details density="compact"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Loading & Duration</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-select 
                                                                v-model="nodeInspector.smartIntentFallback.loadingBarDuration"
                                                                :items="[10, 20, 30, 40, 50, 60]"
                                                                label="Loading Duration (seconds)" variant="outlined"
                                                                density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-timer-sand"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :disabled="!nodeInspector.smartIntentFallback.loadingBar"
                                                                :readonly="isViewMode"
                                                                hint="Duration for loading animation display" persistent-hint
                                                                clearable />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <div class="config-section mb-3">
                                                    <h6 class="section-label mb-3">Monitoring</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-text-field 
                                                                v-model="nodeInspector.smartIntentFallback.monitorLineUserId"
                                                                label="Monitor Line User ID" variant="outlined"
                                                                density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-monitor-eye"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :readonly="isViewMode"
                                                                hint="LINE user ID for monitoring notifications" persistent-hint />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-autocomplete 
                                                                v-model="nodeInspector.smartIntentFallback.monitorSocialID"
                                                                :items="listMonitorLineName" label="Monitor Line Name"
                                                                variant="outlined" density="comfortable" hide-details="auto"
                                                                prepend-inner-icon="mdi-chat" item-title="name" item-value="id"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :readonly="isViewMode"
                                                                hint="Select monitor line name" persistent-hint clearable />
                                                        </v-col>
                                                    </v-row>
                                                </div>

                                                <div class="config-section">
                                                    <h6 class="section-label mb-3">Content Filtering</h6>
                                                    <v-row>
                                                        <v-col cols="12">
                                                            <v-toolbar-title style="font-size: 16px">
                                                                Forbidden word
                                                            </v-toolbar-title>
                                                        </v-col>
                                                    </v-row>
                                                    <div class="pr-1" style="
                                                            border: #dfe0e2 1px solid;
                                                            background: #fff;
                                                            margin-top: 10px;
                                                            border-radius: 8px;
                                                        ">
                                                        <v-row v-for="(item, index) in nodeInspector.smartIntentFallback.forbiddenWords || ['']"
                                                            :key="`fallback-forbidden-${index}`" style="margin: 0px">
                                                            <v-col cols="12" style="padding: 0px">
                                                                <div style="display: flex; align-items: center">
                                                                    <div style="
                                                                            height: 50px;
                                                                            background: #f8f8f8;
                                                                            padding: 14px 20px 0 20px;
                                                                        ">
                                                                        {{ index + 1 }}
                                                                    </div>
                                                                    <v-textarea variant="solo" style="
                                                                            overflow: hidden;
                                                                            overflow-wrap: break-word;
                                                                            resize: none;
                                                                            height: 50px;
                                                                            width: 98%;
                                                                            border-radius: 0px;
                                                                            font-family: Fira code, Fira Mono, Consolas,
                                                                                Menlo, Courier, monospace;
                                                                            font-size: 14px;
                                                                        "
                                                                        :model-value="(nodeInspector.smartIntentFallback.forbiddenWords && nodeInspector.smartIntentFallback.forbiddenWords[index]) || ''"
                                                                        placeholder="Enter word or phrase"
                                                                        @keypress="isEnterSmartIntentFallbackForbidden($event, index)"
                                                                        @keyup.delete="checkDeleteSmartIntentFallbackForbiddenWord(index, (nodeInspector.smartIntentFallback.forbiddenWords && nodeInspector.smartIntentFallback.forbiddenWords[index]) || '')"
                                                                        @update:model-value="updateSmartIntentFallbackForbiddenWord(index, $event)"
                                                                        :readonly="isViewMode" />
                                                                    <v-btn icon="mdi-attachment" variant="text"
                                                                        v-show="(nodeInspector.smartIntentFallback.forbiddenWords || []).length > 1 && index + 1 < (nodeInspector.smartIntentFallback.forbiddenWords || []).length"
                                                                        @click="removeSmartIntentFallbackForbiddenWord(index)"
                                                                        :disabled="isViewMode">
                                                                        <v-tooltip location="top" :open-delay="500">
                                                                            <template v-slot:activator="{ props }">
                                                                                <v-icon color="error" style="cursor: pointer"
                                                                                    v-bind="props">
                                                                                    mdi-delete-forever
                                                                                </v-icon>
                                                                            </template>
                                                                            <span>delete</span>
                                                                        </v-tooltip>
                                                                    </v-btn>
                                                                </div>
                                                            </v-col>
                                                        </v-row>
                                                    </div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- System Prompt Panel -->
                                    <v-expansion-panel value="prompt" class="panel-item">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                                    <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">System Prompt</div>
                                                    <div class="panel-subtitle">Define AI behavior and personality</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content">
                                                <div class="prompt-section">
                                                    <div class="d-flex align-center mb-4">
                                                        <v-avatar size="36" color="info" class="me-3">
                                                            <v-icon color="white" size="20">mdi-robot-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <h6 class="text-h6 mb-1">AI Personality & Instructions</h6>
                                                            <p class="text-body-2 text-medium-emphasis mb-0">
                                                                Define how your AI should behave, respond, and interact with users
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <!-- Time Context -->
                                                    <div class="config-section mt-6">
                                                        <h6 class="section-label mb-3">Time Context</h6>
                                                        <div class="switch-option-compact mb-3">
                                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.enableTime }">
                                                                <div class="d-flex align-center">
                                                                    <v-avatar size="28"
                                                                        :color="nodeInspector.smartIntentFallback.enableTime ? 'success' : 'grey-lighten-1'"
                                                                        class="me-3">
                                                                        <v-icon
                                                                            :color="nodeInspector.smartIntentFallback.enableTime ? 'white' : 'grey-darken-1'"
                                                                            size="16">mdi-clock-outline</v-icon>
                                                                    </v-avatar>
                                                                    <div>
                                                                        <div class="switch-title">Time Context</div>
                                                                        <div class="switch-description">Enable time awareness for AI responses</div>
                                                                    </div>
                                                                </div>
                                                                <v-switch 
                                                                    v-model="nodeInspector.smartIntentFallback.enableTime" 
                                                                    color="success" hide-details density="compact"
                                                                    @update:model-value="updateSmartIntentFallbackData()"
                                                                    :readonly="isViewMode" inset />
                                                            </div>
                                                        </div>

                                                        <!-- Time Zone Dropdown (shown when enableTime is true) -->
                                                        <div v-if="nodeInspector.smartIntentFallback.enableTime" class="mt-3">
                                                            <v-select 
                                                                v-model="nodeInspector.smartIntentFallback.timeZone" 
                                                                :items="DDTimeZone" item-title="name" item-value="value" 
                                                                label="Time Zone" variant="outlined" density="comfortable" 
                                                                hide-details="auto" prepend-inner-icon="mdi-earth"
                                                                @update:model-value="updateSmartIntentFallbackData()"
                                                                class="config-input" :readonly="isViewMode"
                                                                hint="Select your timezone" persistent-hint
                                                                placeholder="Select your timezone">
                                                                <template v-slot:selection="{ item }">
                                                                    <div class="d-flex align-center">
                                                                        <v-icon small color="primary" class="me-2">mdi-map-marker</v-icon>
                                                                        <span class="font-weight-medium">{{ item.raw.name }}</span>
                                                                        <v-chip size="x-small" color="grey-darken-1" text-color="white" class="ml-2">
                                                                            {{ item.raw.value && item.raw.value.includes('/') ? item.raw.value.split('/')[1].replace('_', ' ') : item.raw.value }}
                                                                        </v-chip>
                                                                    </div>
                                                                </template>
                                                            </v-select>
                                                        </div>
                                                    </div>

                                                    <!-- TransactionID Context -->
                                                    <div class="config-section mt-6">
                                                        <h6 class="section-label mb-3">TransactionID Context</h6>
                                                        
                                                        <!-- TransactionID Context Switch -->
                                                        <div class="switch-option-compact mb-3">
                                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.enableTransactionId }">
                                                                <div class="d-flex align-center">
                                                                    <v-avatar size="28"
                                                                        :color="nodeInspector.smartIntentFallback.enableTransactionId ? 'info' : 'grey-lighten-1'"
                                                                        class="me-3">
                                                                        <v-icon
                                                                            :color="nodeInspector.smartIntentFallback.enableTransactionId ? 'white' : 'grey-darken-1'"
                                                                            size="16">mdi-identifier</v-icon>
                                                                    </v-avatar>
                                                                    <div>
                                                                        <div class="switch-title">Add TransactionID to Prompt</div>
                                                                        <div class="switch-description">Include transaction identifier in AI context</div>
                                                                    </div>
                                                                </div>
                                                                <v-switch 
                                                                    v-model="nodeInspector.smartIntentFallback.enableTransactionId" 
                                                                    color="info" hide-details density="compact"
                                                                    @update:model-value="updateSmartIntentFallbackData()"
                                                                    :readonly="isViewMode" inset />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <!-- Prompt Replacement Flags Section -->
                                                    <div class="config-section mt-6">
                                                        <h6 class="section-label mb-3">Prompt Replacement Flags</h6>
                                                        
                                                        <!-- InputMessage Replace Flag -->
                                                        <div class="switch-option-compact mb-3">
                                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.enableInputMessageReplace }">
                                                                <div class="d-flex align-center">
                                                                    <v-avatar size="28"
                                                                        :color="nodeInspector.smartIntentFallback.enableInputMessageReplace ? 'purple' : 'grey-lighten-1'"
                                                                        class="me-3">
                                                                        <v-icon
                                                                            :color="nodeInspector.smartIntentFallback.enableInputMessageReplace ? 'white' : 'grey-darken-1'"
                                                                            size="16">mdi-message-text-outline</v-icon>
                                                                    </v-avatar>
                                                                    <div>
                                                                        <div class="switch-title">InputMessage replacing</div>
                                                                        <div class="switch-description">Replace tag with input value in prompt</div>
                                                                    </div>
                                                                </div>
                                                                <v-switch 
                                                                    v-model="nodeInspector.smartIntentFallback.enableInputMessageReplace" 
                                                                    color="purple" hide-details density="compact"
                                                                    @update:model-value="updateSmartIntentFallbackData()"
                                                                    :readonly="isViewMode" inset />
                                                            </div>
                                                            <!-- Code block with copy button -->
                                                            <div v-if="nodeInspector.smartIntentFallback.enableInputMessageReplace" class="code-block-wrapper mt-2 mx-3">
                                                                <div class="code-block d-flex align-center justify-space-between">
                                                                    <code class="code-text">{{InputMessageTagText}}</code>
                                                                    <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                                        @click="copyToClipboard(InputMessageTagText)"
                                                                        class="copy-btn">
                                                                        <v-icon size="14">mdi-content-copy</v-icon>
                                                                        <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                                    </v-btn>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <!-- VoicePrompt Replace Flag -->
                                                        <div class="switch-option-compact mb-3">
                                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.enableVoicePromptReplace }">
                                                                <div class="d-flex align-center">
                                                                    <v-avatar size="28"
                                                                        :color="nodeInspector.smartIntentFallback.enableVoicePromptReplace ? 'deep-orange' : 'grey-lighten-1'"
                                                                        class="me-3">
                                                                        <v-icon
                                                                            :color="nodeInspector.smartIntentFallback.enableVoicePromptReplace ? 'white' : 'grey-darken-1'"
                                                                            size="16">mdi-microphone-outline</v-icon>
                                                                    </v-avatar>
                                                                    <div>
                                                                        <div class="switch-title">VoicePrompt replacing</div>
                                                                        <div class="switch-description">Replace tag with TTS prompt in prompt</div>
                                                                    </div>
                                                                </div>
                                                                <v-switch 
                                                                    v-model="nodeInspector.smartIntentFallback.enableVoicePromptReplace" 
                                                                    color="deep-orange" hide-details density="compact"
                                                                    @update:model-value="updateSmartIntentFallbackData()"
                                                                    :readonly="isViewMode" inset />
                                                            </div>
                                                            <!-- Code block with copy button -->
                                                            <div v-if="nodeInspector.smartIntentFallback.enableVoicePromptReplace" class="code-block-wrapper mt-2 mx-3">
                                                                <div class="code-block d-flex align-center justify-space-between">
                                                                    <code class="code-text">{{VoicePromptTagText}}</code>
                                                                    <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                                        @click="copyToClipboard(VoicePromptTagText)"
                                                                        class="copy-btn">
                                                                        <v-icon size="14">mdi-content-copy</v-icon>
                                                                        <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                                    </v-btn>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <!-- Language Replace Flag -->
                                                        <div class="switch-option-compact mb-3">
                                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                :class="{ 'switch-active-bg': nodeInspector.smartIntentFallback.enableLanguageReplace }">
                                                                <div class="d-flex align-center">
                                                                    <v-avatar size="28"
                                                                        :color="nodeInspector.smartIntentFallback.enableLanguageReplace ? 'teal' : 'grey-lighten-1'"
                                                                        class="me-3">
                                                                        <v-icon
                                                                            :color="nodeInspector.smartIntentFallback.enableLanguageReplace ? 'white' : 'grey-darken-1'"
                                                                            size="16">mdi-translate</v-icon>
                                                                    </v-avatar>
                                                                    <div>
                                                                        <div class="switch-title">Language replacing</div>
                                                                        <div class="switch-description">Replace tag with detected language in prompt</div>
                                                                    </div>
                                                                </div>
                                                                <v-switch 
                                                                    v-model="nodeInspector.smartIntentFallback.enableLanguageReplace" 
                                                                    color="teal" hide-details density="compact"
                                                                    @update:model-value="updateSmartIntentFallbackData()"
                                                                    :readonly="isViewMode" inset />
                                                            </div>
                                                            <!-- Code block with copy button -->
                                                            <div v-if="nodeInspector.smartIntentFallback.enableLanguageReplace" class="code-block-wrapper mt-2 mx-3">
                                                                <div class="code-block d-flex align-center justify-space-between">
                                                                    <code class="code-text">{{LanguageTagText}}</code>
                                                                    <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                                        @click="copyToClipboard(LanguageTagText)"
                                                                        class="copy-btn">
                                                                        <v-icon size="14">mdi-content-copy</v-icon>
                                                                        <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                                    </v-btn>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <h3 class="text-h6 d-flex align-center mb-3" style="font-size: 16px !important;">
                                                        <v-icon icon="mdi-pencil-outline" size="small"
                                                            class="me-2"></v-icon>
                                                        Configure system message for your AI
                                                    </h3>

                                                    <!-- Prompt Template Header for Fallback -->
                                                    <div class="mb-2" style="display:flex;flex-direction: column;align-items: flex-end;">
                                                        <div style="display: flex; align-items: center;">
                                                            <v-tooltip location="top">
                                                                <template v-slot:activator="{ props }">
                                                                    <v-icon icon="mdi-information-outline" size="small"
                                                                        color="grey" class="me-3" v-bind="props"></v-icon>
                                                                </template>
                                                                <span>Configure additional prompts and templates</span>
                                                            </v-tooltip>
                                                            <v-btn
                                                                v-if="DDGroupPromptTemplate && DDGroupPromptTemplate.length > 0"
                                                                variant="outlined" color="orange"
                                                                prepend-icon="mdi-playlist-plus" @click="showSmartIntentFallbackPromptTemplate()"
                                                                class="text-none font-weight-medium" size="small">
                                                                Show Templates
                                                            </v-btn>
                                                        </div>
                                                    </div>

                                                    <!-- Main editor -->
                                                    <v-row>
                                                        <v-col cols="12" class="prompt-editor-col">
                                                            <v-card variant="outlined" class="prompt-editor-card">
                                                                <v-card-text class="px-2 py-1">
                                                                    <v-textarea 
                                                                        v-model="nodeInspector.smartIntentFallback.systemPrompt" 
                                                                        label="" variant="plain" counter auto-grow rows="10"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode"
                                                                        placeholder="Enter your system message here..."
                                                                        class="prompt-textarea" />
                                                                </v-card-text>
                                                            </v-card>
                                                        </v-col>
                                                    </v-row>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>

                                    <!-- Knowledge Panel for Fallback -->
                                    <v-expansion-panel value="knowledge" class="panel-item mb-3">
                                        <v-expansion-panel-title class="panel-title-custom">
                                            <div class="d-flex align-center py-3">
                                                <div class="panel-icon-wrapper me-4"
                                                    style="background: linear-gradient(135deg, #FF6F00, #E65100);">
                                                    <v-icon icon="mdi-database" size="20" color="white"></v-icon>
                                                </div>
                                                <div>
                                                    <div class="panel-title">Knowledge Sources</div>
                                                    <div class="panel-subtitle">Configure knowledge integration for fallback</div>
                                                </div>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text class="panel-content-wrapper">
                                            <div class="panel-content">
                                                <!-- Knowledge Sources Selection -->
                                                <div class="config-section mb-4">
                                                    <h6 class="section-label mb-3">Enable Knowledge Sources</h6>
                                                    <div class="knowledge-sources-grid">
                                                        <div class="knowledge-source-card mb-3">
                                                            <div class="knowledge-source-frame rounded-lg border">
                                                                <div class="knowledge-source-title">    
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="32" color="blue" class="me-3">
                                                                            <v-icon color="white" size="18">mdi-file-document</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="text-subtitle-2 font-weight-medium">Documents</div>
                                                                            <div class="text-caption text-medium-emphasis">Vector-based document search</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.enableDocuments"
                                                                        color="blue" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                                <!-- Documents Configuration -->
                                                                <div v-if="nodeInspector.smartIntentFallback.enableDocuments" 
                                                                    class="config-section mb-4 knowledge-source-config">
                                                                    <div class="d-flex align-center mb-3">
                                                                        <h6 class="section-label mb-0">Documents Configuration</h6>
                                                                    </div>
                                                                    <!-- Document Selection -->
                                                                    <v-autocomplete 
                                                                        v-model="nodeInspector.smartIntentFallback.document" 
                                                                        :items="DDVector" :no-data-text="kccDocsNoDataText" item-title="displayName" item-value="name" 
                                                                        label="Document" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" chips multiple 
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input mb-3"
                                                                        placeholder="Select documents" :clearable="!isViewMode" :readonly="isViewMode">
                                                                        <template v-slot:chip="{ props, item }">
                                                                            <v-chip v-bind="props" :color="isDocumentMissing(item.value) ? 'error' : undefined" :variant="isDocumentMissing(item.value) ? 'flat' : 'tonal'">
                                                                                <v-icon v-if="isDocumentMissing(item.value)" icon="mdi-alert-circle" size="16" class="me-1" />
                                                                                {{ isDocumentMissing(item.value) ? item.value : item.title }}
                                                                            </v-chip>
                                                                        </template>
                                                                        <template v-slot:item="{ props, item }">
                                                                            <v-list-item v-bind="props" class="document-option">
                                                                                <template v-slot:prepend>
                                                                                    <v-icon size="20" color="primary">mdi-file-document</v-icon>
                                                                                </template>
                                                                                <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                            </v-list-item>
                                                                        </template>
                                                                    </v-autocomplete>
                                                                    
                                                                    <!-- Top-N Chunks -->
                                                                    <v-text-field 
                                                                        v-model.number="nodeInspector.smartIntentFallback.topNChunks" 
                                                                        type="number" label="Top-N Chunks" variant="outlined" 
                                                                        density="comfortable" hide-details="auto" :readonly="isViewMode" 
                                                                        min="1" step="1" 
                                                                        @focus="$event.target.select()"
                                                                        @blur="validateAndFixSmartIntentFallbackTopNChunks($event)"
                                                                        @keydown.enter="validateAndFixSmartIntentFallbackTopNChunks($event)"
                                                                        class="config-input mb-3" 
                                                                        hint="Number of document chunks to retrieve" persistent-hint />
                                                                    
                                                                    <!-- Cosine Similarity -->
                                                                    <v-text-field 
                                                                        v-model.number="nodeInspector.smartIntentFallback.cosineSimilarity" 
                                                                        type="number" label="Cosine Similarity" variant="outlined" 
                                                                        density="comfortable" hide-details="auto" :readonly="isViewMode" 
                                                                        min="0.0001" max="1" step="0.0001"
                                                                        @focus="$event.target.select()"
                                                                        @blur="validateAndFixSmartIntentFallbackCosineSimilarity($event)"
                                                                        @keydown.enter="validateAndFixSmartIntentFallbackCosineSimilarity($event)"
                                                                        class="config-input" 
                                                                        hint="Similarity threshold for document matching" persistent-hint />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div class="knowledge-source-card mb-3">
                                                            <div class="knowledge-source-frame rounded-lg border">
                                                                <div class="knowledge-source-title">
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="32" color="green" class="me-3">
                                                                            <v-icon color="white" size="18">mdi-database</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="text-subtitle-2 font-weight-medium">Database Integration</div>
                                                                            <div class="text-caption text-medium-emphasis">SQL database queries</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.enableDatabaseIntegration"
                                                                        color="green" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                                <!-- Database Integration Configuration -->
                                                                <div v-if="nodeInspector.smartIntentFallback.enableDatabaseIntegration" 
                                                                    class="config-section mb-4 knowledge-source-config">
                                                                    <div class="d-flex align-center mb-3">
                                                                        <h6 class="section-label mb-0">Database Integration Configuration</h6>
                                                                    </div>
                                                                    
                                                                    <!-- Table Name Selection -->
                                                                    <v-autocomplete 
                                                                        v-model="nodeInspector.smartIntentFallback.tableName" 
                                                                        :items="listDataIntegration" item-title="dataName" item-value="id" 
                                                                        label="Table Name" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" @update:model-value="updateSmartIntentFallbackData()" 
                                                                        class="config-input mb-3" placeholder="Select database table" 
                                                                        :clearable="!isViewMode" :readonly="isViewMode">
                                                                        <template v-slot:item="{ props, item }">
                                                                            <v-list-item v-bind="props" class="table-option">
                                                                                <template v-slot:prepend>
                                                                                    <v-icon size="20" color="primary">mdi-table</v-icon>
                                                                                </template>
                                                                                <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                                                                                <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                            </v-list-item>
                                                                        </template>
                                                                    </v-autocomplete>

                                                                    <!-- SQL Extraction Model Selection -->
                                                                    <v-autocomplete 
                                                                        v-model="nodeInspector.smartIntentFallback.sqlExtractionModel" 
                                                                        :items="listSqlExtract" item-title="searchableText" item-value="modelDisplayID" 
                                                                        label="SQL Extraction Model" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" :clearable="!isViewMode" :readonly="isViewMode"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input mb-3" placeholder="Select AI model for SQL generation">
                                                                        <template v-slot:item="{ props, item }">
                                                                            <v-list-item v-bind="props" class="model-option">
                                                                                <template v-slot:prepend>
                                                                                    <div class="model-icon-small mr-2" :style="{ backgroundColor: item.raw.color }">
                                                                                        <v-icon size="14" color="green">mdi-robot</v-icon>
                                                                                    </div>
                                                                                </template>
                                                                                <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                            </v-list-item>
                                                                        </template>
                                                                    </v-autocomplete>

                                                                    <!-- Prompt for No Data -->
                                                                    <v-textarea 
                                                                        v-model="nodeInspector.smartIntentFallback.promptSqlNoData" 
                                                                        label="Prompt for No Data" variant="outlined" density="comfortable" 
                                                                        rows="3" auto-grow hide-details="auto"
                                                                        placeholder="Enter the message to display when no data is found in the database..."
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input" :readonly="isViewMode" 
                                                                        hint="Message shown when database query returns no results" persistent-hint />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div class="knowledge-source-card mb-3">
                                                            <div class="knowledge-source-frame rounded-lg border">
                                                                <div class="knowledge-source-title">
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="32" color="purple" class="me-3">
                                                                            <v-icon color="white" size="18">mdi-package-variant</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="text-subtitle-2 font-weight-medium">Products</div>
                                                                            <div class="text-caption text-medium-emphasis">Product catalog search</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.enableProducts"
                                                                        color="purple" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                                <!-- Products Configuration -->
                                                                <div v-if="nodeInspector.smartIntentFallback.enableProducts" 
                                                                    class="config-section mb-4 knowledge-source-config">
                                                                    <div class="d-flex align-center mb-3">
                                                                        <h6 class="section-label mb-0">Products Configuration</h6>
                                                                    </div>
                                                                    
                                                                    <!-- Product Group List Selection (hidden - use Document Filter Title/SubTitle instead) -->
                                                                    <div style="display: none;">
                                                                    <v-autocomplete
                                                                        v-model="nodeInspector.smartIntentFallback.productGroupList"
                                                                        :items="DDProductGroup" item-title="name" item-value="id"
                                                                        label="Product Group List" variant="outlined" density="comfortable"
                                                                        hide-details="auto" chips multiple return-object
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input mb-3" placeholder="Select product group"
                                                                        :clearable="!isViewMode" :readonly="isViewMode">
                                                                        <template v-slot:item="{ props, item }">
                                                                            <v-list-item v-bind="props" class="product-group-option">
                                                                                <template v-slot:prepend>
                                                                                    <v-icon size="20" color="primary">mdi-package-variant</v-icon>
                                                                                </template>
                                                                                <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                                            </v-list-item>
                                                                        </template>
                                                                    </v-autocomplete>
                                                                    </div>

                                                                    <!-- Top-N Chunks -->
                                                                    <v-text-field 
                                                                        v-model.number="nodeInspector.smartIntentFallback.productTopNChunks" 
                                                                        type="number" label="Top-N Chunks" variant="outlined" 
                                                                        density="comfortable" hide-details="auto" min="1" step="1"
                                                                        @focus="$event.target.select()"
                                                                        @blur="validateAndFixSmartIntentFallbackProductTopNChunks($event)"
                                                                        @keydown.enter="validateAndFixSmartIntentFallbackProductTopNChunks($event)"
                                                                        class="config-input mb-3" placeholder="Enter number of chunks" 
                                                                        :readonly="isViewMode" 
                                                                        hint="Number of product chunks to retrieve" persistent-hint />

                                                                    <!-- Cosine Similarity -->
                                                                    <v-text-field 
                                                                        v-model.number="nodeInspector.smartIntentFallback.productCosineSimilarity"
                                                                        type="number" label="Cosine Similarity" variant="outlined" 
                                                                        density="comfortable" hide-details="auto" min="0.0001" max="1" step="0.0001"
                                                                        @focus="$event.target.select()"
                                                                        @blur="validateAndFixSmartIntentFallbackProductCosineSimilarity($event)"
                                                                        @keydown.enter="validateAndFixSmartIntentFallbackProductCosineSimilarity($event)"
                                                                        class="config-input" placeholder="Enter similarity threshold (0.0001 - 1.0)"
                                                                        :readonly="isViewMode" 
                                                                        hint="Similarity threshold for product matching" persistent-hint />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div class="knowledge-source-card mb-3">
                                                            <div class="knowledge-source-frame rounded-lg border">
                                                                <div class="knowledge-source-title">
                                                                    <div class="d-flex align-center">
                                                                        <v-avatar size="32" color="orange" class="me-3">
                                                                            <v-icon color="white" size="18">mdi-rocket-launch</v-icon>
                                                                        </v-avatar>
                                                                        <div>
                                                                            <div class="text-subtitle-2 font-weight-medium">API Knowledge</div>
                                                                            <div class="text-caption text-medium-emphasis">External API integration</div>
                                                                        </div>
                                                                    </div>
                                                                    <v-switch 
                                                                        v-model="nodeInspector.smartIntentFallback.enableApiKnowledge"
                                                                        color="orange" hide-details density="compact"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        :readonly="isViewMode" inset />
                                                                </div>
                                                                <!-- API Knowledge Configuration -->
                                                                <div v-if="nodeInspector.smartIntentFallback.enableApiKnowledge" 
                                                                    class="config-section mb-4 knowledge-source-config">
                                                                    <div class="d-flex align-center mb-3">
                                                                        <h6 class="section-label mb-0">API Knowledge Configuration</h6>
                                                                    </div>
                                                                    
                                                                    <!-- API URL -->
                                                                    <v-text-field 
                                                                        v-model="nodeInspector.smartIntentFallback.knowledgeApiUrl" 
                                                                        label="API URL" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" placeholder="https://api.example.com/knowledge"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input mb-3" :readonly="isViewMode">
                                                                        <template v-slot:prepend-inner>
                                                                            <v-icon size="20" color="primary">mdi-link</v-icon>
                                                                        </template>
                                                                    </v-text-field>

                                                                    <!-- HTTP Method -->
                                                                    <v-select 
                                                                        v-model="nodeInspector.smartIntentFallback.knowledgeHttpMethod" 
                                                                        :items="['GET', 'POST']" label="HTTP Method" variant="outlined" 
                                                                        density="comfortable" hide-details="auto"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input mb-3" :readonly="isViewMode">
                                                                        <template v-slot:prepend-inner>
                                                                            <v-icon size="20" color="primary">mdi-code-brackets</v-icon>
                                                                        </template>
                                                                    </v-select>

                                                                    <!-- Request Headers -->
                                                                    <div class="headers-section mb-3">
                                                                        <div class="section-header mb-2">
                                                                            <span class="section-title text-subtitle-2 font-weight-medium">Request Headers</span>
                                                                            <v-btn v-if="!isViewMode" @click="addSmartIntentFallbackKnowledgeAPIHeader()" size="small" color="primary"
                                                                                variant="tonal" class="add-btn">
                                                                                <v-icon size="16">mdi-plus</v-icon>
                                                                                Add Header
                                                                            </v-btn>
                                                                        </div>
                                                                        <div v-if="nodeInspector.smartIntentFallback.listHeaders" class="headers-list">
                                                                            <div v-for="(header, headerIndex) in nodeInspector.smartIntentFallback.listHeaders" :key="headerIndex"
                                                                                class="header-row mb-2">
                                                                                <v-row no-gutters>
                                                                                    <v-col cols="5">
                                                                                        <v-text-field
                                                                                            v-model="header.key"
                                                                                            label="Header Name"
                                                                                            variant="outlined"
                                                                                            density="compact"
                                                                                            hide-details="auto"
                                                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                                                            class="header-input"
                                                                                            :readonly="isViewMode"
                                                                                            placeholder="Content-Type" />
                                                                                    </v-col>
                                                                                    <v-col cols="6" class="pl-2">
                                                                                        <v-text-field
                                                                                            v-model="header.value"
                                                                                            label="Header Value"
                                                                                            variant="outlined"
                                                                                            density="compact"
                                                                                            hide-details="auto"
                                                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                                                            class="header-input"
                                                                                            :readonly="isViewMode"
                                                                                            placeholder="application/json" />
                                                                                    </v-col>
                                                                                    <v-col cols="1" class="pl-2 d-flex align-center">
                                                                                        <v-btn
                                                                                            v-if="!isViewMode"
                                                                                            @click="removeSmartIntentFallbackKnowledgeAPIHeader(headerIndex)"
                                                                                            icon
                                                                                            size="small"
                                                                                            variant="text"
                                                                                            color="error">
                                                                                            <v-icon size="16">mdi-delete</v-icon>
                                                                                        </v-btn>
                                                                                    </v-col>
                                                                                </v-row>
                                                                            </div>
                                                                        </div>
                                                                        <div v-else class="text-caption text-medium-emphasis">
                                                                            No headers configured
                                                                        </div>
                                                                    </div>

                                                                    <!-- Request Body -->
                                                                    <div class="mb-3">
                                                                        <div class="section-header mb-2">
                                                                            <span class="section-title text-subtitle-2 font-weight-medium">Request Body</span>
                                                                        </div>
                                                                        <v-textarea
                                                                            v-model="nodeInspector.smartIntentFallback.knowledgeRequestBody"
                                                                            label="Request Body (JSON)"
                                                                            variant="outlined"
                                                                            density="comfortable"
                                                                            rows="4"
                                                                            auto-grow
                                                                            hide-details="auto"
                                                                            @update:model-value="updateSmartIntentFallbackData()"
                                                                            class="config-input"
                                                                            :readonly="isViewMode"
                                                                            placeholder='{"query": "{{userMessage}}", "limit": 10}'
                                                                            hint="JSON payload for API request (use {{ variables }} for dynamic values)" 
                                                                            persistent-hint>
                                                                            <template v-slot:prepend-inner>
                                                                                <v-icon size="20" color="primary">mdi-code-json</v-icon>
                                                                            </template>
                                                                            <template v-slot:append-inner>
                                                                                <v-tooltip location="top">
                                                                                    <template v-slot:activator="{ props }">
                                                                                        <v-icon v-bind="props" size="small" color="grey-darken-1">
                                                                                            mdi-information-outline
                                                                                        </v-icon>
                                                                                    </template>
                                                                                    <span>JSON payload for API request (use {{ variables }} for dynamic values)</span>
                                                                                </v-tooltip>
                                                                            </template>
                                                                        </v-textarea>
                                                                    </div>

                                                                    <!-- Response Data Path -->
                                                                    <v-text-field 
                                                                        v-model="nodeInspector.smartIntentFallback.knowledgeResponseDataPath" 
                                                                        label="Response Data Path" variant="outlined" density="comfortable" 
                                                                        hide-details="auto" placeholder="data.knowledge"
                                                                        @update:model-value="updateSmartIntentFallbackData()"
                                                                        class="config-input" :readonly="isViewMode">
                                                                        <template v-slot:prepend-inner>
                                                                            <v-icon size="20" color="primary">mdi-code-json</v-icon>
                                                                        </template>
                                                                        <template v-slot:append-inner>
                                                                            <v-tooltip location="top">
                                                                                <template v-slot:activator="{ props }">
                                                                                    <v-icon v-bind="props" size="small" color="grey-darken-1">
                                                                                        mdi-information-outline
                                                                                    </v-icon>
                                                                                </template>
                                                                                <span>JSON path to extract knowledge data from API response</span>
                                                                            </v-tooltip>
                                                                        </template>
                                                                    </v-text-field>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>





                                                <!-- Knowledge Integration Tips -->
                                                <div class="knowledge-tips mt-4">
                                                    <v-card class="tips-card" color="orange-lighten-5" flat>
                                                        <v-card-text class="pa-4">
                                                            <div class="d-flex align-start">
                                                                <v-icon color="orange-darken-1" size="20"
                                                                    class="me-2 mt-1">mdi-lightbulb-outline</v-icon>
                                                                <div>
                                                                    <div class="text-subtitle-2 font-weight-medium mb-2">
                                                                        Fallback Knowledge Integration Tips:</div>
                                                                    <ul class="text-body-2 text-medium-emphasis">
                                                                        <li>Fallback knowledge is used when no Smart Intent matches</li>
                                                                        <li>Configure broader knowledge sources for better coverage</li>
                                                                        <li>Consider using general documents or FAQ data</li>
                                                                        <li>Test fallback responses to ensure helpful answers</li>
                                                                    </ul>
                                                                </div>
                                                            </div>
                                                        </v-card-text>
                                                    </v-card>
                                                </div>
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>
                        <!-- Smart Intent Prompt Template Dialog -->
                        <v-dialog v-model="smartIntentPromptTemplateDialog" max-width="800" scrollable>
                            <v-card>
                                <v-card-title class="bg-secondary text-white d-flex align-center">
                                    <v-icon icon="mdi-brain" class="me-2"></v-icon>
                                    Smart Intent Prompt Templates
                                    <v-spacer></v-spacer>
                                    <v-btn icon="mdi-close" variant="text" color="white" size="small" 
                                        @click="smartIntentPromptTemplateDialog = false"></v-btn>
                                </v-card-title>
                                
                                <v-card-text class="pa-4">
                                    <!-- Template Group Selection -->
                                    <v-autocomplete 
                                        label="Select Template Group" 
                                        :items="DDGroupPromptTemplate" 
                                        item-title="name"
                                        item-value="id" 
                                        density="compact" 
                                        clearable
                                        variant="outlined" 
                                        v-model="smartIntentGroupPromptTemplate"
                                        prepend-inner-icon="mdi-folder-outline" 
                                        hide-details
                                        class="mb-4">
                                    </v-autocomplete>
                                    <!-- Search Template -->
                                    <v-text-field 
                                        v-model="smartIntentTemplateSearchQuery"
                                        label="Search templates" 
                                        prepend-inner-icon="mdi-magnify"
                                        variant="outlined" 
                                        density="compact" 
                                        hide-details
                                        class="mb-4" 
                                        clearable>
                                    </v-text-field>
                                    <!-- Templates List -->
                                    <div style="max-height: 400px; overflow-y: auto;">
                                        <div v-if="smartIntentSearchedTemplates.length === 0" 
                                            class="pa-4 text-center text-grey-darken-1">
                                            <v-icon icon="mdi-playlist-remove" size="large" 
                                                class="d-block mx-auto mb-2"></v-icon>
                                            <span>No templates found</span>
                                        </div>
                                        <v-list v-else density="compact" class="pa-0">
                                            <v-list-item 
                                                v-for="(item, i) in smartIntentSearchedTemplates" 
                                                :key="i"
                                                @click="setSelectSmartIntentPromptTemplate(item)"
                                                class="template-list-item ma-1" 
                                                :ripple="true"
                                                rounded>
                                                <template v-slot:prepend>
                                                    <v-icon size="small" color="secondary" 
                                                        icon="mdi-brain"></v-icon>
                                                </template>
                                                <template v-slot:title>
                                                    <span class="font-weight-medium">{{ item.name }}</span>
                                                </template>
                                                <template v-slot:subtitle>
                                                    <span class="text-caption text-grey">
                                                        {{ truncatePrompt(item.prompt) }}
                                                    </span>
                                                </template>
                                                <template v-slot:append>
                                                    <v-btn icon="mdi-plus" size="small" variant="text" 
                                                        color="secondary" @click.stop="setSelectSmartIntentPromptTemplate(item)">
                                                    </v-btn>
                                                </template>
                                            </v-list-item>
                                        </v-list>
                                    </div>
                                </v-card-text>
                                
                                <v-card-actions>
                                    <v-spacer></v-spacer>
                                    <v-btn color="secondary" variant="text" @click="smartIntentPromptTemplateDialog = false">
                                        Close
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>

                        <!-- Smart Intent Fallback Prompt Template Dialog -->
                        <v-dialog v-model="smartIntentFallbackPromptTemplateDialog" max-width="800" scrollable>
                            <v-card>
                                <v-card-title class="bg-orange text-white d-flex align-center">
                                    <v-icon icon="mdi-shield-alert" class="me-2"></v-icon>
                                    Fallback Prompt Templates
                                    <v-spacer></v-spacer>
                                    <v-btn icon="mdi-close" variant="text" color="white" size="small" 
                                        @click="smartIntentFallbackPromptTemplateDialog = false"></v-btn>
                                </v-card-title>
                                
                                <v-card-text class="pa-4">
                                    <!-- Template Group Selection -->
                                    <v-autocomplete 
                                        label="Select Template Group" 
                                        :items="DDGroupPromptTemplate" 
                                        item-title="name"
                                        item-value="id" 
                                        density="compact" 
                                        clearable
                                        variant="outlined" 
                                        v-model="smartIntentFallbackGroupPromptTemplate"
                                        prepend-inner-icon="mdi-folder-outline" 
                                        hide-details
                                        class="mb-4">
                                    </v-autocomplete>

                                    <!-- Search Template -->
                                    <v-text-field 
                                        v-model="smartIntentFallbackTemplateSearchQuery"
                                        label="Search templates" 
                                        prepend-inner-icon="mdi-magnify"
                                        variant="outlined" 
                                        density="compact" 
                                        hide-details
                                        class="mb-4" 
                                        clearable>
                                    </v-text-field>

                                    <!-- Templates List -->
                                    <div style="max-height: 400px; overflow-y: auto;">
                                        <div v-if="smartIntentFallbackSearchedTemplates.length === 0" 
                                            class="pa-4 text-center text-grey-darken-1">
                                            <v-icon icon="mdi-playlist-remove" size="large" 
                                                class="d-block mx-auto mb-2"></v-icon>
                                            <span>No templates found</span>
                                        </div>

                                        <v-list v-else density="compact" class="pa-0">
                                            <v-list-item 
                                                v-for="(item, i) in smartIntentFallbackSearchedTemplates" 
                                                :key="i"
                                                @click="setSelectSmartIntentFallbackPromptTemplate(item)"
                                                class="template-list-item ma-1" 
                                                :ripple="true"
                                                rounded>
                                                <template v-slot:prepend>
                                                    <v-icon size="small" color="orange" 
                                                        icon="mdi-shield-alert"></v-icon>
                                                </template>
                                                <template v-slot:title>
                                                    <span class="font-weight-medium">{{ item.name }}</span>
                                                </template>
                                                <template v-slot:subtitle>
                                                    <span class="text-caption text-grey">
                                                        {{ truncatePrompt(item.prompt) }}
                                                    </span>
                                                </template>
                                                <template v-slot:append>
                                                    <v-btn icon="mdi-plus" size="small" variant="text" 
                                                        color="orange" @click.stop="setSelectSmartIntentFallbackPromptTemplate(item)">
                                                    </v-btn>
                                                </template>
                                            </v-list-item>
                                        </v-list>
                                    </div>
                                </v-card-text>
                                
                                <v-card-actions>
                                    <v-spacer></v-spacer>
                                    <v-btn color="orange" variant="text" @click="smartIntentFallbackPromptTemplateDialog = false">
                                        Close
                                    </v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>
                    </div>

                    <div v-else-if="selectedNode.data.type === 'apiTrigger'" class="form-section">
                        <h6 class="section-title">API Configuration</h6>
                        <v-text-field v-model="nodeInspector.apiUrl" label="API URL" variant="outlined"
                            density="compact" hide-details="auto" :readonly="isViewMode"
                            @update:model-value="updateNodeProperty('apiUrl', $event)" class="modern-input mb-3" />
                        <v-select v-model="nodeInspector.httpMethod" :items="['GET', 'POST', 'PUT', 'DELETE']"
                            label="HTTP Method" variant="outlined" density="compact" hide-details="auto"
                            :readonly="isViewMode" @update:model-value="updateNodeProperty('httpMethod', $event)"
                            class="modern-input mb-3" />
                        
                        <!-- JSON Validation -->
                        <h6 class="section-title mt-4">JSON Request Body</h6>
                        <v-autocomplete v-model="nodeInspector.modelJsonValidator" :items="uniqueAiModelOptions"
                            item-title="name" item-value="value" label="JSON Validator Model" variant="outlined" density="compact"
                            hide-details="auto" :readonly="isViewMode" :clearable="!isViewMode"
                            @update:model-value="updateNodeProperty('modelJsonValidator', $event)"
                            class="modern-input mb-3">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="model-option"
                                    :title="item.raw.value + ': ' + item.raw.name">
                                    <template v-slot:prepend>
                                        <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                            <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                        </div>
                                    </template>
                                </v-list-item>
                            </template>
                            <template v-slot:selection="{ item }">
                                <v-chip variant="text" size="medium"
                                    :text="item.raw.value + ': ' + item.raw.name"></v-chip>
                            </template>
                        </v-autocomplete>

                        <!-- LHB Service Configuration -->
                        <div class="lhb-service-toggle-section mb-3">
                            <v-card variant="outlined" class="pa-3" color="orange-lighten-5" style="border-radius: 12px;">
                                <div class="d-flex align-center">
                                    <v-avatar size="24" color="deep-orange" class="me-3">
                                        <v-icon color="white" size="12">mdi-lock-check</v-icon>
                                    </v-avatar>
                                    <div class="flex-grow-1 me-3">
                                        <div class="text-subtitle-2"><span style="color:#FF6F00;font-weight: 500;">Use LHB Service</span></div>
                                        <div class="text-caption text-medium-emphasis">Encrypt request body JSON and decrypt response</div>
                                    </div>
                                    <v-switch v-model="nodeInspector.useLHBService" color="deep-orange"
                                        hide-details density="compact"
                                        @update:model-value="updateNodeProperty('useLHBService', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                            </v-card>
                        </div>

                        <!-- JSON Editor with Variables - Updated Version -->
                        <div class="json-editor-container mb-3">
                            <!-- Variables Panel Header -->
                            <div class="json-variables-header" v-if="!isViewMode">
                                <div class="variables-header-content">
                                    <div class="variables-title">
                                        <!-- <v-icon size="16" class="mr-2">mdi-variable</v-icon> -->
                                        <span class="text-subtitle-2">Json</span>
                                        <v-chip size="x-small" variant="tonal" color="primary" class="ml-2">
                                            {{ filteredVariables.length }} available variables
                                        </v-chip>
                                    </div>

                                    <!-- Category Filter -->
                                    <v-menu offset-y>
                                        <template v-slot:activator="{ props }">
                                            <v-btn v-bind="props" variant="text" size="small"
                                                class="category-filter-btn"
                                                :class="{ 'filter-active': selectedVariableCategory !== 'All' }">
                                                <v-icon size="14">mdi-filter-variant</v-icon>
                                                <span class="ml-1">{{ selectedVariableCategory }}</span>
                                                <v-icon size="12" class="ml-1">mdi-chevron-down</v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list density="compact" class="category-menu">
                                            <v-list-item v-for="category in variableCategories" :key="category"
                                                @click="selectedVariableCategory = category"
                                                :class="{ 'v-list-item--active': selectedVariableCategory === category }">
                                                <v-list-item-title>{{ category }}</v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>

                                    <!-- Expand/Collapse Button -->
                                    <v-btn icon variant="text" size="small"
                                        @click="variablesPanelExpanded = !variablesPanelExpanded" class="expand-btn">
                                        <v-icon size="16">
                                            {{ variablesPanelExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                        </v-icon>
                                    </v-btn>
                                </div>
                            </div>

                            <!-- Variables Grid - Collapsible -->
                            <v-expand-transition>
                                <div v-show="variablesPanelExpanded" class="json-variables-content">
                                    <!-- Quick Insert Buttons -->
                                    <div class="variables-quick-grid">
                                        <v-tooltip v-for="variable in filteredVariables" :key="variable.key"
                                            :text="variable.description" location="top">
                                            <template v-slot:activator="{ props }">
                                                <v-chip v-bind="props" class="variable-chip-compact"
                                                    @dragstart="onDragStart($event, variable.key)"
                                                    @click="insertVariable(variable.key)" draggable="true"
                                                    :color="getCategoryColor(variable.category)" variant="tonal"
                                                    size="small">
                                                    <v-icon start size="12">{{ variable.icon }}</v-icon>
                                                    <span class="variable-label">{{ variable.label }}</span>
                                                    <v-icon end size="10" class="drag-handle">mdi-drag-vertical</v-icon>
                                                </v-chip>
                                            </template>
                                        </v-tooltip>
                                    </div>
                                </div>
                            </v-expand-transition>
                            <!-- JSON Textarea -->
                            <div class="json-textarea-container">
                                <v-textarea v-model="nodeInspector.jsonValidate" label="JSON Validation Schema"
                                    variant="outlined" density="compact" rows="6" auto-grow :readonly="isViewMode"
                                    hide-details="auto" placeholder='{ "Name": "Value" }'
                                    @update:model-value="updateNodeProperty('jsonValidate', $event)"
                                    class="modern-json-editor" @drop.stop.prevent="onJsonDrop($event)"
                                    @dragover.stop.prevent="onJsonDragOver($event)"
                                    @dragleave.stop="onJsonDragLeave($event)" ref="jsonTextarea" />

                                <!-- Insert Helper -->
                                <div class="insert-helper" v-if="!isViewMode">
                                    <v-icon size="12" class="mr-1">mdi-information-outline</v-icon>
                                    <span class="helper-text">
                                        Click variables to insert or drag & drop into editor
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Headers Configuration -->
                        <h6 class="section-title mt-4">Headers Configuration</h6>
                        <div v-if="nodeInspector.listHeadersAPITrigger" class="headers-section">
                            <div v-for="(header, headerIndex) in nodeInspector.listHeadersAPITrigger" :key="headerIndex"
                                class="header-row mb-3">
                                <div class="d-flex gap-2">
                                    <v-text-field v-model="header.key" label="Key" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateHeaderProperty(headerIndex, 'key', $event)"
                                        class="modern-input" />
                                    <v-text-field v-model="header.value" label="Value" variant="outlined"
                                        density="compact" hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateHeaderProperty(headerIndex, 'value', $event)"
                                        class="modern-input" />
                                    <v-btn icon size="small" color="error" variant="text" v-if="!isViewMode"
                                        @click="deleteHeader(headerIndex)">
                                        <v-icon size="16">mdi-delete</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                            <v-btn size="small" color="primary" variant="tonal" @click="addHeader" v-if="!isViewMode"
                                class="add-btn">
                                <v-icon left size="16">mdi-plus</v-icon>
                                Add Header
                            </v-btn>
                        </div>

                        <!-- Validation Configuration -->
                        <h6 class="section-title mt-4">Validation Configuration</h6>
                        <div v-if="nodeInspector.listValidation" class="validation-section">
                            <div v-for="(validation, validationIndex) in nodeInspector.listValidation"
                                :key="validationIndex" class="validation-row mb-3">
                                <div class="d-flex gap-2 mb-2">
                                    <v-text-field v-model="validation.key" label="Key" variant="outlined"
                                        density="compact" hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateValidationProperty(validationIndex, 'key', $event)"
                                        class="modern-input" />
                                    <v-select v-model="validation.typeValidation" :items="DDTypeValidation"
                                        item-title="name" item-value="value" label="Validation Type" variant="outlined"
                                        density="compact" hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateValidationProperty(validationIndex, 'typeValidation', $event)"
                                        class="modern-input" />
                                    <v-btn icon size="small" color="error" variant="text" v-if="!isViewMode"
                                        @click="deleteValidation(validationIndex)">
                                        <v-icon size="16">mdi-delete</v-icon>
                                    </v-btn>
                                </div>
                                <div class="d-flex gap-2 mb-2">
                                    <v-text-field v-if="validation.typeValidation === 'MCV'"
                                        v-model="validation.numberOfCharacter" label="Number of Characters"
                                        type="number" variant="outlined" density="compact" hide-details="auto"
                                        :readonly="isViewMode"
                                        @update:model-value="updateValidationProperty(validationIndex, 'numberOfCharacter', $event)"
                                        @keypress="isNumberpositive($event)" class="modern-input" />
                                    <v-text-field v-if="validation.typeValidation === 'RV'" v-model="validation.regExp"
                                        label="Regular Expression" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateValidationProperty(validationIndex, 'regExp', $event)"
                                        class="modern-input" />
                                </div>
                                <v-textarea v-model="validation.promptMessage" label="Error Message" variant="outlined"
                                    density="compact" rows="2" auto-grow hide-details="auto"
                                    placeholder="Error message to display when validation fails" :readonly="isViewMode"
                                    @update:model-value="updateValidationProperty(validationIndex, 'promptMessage', $event)"
                                    class="modern-input" />
                            </div>
                            <v-btn size="small" color="primary" variant="tonal" @click="addValidation"
                                v-if="!isViewMode" class="add-btn">
                                <v-icon left size="16">mdi-plus</v-icon>
                                Add Validation
                            </v-btn>
                        </div>

                        <!-- Text Response -->
                        <h6 class="section-title mt-4">Text Response</h6>
                        <v-textarea v-model="nodeInspector.textResponse" label="Response Message" variant="outlined"
                            density="compact" rows="3" auto-grow hide-details="auto"
                            placeholder="Enter the response message..." :readonly="isViewMode"
                            @update:model-value="updateNodeProperty('textResponse', $event)"
                            class="modern-input mb-3" />
                    </div>

                    <div v-else-if="selectedNode.data.type === 'contactAgent' || selectedNode.data.type === 'contactAgentV2'" class="form-section">
                        <h6 class="section-title">Configuration</h6>

                        <!-- Contact Agent Message - Thai -->
                        <v-textarea v-model="nodeInspector.contactAgentMessage" label="Thai Message *"
                            variant="outlined" density="compact" rows="3" auto-grow hide-details="auto"
                            placeholder="กรุณากรอกข้อความสำหรับส่งต่อไปยังเจ้าหน้าที่..."
                            @update:model-value="updateNodeProperty('contactAgentMessage', $event)"
                            :readonly="isViewMode" class="modern-input mb-3" required />

                        <!-- Contact Agent Message - Chinese -->
                        <v-textarea v-model="nodeInspector.contactAgentMessageChinese" label="Chinese Message *"
                            variant="outlined" density="compact" rows="3" auto-grow hide-details="auto"
                            placeholder="请输入转接至客服的消息..."
                            @update:model-value="updateNodeProperty('contactAgentMessageChinese', $event)"
                            :readonly="isViewMode" class="modern-input mb-3" required />

                        <!-- Contact Agent Message - English -->
                        <v-textarea v-model="nodeInspector.contactAgentMessageEnglish" label="English Message *"
                            variant="outlined" density="compact" rows="3" auto-grow hide-details="auto"
                            placeholder="Enter the message for agent handoff..."
                            @update:model-value="updateNodeProperty('contactAgentMessageEnglish', $event)"
                            :readonly="isViewMode" class="modern-input mb-3" required />

                        <!-- Bot Disable Time -->
                        <div class="time-field-container mb-3">
                            <v-text-field v-model="nodeInspector.botDisableTime" label="Bot Disable Time (minutes)"
                                variant="outlined" density="compact" hide-details="auto"
                                placeholder="Enter time in minutes" type="number" min="1"
                                @update:model-value="validateAndUpdateBotDisableTime($event)"
                                @keypress="isNumberpositive($event)" :readonly="isViewMode" class="modern-input">
                                <template v-slot:append-inner>
                                    <v-tooltip location="top" max-width="300px">
                                        <template v-slot:activator="{ props }">
                                            <v-icon size="small" color="grey-darken-1" class="info-icon" v-bind="props">
                                                mdi-information-outline
                                            </v-icon>
                                        </template>
                                        <span>Specify the duration for bot deactivation (in minutes) when transferring
                                            to an agent. Minimum value is 1 minute. If
                                            left blank, the bot will remain active.</span>
                                    </v-tooltip>
                                </template>
                            </v-text-field>
                        </div>
                    </div>

                    <!-- Email Node Configuration -->
                    <div v-else-if="selectedNode.data.type === 'email'" class="form-section">
                        <h6 class="section-title">Email Configuration</h6>
                        
                        <!-- Email Provider Selection -->
                        <v-select 
                            disabled
                            v-model="nodeInspector.emailProvider" 
                            :items="[
                                { title: 'MailGun', value: 'mailgun' },
                                { title: 'SMTP', value: 'smtp' }
                            ]" 
                            item-title="title" 
                            item-value="value"
                            label="Email Provider" 
                            variant="outlined" 
                            density="compact" 
                            hide-details="auto"
                            @update:model-value="updateNodeProperty('emailProvider', $event)"
                            class="modern-input mb-3" 
                            :readonly="isViewMode" />

                        <!-- Recipient Configuration -->
                        <h6 class="section-title mt-4">Recipient Configuration</h6>
                        
                        <!-- TO Email -->
                        <v-text-field 
                            v-model="nodeInspector.toEmail" 
                            label="To Email *" 
                            variant="outlined"
                            density="compact" 
                            hide-details="auto" 
                            placeholder="recipient@example.com"
                            @update:model-value="updateNodeProperty('toEmail', $event)" 
                            class="modern-input mb-3"
                            :readonly="isViewMode"
                            required>
                            <template v-slot:prepend-inner>
                                <v-icon size="small" color="primary">mdi-email</v-icon>
                            </template>
                        </v-text-field>

                        <!-- CC Emails -->
                        <div class="email-list-section mb-3">
                            <div class="d-flex align-center mb-2">
                                <span class="text-subtitle-2 font-weight-medium">CC Emails (Optional)</span>
                                <v-btn v-if="!isViewMode" size="x-small" color="primary" variant="tonal"
                                    @click="addEmailToList('ccEmails')" class="ml-auto">
                                    <v-icon size="12">mdi-plus</v-icon>
                                </v-btn>
                            </div>
                            <div v-for="(email, emailIndex) in nodeInspector.ccEmails || []" :key="emailIndex" 
                                class="d-flex align-center mb-2">
                                <v-text-field 
                                    v-model="nodeInspector.ccEmails[emailIndex]"
                                    placeholder="cc@example.com" 
                                    variant="outlined"
                                    density="compact" 
                                    hide-details="auto"
                                    @update:model-value="updateEmailList('ccEmails')"
                                    class="modern-input flex-grow-1 mr-2"
                                    :readonly="isViewMode" />
                                <v-btn v-if="!isViewMode" icon size="x-small" color="error" variant="text"
                                    @click="removeEmailFromList('ccEmails', emailIndex)">
                                    <v-icon size="14">mdi-close</v-icon>
                                </v-btn>
                            </div>
                        </div>

                        <!-- BCC Emails -->
                        <div class="email-list-section mb-3">
                            <div class="d-flex align-center mb-2">
                                <span class="text-subtitle-2 font-weight-medium">BCC Emails (Optional)</span>
                                <v-btn v-if="!isViewMode" size="x-small" color="primary" variant="tonal"
                                    @click="addEmailToList('bccEmails')" class="ml-auto">
                                    <v-icon size="12">mdi-plus</v-icon>
                                </v-btn>
                            </div>
                            <div v-for="(email, emailIndex) in nodeInspector.bccEmails || []" :key="emailIndex" 
                                class="d-flex align-center mb-2">
                                <v-text-field 
                                    v-model="nodeInspector.bccEmails[emailIndex]"
                                    placeholder="bcc@example.com" 
                                    variant="outlined"
                                    density="compact" 
                                    hide-details="auto"
                                    @update:model-value="updateEmailList('bccEmails')"
                                    class="modern-input flex-grow-1 mr-2"
                                    :readonly="isViewMode" />
                                <v-btn v-if="!isViewMode" icon size="x-small" color="error" variant="text"
                                    @click="removeEmailFromList('bccEmails', emailIndex)">
                                    <v-icon size="14">mdi-close</v-icon>
                                </v-btn>
                            </div>
                        </div>

                        <!-- Email Content -->
                        <h6 class="section-title mt-4">Email Content</h6>
                        
                        <!-- Subject -->
                        <v-text-field 
                            v-model="nodeInspector.subject" 
                            label="Subject" 
                            variant="outlined"
                            density="compact" 
                            hide-details="auto" 
                            placeholder="Email subject"
                            @update:model-value="updateNodeProperty('subject', $event)" 
                            class="modern-input mb-3"
                            :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="small" color="primary">mdi-format-title</v-icon>
                            </template>
                        </v-text-field>

                        <!-- Email Template Selection -->
                        <v-select 
                            v-model="nodeInspector.emailTemplate" 
                            :items="[
                                { title: 'Custom (Manual Content)', value: 'custom' },
                                { title: 'Registration', value: 'register' },
                                { title: 'Reset Password', value: 'resetPassword' }
                            ]" 
                            item-title="title" 
                            item-value="value"
                            label="Email Template" 
                            variant="outlined" 
                            density="compact" 
                            hide-details="auto"
                            @update:model-value="updateNodeProperty('emailTemplate', $event)"
                            class="modern-input mb-3" 
                            :readonly="isViewMode"  style="display:none"/>

                        <!-- Email Body (only show for custom template) -->
                        <v-textarea 
                            v-if="!nodeInspector.emailTemplate || nodeInspector.emailTemplate === 'custom'"
                            v-model="nodeInspector.emailBody" 
                            label="Email Body" 
                            variant="outlined"
                            density="compact" 
                            rows="6" 
                            auto-grow 
                            hide-details="auto"
                            placeholder="Enter email content here..."
                            @update:model-value="updateNodeProperty('emailBody', $event)"
                            class="modern-input mb-3" 
                            :readonly="isViewMode" />

                        <!-- Template Info (show for non-custom templates) -->
                        <v-alert 
                            v-if="nodeInspector.emailTemplate && nodeInspector.emailTemplate !== 'custom'"
                            type="info" 
                            variant="tonal" 
                            density="compact" 
                            class="mb-3">
                            <template v-slot:prepend>
                                <v-icon>mdi-information</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>Template Selected:</strong> {{ nodeInspector.emailTemplate }}<br>
                                This template will automatically generate the email content based on context variables.
                            </div>
                        </v-alert>

                        <!-- Email Options -->
                        <!-- <h6 class="section-title mt-4">Email Options</h6> -->
                        <!-- HTML Format -->
                        <!-- <v-switch 
                            v-model="nodeInspector.isHtml" 
                            label="HTML Format"
                            hide-details 
                            density="compact"
                            color="primary"
                            inset
                            @update:model-value="updateNodeProperty('isHtml', $event)"
                            class="mb-3" 
                            :readonly="isViewMode" /> -->
                    </div>

                    <!-- Interest Optimizer Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'interestOptimizer'" class="form-section">
                        <!-- Header Section -->
                        <div class="d-flex align-center mb-6">
                            <h5 class="text-h6 font-weight-bold text-primary mb-0">Interest Optimizer Configuration</h5>
                        </div>
                        <div class="panel-content">
                            <SummaryMemoryParserSection :config="nodeInspector" :providers="listDDProvider"
                                :models="DDModelShow" :readonly="isViewMode" @update-property="updateNodeProperty"
                                @change-provider="changeProvider" />
                            <!-- Use Base Model Prompt Toggle (Second Row) -->
                            <div class="base-prompt-toggle-section mb-6">
                                <v-card variant="outlined" class="pa-3" color="green-lighten-5" style="border-radius: 12px;">
                                    <div class="d-flex align-center">
                                        <v-avatar size="24"
                                            color="green"
                                            class="me-3">
                                            <v-icon
                                                color="white"
                                                size="12">mdi-text-box-check</v-icon>
                                        </v-avatar>
                                        <div class="flex-grow-1 me-3">
                                            <div class="text-subtitle-2"><span style="color:#4CAF50;font-weight: 500;"> Use Base Model Prompt</span></div>
                                            <div class="text-caption text-medium-emphasis">Combine with the default model prompt</div>
                                        </div>
                                        <v-switch v-model="nodeInspector.useBaseModelPrompt" color="green"
                                            hide-details density="compact"
                                            @update:model-value="updateNodeProperty('useBaseModelPrompt', $event)"
                                            :readonly="isViewMode" inset />
                                    </div>
                                </v-card>
                            </div>
                            <!-- VoicePrompt Replace Toggle -->
                            <div class="switch-option-compact mb-3">
                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                    :class="{ 'switch-active-bg': nodeInspector.enableVoicePromptReplace }">
                                    <div class="d-flex align-center">
                                        <v-avatar size="28"
                                            :color="nodeInspector.enableVoicePromptReplace ? 'deep-orange' : 'grey-lighten-1'"
                                            class="me-3">
                                            <v-icon
                                                :color="nodeInspector.enableVoicePromptReplace ? 'white' : 'grey-darken-1'"
                                                size="16">mdi-microphone-outline</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="switch-label">VoicePrompt Replace</div>
                                            <div class="switch-description">Replace tag with TTS prompt in prompt</div>
                                        </div>
                                    </div>
                                    <v-switch v-model="nodeInspector.enableVoicePromptReplace" color="deep-orange"
                                        hide-details density="compact"
                                        @update:model-value="updateNodeProperty('enableVoicePromptReplace', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                                <!-- Code block with copy button -->
                                <div v-if="nodeInspector.enableVoicePromptReplace" class="code-block-wrapper mt-2 mx-3">
                                    <div class="code-block d-flex align-center justify-space-between">
                                        <code class="code-text">{{VoicePromptTagText}}</code>
                                        <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                            @click="copyToClipboard(VoicePromptTagText)"
                                            class="copy-btn">
                                            <v-icon size="14">mdi-content-copy</v-icon>
                                            <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                            <!-- Language Replace Toggle -->
                            <div class="switch-option-compact mb-3">
                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                    :class="{ 'switch-active-bg': nodeInspector.enableLanguageReplace }">
                                    <div class="d-flex align-center">
                                        <v-avatar size="28"
                                            :color="nodeInspector.enableLanguageReplace ? 'teal' : 'grey-lighten-1'"
                                            class="me-3">
                                            <v-icon
                                                :color="nodeInspector.enableLanguageReplace ? 'white' : 'grey-darken-1'"
                                                size="16">mdi-translate</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="switch-label">Language Replace</div>
                                            <div class="switch-description">Replace tag with detected language in prompt</div>
                                        </div>
                                    </div>
                                    <v-switch v-model="nodeInspector.enableLanguageReplace" color="teal"
                                        hide-details density="compact"
                                        @update:model-value="updateNodeProperty('enableLanguageReplace', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                                <!-- Code block with copy button -->
                                <div v-if="nodeInspector.enableLanguageReplace" class="code-block-wrapper mt-2 mx-3">
                                    <div class="code-block d-flex align-center justify-space-between">
                                        <code class="code-text">{{LanguageTagText}}</code>
                                        <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                            @click="copyToClipboard('{{Language}}')" class="copy-btn">
                                            <v-icon size="14">mdi-content-copy</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                            <!-- TransactionID Context Toggle -->
                            <div class="switch-option-compact mb-3">
                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                    :class="{ 'switch-active-bg': nodeInspector.enableTransactionId }">
                                    <div class="d-flex align-center">
                                        <v-avatar size="28"
                                            :color="nodeInspector.enableTransactionId ? 'info' : 'grey-lighten-1'"
                                            class="me-3">
                                            <v-icon
                                                :color="nodeInspector.enableTransactionId ? 'white' : 'grey-darken-1'"
                                                size="16">mdi-identifier</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="switch-label">Add TransactionID to Prompt</div>
                                            <div class="switch-description">Include transaction identifier in AI context</div>
                                        </div>
                                    </div>
                                    <v-switch v-model="nodeInspector.enableTransactionId" color="info"
                                        hide-details density="compact"
                                        @update:model-value="updateNodeProperty('enableTransactionId', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                            </div>

                            <!-- Model Configuration -->
                            <div class="config-section mb-6">
                                <h6 class="section-label mb-3">Model Configuration</h6>
                                <v-btn
                                    v-if="nodeInspector.ioAnswerModel"
                                    icon
                                    variant="tonal"
                                    color="primary"
                                    size="small"
                                    class="model-config-edit-btn"
                                    @click="navigateToModelDetail(uniqueAiModelOptions, nodeInspector.ioAnswerModel)"
                                    :disabled="isViewMode">
                                    <v-icon size="16">mdi-pencil-outline</v-icon>
                                </v-btn>
                                <v-row>
                                    <v-col cols="12">
                                        <v-autocomplete
                                            v-model="nodeInspector.ioAnswerModel"
                                            :items="uniqueAiModelOptions"
                                            item-title="searchableText" 
                                            item-value="value"
                                            label="Answer Model*" 
                                            variant="outlined" 
                                            density="comfortable" 
                                            hide-details="auto"
                                            prepend-inner-icon="mdi-robot"
                                            @update:model-value="updateNodeProperty('ioAnswerModel', $event)"
                                            class="config-input" 
                                            :readonly="isViewMode"
                                            hint="Model for answering user questions" 
                                            persistent-hint 
                                            required>
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-bind="props" class="model-option"
                                                    :title="item.raw.value + ': ' + item.raw.name">
                                                    <template v-slot:prepend>
                                                        <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                                            <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                                        </div>
                                                    </template>
                                                </v-list-item>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12">
                                        <v-textarea
                                            v-model="nodeInspector.ioFallbackPrompt"
                                            label="Fallback Prompt"
                                            variant="outlined"
                                            density="comfortable"
                                            hide-details="auto"
                                            rows="4"
                                            auto-grow
                                            prepend-inner-icon="mdi-text-box-outline"
                                            @update:model-value="updateNodeProperty('ioFallbackPrompt', $event)"
                                            class="config-input"
                                            :readonly="isViewMode"
                                            hint="Prompt to use when main response fails"
                                            persistent-hint
                                            placeholder="Enter fallback prompt for error handling..." />
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12">
                                        <v-autocomplete 
                                            v-model="nodeInspector.ioParserModel"
                                            :items="uniqueAiModelOptions" 
                                            item-title="searchableText" 
                                            item-value="value"
                                            label="Parser Model*" 
                                            variant="outlined" 
                                            density="comfortable" 
                                            hide-details="auto"
                                            prepend-inner-icon="mdi-code-json"
                                            @update:model-value="updateNodeProperty('ioParserModel', $event)"
                                            class="config-input" 
                                            :readonly="isViewMode"
                                            hint="Model for converting questions to JSON parameters" 
                                            persistent-hint 
                                            required>
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-bind="props" class="model-option"
                                                    :title="item.raw.value + ': ' + item.raw.name">
                                                    <template v-slot:prepend>
                                                        <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                                            <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                                        </div>
                                                    </template>
                                                </v-list-item>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                            </div>
                            
                            <!-- Knowledge Configuration -->
                            <div class="config-section mb-6">
                                <h6 class="section-label mb-3">Knowledge Configuration</h6>
                                <v-row>
                                    <v-col cols="12">
                                        <!-- Document Selection -->
                                        <v-autocomplete 
                                            v-model="nodeInspector.ioDocuments" 
                                            :items="DDVector" :no-data-text="kccDocsNoDataText" 
                                            item-title="displayName"
                                            item-value="name" 
                                            label="Documents*" 
                                            variant="outlined" 
                                            density="comfortable" 
                                            hide-details="auto"
                                            chips 
                                            multiple 
                                            @update:model-value="updateNodeProperty('ioDocuments', $event)"
                                            class="config-input" 
                                            placeholder="Select documents" 
                                            :clearable="!isViewMode" 
                                            :readonly="isViewMode"
                                            prepend-inner-icon="mdi-file-document"
                                            hint="Select documents for knowledge base"
                                            persistent-hint
                                            required>
                                            <template v-slot:chip="{ props, item }">
                                                <v-chip v-bind="props" :color="isDocumentMissing(item.value) ? 'error' : undefined" :variant="isDocumentMissing(item.value) ? 'flat' : 'tonal'">
                                                    <v-icon v-if="isDocumentMissing(item.value)" icon="mdi-alert-circle" size="16" class="me-1" />
                                                    {{ isDocumentMissing(item.value) ? item.value : item.title }}
                                                </v-chip>
                                            </template>
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-bind="props" class="document-option">
                                                    <template v-slot:prepend>
                                                        <v-icon size="20" color="primary">mdi-file-document</v-icon>
                                                    </template>
                                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                </v-list-item>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                            </div>

                            <!-- Account Code Configuration -->
                            <div class="config-section">
                                <h6 class="section-label mb-3">Account Configuration</h6>
                                <v-row>
                                    <v-col cols="12">
                                        <v-autocomplete
                                            v-model="nodeInspector.ioAccountCodes"
                                            label="Account Codes (เลือกได้มากกว่า 1 ค่า)*"
                                            :items="interestMasterOptions"
                                            item-title="name"
                                            item-value="accountCode"
                                            multiple
                                            chips
                                            clearable
                                            variant="outlined"
                                            density="comfortable"
                                            hide-details="auto"
                                            color="primary"
                                            :loading="loadingInterestMaster"
                                            @update:model-value="updateNodeProperty('ioAccountCodes', $event)"
                                            class="config-input"
                                            :readonly="isViewMode"
                                        >
                                            <template v-slot:selection="{ item, index }">
                                                <v-chip
                                                    v-if="index < 2"
                                                    color="primary"
                                                    variant="outlined"
                                                    size="small"
                                                    closable
                                                    @click:close="removeAccountCode(item.value)"
                                                >
                                                    {{ item.title }}
                                                </v-chip>
                                                <span
                                                    v-if="index === 2"
                                                    class="text-grey text-caption align-self-center"
                                                >
                                                    (+{{ (nodeInspector.ioAccountCodes || []).length - 2 }} others)
                                                </span>
                                            </template>
                                            <template v-slot:append-inner>
                                                <v-tooltip location="top">
                                                    <template v-slot:activator="{ props }">
                                                        <v-icon
                                                            v-bind="props"
                                                            size="small"
                                                            color="grey-darken-1"
                                                        >
                                                            mdi-information-outline
                                                        </v-icon>
                                                    </template>
                                                    <span>เลือก Account Code ที่ต้องการให้ระบบคำนวณ (สามารถเลือกได้หลายค่า)</span>
                                                </v-tooltip>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                            </div>

                            <!-- Required Fields Validation -->
                            <div class="config-section mt-6">
                                <h6 class="section-label mb-3">Required Fields Validation</h6>
                                <v-card variant="outlined" class="pa-3" style="border-radius: 12px; border-color: #FF8F00; background-color: #FFF8E1;">
                                    <div class="d-flex align-center mb-3">
                                        <v-avatar size="24" color="amber-darken-2" class="me-3">
                                            <v-icon color="white" size="12">mdi-shield-check</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="text-subtitle-2" style="color:#E65100;font-weight:600;">Validate Parsed JSON Fields</div>
                                            <div class="text-caption" style="color:#5D4037;">เลือก field ที่ห้ามเป็น null, empty หรือ [] หลังจาก LLM parse JSON</div>
                                        </div>
                                    </div>
                                    <div class="d-flex flex-wrap ga-2">
                                        <v-checkbox
                                            v-for="field in ioValidationFields"
                                            :key="field.value"
                                            v-model="nodeInspector.ioRequiredFields"
                                            :label="field.label"
                                            :value="field.value"
                                            density="compact"
                                            hide-details
                                            color="amber-darken-3"
                                            :readonly="isViewMode"
                                            @update:model-value="updateNodeProperty('ioRequiredFields', $event)"
                                            class="me-2"
                                            style="color: #212121;"
                                        />
                                    </div>
                                </v-card>
                            </div>

                            <!-- Advanced Settings -->
                            <div class="config-section mt-6">
                                <h6 class="section-label mb-3">Advanced Settings</h6>
                                <v-text-field
                                    v-model.number="nodeInspector.nodeTimeout"
                                    type="number"
                                    min="0"
                                    label="Request Timeout (seconds)"
                                    hint="0 = use global timeout setting"
                                    persistent-hint
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details="auto"
                                    class="config-input"
                                    @update:model-value="updateNodeProperty('nodeTimeout', $event)"
                                    :readonly="isViewMode"
                                />
                                <v-text-field
                                    v-model.number="nodeInspector.nodeMaxRetry"
                                    type="number"
                                    min="0"
                                    label="Max Retry"
                                    hint="0 = use global max retry setting"
                                    persistent-hint
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details="auto"
                                    class="config-input mt-3"
                                    @update:model-value="updateNodeProperty('nodeMaxRetry', $event)"
                                    :readonly="isViewMode"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Loan Interest Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'loanInterest'" class="form-section">
                        <!-- Header Section -->
                        <div class="d-flex align-center mb-6">
                            <h5 class="text-h6 font-weight-bold text-primary mb-0">Loan Interest Configuration</h5>
                        </div>

                        <div class="panel-content">
                            <SummaryMemoryParserSection :config="nodeInspector" :providers="listDDProvider"
                                :models="DDModelShow" :readonly="isViewMode" @update-property="updateNodeProperty"
                                @change-provider="changeProvider" />
                            <!-- Use Base Model Prompt Toggle (Second Row) -->
                            <div class="base-prompt-toggle-section mb-6">
                                <v-card variant="outlined" class="pa-3" color="green-lighten-5" style="border-radius: 12px;">
                                    <div class="d-flex align-center">
                                        <v-avatar size="24"
                                            color="green"
                                            class="me-3">
                                            <v-icon
                                                color="white"
                                                size="12">mdi-text-box-check</v-icon>
                                        </v-avatar>
                                        <div class="flex-grow-1 me-3">
                                            <div class="text-subtitle-2"><span style="color:#4CAF50;font-weight: 500;"> Use Base Model Prompt</span></div>
                                            <div class="text-caption text-medium-emphasis">Combine with the default model prompt</div>
                                        </div>
                                        <v-switch v-model="nodeInspector.useBaseModelPrompt" color="green"
                                            hide-details density="compact"
                                            @update:model-value="updateNodeProperty('useBaseModelPrompt', $event)"
                                            :readonly="isViewMode" inset />
                                    </div>
                                </v-card>
                            </div>
                            <!-- VoicePrompt Replace Toggle -->
                            <div class="switch-option-compact mb-3">
                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                    :class="{ 'switch-active-bg': nodeInspector.enableVoicePromptReplace }">
                                    <div class="d-flex align-center">
                                        <v-avatar size="28"
                                            :color="nodeInspector.enableVoicePromptReplace ? 'deep-orange' : 'grey-lighten-1'"
                                            class="me-3">
                                            <v-icon
                                                :color="nodeInspector.enableVoicePromptReplace ? 'white' : 'grey-darken-1'"
                                                size="16">mdi-microphone-outline</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="switch-label">VoicePrompt Replace</div>
                                            <div class="switch-description">Replace tag with TTS prompt in prompt</div>
                                        </div>
                                    </div>
                                    <v-switch v-model="nodeInspector.enableVoicePromptReplace" color="deep-orange"
                                        hide-details density="compact"
                                        @update:model-value="updateNodeProperty('enableVoicePromptReplace', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                                <!-- Code block with copy button -->
                                <div v-if="nodeInspector.enableVoicePromptReplace" class="code-block-wrapper mt-2 mx-3">
                                    <div class="code-block d-flex align-center justify-space-between">
                                        <code class="code-text">{{VoicePromptTagText}}</code>
                                        <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                            @click="copyToClipboard(VoicePromptTagText)"
                                            class="copy-btn">
                                            <v-icon size="14">mdi-content-copy</v-icon>
                                            <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                            <!-- Language Replace Toggle -->
                            <div class="switch-option-compact mb-3">
                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                    :class="{ 'switch-active-bg': nodeInspector.enableLanguageReplace }">
                                    <div class="d-flex align-center">
                                        <v-avatar size="28"
                                            :color="nodeInspector.enableLanguageReplace ? 'teal' : 'grey-lighten-1'"
                                            class="me-3">
                                            <v-icon
                                                :color="nodeInspector.enableLanguageReplace ? 'white' : 'grey-darken-1'"
                                                size="16">mdi-translate</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="switch-label">Language Replace</div>
                                            <div class="switch-description">Replace tag with detected language in prompt</div>
                                        </div>
                                    </div>
                                    <v-switch v-model="nodeInspector.enableLanguageReplace" color="teal"
                                        hide-details density="compact"
                                        @update:model-value="updateNodeProperty('enableLanguageReplace', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                                <!-- Code block with copy button -->
                                <div v-if="nodeInspector.enableLanguageReplace" class="code-block-wrapper mt-2 mx-3">
                                    <div class="code-block d-flex align-center justify-space-between">
                                        <code class="code-text">{{LanguageTagText}}</code>
                                        <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                            @click="copyToClipboard('{{Language}}')" class="copy-btn">
                                            <v-icon size="14">mdi-content-copy</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                            <!-- TransactionID Context Toggle -->
                            <div class="switch-option-compact mb-3">
                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                    :class="{ 'switch-active-bg': nodeInspector.enableTransactionId }">
                                    <div class="d-flex align-center">
                                        <v-avatar size="28"
                                            :color="nodeInspector.enableTransactionId ? 'info' : 'grey-lighten-1'"
                                            class="me-3">
                                            <v-icon
                                                :color="nodeInspector.enableTransactionId ? 'white' : 'grey-darken-1'"
                                                size="16">mdi-identifier</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="switch-label">Add TransactionID to Prompt</div>
                                            <div class="switch-description">Include transaction identifier in AI context</div>
                                        </div>
                                    </div>
                                    <v-switch v-model="nodeInspector.enableTransactionId" color="info"
                                        hide-details density="compact"
                                        @update:model-value="updateNodeProperty('enableTransactionId', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                            </div>

                            <!-- Model Configuration -->
                            <div class="config-section mb-6">
                                <h6 class="section-label mb-3">Model Configuration</h6>
                                <v-btn
                                    v-if="nodeInspector.liAnswerModel"
                                    icon
                                    variant="tonal"
                                    color="primary"
                                    size="small"
                                    class="model-config-edit-btn"
                                    @click="navigateToModelDetail(uniqueAiModelOptions, nodeInspector.liAnswerModel)"
                                    :disabled="isViewMode">
                                    <v-icon size="16">mdi-pencil-outline</v-icon>
                                </v-btn>
                                <v-row>
                                    <v-col cols="12">
                                        <v-autocomplete
                                            v-model="nodeInspector.liAnswerModel"
                                            :items="uniqueAiModelOptions" 
                                            item-title="searchableText" 
                                            item-value="value"
                                            label="Answer Model*" 
                                            variant="outlined" 
                                            density="comfortable" 
                                            hide-details="auto"
                                            prepend-inner-icon="mdi-robot"
                                            @update:model-value="updateNodeProperty('liAnswerModel', $event)"
                                            class="config-input" 
                                            :readonly="isViewMode"
                                            hint="Model for answering user questions" 
                                            persistent-hint 
                                            required>
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-bind="props" class="model-option"
                                                    :title="item.raw.value + ': ' + item.raw.name">
                                                    <template v-slot:prepend>
                                                        <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                                            <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                                        </div>
                                                    </template>
                                                </v-list-item>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12">
                                        <v-textarea
                                            v-model="nodeInspector.liFallbackPrompt"
                                            label="Fallback Prompt"
                                            variant="outlined"
                                            density="comfortable"
                                            hide-details="auto"
                                            rows="4"
                                            auto-grow
                                            prepend-inner-icon="mdi-text-box-outline"
                                            @update:model-value="updateNodeProperty('liFallbackPrompt', $event)"
                                            class="config-input"
                                            :readonly="isViewMode"
                                            hint="Prompt to use when main response fails"
                                            persistent-hint
                                            placeholder="Enter fallback prompt for error handling..." />
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="12">
                                        <v-autocomplete 
                                            v-model="nodeInspector.liParserModel"
                                            :items="uniqueAiModelOptions" 
                                            item-title="searchableText" 
                                            item-value="value"
                                            label="Parser Model*" 
                                            variant="outlined" 
                                            density="comfortable" 
                                            hide-details="auto"
                                            prepend-inner-icon="mdi-code-json"
                                            @update:model-value="updateNodeProperty('liParserModel', $event)"
                                            class="config-input" 
                                            :readonly="isViewMode"
                                            hint="Model for converting questions to JSON parameters" 
                                            persistent-hint 
                                            required>
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-bind="props" class="model-option"
                                                    :title="item.raw.value + ': ' + item.raw.name">
                                                    <template v-slot:prepend>
                                                        <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                                            <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                                        </div>
                                                    </template>
                                                </v-list-item>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                            </div>
                            
                            <!-- Knowledge Configuration -->
                            <div class="config-section mb-6">
                                <h6 class="section-label mb-3">Knowledge Configuration</h6>
                                <v-row>
                                    <v-col cols="12">
                                        <!-- Document Selection -->
                                        <v-autocomplete 
                                            v-model="nodeInspector.liDocuments" 
                                            :items="DDVector" :no-data-text="kccDocsNoDataText" 
                                            item-title="displayName"
                                            item-value="name" 
                                            label="Documents" 
                                            variant="outlined" 
                                            density="comfortable" 
                                            hide-details="auto"
                                            chips 
                                            multiple 
                                            @update:model-value="updateNodeProperty('liDocuments', $event)"
                                            class="config-input" 
                                            placeholder="Select documents" 
                                            :clearable="!isViewMode" 
                                            :readonly="isViewMode"
                                            prepend-inner-icon="mdi-file-document"
                                            hint="Select documents for knowledge base"
                                            persistent-hint
                                            required>
                                            <template v-slot:chip="{ props, item }">
                                                <v-chip v-bind="props" :color="isDocumentMissing(item.value) ? 'error' : undefined" :variant="isDocumentMissing(item.value) ? 'flat' : 'tonal'">
                                                    <v-icon v-if="isDocumentMissing(item.value)" icon="mdi-alert-circle" size="16" class="me-1" />
                                                    {{ isDocumentMissing(item.value) ? item.value : item.title }}
                                                </v-chip>
                                            </template>
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-bind="props" class="document-option">
                                                    <template v-slot:prepend>
                                                        <v-icon size="20" color="primary">mdi-file-document</v-icon>
                                                    </template>
                                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                </v-list-item>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                            </div>

                            <!-- Loan Interest Rate Configuration -->
                            <div class="config-section">
                                <h6 class="section-label mb-3">Interest Rate Configuration</h6>
                                <v-row>
                                    <v-col cols="12">
                                        <v-autocomplete
                                            v-model="nodeInspector.liInterestRates"
                                            label="Loan Interest Rates (เลือกได้มากกว่า 1 ค่า)*"
                                            :items="loanInterestRateOptions"
                                            item-title="interestRateName"
                                            item-value="interestRateID"
                                            chips
                                            clearable
                                            variant="outlined"
                                            density="comfortable"
                                            hide-details="auto"
                                            color="primary"
                                            :loading="loadingLoanInterestRates"
                                            @update:model-value="updateNodeProperty('liInterestRates', $event)"
                                            class="config-input"
                                            :readonly="isViewMode"
                                            :multiple="true"
                                        >
                                            <template v-slot:selection="{ item, index }">
                                                <v-chip
                                                    v-if="index < 2"
                                                    color="primary"
                                                    variant="outlined"
                                                    size="small"
                                                    closable
                                                    @click:close="removeLoanInterestRate(item.value)"
                                                >
                                                    {{ item.title }}
                                                </v-chip>
                                                <span
                                                    v-if="index === 2"
                                                    class="text-grey text-caption align-self-center"
                                                >
                                                    (+{{ (nodeInspector.liInterestRates || []).length - 2 }} others)
                                                </span>
                                            </template>
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-bind="props">
                                                    <template v-slot:prepend>
                                                        <v-icon size="20" color="green">mdi-home-analytics</v-icon>
                                                    </template>
                                                    <v-list-item-subtitle>{{ item.raw.interestRateType }} - {{ item.raw.categoryName }}</v-list-item-subtitle>
                                                </v-list-item>
                                            </template>
                                            <template v-slot:append-inner>
                                                <v-tooltip location="top">
                                                    <template v-slot:activator="{ props }">
                                                        <v-icon
                                                            v-bind="props"
                                                            size="small"
                                                            color="grey-darken-1"
                                                        >
                                                            mdi-information-outline
                                                        </v-icon>
                                                    </template>
                                                    <span>เลือก Loan Interest Rate ที่ต้องการให้ระบบคำนวณ (สามารถเลือกได้หลายค่า)</span>
                                                </v-tooltip>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                            </div>

                            

                            <!-- Required Fields Validation -->
                            <div class="config-section mt-6">
                                <h6 class="section-label mb-3">Required Fields Validation</h6>
                                <v-card variant="outlined" class="pa-3" style="border-radius: 12px; border-color: #FF8F00; background-color: #FFF8E1;">
                                    <div class="d-flex align-center mb-3">
                                        <v-avatar size="24" color="amber-darken-2" class="me-3">
                                            <v-icon color="white" size="12">mdi-shield-check</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="text-subtitle-2" style="color:#E65100;font-weight:600;">Validate Parsed JSON Fields</div>
                                            <div class="text-caption" style="color:#5D4037;">เลือก field ที่ห้ามเป็น null, empty หรือ 0 หลังจาก LLM parse JSON</div>
                                        </div>
                                    </div>
                                    <div class="d-flex flex-wrap ga-2">
                                        <v-checkbox
                                            v-for="field in liValidationFields"
                                            :key="field.value"
                                            v-model="nodeInspector.liRequiredFields"
                                            :label="field.label"
                                            :value="field.value"
                                            density="compact"
                                            hide-details
                                            color="amber-darken-3"
                                            :readonly="isViewMode"
                                            @update:model-value="updateNodeProperty('liRequiredFields', $event)"
                                            class="me-2"
                                            style="color: #212121;"
                                        />
                                    </div>
                                </v-card>
                            </div>

                            <!-- Advanced Settings -->
                            <div class="config-section mt-6">
                                <h6 class="section-label mb-3">Advanced Settings</h6>
                                <v-text-field
                                    v-model.number="nodeInspector.nodeTimeout"
                                    type="number"
                                    min="0"
                                    label="Request Timeout (seconds)"
                                    hint="0 = use global timeout setting"
                                    persistent-hint
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details="auto"
                                    class="config-input"
                                    @update:model-value="updateNodeProperty('nodeTimeout', $event)"
                                    :readonly="isViewMode"
                                />
                                <v-text-field
                                    v-model.number="nodeInspector.nodeMaxRetry"
                                    type="number"
                                    min="0"
                                    label="Max Retry"
                                    hint="0 = use global max retry setting"
                                    persistent-hint
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details="auto"
                                    class="config-input mt-3"
                                    @update:model-value="updateNodeProperty('nodeMaxRetry', $event)"
                                    :readonly="isViewMode"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Interest Optimizer V2 Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'interestOptimizerV2'" class="form-section">
                        <!-- Header Section -->
                        <div class="d-flex align-center mb-6">
                            <h5 class="text-h6 font-weight-bold text-primary mb-0">Interest Optimizer V2 Configuration</h5>
                        </div>
                        <div class="panel-content">
                            <SummaryMemoryParserSection :config="nodeInspector" :providers="listDDProvider"
                                :models="DDModelShow" :readonly="isViewMode" @update-property="updateNodeProperty"
                                @change-provider="changeProvider" />
                            <!-- Use Base Model Prompt Toggle (Second Row) -->
                            <div class="base-prompt-toggle-section mb-6">
                                <v-card variant="outlined" class="pa-3" color="green-lighten-5" style="border-radius: 12px;">
                                    <div class="d-flex align-center">
                                        <v-avatar size="24"
                                            color="green"
                                            class="me-3">
                                            <v-icon
                                                color="white"
                                                size="12">mdi-text-box-check</v-icon>
                                        </v-avatar>
                                        <div class="flex-grow-1 me-3">
                                            <div class="text-subtitle-2"><span style="color:#4CAF50;font-weight: 500;"> Use Base Model Prompt</span></div>
                                            <div class="text-caption text-medium-emphasis">Combine with the default model prompt</div>
                                        </div>
                                        <v-switch v-model="nodeInspector.useBaseModelPrompt" color="green"
                                            hide-details density="compact"
                                            @update:model-value="updateNodeProperty('useBaseModelPrompt', $event)"
                                            :readonly="isViewMode" inset />
                                    </div>
                                </v-card>
                            </div>
                            <!-- VoicePrompt Replace Toggle -->
                            <div class="switch-option-compact mb-3">
                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                    :class="{ 'switch-active-bg': nodeInspector.enableVoicePromptReplace }">
                                    <div class="d-flex align-center">
                                        <v-avatar size="28"
                                            :color="nodeInspector.enableVoicePromptReplace ? 'deep-orange' : 'grey-lighten-1'"
                                            class="me-3">
                                            <v-icon
                                                :color="nodeInspector.enableVoicePromptReplace ? 'white' : 'grey-darken-1'"
                                                size="16">mdi-microphone-outline</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="switch-label">VoicePrompt Replace</div>
                                            <div class="switch-description">Replace tag with TTS prompt in prompt</div>
                                        </div>
                                    </div>
                                    <v-switch v-model="nodeInspector.enableVoicePromptReplace" color="deep-orange"
                                        hide-details density="compact"
                                        @update:model-value="updateNodeProperty('enableVoicePromptReplace', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                                <!-- Code block with copy button -->
                                <div v-if="nodeInspector.enableVoicePromptReplace" class="code-block-wrapper mt-2 mx-3">
                                    <div class="code-block d-flex align-center justify-space-between">
                                        <code class="code-text">{{VoicePromptTagText}}</code>
                                        <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                            @click="copyToClipboard(VoicePromptTagText)"
                                            class="copy-btn">
                                            <v-icon size="14">mdi-content-copy</v-icon>
                                            <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                            <!-- Language Replace Toggle -->
                            <div class="switch-option-compact mb-3">
                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                    :class="{ 'switch-active-bg': nodeInspector.enableLanguageReplace }">
                                    <div class="d-flex align-center">
                                        <v-avatar size="28"
                                            :color="nodeInspector.enableLanguageReplace ? 'teal' : 'grey-lighten-1'"
                                            class="me-3">
                                            <v-icon
                                                :color="nodeInspector.enableLanguageReplace ? 'white' : 'grey-darken-1'"
                                                size="16">mdi-translate</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="switch-label">Language Replace</div>
                                            <div class="switch-description">Replace tag with detected language in prompt</div>
                                        </div>
                                    </div>
                                    <v-switch v-model="nodeInspector.enableLanguageReplace" color="teal"
                                        hide-details density="compact"
                                        @update:model-value="updateNodeProperty('enableLanguageReplace', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                                <!-- Code block with copy button -->
                                <div v-if="nodeInspector.enableLanguageReplace" class="code-block-wrapper mt-2 mx-3">
                                    <div class="code-block d-flex align-center justify-space-between">
                                        <code class="code-text">{{LanguageTagText}}</code>
                                        <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                            @click="copyToClipboard('{{Language}}')" class="copy-btn">
                                            <v-icon size="14">mdi-content-copy</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                            <!-- TransactionID Context Toggle -->
                            <div class="switch-option-compact mb-3">
                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                    :class="{ 'switch-active-bg': nodeInspector.enableTransactionId }">
                                    <div class="d-flex align-center">
                                        <v-avatar size="28"
                                            :color="nodeInspector.enableTransactionId ? 'info' : 'grey-lighten-1'"
                                            class="me-3">
                                            <v-icon
                                                :color="nodeInspector.enableTransactionId ? 'white' : 'grey-darken-1'"
                                                size="16">mdi-identifier</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="switch-label">Add TransactionID to Prompt</div>
                                            <div class="switch-description">Include transaction identifier in AI context</div>
                                        </div>
                                    </div>
                                    <v-switch v-model="nodeInspector.enableTransactionId" color="info"
                                        hide-details density="compact"
                                        @update:model-value="updateNodeProperty('enableTransactionId', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                            </div>

                            <!-- Model Configuration -->
                            <div class="config-section mb-6">
                                <h6 class="section-label mb-3">Model Configuration</h6>
                                <v-btn
                                    v-if="nodeInspector.ioAnswerModel"
                                    icon
                                    variant="tonal"
                                    color="primary"
                                    size="small"
                                    class="model-config-edit-btn"
                                    @click="navigateToModelDetail(uniqueAiModelOptions, nodeInspector.ioAnswerModel)"
                                    :disabled="isViewMode">
                                    <v-icon size="16">mdi-pencil-outline</v-icon>
                                </v-btn>
                                <v-row>
                                    <v-col cols="12">
                                        <v-autocomplete
                                            v-model="nodeInspector.ioAnswerModel"
                                            :items="uniqueAiModelOptions"
                                            item-title="searchableText" 
                                            item-value="value"
                                            label="Answer Model*" 
                                            variant="outlined" 
                                            density="comfortable" 
                                            hide-details="auto"
                                            prepend-inner-icon="mdi-robot"
                                            @update:model-value="updateNodeProperty('ioAnswerModel', $event)"
                                            class="config-input" 
                                            :readonly="isViewMode"
                                            hint="Model for answering user questions" 
                                            persistent-hint 
                                            required>
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-bind="props" class="model-option"
                                                    :title="item.raw.value + ': ' + item.raw.name">
                                                    <template v-slot:prepend>
                                                        <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                                            <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                                        </div>
                                                    </template>
                                                </v-list-item>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12">
                                        <v-textarea
                                            v-model="nodeInspector.ioFallbackPrompt"
                                            label="Fallback Prompt"
                                            variant="outlined"
                                            density="comfortable"
                                            hide-details="auto"
                                            rows="4"
                                            auto-grow
                                            prepend-inner-icon="mdi-text-box-outline"
                                            @update:model-value="updateNodeProperty('ioFallbackPrompt', $event)"
                                            class="config-input"
                                            :readonly="isViewMode"
                                            hint="Prompt to use when main response fails"
                                            persistent-hint
                                            placeholder="Enter fallback prompt for error handling..." />
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12">
                                        <v-autocomplete 
                                            v-model="nodeInspector.ioParserModel"
                                            :items="uniqueAiModelOptions" 
                                            item-title="searchableText" 
                                            item-value="value"
                                            label="Parser Model*" 
                                            variant="outlined" 
                                            density="comfortable" 
                                            hide-details="auto"
                                            prepend-inner-icon="mdi-code-json"
                                            @update:model-value="updateNodeProperty('ioParserModel', $event)"
                                            class="config-input" 
                                            :readonly="isViewMode"
                                            hint="Model for converting questions to JSON parameters" 
                                            persistent-hint 
                                            required>
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-bind="props" class="model-option"
                                                    :title="item.raw.value + ': ' + item.raw.name">
                                                    <template v-slot:prepend>
                                                        <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                                            <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                                        </div>
                                                    </template>
                                                </v-list-item>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                            </div>
                            
                            <!-- Knowledge Configuration -->
                            <div class="config-section mb-6">
                                <h6 class="section-label mb-3">Knowledge Configuration</h6>
                                <v-row>
                                    <v-col cols="12">
                                        <!-- Document Selection -->
                                        <v-autocomplete 
                                            v-model="nodeInspector.ioDocuments" 
                                            :items="DDVector" :no-data-text="kccDocsNoDataText" 
                                            item-title="displayName"
                                            item-value="name" 
                                            label="Documents*" 
                                            variant="outlined" 
                                            density="comfortable" 
                                            hide-details="auto"
                                            chips 
                                            multiple 
                                            @update:model-value="updateNodeProperty('ioDocuments', $event)"
                                            class="config-input" 
                                            placeholder="Select documents" 
                                            :clearable="!isViewMode" 
                                            :readonly="isViewMode"
                                            prepend-inner-icon="mdi-file-document"
                                            hint="Select documents for knowledge base"
                                            persistent-hint
                                            required>
                                            <template v-slot:chip="{ props, item }">
                                                <v-chip v-bind="props" :color="isDocumentMissing(item.value) ? 'error' : undefined" :variant="isDocumentMissing(item.value) ? 'flat' : 'tonal'">
                                                    <v-icon v-if="isDocumentMissing(item.value)" icon="mdi-alert-circle" size="16" class="me-1" />
                                                    {{ isDocumentMissing(item.value) ? item.value : item.title }}
                                                </v-chip>
                                            </template>
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-bind="props" class="document-option">
                                                    <template v-slot:prepend>
                                                        <v-icon size="20" color="primary">mdi-file-document</v-icon>
                                                    </template>
                                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                </v-list-item>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                            </div>

                            <!-- MetaDoc Configuration -->
                            <div class="config-section">
                                <h6 class="section-label mb-3">MetaDoc Configuration</h6>
                                <v-row>
                                    <v-col cols="12">
                                        <v-autocomplete
                                            v-model="nodeInspector.ioV2MetaDocs"
                                            label="MetaDocs (เลือกได้มากกว่า 1 ค่า)*"
                                            :items="DDVector" :no-data-text="kccDocsNoDataText"
                                            item-title="displayName"
                                            item-value="name"
                                            multiple
                                            chips
                                            clearable
                                            variant="outlined"
                                            density="comfortable"
                                            hide-details="auto"
                                            color="primary"
                                            @update:model-value="updateNodeProperty('ioV2MetaDocs', $event)"
                                            class="config-input"
                                            :readonly="isViewMode"
                                        >
                                            <template v-slot:chip="{ props, item }">
                                                <v-chip v-bind="props" :color="isDocumentMissing(item.value) ? 'error' : undefined" :variant="isDocumentMissing(item.value) ? 'flat' : 'tonal'">
                                                    <v-icon v-if="isDocumentMissing(item.value)" icon="mdi-alert-circle" size="16" class="me-1" />
                                                    {{ isDocumentMissing(item.value) ? item.value : item.title }}
                                                </v-chip>
                                            </template>
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-bind="props" class="document-option">
                                                    <template v-slot:prepend>
                                                        <v-icon size="20" color="orange">mdi-file-document-multiple</v-icon>
                                                    </template>
                                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                </v-list-item>
                                            </template>
                                            <template v-slot:append-inner>
                                                <v-tooltip location="top">
                                                    <template v-slot:activator="{ props }">
                                                        <v-icon v-bind="props" size="small" color="grey-darken-1">mdi-information-outline</v-icon>
                                                    </template>
                                                    <span>เลือก MetaDoc ที่มี interest rate ผูกไว้ — ระบบจะดึง deposit accounts อัตโนมัติ</span>
                                                </v-tooltip>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                            </div>

                            <!-- Required Fields Validation -->
                            <div class="config-section mt-6">
                                <h6 class="section-label mb-3">Required Fields Validation</h6>
                                <v-card variant="outlined" class="pa-3" style="border-radius: 12px; border-color: #FF8F00; background-color: #FFF8E1;">
                                    <div class="d-flex align-center mb-3">
                                        <v-avatar size="24" color="amber-darken-2" class="me-3">
                                            <v-icon color="white" size="12">mdi-shield-check</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="text-subtitle-2" style="color:#E65100;font-weight:600;">Validate Parsed JSON Fields</div>
                                            <div class="text-caption" style="color:#5D4037;">เลือก field ที่ห้ามเป็น null, empty หรือ [] หลังจาก LLM parse JSON</div>
                                        </div>
                                    </div>
                                    <div class="d-flex flex-wrap ga-2">
                                        <v-checkbox
                                            v-for="field in ioValidationFields"
                                            :key="field.value"
                                            v-model="nodeInspector.ioRequiredFields"
                                            :label="field.label"
                                            :value="field.value"
                                            density="compact"
                                            hide-details
                                            color="amber-darken-3"
                                            :readonly="isViewMode"
                                            @update:model-value="updateNodeProperty('ioRequiredFields', $event)"
                                            class="me-2"
                                            style="color: #212121;"
                                        />
                                    </div>
                                </v-card>
                            </div>

                            <!-- Advanced Settings -->
                            <div class="config-section mt-6">
                                <h6 class="section-label mb-3">Advanced Settings</h6>
                                <v-text-field
                                    v-model.number="nodeInspector.nodeTimeout"
                                    type="number"
                                    min="0"
                                    label="Request Timeout (seconds)"
                                    hint="0 = use global timeout setting"
                                    persistent-hint
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details="auto"
                                    class="config-input"
                                    @update:model-value="updateNodeProperty('nodeTimeout', $event)"
                                    :readonly="isViewMode"
                                />
                                <v-text-field
                                    v-model.number="nodeInspector.nodeMaxRetry"
                                    type="number"
                                    min="0"
                                    label="Max Retry"
                                    hint="0 = use global max retry setting"
                                    persistent-hint
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details="auto"
                                    class="config-input mt-3"
                                    @update:model-value="updateNodeProperty('nodeMaxRetry', $event)"
                                    :readonly="isViewMode"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Loan Interest V2 Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'loanInterestV2'" class="form-section">
                        <!-- Header Section -->
                        <div class="d-flex align-center mb-6">
                            <h5 class="text-h6 font-weight-bold text-primary mb-0">Loan Interest V2 Configuration</h5>
                        </div>

                        <div class="panel-content">
                            <SummaryMemoryParserSection :config="nodeInspector" :providers="listDDProvider"
                                :models="DDModelShow" :readonly="isViewMode" @update-property="updateNodeProperty"
                                @change-provider="changeProvider" />
                            <!-- Use Base Model Prompt Toggle (Second Row) -->
                            <div class="base-prompt-toggle-section mb-6">
                                <v-card variant="outlined" class="pa-3" color="green-lighten-5" style="border-radius: 12px;">
                                    <div class="d-flex align-center">
                                        <v-avatar size="24"
                                            color="green"
                                            class="me-3">
                                            <v-icon
                                                color="white"
                                                size="12">mdi-text-box-check</v-icon>
                                        </v-avatar>
                                        <div class="flex-grow-1 me-3">
                                            <div class="text-subtitle-2"><span style="color:#4CAF50;font-weight: 500;"> Use Base Model Prompt</span></div>
                                            <div class="text-caption text-medium-emphasis">Combine with the default model prompt</div>
                                        </div>
                                        <v-switch v-model="nodeInspector.useBaseModelPrompt" color="green"
                                            hide-details density="compact"
                                            @update:model-value="updateNodeProperty('useBaseModelPrompt', $event)"
                                            :readonly="isViewMode" inset />
                                    </div>
                                </v-card>
                            </div>
                            <!-- VoicePrompt Replace Toggle -->
                            <div class="switch-option-compact mb-3">
                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                    :class="{ 'switch-active-bg': nodeInspector.enableVoicePromptReplace }">
                                    <div class="d-flex align-center">
                                        <v-avatar size="28"
                                            :color="nodeInspector.enableVoicePromptReplace ? 'deep-orange' : 'grey-lighten-1'"
                                            class="me-3">
                                            <v-icon
                                                :color="nodeInspector.enableVoicePromptReplace ? 'white' : 'grey-darken-1'"
                                                size="16">mdi-microphone-outline</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="switch-label">VoicePrompt Replace</div>
                                            <div class="switch-description">Replace tag with TTS prompt in prompt</div>
                                        </div>
                                    </div>
                                    <v-switch v-model="nodeInspector.enableVoicePromptReplace" color="deep-orange"
                                        hide-details density="compact"
                                        @update:model-value="updateNodeProperty('enableVoicePromptReplace', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                                <!-- Code block with copy button -->
                                <div v-if="nodeInspector.enableVoicePromptReplace" class="code-block-wrapper mt-2 mx-3">
                                    <div class="code-block d-flex align-center justify-space-between">
                                        <code class="code-text">{{VoicePromptTagText}}</code>
                                        <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                            @click="copyToClipboard(VoicePromptTagText)"
                                            class="copy-btn">
                                            <v-icon size="14">mdi-content-copy</v-icon>
                                            <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                            <!-- Language Replace Toggle -->
                            <div class="switch-option-compact mb-3">
                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                    :class="{ 'switch-active-bg': nodeInspector.enableLanguageReplace }">
                                    <div class="d-flex align-center">
                                        <v-avatar size="28"
                                            :color="nodeInspector.enableLanguageReplace ? 'teal' : 'grey-lighten-1'"
                                            class="me-3">
                                            <v-icon
                                                :color="nodeInspector.enableLanguageReplace ? 'white' : 'grey-darken-1'"
                                                size="16">mdi-translate</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="switch-label">Language Replace</div>
                                            <div class="switch-description">Replace tag with detected language in prompt</div>
                                        </div>
                                    </div>
                                    <v-switch v-model="nodeInspector.enableLanguageReplace" color="teal"
                                        hide-details density="compact"
                                        @update:model-value="updateNodeProperty('enableLanguageReplace', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                                <!-- Code block with copy button -->
                                <div v-if="nodeInspector.enableLanguageReplace" class="code-block-wrapper mt-2 mx-3">
                                    <div class="code-block d-flex align-center justify-space-between">
                                        <code class="code-text">{{LanguageTagText}}</code>
                                        <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                            @click="copyToClipboard('{{Language}}')" class="copy-btn">
                                            <v-icon size="14">mdi-content-copy</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                            <!-- TransactionID Context Toggle -->
                            <div class="switch-option-compact mb-3">
                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                    :class="{ 'switch-active-bg': nodeInspector.enableTransactionId }">
                                    <div class="d-flex align-center">
                                        <v-avatar size="28"
                                            :color="nodeInspector.enableTransactionId ? 'info' : 'grey-lighten-1'"
                                            class="me-3">
                                            <v-icon
                                                :color="nodeInspector.enableTransactionId ? 'white' : 'grey-darken-1'"
                                                size="16">mdi-identifier</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="switch-label">Add TransactionID to Prompt</div>
                                            <div class="switch-description">Include transaction identifier in AI context</div>
                                        </div>
                                    </div>
                                    <v-switch v-model="nodeInspector.enableTransactionId" color="info"
                                        hide-details density="compact"
                                        @update:model-value="updateNodeProperty('enableTransactionId', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                            </div>

                            <!-- Model Configuration -->
                            <div class="config-section mb-6">
                                <h6 class="section-label mb-3">Model Configuration</h6>
                                <v-btn
                                    v-if="nodeInspector.liAnswerModel"
                                    icon
                                    variant="tonal"
                                    color="primary"
                                    size="small"
                                    class="model-config-edit-btn"
                                    @click="navigateToModelDetail(uniqueAiModelOptions, nodeInspector.liAnswerModel)"
                                    :disabled="isViewMode">
                                    <v-icon size="16">mdi-pencil-outline</v-icon>
                                </v-btn>
                                <v-row>
                                    <v-col cols="12">
                                        <v-autocomplete
                                            v-model="nodeInspector.liAnswerModel"
                                            :items="uniqueAiModelOptions" 
                                            item-title="searchableText" 
                                            item-value="value"
                                            label="Answer Model*" 
                                            variant="outlined" 
                                            density="comfortable" 
                                            hide-details="auto"
                                            prepend-inner-icon="mdi-robot"
                                            @update:model-value="updateNodeProperty('liAnswerModel', $event)"
                                            class="config-input" 
                                            :readonly="isViewMode"
                                            hint="Model for answering user questions" 
                                            persistent-hint 
                                            required>
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-bind="props" class="model-option"
                                                    :title="item.raw.value + ': ' + item.raw.name">
                                                    <template v-slot:prepend>
                                                        <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                                            <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                                        </div>
                                                    </template>
                                                </v-list-item>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12">
                                        <v-textarea
                                            v-model="nodeInspector.liFallbackPrompt"
                                            label="Fallback Prompt"
                                            variant="outlined"
                                            density="comfortable"
                                            hide-details="auto"
                                            rows="4"
                                            auto-grow
                                            prepend-inner-icon="mdi-text-box-outline"
                                            @update:model-value="updateNodeProperty('liFallbackPrompt', $event)"
                                            class="config-input"
                                            :readonly="isViewMode"
                                            hint="Prompt to use when main response fails"
                                            persistent-hint
                                            placeholder="Enter fallback prompt for error handling..." />
                                    </v-col>
                                </v-row>

                                <v-row>
                                    <v-col cols="12">
                                        <v-autocomplete 
                                            v-model="nodeInspector.liParserModel"
                                            :items="uniqueAiModelOptions" 
                                            item-title="searchableText" 
                                            item-value="value"
                                            label="Parser Model*" 
                                            variant="outlined" 
                                            density="comfortable" 
                                            hide-details="auto"
                                            prepend-inner-icon="mdi-code-json"
                                            @update:model-value="updateNodeProperty('liParserModel', $event)"
                                            class="config-input" 
                                            :readonly="isViewMode"
                                            hint="Model for converting questions to JSON parameters" 
                                            persistent-hint 
                                            required>
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-bind="props" class="model-option"
                                                    :title="item.raw.value + ': ' + item.raw.name">
                                                    <template v-slot:prepend>
                                                        <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                                            <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                                        </div>
                                                    </template>
                                                </v-list-item>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                            </div>
                            
                            <!-- Knowledge Configuration -->
                            <div class="config-section mb-6">
                                <h6 class="section-label mb-3">Knowledge Configuration</h6>
                                <v-row>
                                    <v-col cols="12">
                                        <!-- Document Selection -->
                                        <v-autocomplete 
                                            v-model="nodeInspector.liDocuments" 
                                            :items="DDVector" :no-data-text="kccDocsNoDataText" 
                                            item-title="displayName"
                                            item-value="name" 
                                            label="Documents" 
                                            variant="outlined" 
                                            density="comfortable" 
                                            hide-details="auto"
                                            chips 
                                            multiple 
                                            @update:model-value="updateNodeProperty('liDocuments', $event)"
                                            class="config-input" 
                                            placeholder="Select documents" 
                                            :clearable="!isViewMode" 
                                            :readonly="isViewMode"
                                            prepend-inner-icon="mdi-file-document"
                                            hint="Select documents for knowledge base"
                                            persistent-hint
                                            required>
                                            <template v-slot:chip="{ props, item }">
                                                <v-chip v-bind="props" :color="isDocumentMissing(item.value) ? 'error' : undefined" :variant="isDocumentMissing(item.value) ? 'flat' : 'tonal'">
                                                    <v-icon v-if="isDocumentMissing(item.value)" icon="mdi-alert-circle" size="16" class="me-1" />
                                                    {{ isDocumentMissing(item.value) ? item.value : item.title }}
                                                </v-chip>
                                            </template>
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-bind="props" class="document-option">
                                                    <template v-slot:prepend>
                                                        <v-icon size="20" color="primary">mdi-file-document</v-icon>
                                                    </template>
                                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                </v-list-item>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                            </div>

                            <!-- MetaDoc Configuration -->
                            <div class="config-section">
                                <h6 class="section-label mb-3">MetaDoc Configuration</h6>
                                <v-row>
                                    <v-col cols="12">
                                        <v-autocomplete
                                            v-model="nodeInspector.liV2MetaDocs"
                                            label="MetaDocs (เลือกได้มากกว่า 1 ค่า)*"
                                            :items="DDVector" :no-data-text="kccDocsNoDataText"
                                            item-title="displayName"
                                            item-value="name"
                                            multiple
                                            chips
                                            clearable
                                            variant="outlined"
                                            density="comfortable"
                                            hide-details="auto"
                                            color="primary"
                                            @update:model-value="updateNodeProperty('liV2MetaDocs', $event)"
                                            class="config-input"
                                            :readonly="isViewMode"
                                        >
                                            <template v-slot:chip="{ props, item }">
                                                <v-chip v-bind="props" :color="isDocumentMissing(item.value) ? 'error' : undefined" :variant="isDocumentMissing(item.value) ? 'flat' : 'tonal'">
                                                    <v-icon v-if="isDocumentMissing(item.value)" icon="mdi-alert-circle" size="16" class="me-1" />
                                                    {{ isDocumentMissing(item.value) ? item.value : item.title }}
                                                </v-chip>
                                            </template>
                                            <template v-slot:item="{ props, item }">
                                                <v-list-item v-bind="props" class="document-option">
                                                    <template v-slot:prepend>
                                                        <v-icon size="20" color="green">mdi-file-document-multiple</v-icon>
                                                    </template>
                                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                                </v-list-item>
                                            </template>
                                            <template v-slot:append-inner>
                                                <v-tooltip location="top">
                                                    <template v-slot:activator="{ props }">
                                                        <v-icon v-bind="props" size="small" color="grey-darken-1">mdi-information-outline</v-icon>
                                                    </template>
                                                    <span>เลือก MetaDoc ที่มี interest rate ผูกไว้ — ระบบจะดึง loan interest rates อัตโนมัติ</span>
                                                </v-tooltip>
                                            </template>
                                        </v-autocomplete>
                                    </v-col>
                                </v-row>
                            </div>

                            <!-- Required Fields Validation -->
                            <div class="config-section mt-6">
                                <h6 class="section-label mb-3">Required Fields Validation</h6>
                                <v-card variant="outlined" class="pa-3" style="border-radius: 12px; border-color: #FF8F00; background-color: #FFF8E1;">
                                    <div class="d-flex align-center mb-3">
                                        <v-avatar size="24" color="amber-darken-2" class="me-3">
                                            <v-icon color="white" size="12">mdi-shield-check</v-icon>
                                        </v-avatar>
                                        <div>
                                            <div class="text-subtitle-2" style="color:#E65100;font-weight:600;">Validate Parsed JSON Fields</div>
                                            <div class="text-caption" style="color:#5D4037;">เลือก field ที่ห้ามเป็น null, empty หรือ 0 หลังจาก LLM parse JSON</div>
                                        </div>
                                    </div>
                                    <div class="d-flex flex-wrap ga-2">
                                        <v-checkbox
                                            v-for="field in liValidationFields"
                                            :key="field.value"
                                            v-model="nodeInspector.liRequiredFields"
                                            :label="field.label"
                                            :value="field.value"
                                            density="compact"
                                            hide-details
                                            color="amber-darken-3"
                                            :readonly="isViewMode"
                                            @update:model-value="updateNodeProperty('liRequiredFields', $event)"
                                            class="me-2"
                                            style="color: #212121;"
                                        />
                                    </div>
                                </v-card>
                            </div>

                            <!-- Advanced Settings -->
                            <div class="config-section mt-6">
                                <h6 class="section-label mb-3">Advanced Settings</h6>
                                <v-text-field
                                    v-model.number="nodeInspector.nodeTimeout"
                                    type="number"
                                    min="0"
                                    label="Request Timeout (seconds)"
                                    hint="0 = use global timeout setting"
                                    persistent-hint
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details="auto"
                                    class="config-input"
                                    @update:model-value="updateNodeProperty('nodeTimeout', $event)"
                                    :readonly="isViewMode"
                                />
                                <v-text-field
                                    v-model.number="nodeInspector.nodeMaxRetry"
                                    type="number"
                                    min="0"
                                    label="Max Retry"
                                    hint="0 = use global max retry setting"
                                    persistent-hint
                                    variant="outlined"
                                    density="comfortable"
                                    hide-details="auto"
                                    class="config-input mt-3"
                                    @update:model-value="updateNodeProperty('nodeMaxRetry', $event)"
                                    :readonly="isViewMode"
                                />
                            </div>
                        </div>
                    </div>

                    <div v-else-if="selectedNode.data.type === 'switch'" class="form-section">
                        <h6 class="section-title">Switch Configuration</h6>
                        <v-text-field v-model="nodeInspector.switchName" label="Switch Name" variant="outlined"
                            density="compact" hide-details="auto" placeholder="Conditional Switch"
                            @update:model-value="updateNodeProperty('switchName', $event)" class="modern-input mb-3" />

                        <!-- Switch Outputs Management -->
                        <div class="switch-outputs-section">
                            <div class="section-header">
                                <h6 class="section-title">Output Paths</h6>
                                <v-btn size="small" color="primary" variant="tonal" @click="addInspectorSwitchOutput"
                                    class="add-btn">
                                    <v-icon left size="16">mdi-plus</v-icon>
                                    Add Output
                                </v-btn>
                            </div>

                            <div v-for="(output, outputIndex) in nodeInspector.switchOutputs" :key="output.id"
                                class="switch-output-card mb-3">
                                <v-expansion-panels variant="accordion" class="switch-output-expansion">
                                    <v-expansion-panel>
                                        <v-expansion-panel-title>
                                            <div class="d-flex align-center w-100">
                                                <v-icon class="mr-2" color="primary"
                                                    size="small">mdi-arrow-decision</v-icon>
                                                <span class="font-weight-medium">
                                                    {{
                                                        output.name || `Output ${outputIndex
                                                        + 1}`
                                                    }}
                                                </span>
                                                <v-spacer></v-spacer>
                                                <v-btn icon size="x-small" color="error" variant="text"
                                                    @click.stop="removeInspectorSwitchOutput(outputIndex)"
                                                    :disabled="nodeInspector.switchOutputs.length <= 2" class="ml-2">
                                                    <v-icon size="16">mdi-delete</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <div class="switch-output-content pa-2">
                                                <!-- Output Name -->
                                                <v-text-field v-model="nodeInspector.switchOutputs[outputIndex].name"
                                                    label="Output Name" variant="outlined" density="compact"
                                                    hide-details="auto" @update:model-value="updateSwitchData()"
                                                    class="modern-input mb-3" />

                                                <!-- Condition Builder -->
                                                <div class="condition-builder-section mb-3">
                                                    <div class="d-flex align-center mb-2">
                                                        <h6 class="condition-section-title">Condition Builder</h6>
                                                        <v-spacer></v-spacer>
                                                        <v-btn-toggle
                                                            v-model="nodeInspector.switchOutputs[outputIndex].conditionMode"
                                                            mandatory density="compact" variant="outlined"
                                                            class="condition-mode-toggle">
                                                            <v-btn value="simple" size="small">
                                                                <v-icon size="14" class="mr-1">mdi-cog</v-icon>
                                                                Simple
                                                            </v-btn>
                                                            <v-btn value="advanced" size="small">
                                                                <v-icon size="14" class="mr-1">mdi-code-braces</v-icon>
                                                                Advanced
                                                            </v-btn>
                                                        </v-btn-toggle>
                                                    </div>

                                                    <!-- Simple Condition Builder -->
                                                    <div v-if="!nodeInspector.switchOutputs[outputIndex].conditionMode || nodeInspector.switchOutputs[outputIndex].conditionMode === 'simple'"
                                                        class="simple-condition-builder">

                                                        <!-- Condition Type -->
                                                        <v-select
                                                            v-model="nodeInspector.switchOutputs[outputIndex].conditionType"
                                                            :items="[
                                                                { title: 'Text Contains', value: 'text_contains' },
                                                                { title: 'Text Equals', value: 'text_equals' },
                                                                { title: 'Number Comparison', value: 'number_comparison' },
                                                                { title: 'Boolean Check', value: 'boolean_check' },
                                                                { title: 'Regex Match', value: 'regex_match' },
                                                                { title: 'Length Check', value: 'length_check' }
                                                            ]" item-title="title" item-value="value"
                                                            label="Condition Type" variant="outlined" density="compact"
                                                            hide-details="auto" @update:model-value="updateSwitchData()"
                                                            class="modern-input mb-2" />

                                                        <!-- Dynamic Condition Fields -->
                                                        <div v-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'text_contains'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    label="Source Field" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="input.text"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].searchValue"
                                                                    label="Contains Text" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="keyword"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                            </div>
                                                            <v-checkbox
                                                                v-model="nodeInspector.switchOutputs[outputIndex].caseSensitive"
                                                                label="Case Sensitive" density="compact" hide-details
                                                                @update:model-value="updateSwitchData()" class="mt-1" />
                                                        </div>

                                                        <div v-else-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'text_equals'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    label="Source Field" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="input.text"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].compareValue"
                                                                    label="Equals To" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="exact text"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                            </div>
                                                            <v-checkbox
                                                                v-model="nodeInspector.switchOutputs[outputIndex].caseSensitive"
                                                                label="Case Sensitive" density="compact" hide-details
                                                                @update:model-value="updateSwitchData()" class="mt-1" />
                                                        </div>

                                                        <div v-else-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'number_comparison'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2 mb-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    label="Number Source" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="input.value"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-select
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].operator"
                                                                    :items="[
                                                                        { title: 'Greater than (>)', value: 'gt' },
                                                                        { title: 'Less than (<)', value: 'lt' },
                                                                        { title: 'Equal (=)', value: 'eq' },
                                                                        { title: 'Greater or equal (>=)', value: 'gte' },
                                                                        { title: 'Less or equal (<=)', value: 'lte' },
                                                                        { title: 'Not equal (!=)', value: 'ne' }
                                                                    ]" item-title="title" item-value="value"
                                                                    label="Operator" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].compareValue"
                                                                    label="Compare Value" type="number"
                                                                    variant="outlined" density="compact"
                                                                    hide-details="auto"
                                                                    @update:model-value="updateSwitchData()"
                                                                    @keypress="isNumberpositive($event)"
                                                                    class="modern-input" />
                                                            </div>
                                                        </div>

                                                        <div v-else-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'boolean_check'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    label="Boolean Source" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="user.isLoggedIn"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-select
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].expectedValue"
                                                                    :items="[
                                                                        { title: 'True', value: true },
                                                                        { title: 'False', value: false }
                                                                    ]" item-title="title" item-value="value"
                                                                    label="Expected Value" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                            </div>
                                                        </div>

                                                        <div v-else-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'regex_match'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    label="Source Field" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="input.text"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].regexPattern"
                                                                    label="Regex Pattern" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="^[0-9]+$"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                            </div>
                                                            <v-text-field
                                                                v-model="nodeInspector.switchOutputs[outputIndex].regexFlags"
                                                                label="Regex Flags (Optional)" variant="outlined"
                                                                density="compact" hide-details="auto" placeholder="gi"
                                                                @update:model-value="updateSwitchData()"
                                                                class="modern-input mt-2" />
                                                        </div>

                                                        <div v-else-if="nodeInspector.switchOutputs[outputIndex].conditionType === 'length_check'"
                                                            class="condition-fields">
                                                            <div class="d-flex gap-2">
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].sourceField"
                                                                    label="Source Field" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    placeholder="input.text"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-select
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].lengthOperator"
                                                                    :items="[
                                                                        { title: 'Greater than', value: 'gt' },
                                                                        { title: 'Less than', value: 'lt' },
                                                                        { title: 'Equal to', value: 'eq' },
                                                                        { title: 'Between', value: 'between' }
                                                                    ]" item-title="title" item-value="value"
                                                                    label="Length Operator" variant="outlined"
                                                                    density="compact" hide-details="auto"
                                                                    @update:model-value="updateSwitchData()"
                                                                    class="modern-input" />
                                                                <v-text-field
                                                                    v-model="nodeInspector.switchOutputs[outputIndex].lengthValue"
                                                                    label="Length Value" type="number"
                                                                    variant="outlined" density="compact"
                                                                    hide-details="auto"
                                                                    @update:model-value="updateSwitchData()"
                                                                    @keypress="isNumberpositive($event)"
                                                                    class="modern-input" />
                                                            </div>
                                                            <v-text-field
                                                                v-if="nodeInspector.switchOutputs[outputIndex].lengthOperator === 'between'"
                                                                v-model="nodeInspector.switchOutputs[outputIndex].lengthValueMax"
                                                                label="Max Length" type="number" variant="outlined"
                                                                density="compact" hide-details="auto"
                                                                @update:model-value="updateSwitchData()"
                                                                @keypress="isNumberpositive($event)"
                                                                class="modern-input mt-2" />
                                                        </div>

                                                        <!-- Generated Condition Preview -->
                                                        <v-alert
                                                            v-if="nodeInspector.switchOutputs[outputIndex].conditionType"
                                                            type="info" variant="tonal" density="compact" class="mt-3">
                                                            <template v-slot:prepend>
                                                                <v-icon>mdi-code-braces</v-icon>
                                                            </template>
                                                            <div class="text-caption">
                                                                <strong>Generated Condition:</strong><br>
                                                                <code class="condition-preview">
                                                            {{
                                                                generateConditionPreview(nodeInspector.switchOutputs[outputIndex])
                                                            }}
                                                        </code>
                                                            </div>
                                                        </v-alert>
                                                    </div>

                                                    <!-- Advanced Condition Builder -->
                                                    <div v-else class="advanced-condition-builder">
                                                        <v-textarea
                                                            v-model="nodeInspector.switchOutputs[outputIndex].condition"
                                                            label="Custom Condition Expression" variant="outlined"
                                                            density="compact" rows="3" auto-grow hide-details="auto"
                                                            placeholder="input.text.toLowerCase().includes('hello') && input.confidence > 0.8"
                                                            @update:model-value="updateSwitchData()"
                                                            class="modern-input" />

                                                        <div class="condition-hints mt-2">
                                                            <v-chip-group column>
                                                                <v-chip size="small" variant="outlined"
                                                                    @click="insertConditionHelper(outputIndex, 'input.text')"
                                                                    class="hint-chip">input.text</v-chip>
                                                                <v-chip size="small" variant="outlined"
                                                                    @click="insertConditionHelper(outputIndex, 'input.confidence')"
                                                                    class="hint-chip">input.confidence</v-chip>
                                                                <v-chip size="small" variant="outlined"
                                                                    @click="insertConditionHelper(outputIndex, 'user.data')"
                                                                    class="hint-chip">user.data</v-chip>
                                                                <v-chip size="small" variant="outlined"
                                                                    @click="insertConditionHelper(outputIndex, 'context.variables')"
                                                                    class="hint-chip">context.variables</v-chip>
                                                            </v-chip-group>
                                                        </div>
                                                    </div>
                                                </div>

                                                <!-- Output Description -->
                                                <v-textarea
                                                    v-model="nodeInspector.switchOutputs[outputIndex].description"
                                                    label="Description" variant="outlined" density="compact" rows="1"
                                                    auto-grow hide-details="auto" placeholder="Optional description..."
                                                    @update:model-value="updateSwitchData()" class="modern-input" />
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="selectedNode.data.type === 'condition'" class="form-section">
                        <h6 class="section-title">Condition Configuration</h6>

                        <!-- Condition Type -->
                        <v-select v-model="nodeInspector.conditionType" :items="[
                            { title: 'Value Comparison', value: 'value_comparison' },
                            { title: 'Text Contains', value: 'text_contains' },
                            { title: 'Number Range', value: 'number_range' },
                            { title: 'Boolean Check', value: 'boolean_check' },
                            { title: 'Custom Expression', value: 'custom_expression' }
                        ]" item-title="title" item-value="value" label="Condition Type" variant="outlined"
                            density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('conditionType', $event)"
                            class="modern-input mb-3" />

                        <!-- Condition Builder based on type -->
                        <div v-if="nodeInspector.conditionType === 'value_comparison'" class="condition-builder mb-3">
                            <div class="row-container">
                                <v-text-field v-model="nodeInspector.leftOperand" label="Left Value" variant="outlined"
                                    density="compact" hide-details="auto" placeholder="e.g., input.text"
                                    @update:model-value="updateNodeProperty('leftOperand', $event)"
                                    class="modern-input mb-2" />

                                <v-select v-model="nodeInspector.operator" :items="[
                                    { title: 'Equals', value: 'equals' },
                                    { title: 'Not Equals', value: 'not_equals' },
                                    { title: 'Greater Than', value: 'greater_than' },
                                    { title: 'Less Than', value: 'less_than' },
                                    { title: 'Greater or Equal', value: 'greater_equal' },
                                    { title: 'Less or Equal', value: 'less_equal' }
                                ]" item-title="title" item-value="value" label="Operator" variant="outlined"
                                    density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('operator', $event)"
                                    class="modern-input mb-2" />

                                <v-text-field v-model="nodeInspector.rightOperand" label="Right Value"
                                    variant="outlined" density="compact" hide-details="auto" placeholder="e.g., 'hello'"
                                    @update:model-value="updateNodeProperty('rightOperand', $event)"
                                    class="modern-input mb-2" />
                            </div>
                        </div>

                        <div v-else-if="nodeInspector.conditionType === 'text_contains'" class="condition-builder mb-3">
                            <v-text-field v-model="nodeInspector.leftOperand" label="Text Source" variant="outlined"
                                density="compact" hide-details="auto" placeholder="e.g., input.text"
                                @update:model-value="updateNodeProperty('leftOperand', $event)"
                                class="modern-input mb-2" />

                            <v-text-field v-model="nodeInspector.rightOperand" label="Contains Text" variant="outlined"
                                density="compact" hide-details="auto" placeholder="e.g., 'keyword'"
                                @update:model-value="updateNodeProperty('rightOperand', $event)"
                                class="modern-input mb-2" />
                        </div>

                        <div v-else-if="nodeInspector.conditionType === 'number_range'" class="condition-builder mb-3">
                            <v-text-field v-model="nodeInspector.leftOperand" label="Number Source" variant="outlined"
                                density="compact" hide-details="auto" placeholder="e.g., input.value"
                                @update:model-value="updateNodeProperty('leftOperand', $event)"
                                class="modern-input mb-2" />

                            <div class="d-flex gap-2">
                                <v-text-field v-model="nodeInspector.minValue" label="Min Value" type="number"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('minValue', $event)"
                                    @keypress="isNumberpositive($event)" class="modern-input" />

                                <v-text-field v-model="nodeInspector.maxValue" label="Max Value" type="number"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('maxValue', $event)"
                                    @keypress="isNumberpositive($event)" class="modern-input" />
                            </div>
                        </div>

                        <div v-else-if="nodeInspector.conditionType === 'boolean_check'" class="condition-builder mb-3">
                            <v-text-field v-model="nodeInspector.leftOperand" label="Boolean Source" variant="outlined"
                                density="compact" hide-details="auto" placeholder="e.g., user.isLoggedIn"
                                @update:model-value="updateNodeProperty('leftOperand', $event)"
                                class="modern-input mb-2" />

                            <v-switch v-model="nodeInspector.expectedValue" label="Expected Value" color="primary"
                                hide-details @update:model-value="updateNodeProperty('expectedValue', $event)"
                                class="modern-switch" />
                        </div>

                        <div v-else-if="nodeInspector.conditionType === 'custom_expression'"
                            class="condition-builder mb-3">
                            <v-textarea v-model="nodeInspector.conditionExpression" label="Custom Expression"
                                variant="outlined" density="compact" rows="2" auto-grow hide-details="auto"
                                placeholder="e.g., input.text.length > 5 && input.text.includes('hello')"
                                @update:model-value="updateNodeProperty('conditionExpression', $event)"
                                class="modern-input" />
                        </div>

                        <!-- Output Labels -->
                        <div class="output-labels-section">
                            <h6 class="section-title mb-2">Output Labels</h6>
                            <div class="d-flex gap-2">
                                <v-text-field v-model="nodeInspector.trueLabel" label="True Output Label"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('trueLabel', $event)"
                                    class="modern-input" />

                                <v-text-field v-model="nodeInspector.falseLabel" label="False Output Label"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('falseLabel', $event)"
                                    class="modern-input" />
                            </div>
                        </div>

                        <!-- Description -->
                        <v-textarea v-model="nodeInspector.description" label="Description" variant="outlined"
                            density="compact" rows="2" auto-grow hide-details="auto"
                            placeholder="Optional description of this condition..."
                            @update:model-value="updateNodeProperty('description', $event)" class="modern-input mt-3" />
                    </div>

                    <!-- JSON Condition Inspector -->
                    <div v-else-if="selectedNode.data.type === 'jsonCondition'" class="form-section">
                        <h6 class="section-title">JSON Condition Configuration</h6>

                        <!-- Condition Name -->
                        <v-text-field v-model="nodeInspector.jsonConditionName" label="Condition Name"
                            variant="outlined" density="compact" hide-details="auto"
                            placeholder="e.g., Check Matches"
                            @update:model-value="updateJsonConditionData()"
                            class="modern-input mb-3" />

                        <!-- Match Mode -->
                        <v-select v-model="nodeInspector.matchMode" :items="[
                            { title: 'ALL conditions must be true (AND)', value: 'all' },
                            { title: 'ANY condition must be true (OR)', value: 'any' }
                        ]" item-title="title" item-value="value" label="Match Mode" variant="outlined"
                            density="compact" hide-details="auto"
                            @update:model-value="updateJsonConditionData()"
                            class="modern-input mb-3" />

                        <!-- Conditions List -->
                        <div class="conditions-list">
                            <div class="d-flex justify-space-between align-center mb-2">
                                <h6 class="section-title mb-0">Rules ({{ nodeInspector.jsonConditions?.length || 0 }})</h6>
                                <v-btn size="small" color="teal" variant="text" @click="addJsonConditionRule()">
                                    <v-icon size="16" class="mr-1">mdi-plus</v-icon> Add Rule
                                </v-btn>
                            </div>

                            <v-expansion-panels v-model="jsonConditionExpandedPanel" variant="accordion" class="mb-3">
                                <v-expansion-panel
                                    v-for="(condition, index) in nodeInspector.jsonConditions"
                                    :key="condition.id"
                                    class="json-condition-rule-panel">
                                    <v-expansion-panel-title class="py-2">
                                        <div class="d-flex align-center justify-space-between w-100">
                                            <span class="text-body-2">
                                                <strong>Rule {{ index + 1 }}:</strong>
                                                {{ condition.jsonPath || '(empty)' }}
                                                {{ getJsonConditionOperatorLabel(condition.operator) }}
                                                {{ condition.compareValue || '' }}
                                            </span>
                                            <v-btn v-if="nodeInspector.jsonConditions.length > 1"
                                                icon size="x-small" variant="text" color="red"
                                                @click.stop="removeJsonConditionRule(index)">
                                                <v-icon size="14">mdi-delete</v-icon>
                                            </v-btn>
                                        </div>
                                    </v-expansion-panel-title>
                                    <v-expansion-panel-text>
                                        <div class="pa-1">
                                            <!-- JSON Path -->
                                            <v-text-field v-model="condition.jsonPath" label="JSON Path"
                                                variant="outlined" density="compact" hide-details="auto"
                                                placeholder="e.g., matches, data.items[0].score"
                                                @update:model-value="updateJsonConditionData()"
                                                class="modern-input mb-2" />

                                            <!-- Operator -->
                                            <v-select v-model="condition.operator" :items="jsonConditionOperators"
                                                item-title="title" item-value="value" label="Operator"
                                                variant="outlined" density="compact" hide-details="auto"
                                                @update:model-value="updateJsonConditionData()"
                                                class="modern-input mb-2" />

                                            <!-- Compare Value -->
                                            <v-text-field
                                                v-if="!['is_empty', 'is_not_empty', 'array_empty', 'array_not_empty', 'exists', 'not_exists', 'is_true', 'is_false'].includes(condition.operator)"
                                                v-model="condition.compareValue" label="Compare Value"
                                                variant="outlined" density="compact" hide-details="auto"
                                                placeholder="Value to compare against"
                                                @update:model-value="updateJsonConditionData()"
                                                class="modern-input" />
                                        </div>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>
                            </v-expansion-panels>

                            <!-- Quick add hint -->
                            <div v-if="!nodeInspector.jsonConditions || nodeInspector.jsonConditions.length === 0"
                                class="text-center text-grey py-3" style="font-size: 12px;">
                                Click "Add Rule" to create your first condition
                            </div>
                        </div>

                        <!-- Output Labels -->
                        <div class="output-labels-section mt-3">
                            <h6 class="section-title mb-2">Output Labels</h6>
                            <div class="d-flex gap-2">
                                <v-text-field v-model="nodeInspector.matchLabel" label="Match Label"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateJsonConditionData()"
                                    class="modern-input" />

                                <v-text-field v-model="nodeInspector.fallbackLabel" label="Fallback Label"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateJsonConditionData()"
                                    class="modern-input" />
                            </div>
                        </div>
                    </div>

                    <!-- Service Fallback Inspector -->
                    <div v-else-if="selectedNode.data.type === 'serviceFallback'" class="form-section">
                        <h6 class="section-title">Service Fallback Configuration</h6>

                        <!-- Service Type -->
                        <v-select v-model="nodeInspector.serviceType" :items="[
                            { title: 'Transfer (โอนเงิน)', value: 'TRANSFER' },
                            { title: 'Top Up (เติมเงิน)', value: 'TOPUP' },
                            { title: 'Bill Payment (ชำระเงิน)', value: 'BILL_PAYMENT' }
                        ]" item-title="title" item-value="value" label="Service Type" variant="outlined"
                            density="compact" hide-details="auto"
                            @update:model-value="onServiceTypeChange($event)"
                            class="modern-input mb-3" />

                        <!-- Output Messages (primary / success path) — one match -->
                        <h6 class="section-title mt-2">Output Messages (1 result)</h6>
                        <v-textarea v-model="nodeInspector.serviceFallbackPrimarySingleMessageTH"
                            label="🇹🇭 Thai Message" variant="outlined" density="compact"
                            rows="2" auto-grow hide-details="auto"
                            @update:model-value="updateServiceFallbackData()"
                            class="modern-input mb-2" />
                        <v-textarea v-model="nodeInspector.serviceFallbackPrimarySingleMessageEN"
                            label="🇬🇧 English Message" variant="outlined" density="compact"
                            rows="2" auto-grow hide-details="auto"
                            @update:model-value="updateServiceFallbackData()"
                            class="modern-input mb-2" />
                        <v-textarea v-model="nodeInspector.serviceFallbackPrimarySingleMessageCN"
                            label="🇨🇳 Chinese Message" variant="outlined" density="compact"
                            rows="2" auto-grow hide-details="auto"
                            @update:model-value="updateServiceFallbackData()"
                            class="modern-input mb-3" />

                        <!-- Output Messages (primary / success path) — more than one match -->
                        <h6 class="section-title mt-2">Output Messages (2+ results)</h6>
                        <v-textarea v-model="nodeInspector.serviceFallbackPrimaryMessageTH"
                            label="🇹🇭 Thai Message" variant="outlined" density="compact"
                            rows="2" auto-grow hide-details="auto"
                            @update:model-value="updateServiceFallbackData()"
                            class="modern-input mb-2" />
                        <v-textarea v-model="nodeInspector.serviceFallbackPrimaryMessageEN"
                            label="🇬🇧 English Message" variant="outlined" density="compact"
                            rows="2" auto-grow hide-details="auto"
                            @update:model-value="updateServiceFallbackData()"
                            class="modern-input mb-2" />
                        <v-textarea v-model="nodeInspector.serviceFallbackPrimaryMessageCN"
                            label="🇨🇳 Chinese Message" variant="outlined" density="compact"
                            rows="2" auto-grow hide-details="auto"
                            @update:model-value="updateServiceFallbackData()"
                            class="modern-input mb-3" />

                        <!-- Output JSON (primary / success path) -->
                        <h6 class="section-title mt-2">Output JSON</h6>
                        <v-switch v-model="nodeInspector.serviceFallbackPrimaryAllowEditOutput"
                            label="Allow custom output editing"
                            color="teal" density="compact" hide-details="auto"
                            @update:model-value="updateServiceFallbackData()"
                            class="modern-switch mb-2" />
                        <v-textarea v-model="nodeInspector.serviceFallbackPrimaryOutputJson"
                            label="Output JSON" variant="outlined" density="compact"
                            rows="8" auto-grow hide-details="auto"
                            :readonly="!nodeInspector.serviceFallbackPrimaryAllowEditOutput"
                            :bg-color="nodeInspector.serviceFallbackPrimaryAllowEditOutput ? '' : 'grey-lighten-4'"
                            @update:model-value="updateServiceFallbackData()"
                            class="modern-input" style="font-family: monospace; font-size: 11px;" />

                        <!-- Fallback Output Messages -->
                        <h6 class="section-title mt-2">Fallback Output Messages</h6>
                        <v-textarea v-model="nodeInspector.serviceFallbackMessageTH"
                            label="Thai Message" variant="outlined" density="compact"
                            rows="2" auto-grow hide-details="auto"
                            @update:model-value="updateServiceFallbackData()"
                            class="modern-input mb-2" />
                        <v-textarea v-model="nodeInspector.serviceFallbackMessageEN"
                            label="English Message" variant="outlined" density="compact"
                            rows="2" auto-grow hide-details="auto"
                            @update:model-value="updateServiceFallbackData()"
                            class="modern-input mb-2" />
                        <v-textarea v-model="nodeInspector.serviceFallbackMessageCN"
                            label="Chinese Message" variant="outlined" density="compact"
                            rows="2" auto-grow hide-details="auto"
                            @update:model-value="updateServiceFallbackData()"
                            class="modern-input mb-3" />

                        <!-- Fallback Output JSON Preview -->
                        <h6 class="section-title mt-2">Fallback Output JSON</h6>
                        <v-switch v-model="nodeInspector.serviceFallbackAllowEditOutput"
                            label="Allow custom output editing"
                            color="teal" density="compact" hide-details="auto"
                            @update:model-value="updateServiceFallbackData()"
                            class="modern-switch mb-2" />
                        <v-textarea v-model="nodeInspector.serviceFallbackOutputJson"
                            label="Fallback Output JSON" variant="outlined" density="compact"
                            rows="8" auto-grow hide-details="auto"
                            :readonly="!nodeInspector.serviceFallbackAllowEditOutput"
                            :bg-color="nodeInspector.serviceFallbackAllowEditOutput ? '' : 'grey-lighten-4'"
                            @update:model-value="updateServiceFallbackData()"
                            class="modern-input" style="font-family: monospace; font-size: 11px;" />
                    </div>

                    <div v-else-if="selectedNode.data.type === 'delay'" class="form-section">
                        <h6 class="section-title">Delay Configuration</h6>

                        <!-- Delay Type -->
                        <v-select v-model="nodeInspector.delayType" :items="[
                            { title: 'Fixed Duration', value: 'fixed' },
                            { title: 'Random Range', value: 'random' },
                            { title: 'Variable Delay', value: 'variable' }
                        ]" item-title="title" item-value="value" label="Delay Type" variant="outlined"
                            density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('delayType', $event)" class="modern-input mb-3" />

                        <!-- Fixed Duration Configuration -->
                        <div v-if="nodeInspector.delayType === 'fixed'" class="delay-config-section mb-3">
                            <div class="d-flex gap-2">
                                <v-text-field v-model="nodeInspector.delayValue" label="Duration" type="number" min="0"
                                    step="0.1" variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('delayValue', $event)"
                                    @keypress="isNumberpositive($event)" class="modern-input flex-grow-1" />

                                <v-select v-model="nodeInspector.delayUnit" :items="[
                                    { title: 'Seconds', value: 'seconds' },
                                    { title: 'Minutes', value: 'minutes' },
                                    { title: 'Hours', value: 'hours' }
                                ]" item-title="title" item-value="value" label="Unit" variant="outlined"
                                    density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('delayUnit', $event)" class="modern-input"
                                    style="min-width: 100px;" />
                            </div>
                        </div>

                        <!-- Random Range Configuration -->
                        <div v-else-if="nodeInspector.delayType === 'random'" class="delay-config-section mb-3">
                            <div class="d-flex gap-2 mb-2">
                                <v-text-field v-model="nodeInspector.randomMin" label="Min Duration" type="number"
                                    min="0" step="0.1" variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('randomMin', $event)"
                                    class="modern-input" />

                                <v-text-field v-model="nodeInspector.randomMax" label="Max Duration" type="number"
                                    min="0" step="0.1" variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('randomMax', $event)"
                                    class="modern-input" />
                            </div>

                            <v-select v-model="nodeInspector.delayUnit" :items="[
                                { title: 'Seconds', value: 'seconds' },
                                { title: 'Minutes', value: 'minutes' },
                                { title: 'Hours', value: 'hours' }
                            ]" item-title="title" item-value="value" label="Unit" variant="outlined" density="compact"
                                hide-details="auto" @update:model-value="updateNodeProperty('delayUnit', $event)"
                                class="modern-input" />
                        </div>

                        <!-- Variable Delay Configuration -->
                        <div v-else-if="nodeInspector.delayType === 'variable'" class="delay-config-section mb-3">
                            <v-text-field v-model="nodeInspector.delayExpression" label="Delay Expression"
                                variant="outlined" density="compact" hide-details="auto"
                                placeholder="e.g., input.length * 0.5"
                                @update:model-value="updateNodeProperty('delayExpression', $event)"
                                class="modern-input mb-2" />

                            <v-select v-model="nodeInspector.delayUnit" :items="[
                                { title: 'Seconds', value: 'seconds' },
                                { title: 'Minutes', value: 'minutes' },
                                { title: 'Hours', value: 'hours' }
                            ]" item-title="title" item-value="value" label="Unit" variant="outlined" density="compact"
                                hide-details="auto" @update:model-value="updateNodeProperty('delayUnit', $event)"
                                class="modern-input" />
                        </div>

                        <!-- Delay Message -->
                        <v-text-field v-model="nodeInspector.delayMessage" label="Delay Message (Optional)"
                            variant="outlined" density="compact" hide-details="auto"
                            placeholder="Please wait while processing..."
                            @update:model-value="updateNodeProperty('delayMessage', $event)"
                            class="modern-input mb-3" />

                        <!-- Progress Indicator -->
                        <v-switch v-model="nodeInspector.showProgress" label="Show Progress Indicator" color="primary"
                            hide-details @update:model-value="updateNodeProperty('showProgress', $event)"
                            class="modern-switch mb-3" />

                        <!-- Duration Preview -->
                        <div class="duration-preview">
                            <v-alert type="info" variant="tonal" density="compact" class="mb-2">
                                <template v-slot:prepend>
                                    <v-icon>mdi-clock-outline</v-icon>
                                </template>
                                <div class="text-caption">
                                    <strong>Duration Preview:</strong><br>
                                    <span v-if="nodeInspector.delayType === 'fixed'">
                                        {{ nodeInspector.delayValue }} {{ nodeInspector.delayUnit }}
                                    </span>
                                    <span v-else-if="nodeInspector.delayType === 'random'">
                                        {{ nodeInspector.randomMin }} - {{ nodeInspector.randomMax }} {{
                                            nodeInspector.delayUnit
                                        }}
                                    </span>
                                    <span v-else-if="nodeInspector.delayType === 'variable'">
                                        Variable based on expression
                                    </span>
                                </div>
                            </v-alert>
                        </div>

                        <!-- Description -->
                        <v-textarea v-model="nodeInspector.description" label="Description" variant="outlined"
                            density="compact" rows="2" auto-grow hide-details="auto"
                            placeholder="Optional description of this delay..."
                            @update:model-value="updateNodeProperty('description', $event)" class="modern-input" />
                    </div>

                    <!-- LLM Enrich Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'buttonStyle'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-gesture-tap-button" size="24" color="purple" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-primary mb-0">Button Style Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">แปลงปุ่ม |productbutton| และ |servicebutton|
                                ที่ agent ตอบ ให้เป็นปุ่มพร้อมสไตล์ — ค่าในวงเล็บเดิมจะถูกใส่ที่ <code>value</code>
                                ของ JSON ด้านล่าง ข้อความส่วนอื่นของคำตอบไม่ถูกแตะ</p>
                        </div>

                        <!-- Node Label -->
                        <div class="config-section mb-6">
                            <h6 class="section-label mb-3">Node Label</h6>
                            <v-row>
                                <v-col cols="12">
                                    <v-text-field v-model="nodeInspector.buttonStyleName"
                                        label="Node Label" variant="outlined"
                                        density="comfortable" hide-details="auto"
                                        prepend-inner-icon="mdi-tag-outline"
                                        placeholder="Style |productbutton| / |servicebutton|"
                                        @update:model-value="updateNodeProperty('buttonStyleName', $event)"
                                        class="config-input" :readonly="isViewMode"
                                        hint="Override the node subtitle shown on the canvas" persistent-hint
                                        data-testid="buttonstyle-name" />
                                </v-col>
                            </v-row>
                        </div>

                        <v-alert type="info" variant="tonal" density="compact" class="mb-6">
                            <div class="text-caption">ก่อน: <code>|servicebutton|[ค้นหาสาขาใกล้ฉัน](FIND_BRANCH)</code></div>
                            <div class="text-caption">หลัง: <code>|servicebutton|[ค้นหาสาขาใกล้ฉัน]({"value":"FIND_BRANCH", …สไตล์จาก config})</code></div>
                            <div class="text-caption mt-2">แขวน node นี้ใต้ Agent (ลากจาก handle สีส้มด้านล่างของ Agent มาที่ node นี้)
                                มีผลเฉพาะคำตอบของ agent นั้น และปิด streaming ของ agent นั้นโดยอัตโนมัติ ·
                                เว้นว่าง = ใช้ค่าเริ่มต้น · JSON ไม่ถูกต้อง = ปุ่มชนิดนั้นถูกปล่อยไว้ตามเดิม ·
                                key <code>value</code> ถูกแทนด้วยค่าในวงเล็บเสมอ</div>
                        </v-alert>

                        <!-- Product button style -->
                        <div class="config-section mb-6">
                            <div class="d-flex align-center justify-space-between mb-3">
                                <h6 class="section-label mb-0">Product button — <code>|productbutton|</code></h6>
                                <v-btn size="x-small" variant="text" color="primary" prepend-icon="mdi-restore"
                                    :disabled="isViewMode" @click="resetButtonStyle('productButtonStyle')"
                                    data-testid="buttonstyle-product-reset">ค่าเริ่มต้น</v-btn>
                            </div>
                            <v-textarea v-model="nodeInspector.productButtonStyle"
                                label="JSON สไตล์ปุ่ม product" variant="outlined"
                                density="comfortable" auto-grow rows="6" hide-details="auto"
                                class="config-input" :readonly="isViewMode"
                                :error-messages="buttonStyleJsonErrors(nodeInspector.productButtonStyle)"
                                :hint="'ตัวอย่างผลลัพธ์: ' + buttonStylePreview('productbutton', 'ซื้อผลิตภัณฑ์', 'https://lhbank.example/fund?code=B-TOP', nodeInspector.productButtonStyle)"
                                persistent-hint
                                @update:model-value="updateNodeProperty('productButtonStyle', $event)"
                                data-testid="buttonstyle-product" />
                        </div>

                        <!-- Service button style -->
                        <div class="config-section">
                            <div class="d-flex align-center justify-space-between mb-3">
                                <h6 class="section-label mb-0">Service button — <code>|servicebutton|</code></h6>
                                <v-btn size="x-small" variant="text" color="primary" prepend-icon="mdi-restore"
                                    :disabled="isViewMode" @click="resetButtonStyle('serviceButtonStyle')"
                                    data-testid="buttonstyle-service-reset">ค่าเริ่มต้น</v-btn>
                            </div>
                            <v-textarea v-model="nodeInspector.serviceButtonStyle"
                                label="JSON สไตล์ปุ่ม service" variant="outlined"
                                density="comfortable" auto-grow rows="6" hide-details="auto"
                                class="config-input" :readonly="isViewMode"
                                :error-messages="buttonStyleJsonErrors(nodeInspector.serviceButtonStyle)"
                                :hint="'ตัวอย่างผลลัพธ์: ' + buttonStylePreview('servicebutton', 'ค้นหาสาขาใกล้ฉัน', 'FIND_BRANCH', nodeInspector.serviceButtonStyle)"
                                persistent-hint
                                @update:model-value="updateNodeProperty('serviceButtonStyle', $event)"
                                data-testid="buttonstyle-service" />
                        </div>
                    </div>

                    <div v-else-if="selectedNode.data.type === 'llmEnrich'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-text-box-plus" size="24" color="teal" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-primary mb-0">LLM Enrich Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Send the incoming input to the LLM, then
                                append its result to the original input</p>
                        </div>

                        <!-- Node Label -->
                        <div class="config-section mb-6">
                            <h6 class="section-label mb-3">Node Label</h6>
                            <v-row>
                                <v-col cols="12">
                                    <v-text-field v-model="nodeInspector.llmEnrichName"
                                        label="Node Label" variant="outlined"
                                        density="comfortable" hide-details="auto"
                                        prepend-inner-icon="mdi-tag-outline"
                                        placeholder="Append LLM result"
                                        @update:model-value="updateNodeProperty('llmEnrichName', $event)"
                                        class="config-input" :readonly="isViewMode"
                                        hint="Override the node subtitle shown on the canvas" persistent-hint
                                        data-testid="llmenrich-name" />
                                </v-col>
                            </v-row>
                        </div>

                        <!-- LLM Settings (same fields/look as AI Model) -->
                        <v-expansion-panels v-model="modelConfigPanels" multiple class="config-panels elevation-0"
                            variant="accordion">
                            <!-- Basic Information Panel -->
                            <v-expansion-panel value="basic" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">LLM · Basic Information</div>
                                            <div class="panel-subtitle">Provider and model</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-6">
                                            <h6 class="section-label mb-3">Service Configuration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.provider"
                                                        :items="listDDProvider" item-title="providerName"
                                                        item-value="providerName" label="Provider*" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-cloud-outline"
                                                        @update:model-value="updateNodeProperty('provider', $event); changeProvider($event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select AI service provider" persistent-hint required
                                                        data-testid="llmenrich-provider" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.model" :items="DDModelShow"
                                                        item-title="modelDisplay" item-value="modelName" label="Model*"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-brain"
                                                        @update:model-value="updateNodeProperty('model', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :disabled="!nodeInspector.provider"
                                                        hint="Select specific model version" persistent-hint required
                                                        data-testid="llmenrich-model" />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- System Prompt Panel -->
                            <v-expansion-panel value="prompt" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-text-box-outline" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">LLM · System Prompt</div>
                                            <div class="panel-subtitle">Guidance for the LLM call</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section">
                                            <h6 class="section-label mb-2">System Prompt</h6>
                                            <v-textarea v-model="nodeInspector.systemPrompt"
                                                label="System Prompt" variant="outlined" counter auto-grow rows="6"
                                                @update:model-value="updateNodeProperty('systemPrompt', $event)"
                                                :readonly="isViewMode"
                                                hint="Instruction sent to the LLM together with the incoming input"
                                                persistent-hint
                                                placeholder="How the LLM should process the input..."
                                                data-testid="llmenrich-systemprompt" />
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- AI Parameters Panel -->
                            <v-expansion-panel value="parameters" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">LLM · AI Parameters</div>
                                            <div class="panel-subtitle">Temperature, output, format</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Core Parameters</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.temperature"
                                                        label="Temperature*" type="number" step="0.1" min="0" max="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-thermometer"
                                                        @update:model-value="updateNodeProperty('temperature', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Creativity level (0 = focused, 1 = creative)"
                                                        persistent-hint data-testid="llmenrich-temperature" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.topP" label="Top P"
                                                        type="number" step="0.1" min="0" max="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-filter-variant"
                                                        @update:model-value="updateNodeProperty('topP', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Token selection probability threshold" persistent-hint
                                                        data-testid="llmenrich-topp" />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Output Control</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.maxTokens"
                                                        label="Max Tokens Response" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-check-outline"
                                                        @update:model-value="updateNodeProperty('maxTokens', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Maximum response length in tokens"
                                                        persistent-hint data-testid="llmenrich-maxtokens" />
                                                </v-col>
                                            </v-row>
                                            <!-- Json Response -->
                                            <div class="switch-option-compact mt-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.jsonResponse }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.jsonResponse ? 'primary' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.jsonResponse ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-code-json</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">JSON Response</div>
                                                            <div class="switch-description">Return structured JSON format
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.jsonResponse" color="primary"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('jsonResponse', $event)"
                                                        :readonly="isViewMode" inset
                                                        data-testid="llmenrich-jsonresponse" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Enrich Settings Panel -->
                            <v-expansion-panel value="enrich" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-text-box-plus" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Enrich Settings</div>
                                            <div class="panel-subtitle">Connector text & error behavior</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                                            <div class="text-caption">Output = input เดิม + ข้อความเชื่อม + ผลลัพธ์ LLM
                                            </div>
                                        </v-alert>
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Connector Text</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-textarea v-model="nodeInspector.llmEnrichConnectorText"
                                                        label="ข้อความเชื่อม (Connector)" variant="outlined"
                                                        density="comfortable" auto-grow rows="3" hide-details="auto"
                                                        prepend-inner-icon="mdi-link-variant"
                                                        hint="แทรกระหว่าง input เดิมกับผลลัพธ์ LLM — รองรับขึ้นบรรทัดใหม่"
                                                        persistent-hint
                                                        @update:model-value="updateNodeProperty('llmEnrichConnectorText', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        data-testid="llmenrich-connector" />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Error Behavior</h6>
                                            <!-- Bypass on error -->
                                            <div class="switch-option-compact">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.bypassOnError }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.bypassOnError ? 'warning' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.bypassOnError ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-debug-step-over</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Bypass on error</div>
                                                            <div class="switch-description">Pass the original input
                                                                through if the LLM fails</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.bypassOnError" color="warning"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('bypassOnError', $event)"
                                                        :readonly="isViewMode" inset data-testid="llmenrich-bypass" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </div>

                    <!-- Documents Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'documents'" class="form-section">
                        <h6 class="section-title">Documents Configuration</h6>

                        <!-- Document Source Type Toggle (Group Doc hidden - use Document Filter Title/SubTitle instead) -->
                        <div class="mb-3" style="display: none;">
                            <div class="text-caption text-grey mb-1">Document Source</div>
                            <v-btn-toggle
                                v-model="nodeInspector.documentSourceType"
                                mandatory
                                density="compact"
                                color="primary"
                                :disabled="isViewMode"
                                @update:model-value="onDocumentSourceTypeChange($event)"
                            >
                                <v-btn value="doc" size="small">
                                    <v-icon start size="small">mdi-file-document</v-icon>
                                    Document
                                </v-btn>
                                <v-btn value="group" size="small">
                                    <v-icon start size="small">mdi-folder-multiple</v-icon>
                                    Group Doc
                                </v-btn>
                            </v-btn-toggle>
                        </div>

                        <!-- Document Selection (when sourceType = 'doc') -->
                        <v-autocomplete v-if="nodeInspector.documentSourceType !== 'group'"
                            v-model="nodeInspector.document" :items="DDVector" :no-data-text="kccDocsNoDataText" item-title="displayName"
                            item-value="name" label="Document" variant="outlined" density="compact" hide-details="auto"
                            chips multiple @update:model-value="updateNodeProperty('document', $event);"
                            @click:clear="resetDocumentDataAndSelection" class="modern-input mb-3"
                            placeholder="Select documents" :clearable="!isViewMode" :readonly="isViewMode">
                            <template v-slot:chip="{ props, item }">
                                <v-chip v-bind="props" :color="isDocumentMissing(item.value) ? 'error' : undefined" :variant="isDocumentMissing(item.value) ? 'flat' : 'tonal'">
                                    <v-icon v-if="isDocumentMissing(item.value)" icon="mdi-alert-circle" size="16" class="me-1" />
                                    {{ isDocumentMissing(item.value) ? item.value : item.title }}
                                </v-chip>
                            </template>
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="document-option">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="primary">mdi-file-document</v-icon>
                                    </template>
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>

                        <!-- Document Group Selection (when sourceType = 'group') -->
                        <v-autocomplete v-else
                            v-model="nodeInspector.documentGroupId"
                            :items="DDDocumentGroup"
                            item-title="name"
                            item-value="id"
                            label="Document Group"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            class="modern-input mb-3"
                            placeholder="Select document group"
                            :clearable="!isViewMode"
                            :readonly="isViewMode"
                            @update:model-value="onDocumentGroupChange($event)"
                        >
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="primary">mdi-folder-multiple</v-icon>
                                    </template>
                                    <template v-slot:append>
                                        <v-chip size="x-small" color="primary" variant="tonal">
                                            {{ item.raw.documentCount }} docs
                                        </v-chip>
                                    </template>
                                </v-list-item>
                            </template>
                            <template v-slot:selection="{ item }">
                                <v-chip color="primary" variant="tonal" size="small">
                                    <v-icon start size="small">mdi-folder-multiple</v-icon>
                                    {{ item.title }} ({{ item.raw.documentCount }} docs)
                                </v-chip>
                            </template>
                        </v-autocomplete>

                        <!-- App Knowledge Center: picker เอกสารแสดงเฉพาะของ Center / ดึงรายการไม่ได้ (โหมดปิดไม่แสดง) -->
                        <v-alert v-if="kccCenterMode && nodeInspector.documentSourceType !== 'group'"
                            :type="kccDocsUnavailable ? 'warning' : 'info'" variant="tonal" density="compact"
                            class="mb-3 text-caption">
                            {{ $t(kccDocsUnavailable || 'kccPickerCenterOnly') }}
                        </v-alert>

                        <!-- Document Data Selection Section (only for doc mode) -->
                        <div v-if="nodeInspector.documentSourceType !== 'group' && nodeInspector.document && nodeInspector.document.length > 0"
                            class="document-data-section mb-3">
                            <!-- Document Data Selection Header with Toggle -->
                            <div style="display: flex;align-items: center;justify-content: space-between;">
                                <!-- Enable Specific Data Selection Switch -->
                                <div style="display: flex; align-items: center;">
                                    <!-- App Knowledge Center (D4): เปิดไม่ได้ในโหมด Center (ปิดได้ — ล้างค่าที่เลือกไว้เดิม) -->
                                    <v-switch v-model="nodeInspector.enableSpecificDataSelection" color="primary"
                                        hide-details density="compact"
                                        @update:model-value="onSpecificDataSelectionChange($event)"
                                        :disabled="kccCenterMode && !nodeInspector.enableSpecificDataSelection"
                                        :readonly="isViewMode" class="mr-2" inset style="transform: scale(0.8);">
                                    </v-switch>
                                    <span class="text-subtitle-2">
                                        Use Specific Data Selection
                                    </span>
                                </div>
                                <!-- Expand/Collapse Button -->
                                <v-btn v-if="nodeInspector.enableSpecificDataSelection"
                                    @click="documentDataExpanded = !documentDataExpanded" icon variant="text"
                                    size="small" color="primary">
                                    <v-icon>{{ documentDataExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                </v-btn>
                            </div>
                            <v-alert v-if="kccCenterMode" type="warning" variant="tonal" density="compact"
                                class="mt-2 mb-2 text-caption">
                                {{ $t('kccSpecificDataUnavailable') }}
                            </v-alert>

                            <!-- Expandable Content -->
                            <v-expand-transition>
                                <div v-show="documentDataExpanded && nodeInspector.enableSpecificDataSelection"
                                    class="document-data-content pa-3 bg-grey-lighten-5 rounded-lg">

                                    <h6 class="section-title mb-0">
                                        <v-icon size="20" class="mr-2 mb-4" color="primary">mdi-file-search</v-icon>
                                        Select Document Data
                                    </h6>
                                    <!-- Vector Name Selection for Document Data -->
                                    <v-autocomplete v-if="!isViewMode" v-model="nodeInspector.documentVectorName"
                                        :items="nodeInspector.document" label="Select Document for Data"
                                        variant="outlined" density="compact" hide-details="auto"
                                        @update:model-value="updateNodeProperty('documentVectorName', $event); getDocumentData();"
                                        class="modern-input mb-3" clearable :readonly="isViewMode">
                                        <template v-slot:prepend-inner>
                                            <v-icon size="20" color="primary">mdi-file-document-outline</v-icon>
                                        </template>
                                        <template v-slot:append-inner>
                                            <v-tooltip location="top">
                                                <template v-slot:activator="{ props }">
                                                    <v-icon v-bind="props" size="small" color="grey-darken-1">
                                                        mdi-information-outline
                                                    </v-icon>
                                                </template>
                                                <span>Select a document to view its data</span>
                                            </v-tooltip>
                                        </template>
                                    </v-autocomplete>

                                    <!-- Selected Document Data Display -->
                                    <div class="selected-document-data mb-4"
                                        v-if="nodeInspector.selectedDocumentData && nodeInspector.selectedDocumentData.length > 0">
                                        <div class="mb-2">
                                            <span class="text-subtitle-2 font-weight-medium text-primary">
                                                <v-icon size="16" class="mr-1">mdi-check-circle</v-icon>
                                                Selected Document Data:
                                            </span>
                                        </div>
                                        <div class="d-flex flex-wrap ga-2">
                                            <v-chip v-for="(item, dataIndex) in nodeInspector.selectedDocumentData"
                                                :key="dataIndex + '_DocumentData'" :closable="!isViewMode"
                                                @click:close="deleteSelectedDocumentData(item.id)" color="primary"
                                                variant="elevated" size="small" class="selected-document-data-chip">
                                                <v-tooltip location="top" open-delay="500">
                                                    <template v-slot:activator="{ props }">
                                                        <span v-bind="props" class="chip-text">{{
                                                            item.textORG.length > 50 ? item.textORG.substring(0, 50) +
                                                                '...' : item.textORG
                                                        }}</span>
                                                    </template>
                                                    <span>{{ item.textORG }}</span>
                                                </v-tooltip>
                                            </v-chip>
                                        </div>
                                    </div>

                                    <div v-if="loadingDocumentDataTable && nodeInspector.documentVectorName"
                                        class="text-center mb-4">
                                        <v-progress-circular indeterminate color="primary"
                                            size="24"></v-progress-circular>

                                    </div>

                                    <!-- Document Data Table -->
                                    <v-data-table
                                        v-show="showDocumentDataTable && nodeInspector.enableSpecificDataSelection"
                                        :items-per-page="documentDataItemsPerPage"
                                        :server-items-length="documentDataTotalTable"
                                        v-model:options="documentDataOptions" :items="documentDataTable"
                                        :headers="documentDataHeaders" loading-text="Loading document data..."
                                        fixed-header class="elevation-2 document-data-table mb-4 rounded-lg"
                                        density="comfortable">
                                        <template v-slot:item="{ item }">
                                            <tr>
                                                <td class="text-center pa-2">
                                                    <v-checkbox v-model="item.selected"
                                                        @change="selectDocumentData(item.selected, item.id, item.textORG)"
                                                        hide-details density="compact" color="primary">
                                                    </v-checkbox>
                                                </td>
                                                <td class="pa-2">
                                                    <div class="text-container">
                                                        <span
                                                            :class="[item.show ? 'text-content-show' : 'text-content']"
                                                            v-html="formatText(item.textORG)">
                                                        </span>
                                                    </div>
                                                </td>
                                                <td class="pa-2">
                                                    <div class="text-container">
                                                        <span
                                                            :class="[item.show ? 'text-content-show' : 'text-content']"
                                                            v-html="formatText(item.textEN)">
                                                        </span>
                                                        <v-btn @click="item.show = !item.show" icon size="small"
                                                            variant="text" class="ml-2">
                                                            <v-icon>{{
                                                                item.show ? "mdi-chevron-up" : "mdi-chevron-down"
                                                            }}</v-icon>
                                                        </v-btn>
                                                    </div>
                                                </td>
                                            </tr>
                                        </template>
                                    </v-data-table>

                                </div>
                            </v-expand-transition>
                        </div>
                        <v-divider class="my-4"></v-divider>

                        <!-- Text Replacement -->
                        <v-autocomplete v-model="nodeInspector.textReplacement" :items="DDGroupReplace"
                            item-title="name" item-value="id" label="Text Replacement" variant="outlined"
                            density="compact" hide-details="auto" chips multiple
                            @update:model-value="updateNodeProperty('textReplacement', $event)"
                            class="modern-input mb-3" placeholder="Select text replacement groups"
                            :clearable="!isViewMode" :readonly="isViewMode">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="text-replacement-option">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="primary">mdi-find-replace</v-icon>
                                    </template>
                                    <!-- <v-list-item-title>{{ item.raw.name }}</v-list-item-title> -->
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>
                        <!-- Top-N Chunks -->
                        <v-text-field v-model="nodeInspector.topNChunks" type="number" label="Top-N Chunks"
                            variant="outlined" density="compact" hide-details="auto" :readonly="isViewMode" min="1"
                            step="1" @focus="$event.target.select()"
                            @blur="validateAndFixTopNChunks($event)"
                            @keydown.enter="validateAndFixTopNChunks($event)"
                            class="modern-input mb-3" />
                        <!-- Summary Memory (Embedding) -->
                        <v-divider class="my-4"></v-divider>
                        <h6 class="section-title">Summary Memory (Embedding)</h6>
                        <div class="text-caption text-grey mb-2">
                            When enabled, an LLM summarizes recent conversation turns + the latest message into the
                            search query so follow-up questions retrieve the right documents. Affects document search
                            only — the final answer is unaffected. Memory Turns = 0 embeds the latest message directly.
                        </div>
                        <v-switch v-model="nodeInspector.summaryMemoryEnabled" color="primary" hide-details
                            density="compact" inset :readonly="isViewMode" label="Enable Summary Memory"
                            @update:model-value="updateNodeProperty('summaryMemoryEnabled', $event)" class="mb-2" />
                        <template v-if="nodeInspector.summaryMemoryEnabled">
                            <v-autocomplete v-model="nodeInspector.summaryMemoryProvider" :items="listDDProvider"
                                item-title="providerName" item-value="providerName" label="Provider" variant="outlined"
                                density="compact" hide-details="auto" :readonly="isViewMode"
                                @update:model-value="updateNodeProperty('summaryMemoryProvider', $event); changeProvider($event)"
                                class="modern-input mb-3" />
                            <v-autocomplete v-model="nodeInspector.summaryMemoryModel" :items="DDModelShow"
                                item-title="modelDisplay" item-value="modelName" label="Model" variant="outlined"
                                density="compact" hide-details="auto" :readonly="isViewMode"
                                :disabled="!nodeInspector.summaryMemoryProvider"
                                @update:model-value="updateNodeProperty('summaryMemoryModel', $event)"
                                class="modern-input mb-3" />
                            <v-text-field v-if="nodeInspector.summaryMemoryApiVersion"
                                v-model="nodeInspector.summaryMemoryApiVersion" label="API Version" variant="outlined"
                                density="compact" hide-details="auto" readonly class="modern-input mb-3" />
                            <v-row>
                                <v-col cols="6">
                                    <v-text-field v-model.number="nodeInspector.summaryMemoryMemoryTurn" type="number"
                                        min="0" step="1" label="Memory Turns" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode" @focus="$event.target.select()"
                                        @update:model-value="updateNodeProperty('summaryMemoryMemoryTurn', $event)"
                                        class="modern-input" hint="0 = embed latest message only" />
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field v-model.number="nodeInspector.summaryMemoryMemoryDuration" type="number"
                                        min="1" step="1" label="Memory Duration (min)" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode" @focus="$event.target.select()"
                                        @update:model-value="updateNodeProperty('summaryMemoryMemoryDuration', $event)"
                                        class="modern-input" />
                                </v-col>
                            </v-row>
                            <v-row class="mt-1">
                                <v-col cols="6">
                                    <v-text-field v-model.number="nodeInspector.summaryMemoryTemperature" type="number"
                                        step="0.1" min="0" max="2" label="Temperature" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateNodeProperty('summaryMemoryTemperature', $event)"
                                        class="modern-input" />
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field v-model.number="nodeInspector.summaryMemoryTopP" type="number" step="0.1"
                                        min="0" max="1" label="Top P" variant="outlined" density="compact"
                                        hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateNodeProperty('summaryMemoryTopP', $event)"
                                        class="modern-input" />
                                </v-col>
                            </v-row>
                            <v-text-field v-model.number="nodeInspector.summaryMemoryMaxTokens" type="number" min="1"
                                label="Max Tokens" variant="outlined" density="compact" hide-details="auto"
                                :readonly="isViewMode"
                                @update:model-value="updateNodeProperty('summaryMemoryMaxTokens', $event)"
                                class="modern-input mt-3" />
                            <v-row class="mt-1">
                                <v-col cols="6">
                                    <v-text-field v-model.number="nodeInspector.summaryMemoryFrequencyPenalty"
                                        type="number" step="0.1" min="-2" max="2" label="Frequency Penalty"
                                        variant="outlined" density="compact" hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateNodeProperty('summaryMemoryFrequencyPenalty', $event)"
                                        class="modern-input" />
                                </v-col>
                                <v-col cols="6">
                                    <v-text-field v-model.number="nodeInspector.summaryMemoryPresencePenalty" type="number"
                                        step="0.1" min="-2" max="2" label="Presence Penalty" variant="outlined"
                                        density="compact" hide-details="auto" :readonly="isViewMode"
                                        @update:model-value="updateNodeProperty('summaryMemoryPresencePenalty', $event)"
                                        class="modern-input" />
                                </v-col>
                            </v-row>
                            <v-textarea v-model="nodeInspector.summaryMemorySystemPrompt" label="System Prompt"
                                variant="outlined" density="compact" auto-grow rows="6" hide-details="auto"
                                :readonly="isViewMode"
                                @update:model-value="updateNodeProperty('summaryMemorySystemPrompt', $event)"
                                class="modern-input mt-3"
                                placeholder="You are a conversation summarizer. Given the chat history and the latest user message, write a concise standalone search query that captures the full context." />
                            <v-text-field v-model.number="nodeInspector.summaryMemoryTimeout" type="number" min="0"
                                label="LLM Timeout (seconds)" variant="outlined" density="compact" hide-details="auto"
                                :readonly="isViewMode" @keypress="isNumberpositive($event)"
                                @update:model-value="updateNodeProperty('summaryMemoryTimeout', $event)"
                                class="modern-input mt-3" hint="0 = use global timeout setting" />
                        </template>
                        <v-divider class="my-4"></v-divider>
                        <!-- Cosine Similarity -->
                        <!-- <v-text-field v-model="nodeInspector.cosineSimilarity" type="number" label="Cosine Similarity"
                            variant="outlined" density="compact" hide-details="auto" :readonly="isViewMode" min="0.0001"
                            max="1" step="0.0001"
                            @focus="$event.target.select()"
                            @blur="validateAndFixCosineSimilarity($event)"
                            @keydown.enter="validateAndFixCosineSimilarity($event)"
                            class="modern-input" /> -->

                        <v-divider class="my-4"></v-divider>

                        <!-- Community Graph RAG Section -->
                        <h6 class="section-title">
                            <v-icon size="20" class="mr-2" color="purple">mdi-graph</v-icon>
                            Community Graph (Optional)
                        </h6>
                        <div class="text-caption text-grey mb-2">
                            Use community graph for improved search accuracy with GraphRAG
                        </div>

                        <!-- Community Graph Selection -->
                        <v-autocomplete
                            v-model="nodeInspector.communityId"
                            :items="DDCommunityGraph"
                            item-title="communityName"
                            item-value="id"
                            label="Community Graph"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            class="modern-input mb-3"
                            placeholder="Select community graph (optional)"
                            :clearable="!isViewMode"
                            :readonly="isViewMode"
                            @update:model-value="onCommunityGraphChange($event)"
                        >
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="purple">mdi-graph</v-icon>
                                    </template>
                                    <template v-slot:append>
                                        <v-chip size="x-small" color="purple" variant="tonal">
                                            {{ item.raw.groupCount }} groups
                                        </v-chip>
                                    </template>
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                            <template v-slot:selection="{ item }">
                                <v-chip color="purple" variant="tonal" size="small">
                                    <v-icon start size="small">mdi-graph</v-icon>
                                    {{ item.title }} ({{ item.raw.groupCount }} groups)
                                </v-chip>
                            </template>
                        </v-autocomplete>

                        <!-- Community Graph Parameters (only show when community is selected) -->
                        <div v-if="nodeInspector.communityId" class="community-params mb-3">
                            <!-- Search Method Selection -->
                            <v-select
                                v-model="nodeInspector.communitySearchMethod"
                                :items="[
                                    { title: 'Top N', value: 'topn' },
                                    { title: 'Cosine Threshold', value: 'cosine' }
                                ]"
                                item-title="title"
                                item-value="value"
                                label="Search Method"
                                variant="outlined"
                                density="compact"
                                hide-details="auto"
                                :readonly="isViewMode"
                                @update:model-value="updateNodeProperty('communitySearchMethod', $event)"
                                class="modern-input mb-3"
                                hint="Method to retrieve chunks"
                            />

                            <!-- Top N Mode Parameters -->
                            <div v-if="nodeInspector.communitySearchMethod === 'topn'" class="d-flex gap-3">
                                <v-text-field
                                    v-model="nodeInspector.communityTopGroups"
                                    type="number"
                                    label="Top Groups"
                                    variant="outlined"
                                    density="compact"
                                    hide-details="auto"
                                    :readonly="isViewMode"
                                    min="1"
                                    max="100"
                                    step="1"
                                    @focus="$event.target.select()"
                                    @blur="validateCommunityTopGroups($event)"
                                    class="modern-input"
                                    style="max-width: 120px;"
                                    hint="Number of community groups to match"
                                />
                                <v-text-field
                                    v-model="nodeInspector.communityTopChunks"
                                    type="number"
                                    label="Top Chunks"
                                    variant="outlined"
                                    density="compact"
                                    hide-details="auto"
                                    :readonly="isViewMode"
                                    min="1"
                                    max="100"
                                    step="1"
                                    @focus="$event.target.select()"
                                    @blur="validateCommunityTopChunks($event)"
                                    class="modern-input"
                                    style="max-width: 120px;"
                                    hint="Number of chunks to retrieve"
                                />
                            </div>

                            <!-- Cosine Threshold Mode Parameters -->
                            <div v-else-if="nodeInspector.communitySearchMethod === 'cosine'">
                                <v-text-field
                                    v-model="nodeInspector.communityMinCosineScore"
                                    type="number"
                                    label="Min Cosine Score"
                                    variant="outlined"
                                    density="compact"
                                    hide-details="auto"
                                    :readonly="isViewMode"
                                    min="0"
                                    max="1"
                                    step="0.05"
                                    @focus="$event.target.select()"
                                    @blur="validateCommunityMinCosineScore($event)"
                                    class="modern-input"
                                    style="max-width: 160px;"
                                    hint="Min score (0-1) to retrieve chunks"
                                />
                            </div>
                        </div>

                        <v-divider class="my-4"></v-divider>

                        <!-- Meta Community Graph RAG Section -->
                        <h6 class="section-title">
                            <v-icon size="20" class="mr-2" color="deep-purple">mdi-graph-outline</v-icon>
                            Meta Community Graph (Optional)
                        </h6>
                        <div class="text-caption text-grey mb-2">
                            Use meta community graph for improved search accuracy with Meta GraphRAG
                        </div>

                        <!-- Meta Community Graph Selection -->
                        <v-autocomplete
                            v-model="nodeInspector.metaCommunityId"
                            :items="DDMetaCommunityGraph" :no-data-text="kccGraphsNoDataText"
                            item-title="communityName"
                            item-value="id"
                            label="Meta Community Graph"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            class="modern-input mb-3"
                            placeholder="Select meta community graph (optional)"
                            :clearable="!isViewMode"
                            :readonly="isViewMode"
                            @update:model-value="onMetaCommunityGraphChange($event)"
                        >
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="deep-purple">mdi-graph-outline</v-icon>
                                    </template>
                                    <template v-slot:append>
                                        <v-chip size="x-small" color="deep-purple" variant="tonal">
                                            {{ item.raw.groupCount }} groups
                                        </v-chip>
                                    </template>
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                            <template v-slot:selection="{ item }">
                                <v-chip color="deep-purple" variant="tonal" size="small">
                                    <v-icon start size="small">mdi-graph-outline</v-icon>
                                    {{ item.title }} ({{ item.raw.groupCount }} groups)
                                </v-chip>
                            </template>
                        </v-autocomplete>
                        <!-- App Knowledge Center (D2): graph ของ Center เท่านั้น / ดึงรายการไม่ได้ (โหมดปิดไม่แสดง) -->
                        <v-alert v-if="kccCenterMode" :type="kccGraphsUnavailable ? 'warning' : 'info'" variant="tonal"
                            density="compact" class="mb-3 text-caption">
                            {{ $t(kccGraphsUnavailable || 'kccGraphsCenterOnly') }}
                        </v-alert>

                        <!-- Meta Community Graph Parameters (only show when community is selected) -->
                        <div v-if="nodeInspector.metaCommunityId" class="community-params mb-3">
                            <!-- Search Method Selection -->
                            <v-select
                                v-model="nodeInspector.metaCommunitySearchMethod"
                                :items="[
                                    { title: 'Top N', value: 'topn' },
                                    { title: 'Cosine Threshold', value: 'cosine' }
                                ]"
                                item-title="title"
                                item-value="value"
                                label="Search Method"
                                variant="outlined"
                                density="compact"
                                hide-details="auto"
                                :readonly="isViewMode"
                                @update:model-value="updateNodeProperty('metaCommunitySearchMethod', $event)"
                                class="modern-input mb-3"
                                hint="Method to retrieve chunks"
                            />

                            <!-- Top N Mode Parameters -->
                            <div v-if="nodeInspector.metaCommunitySearchMethod === 'topn'" class="d-flex gap-3">
                                <v-text-field
                                    v-model="nodeInspector.metaCommunityTopGroups"
                                    type="number"
                                    label="Top Groups"
                                    variant="outlined"
                                    density="compact"
                                    hide-details="auto"
                                    :readonly="isViewMode"
                                    min="1"
                                    max="100"
                                    step="1"
                                    @focus="$event.target.select()"
                                    @blur="validateMetaCommunityTopGroups($event)"
                                    class="modern-input"
                                    style="max-width: 120px;"
                                    hint="Number of community groups to match"
                                />
                                <v-text-field
                                    v-model="nodeInspector.metaCommunityTopChunks"
                                    type="number"
                                    label="Top Chunks"
                                    variant="outlined"
                                    density="compact"
                                    hide-details="auto"
                                    :readonly="isViewMode"
                                    min="1"
                                    max="100"
                                    step="1"
                                    @focus="$event.target.select()"
                                    @blur="validateMetaCommunityTopChunks($event)"
                                    class="modern-input"
                                    style="max-width: 120px;"
                                    hint="Number of chunks to retrieve"
                                />
                            </div>

                            <!-- Cosine Threshold Mode Parameters -->
                            <div v-else-if="nodeInspector.metaCommunitySearchMethod === 'cosine'">
                                <v-text-field
                                    v-model="nodeInspector.metaCommunityMinCosineScore"
                                    type="number"
                                    label="Min Cosine Score"
                                    variant="outlined"
                                    density="compact"
                                    hide-details="auto"
                                    :readonly="isViewMode"
                                    min="0"
                                    max="1"
                                    step="0.05"
                                    @focus="$event.target.select()"
                                    @blur="validateMetaCommunityMinCosineScore($event)"
                                    class="modern-input"
                                    style="max-width: 160px;"
                                    hint="Min score (0-1) to retrieve chunks"
                                />
                            </div>
                        </div>

                        <!-- Title/Sub-Title Filter Section -->
                        <v-divider class="my-3"></v-divider>
                        <div class="text-subtitle-2 mb-2 d-flex align-center">
                            <v-icon size="small" class="mr-1">mdi-filter-variant</v-icon>
                            Document Filter (Optional)
                        </div>

                        <v-autocomplete
                            v-model="nodeInspector.filterTopic"
                            :items="titleOptions"
                            item-title="name"
                            item-value="value"
                            label="Filter by Title"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            class="modern-input mb-3"
                            placeholder="Select Title to filter chunks"
                            clearable
                            :readonly="isViewMode"
                            @update:model-value="onFilterTopicChange($event)"
                        >
                            <template v-slot:item="{ item, props }">
                                <v-list-item v-bind="props">
                                    <template v-slot:prepend>
                                        <v-icon color="orange" size="small">mdi-folder-star</v-icon>
                                    </template>
                                </v-list-item>
                            </template>
                        </v-autocomplete>

                        <v-autocomplete
                            v-model="nodeInspector.filterSubTopic"
                            :items="getAvailableSubTitles()"
                            item-title="name"
                            item-value="value"
                            label="Filter by Sub-Title"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            class="modern-input mb-3"
                            placeholder="Select Sub-Title (optional)"
                            clearable
                            :readonly="isViewMode"
                            :disabled="!nodeInspector.filterTopic || getAvailableSubTitles().length === 0"
                            @update:model-value="updateNodeProperty('filterSubTopic', $event)"
                        >
                            <template v-slot:item="{ item, props }">
                                <v-list-item v-bind="props">
                                    <template v-slot:prepend>
                                        <v-icon color="primary" size="small">mdi-folder</v-icon>
                                    </template>
                                </v-list-item>
                            </template>
                        </v-autocomplete>

                        <v-select
                            v-model="nodeInspector.filterMode"
                            :items="filterModeOptions"
                            item-title="label"
                            item-value="value"
                            label="Filter Mode"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            class="modern-input mb-3"
                            :readonly="isViewMode"
                            @update:model-value="updateNodeProperty('filterMode', $event)"
                        />
                    </div>

                    <!-- Meta Documents Node Properties — pick Meta Documents by header ▸ category.
                         Same accordion picker as the Tax Planning Agent's knowledge card (see that block's
                         UX rationale near line 15324): chips of the SAVED selection always visible on top,
                         one search box over both levels, tri-state header checkboxes. No enable switch here —
                         a dedicated knowledge node is "enabled" by existing and being connected. -->
                    <div v-else-if="selectedNode.data.type === 'metaDocuments'" class="form-section">
                        <div class="config-header mb-4">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-bookshelf" size="24" color="indigo" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-indigo mb-0">Meta Documents</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">
                                เลือกคลังความรู้ Meta Document เป็นหัวข้อหรือหมวด — ระบบดึงเอกสารเวอร์ชันที่ Active
                                ในหมวดนั้นให้เองทุกครั้งที่ตอบ ไม่ต้องแก้ flow เมื่อเพิ่มเอกสารใหม่
                            </p>
                        </div>

                        <v-card variant="outlined" class="pa-0 mb-4 overflow-hidden" rounded="lg">
                            <div class="d-flex align-center pa-3">
                                <v-icon icon="mdi-bookshelf" size="20" color="indigo" class="me-3"></v-icon>
                                <div class="flex-grow-1">
                                    <div class="text-body-2 font-weight-medium">หัวข้อ / หมวดที่เลือก</div>
                                    <div class="text-caption text-medium-emphasis">
                                        เลือกทั้งหัวข้อ หรือเลือกเป็นรายหมวด — เลือกข้ามหัวข้อได้ทุกหัวข้อ
                                    </div>
                                </div>
                            </div>
                            <v-divider></v-divider>

                            <!-- The current selection, always visible without opening a panel. -->
                            <div class="pa-3 pb-0">
                                <div v-if="metaDocSelectionCount === 0"
                                    class="text-caption text-medium-emphasis d-flex align-start mb-3">
                                    <v-icon size="16" class="me-2">mdi-information-outline</v-icon>
                                    <span>ยังไม่ได้เลือกหมวดไหน — node นี้จะยังไม่ส่งความรู้ให้โมเดล</span>
                                </div>
                                <div v-else class="d-flex flex-wrap ga-1 mb-3">
                                    <v-chip v-for="chip in metaDocSelectedChips" :key="chip.key"
                                        size="small" color="indigo" variant="tonal" label
                                        :closable="!isViewMode"
                                        @click:close="removeMetaDocSelection(chip.header, chip.category)">
                                        <v-icon start size="14">{{ chip.category ? 'mdi-tag-outline' : 'mdi-folder-outline' }}</v-icon>
                                        {{ chip.label }}
                                    </v-chip>
                                </div>
                            </div>

                            <div class="px-3">
                                <v-text-field v-model="metaDocSearch"
                                    placeholder="ค้นหาหัวข้อหรือหมวด…"
                                    variant="outlined" density="compact" hide-details clearable
                                    prepend-inner-icon="mdi-magnify" class="mb-2"></v-text-field>
                            </div>

                            <!-- App Knowledge Center (A4): หัวข้อ/หมวดของ Center (ทะเบียน › catalog) — โหมดปิดไม่แสดง -->
                            <v-alert v-if="kccTaxonomyNotice && !taxKnowledgeLoading"
                                :type="kccTaxonomyNotice === 'kccTaxonomyCenterOnly' ? 'info' : 'warning'"
                                variant="tonal" density="compact" class="mx-3 mb-3 text-caption">
                                {{ $t(kccTaxonomyNotice) }}
                            </v-alert>
                            <div v-if="taxKnowledgeLoading" class="px-3 pb-3">
                                <v-skeleton-loader type="list-item-two-line, list-item-two-line"></v-skeleton-loader>
                            </div>
                            <v-alert v-else-if="taxKnowledgeLoadError" type="warning" variant="tonal"
                                density="compact" class="mx-3 mb-3">
                                <div class="text-caption">
                                    โหลดรายการหัวข้อ/หมวดไม่สำเร็จ — ค่าที่เลือกไว้เดิมยังอยู่ครบ ไม่ถูกล้าง
                                    <v-btn size="x-small" variant="text" color="warning" class="ms-1"
                                        @click="loadTaxKnowledgeTaxonomy(true)">ลองอีกครั้ง</v-btn>
                                </div>
                            </v-alert>
                            <div v-else-if="metaDocFilteredHeaders.length === 0"
                                class="px-3 pb-3 text-caption text-medium-emphasis">
                                ไม่พบหัวข้อหรือหมวดที่ตรงกับคำค้นนี้
                            </div>

                            <v-expansion-panels v-else v-model="metaDocOpenPanels" multiple flat
                                class="tax-knowledge-panels">
                                <v-expansion-panel v-for="h in metaDocFilteredHeaders" :key="h.title"
                                    :value="h.title" elevation="0">
                                    <v-expansion-panel-title class="px-3">
                                        <div class="d-flex align-center flex-grow-1" style="min-width:0">
                                            <!-- Wrapper stops the panel toggle; checkbox reports through
                                                 @update:model-value, NOT @click (Vuetify double-fire — see the
                                                 taxPlanningAgent picker's comment). -->
                                            <div class="flex-grow-0 me-1 d-flex" @click.stop>
                                                <v-checkbox-btn
                                                    :model-value="metaDocHeaderSelectState(h) === 'all'"
                                                    :indeterminate="metaDocHeaderSelectState(h) === 'some'"
                                                    color="indigo" density="compact"
                                                    :disabled="isViewMode"
                                                    @update:model-value="toggleMetaDocHeader(h)"></v-checkbox-btn>
                                            </div>
                                            <span class="text-body-2 text-truncate">{{ prettyTaxonomy(h.title) }}</span>
                                            <v-chip v-if="metaDocHeaderSelectedCount(h) > 0" size="x-small"
                                                color="indigo" variant="flat" label class="ms-2 flex-grow-0">
                                                {{ metaDocHeaderSelectedCount(h) }}</v-chip>
                                            <v-spacer></v-spacer>
                                            <span class="text-caption text-medium-emphasis me-2 flex-grow-0">
                                                {{ h.categories.length || '–' }}</span>
                                        </div>
                                    </v-expansion-panel-title>
                                    <v-expansion-panel-text class="tax-knowledge-panel-body">
                                        <div v-if="h.categories.length === 0"
                                            class="text-caption text-medium-emphasis pb-2">
                                            หัวข้อนี้ไม่มีหมวดย่อย — ติ๊กที่หัวข้อเพื่อใช้ทั้งหัวข้อ
                                        </div>
                                        <!-- 🔴 Ticking the header shows every category ticked as well
                                             (isMetaDocCategoryChecked), because "the whole topic" and "all its
                                             categories" mean the same thing to the reader — greyed-out EMPTY
                                             boxes under a ticked header read as "excluded", which is the
                                             opposite of what is stored. They stay clickable: unticking one
                                             narrows the header down (see toggleMetaDocCategory). -->
                                        <v-checkbox v-for="c in h.categories" :key="c.key"
                                            :model-value="isMetaDocCategoryChecked(h.title, c.key)"
                                            :label="prettyTaxonomy(c.key)"
                                            color="indigo" density="compact" hide-details
                                            class="tax-knowledge-cat"
                                            :disabled="isViewMode"
                                            @update:model-value="toggleMetaDocCategory(h, c.key)"></v-checkbox>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>
                            </v-expansion-panels>

                            <v-divider></v-divider>
                            <div class="d-flex align-center pa-2 px-3">
                                <span class="text-caption text-medium-emphasis">
                                    เลือกไว้ {{ metaDocSelectionCount }} รายการ
                                </span>
                                <v-spacer></v-spacer>
                                <v-btn size="x-small" variant="text"
                                    :disabled="isViewMode || metaDocSelectionCount === 0"
                                    @click="clearMetaDocSelections">ล้างทั้งหมด</v-btn>
                            </div>
                        </v-card>

                        <!-- Top-N Chunks -->
                        <v-text-field v-model="nodeInspector.topNChunks" type="number" label="Top-N Chunks"
                            variant="outlined" density="compact" hide-details="auto" :readonly="isViewMode" min="1"
                            step="1" @focus="$event.target.select()"
                            @blur="validateAndFixTopNChunks($event)"
                            @keydown.enter="validateAndFixTopNChunks($event)"
                            class="modern-input mb-3"
                            hint="จำนวน chunk สูงสุดที่ดึงจากเอกสารในหมวดที่เลือก (vector search)" persistent-hint />

                        <!-- Description (used by Agent planners) -->
                        <v-textarea v-model="nodeInspector.metaDocDescription" label="Description (สำหรับ Agent)"
                            variant="outlined" density="compact" rows="2" auto-grow hide-details="auto"
                            @update:model-value="updateNodeProperty('metaDocDescription', $event)"
                            class="modern-input mb-4" :readonly="isViewMode"
                            placeholder="อธิบายว่าคลังความรู้ชุดนี้มีอะไร ใช้เมื่อไหร่ — Agent ใช้ตัดสินใจว่าจะค้นเมื่อใด"
                            hint="ใช้เฉพาะเมื่อ node นี้ต่อกับ Agent — ช่วยให้ Agent เลือกใช้แหล่งความรู้ถูกตัว" persistent-hint />

                        <v-alert type="info" variant="tonal" density="compact">
                            <div class="text-caption">
                                ต่อ node นี้เข้าใต้ <strong>AI Model</strong> (จุดเชื่อมด้านล่าง) หรือ
                                <strong>Agent</strong> (Tools) — ระบบจะ resolve หัวข้อ/หมวดเป็นเอกสารที่ Active
                                ณ เวลาตอบ แล้วค้นด้วย vector search ส่งให้โมเดลเป็นความรู้ประกอบคำตอบ
                            </div>
                            <!-- #746 -->
                            <div class="text-caption mt-2">
                                ถ้า Agent ตัวเดียวกันต่อ <strong>Tax Planning Form V2</strong> ไว้ด้วย จะใช้เฉพาะเอกสารที่ปีภาษีตรงกับฟอร์มของลูกค้า
                                (ยังไม่มีฟอร์ม = ปีที่ Tax Configuration ใช้) หรือเอกสารที่ไม่ระบุปี · ไม่ได้ต่อ Form V2 = ไม่กรองปี
                            </div>
                        </v-alert>
                    </div>

                    <!-- DB Integration Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'databaseIntegration'" class="form-section">
                        <h6 class="section-title">Database Integration Configuration</h6>

                        <!-- Table Name Selection -->
                        <v-autocomplete v-model="nodeInspector.tableName" :items="listDataIntegration"
                            item-title="dataName" item-value="id" label="Table Name" variant="outlined"
                            density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('tableName', $event)" class="modern-input mb-3"
                            placeholder="Select database table" :clearable="!isViewMode" :readonly="isViewMode">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="table-option">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="primary">mdi-table</v-icon>
                                    </template>
                                    <v-list-item-title>{{ item.raw.name }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>

                        <!-- SQL Extraction Model Selection -->
                        <v-row>
                        <v-col :cols="nodeInspector.sqlExtractionModel ? 9 : 12">
                        <v-autocomplete v-model="nodeInspector.sqlExtractionModel" :items="listSqlExtract"
                            item-title="searchableText" item-value="modelDisplayID" label="SQL Extraction Model"
                            variant="outlined" density="compact" hide-details="auto" :clearable="!isViewMode"
                            :readonly="isViewMode"
                            @update:model-value="updateNodeProperty('sqlExtractionModel', $event)"
                            class="modern-input mb-3" placeholder="Select AI model for SQL generation">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="model-option"
                                :title="item.raw.modelDisplayID + ': ' + item.raw.name">
                                    <template v-slot:prepend>
                                        <div class="model-icon-small mr-2" :style="{ backgroundColor: item.raw.color }">
                                            <v-icon size="14" color="green">mdi-robot</v-icon>
                                        </div>
                                    </template>
                                    <!-- <v-list-item-title>{{ item.raw.name }}</v-list-item-title> -->
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>
                        </v-col>
                        <v-col v-if="nodeInspector.sqlExtractionModel" cols="3" class="px-0">
                        <v-btn
                            icon
                            variant="tonal"
                            color="primary"
                            class="sql-extraction-edit-btn"
                            @click="navigateToModelDetailSql(listSqlExtract, nodeInspector.sqlExtractionModel)"
                            :disabled="isViewMode">
                            <v-icon size="16">mdi-pencil-outline</v-icon>
                        </v-btn>
                        </v-col>
                        </v-row>

                        <!-- Prompt for No Data -->
                        <v-textarea v-model="nodeInspector.promptSqlNoData" label="Prompt for No Data"
                            variant="outlined" density="compact" rows="3" auto-grow hide-details="auto"
                            placeholder="Enter the message to display when no data is found in the database..."
                            @update:model-value="updateNodeProperty('promptSqlNoData', $event)"
                            class="modern-input mb-3" :readonly="isViewMode" />

                        <!-- Request Timeout -->
                        <v-text-field
                            v-model.number="nodeInspector.nodeTimeout"
                            type="number"
                            min="0"
                            label="Request Timeout (seconds)"
                            hint="0 = use global timeout setting"
                            persistent-hint
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            class="modern-input mb-3"
                            @update:model-value="updateNodeProperty('nodeTimeout', $event)"
                            :readonly="isViewMode">
                        </v-text-field>

                        <!-- Max Retry -->
                        <v-text-field
                            v-model.number="nodeInspector.nodeMaxRetry"
                            type="number"
                            min="0"
                            label="Max Retry"
                            hint="0 = use global max retry setting"
                            persistent-hint
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            class="modern-input"
                            @update:model-value="updateNodeProperty('nodeMaxRetry', $event)"
                            :readonly="isViewMode">
                        </v-text-field>
                    </div>

                    <!-- Mutual Fund Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'mutualFund'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-chart-donut-variant" size="24" color="purple" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-primary mb-0">Mutual Fund Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">อ่านข้อความลูกค้า → LLM สกัดเฉพาะ
                                <strong>รหัสกอง / ชื่อกอง / ขอเทียบหรือไม่</strong> ออกมา แล้วโหนดนี้ไปค้นใน Mutual Fund
                                views เอง (ไม่มีรายชื่อกองทุนอยู่ใน prompt เลย). การ์ดที่ได้: เจอกองเดียว ⇒
                                <strong>factsheet (type8) + performance (type10)</strong> · ลูกค้าขอเทียบและเจอตั้งแต่ 2
                                กอง ⇒ <strong>comparison (type9) ใบเดียว</strong> · เจอหลายกองแต่ไม่ได้ขอเทียบ
                                หรือไม่เจอเลย ⇒ <strong>ไม่มีการ์ด</strong> — โหนดส่งรายชื่อ/ผลค้นให้บอทถามลูกค้ากลับแทน</p>
                        </div>

                        <v-expansion-panels v-model="modelConfigPanels" multiple class="config-panels elevation-0"
                            variant="accordion">
                            <!-- Fund Resolver Panel -->
                            <v-expansion-panel value="basic" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-auto-fix" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Fund Resolver</div>
                                            <div class="panel-subtitle">AI สกัดคำจากข้อความลูกค้า — โหนดค้นฐานข้อมูลเอง
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-4">
                                            <!-- Resolver on/off — the headline toggle for this node. Everything else in
                                                 this panel only matters while it is on. -->
                                            <div class="switch-option-compact">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.mutualFundResolverEnabled }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.mutualFundResolverEnabled ? 'primary' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.mutualFundResolverEnabled ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-auto-fix</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">ให้ AI หากองทุนจากข้อความลูกค้า
                                                            </div>
                                                            <div class="switch-description">On = LLM สกัดรหัสกอง/ชื่อกอง
                                                                และ "ขอเทียบไหม" ออกจากข้อความ แล้วโหนดค้น DB เอง ·
                                                                Off = ใช้เฉพาะรหัสกองที่พิมพ์ไว้ใน "Fallback (legacy)"
                                                                ข้างล่าง</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.mutualFundResolverEnabled"
                                                        color="primary" hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('mutualFundResolverEnabled', $event)"
                                                        :readonly="isViewMode" inset
                                                        data-testid="mutualfund-resolver-enabled" />
                                                </div>
                                            </div>
                                        </div>

                                        <!-- แหล่งข้อมูล: read-only. ก่อนหน้านี้ชื่อ view เป็น const ในโค้ด BAL เท่านั้น
                                             คนดูหน้านี้จึงไม่มีทางรู้ว่าโหนดอ่านอะไรอยู่ เวลากองหายจากแอปเลยดูเหมือน
                                             บั๊กของโหนด ทั้งที่ปัญหาอยู่ที่ view/การ import. โหนดอ่าน view นี้ตายตัว
                                             จึงไม่ทำเป็น dropdown — เลือกผิดแล้วคอลัมน์ที่โหนดต้องการหายไปเงียบ ๆ -->
                                        <div class="config-section mb-6">
                                            <h6 class="section-label mb-3">แหล่งข้อมูล</h6>
                                            <v-alert type="info" variant="tonal" density="comfortable"
                                                icon="mdi-database-search" class="mb-0"
                                                data-testid="mutualfund-datasource">
                                                <div class="text-body-2">
                                                    โหนดนี้อ่านกองทุนจากวิว
                                                    <code class="mf-view-code">dbo.vw_MutualFund_AllSources</code>
                                                    — หนึ่งแถวต่อหนึ่งกองทุน ประกอบจากตาราง import ทุกตัวที่ต่ออยู่
                                                </div>
                                                <div class="text-caption mt-2">
                                                    ดูว่าวิวนี้ประกอบจากอะไร แก้กติกาการ join หรือสั่ง Refresh ได้ที่หน้า
                                                    <router-link to="/MutualFund" class="mf-view-link">Mutual Fund</router-link>
                                                    → การ์ด <strong>View layer</strong>
                                                </div>
                                            </v-alert>
                                        </div>

                                        <!-- ไม่มี "Candidate Scope" / "Candidate limit" อีกแล้ว: prompt ไม่มีรายชื่อ
                                             กองทุนอยู่ในนั้น จึงไม่มี shortlist ให้กรองหรือ cap. คีย์เดิม
                                             (mutualFundScopeTaxSavingFunds / …ScopeRecommendedOnly / …CandidateLimit)
                                             ยังถูกเซฟไว้ใน node.data เพื่อให้ flow เก่า round-trip ได้ แต่ไม่มีโค้ดอ่าน. -->
                                        <div class="config-section mb-6">
                                            <h6 class="section-label mb-3">Limits</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.mutualFundTopN"
                                                        label="Max funds fetched (Top N)" type="number" min="1"
                                                        max="50" variant="outlined" density="comfortable"
                                                        hide-details="auto"
                                                        prepend-inner-icon="mdi-format-list-numbered"
                                                        @update:model-value="updateNodeProperty('mutualFundTopN', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        :disabled="!nodeInspector.mutualFundResolverEnabled"
                                                        hint="จำนวนกองสูงสุดที่ดึงออกมาจากฐานข้อมูลต่อหนึ่งเทิร์น (1-50) — เป็นเพดานของทั้งเทิร์น: ค้นเจอเกินจำนวนนี้จะถูกตัดทิ้ง แล้วบอทจะบอกลูกค้าว่ายังมีกองมากกว่านี้ ให้ระบุชื่อ/รหัสให้แคบลง · คนละตัวกับ 'Max funds per comparison' ข้างล่าง ที่เป็นเพดานเฉพาะการ์ดเปรียบเทียบ (type9) จึงไม่ควรตั้งเกินค่านี้"
                                                        persistent-hint data-testid="mutualfund-topn" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.mutualFundMaxSelect"
                                                        label="Max funds per comparison" type="number" min="1" max="10"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-compare-horizontal"
                                                        @update:model-value="updateNodeProperty('mutualFundMaxSelect', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        :disabled="!nodeInspector.mutualFundResolverEnabled"
                                                        hint="จำนวนกองสูงสุดที่นำมาเปรียบเทียบเมื่อลูกค้าขอเทียบ (1-10) — ใช้กับการ์ด comparison (type9) เท่านั้น ไม่มีผลกับเทิร์นที่ถามกองเดียว"
                                                        persistent-hint data-testid="mutualfund-max-select" />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <!-- #924 Fuzzy search — speech-to-text has no hyphen: "SCBA" / "เอสซีบี เอ" never
                                             LIKE-matches "SCB-A". The fuzzy lane only runs for a code the search did not find
                                             as typed, so a turn that already found its fund is unchanged. Default OFF (a new node,
                                             and a node saved before this key existed — the backend reads absent as OFF); the
                                             operator switches it on here. Min similarity defaults to 0.78. -->
                                        <div class="config-section mb-6">
                                            <h6 class="section-label mb-3">Fuzzy search</h6>
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.mutualFundFuzzyEnabled }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.mutualFundFuzzyEnabled ? 'primary' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.mutualFundFuzzyEnabled ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-text-search-variant</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">ค้นรหัสกองแบบใกล้เคียง (Fuzzy)</div>
                                                            <div class="switch-description">On = รหัสที่ค้นตรง ๆ ไม่เจอ จะค้นซ้ำแบบไม่สนขีด/ช่องว่าง
                                                                (SCBA → SCB-A) และยอมให้พิมพ์/ได้ยินผิดเล็กน้อย · คำไทย/จีน LLM แปลงเป็นอังกฤษให้ก่อนตาม System prompt
                                                                · Off = ค้นแบบตรงตัว (LIKE) อย่างเดียว</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.mutualFundFuzzyEnabled"
                                                        color="primary" hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('mutualFundFuzzyEnabled', $event)"
                                                        :readonly="isViewMode"
                                                        :disabled="!nodeInspector.mutualFundResolverEnabled" inset
                                                        data-testid="mutualfund-fuzzy-enabled" />
                                                </div>
                                            </div>
                                            <div class="slider-container">
                                                <div class="d-flex justify-space-between align-center mb-2">
                                                    <span class="slider-label">Min similarity</span>
                                                    <span class="text-caption text-grey-darken-1">0.60 – 1.00</span>
                                                </div>
                                                <v-slider v-model="nodeInspector.mutualFundFuzzyMinSimilarity"
                                                    :min="0.6" :max="1" :step="0.01" color="primary"
                                                    track-color="grey-lighten-3" thumb-label
                                                    @update:model-value="updateNodeProperty('mutualFundFuzzyMinSimilarity', $event)"
                                                    class="modern-slider" :readonly="isViewMode"
                                                    :disabled="!nodeInspector.mutualFundResolverEnabled || !nodeInspector.mutualFundFuzzyEnabled"
                                                    data-testid="mutualfund-fuzzy-min-similarity">
                                                    <!-- Right side: − · typed value · +. The box takes digits and one dot only
                                                         (no minus, no "e"), caps anything above 1.00 while typing, and clamps
                                                         to 0.60–1.00 on Enter / leaving the box — the range the backend reads. -->
                                                    <template v-slot:append>
                                                        <div class="d-flex align-center mf-sim-stepper">
                                                            <v-btn icon="mdi-minus" size="x-small" density="compact" variant="tonal" color="primary" class="mf-sim-btn"
                                                                aria-label="ลด Min similarity 0.01"
                                                                :disabled="isViewMode || !nodeInspector.mutualFundResolverEnabled || !nodeInspector.mutualFundFuzzyEnabled || Number(nodeInspector.mutualFundFuzzyMinSimilarity) <= 0.6"
                                                                @click="stepMutualFundFuzzyMinSimilarity(-1)"
                                                                data-testid="mutualfund-fuzzy-min-similarity-minus" />
                                                            <v-text-field
                                                                :model-value="mutualFundFuzzyMinSimilarityDraft !== null ? mutualFundFuzzyMinSimilarityDraft : Number(nodeInspector.mutualFundFuzzyMinSimilarity).toFixed(2)"
                                                                @update:model-value="onMutualFundFuzzyMinSimilarityInput"
                                                                @keypress="onMutualFundFuzzyMinSimilarityKeypress"
                                                                @blur="commitMutualFundFuzzyMinSimilarity"
                                                                @keydown.enter.prevent="commitMutualFundFuzzyMinSimilarity"
                                                                type="text" inputmode="decimal" maxlength="4"
                                                                variant="outlined" density="compact" hide-details
                                                                class="mf-sim-input"
                                                                aria-label="Min similarity (0.60–1.00)"
                                                                :readonly="isViewMode"
                                                                :disabled="!nodeInspector.mutualFundResolverEnabled || !nodeInspector.mutualFundFuzzyEnabled"
                                                                data-testid="mutualfund-fuzzy-min-similarity-input" />
                                                            <v-btn icon="mdi-plus" size="x-small" density="compact" variant="tonal" color="primary" class="mf-sim-btn"
                                                                aria-label="เพิ่ม Min similarity 0.01"
                                                                :disabled="isViewMode || !nodeInspector.mutualFundResolverEnabled || !nodeInspector.mutualFundFuzzyEnabled || Number(nodeInspector.mutualFundFuzzyMinSimilarity) >= 1"
                                                                @click="stepMutualFundFuzzyMinSimilarity(1)"
                                                                data-testid="mutualfund-fuzzy-min-similarity-plus" />
                                                        </div>
                                                    </template>
                                                    <template v-slot:thumb-label="{ modelValue }">
                                                        {{ Number(modelValue).toFixed(2) }}
                                                    </template>
                                                </v-slider>
                                                <div class="text-caption text-grey-darken-1">
                                                    ค่าเริ่มต้น 0.78 · ยิ่งต่ำยิ่งหาเจอง่าย แต่เสี่ยงจับกองผิด · 1.00 = ต้องตรงหลังตัดขีด/ช่องว่าง
                                                    (ไม่รับพิมพ์ผิด/เสียงคล้าย)
                                                </div>
                                            </div>
                                            <div class="slider-container">
                                                <div class="d-flex justify-space-between align-center mb-2">
                                                    <span class="slider-label">ห่างจากอันดับ 1 ได้ไม่เกิน</span>
                                                    <span class="text-caption text-grey-darken-1">0.00 – 0.40</span>
                                                </div>
                                                <v-slider v-model="nodeInspector.mutualFundFuzzyTopGap"
                                                    :min="0" :max="0.4" :step="0.01" color="primary"
                                                    track-color="grey-lighten-3" thumb-label
                                                    @update:model-value="updateNodeProperty('mutualFundFuzzyTopGap', $event)"
                                                    class="modern-slider" :readonly="isViewMode"
                                                    :disabled="!nodeInspector.mutualFundResolverEnabled || !nodeInspector.mutualFundFuzzyEnabled"
                                                    data-testid="mutualfund-fuzzy-top-gap">
                                                    <!-- Same − · typed value · + as Min similarity, clamped to 0.00–0.40. -->
                                                    <template v-slot:append>
                                                        <div class="d-flex align-center mf-sim-stepper">
                                                            <v-btn icon="mdi-minus" size="x-small" density="compact" variant="tonal" color="primary" class="mf-sim-btn"
                                                                aria-label="ลดระยะห่างจากอันดับ 1 0.01"
                                                                :disabled="isViewMode || !nodeInspector.mutualFundResolverEnabled || !nodeInspector.mutualFundFuzzyEnabled || Number(nodeInspector.mutualFundFuzzyTopGap) <= 0"
                                                                @click="stepMutualFundFuzzyTopGap(-1)"
                                                                data-testid="mutualfund-fuzzy-top-gap-minus" />
                                                            <v-text-field
                                                                :model-value="mutualFundFuzzyTopGapDraft !== null ? mutualFundFuzzyTopGapDraft : Number(nodeInspector.mutualFundFuzzyTopGap).toFixed(2)"
                                                                @update:model-value="onMutualFundFuzzyTopGapInput"
                                                                @keypress="onMutualFundFuzzyMinSimilarityKeypress"
                                                                @blur="commitMutualFundFuzzyTopGap"
                                                                @keydown.enter.prevent="commitMutualFundFuzzyTopGap"
                                                                type="text" inputmode="decimal" maxlength="4"
                                                                variant="outlined" density="compact" hide-details
                                                                class="mf-sim-input"
                                                                aria-label="ห่างจากอันดับ 1 (0.00–0.40)"
                                                                :readonly="isViewMode"
                                                                :disabled="!nodeInspector.mutualFundResolverEnabled || !nodeInspector.mutualFundFuzzyEnabled"
                                                                data-testid="mutualfund-fuzzy-top-gap-input" />
                                                            <v-btn icon="mdi-plus" size="x-small" density="compact" variant="tonal" color="primary" class="mf-sim-btn"
                                                                aria-label="เพิ่มระยะห่างจากอันดับ 1 0.01"
                                                                :disabled="isViewMode || !nodeInspector.mutualFundResolverEnabled || !nodeInspector.mutualFundFuzzyEnabled || Number(nodeInspector.mutualFundFuzzyTopGap) >= 0.4"
                                                                @click="stepMutualFundFuzzyTopGap(1)"
                                                                data-testid="mutualfund-fuzzy-top-gap-plus" />
                                                        </div>
                                                    </template>
                                                    <template v-slot:thumb-label="{ modelValue }">
                                                        {{ Number(modelValue).toFixed(2) }}
                                                    </template>
                                                </v-slider>
                                                <div class="text-caption text-grey-darken-1">
                                                    ค่าเริ่มต้น 0.10 (10%) · เก็บกองที่คะแนนห่างจากอันดับ 1 ไม่เกินค่านี้ เช่น 0.98 / 0.95 / 0.90 / 0.80 → เหลือ 3 กองแรก ·
                                                    ถ้าอันดับ 1 ได้ 1.00 เก็บเฉพาะกองที่ได้ 1.00 · สูงสุด 5 กอง
                                                </div>
                                            </div>
                                        </div>

                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Service Configuration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.provider"
                                                        :items="listDDProvider" item-title="providerName"
                                                        item-value="providerName" label="Provider*" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-cloud-outline"
                                                        @update:model-value="updateNodeProperty('provider', $event); changeProvider($event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Provider used by the fund resolver call" persistent-hint
                                                        required data-testid="mutualfund-provider" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.model" :items="DDModelShow"
                                                        item-title="modelDisplay" item-value="modelName" label="Model*"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-brain"
                                                        @update:model-value="updateNodeProperty('model', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :disabled="!nodeInspector.provider"
                                                        hint="Select specific model version" persistent-hint required
                                                        data-testid="mutualfund-model" />
                                                </v-col>
                                            </v-row>
                                            <v-row v-if="nodeInspector.apiVersion">
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.apiVersion"
                                                        label="API Version" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-api"
                                                        class="config-input" readonly
                                                        hint="Auto-detected from selected model" persistent-hint
                                                        data-testid="mutualfund-apiversion" />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- AI Parameters Panel -->
                            <v-expansion-panel value="parameters" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">LLM · AI Parameters</div>
                                            <div class="panel-subtitle">Temperature, top p, output length</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Core Parameters</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.temperature"
                                                        label="Temperature*" type="number" step="0.1" min="0" max="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-thermometer"
                                                        @update:model-value="updateNodeProperty('temperature', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Creativity level (0 = focused, 1 = creative)"
                                                        persistent-hint data-testid="mutualfund-temperature" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.topP" label="Top P"
                                                        type="number" step="0.1" min="0" max="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-filter-variant"
                                                        @update:model-value="updateNodeProperty('topP', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Token selection probability threshold" persistent-hint
                                                        data-testid="mutualfund-topp" />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Output Control</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.maxTokens"
                                                        label="Max Tokens Response" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-check-outline"
                                                        @update:model-value="updateNodeProperty('maxTokens', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" hint="Maximum response length in tokens"
                                                        persistent-hint data-testid="mutualfund-maxtokens" />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Prompts Panel -->
                            <v-expansion-panel value="prompt" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-text-box-outline" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Prompts</div>
                                            <div class="panel-subtitle">ทุกคำที่ส่งให้ตัวสกัด — ไม่มีรายชื่อกองทุนอยู่ใน
                                                prompt แล้ว</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                                            <div class="text-caption">LLM ทำหน้าที่ <strong>สกัดคำ</strong> อย่างเดียว —
                                                คืน JSON ก้อนเดียวรูป
                                                <code>{"fc":[รหัสกอง],"fn":[ชื่อกอง],"cp":ขอเทียบไหม}</code>
                                                แล้วโหนดเอาไปค้น DB เอง จึงไม่มีรายชื่อกองทุนถูกพิมพ์ลง prompt เลย.
                                                เว้นช่องว่างไว้ = ใช้ค่า default ที่ระบบมีให้ (ข้อความสีจางคือสิ่งที่จะถูกส่งจริง)
                                            </div>
                                        </v-alert>
                                        <div class="config-section mb-4">
                                            <h6 class="section-label mb-2">System Prompt</h6>
                                            <v-textarea v-model="nodeInspector.systemPrompt" label="System Prompt"
                                                variant="outlined" counter auto-grow
                                                :rows="mutualFundPromptRows.systemPrompt"
                                                :placeholder="mutualFundPromptDefaults.systemPrompt"
                                                @update:model-value="updateNodeProperty('systemPrompt', $event)"
                                                :readonly="isViewMode"
                                                hint="กติกาของตัวสกัด: อะไรนับเป็นรหัสกอง (fc) อะไรนับเป็นชื่อกอง (fn) และแบบไหนคือขอเปรียบเทียบ (cp). เว้นว่าง = ใช้ default"
                                                persistent-hint data-testid="mutualfund-systemprompt" />
                                        </div>
                                        <div class="config-section">
                                            <h6 class="section-label mb-2">Extraction Template</h6>
                                            <v-textarea v-model="nodeInspector.mutualFundExtractionTemplate"
                                                label="Extraction Template" variant="outlined" counter auto-grow
                                                :rows="mutualFundPromptRows.extractionTemplate"
                                                :placeholder="mutualFundPromptDefaults.extractionTemplate"
                                                @update:model-value="updateNodeProperty('mutualFundExtractionTemplate', $event)"
                                                :readonly="isViewMode"
                                                hint='Required token ตัวเดียว: {userMessage} — เทมเพลตที่ไม่มีจะถูกทิ้งแล้วใช้ default. คำตอบต้องเป็น JSON ก้อนเดียว {"fc":[],"fn":[],"cp":false}'
                                                persistent-hint data-testid="mutualfund-extractiontemplate" />
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Cards Panel -->
                            <v-expansion-panel value="response" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-card-account-details-outline" size="20"
                                                color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Cards</div>
                                            <div class="panel-subtitle">ใบไหนออกเมื่อไร และใช้เทมเพลตที่เก็บไว้ใบไหน
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <!-- Card configs: which stored card template renders each card kind. The value
                                             saved is the configCode (never the row id) — the code is the business key
                                             the node resolves at run time.
                                             🔴 กติกาใหม่: ไม่มีใบไหนที่ออกทุกเทิร์นอีกแล้ว. เจอกองเดียว ⇒ factsheet +
                                             performance · เจอหลายกอง ⇒ picker (type14) ให้ลูกค้าเลือกก่อน · พอรู้ว่า
                                             เป็นกองไหนแน่แล้วและลูกค้าขอเทียบ ⇒ comparison ใบเดียว (ไม่มี factsheet
                                             ประกบ) ⇒ ทุกใบเป็น "ใบเดียวของผลลัพธ์นั้น" ถ้าเว้นว่าง ผลลัพธ์นั้นจะไม่มี
                                             การ์ดเลย -->
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Card Templates</h6>
                                            <v-alert type="info" variant="tonal" density="compact" class="mb-4">
                                                <div class="text-caption">
                                                    เจอกองเดียว ⇒ <strong>factsheet (type8) + performance (type10)</strong>
                                                    · เจอตั้งแต่ 2 กอง ⇒ <strong>picker (type14)</strong>
                                                    ให้ลูกค้าเลือกก่อนว่าหมายถึงกองไหน (คนละใบกันระหว่างเคสขอเทียบกับไม่ขอเทียบ)
                                                    · ลูกค้าขอเทียบและระบุกองมาครบแล้ว ⇒
                                                    <strong>comparison (type9) ใบเดียว</strong> · ไม่เจอเลย ⇒ ไม่มีการ์ด
                                                    (บอทตอบเป็นข้อความว่าไม่พบ)
                                                </div>
                                            </v-alert>
                                            <v-alert v-if="!nodeInspector.mutualFundFactsheetConfigCode"
                                                type="warning" variant="tonal" density="compact" class="mb-4">
                                                <div class="text-caption">ยังไม่ได้เลือก <strong>Factsheet
                                                        (type8)</strong> — เทิร์นที่ลูกค้าถามถึงกองเดียว
                                                    (เคสที่พบบ่อยที่สุด) จะเหลือแค่การ์ด performance
                                                    และถ้า performance ก็ว่างด้วย เทิร์นนั้นจะไม่มีการ์ดเลย</div>
                                            </v-alert>
                                            <v-alert v-if="!nodeInspector.mutualFundComparisonConfigCode"
                                                type="warning" variant="tonal" density="compact" class="mb-4">
                                                <div class="text-caption">ยังไม่ได้เลือก <strong>Comparison
                                                        (type9)</strong> — เทิร์นที่ลูกค้าขอ "เทียบ" จะ
                                                    <strong>ไม่มีการ์ดเลย</strong> เพราะกติกาใหม่ไม่ออก factsheet
                                                    ประกบให้แล้ว (บอทตอบเป็นข้อความอย่างเดียว)</div>
                                            </v-alert>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.mutualFundFactsheetConfigCode"
                                                        :items="mutualFundConfigOptions('TAX_FUND_FACTSHEET')"
                                                        label="Factsheet card config (type8) — ออกเมื่อเจอกองเดียว"
                                                        hint="ใบหลักของเคสกองเดียว ออกคู่กับ performance เสมอ (รวมกรณีขอเทียบแต่ค้นเจอกองเดียว). เว้นว่าง = เคสนั้นเหลือแค่การ์ด performance"
                                                        persistent-hint clearable
                                                        :loading="mutualFundCardConfigsLoading" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-file-document-outline"
                                                        class="config-input" :readonly="isViewMode"
                                                        @update:model-value="updateNodeProperty('mutualFundFactsheetConfigCode', $event || '')" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.mutualFundComparisonConfigCode"
                                                        :items="mutualFundConfigOptions('TAX_FUND_COMPARISON')"
                                                        label="Comparison card config (type9) — ออกเมื่อลูกค้าขอเทียบ ≥2 กอง"
                                                        hint="🔴 ใบเดียวของเทิร์นเปรียบเทียบ — ไม่มี factsheet ประกบแล้ว. เว้นว่างไว้แล้วลูกค้าขอเทียบ = เทิร์นนั้นไม่มีการ์ดเลย"
                                                        persistent-hint clearable
                                                        :loading="mutualFundCardConfigsLoading" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-compare-horizontal"
                                                        class="config-input" :readonly="isViewMode"
                                                        @update:model-value="updateNodeProperty('mutualFundComparisonConfigCode', $event || '')" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.mutualFundPerformanceConfigCode"
                                                        :items="mutualFundConfigOptions('TAX_FUND_PERFORMANCE')"
                                                        label="Performance card config (type10) — ออกคู่กับ factsheet"
                                                        hint="ออกพร้อม factsheet ทุกครั้งที่เจอกองเดียว และไม่เคยออกในเทิร์นเปรียบเทียบ. เว้นว่าง = เคสกองเดียวเหลือแค่การ์ด factsheet"
                                                        persistent-hint clearable
                                                        :loading="mutualFundCardConfigsLoading" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-chart-line" class="config-input"
                                                        :readonly="isViewMode"
                                                        @update:model-value="updateNodeProperty('mutualFundPerformanceConfigCode', $event || '')" />
                                                </v-col>
                                            </v-row>
                                            <!-- 🔴 Both pickers filter the SAME card type ('MF'): they are two
                                                 documents of one type, telling themselves apart by their own `cp`,
                                                 wording and join — not two types. Two node keys, because the node
                                                 has to know which stored config belongs to which outcome. -->
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.mutualFundPickerConfigCode"
                                                        :items="mutualFundConfigOptions('MF')"
                                                        label="Fund picker card config (type14) — เจอหลายกอง แต่ไม่ได้ขอเทียบ"
                                                        hint="🔴 คนละจังหวะกับ 3 ใบบน — สามใบนั้นออกตอนรู้แล้วว่าเป็นกองไหน ส่วนใบนี้ออกตอน 'ยังไม่รู้': ลูกค้าถามหาข้อมูลกองแล้วค้นเจอตั้งแต่ 2 กองขึ้นไป จึงยกรายชื่อให้เลือกเอง — เลือกได้กองเดียว แตะแล้วส่งกลับทันที ไม่มีปุ่มยืนยัน. เว้นว่าง = เคสนี้ลูกค้าได้แต่ข้อความเปล่า ๆ ไม่มีปุ่มให้เลือก ต้องพิมพ์ชื่อกองเองใหม่"
                                                        persistent-hint clearable
                                                        :loading="mutualFundCardConfigsLoading" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-radiobox-marked"
                                                        class="config-input" :readonly="isViewMode"
                                                        @update:model-value="updateNodeProperty('mutualFundPickerConfigCode', $event || '')" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.mutualFundPickerCompareConfigCode"
                                                        :items="mutualFundConfigOptions('MF')"
                                                        label="Fund picker card config (type14) — ขอเทียบ แต่ยังไม่ชัดว่ากองไหน"
                                                        hint="ลูกค้าขอ 'เทียบ' แต่ค้นเจอหลายกองจนยังไม่รู้ว่าหมายถึงกองไหนบ้าง — ใบนี้ให้ติ๊กได้หลายกอง แล้วกดปุ่มยืนยันทีเดียว (รหัสที่เลือกถูกต่อกันด้วย join ของการ์ดใบนั้น). พอลูกค้ากดปุ่มแล้วเทิร์นถัดไปจึงได้การ์ด comparison (type9). เว้นว่าง = เคสนี้ลูกค้าได้แต่ข้อความ ไม่มีอะไรให้ติ๊ก"
                                                        persistent-hint clearable
                                                        :loading="mutualFundCardConfigsLoading" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-checkbox-multiple-marked-outline"
                                                        class="config-input" :readonly="isViewMode"
                                                        @update:model-value="updateNodeProperty('mutualFundPickerCompareConfigCode', $event || '')" />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Demo Data</h6>
                                            <div class="switch-option-compact">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.mutualFundDemoMode }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.mutualFundDemoMode ? 'warning' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.mutualFundDemoMode ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-flask-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Use local demo data (for team
                                                                review only)</div>
                                                            <div class="switch-description">Sample type8/type9/type10
                                                                data without reading the database. Turn it off before
                                                                customer data is connected.</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.mutualFundDemoMode" color="warning"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('mutualFundDemoMode', $event)"
                                                        :readonly="isViewMode" inset
                                                        data-testid="mutualfund-demomode" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Fallback (legacy) Panel -->
                            <v-expansion-panel value="advanced" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4">
                                            <v-icon icon="mdi-archive-outline" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Fallback (legacy)</div>
                                            <div class="panel-subtitle">Fund codes typed by hand</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-alert type="warning" variant="tonal" density="compact" class="mb-4">
                                            <div class="text-caption">ใช้เมื่อ "ให้ AI หากองทุนจากข้อความลูกค้า" ปิดอยู่
                                                — หรือเมื่อ resolver หากองทุนไม่เจอ. Flows built before the resolver
                                                existed still run on these fields, so they are kept.</div>
                                        </v-alert>
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Manual Lookup</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.mutualFundLookupType"
                                                        :items="[{ title: 'Fund code', value: 'fundCode' }, { title: 'Morningstar ID', value: 'mStarId' }]"
                                                        label="Lookup type" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-key-outline"
                                                        class="config-input" :readonly="isViewMode"
                                                        @update:model-value="updateNodeProperty('mutualFundLookupType', $event)"
                                                        data-testid="mutualfund-lookuptype" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.mutualFundLookupValue"
                                                        :label="nodeInspector.mutualFundLookupType === 'mStarId' ? 'Morningstar ID' : 'Fund code'"
                                                        :placeholder="nodeInspector.mutualFundLookupType === 'mStarId' ? 'Example: F0GBR04JCU' : 'Example: LHBPLUS'"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-pound" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Primary fund the card is built from when the resolver is off"
                                                        persistent-hint
                                                        @update:model-value="updateNodeProperty('mutualFundLookupValue', $event)"
                                                        data-testid="mutualfund-lookupvalue" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-textarea v-model="nodeInspector.mutualFundComparisonValues"
                                                        label="Comparison fund codes (optional)"
                                                        placeholder="LHBPLUS, KKP-GNP, SCBGOLD"
                                                        hint="Enter multiple fund codes separated by commas to populate the type9 comparison widget."
                                                        persistent-hint variant="outlined" density="comfortable"
                                                        rows="2" auto-grow hide-details="auto"
                                                        prepend-inner-icon="mdi-compare-horizontal" class="config-input"
                                                        :readonly="isViewMode"
                                                        @update:model-value="updateNodeProperty('mutualFundComparisonValues', $event)"
                                                        data-testid="mutualfund-comparisonvalues" />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </div>

                    <!-- Knowledge API Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'apiknowledge'" class="form-section">
                        <h6 class="section-title">Knowledge API Configuration</h6>

                        <!-- API URL -->
                        <v-text-field v-model="nodeInspector.knowledgeApiUrl" label="API URL" variant="outlined"
                            density="compact" hide-details="auto" placeholder="https://api.example.com/knowledge"
                            @update:model-value="updateNodeProperty('knowledgeApiUrl', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="primary">mdi-link</v-icon>
                            </template>
                        </v-text-field>

                        <!-- HTTP Method -->
                        <v-select v-model="nodeInspector.knowledgeHttpMethod" :items="['GET', 'POST']"
                            label="HTTP Method" variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('knowledgeHttpMethod', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="primary">mdi-code-brackets</v-icon>
                            </template>
                        </v-select>

                        <!-- Request Headers -->
                        <div class="headers-section mb-3">
                            <div class="section-header mb-2">
                                <span class="section-title">Request Headers</span>
                                <v-btn v-if="!isViewMode" @click="addKnowledgeAPIHeader" size="small" color="primary"
                                    variant="tonal" class="add-btn">
                                    <v-icon size="16">mdi-plus</v-icon>
                                    Add Header
                                </v-btn>
                            </div>
                            <div v-if="nodeInspector.listHeaders" class="headers-list">
                                <div v-for="(header, headerIndex) in nodeInspector.listHeaders" :key="headerIndex"
                                    class="header-row mb-2">
                                    <v-text-field v-model="header.key" label="Header Name" variant="outlined"
                                        density="compact" hide-details="auto" class="modern-input mr-2 mb-2"
                                        @update:model-value="updateKnowledgeAPIHeaderProperty(headerIndex, 'key', $event)"
                                        :readonly="isViewMode" />

                                    <v-text-field v-model="header.value" label="Header Value" variant="outlined"
                                        density="compact" hide-details="auto" class="modern-input mr-2"
                                        @update:model-value="updateKnowledgeAPIHeaderProperty(headerIndex, 'value', $event)"
                                        :readonly="isViewMode" />
                                    <div style="display:flex; justify-content:flex-end; width:100%;">
                                        <v-btn v-if="!isViewMode" @click="deleteKnowledgeAPIHeader(headerIndex)"
                                            size="small" color="error" variant="text" icon>
                                            <v-icon size="16">mdi-delete</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Request Body with Variables Panel -->
                        <h6 class="section-title mt-4">Request Body JSON</h6>
                        
                        <!-- LHB Service Configuration -->
                        <div class="lhb-service-toggle-section mb-3">
                            <v-card variant="outlined" class="pa-3" color="orange-lighten-5" style="border-radius: 12px;">
                                <div class="d-flex align-center">
                                    <v-avatar size="24" color="deep-orange" class="me-3">
                                        <v-icon color="white" size="12">mdi-lock-check</v-icon>
                                    </v-avatar>
                                    <div class="flex-grow-1 me-3">
                                        <div class="text-subtitle-2"><span style="color:#FF6F00;font-weight: 500;">Use LHB Service</span></div>
                                        <div class="text-caption text-medium-emphasis">Encrypt request body JSON and decrypt response</div>
                                    </div>
                                    <v-switch v-model="nodeInspector.useLHBService" color="deep-orange"
                                        hide-details density="compact"
                                        @update:model-value="updateNodeProperty('useLHBService', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                            </v-card>
                        </div>

                        <div class="json-editor-container mb-3">
                            <!-- Variables Panel Header -->
                            <div class="json-variables-header" v-if="!isViewMode">
                                <div class="variables-header-content">
                                    <div class="variables-title">
                                        <span class="text-subtitle-2">Json</span>
                                        <v-chip size="x-small" variant="tonal" color="primary" class="ml-2">
                                            {{ filteredVariables.length }} available variables
                                        </v-chip>
                                    </div>

                                    <!-- Category Filter -->
                                    <v-menu offset-y>
                                        <template v-slot:activator="{ props }">
                                            <v-btn v-bind="props" variant="text" size="small"
                                                class="category-filter-btn"
                                                :class="{ 'filter-active': selectedVariableCategory !== 'All' }">
                                                <v-icon size="14">mdi-filter-variant</v-icon>
                                                <span class="ml-1">{{ selectedVariableCategory }}</span>
                                                <v-icon size="12" class="ml-1">mdi-chevron-down</v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list density="compact" class="category-menu">
                                            <v-list-item v-for="category in variableCategories" :key="category"
                                                @click="selectedVariableCategory = category"
                                                :class="{ 'v-list-item--active': selectedVariableCategory === category }">
                                                <v-list-item-title>{{ category }}</v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>

                                    <!-- Expand/Collapse Button -->
                                    <v-btn icon variant="text" size="small"
                                        @click="variablesPanelExpanded = !variablesPanelExpanded" class="expand-btn">
                                        <v-icon size="16">
                                            {{ variablesPanelExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                        </v-icon>
                                    </v-btn>
                                </div>
                            </div>

                            <!-- Variables Grid - Collapsible -->
                            <v-expand-transition>
                                <div v-show="variablesPanelExpanded" class="json-variables-content">
                                    <!-- Quick Insert Buttons -->
                                    <div class="variables-quick-grid">
                                        <v-tooltip v-for="variable in filteredVariables" :key="variable.key"
                                            :text="variable.description" location="top">
                                            <template v-slot:activator="{ props }">
                                                <v-chip v-bind="props" class="variable-chip-compact"
                                                    @dragstart="onDragStart($event, variable.key)"
                                                    @click="insertKnowledgeVariable(variable.key)" draggable="true"
                                                    :color="getCategoryColor(variable.category)" variant="tonal"
                                                    size="small">
                                                    <v-icon start size="12">{{ variable.icon }}</v-icon>
                                                    <span class="variable-label">{{ variable.label }}</span>
                                                    <v-icon end size="10" class="drag-handle">mdi-drag-vertical</v-icon>
                                                </v-chip>
                                            </template>
                                        </v-tooltip>
                                    </div>
                                </div>
                            </v-expand-transition>

                            <!-- JSON Textarea -->
                            <div class="json-textarea-container">
                                <v-textarea v-model="nodeInspector.knowledgeRequestBody" label="Request Body (JSON)"
                                    variant="outlined" density="compact" rows="6" auto-grow :readonly="isViewMode"
                                    hide-details="auto" placeholder='{"query": "{{userMessage}}", "limit": 10}'
                                    @update:model-value="updateNodeProperty('knowledgeRequestBody', $event)"
                                    class="modern-json-editor" @drop.stop.prevent="onKnowledgeJsonDrop($event)"
                                    @dragover.stop.prevent="onJsonDragOver($event)"
                                    @dragleave.stop="onJsonDragLeave($event)" ref="knowledgeJsonTextarea" />

                                <!-- Insert Helper -->
                                <div class="insert-helper" v-if="!isViewMode">
                                    <v-icon size="12" class="mr-1">mdi-information-outline</v-icon>
                                    <span class="helper-text">
                                        Click variables to insert or drag & drop into editor
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Response Processing -->
                        <v-text-field v-model="nodeInspector.responseDataPath" label="Response Data Path"
                            variant="outlined" density="compact" hide-details="auto"
                            placeholder="data.results (JSON path to extract knowledge)"
                            @update:model-value="updateNodeProperty('responseDataPath', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="primary">mdi-format-list-bulleted</v-icon>
                            </template>
                            <template v-slot:append-inner>
                                <v-tooltip location="top">
                                    <template v-slot:activator="{ props }">
                                        <v-icon v-bind="props" size="small" color="grey-darken-1">
                                            mdi-information-outline
                                        </v-icon>
                                    </template>
                                    <span>JSON path to extract knowledge data from API response</span>
                                </v-tooltip>
                            </template>
                        </v-text-field>
                    </div>

                    <!-- API Request Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'apiRequest'" class="form-section">
                        <h6 class="section-title">API Request Configuration</h6>

                        <!-- Response variable hint -->
                        <v-alert type="info" variant="tonal" density="compact" class="mb-3" style="border-radius:10px;">
                            <div class="text-caption">
                                Response is stored in <strong>&#123;&#123;apiRequestResponse&#125;&#125;</strong> — use it in any node after this.
                            </div>
                        </v-alert>

                        <!-- API URL -->
                        <v-text-field v-model="nodeInspector.apiRequestUrl" label="API URL" variant="outlined"
                            density="compact" hide-details="auto" placeholder="https://api.example.com/endpoint"
                            @update:model-value="updateNodeProperty('apiRequestUrl', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="indigo">mdi-link</v-icon>
                            </template>
                        </v-text-field>

                        <!-- HTTP Method -->
                        <v-select v-model="nodeInspector.apiRequestMethod" :items="['GET', 'POST', 'PUT', 'DELETE']"
                            label="HTTP Method" variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('apiRequestMethod', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="indigo">mdi-code-brackets</v-icon>
                            </template>
                        </v-select>

                        <!-- Request Headers -->
                        <div class="headers-section mb-3">
                            <div class="section-header mb-2">
                                <span class="section-title">Request Headers</span>
                                <v-btn v-if="!isViewMode" @click="addApiRequestHeader" size="small" color="indigo"
                                    variant="tonal" class="add-btn">
                                    <v-icon size="16">mdi-plus</v-icon>
                                    Add Header
                                </v-btn>
                            </div>
                            <div v-if="nodeInspector.apiRequestHeaders" class="headers-list">
                                <div v-for="(header, headerIndex) in nodeInspector.apiRequestHeaders" :key="headerIndex"
                                    class="header-row mb-2">
                                    <v-text-field v-model="header.key" label="Header Name" variant="outlined"
                                        density="compact" hide-details="auto" class="modern-input mr-2 mb-2"
                                        @update:model-value="updateApiRequestHeaderProperty(headerIndex, 'key', $event)"
                                        :readonly="isViewMode" />
                                    <v-text-field v-model="header.value" label="Header Value" variant="outlined"
                                        density="compact" hide-details="auto" class="modern-input mr-2"
                                        @update:model-value="updateApiRequestHeaderProperty(headerIndex, 'value', $event)"
                                        :readonly="isViewMode" />
                                    <div style="display:flex; justify-content:flex-end; width:100%;">
                                        <v-btn v-if="!isViewMode" @click="deleteApiRequestHeader(headerIndex)"
                                            size="small" color="error" variant="text" icon>
                                            <v-icon size="16">mdi-delete</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- LHB Service -->
                        <div class="lhb-service-toggle-section mb-3">
                            <v-card variant="outlined" class="pa-3" color="orange-lighten-5" style="border-radius: 12px;">
                                <div class="d-flex align-center">
                                    <v-avatar size="24" color="deep-orange" class="me-3">
                                        <v-icon color="white" size="12">mdi-lock-check</v-icon>
                                    </v-avatar>
                                    <div class="flex-grow-1 me-3">
                                        <div class="text-subtitle-2"><span style="color:#FF6F00;font-weight: 500;">Use LHB Service</span></div>
                                        <div class="text-caption text-medium-emphasis">Encrypt request body JSON and decrypt response</div>
                                    </div>
                                    <v-switch v-model="nodeInspector.useLHBService" color="deep-orange"
                                        hide-details density="compact"
                                        @update:model-value="updateNodeProperty('useLHBService', $event)"
                                        :readonly="isViewMode" inset />
                                </div>
                            </v-card>
                        </div>

                        <!-- Request Body with Variables -->
                        <h6 class="section-title mt-2">Request Body JSON</h6>
                        <div class="json-editor-container mb-3">
                            <div class="json-variables-header" v-if="!isViewMode">
                                <div class="variables-header-content">
                                    <div class="variables-title">
                                        <span class="text-subtitle-2">Json</span>
                                        <v-chip size="x-small" variant="tonal" color="indigo" class="ml-2">
                                            {{ filteredVariables.length }} available variables
                                        </v-chip>
                                    </div>
                                    <v-menu offset-y>
                                        <template v-slot:activator="{ props }">
                                            <v-btn v-bind="props" variant="text" size="small"
                                                class="category-filter-btn"
                                                :class="{ 'filter-active': selectedVariableCategory !== 'All' }">
                                                <v-icon size="14">mdi-filter-variant</v-icon>
                                                <span class="ml-1">{{ selectedVariableCategory }}</span>
                                                <v-icon size="12" class="ml-1">mdi-chevron-down</v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list density="compact" class="category-menu">
                                            <v-list-item v-for="category in variableCategories" :key="category"
                                                @click="selectedVariableCategory = category"
                                                :class="{ 'v-list-item--active': selectedVariableCategory === category }">
                                                <v-list-item-title>{{ category }}</v-list-item-title>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                    <v-btn icon variant="text" size="small"
                                        @click="variablesPanelExpanded = !variablesPanelExpanded" class="expand-btn">
                                        <v-icon size="16">{{ variablesPanelExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                            <v-expand-transition>
                                <div v-show="variablesPanelExpanded" class="json-variables-content">
                                    <div class="variables-quick-grid">
                                        <v-tooltip v-for="variable in filteredVariables" :key="variable.key"
                                            :text="variable.description" location="top">
                                            <template v-slot:activator="{ props }">
                                                <v-chip v-bind="props" class="variable-chip-compact"
                                                    @dragstart="onDragStart($event, variable.key)"
                                                    @click="insertApiRequestVariable(variable.key)" draggable="true"
                                                    :color="getCategoryColor(variable.category)" variant="tonal"
                                                    size="small">
                                                    <v-icon start size="12">{{ variable.icon }}</v-icon>
                                                    <span class="variable-label">{{ variable.label }}</span>
                                                    <v-icon end size="10" class="drag-handle">mdi-drag-vertical</v-icon>
                                                </v-chip>
                                            </template>
                                        </v-tooltip>
                                    </div>
                                </div>
                            </v-expand-transition>
                            <div class="json-textarea-container">
                                <v-textarea v-model="nodeInspector.apiRequestBody" label="Request Body (JSON)"
                                    variant="outlined" density="compact" rows="6" auto-grow :readonly="isViewMode"
                                    hide-details="auto" placeholder='{"key": "value"}'
                                    @update:model-value="updateNodeProperty('apiRequestBody', $event)"
                                    class="modern-json-editor" @drop.stop.prevent="onApiRequestJsonDrop($event)"
                                    @dragover.stop.prevent="onJsonDragOver($event)"
                                    @dragleave.stop="onJsonDragLeave($event)" ref="apiRequestJsonTextarea" />
                                <div class="insert-helper" v-if="!isViewMode">
                                    <v-icon size="12" class="mr-1">mdi-information-outline</v-icon>
                                    <span class="helper-text">Click variables to insert or drag &amp; drop into editor</span>
                                </div>
                            </div>
                        </div>

                        <!-- Response Data Path -->
                        <v-text-field v-model="nodeInspector.apiRequestResponsePath" label="Response Data Path (optional)"
                            variant="outlined" density="compact" hide-details="auto"
                            placeholder="data.result (leave blank to use full response)"
                            @update:model-value="updateNodeProperty('apiRequestResponsePath', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="indigo">mdi-format-list-bulleted</v-icon>
                            </template>
                            <template v-slot:append-inner>
                                <v-tooltip location="top">
                                    <template v-slot:activator="{ props }">
                                        <v-icon v-bind="props" size="small" color="grey-darken-1">mdi-information-outline</v-icon>
                                    </template>
                                    <span>Dot-notation JSON path to extract specific value from response (e.g. data.result)</span>
                                </v-tooltip>
                            </template>
                        </v-text-field>

                        <!-- Use Result as Message Input -->
                        <div class="mb-3">
                            <v-card variant="outlined" class="pa-3" color="indigo-lighten-5" style="border-radius: 12px; overflow: hidden;">
                                <div class="d-flex align-center" style="gap: 8px;">
                                    <v-avatar size="24" color="indigo" style="flex-shrink: 0;">
                                        <v-icon color="white" size="12">mdi-message-arrow-right</v-icon>
                                    </v-avatar>
                                    <div style="flex: 1; min-width: 0;">
                                        <div class="text-subtitle-2"><span style="color:#3949AB;font-weight: 500;">Use Result as Message Input</span></div>
                                        <div class="text-caption text-medium-emphasis">Replace the user's original message with the API response for downstream nodes</div>
                                    </div>
                                    <div style="flex-shrink: 0;">
                                        <v-switch v-model="nodeInspector.useResultAsMessage" color="indigo"
                                            hide-details density="compact"
                                            @update:model-value="updateNodeProperty('useResultAsMessage', $event)"
                                            :readonly="isViewMode" inset />
                                    </div>
                                </div>
                            </v-card>
                        </div>
                    </div>

                    <!-- Favorite Matcher Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'favoriteMatcher'" class="form-section">
                        <h6 class="section-title">Favorite Matcher Configuration</h6>

                        <!-- Usage hint -->
                        <v-alert type="info" variant="tonal" density="compact" class="mb-3" style="border-radius:10px;">
                            <div class="text-caption">
                                Attach under an <strong>API Request</strong> node. Reads favorites from
                                <strong>&#123;&#123;favoritesSourceVariable&#125;&#125;</strong> and writes the match result to
                                <strong>&#123;&#123;outputVariable&#125;&#125;</strong>.
                            </div>
                        </v-alert>

                        <!-- Service Type -->
                        <v-select v-model="nodeInspector.serviceType" :items="['TRANSFER', 'TOPUP', 'BILL_PAYMENT']"
                            label="Service Type" variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('serviceType', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="teal">mdi-shape</v-icon>
                            </template>
                        </v-select>

                        <!-- Favorites Source Variable -->
                        <v-text-field v-model="nodeInspector.favoritesSourceVariable" label="Favorites Source Variable"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('favoritesSourceVariable', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="teal">mdi-variable</v-icon>
                            </template>
                        </v-text-field>

                        <!-- Output Variable -->
                        <v-text-field v-model="nodeInspector.outputVariable" label="Output Variable"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('outputVariable', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="teal">mdi-variable</v-icon>
                            </template>
                        </v-text-field>

                        <!-- Enable Embedding Fallback -->
                        <v-switch v-model="nodeInspector.enableEmbedding" label="Enable Embedding Fallback (Tier 5)"
                            color="teal" hide-details density="compact" inset class="mb-2"
                            @update:model-value="updateNodeProperty('enableEmbedding', $event)"
                            :readonly="isViewMode" />

                        <!-- Fuzzy Threshold -->
                        <v-slider v-model="nodeInspector.fuzzyThreshold" :min="0.5" :max="1" :step="0.01" thumb-label
                            label="Fuzzy Threshold" color="teal" class="mb-2"
                            @update:model-value="updateNodeProperty('fuzzyThreshold', $event)"
                            :readonly="isViewMode" />

                        <!-- Embedding Threshold -->
                        <v-slider v-model="nodeInspector.embeddingThreshold" :min="0.3" :max="1" :step="0.01" thumb-label
                            label="Embedding Threshold" color="teal"
                            @update:model-value="updateNodeProperty('embeddingThreshold', $event)"
                            :readonly="isViewMode" />

                        <!-- Max Results (0 = unlimited) -->
                        <v-text-field v-model.number="nodeInspector.maxResults" type="number" min="0" step="1"
                            label="Max Results (0 = unlimited)" variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('maxResults', Math.max(0, parseInt($event) || 0))"
                            class="modern-input mt-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="teal">mdi-format-list-numbered</v-icon>
                            </template>
                        </v-text-field>
                    </div>

                    <!-- Service Matcher Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'serviceMatcher'" class="form-section">
                        <h6 class="section-title">Service Matcher Configuration</h6>

                        <!-- Usage hint -->
                        <v-alert type="info" variant="tonal" density="compact" class="mb-3" style="border-radius:10px;">
                            <div class="text-caption">
                                Matches <strong>&#123;&#123;inputVariable&#125;&#125;</strong> against DB data
                                (TRANSFER&nbsp;=&nbsp;banks), and writes the match result to
                                <strong>&#123;&#123;outputVariable&#125;&#125;</strong> for a downstream Service Fallback.
                                Turn on <strong>Enable Favorite Lookup</strong> to also call an API for the user's favorites,
                                match those first, and split the flow into
                                <strong>Favorite</strong> / <strong>Service</strong> / <strong>No Match</strong> branches.
                            </div>
                        </v-alert>

                        <!-- === Favorite Lookup (master toggle) === -->
                        <h6 class="section-title">Favorite Lookup</h6>
                        <div class="mb-3">
                            <v-card variant="outlined" class="pa-3" color="cyan-lighten-5" style="border-radius: 12px; overflow: hidden;">
                                <div class="d-flex align-center" style="gap: 8px;">
                                    <v-avatar size="24" color="cyan-darken-2" style="flex-shrink: 0;">
                                        <v-icon color="white" size="12">mdi-star-check</v-icon>
                                    </v-avatar>
                                    <div style="flex: 1; min-width: 0;">
                                        <div class="text-subtitle-2"><span style="color:#00838F;font-weight: 500;">Enable Favorite Lookup</span></div>
                                        <div class="text-caption text-medium-emphasis">
                                            Off = legacy behaviour (one <strong>output</strong> handle, service matching only).
                                            On = call the favorites API, match favorites first, then branch into
                                            Favorite / Service / No Match.
                                        </div>
                                    </div>
                                    <div style="flex-shrink: 0;">
                                        <v-switch v-model="nodeInspector.enableFavoriteLookup" color="cyan-darken-2"
                                            hide-details density="compact"
                                            @update:model-value="updateNodeProperty('enableFavoriteLookup', $event)"
                                            :readonly="isViewMode" inset />
                                    </div>
                                </div>
                            </v-card>
                        </div>

                        <template v-if="nodeInspector.enableFavoriteLookup">
                            <!-- Match Strategy -->
                            <v-select v-model="nodeInspector.matchStrategy" :items="['favorite-first', 'service-first']"
                                label="Match Strategy" variant="outlined" density="compact" hide-details="auto"
                                @update:model-value="updateNodeProperty('matchStrategy', $event)"
                                class="modern-input mb-1" :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="20" color="cyan-darken-2">mdi-directions-fork</v-icon>
                                </template>
                            </v-select>
                            <div class="text-caption text-medium-emphasis mb-3" style="padding-left: 4px;">
                                <strong>favorite-first</strong> (default) matches favorites before service data.
                                <strong>service-first</strong> is reserved for a future release — it runs service matching
                                first but its result is <em>not</em> used to filter favorites yet.
                            </div>

                            <!-- === API Request === -->
                            <h6 class="section-title">API Request</h6>

                            <!-- API URL -->
                            <v-text-field v-model="nodeInspector.apiRequestUrl" label="API URL" variant="outlined"
                                density="compact" hide-details="auto" placeholder="https://api.example.com/favorites"
                                @update:model-value="updateNodeProperty('apiRequestUrl', $event)"
                                class="modern-input mb-1" :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="20" color="cyan-darken-2">mdi-link</v-icon>
                                </template>
                            </v-text-field>
                            <div class="text-caption text-medium-emphasis mb-3" style="padding-left: 4px;">
                                Leave empty to skip the API call and read favorites from
                                <strong>Favorites Source Variable</strong> instead.
                            </div>

                            <!-- HTTP Method -->
                            <v-select v-model="nodeInspector.apiRequestMethod" :items="['GET', 'POST', 'PUT', 'DELETE']"
                                label="HTTP Method" variant="outlined" density="compact" hide-details="auto"
                                @update:model-value="updateNodeProperty('apiRequestMethod', $event)"
                                class="modern-input mb-3" :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="20" color="cyan-darken-2">mdi-code-brackets</v-icon>
                                </template>
                            </v-select>

                            <!-- Request Headers -->
                            <div class="headers-section mb-3">
                                <div class="section-header mb-2">
                                    <span class="section-title">Request Headers</span>
                                    <v-btn v-if="!isViewMode" @click="addApiRequestHeader" size="small"
                                        color="cyan-darken-2" variant="tonal" class="add-btn">
                                        <v-icon size="16">mdi-plus</v-icon>
                                        Add Header
                                    </v-btn>
                                </div>
                                <div v-if="nodeInspector.apiRequestHeaders" class="headers-list">
                                    <div v-for="(header, headerIndex) in nodeInspector.apiRequestHeaders"
                                        :key="headerIndex" class="header-row mb-2">
                                        <v-text-field v-model="header.key" label="Header Name" variant="outlined"
                                            density="compact" hide-details="auto" class="modern-input mr-2 mb-2"
                                            @update:model-value="updateApiRequestHeaderProperty(headerIndex, 'key', $event)"
                                            :readonly="isViewMode" />
                                        <v-text-field v-model="header.value" label="Header Value" variant="outlined"
                                            density="compact" hide-details="auto" class="modern-input mr-2"
                                            @update:model-value="updateApiRequestHeaderProperty(headerIndex, 'value', $event)"
                                            :readonly="isViewMode" />
                                        <div style="display:flex; justify-content:flex-end; width:100%;">
                                            <v-btn v-if="!isViewMode" @click="deleteApiRequestHeader(headerIndex)"
                                                size="small" color="error" variant="text" icon>
                                                <v-icon size="16">mdi-delete</v-icon>
                                            </v-btn>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- LHB Service -->
                            <div class="lhb-service-toggle-section mb-3">
                                <v-card variant="outlined" class="pa-3" color="orange-lighten-5" style="border-radius: 12px;">
                                    <div class="d-flex align-center">
                                        <v-avatar size="24" color="deep-orange" class="me-3">
                                            <v-icon color="white" size="12">mdi-lock-check</v-icon>
                                        </v-avatar>
                                        <div class="flex-grow-1 me-3">
                                            <div class="text-subtitle-2"><span style="color:#FF6F00;font-weight: 500;">Use LHB Service</span></div>
                                            <div class="text-caption text-medium-emphasis">Encrypt request body JSON and decrypt response</div>
                                        </div>
                                        <v-switch v-model="nodeInspector.useLHBService" color="deep-orange"
                                            hide-details density="compact"
                                            @update:model-value="updateNodeProperty('useLHBService', $event)"
                                            :readonly="isViewMode" inset />
                                    </div>
                                </v-card>
                            </div>

                            <!-- Request Body with Variables -->
                            <h6 class="section-title mt-2">Request Body JSON</h6>
                            <div class="json-editor-container mb-3">
                                <div class="json-variables-header" v-if="!isViewMode">
                                    <div class="variables-header-content">
                                        <div class="variables-title">
                                            <span class="text-subtitle-2">Json</span>
                                            <v-chip size="x-small" variant="tonal" color="cyan-darken-2" class="ml-2">
                                                {{ filteredVariables.length }} available variables
                                            </v-chip>
                                        </div>
                                        <v-menu offset-y>
                                            <template v-slot:activator="{ props }">
                                                <v-btn v-bind="props" variant="text" size="small"
                                                    class="category-filter-btn"
                                                    :class="{ 'filter-active': selectedVariableCategory !== 'All' }">
                                                    <v-icon size="14">mdi-filter-variant</v-icon>
                                                    <span class="ml-1">{{ selectedVariableCategory }}</span>
                                                    <v-icon size="12" class="ml-1">mdi-chevron-down</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list density="compact" class="category-menu">
                                                <v-list-item v-for="category in variableCategories" :key="category"
                                                    @click="selectedVariableCategory = category"
                                                    :class="{ 'v-list-item--active': selectedVariableCategory === category }">
                                                    <v-list-item-title>{{ category }}</v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                        <v-btn icon variant="text" size="small"
                                            @click="variablesPanelExpanded = !variablesPanelExpanded" class="expand-btn">
                                            <v-icon size="16">{{ variablesPanelExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                                <v-expand-transition>
                                    <div v-show="variablesPanelExpanded" class="json-variables-content">
                                        <div class="variables-quick-grid">
                                            <v-tooltip v-for="variable in filteredVariables" :key="variable.key"
                                                :text="variable.description" location="top">
                                                <template v-slot:activator="{ props }">
                                                    <v-chip v-bind="props" class="variable-chip-compact"
                                                        @dragstart="onDragStart($event, variable.key)"
                                                        @click="insertApiRequestVariable(variable.key)" draggable="true"
                                                        :color="getCategoryColor(variable.category)" variant="tonal"
                                                        size="small">
                                                        <v-icon start size="12">{{ variable.icon }}</v-icon>
                                                        <span class="variable-label">{{ variable.label }}</span>
                                                        <v-icon end size="10" class="drag-handle">mdi-drag-vertical</v-icon>
                                                    </v-chip>
                                                </template>
                                            </v-tooltip>
                                        </div>
                                    </div>
                                </v-expand-transition>
                                <div class="json-textarea-container">
                                    <v-textarea v-model="nodeInspector.apiRequestBody" label="Request Body (JSON)"
                                        variant="outlined" density="compact" rows="6" auto-grow :readonly="isViewMode"
                                        hide-details="auto" placeholder='{"cifNo": "{CIFNo}"}'
                                        @update:model-value="updateNodeProperty('apiRequestBody', $event)"
                                        class="modern-json-editor" @drop.stop.prevent="onApiRequestJsonDrop($event)"
                                        @dragover.stop.prevent="onJsonDragOver($event)"
                                        @dragleave.stop="onJsonDragLeave($event)" ref="apiRequestJsonTextarea" />
                                    <div class="insert-helper" v-if="!isViewMode">
                                        <v-icon size="12" class="mr-1">mdi-information-outline</v-icon>
                                        <span class="helper-text">Click variables to insert or drag &amp; drop into editor</span>
                                    </div>
                                </div>
                            </div>

                            <!-- Response Data Path -->
                            <v-text-field v-model="nodeInspector.apiRequestResponsePath"
                                label="Response Data Path (optional)" variant="outlined" density="compact"
                                hide-details="auto" placeholder="data.favorites (leave blank to use full response)"
                                @update:model-value="updateNodeProperty('apiRequestResponsePath', $event)"
                                class="modern-input mb-1" :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="20" color="cyan-darken-2">mdi-format-list-bulleted</v-icon>
                                </template>
                                <template v-slot:append-inner>
                                    <v-tooltip location="top">
                                        <template v-slot:activator="{ props }">
                                            <v-icon v-bind="props" size="small" color="grey-darken-1">mdi-information-outline</v-icon>
                                        </template>
                                        <span>Dot-notation JSON path to extract the favorites array from the response (e.g. data.favorites)</span>
                                    </v-tooltip>
                                </template>
                            </v-text-field>
                            <div class="text-caption text-medium-emphasis mb-3" style="padding-left: 4px;">
                                <strong>Required when Use LHB Service is on</strong> — the decrypted payload is read from
                                this path before matching.
                            </div>

                            <!-- Timeout -->
                            <v-text-field v-model.number="nodeInspector.timeout" type="number" min="1" step="1"
                                label="Timeout (seconds)" variant="outlined" density="compact" hide-details="auto"
                                @update:model-value="updateNodeProperty('timeout', Math.max(1, parseInt($event) || 30))"
                                class="modern-input mb-3" :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="20" color="cyan-darken-2">mdi-timer-outline</v-icon>
                                </template>
                            </v-text-field>

                            <!-- Favorites Source Variable -->
                            <v-text-field v-model="nodeInspector.favoritesSourceVariable"
                                label="Favorites Source Variable (optional)" variant="outlined" density="compact"
                                hide-details="auto" placeholder="Leave empty to use this node's API"
                                @update:model-value="updateNodeProperty('favoritesSourceVariable', $event)"
                                class="modern-input mb-1" :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="20" color="cyan-darken-2">mdi-variable</v-icon>
                                </template>
                            </v-text-field>
                            <div class="text-caption text-medium-emphasis mb-3" style="padding-left: 4px;">
                                <strong>API URL wins.</strong> Whenever <strong>API URL</strong> above is filled in, this
                                node calls that API itself and this variable is <strong>ignored</strong>. Use it only when
                                <strong>API URL</strong> is left empty &mdash; the favorites list is then read from that
                                context variable instead (produced by an earlier node).
                            </div>
                            <v-alert
                                v-if="(nodeInspector.apiRequestUrl || '').trim() && (nodeInspector.favoritesSourceVariable || '').trim()"
                                type="warning" variant="tonal" density="compact" class="mb-3" style="border-radius:10px;">
                                <div class="text-caption">
                                    <strong>API URL is set &mdash; this variable will be ignored.</strong>
                                    This node calls the API above and reads the favorites from its response.
                                    If you did not mean to keep it, clear the
                                    <strong>Favorites Source Variable</strong> box.
                                </div>
                            </v-alert>

                            <!-- === Favorite Matching === -->
                            <h6 class="section-title">Favorite Matching</h6>

                            <!-- Enable Embedding Fallback (favorite) -->
                            <v-switch v-model="nodeInspector.favoriteEnableEmbedding"
                                label="Enable Embedding Fallback (Tier 5)" color="cyan-darken-2" hide-details
                                density="compact" inset class="mb-2"
                                @update:model-value="updateNodeProperty('favoriteEnableEmbedding', $event)"
                                :readonly="isViewMode" />

                            <!-- Keyword Funnel (favorite) -->
                            <v-switch v-model="nodeInspector.favoriteKeywordFunnel"
                                label="ตัด keyword บริการก่อนค้น favorite" color="cyan-darken-2" hide-details
                                density="compact" inset class="mb-1"
                                @update:model-value="updateNodeProperty('favoriteKeywordFunnel', $event)"
                                :readonly="isViewMode" />
                            <div class="text-caption text-medium-emphasis mb-3" style="padding-left: 4px;">
                                ตัดชื่อธนาคาร/ผู้ให้บริการออกจากข้อความก่อนค้น favorite แล้วใช้ข้อความเต็มคัดอีกรอบ &mdash;
                                กัน &quot;โอนเงิน scb&quot; ไปติด favorite ชื่อ &quot;แม่ scb&quot;
                            </div>

                            <!-- Fuzzy Threshold (favorite) -->
                            <v-slider v-model="nodeInspector.favoriteFuzzyThreshold" :min="0.5" :max="1" :step="0.01"
                                thumb-label label="Fuzzy Threshold" color="cyan-darken-2" class="mb-2"
                                @update:model-value="updateNodeProperty('favoriteFuzzyThreshold', $event)"
                                :readonly="isViewMode" />

                            <!-- Embedding Threshold (favorite) -->
                            <v-slider v-model="nodeInspector.favoriteEmbeddingThreshold" :min="0.3" :max="1" :step="0.01"
                                thumb-label label="Embedding Threshold" color="cyan-darken-2"
                                @update:model-value="updateNodeProperty('favoriteEmbeddingThreshold', $event)"
                                :readonly="isViewMode" />

                            <!-- Max Results (favorite) -->
                            <v-text-field v-model.number="nodeInspector.favoriteMaxResults" type="number" min="0"
                                step="1" label="Max Results (0 = unlimited)" variant="outlined" density="compact"
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('favoriteMaxResults', Math.max(0, parseInt($event) || 0))"
                                class="modern-input mt-3 mb-3" :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="20" color="cyan-darken-2">mdi-format-list-numbered</v-icon>
                                </template>
                            </v-text-field>

                            <!-- === Favorite Match Rules === -->
                            <h6 class="section-title">Favorite Match Rules</h6>

                            <v-select v-model="nodeInspector.favoriteMatchMode" :items="[
                                { title: 'ALL conditions must be true (AND)', value: 'all' },
                                { title: 'ANY condition must be true (OR)', value: 'any' }
                            ]" item-title="title" item-value="value" label="Match Mode" variant="outlined"
                                density="compact" hide-details="auto"
                                @update:model-value="updateNodeProperty('favoriteMatchMode', $event)"
                                class="modern-input mb-3" :readonly="isViewMode" />

                            <div class="conditions-list mb-3">
                                <div class="d-flex justify-space-between align-center mb-2">
                                    <h6 class="section-title mb-0">Rules ({{ nodeInspector.serviceMatcherFavoriteRules?.length || 0 }})</h6>
                                    <v-btn v-if="!isViewMode" size="small" color="cyan-darken-2" variant="tonal"
                                        class="add-btn" @click="addServiceMatcherRule('favorite')">
                                        <v-icon size="16">mdi-plus</v-icon>
                                        Add Rule
                                    </v-btn>
                                </div>

                                <v-expansion-panels v-model="serviceMatcherFavoritePanel" variant="accordion" class="mb-2">
                                    <v-expansion-panel
                                        v-for="(condition, index) in nodeInspector.serviceMatcherFavoriteRules"
                                        :key="condition.id || index"
                                        class="json-condition-rule-panel">
                                        <v-expansion-panel-title class="py-2">
                                            <div class="d-flex align-center justify-space-between w-100">
                                                <span class="text-body-2">
                                                    <strong>Rule {{ index + 1 }}:</strong>
                                                    {{ condition.jsonPath || '(empty)' }}
                                                    {{ getJsonConditionOperatorLabel(condition.operator) }}
                                                    {{ condition.compareValue || '' }}
                                                </span>
                                                <v-btn v-if="!isViewMode && nodeInspector.serviceMatcherFavoriteRules.length > 1"
                                                    icon size="x-small" variant="text" color="red"
                                                    @click.stop="removeServiceMatcherRule('favorite', index)">
                                                    <v-icon size="14">mdi-delete</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <div class="pa-1">
                                                <!-- JSON Path -->
                                                <v-text-field v-model="condition.jsonPath" label="JSON Path"
                                                    variant="outlined" density="compact" hide-details="auto"
                                                    placeholder="e.g., match_count, matches[0].score"
                                                    @update:model-value="updateServiceMatcherRules('favorite')"
                                                    class="modern-input mb-2" :readonly="isViewMode" />

                                                <!-- Operator -->
                                                <v-select v-model="condition.operator" :items="jsonConditionOperators"
                                                    item-title="title" item-value="value" label="Operator"
                                                    variant="outlined" density="compact" hide-details="auto"
                                                    @update:model-value="updateServiceMatcherRules('favorite')"
                                                    class="modern-input mb-2" :readonly="isViewMode" />

                                                <!-- Compare Value -->
                                                <v-text-field
                                                    v-if="!['is_empty', 'is_not_empty', 'array_empty', 'array_not_empty', 'exists', 'not_exists', 'is_true', 'is_false'].includes(condition.operator)"
                                                    v-model="condition.compareValue" label="Compare Value"
                                                    variant="outlined" density="compact" hide-details="auto"
                                                    placeholder="Value to compare against"
                                                    @update:model-value="updateServiceMatcherRules('favorite')"
                                                    class="modern-input" :readonly="isViewMode" />
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>

                                <div class="text-caption text-medium-emphasis" style="padding-left: 4px;">
                                    Evaluated against the favorite match envelope. Leave unconfigured and the node falls
                                    back to the default rule <strong>match_count greater_than 0</strong>.
                                </div>
                            </div>

                            <!-- === Service Match Rules === -->
                            <h6 class="section-title">Service Match Rules</h6>

                            <v-select v-model="nodeInspector.serviceMatchMode" :items="[
                                { title: 'ALL conditions must be true (AND)', value: 'all' },
                                { title: 'ANY condition must be true (OR)', value: 'any' }
                            ]" item-title="title" item-value="value" label="Match Mode" variant="outlined"
                                density="compact" hide-details="auto"
                                @update:model-value="updateNodeProperty('serviceMatchMode', $event)"
                                class="modern-input mb-3" :readonly="isViewMode" />

                            <div class="conditions-list mb-3">
                                <div class="d-flex justify-space-between align-center mb-2">
                                    <h6 class="section-title mb-0">Rules ({{ nodeInspector.serviceMatcherServiceRules?.length || 0 }})</h6>
                                    <v-btn v-if="!isViewMode" size="small" color="cyan-darken-2" variant="tonal"
                                        class="add-btn" @click="addServiceMatcherRule('service')">
                                        <v-icon size="16">mdi-plus</v-icon>
                                        Add Rule
                                    </v-btn>
                                </div>

                                <v-expansion-panels v-model="serviceMatcherServicePanel" variant="accordion" class="mb-2">
                                    <v-expansion-panel
                                        v-for="(condition, index) in nodeInspector.serviceMatcherServiceRules"
                                        :key="condition.id || index"
                                        class="json-condition-rule-panel">
                                        <v-expansion-panel-title class="py-2">
                                            <div class="d-flex align-center justify-space-between w-100">
                                                <span class="text-body-2">
                                                    <strong>Rule {{ index + 1 }}:</strong>
                                                    {{ condition.jsonPath || '(empty)' }}
                                                    {{ getJsonConditionOperatorLabel(condition.operator) }}
                                                    {{ condition.compareValue || '' }}
                                                </span>
                                                <v-btn v-if="!isViewMode && nodeInspector.serviceMatcherServiceRules.length > 1"
                                                    icon size="x-small" variant="text" color="red"
                                                    @click.stop="removeServiceMatcherRule('service', index)">
                                                    <v-icon size="14">mdi-delete</v-icon>
                                                </v-btn>
                                            </div>
                                        </v-expansion-panel-title>
                                        <v-expansion-panel-text>
                                            <div class="pa-1">
                                                <!-- JSON Path -->
                                                <v-text-field v-model="condition.jsonPath" label="JSON Path"
                                                    variant="outlined" density="compact" hide-details="auto"
                                                    placeholder="e.g., match_count, matches[0].score"
                                                    @update:model-value="updateServiceMatcherRules('service')"
                                                    class="modern-input mb-2" :readonly="isViewMode" />

                                                <!-- Operator -->
                                                <v-select v-model="condition.operator" :items="jsonConditionOperators"
                                                    item-title="title" item-value="value" label="Operator"
                                                    variant="outlined" density="compact" hide-details="auto"
                                                    @update:model-value="updateServiceMatcherRules('service')"
                                                    class="modern-input mb-2" :readonly="isViewMode" />

                                                <!-- Compare Value -->
                                                <v-text-field
                                                    v-if="!['is_empty', 'is_not_empty', 'array_empty', 'array_not_empty', 'exists', 'not_exists', 'is_true', 'is_false'].includes(condition.operator)"
                                                    v-model="condition.compareValue" label="Compare Value"
                                                    variant="outlined" density="compact" hide-details="auto"
                                                    placeholder="Value to compare against"
                                                    @update:model-value="updateServiceMatcherRules('service')"
                                                    class="modern-input" :readonly="isViewMode" />
                                            </div>
                                        </v-expansion-panel-text>
                                    </v-expansion-panel>
                                </v-expansion-panels>

                                <div class="text-caption text-medium-emphasis" style="padding-left: 4px;">
                                    Evaluated against the service match envelope when the favorite rules do not pass.
                                    Leave unconfigured and the node falls back to the default rule
                                    <strong>match_count greater_than 0</strong>.
                                </div>
                            </div>

                            <!-- === Output Labels === -->
                            <h6 class="section-title">Output Labels</h6>
                            <div class="output-labels-section mb-3">
                                <v-text-field v-model="nodeInspector.favoriteLabel" label="Favorite Label"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('favoriteLabel', $event)"
                                    class="modern-input mb-2" :readonly="isViewMode">
                                    <template v-slot:prepend-inner>
                                        <v-icon size="20" color="cyan-darken-2">mdi-star-outline</v-icon>
                                    </template>
                                </v-text-field>

                                <v-text-field v-model="nodeInspector.serviceLabel" label="Service Label"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('serviceLabel', $event)"
                                    class="modern-input mb-2" :readonly="isViewMode">
                                    <template v-slot:prepend-inner>
                                        <v-icon size="20" color="cyan-darken-2">mdi-database-search</v-icon>
                                    </template>
                                </v-text-field>

                                <v-text-field v-model="nodeInspector.noMatchLabel" label="No Match Label"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('noMatchLabel', $event)"
                                    class="modern-input" :readonly="isViewMode">
                                    <template v-slot:prepend-inner>
                                        <v-icon size="20" color="cyan-darken-2">mdi-close-circle-outline</v-icon>
                                    </template>
                                </v-text-field>
                            </div>
                        </template>

                        <!-- === Service Matching (used by both modes) === -->
                        <h6 class="section-title">Service Matching</h6>

                        <!-- Service Type -->
                        <v-select v-model="nodeInspector.serviceType" :items="['TRANSFER', 'TOPUP', 'BILL_PAYMENT']"
                            label="Service Type" variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('serviceType', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="cyan-darken-2">mdi-shape</v-icon>
                            </template>
                        </v-select>

                        <!-- Data Integration source (empty = use built-in Service Configuration) -->
                        <v-autocomplete v-model="nodeInspector.dataIntegrationId" :items="listDataIntegration"
                            item-title="dataName" item-value="id" label="Data Integration" variant="outlined"
                            density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('dataIntegrationId', $event)"
                            class="modern-input mb-1" placeholder="Select a Data Integration"
                            :clearable="!isViewMode" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="cyan-darken-2">mdi-database-import</v-icon>
                            </template>
                        </v-autocomplete>
                        <div class="text-caption text-medium-emphasis mb-3" style="padding-left: 4px;">
                            Columns are read with a fixed schema per Service Type. Leave empty to use the built-in Service Configuration.
                        </div>

                        <!-- Input Variable -->
                        <v-text-field v-model="nodeInspector.inputVariable" label="Input Variable"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('inputVariable', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="cyan-darken-2">mdi-variable</v-icon>
                            </template>
                        </v-text-field>

                        <!-- Output Variable -->
                        <v-text-field v-model="nodeInspector.outputVariable" label="Output Variable"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('outputVariable', $event)"
                            class="modern-input mb-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="cyan-darken-2">mdi-variable</v-icon>
                            </template>
                        </v-text-field>

                        <!-- Active candidates only -->
                        <v-switch v-model="nodeInspector.activeOnly" label="Active candidates only"
                            color="cyan-darken-2" hide-details density="compact" inset class="mb-2"
                            @update:model-value="updateNodeProperty('activeOnly', $event)"
                            :readonly="isViewMode" />

                        <!-- Enable Embedding Fallback -->
                        <v-switch v-model="nodeInspector.enableEmbedding" label="Enable Embedding Fallback (Tier 5)"
                            color="cyan-darken-2" hide-details density="compact" inset class="mb-2"
                            @update:model-value="updateNodeProperty('enableEmbedding', $event)"
                            :readonly="isViewMode" />

                        <!-- Fuzzy Threshold -->
                        <v-slider v-model="nodeInspector.fuzzyThreshold" :min="0.5" :max="1" :step="0.01" thumb-label
                            label="Fuzzy Threshold" color="cyan-darken-2" class="mb-2"
                            @update:model-value="updateNodeProperty('fuzzyThreshold', $event)"
                            :readonly="isViewMode" />

                        <!-- Embedding Threshold -->
                        <v-slider v-model="nodeInspector.embeddingThreshold" :min="0.3" :max="1" :step="0.01" thumb-label
                            label="Embedding Threshold" color="cyan-darken-2"
                            @update:model-value="updateNodeProperty('embeddingThreshold', $event)"
                            :readonly="isViewMode" />

                        <!-- Max Results (0 = unlimited) -->
                        <v-text-field v-model.number="nodeInspector.maxResults" type="number" min="0" step="1"
                            label="Max Results (0 = unlimited)" variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('maxResults', Math.max(0, parseInt($event) || 0))"
                            class="modern-input mt-3" :readonly="isViewMode">
                            <template v-slot:prepend-inner>
                                <v-icon size="20" color="cyan-darken-2">mdi-format-list-numbered</v-icon>
                            </template>
                        </v-text-field>
                    </div>

                    <!-- Products Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'products'" class="form-section">
                        <h6 class="section-title">Products Configuration</h6>

                        <!-- Product Source Type Toggle (Group Product hidden - use Document Filter Title/SubTitle instead) -->
                        <div class="mb-3" style="display: none;">
                            <div class="text-caption text-grey mb-1">Product Source</div>
                            <v-btn-toggle
                                v-model="nodeInspector.productSourceType"
                                mandatory
                                density="compact"
                                color="primary"
                                :disabled="isViewMode"
                                @update:model-value="onProductSourceTypeChange($event)"
                            >
                                <v-btn value="product" size="small">
                                    <v-icon start size="small">mdi-package-variant</v-icon>
                                    Product
                                </v-btn>
                                <v-btn value="group" size="small">
                                    <v-icon start size="small">mdi-folder-multiple</v-icon>
                                    Group Product
                                </v-btn>
                            </v-btn-toggle>
                        </div>

                        <!-- Product Group List Selection (when sourceType = 'product') -->
                        <v-autocomplete v-if="nodeInspector.productSourceType !== 'group'"
                            v-model="nodeInspector.productGroupList" :items="DDProductGroup"
                            item-title="name" item-value="id" label="Product Group List" variant="outlined"
                            density="compact" hide-details="auto" chips multiple return-object
                            @update:model-value="updateNodeProperty('productGroupList', $event)"
                            class="modern-input mb-3" placeholder="Select product group" :clearable="!isViewMode"
                            :readonly="isViewMode">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props" class="product-group-option">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="primary">mdi-package-variant</v-icon>
                                    </template>
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-autocomplete>

                        <!-- Product Group Collection Selection (when sourceType = 'group') -->
                        <v-autocomplete v-else
                            v-model="nodeInspector.productGroupCollectionId"
                            :items="DDProductGroupCollection"
                            item-title="name"
                            item-value="id"
                            label="Product Group Collection"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            class="modern-input mb-3"
                            placeholder="Select product group collection"
                            :clearable="!isViewMode"
                            :readonly="isViewMode"
                            @update:model-value="onProductGroupCollectionChange($event)"
                        >
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props">
                                    <template v-slot:prepend>
                                        <v-icon size="20" color="primary">mdi-folder-multiple</v-icon>
                                    </template>
                                    <template v-slot:append>
                                        <v-chip size="x-small" color="primary" variant="tonal">
                                            {{ item.raw.productGroupCount }} groups
                                        </v-chip>
                                    </template>
                                </v-list-item>
                            </template>
                            <template v-slot:selection="{ item }">
                                <v-chip color="primary" variant="tonal" size="small">
                                    <v-icon start size="small">mdi-folder-multiple</v-icon>
                                    {{ item.title }} ({{ item.raw.productGroupCount }} groups)
                                </v-chip>
                            </template>
                        </v-autocomplete>

                        <!-- Top-N Chunks -->
                        <v-text-field v-model="nodeInspector.productTopNChunks" type="number" label="Top-N Chunks"
                            variant="outlined" density="compact" hide-details="auto" min="1" step="1"
                            @focus="$event.target.select()"
                            @blur="validateAndFixProductTopNChunks($event)"
                            @keydown.enter="validateAndFixProductTopNChunks($event)"
                            class="modern-input mb-3" placeholder="Enter number of chunks" :readonly="isViewMode" />

                        <!-- Cosine Similarity -->
                        <v-text-field v-model="nodeInspector.productCosineSimilarity" type="number"
                            label="Cosine Similarity" variant="outlined" density="compact" hide-details="auto"
                            min="0.0001" max="1" step="0.0001"
                            @focus="$event.target.select()"
                            @blur="validateAndFixProductCosineSimilarity($event)"
                            @keydown.enter="validateAndFixProductCosineSimilarity($event)"
                            class="modern-input" placeholder="Enter similarity threshold (0.0001 - 1.0)"
                            :readonly="isViewMode" />
                    </div>

                    <!-- SharePoint Node Properties -->
                    <div v-else-if="selectedNode.data.type === 'sharepoint'" class="form-section">
                        <h6 class="section-title">SharePoint Configuration</h6>
                        
                        <!-- File Location Section -->
                        <div class="subsection">
                            <div class="subsection-header mb-3">
                                <v-icon size="20" color="info" class="mr-2">mdi-file-search</v-icon>
                                <span class="subsection-title">File Location</span>
                            </div>
                            
                            <!-- Filename -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.filename" 
                                label="Filename" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('sharepoint.filename', $event)"
                                class="modern-input mb-3" 
                                placeholder="example.docx"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="info">mdi-file-document</v-icon>
                                </template>
                            </v-text-field>

                            <!-- Folder Path -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.folderPath" 
                                label="Subfolder Path"
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('sharepoint.folderPath', $event)"
                                class="modern-input" 
                                placeholder="/SubfolderName (optional)"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="info">mdi-folder</v-icon>
                                </template>
                                <template v-slot:append-inner>
                                    <v-tooltip location="top">
                                        <template v-slot:activator="{ props }">
                                            <v-icon v-bind="props" size="16" color="grey-darken-1">mdi-help-circle-outline</v-icon>
                                        </template>
                                        <span>Subfolder path within the SharePoint site<br><strong>Root: /Documents</strong><br>Example: /Data will become /Documents/Data</span>
                                    </v-tooltip>
                                </template>
                            </v-text-field>
                        </div>
                        
                        <!-- Connection Settings Section -->
                        <div class="subsection mb-4">
                            <div class="subsection-header mb-3">
                                <v-icon size="20" color="primary" class="mr-2">mdi-connection</v-icon>
                                <span class="subsection-title">Connection Settings</span>
                            </div>
                            
                            <!-- Site URL -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.siteUrl" 
                                label="Site URL" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('sharepoint.siteUrl', $event)"
                                class="modern-input mb-3" 
                                placeholder="https://yourtenant.sharepoint.com/sites/yoursite"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="primary">mdi-web</v-icon>
                                </template>
                            </v-text-field>

                            <!-- Tenant ID -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.tenantId" 
                                label="Tenant ID" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('sharepoint.tenantId', $event)"
                                class="modern-input mb-3" 
                                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                autocomplete="off"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="primary">mdi-domain</v-icon>
                                </template>
                            </v-text-field>
                        </div>

                        <!-- Authentication Section -->
                        <div class="subsection mb-4">
                            <div class="subsection-header mb-3">
                                <v-icon size="20" color="success" class="mr-2">mdi-shield-lock</v-icon>
                                <span class="subsection-title">Authentication</span>
                            </div>
                            
                            <!-- Client ID -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.clientId" 
                                label="Client ID" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('sharepoint.clientId', $event)"
                                class="modern-input mb-3" 
                                placeholder="Application (client) ID"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="success">mdi-key-variant</v-icon>
                                </template>
                            </v-text-field>

                            <!-- Client Secret -->
                            <v-text-field 
                                v-model="nodeInspector.sharepoint.clientSecret" 
                                label="Client Secret" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                type="password"
                                @update:model-value="updateNodeProperty('sharepoint.clientSecret', $event)"
                                class="modern-input" 
                                autocomplete="off"
                                placeholder="Enter client secret"
                                :readonly="isViewMode">
                                <template v-slot:prepend-inner>
                                    <v-icon size="18" color="success">mdi-shield-key</v-icon>
                                </template>
                            </v-text-field>
                        </div>

                    </div>

                    <!-- Orchestrator Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'orchestrator'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-hub" size="24" color="pink" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-pink mb-0">Orchestrator Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Configure multi-agent orchestration and dispatching</p>
                        </div>

                        <!-- Orchestrator Name -->
                        <v-text-field v-model="nodeInspector.orchestratorName" label="Orchestrator Name"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('orchestratorName', $event)"
                            class="modern-input mb-4" :readonly="isViewMode"
                            prepend-inner-icon="mdi-label" />

                        <!-- Model Configuration Panels -->
                        <v-expansion-panels v-model="orchestratorConfigPanels" multiple class="config-panels elevation-0 mb-4"
                            variant="accordion">
                            
                            <!-- Model Selection Panel -->
                            <v-expansion-panel value="model" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #ec4899, #be185d);">
                                            <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Model Configuration</div>
                                            <div class="panel-subtitle">Provider, model, and parameters</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <!-- Provider and Model -->
                                        <div class="config-section mb-4">
                                            <h6 class="section-label mb-3">Service Configuration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.provider"
                                                        :items="listDDProvider" item-title="providerName"
                                                        item-value="providerName" label="Provider*" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-cloud-outline"
                                                        @update:model-value="updateNodeProperty('provider', $event); changeProvider($event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select AI service provider" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.model" :items="DDModelShow"
                                                        item-title="modelDisplay" item-value="modelName" label="Model*"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-brain"
                                                        @update:model-value="updateNodeProperty('model', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        :disabled="!nodeInspector.provider"
                                                        hint="Select specific model version" persistent-hint required />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.nodeTimeout"
                                                        label="Request Timeout (seconds)" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('nodeTimeout', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="0 = use global timeout setting" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.nodeMaxRetry"
                                                        label="Max Retry" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-refresh"
                                                        @update:model-value="updateNodeProperty('nodeMaxRetry', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="0 = use global max retry setting" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <!-- Core Parameters -->
                                        <div class="config-section mb-4">
                                            <h6 class="section-label mb-3">Core Parameters</h6>
                                            <v-row>
                                                <v-col cols="6">
                                                    <v-text-field v-model.number="nodeInspector.temperature"
                                                        label="Temperature" type="number" step="0.1" min="0" max="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-thermometer"
                                                        @update:model-value="updateNodeProperty('temperature', $event)"
                                                        class="config-input" :readonly="isViewMode" />
                                                </v-col>
                                                <v-col cols="6">
                                                    <v-text-field v-model.number="nodeInspector.maxTokens"
                                                        label="Max Tokens" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-check-outline"
                                                        @update:model-value="updateNodeProperty('maxTokens', $event)"
                                                        class="config-input" :readonly="isViewMode" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.topP" label="Top P"
                                                        type="number" step="0.1" min="0" max="1" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-filter-variant"
                                                        @update:model-value="updateNodeProperty('topP', $event)"
                                                        class="config-input" :readonly="isViewMode" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="6">
                                                    <v-text-field v-model.number="nodeInspector.frequencyPenalty"
                                                        label="Frequency Penalty" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-repeat-off"
                                                        @update:model-value="updateNodeProperty('frequencyPenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" />
                                                </v-col>
                                                <v-col cols="6">
                                                    <v-text-field v-model.number="nodeInspector.presencePenalty"
                                                        label="Presence Penalty" type="number" step="0.1" min="0"
                                                        max="2" variant="outlined" density="comfortable"
                                                        hide-details="auto"
                                                        prepend-inner-icon="mdi-minus-circle-outline"
                                                        @update:model-value="updateNodeProperty('presencePenalty', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.stopSequence"
                                                        label="Stop Sequence" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-stop-circle-outline"
                                                        @update:model-value="updateNodeProperty('stopSequence', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Text sequence to stop generation" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <!-- Behavior Settings -->
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Behavior Settings</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.gender"
                                                        :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                        item-title="name" item-value="id" label="Gender"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-account"
                                                        @update:model-value="updateNodeProperty('gender', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Voice/persona gender preference" persistent-hint
                                                        :model-value="nodeInspector.gender || 'male'" />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="6">
                                                    <v-text-field v-model.number="nodeInspector.typingSpeed"
                                                        label="Typing Speed" type="number" min="0" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-keyboard-outline"
                                                        @update:model-value="updateNodeProperty('typingSpeed', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="Chars/sec (0 = instant)" persistent-hint />
                                                </v-col>
                                                <v-col cols="6">
                                                    <v-text-field v-model.number="nodeInspector.maxInputLength"
                                                        label="Max Input Length" type="number" min="0" max="200000"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-outline"
                                                        @update:model-value="updateNodeProperty('maxInputLength', $event)"
                                                        @keypress="maxNumberpositive($event)"
                                                        @focusin="validateAndCorrectValue($event, 1, 200000)"
                                                        @focusout="validateAndCorrectValue($event, 1, 200000)"
                                                        @keydown.enter="validateAndCorrectValue($event, 1, 200000)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Max input chars" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Response Options Panel -->
                            <v-expansion-panel value="response" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                            <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Response Options</div>
                                            <div class="panel-subtitle">Configure how responses are generated</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <!-- Stream Response -->
                                        <div class="switch-option-compact mb-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.streamResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.streamResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.streamResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-text-box-outline</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">Stream Response</div>
                                                        <div class="switch-description">Progressive text generation</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.streamResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('streamResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>

                                        <!-- Json Response -->
                                        <div class="switch-option-compact mt-3">
                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                :class="{ 'switch-active-bg': nodeInspector.jsonResponse }">
                                                <div class="d-flex align-center">
                                                    <v-avatar size="28"
                                                        :color="nodeInspector.jsonResponse ? 'primary' : 'grey-lighten-1'"
                                                        class="me-3">
                                                        <v-icon
                                                            :color="nodeInspector.jsonResponse ? 'white' : 'grey-darken-1'"
                                                            size="16">mdi-code-json</v-icon>
                                                    </v-avatar>
                                                    <div>
                                                        <div class="switch-title">JSON Response</div>
                                                        <div class="switch-description">Return structured JSON format</div>
                                                    </div>
                                                </div>
                                                <v-switch v-model="nodeInspector.jsonResponse" color="primary"
                                                    hide-details density="compact"
                                                    @update:model-value="updateNodeProperty('jsonResponse', $event)"
                                                    :readonly="isViewMode" inset />
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Memory Settings Panel -->
                            <v-expansion-panel value="memory" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                            <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Memory Settings</div>
                                            <div class="panel-subtitle">Configure conversation memory and context</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Conversation Memory</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.memoryTurn"
                                                        label="Turn Count" type="number" min="0" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-history"
                                                        @update:model-value="updateNodeProperty('memoryTurn', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Number of conversation turns to remember" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.memoryDuration"
                                                        label="Duration (minutes)" type="number" min="0"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('memoryDuration', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="How long to keep memory active" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Memory Types</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.shortTermMemory"
                                                        label="Short-Term Memory" type="number" min="1"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-memory"
                                                        @update:model-value="updateNodeProperty('shortTermMemory', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Recent context window size" persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col>
                                                    <!-- Long-Term Memory Switch -->
                                                    <div class="switch-option-compact mb-4">
                                                        <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                            :class="{ 'switch-active-bg': nodeInspector.longTermMemory }">
                                                            <div class="d-flex align-center">
                                                                <v-avatar size="28"
                                                                    :color="nodeInspector.longTermMemory ? 'success' : 'grey-lighten-1'"
                                                                    class="me-3">
                                                                    <v-icon
                                                                        :color="nodeInspector.longTermMemory ? 'white' : 'grey-darken-1'"
                                                                        size="16">mdi-database</v-icon>
                                                                </v-avatar>
                                                                <div>
                                                                    <div class="switch-title">Long-Term Memory</div>
                                                                    <div class="switch-description">Persistent storage</div>
                                                                </div>
                                                            </div>
                                                            <v-switch v-model="nodeInspector.longTermMemory"
                                                                color="success" hide-details density="compact"
                                                                @update:model-value="updateNodeProperty('longTermMemory', $event)"
                                                                :readonly="isViewMode" inset />
                                                        </div>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- System Prompt Panel -->
                            <v-expansion-panel value="prompt" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #8b5cf6, #6d28d9);">
                                            <v-icon icon="mdi-text-box-edit" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">System Prompt</div>
                                            <div class="panel-subtitle">Instructions for agent routing</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-textarea v-model="nodeInspector.systemPrompt" label="System Prompt"
                                            variant="outlined" density="compact" rows="8" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('systemPrompt', $event)"
                                            class="modern-input" :readonly="isViewMode" />
                                        <div class="text-caption text-grey-darken-1 mt-2">
                                            This prompt guides the orchestrator in extracting user goals and selecting agents.
                                        </div>
                                        <v-textarea v-model="nodeInspector.orchSingleSelectionTemplate" label="Single-Agent Selection Template"
                                            variant="outlined" density="compact" rows="8" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchSingleSelectionTemplate', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Appended after the System Prompt when the orchestrator selects one agent. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                                            <template v-slot:prepend>
                                                <v-icon size="small">mdi-information</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                <strong>Required tokens:</strong> {conversationState}, {agentList}, {userMessage}<br>
                                                If a required token is removed the built-in default is used instead.
                                            </div>
                                        </v-alert>
                                        <v-textarea v-model="nodeInspector.orchPipelineSelectionTemplate" label="Pipeline Selection Template"
                                            variant="outlined" density="compact" rows="8" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchPipelineSelectionTemplate', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Used when the multi-agent strategy is pipeline. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                                            <template v-slot:prepend>
                                                <v-icon size="small">mdi-information</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                <strong>Required tokens:</strong> {conversationState}, {agentList}, {maxAgents}, {userMessage}<br>
                                                If a required token is removed the built-in default is used instead.
                                            </div>
                                        </v-alert>
                                        <v-textarea v-model="nodeInspector.orchVerifySelectionTemplate" label="Verify Selection Template"
                                            variant="outlined" density="compact" rows="8" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchVerifySelectionTemplate', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Used when the multi-agent strategy is verify. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                                            <template v-slot:prepend>
                                                <v-icon size="small">mdi-information</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                <strong>Required tokens:</strong> {conversationState}, {agentList}, {maxAgents}, {userMessage}<br>
                                                If a required token is removed the built-in default is used instead.
                                            </div>
                                        </v-alert>
                                        <v-textarea v-model="nodeInspector.orchAgentListItemTemplate" label="Agent List Item Template"
                                            variant="outlined" density="compact" rows="3" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchAgentListItemTemplate', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Format of a single line inside the rendered {agentList}. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                                            <template v-slot:prepend>
                                                <v-icon size="small">mdi-information</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                <strong>Required tokens:</strong> {agentId}, {agentName}, {description}, {tools}<br>
                                                If a required token is removed the built-in default is used instead.
                                            </div>
                                        </v-alert>
                                        <v-text-field v-model="nodeInspector.orchAgentNoToolsLabel" label="Agent No-Tools Label"
                                            variant="outlined" density="compact" hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchAgentNoToolsLabel', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Rendered in place of {tools} when an agent has no tools. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-textarea v-model="nodeInspector.orchDirectLlmPrompt" label="Direct LLM Answering Prompt"
                                            variant="outlined" density="compact" rows="8" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchDirectLlmPrompt', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Persona used when the orchestrator answers the customer directly instead of dispatching an agent. Leave blank to fall back to the built-in default." persistent-hint />
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Agent Coordination Panel -->
                            <v-expansion-panel value="agentCoordination" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #4d7c0f, #3f6212);">
                                            <v-icon icon="mdi-account-group-outline" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Agent Coordination</div>
                                            <div class="panel-subtitle">Conversation state, companion notes and result aggregation</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-textarea v-model="nodeInspector.orchConversationStateTemplate" label="Conversation State Section"
                                            variant="outlined" density="compact" rows="8" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchConversationStateTemplate', $event)"
                                            class="modern-input" :readonly="isViewMode"
                                            hint="Heading and guidance wrapped around the facts collected this turn. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                                            <template v-slot:prepend>
                                                <v-icon size="small">mdi-information</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                <strong>Required tokens:</strong> {facts}<br>
                                                If a required token is removed the built-in default is used instead.
                                            </div>
                                        </v-alert>
                                        <v-textarea v-model="nodeInspector.orchConversationStateFactTemplate" label="Conversation State Fact Line"
                                            variant="outlined" density="compact" rows="2" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchConversationStateFactTemplate', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Format of one fact line inside {facts}. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                                            <template v-slot:prepend>
                                                <v-icon size="small">mdi-information</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                <strong>Required tokens:</strong> {agentName}, {fact}<br>
                                                If a required token is removed the built-in default is used instead.
                                            </div>
                                        </v-alert>
                                        <v-textarea v-model="nodeInspector.orchCompanionNote" label="Companion Agent Note"
                                            variant="outlined" density="compact" rows="4" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchCompanionNote', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Appended to a companion agent's sub-goal so it does not repeat the stateful agent's question. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-textarea v-model="nodeInspector.orchSequentialContextTemplate" label="Sequential Hand-off Template"
                                            variant="outlined" density="compact" rows="4" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchSequentialContextTemplate', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Sub-goal envelope that carries the previous agent's output into the next agent. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                                            <template v-slot:prepend>
                                                <v-icon size="small">mdi-information</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                <strong>Required tokens:</strong> {subGoal}, {previousSummary}<br>
                                                If a required token is removed the built-in default is used instead.
                                            </div>
                                        </v-alert>
                                        <v-textarea v-model="nodeInspector.orchCheckerGoalTemplate" label="Checker Goal Template"
                                            variant="outlined" density="compact" rows="4" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchCheckerGoalTemplate', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Sub-goal envelope handed to the checker agent in the verify strategy. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                                            <template v-slot:prepend>
                                                <v-icon size="small">mdi-information</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                <strong>Required tokens:</strong> {subGoal}, {executorSummary}<br>
                                                If a required token is removed the built-in default is used instead.
                                            </div>
                                        </v-alert>
                                        <v-text-field v-model="nodeInspector.orchFallbackSubGoal" label="Fallback Sub-Goal"
                                            variant="outlined" density="compact" hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchFallbackSubGoal', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Used when the selection step returns no sub-goal for an agent. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-textarea v-model="nodeInspector.orchAggregationTemplate" label="Result Aggregation Prompt"
                                            variant="outlined" density="compact" rows="8" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchAggregationTemplate', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Prompt that merges every agent result into the final reply. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                                            <template v-slot:prepend>
                                                <v-icon size="small">mdi-information</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                <strong>Required tokens:</strong> {goal}, {userMessage}, {strategyHint}, {agentResults}<br>
                                                If a required token is removed the built-in default is used instead.
                                            </div>
                                        </v-alert>
                                        <v-textarea v-model="nodeInspector.orchStrategyHintParallel" label="Strategy Hint - Parallel"
                                            variant="outlined" density="compact" rows="2" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchStrategyHintParallel', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Rendered into {strategyHint} when agents ran in parallel. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-textarea v-model="nodeInspector.orchStrategyHintPipeline" label="Strategy Hint - Pipeline"
                                            variant="outlined" density="compact" rows="2" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchStrategyHintPipeline', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Rendered into {strategyHint} when agents ran as a pipeline. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-textarea v-model="nodeInspector.orchAggregationFallbackItemTemplate" label="Aggregation Fallback Item Template"
                                            variant="outlined" density="compact" rows="2" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchAggregationFallbackItemTemplate', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Per-agent line used when the aggregation model cannot be reached. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                                            <template v-slot:prepend>
                                                <v-icon size="small">mdi-information</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                <strong>Required tokens:</strong> {agentName}, {response}<br>
                                                If a required token is removed the built-in default is used instead.
                                            </div>
                                        </v-alert>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Guardrail Panel -->
                            <v-expansion-panel value="guardrail" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #f59e0b, #d97706);">
                                            <v-icon icon="mdi-shield-check" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Guardrail</div>
                                            <div class="panel-subtitle">Response validation before output</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-textarea v-model="nodeInspector.guardrailPrompt" label="Guardrail Prompt"
                                            variant="outlined" density="compact" rows="6" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('guardrailPrompt', $event)"
                                            class="modern-input" :readonly="isViewMode"
                                            placeholder="Define guardrail rules to validate agent responses before sending to user..." />
                                        <v-alert type="info" variant="tonal" density="compact" class="mt-3">
                                            <template v-slot:prepend>
                                                <v-icon size="small">mdi-information</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                <strong>Placeholders:</strong> {goal}, {response}, {agentResponse}, {userMessage}<br>
                                                Leave empty to bypass guardrail check.
                                            </div>
                                        </v-alert>

                                        <v-textarea v-model="nodeInspector.orchGuardrailFormatSuffix" label="Guardrail Format Instruction"
                                            variant="outlined" density="compact" rows="4" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchGuardrailFormatSuffix', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Appended to the Guardrail Prompt to tell the model how to shape its verdict. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-textarea v-model="nodeInspector.orchGuardrailUserTemplate" label="Guardrail User Message Template"
                                            variant="outlined" density="compact" rows="6" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchGuardrailUserTemplate', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Envelope of the user message sent to the guardrail model. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                                            <template v-slot:prepend>
                                                <v-icon size="small">mdi-information</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                <strong>Required tokens:</strong> {goal}, {agentResponse}, {userMessage}<br>
                                                If a required token is removed the built-in default is used instead.
                                            </div>
                                        </v-alert>
                                        <!-- Self-guarded agents (e.g. Tax Planning Agent) -->
                                        <v-switch v-model="nodeInspector.allowAgentGuardrailBypass"
                                            color="primary" density="compact" hide-details="auto" class="mt-3"
                                            :readonly="isViewMode"
                                            label="ให้ Agent ที่มี guardrail ในตัวข้าม Guardrail นี้ได้"
                                            @update:model-value="updateNodeProperty('allowAgentGuardrailBypass', $event)" />
                                        <v-alert
                                            :type="nodeInspector.allowAgentGuardrailBypass === false ? 'warning' : 'info'"
                                            variant="tonal" density="compact" class="mt-2">
                                            <div class="text-caption">
                                                <template v-if="nodeInspector.allowAgentGuardrailBypass === false">
                                                    <strong>ปิดอยู่</strong> — Guardrail จะทำงานกับคำตอบของทุก Agent รวมถึงตัวที่ตรวจ output
                                                    ของตัวเองแล้ว คำตอบที่มีการ์ด/ตัวเลข (เช่น Tax Planning Agent)
                                                    <strong>จะถูก LLM เขียนใหม่</strong> ทำให้การ์ดแสดงผลไม่ได้และตัวเลขอาจเพี้ยน —
                                                    เปิดไว้เพื่อ debug เท่านั้น
                                                </template>
                                                <template v-else>
                                                    เปิดอยู่ (ค่าเริ่มต้น) — เมื่อ Agent ที่ตรวจ output ของตัวเองแล้วทำงาน
                                                    <strong>ตัวเดียว</strong>ในเทิร์นนั้น จะข้าม Guardrail นี้
                                                    (ยังบันทึกเป็น step <code>Bypassed</code> ให้เห็นในล็อกเสมอ)
                                                    หลาย Agent ในเทิร์นเดียว Guardrail ยังทำงานปกติ
                                                </template>
                                            </div>
                                        </v-alert>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Customer Messages Panel -->
                            <v-expansion-panel value="messages" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #e11d48, #9f1239);">
                                            <v-icon icon="mdi-message-alert-outline" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Customer Messages</div>
                                            <div class="panel-subtitle">Text sent directly to the customer</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <v-alert type="warning" variant="tonal" density="compact" class="mb-4">
                                            <template v-slot:prepend>
                                                <v-icon size="small">mdi-alert</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                <strong>The customer sees these texts verbatim.</strong> They are not prompts - nothing rewrites them.
                                                The built-in defaults are written in Thai, so keep any override in the language your customers use.
                                            </div>
                                        </v-alert>
                                        <v-textarea v-model="nodeInspector.orchBlockedMessage" label="Blocked By Guardrail Message"
                                            variant="outlined" density="compact" rows="2" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchBlockedMessage', $event)"
                                            class="modern-input" :readonly="isViewMode"
                                            hint="Sent when the guardrail rejects the agent reply. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-textarea v-model="nodeInspector.orchDirectLlmEmptyMessage" label="Direct Answer Empty Message"
                                            variant="outlined" density="compact" rows="2" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchDirectLlmEmptyMessage', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Sent when the direct answering model returns an empty reply. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-textarea v-model="nodeInspector.orchDirectLlmErrorMessage" label="Direct Answer Error Message"
                                            variant="outlined" density="compact" rows="2" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchDirectLlmErrorMessage', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Sent when the direct answering model call fails. Leave blank to fall back to the built-in default." persistent-hint />
                                        <v-textarea v-model="nodeInspector.orchNoAgentSucceededMessage" label="No Agent Succeeded Message"
                                            variant="outlined" density="compact" rows="2" auto-grow hide-details="auto"
                                            @update:model-value="updateNodeProperty('orchNoAgentSucceededMessage', $event)"
                                            class="modern-input mt-4" :readonly="isViewMode"
                                            hint="Sent when every dispatched agent failed to produce a reply. Leave blank to fall back to the built-in default." persistent-hint />
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Cache Settings Panel -->
                            <v-expansion-panel value="cache" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                            <v-icon icon="mdi-cached" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Cache Settings</div>
                                            <div class="panel-subtitle">Configure response caching for performance</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Response Cache</h6>
                                            <div class="switch-option-compact mb-4">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableCache }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableCache ? 'warning' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableCache ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-lightning-bolt</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Enable Cache</div>
                                                            <div class="switch-description">Cache responses to reduce LLM calls</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableCache"
                                                        color="warning" hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableCache', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>
                                            <v-row v-if="nodeInspector.enableCache">
                                                <v-col cols="12">
                                                    <v-text-field v-model.number="nodeInspector.cacheTTLMinutes"
                                                        label="Cache TTL (minutes)" type="number" min="1" max="10080"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-outline"
                                                        @update:model-value="updateNodeProperty('cacheTTLMinutes', $event)"
                                                        @keypress="isNumberpositive($event)" class="config-input"
                                                        :readonly="isViewMode"
                                                        hint="How long to keep cached responses (1-10080 min)"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row v-if="nodeInspector.enableCache">
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.cacheScope"
                                                        :items="cacheScopeOptions"
                                                        item-title="label" item-value="value"
                                                        label="Cache Scope"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-account-group-outline"
                                                        @update:model-value="updateNodeProperty('cacheScope', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Who can use cached responses" persistent-hint />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <div class="config-section mb-3" v-if="nodeInspector.enableCache">
                                            <h6 class="section-label mb-3">Semantic Cache</h6>
                                            <div class="switch-option-compact mb-4">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableSemanticCache }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableSemanticCache ? 'deep-purple' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableSemanticCache ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-brain</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Semantic Match</div>
                                                            <div class="switch-description">Match similar questions using AI</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableSemanticCache"
                                                        color="deep-purple" hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableSemanticCache', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>
                                            <v-row v-if="nodeInspector.enableSemanticCache">
                                                <v-col cols="12">
                                                    <div class="slider-label mb-2">
                                                        <span>Similarity Threshold</span>
                                                        <span class="text-primary font-weight-bold">{{ (nodeInspector.similarityThreshold * 100).toFixed(0) }}%</span>
                                                    </div>
                                                    <v-slider v-model="nodeInspector.similarityThreshold"
                                                        min="0.50" max="1.00" step="0.01"
                                                        color="deep-purple" track-color="grey-lighten-2"
                                                        thumb-label hide-details
                                                        @update:model-value="updateNodeProperty('similarityThreshold', $event)"
                                                        :readonly="isViewMode">
                                                        <template v-slot:thumb-label="{ modelValue }">
                                                            {{ (modelValue * 100).toFixed(0) }}%
                                                        </template>
                                                    </v-slider>
                                                    <div class="text-caption text-grey mt-1">
                                                        Higher = more strict (exact match), Lower = more flexible
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <v-alert v-if="nodeInspector.enableCache" type="info" variant="tonal" density="compact" class="mt-3">
                                            <template v-slot:prepend>
                                                <v-icon>mdi-information-outline</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                Cache will skip when streaming is enabled or when data integrations are used.
                                            </div>
                                        </v-alert>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- LINE Configuration Panel -->
                            <v-expansion-panel value="line" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #4CAF50, #388E3C);">
                                            <v-icon icon="mdi-message" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">LINE Configuration</div>
                                            <div class="panel-subtitle">LINE messaging platform settings</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Display Options</h6>
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.showLineName }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.showLineName ? 'info' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.showLineName ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-account-circle</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Show Name</div>
                                                            <div class="switch-description">Display user name</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.showLineName" color="info"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('showLineName', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.loadingBar }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.loadingBar ? 'success' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.loadingBar ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-progress-clock</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Loading Indicator</div>
                                                            <div class="switch-description">Show typing animation</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.loadingBar" color="success"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('loadingBar', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>
                                        </div>
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Loading & Duration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select v-model="nodeInspector.loadingBarDuration"
                                                        :items="[10, 20, 30, 40, 50, 60]"
                                                        label="Loading Duration (seconds)" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-timer-sand"
                                                        @update:model-value="updateNodeProperty('loadingBarDuration', $event)"
                                                        class="config-input" :disabled="!nodeInspector.loadingBar"
                                                        :readonly="isViewMode"
                                                        hint="Duration for loading animation display" persistent-hint
                                                        clearable />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <div class="config-section mb-3">
                                            <h6 class="section-label mb-3">Monitoring</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-text-field v-model="nodeInspector.monitorLineUserId"
                                                        label="Monitor Line User ID" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-monitor-eye"
                                                        @update:model-value="updateNodeProperty('monitorLineUserId', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="LINE user ID for monitoring notifications"
                                                        persistent-hint />
                                                </v-col>
                                            </v-row>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-autocomplete v-model="nodeInspector.monitorSocialID"
                                                        :items="listMonitorLineName" label="Monitor Line Name"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-chat" item-title="name" item-value="id"
                                                        @update:model-value="updateNodeProperty('monitorSocialID', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select monitor line name" persistent-hint clearable />
                                                </v-col>
                                            </v-row>
                                        </div>
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Content Filtering</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-toolbar-title style="font-size: 16px">
                                                        Forbidden word
                                                    </v-toolbar-title>
                                                </v-col>
                                            </v-row>
                                            <div class="pr-1" style="border: #dfe0e2 1px solid; background: #fff; margin-top: 10px; border-radius: 8px;">
                                                <v-row v-for="(item, index) in nodeInspector.forbiddenWords || ['']"
                                                    :key="index" style="margin: 0px">
                                                    <v-col cols="12" style="padding: 0px">
                                                        <div style="display: flex; align-items: center">
                                                            <div style="height: 50px; background: #f8f8f8; padding: 14px 20px 0 20px;">
                                                                {{ index + 1 }}
                                                            </div>
                                                            <v-textarea variant="solo" style="overflow: hidden; overflow-wrap: break-word; resize: none; height: 50px; width: 98%; border-radius: 0px; font-family: Fira code, Fira Mono, Consolas, Menlo, Courier, monospace; font-size: 14px;"
                                                                :model-value="(nodeInspector.forbiddenWords && nodeInspector.forbiddenWords[index]) || ''"
                                                                placeholder="Enter word or phrase"
                                                                @keypress="isEnterForbidden($event, index)"
                                                                @keyup.delete="checkDeleteForbiddenWord(index, (nodeInspector.forbiddenWords && nodeInspector.forbiddenWords[index]) || '')"
                                                                @update:model-value="updateForbiddenWordProperty(index, $event)"
                                                                :readonly="isViewMode" />
                                                            <v-btn icon="mdi-attachment" variant="text"
                                                                v-show="(nodeInspector.forbiddenWords || []).length > 1 && index + 1 < (nodeInspector.forbiddenWords || []).length"
                                                                @click="removeForbiddenWordDirect(index)"
                                                                :disabled="isViewMode">
                                                                <v-tooltip location="top" :open-delay="500">
                                                                    <template v-slot:activator="{ props }">
                                                                        <v-icon color="error" style="cursor: pointer" v-bind="props">
                                                                            mdi-delete-forever
                                                                        </v-icon>
                                                                    </template>
                                                                    <span>delete</span>
                                                                </v-tooltip>
                                                            </v-btn>
                                                        </div>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                            <!-- Prompt Settings Panel -->
                            <v-expansion-panel value="promptSettings" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4"
                                            style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                            <v-icon icon="mdi-cog-outline" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Prompt Settings</div>
                                            <div class="panel-subtitle">Time context, TransactionID & replacement flags</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <!-- Time Context Section -->
                                        <div class="config-section mb-4">
                                            <h6 class="section-label mb-3">Time Context</h6>
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableTime }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableTime ? 'success' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableTime ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-clock-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Time Context</div>
                                                            <div class="switch-description">Enable time awareness for AI responses</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableTime" color="success"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableTime', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>
                                            <div v-if="nodeInspector.enableTime" class="mt-3">
                                                <v-select v-model="nodeInspector.timeZone" :items="DDTimeZone"
                                                    item-title="name" item-value="value" label="Time Zone"
                                                    variant="outlined" density="comfortable" hide-details="auto"
                                                    prepend-inner-icon="mdi-earth"
                                                    @update:model-value="updateNodeProperty('timeZone', $event)"
                                                    class="config-input" :readonly="isViewMode"
                                                    hint="Select your timezone for time-aware responses" persistent-hint
                                                    placeholder="Select your timezone">
                                                    <template v-slot:selection="{ item }">
                                                        <div class="d-flex align-center">
                                                            <v-icon small color="primary" class="me-2">mdi-map-marker</v-icon>
                                                            <span class="font-weight-medium">{{ item.raw.name }}</span>
                                                            <v-chip size="x-small" color="grey-darken-1" text-color="white" class="ml-2">
                                                                {{ item.raw.value.split('/')[1].replace('_', ' ') }}
                                                            </v-chip>
                                                        </div>
                                                    </template>
                                                </v-select>
                                            </div>
                                        </div>
                                        <!-- TransactionID Context Section -->
                                        <div class="config-section mb-4">
                                            <h6 class="section-label mb-3">TransactionID Context</h6>
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableTransactionId }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableTransactionId ? 'info' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableTransactionId ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-identifier</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Add TransactionID to Prompt</div>
                                                            <div class="switch-description">Include transaction identifier in AI context</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableTransactionId" color="info"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableTransactionId', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                            </div>
                                        </div>
                                        <!-- Prompt Replacement Flags Section -->
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Prompt Replacement Flags</h6>
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableInputMessageReplace }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableInputMessageReplace ? 'purple' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableInputMessageReplace ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-message-text-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">InputMessage replacing</div>
                                                            <div class="switch-description">Replace tag with input value in prompt</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableInputMessageReplace" color="purple"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableInputMessageReplace', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                                <div v-if="nodeInspector.enableInputMessageReplace" class="code-block-wrapper mt-2 mx-3">
                                                    <div class="code-block d-flex align-center justify-space-between">
                                                        <code class="code-text">{{InputMessageTagText}}</code>
                                                        <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                            @click="copyToClipboard(InputMessageTagText)" class="copy-btn">
                                                            <v-icon size="14">mdi-content-copy</v-icon>
                                                            <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                        </v-btn>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableVoicePromptReplace }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableVoicePromptReplace ? 'deep-orange' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableVoicePromptReplace ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-microphone-outline</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">VoicePrompt replacing</div>
                                                            <div class="switch-description">Replace tag with TTS prompt in prompt</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableVoicePromptReplace" color="deep-orange"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableVoicePromptReplace', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                                <div v-if="nodeInspector.enableVoicePromptReplace" class="code-block-wrapper mt-2 mx-3">
                                                    <div class="code-block d-flex align-center justify-space-between">
                                                        <code class="code-text">{{VoicePromptTagText}}</code>
                                                        <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                            @click="copyToClipboard(VoicePromptTagText)" class="copy-btn">
                                                            <v-icon size="14">mdi-content-copy</v-icon>
                                                            <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                        </v-btn>
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="switch-option-compact mb-3">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                    :class="{ 'switch-active-bg': nodeInspector.enableLanguageReplace }">
                                                    <div class="d-flex align-center">
                                                        <v-avatar size="28"
                                                            :color="nodeInspector.enableLanguageReplace ? 'teal' : 'grey-lighten-1'"
                                                            class="me-3">
                                                            <v-icon
                                                                :color="nodeInspector.enableLanguageReplace ? 'white' : 'grey-darken-1'"
                                                                size="16">mdi-translate</v-icon>
                                                        </v-avatar>
                                                        <div>
                                                            <div class="switch-title">Language replacing</div>
                                                            <div class="switch-description">Replace tag with detected language in prompt</div>
                                                        </div>
                                                    </div>
                                                    <v-switch v-model="nodeInspector.enableLanguageReplace" color="teal"
                                                        hide-details density="compact"
                                                        @update:model-value="updateNodeProperty('enableLanguageReplace', $event)"
                                                        :readonly="isViewMode" inset />
                                                </div>
                                                <div v-if="nodeInspector.enableLanguageReplace" class="code-block-wrapper mt-2 mx-3">
                                                    <div class="code-block d-flex align-center justify-space-between">
                                                        <code class="code-text">{{LanguageTagText}}</code>
                                                        <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                            @click="copyToClipboard(LanguageTagText)" class="copy-btn">
                                                            <v-icon size="14">mdi-content-copy</v-icon>
                                                            <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                        </v-btn>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>

                        </v-expansion-panels>
                        <div class="switch-option-compact mb-4">
                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                :class="{ 'switch-active-bg': nodeInspector.enableMultiAgent }">
                                <div class="d-flex align-center">
                                    <v-avatar size="28"
                                        :color="nodeInspector.enableMultiAgent ? 'pink' : 'grey-lighten-1'"
                                        class="me-3">
                                        <v-icon
                                            :color="nodeInspector.enableMultiAgent ? 'white' : 'grey-darken-1'"
                                            size="16">mdi-account-group</v-icon>
                                    </v-avatar>
                                    <div>
                                        <div class="switch-title">Enable Multi-Agent</div>
                                        <div class="switch-description">Allow multiple agents to collaborate</div>
                                    </div>
                                </div>
                                <v-switch v-model="nodeInspector.enableMultiAgent"
                                    color="pink" hide-details density="compact"
                                    @update:model-value="updateNodeProperty('enableMultiAgent', $event)"
                                    :readonly="isViewMode" inset />
                            </div>
                            <div class="text-caption text-grey-darken-1 mt-1 px-3">
                                When enabled, orchestrator can dispatch multiple agents. When disabled, uses single agent mode.
                            </div>
                        </div>

                        <!-- Multi-Agent Strategy Dropdown -->
                        <div v-if="nodeInspector.enableMultiAgent" class="mb-4">
                            <div class="d-flex align-center mb-1">
                                <v-select v-model="nodeInspector.multiAgentStrategy"
                                    :items="[
                                        { title: 'Pipeline (Sequential)', value: 'pipeline' },
                                        { title: 'Verify (Parallel + Cross-check)', value: 'verify' }
                                    ]"
                                    label="Multi-Agent Strategy"
                                    variant="outlined" density="compact" hide-details="auto"
                                    @update:model-value="updateNodeProperty('multiAgentStrategy', $event)"
                                    :readonly="isViewMode"
                                    class="modern-input flex-grow-1">
                                    <template v-slot:item="{ props, item }">
                                        <v-list-item v-bind="props" />
                                    </template>
                                </v-select>
                                <v-menu :open-on-hover="true" :close-on-content-click="false" location="start" :offset="8" max-width="640">
                                    <template v-slot:activator="{ props }">
                                        <v-btn v-bind="props" icon variant="text" size="x-small" class="ml-1 flex-shrink-0" color="grey-darken-1">
                                            <v-icon size="18">mdi-information-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <v-card class="strategy-info-card" elevation="8" rounded="lg" width="600">
                                        <v-card-title class="text-subtitle-2 font-weight-bold pb-1 pt-3 px-4">
                                            <v-icon size="18" color="primary" class="me-2">mdi-strategy</v-icon>
                                            Multi-Agent Strategy
                                        </v-card-title>
                                        <v-divider class="mx-4" />
                                        <v-card-text class="px-4 pt-3 pb-4">
                                            <!-- Pipeline Diagram -->
                                            <div class="strategy-section mb-4">
                                                <div class="d-flex align-center mb-2">
                                                    <v-chip size="x-small" color="blue" variant="flat" class="me-2 font-weight-bold">Pipeline</v-chip>
                                                    <span class="text-caption font-weight-medium">Sequential Processing</span>
                                                </div>
                                                <div class="strategy-diagram-wrapper">
                                                    <svg viewBox="0 0 420 70" xmlns="http://www.w3.org/2000/svg" class="strategy-diagram">
                                                        <defs>
                                                            <linearGradient id="agentGradA" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#14b8a6"/><stop offset="100%" stop-color="#0d9488"/></linearGradient>
                                                            <linearGradient id="agentGradB" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#f59e0b"/><stop offset="100%" stop-color="#d97706"/></linearGradient>
                                                            <linearGradient id="agentGradC" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#8b5cf6"/><stop offset="100%" stop-color="#7c3aed"/></linearGradient>
                                                            <marker id="pArrow1" markerWidth="7" markerHeight="5" refX="7" refY="2.5" orient="auto"><path d="M0,0 L7,2.5 L0,5" fill="#94a3b8"/></marker>
                                                            <marker id="pArrow2" markerWidth="7" markerHeight="5" refX="7" refY="2.5" orient="auto"><path d="M0,0 L7,2.5 L0,5" fill="#14b8a6"/></marker>
                                                            <marker id="pArrow3" markerWidth="7" markerHeight="5" refX="7" refY="2.5" orient="auto"><path d="M0,0 L7,2.5 L0,5" fill="#f59e0b"/></marker>
                                                            <marker id="pArrow4" markerWidth="7" markerHeight="5" refX="7" refY="2.5" orient="auto"><path d="M0,0 L7,2.5 L0,5" fill="#8b5cf6"/></marker>
                                                        </defs>
                                                        <!-- User -->
                                                        <circle cx="28" cy="35" r="16" fill="#f1f5f9" stroke="#cbd5e1" stroke-width="1.2"/>
                                                        <circle cx="28" cy="31" r="4.5" fill="#64748b"/>
                                                        <path d="M20,43 Q20,37 28,37 Q36,37 36,43" fill="#64748b"/>
                                                        <text x="28" y="60" text-anchor="middle" font-size="7" fill="#64748b" font-weight="500">User</text>
                                                        <!-- Arrow 1 -->
                                                        <line x1="46" y1="35" x2="78" y2="35" stroke="#94a3b8" stroke-width="1.5" marker-end="url(#pArrow1)"/>
                                                        <!-- Agent A -->
                                                        <rect x="82" y="18" width="72" height="34" rx="8" fill="#f0fdfa" stroke="#14b8a6" stroke-width="1.3"/>
                                                        <rect x="89" y="24" width="18" height="18" rx="5" fill="url(#agentGradA)"/>
                                                        <path d="M95,30 a3,3 0 1,1 6,0 a3,3 0 1,1 -6,0 M94,37 Q94,34 98,34 Q102,34 102,37" fill="white" opacity="0.9"/>
                                                        <text x="126" y="38" text-anchor="middle" font-size="8" fill="#0f766e" font-weight="600">Agent A</text>
                                                        <!-- Arrow 2 -->
                                                        <line x1="156" y1="35" x2="176" y2="35" stroke="#14b8a6" stroke-width="1.5" marker-end="url(#pArrow2)"/>
                                                        <text x="166" y="28" text-anchor="middle" font-size="5.5" fill="#14b8a6" font-style="italic">result</text>
                                                        <!-- Agent B -->
                                                        <rect x="180" y="18" width="72" height="34" rx="8" fill="#fffbeb" stroke="#f59e0b" stroke-width="1.3"/>
                                                        <rect x="187" y="24" width="18" height="18" rx="5" fill="url(#agentGradB)"/>
                                                        <path d="M193,30 a3,3 0 1,1 6,0 a3,3 0 1,1 -6,0 M192,37 Q192,34 196,34 Q200,34 200,37" fill="white" opacity="0.9"/>
                                                        <text x="224" y="38" text-anchor="middle" font-size="8" fill="#92400e" font-weight="600">Agent B</text>
                                                        <!-- Arrow 3 -->
                                                        <line x1="254" y1="35" x2="274" y2="35" stroke="#f59e0b" stroke-width="1.5" marker-end="url(#pArrow3)"/>
                                                        <text x="264" y="28" text-anchor="middle" font-size="5.5" fill="#f59e0b" font-style="italic">result</text>
                                                        <!-- Agent C -->
                                                        <rect x="278" y="18" width="72" height="34" rx="8" fill="#f5f3ff" stroke="#8b5cf6" stroke-width="1.3"/>
                                                        <rect x="285" y="24" width="18" height="18" rx="5" fill="url(#agentGradC)"/>
                                                        <path d="M291,30 a3,3 0 1,1 6,0 a3,3 0 1,1 -6,0 M290,37 Q290,34 294,34 Q298,34 298,37" fill="white" opacity="0.9"/>
                                                        <text x="322" y="38" text-anchor="middle" font-size="8" fill="#5b21b6" font-weight="600">Agent C</text>
                                                        <!-- Arrow 4 -->
                                                        <line x1="352" y1="35" x2="374" y2="35" stroke="#8b5cf6" stroke-width="1.5" marker-end="url(#pArrow4)"/>
                                                        <!-- Result -->
                                                        <rect x="378" y="20" width="34" height="30" rx="6" fill="#f1f5f9" stroke="#94a3b8" stroke-width="1.2"/>
                                                        <rect x="384" y="26" width="22" height="3" rx="1" fill="#cbd5e1"/>
                                                        <rect x="384" y="31" width="16" height="3" rx="1" fill="#cbd5e1"/>
                                                        <rect x="384" y="36" width="19" height="3" rx="1" fill="#cbd5e1"/>
                                                        <text x="395" y="60" text-anchor="middle" font-size="6.5" fill="#64748b" font-weight="500">Result</text>
                                                    </svg>
                                                </div>
                                                <div class="text-caption text-grey-darken-1 mt-1">
                                                    Agent ทำงานทีละตัวตามลำดับ — ผลลัพธ์ของ Agent ก่อนหน้าจะส่งต่อเป็น context ให้ Agent ถัดไป<br/>
                                                    <b>เหมาะกับ:</b> งานที่ต้องทำเป็นขั้นตอน เช่น รวบรวมข้อมูล → วิเคราะห์ → สรุป
                                                </div>
                                            </div>
                                            <!-- Verify Diagram -->
                                            <div class="strategy-section">
                                                <div class="d-flex align-center mb-2">
                                                    <v-chip size="x-small" color="deep-purple" variant="flat" class="me-2 font-weight-bold">Verify</v-chip>
                                                    <span class="text-caption font-weight-medium">Parallel + Cross-check</span>
                                                </div>
                                                <div class="strategy-diagram-wrapper">
                                                    <svg viewBox="0 0 420 120" xmlns="http://www.w3.org/2000/svg" class="strategy-diagram">
                                                        <defs>
                                                            <linearGradient id="vAgentGradA" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#14b8a6"/><stop offset="100%" stop-color="#0d9488"/></linearGradient>
                                                            <linearGradient id="vAgentGradB" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#f59e0b"/><stop offset="100%" stop-color="#d97706"/></linearGradient>
                                                            <linearGradient id="vAgentGradC" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#8b5cf6"/><stop offset="100%" stop-color="#7c3aed"/></linearGradient>
                                                            <linearGradient id="vCheckerGrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stop-color="#ec4899"/><stop offset="100%" stop-color="#db2777"/></linearGradient>
                                                            <marker id="vArrow1" markerWidth="7" markerHeight="5" refX="7" refY="2.5" orient="auto"><path d="M0,0 L7,2.5 L0,5" fill="#94a3b8"/></marker>
                                                            <marker id="vArrow2" markerWidth="7" markerHeight="5" refX="7" refY="2.5" orient="auto"><path d="M0,0 L7,2.5 L0,5" fill="#cbd5e1"/></marker>
                                                            <marker id="vArrow3" markerWidth="7" markerHeight="5" refX="7" refY="2.5" orient="auto"><path d="M0,0 L7,2.5 L0,5" fill="#06b6d4"/></marker>
                                                            <marker id="vArrow4" markerWidth="7" markerHeight="5" refX="7" refY="2.5" orient="auto"><path d="M0,0 L7,2.5 L0,5" fill="#ec4899"/></marker>
                                                        </defs>
                                                        <!-- User -->
                                                        <circle cx="28" cy="60" r="16" fill="#f1f5f9" stroke="#cbd5e1" stroke-width="1.2"/>
                                                        <circle cx="28" cy="56" r="4.5" fill="#64748b"/>
                                                        <path d="M20,68 Q20,62 28,62 Q36,62 36,68" fill="#64748b"/>
                                                        <!-- Fan-out arrows -->
                                                        <line x1="46" y1="48" x2="88" y2="25" stroke="#94a3b8" stroke-width="1.2" marker-end="url(#vArrow1)"/>
                                                        <line x1="46" y1="60" x2="88" y2="60" stroke="#94a3b8" stroke-width="1.2" marker-end="url(#vArrow1)"/>
                                                        <line x1="46" y1="72" x2="88" y2="95" stroke="#94a3b8" stroke-width="1.2" marker-end="url(#vArrow1)"/>
                                                        <!-- Agent A (top) -->
                                                        <rect x="92" y="8" width="72" height="34" rx="8" fill="#f0fdfa" stroke="#14b8a6" stroke-width="1.3"/>
                                                        <rect x="99" y="14" width="16" height="16" rx="4.5" fill="url(#vAgentGradA)"/>
                                                        <path d="M104.5,20 a2.5,2.5 0 1,1 5,0 a2.5,2.5 0 1,1 -5,0 M103.5,27 Q103.5,24 107,24 Q110.5,24 110.5,27" fill="white" opacity="0.9"/>
                                                        <text x="134" y="28" text-anchor="middle" font-size="8" fill="#0f766e" font-weight="600">Agent A</text>
                                                        <!-- Agent B (mid) -->
                                                        <rect x="92" y="43" width="72" height="34" rx="8" fill="#fffbeb" stroke="#f59e0b" stroke-width="1.3"/>
                                                        <rect x="99" y="49" width="16" height="16" rx="4.5" fill="url(#vAgentGradB)"/>
                                                        <path d="M104.5,55 a2.5,2.5 0 1,1 5,0 a2.5,2.5 0 1,1 -5,0 M103.5,62 Q103.5,59 107,59 Q110.5,59 110.5,62" fill="white" opacity="0.9"/>
                                                        <text x="134" y="63" text-anchor="middle" font-size="8" fill="#92400e" font-weight="600">Agent B</text>
                                                        <!-- Agent C (bottom) -->
                                                        <rect x="92" y="78" width="72" height="34" rx="8" fill="#f5f3ff" stroke="#8b5cf6" stroke-width="1.3"/>
                                                        <rect x="99" y="84" width="16" height="16" rx="4.5" fill="url(#vAgentGradC)"/>
                                                        <path d="M104.5,90 a2.5,2.5 0 1,1 5,0 a2.5,2.5 0 1,1 -5,0 M103.5,97 Q103.5,94 107,94 Q110.5,94 110.5,97" fill="white" opacity="0.9"/>
                                                        <text x="134" y="98" text-anchor="middle" font-size="8" fill="#5b21b6" font-weight="600">Agent C</text>
                                                        <!-- Parallel label -->
                                                        <text x="80" y="118" font-size="6.5" fill="#94a3b8" font-style="italic">parallel</text>
                                                        <!-- Fan-in arrows -->
                                                        <line x1="166" y1="25" x2="218" y2="50" stroke="#cbd5e1" stroke-width="1.2" marker-end="url(#vArrow2)"/>
                                                        <line x1="166" y1="60" x2="218" y2="57" stroke="#cbd5e1" stroke-width="1.2" marker-end="url(#vArrow2)"/>
                                                        <line x1="166" y1="95" x2="218" y2="65" stroke="#cbd5e1" stroke-width="1.2" marker-end="url(#vArrow2)"/>
                                                        <!-- Aggregate box -->
                                                        <rect x="222" y="38" width="64" height="40" rx="8" fill="#f0f9ff" stroke="#06b6d4" stroke-width="1.2" stroke-dasharray="4,2"/>
                                                        <path d="M248,50 l6,4 l-6,4 M254,50 l6,4 l-6,4" stroke="#06b6d4" stroke-width="1.3" fill="none" stroke-linecap="round"/>
                                                        <text x="254" y="70" text-anchor="middle" font-size="7" fill="#0e7490" font-weight="600">Aggregate</text>
                                                        <!-- Arrow to Checker -->
                                                        <line x1="288" y1="58" x2="314" y2="58" stroke="#06b6d4" stroke-width="1.5" marker-end="url(#vArrow3)"/>
                                                        <!-- Checker Agent -->
                                                        <rect x="318" y="38" width="64" height="40" rx="8" fill="#fdf2f8" stroke="#ec4899" stroke-width="1.3"/>
                                                        <rect x="325" y="44" width="16" height="16" rx="4.5" fill="url(#vCheckerGrad)"/>
                                                        <path d="M329,52 l3,3 l6,-6" stroke="white" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
                                                        <text x="356" y="55" text-anchor="middle" font-size="7.5" fill="#9d174d" font-weight="600">Checker</text>
                                                        <text x="350" y="71" text-anchor="middle" font-size="6" fill="#ec4899" font-style="italic">(optional)</text>
                                                        <!-- Arrow to Result -->
                                                        <line x1="384" y1="58" x2="396" y2="58" stroke="#ec4899" stroke-width="1.5" marker-end="url(#vArrow4)"/>
                                                        <!-- Result -->
                                                        <rect x="398" y="44" width="18" height="28" rx="5" fill="#f1f5f9" stroke="#94a3b8" stroke-width="1.2"/>
                                                        <rect x="401" y="49" width="12" height="2.5" rx="1" fill="#cbd5e1"/>
                                                        <rect x="401" y="53" width="9" height="2.5" rx="1" fill="#cbd5e1"/>
                                                        <rect x="401" y="57" width="11" height="2.5" rx="1" fill="#cbd5e1"/>
                                                    </svg>
                                                </div>
                                                <div class="text-caption text-grey-darken-1 mt-1">
                                                    Agent ทุกตัวทำงานพร้อมกัน (parallel) แล้วนำผลมารวม — สามารถกำหนด Checker ตรวจสอบผลได้<br/>
                                                    <b>เหมาะกับ:</b> งานที่ต้องมองหลายมุม เช่น ตรวจสอบ fraud, cross-check ข้อมูล
                                                </div>
                                            </div>
                                        </v-card-text>
                                    </v-card>
                                </v-menu>
                            </div>
                        </div>

                        <v-alert type="info" variant="tonal" density="compact" class="mt-2">
                            <template v-slot:prepend>
                                <v-icon size="small">mdi-information</v-icon>
                            </template>
                            <div class="text-caption">
                                Orchestrator analyzes user input and routes to appropriate agents.
                                Connect agents to the bottom handle for dispatching.
                            </div>
                        </v-alert>
                    </div>

                    <!-- Agent Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'agent'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-robot" size="24" color="teal" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-teal mb-0">Agent Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Configure autonomous agent with tools</p>
                        </div>

                        <!-- Agent Name -->
                        <v-text-field v-model="nodeInspector.agentName" label="Agent Name"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('agentName', $event)"
                            class="modern-input mb-4" :readonly="isViewMode"
                            prepend-inner-icon="mdi-label" />

                        <!-- Agent Description -->
                        <v-textarea v-model="nodeInspector.agentDescription" label="Agent Description"
                            variant="outlined" density="compact" rows="2" auto-grow hide-details="auto"
                            @update:model-value="updateNodeProperty('agentDescription', $event)"
                            class="modern-input mb-4" :readonly="isViewMode"
                            placeholder="Brief description of what this agent does and its capabilities..."
                            hint="This description helps the Orchestrator select the right agent" persistent-hint />

                        <!-- Persona Prompt -->
                        <v-textarea v-model="nodeInspector.personaPrompt" label="Persona Prompt"
                            variant="outlined" density="compact" rows="4" auto-grow hide-details="auto"
                            @update:model-value="updateNodeProperty('personaPrompt', $event)"
                            class="modern-input mb-4" :readonly="isViewMode"
                            placeholder="Define the agent's personality, expertise, and behavior guidelines..." />

                        <!-- Final Summary Template — prompt สรุปคำตอบสุดท้ายหลังรัน Tools/Knowledge ครบ (เว้นว่าง = ใช้ built-in) -->
                        <v-textarea v-model="nodeInspector.agentFinalSummaryTemplate" label="Final Summary Template"
                            variant="outlined" density="compact" auto-grow hide-details="auto"
                            :rows="agentPromptRows.finalSummaryTemplate"
                            :placeholder="agentPromptDefaults.finalSummaryTemplate"
                            @update:model-value="updateNodeProperty('agentFinalSummaryTemplate', $event)"
                            class="modern-input" :readonly="isViewMode"
                            hint="Prompt สรุปคำตอบสุดท้ายหลังรัน Tools/Knowledge ครบ — ต่อท้าย Persona Prompt · เว้นว่าง = ใช้ค่าเริ่มต้นของระบบ"
                            persistent-hint data-testid="agent-finalsummarytemplate" />
                        <v-alert type="info" variant="tonal" density="compact" class="mt-2 mb-4">
                            <template v-slot:prepend>
                                <v-icon size="small">mdi-information</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>Tokens:</strong> {goal}, {userMessage}, {toolCount}, {toolResultsText}<br>
                                <strong>Required:</strong> {userMessage}, {toolResultsText} — ถ้าขาดตัวใดตัวหนึ่ง ระบบจะทิ้ง template นี้แล้วใช้ค่าเริ่มต้นแทน
                                <div v-if="!isViewMode && !nodeInspector.agentFinalSummaryTemplate" class="mt-1">
                                    <v-btn size="x-small" variant="tonal" prepend-icon="mdi-content-paste"
                                        @click="loadAgentFinalSummaryDefault" data-testid="agent-finalsummarytemplate-loaddefault">
                                        วางค่าเริ่มต้นลงช่องเพื่อแก้
                                    </v-btn>
                                </div>
                            </div>
                        </v-alert>

                        <!-- Use Base Model Prompt Switch — seed the agent's persona with the flow's Base Model prompt -->
                        <div class="switch-option-compact mb-4">
                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                :class="{ 'switch-active-bg': nodeInspector.useBaseModelPrompt }">
                                <div class="d-flex align-center">
                                    <v-avatar size="28"
                                        :color="nodeInspector.useBaseModelPrompt ? 'green' : 'grey-lighten-1'"
                                        class="me-3">
                                        <v-icon
                                            :color="nodeInspector.useBaseModelPrompt ? 'white' : 'grey-darken-1'"
                                            size="16">mdi-text-box-multiple</v-icon>
                                    </v-avatar>
                                    <div>
                                        <div class="switch-title">Use Base Model Prompt</div>
                                        <div class="switch-description">Prepend the flow's Base Model prompt to this agent's persona</div>
                                    </div>
                                </div>
                                <v-switch v-model="nodeInspector.useBaseModelPrompt"
                                    color="green" hide-details density="compact"
                                    @update:model-value="updateNodeProperty('useBaseModelPrompt', $event)"
                                    :readonly="isViewMode" inset />
                            </div>
                            <div class="text-caption text-grey-darken-1 mt-1 px-3">
                                When enabled, the Base Model prompt is prepended to the Persona Prompt above as the agent's starting system prompt.
                            </div>
                        </div>

                        <!-- Enable Agent Tool Decision Switch -->
                        <div class="switch-option-compact mb-4">
                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                :class="{ 'switch-active-bg': nodeInspector.enableAgentToolDecision }">
                                <div class="d-flex align-center">
                                    <v-avatar size="28"
                                        :color="nodeInspector.enableAgentToolDecision ? 'teal' : 'grey-lighten-1'"
                                        class="me-3">
                                        <v-icon
                                            :color="nodeInspector.enableAgentToolDecision ? 'white' : 'grey-darken-1'"
                                            size="16">mdi-head-cog</v-icon>
                                    </v-avatar>
                                    <div>
                                        <div class="switch-title">Enable Tool Decision</div>
                                        <div class="switch-description">LLM decides which tool to use per step</div>
                                    </div>
                                </div>
                                <v-switch v-model="nodeInspector.enableAgentToolDecision"
                                    color="teal" hide-details density="compact"
                                    @update:model-value="updateNodeProperty('enableAgentToolDecision', $event)"
                                    :readonly="isViewMode" inset />
                            </div>
                            <div class="text-caption text-grey-darken-1 mt-1 px-3">
                                When enabled, LLM chooses tools per step. When disabled, executes all tools and knowledge sources.
                            </div>
                        </div>
                        <!-- Knowledge Connections Section -->
                        <v-expansion-panels class="mt-4 mb-4" variant="accordion">
                            <v-expansion-panel value="knowledge" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #10b981, #059669);">
                                            <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Knowledge Connections</div>
                                            <div class="panel-subtitle">Connect knowledge sources to agent</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content">
                                        <!-- Connected Knowledge Display -->
                                        <div v-for="(connection, index) in getConnectedKnowledge()" :key="index" class="mb-3">
                                            <v-card variant="flat" class="knowledge-connection-card pa-0">
                                                <div class="knowledge-card-header pa-3" :style="{ backgroundColor: connection.bgColor }">
                                                    <div class="d-flex align-center justify-space-between">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="32" :style="{ backgroundColor: connection.color }" class="me-3">
                                                                <v-icon :icon="connection.icon" size="18" color="white"></v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="text-subtitle-2 font-weight-bold" :style="{ color: connection.color }">{{ connection.label }}</div>
                                                                <div class="text-caption text-grey-darken-1">{{ connection.nodeName }}</div>
                                                            </div>
                                                        </div>
                                                        <v-chip size="x-small" :style="{ backgroundColor: connection.color, color: 'white' }">
                                                            Connected
                                                        </v-chip>
                                                    </div>
                                                </div>
                                                
                                                <!-- Description field for all knowledge types -->
                                                <div class="pa-3">
                                                    <v-textarea
                                                        v-model="nodeInspector.knowledgeConnections[connection.key].description"
                                                        :label="`${connection.label} Usage Description`"
                                                        variant="outlined" density="compact" rows="2" auto-grow
                                                        hide-details="auto"
                                                        @update:model-value="updateKnowledgeDescription(connection.key, $event)"
                                                        class="modern-input" :readonly="isViewMode"
                                                        :placeholder="getKnowledgePlaceholder(connection.key)"
                                                        bg-color="white">
                                                        <template v-slot:prepend-inner>
                                                            <v-icon size="18" :style="{ color: connection.color }">mdi-text-box-outline</v-icon>
                                                        </template>
                                                    </v-textarea>
                                                    <div class="text-caption text-grey mt-1">
                                                        <v-icon size="12" class="me-1">mdi-lightbulb-outline</v-icon>
                                                        {{ getKnowledgeHint(connection.key) }}
                                                    </div>
                                                </div>
                                            </v-card>
                                        </div>
                                        
                                        <v-alert v-if="getConnectedKnowledge().length === 0" type="info" variant="tonal" density="compact">
                                            <template v-slot:prepend>
                                                <v-icon size="small">mdi-information</v-icon>
                                            </template>
                                            <div class="text-caption">
                                                No knowledge sources connected. Drag from the agent's bottom handle (Tools) to Document, Database, Knowledge API, Products, or SharePoint nodes.
                                            </div>
                                        </v-alert>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>

                        <!-- Custom AI Model Toggle -->
                        <div class="switch-option-compact mb-4">
                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                :class="{ 'switch-active-bg': nodeInspector.useCustomAIModel }">
                                <div class="d-flex align-center">
                                    <v-avatar size="28"
                                        :color="nodeInspector.useCustomAIModel ? 'deep-purple' : 'grey-lighten-1'"
                                        class="me-3">
                                        <v-icon
                                            :color="nodeInspector.useCustomAIModel ? 'white' : 'grey-darken-1'"
                                            size="16">mdi-brain</v-icon>
                                    </v-avatar>
                                    <div>
                                        <div class="switch-title">Custom AI Model</div>
                                        <div class="switch-description">Use agent-specific AI model instead of Orchestrator's</div>
                                    </div>
                                </div>
                                <v-switch v-model="nodeInspector.useCustomAIModel"
                                    color="deep-purple" hide-details density="compact"
                                    @update:model-value="updateNodeProperty('useCustomAIModel', $event)"
                                    :readonly="isViewMode" inset />
                            </div>
                            <div class="text-caption text-grey-darken-1 mt-1 px-3">
                                When enabled, this agent uses its own AI model settings. When disabled, it uses the Orchestrator's model.
                            </div>
                        </div>

                        <!-- Agent AI Model Settings (shown when useCustomAIModel is enabled) -->
                        <v-expand-transition>
                            <div v-if="nodeInspector.useCustomAIModel">
                                
                                <!-- Validation Warning -->
                                <v-alert 
                                    v-if="!nodeInspector.agentProvider || !nodeInspector.agentModel"
                                    type="warning" variant="tonal" density="compact" class="mb-4"
                                    border="start">
                                    <template v-slot:prepend>
                                        <v-icon>mdi-alert-circle-outline</v-icon>
                                    </template>
                                    <div class="text-caption">
                                        <strong>Incomplete Configuration</strong>
                                        <ul class="mt-1 mb-0" style="padding-left: 16px;">
                                            <li v-if="!nodeInspector.agentProvider">Provider is required</li>
                                            <li v-if="!nodeInspector.agentModel">Model is required</li>
                                        </ul>
                                        <div class="mt-1 text-grey-darken-1">
                                            The agent will fall back to the Orchestrator's model until these settings are configured.
                                        </div>
                                    </div>
                                </v-alert>

                            <v-expansion-panels multiple class="config-panels elevation-0 mb-4" variant="accordion">
                                
                                <!-- Basic Information Panel -->
                                <v-expansion-panel value="agentBasic" class="panel-item">
                                    <v-expansion-panel-title class="panel-title-custom">
                                        <div class="d-flex align-center py-3">
                                            <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #7c3aed, #5b21b6);">
                                                <v-icon icon="mdi-information" size="20" color="white"></v-icon>
                                            </div>
                                            <div>
                                                <div class="panel-title">Basic Information</div>
                                                <div class="panel-subtitle">Provider, model, and basic settings</div>
                                            </div>
                                        </div>
                                    </v-expansion-panel-title>
                                    <v-expansion-panel-text class="panel-content-wrapper">
                                        <div class="panel-content">
                                            <!-- Provider and Model -->
                                            <div class="config-section mb-4">
                                                <h6 class="section-label mb-3">Service Configuration</h6>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-autocomplete v-model="nodeInspector.agentProvider"
                                                            :items="listDDProvider" item-title="providerName"
                                                            item-value="providerName" label="Provider*" variant="outlined"
                                                            density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-cloud-outline"
                                                            @update:model-value="updateNodeProperty('agentProvider', $event); changeProvider($event)"
                                                            class="config-input" :readonly="isViewMode"
                                                            hint="Select AI service provider" persistent-hint required />
                                                    </v-col>
                                                </v-row>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-autocomplete v-model="nodeInspector.agentModel" :items="DDModelShow"
                                                            item-title="modelDisplay" item-value="modelName" label="Model*"
                                                            variant="outlined" density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-brain"
                                                            @update:model-value="updateNodeProperty('agentModel', $event)"
                                                            class="config-input" :readonly="isViewMode"
                                                            :disabled="!nodeInspector.agentProvider"
                                                            hint="Select specific model version" persistent-hint required />
                                                    </v-col>
                                                </v-row>
                                            </div>

                                            <!-- Behavior Settings -->
                                            <div class="config-section">
                                                <h6 class="section-label mb-3">Behavior Settings</h6>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-select v-model="nodeInspector.agentGender"
                                                            :items="[{ id: 'male', name: 'Male' }, { id: 'female', name: 'Female' }]"
                                                            item-title="name" item-value="id" label="Gender"
                                                            variant="outlined" density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-account"
                                                            @update:model-value="updateNodeProperty('agentGender', $event)"
                                                            class="config-input" :readonly="isViewMode"
                                                            hint="Voice/persona gender preference" persistent-hint
                                                            :model-value="nodeInspector.agentGender || 'male'" />
                                                    </v-col>
                                                    <v-col cols="6">
                                                        <v-text-field v-model.number="nodeInspector.agentTypingSpeed"
                                                            label="Typing Speed" type="number" variant="outlined"
                                                            density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-keyboard"
                                                            @update:model-value="updateNodeProperty('agentTypingSpeed', $event)"
                                                            class="config-input" :readonly="isViewMode"
                                                            hint="Simulated typing delay (ms)" persistent-hint />
                                                    </v-col>
                                                    <v-col cols="6">
                                                        <v-text-field v-model.number="nodeInspector.agentMaxInputLength"
                                                            label="Max Input Length" type="number" variant="outlined"
                                                            density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-text-box-outline"
                                                            @update:model-value="updateNodeProperty('agentMaxInputLength', $event)"
                                                            class="config-input" :readonly="isViewMode"
                                                            hint="Max user input characters" persistent-hint />
                                                    </v-col>
                                                </v-row>
                                                <v-row>
                                                    <v-col cols="12">
                                                       <v-text-field v-model.number="nodeInspector.agentTimeout"
                                                           label="LLM Timeout (seconds)" type="number" min="0"
                                                           variant="outlined" density="comfortable" hide-details="auto"
                                                           prepend-inner-icon="mdi-timer-outline"
                                                           @update:model-value="updateNodeProperty('agentTimeout', $event)"
                                                           @keypress="isNumberpositive($event)" class="config-input"
                                                           :readonly="isViewMode"
                                                           hint="0 = use global timeout setting" persistent-hint />
                                                    </v-col>
                                                </v-row>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-text-field v-model.number="nodeInspector.agentMaxRetry"
                                                            label="Max Retry" type="number" min="0"
                                                            variant="outlined" density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-refresh"
                                                            @update:model-value="updateNodeProperty('agentMaxRetry', $event)"
                                                            @keypress="isNumberpositive($event)" class="config-input"
                                                            :readonly="isViewMode"
                                                            hint="0 = use global max retry setting" persistent-hint />
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </div>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>

                                <!-- Response Options Panel -->
                                <v-expansion-panel value="agentResponse" class="panel-item">
                                    <v-expansion-panel-title class="panel-title-custom">
                                        <div class="d-flex align-center py-3">
                                            <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #FF9800, #F57C00);">
                                                <v-icon icon="mdi-tune" size="20" color="white"></v-icon>
                                            </div>
                                            <div>
                                                <div class="panel-title">Response Options</div>
                                                <div class="panel-subtitle">Configure how responses are generated</div>
                                            </div>
                                        </div>
                                    </v-expansion-panel-title>
                                    <v-expansion-panel-text class="panel-content-wrapper">
                                        <div class="panel-content pa-4">
                                            <!-- Stream Response (Controlled by Orchestrator) -->
                                            <div class="switch-option-compact mb-3" style="opacity: 0.55; pointer-events: none;">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg" style="gap: 8px;">
                                                    <div class="d-flex align-center" style="min-width: 0; flex: 1;">
                                                        <v-avatar size="28" color="grey-lighten-1" class="me-3" style="flex-shrink: 0;">
                                                            <v-icon color="grey-darken-1" size="16">mdi-text-box-outline</v-icon>
                                                        </v-avatar>
                                                        <div style="min-width: 0;">
                                                            <div class="switch-title">Stream Response</div>
                                                            <div class="switch-description" style="color: #fb8c00; font-size: 11px;">Controlled by Orchestrator</div>
                                                        </div>
                                                    </div>
                                                    <v-switch :model-value="false" color="grey" hide-details density="compact" disabled inset style="flex-shrink: 0;" />
                                                </div>
                                            </div>

                                            <!-- Json Response (Controlled by Orchestrator) -->
                                            <div class="switch-option-compact mt-3" style="opacity: 0.55; pointer-events: none;">
                                                <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg" style="gap: 8px;">
                                                    <div class="d-flex align-center" style="min-width: 0; flex: 1;">
                                                        <v-avatar size="28" color="grey-lighten-1" class="me-3" style="flex-shrink: 0;">
                                                            <v-icon color="grey-darken-1" size="16">mdi-code-json</v-icon>
                                                        </v-avatar>
                                                        <div style="min-width: 0;">
                                                            <div class="switch-title">JSON Response</div>
                                                            <div class="switch-description" style="color: #fb8c00; font-size: 11px;">Controlled by Orchestrator</div>
                                                        </div>
                                                    </div>
                                                    <v-switch :model-value="false" color="grey" hide-details density="compact" disabled inset style="flex-shrink: 0;" />
                                                </div>
                                            </div>
                                        </div>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>

                                <!-- Memory Settings Panel -->
                                <v-expansion-panel value="agentMemory" class="panel-item">
                                    <v-expansion-panel-title class="panel-title-custom">
                                        <div class="d-flex align-center py-3">
                                            <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #00BCD4, #0097A7);">
                                                <v-icon icon="mdi-brain" size="20" color="white"></v-icon>
                                            </div>
                                            <div>
                                                <div class="panel-title">Memory Settings</div>
                                                <div class="panel-subtitle">Configure conversation memory and context</div>
                                            </div>
                                        </div>
                                    </v-expansion-panel-title>
                                    <v-expansion-panel-text class="panel-content-wrapper">
                                        <div class="panel-content">
                                            <div class="config-section mb-3">
                                                <h6 class="section-label mb-3">Conversation Memory</h6>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-text-field v-model.number="nodeInspector.agentMemoryTurn"
                                                            label="Turn Count" type="number" min="0" variant="outlined"
                                                            density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-history"
                                                            @update:model-value="updateNodeProperty('agentMemoryTurn', $event)"
                                                            @keypress="isNumberpositive($event)" class="config-input"
                                                            :readonly="isViewMode"
                                                            hint="Number of conversation turns to remember"
                                                            persistent-hint />
                                                    </v-col>
                                                </v-row>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-text-field v-model.number="nodeInspector.agentMemoryDuration"
                                                            label="Duration (minutes)" type="number" min="0"
                                                            variant="outlined" density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-timer-outline"
                                                            @update:model-value="updateNodeProperty('agentMemoryDuration', $event)"
                                                            @keypress="isNumberpositive($event)" class="config-input"
                                                            :readonly="isViewMode" hint="How long to keep memory active"
                                                            persistent-hint />
                                                    </v-col>
                                                </v-row>
                                            </div>

                                            <div class="config-section mb-3">
                                                <h6 class="section-label mb-3">Memory Types</h6>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-text-field v-model.number="nodeInspector.agentShortTermMemory"
                                                            label="Short-Term Memory" type="number" min="1"
                                                            variant="outlined" density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-memory"
                                                            @update:model-value="updateNodeProperty('agentShortTermMemory', $event)"
                                                            @keypress="isNumberpositive($event)" class="config-input"
                                                            :readonly="isViewMode" hint="Recent context window size"
                                                            persistent-hint />
                                                    </v-col>
                                                </v-row>
                                                <v-row>
                                                    <v-col>
                                                        <!-- Long-Term Memory Switch -->
                                                        <div class="switch-option-compact mb-4">
                                                            <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                                :class="{ 'switch-active-bg': nodeInspector.agentLongTermMemory }">
                                                                <div class="d-flex align-center">
                                                                    <v-avatar size="28"
                                                                        :color="nodeInspector.agentLongTermMemory ? 'success' : 'grey-lighten-1'"
                                                                        class="me-3">
                                                                        <v-icon
                                                                            :color="nodeInspector.agentLongTermMemory ? 'white' : 'grey-darken-1'"
                                                                            size="16">mdi-database</v-icon>
                                                                    </v-avatar>
                                                                    <div>
                                                                        <div class="switch-title">Long-Term Memory</div>
                                                                        <div class="switch-description">Persistent storage</div>
                                                                    </div>
                                                                </div>
                                                                <v-switch v-model="nodeInspector.agentLongTermMemory"
                                                                    color="success" hide-details density="compact"
                                                                    @update:model-value="updateNodeProperty('agentLongTermMemory', $event)"
                                                                    :readonly="isViewMode" inset />
                                                            </div>
                                                        </div>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </div>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>

                                <!-- AI Parameters Panel -->
                                <v-expansion-panel value="agentParameters" class="panel-item">
                                    <v-expansion-panel-title class="panel-title-custom">
                                        <div class="d-flex align-center py-3">
                                            <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #9C27B0, #7B1FA2);">
                                                <v-icon icon="mdi-cog" size="20" color="white"></v-icon>
                                            </div>
                                            <div>
                                                <div class="panel-title">AI Parameters</div>
                                                <div class="panel-subtitle">Fine-tune model behavior and output</div>
                                            </div>
                                        </div>
                                    </v-expansion-panel-title>
                                    <v-expansion-panel-text class="panel-content-wrapper">
                                        <div class="panel-content">
                                            <div class="config-section mb-3">
                                                <h6 class="section-label mb-3">Core Parameters</h6>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-text-field v-model.number="nodeInspector.agentTemperature"
                                                            label="Temperature*" type="number" step="0.1" min="0" max="1"
                                                            variant="outlined" density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-thermometer"
                                                            @update:model-value="updateNodeProperty('agentTemperature', $event)"
                                                            @keypress="isNumberpositive($event)" class="config-input"
                                                            :readonly="isViewMode"
                                                            hint="Creativity level (0 = focused, 1 = creative)"
                                                            persistent-hint />
                                                    </v-col>
                                                </v-row>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-text-field v-model.number="nodeInspector.agentTopP" label="Top P"
                                                            type="number" step="0.1" min="0" max="1" variant="outlined"
                                                            density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-filter-variant"
                                                            @update:model-value="updateNodeProperty('agentTopP', $event)"
                                                            @keypress="isNumberpositive($event)" class="config-input"
                                                            :readonly="isViewMode"
                                                            hint="Token selection probability threshold" persistent-hint />
                                                    </v-col>
                                                </v-row>
                                            </div>

                                            <div class="config-section mb-3">
                                                <h6 class="section-label mb-3">Penalty Settings</h6>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-text-field v-model.number="nodeInspector.agentFrequencyPenalty"
                                                            label="Frequency Penalty" type="number" step="0.1" min="0"
                                                            max="2" variant="outlined" density="comfortable"
                                                            hide-details="auto" prepend-inner-icon="mdi-repeat-off"
                                                            @update:model-value="updateNodeProperty('agentFrequencyPenalty', $event)"
                                                            @keypress="isNumberpositive($event)" class="config-input"
                                                            :readonly="isViewMode"
                                                            hint="Reduce repetition of frequent tokens" persistent-hint />
                                                    </v-col>
                                                </v-row>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-text-field v-model.number="nodeInspector.agentPresencePenalty"
                                                            label="Presence Penalty" type="number" step="0.1" min="0"
                                                            max="2" variant="outlined" density="comfortable"
                                                            hide-details="auto"
                                                            prepend-inner-icon="mdi-minus-circle-outline"
                                                            @update:model-value="updateNodeProperty('agentPresencePenalty', $event)"
                                                            @keypress="isNumberpositive($event)" class="config-input"
                                                            :readonly="isViewMode" hint="Reduce repetition of any tokens"
                                                            persistent-hint />
                                                    </v-col>
                                                </v-row>
                                            </div>

                                            <div class="config-section">
                                                <h6 class="section-label mb-3">Output Control</h6>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-text-field v-model.number="nodeInspector.agentMaxTokens"
                                                            label="Max Tokens Response" type="number" min="1"
                                                            variant="outlined" density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-text-box-check-outline"
                                                            @update:model-value="updateNodeProperty('agentMaxTokens', $event)"
                                                            @keypress="isNumberpositive($event)" class="config-input"
                                                            :readonly="isViewMode" hint="Maximum response length in tokens"
                                                            persistent-hint />
                                                    </v-col>
                                                </v-row>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-text-field v-model="nodeInspector.agentStopSequence"
                                                            label="Stop Sequence" variant="outlined" density="comfortable"
                                                            hide-details="auto" prepend-inner-icon="mdi-stop-circle-outline"
                                                            @update:model-value="updateNodeProperty('agentStopSequence', $event)"
                                                            class="config-input" :readonly="isViewMode"
                                                            hint="Text sequence to stop generation" persistent-hint />
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </div>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>

                                <!-- Prompt Settings Panel -->
                                <v-expansion-panel value="agentPromptSettings" class="panel-item">
                                    <v-expansion-panel-title class="panel-title-custom">
                                        <div class="d-flex align-center py-3">
                                            <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #607D8B, #455A64);">
                                                <v-icon icon="mdi-message-text" size="20" color="white"></v-icon>
                                            </div>
                                            <div>
                                                <div class="panel-title">Prompt Settings</div>
                                                <div class="panel-subtitle">Time context and transaction options</div>
                                            </div>
                                        </div>
                                    </v-expansion-panel-title>
                                    <v-expansion-panel-text class="panel-content-wrapper">
                                        <div class="panel-content">
                                            <!-- Time Context Section -->
                                            <div class="config-section mt-3">
                                                <h6 class="section-label mb-3">Time Context</h6>
                                                <div class="switch-option-compact mb-3">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.agentEnableTime }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.agentEnableTime ? 'success' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.agentEnableTime ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-clock-outline</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">Time Context</div>
                                                                <div class="switch-description">Enable time awareness for AI responses</div>
                                                            </div>
                                                        </div>
                                                        <v-switch v-model="nodeInspector.agentEnableTime" color="success"
                                                            hide-details density="compact"
                                                            @update:model-value="updateNodeProperty('agentEnableTime', $event)"
                                                            :readonly="isViewMode" inset />
                                                    </div>
                                                </div>
                                                <div v-if="nodeInspector.agentEnableTime" class="mt-3">
                                                    <v-select v-model="nodeInspector.agentTimeZone" :items="DDTimeZone"
                                                        item-title="name" item-value="value" label="Time Zone"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-earth"
                                                        @update:model-value="updateNodeProperty('agentTimeZone', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="Select your timezone for time-aware responses" persistent-hint
                                                        placeholder="Select your timezone">
                                                        <template v-slot:selection="{ item }">
                                                            <div class="d-flex align-center">
                                                                <v-icon small color="primary" class="me-2">mdi-map-marker</v-icon>
                                                                <span class="font-weight-medium">{{ item.raw.name }}</span>
                                                                <v-chip size="x-small" color="grey-darken-1" text-color="white" class="ml-2">
                                                                    {{ item.raw.value.split('/')[1].replace('_', ' ') }}
                                                                </v-chip>
                                                            </div>
                                                        </template>
                                                    </v-select>
                                                </div>
                                            </div>

                                            <!-- TransactionID Context Section -->
                                            <div class="config-section mt-6">
                                                <h6 class="section-label mb-3">TransactionID Context</h6>
                                                <div class="switch-option-compact mb-3">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.agentEnableTransactionId }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.agentEnableTransactionId ? 'info' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.agentEnableTransactionId ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-identifier</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">Add TransactionID to Prompt</div>
                                                                <div class="switch-description">Include transaction identifier in AI context</div>
                                                            </div>
                                                        </div>
                                                        <v-switch v-model="nodeInspector.agentEnableTransactionId" color="info"
                                                            hide-details density="compact"
                                                            @update:model-value="updateNodeProperty('agentEnableTransactionId', $event)"
                                                            :readonly="isViewMode" inset />
                                                    </div>
                                                </div>
                                            </div>

                                            <!-- Prompt Replacement Flags Section -->
                                            <div class="config-section mt-6">
                                                <h6 class="section-label mb-3">Prompt Replacement Flags</h6>
                                                <div class="switch-option-compact mb-3">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.agentEnableLanguageReplace }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.agentEnableLanguageReplace ? 'teal' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.agentEnableLanguageReplace ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-translate</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">Language replacing</div>
                                                                <div class="switch-description">Replace tag with detected language in prompt</div>
                                                            </div>
                                                        </div>
                                                        <v-switch v-model="nodeInspector.agentEnableLanguageReplace" color="teal"
                                                            hide-details density="compact"
                                                            @update:model-value="updateNodeProperty('agentEnableLanguageReplace', $event)"
                                                            :readonly="isViewMode" inset />
                                                    </div>
                                                    <!-- Code block with copy button -->
                                                    <div v-if="nodeInspector.agentEnableLanguageReplace" class="code-block-wrapper mt-2 mx-3">
                                                        <div class="code-block d-flex align-center justify-space-between">
                                                            <code class="code-text">{{LanguageTagText}}</code>
                                                            <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                                @click="copyToClipboard(LanguageTagText)"
                                                                class="copy-btn">
                                                                <v-icon size="14">mdi-content-copy</v-icon>
                                                                <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                            </v-btn>
                                                        </div>
                                                    </div>
                                                </div>
                                                <!-- #779 Insurance replacing: {{insurance}} → ตาราง product ประกันที่ Active -->
                                                <div class="switch-option-compact mb-3">
                                                    <div class="d-flex align-center justify-space-between py-2 px-3 rounded-lg"
                                                        :class="{ 'switch-active-bg': nodeInspector.agentEnableInsurance }">
                                                        <div class="d-flex align-center">
                                                            <v-avatar size="28"
                                                                :color="nodeInspector.agentEnableInsurance ? 'indigo' : 'grey-lighten-1'"
                                                                class="me-3">
                                                                <v-icon
                                                                    :color="nodeInspector.agentEnableInsurance ? 'white' : 'grey-darken-1'"
                                                                    size="16">mdi-shield-check-outline</v-icon>
                                                            </v-avatar>
                                                            <div>
                                                                <div class="switch-title">Insurance replacing</div>
                                                                <div class="switch-description">Replace tag with active insurance products table</div>
                                                            </div>
                                                        </div>
                                                        <v-switch v-model="nodeInspector.agentEnableInsurance" color="indigo"
                                                            hide-details density="compact"
                                                            @update:model-value="updateNodeProperty('agentEnableInsurance', $event)"
                                                            :readonly="isViewMode" inset />
                                                    </div>
                                                    <!-- Code block with copy button -->
                                                    <div v-if="nodeInspector.agentEnableInsurance" class="code-block-wrapper mt-2 mx-3">
                                                        <div class="code-block d-flex align-center justify-space-between">
                                                            <code class="code-text">{{InsuranceTagText}}</code>
                                                            <v-btn icon size="x-small" variant="text" color="grey-darken-1"
                                                                @click="copyToClipboard(InsuranceTagText)"
                                                                class="copy-btn">
                                                                <v-icon size="14">mdi-content-copy</v-icon>
                                                                <v-tooltip activator="parent" location="top">Copy to clipboard</v-tooltip>
                                                            </v-btn>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <v-alert type="info" variant="tonal" density="compact" class="mt-4">
                                                <template v-slot:prepend>
                                                    <v-icon size="small">mdi-information</v-icon>
                                                </template>
                                                <div class="text-caption">
                                                    System prompt uses the <strong>Persona Prompt</strong> configured above.
                                                    Time and TransactionID context will be appended automatically.
                                                </div>
                                            </v-alert>
                                        </div>
                                    </v-expansion-panel-text>
                                </v-expansion-panel>

                            </v-expansion-panels>
                            </div>
                        </v-expand-transition>

                        <!-- Hosted Tools Section — ค่าเก็บที่ node.data.toolsConfig (JSON string) ของโหนดนี้
                             ไม่ gate ตาม provider/model ที่เลือก (ต้นทางเคย gate แล้วการ์ดหายทั้งใบ)
                             คนละเรื่องกับ Agent Tools (custom HTTP tool แบบ ReAct) ด้านบน -->
                        <v-expansion-panels class="mt-4 mb-4" variant="accordion">
                            <v-expansion-panel value="tools" class="panel-item">
                                <v-expansion-panel-title class="panel-title-custom">
                                    <div class="d-flex align-center py-3">
                                        <div class="panel-icon-wrapper me-4" style="background: linear-gradient(135deg, #26A69A, #00796B);">
                                            <v-icon icon="mdi-tools" size="20" color="white"></v-icon>
                                        </div>
                                        <div>
                                            <div class="panel-title">Tools</div>
                                            <div class="panel-subtitle">Hosted tools for this node</div>
                                        </div>
                                    </div>
                                </v-expansion-panel-title>
                                <v-expansion-panel-text class="panel-content-wrapper">
                                    <div class="panel-content pa-4">
                                        <div class="config-section">
                                            <h6 class="section-label mb-3">Tool Configuration</h6>
                                            <v-row>
                                                <v-col cols="12">
                                                    <v-select
                                                        id="tools-type-FlowAgent"
                                                        :model-value="nodeInspector.toolsType"
                                                        :items="toolTypeOptions" item-title="text" item-value="value"
                                                        label="Type" variant="outlined" density="comfortable"
                                                        hide-details="auto" prepend-inner-icon="mdi-cog"
                                                        @update:model-value="setNodeToolsType($event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="The type of hosted tool this bot can call in addition to its normal reply. One type per model. รองรับเฉพาะ provider OpenAI และ Azure เท่านั้น (Anthropic / Google / Other ยังไม่รองรับ)"
                                                        persistent-hint clearable />
                                                </v-col>

                                                <!-- เตือนเรื่อง provider ของ hosted web_search — เตือนเท่านั้น ห้าม gate/ซ่อนการ์ด Tools -->
                                                <v-col
                                                    v-if="nodeInspector.toolsType === 'web_search' && !nodeInspector.useCustomAIModel"
                                                    cols="12">
                                                    <v-alert type="warning" variant="tonal" density="compact">
                                                        <template v-slot:prepend>
                                                            <v-icon size="small">mdi-alert-outline</v-icon>
                                                        </template>
                                                        <div class="text-caption">
                                                            โหนดนี้ยังไม่ได้เปิด <strong>Custom AI Model</strong> จึงจะใช้ provider/model
                                                            ตามที่ตั้งไว้ที่โหนด <strong>Orchestrator</strong>
                                                            กรุณาตรวจสอบว่า provider นั้นเป็น <strong>OpenAI</strong> หรือ
                                                            <strong>Azure</strong> มิฉะนั้น hosted web_search จะไม่ทำงาน
                                                        </div>
                                                    </v-alert>
                                                </v-col>
                                                <v-col
                                                    v-if="nodeInspector.toolsType === 'web_search' && nodeInspector.useCustomAIModel && nodeInspector.agentProvider && !['openai', 'azure'].includes(String(nodeInspector.agentProvider).trim().toLowerCase())"
                                                    cols="12">
                                                    <v-alert type="error" variant="tonal" density="compact">
                                                        <template v-slot:prepend>
                                                            <v-icon size="small">mdi-alert-circle-outline</v-icon>
                                                        </template>
                                                        <div class="text-caption">
                                                            provider <strong>{{ nodeInspector.agentProvider }}</strong>
                                                            ยังไม่รองรับ hosted web_search — รองรับเฉพาะ
                                                            <strong>OpenAI</strong> และ <strong>Azure</strong> เท่านั้น
                                                            กรุณาเปลี่ยน provider ของโหนดนี้ หรือเอา Tool type ออก
                                                        </div>
                                                    </v-alert>
                                                </v-col>

                                                <v-col v-if="nodeInspector.toolsType === 'web_search'" cols="12">
                                                    <v-select
                                                        id="websearch-tool-choice-FlowAgent"
                                                        :model-value="nodeInspector.toolsChoice"
                                                        :items="toolChoiceOptions" item-title="text" item-value="value"
                                                        label="Tool choice (optional)" variant="outlined"
                                                        density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-gesture-tap-button"
                                                        @update:model-value="setNodeToolsChoice($event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="How tools are invoked per request — web_search: always force a web search (default, most reliable) · auto: let the model decide (cheaper, but may skip searching) · none: temporarily disable searching without clearing the config."
                                                        persistent-hint clearable />
                                                </v-col>
                                                <v-col v-if="nodeInspector.toolsType === 'web_search'" cols="12">
                                                    <v-select
                                                        id="websearch-context-size-FlowAgent"
                                                        :model-value="nodeInspector.toolsWebSearch.searchContextSize"
                                                        :items="webSearchContextOptions" item-title="text" item-value="value"
                                                        label="Search context size (optional)" placeholder="Medium (default)"
                                                        variant="outlined" density="comfortable" hide-details="auto"
                                                        prepend-inner-icon="mdi-text-box-search-outline"
                                                        @update:model-value="setNodeToolsWebSearchField('searchContextSize', $event)"
                                                        class="config-input" :readonly="isViewMode"
                                                        hint="How much web content is pulled in for the model to read before answering — Low: fastest and cheapest, less context · Medium: balanced · High: more complete answers, but slower and more expensive · Leave empty to use the OpenAI default (Medium)."
                                                        persistent-hint clearable />
                                                </v-col>
                                            </v-row>
                                        </div>

                                        <!-- ===== web_search params ===== -->
                                        <template v-if="nodeInspector.toolsType === 'web_search'">
                                            <div class="config-section">
                                                <h6 class="section-label mb-3">Allowed Domains</h6>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-combobox
                                                            id="websearch-allowed-domains-FlowAgent"
                                                            :model-value="nodeInspector.toolsWebSearch.allowedDomains"
                                                            label="Allowed domains (optional)"
                                                            placeholder="e.g. example.com (Enter to add)"
                                                            multiple chips closable-chips clearable
                                                            variant="outlined" density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-web"
                                                            @update:model-value="setNodeToolsWebSearchField('allowedDomains', $event)"
                                                            class="config-input" :readonly="isViewMode"
                                                            hint="Restrict search to the domains you list (up to 20). Leave empty to search the whole web. Press Enter to add each domain."
                                                            persistent-hint />
                                                    </v-col>
                                                </v-row>
                                            </div>

                                            <div class="config-section">
                                                <h6 class="section-label mb-3">User Location (optional)</h6>
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-combobox
                                                            id="websearch-loc-country-FlowAgent"
                                                            :model-value="nodeInspector.toolsWebSearch.locationCountry"
                                                            :items="webSearchCountryOptions"
                                                            label="Country (ISO-2)" placeholder="e.g. TH"
                                                            variant="outlined" density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-flag-outline" clearable
                                                            @update:model-value="setNodeToolsWebSearchField('locationCountry', $event)"
                                                            class="config-input" :readonly="isViewMode"
                                                            hint="Tells the model roughly where the user is so search results are localised. Timezone matters most for time-sensitive questions such as &quot;today's news&quot;. Every field is optional; Country and Timezone alone are usually enough."
                                                            persistent-hint />
                                                    </v-col>
                                                    <v-col cols="12" md="6">
                                                        <v-text-field
                                                            id="websearch-loc-city-FlowAgent"
                                                            :model-value="nodeInspector.toolsWebSearch.locationCity"
                                                            label="City" placeholder="e.g. Bangkok"
                                                            variant="outlined" density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-city-variant-outline" clearable
                                                            @update:model-value="setNodeToolsWebSearchField('locationCity', $event)"
                                                            class="config-input" :readonly="isViewMode" />
                                                    </v-col>
                                                    <v-col cols="12" md="6">
                                                        <v-text-field
                                                            id="websearch-loc-region-FlowAgent"
                                                            :model-value="nodeInspector.toolsWebSearch.locationRegion"
                                                            label="Region" placeholder="e.g. Bangkok"
                                                            variant="outlined" density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-map-marker-outline" clearable
                                                            @update:model-value="setNodeToolsWebSearchField('locationRegion', $event)"
                                                            class="config-input" :readonly="isViewMode" />
                                                    </v-col>
                                                    <v-col cols="12">
                                                        <v-combobox
                                                            id="websearch-loc-timezone-FlowAgent"
                                                            :model-value="nodeInspector.toolsWebSearch.locationTimezone"
                                                            :items="webSearchTimezoneOptions"
                                                            label="Timezone (IANA)" placeholder="e.g. Asia/Bangkok"
                                                            variant="outlined" density="comfortable" hide-details="auto"
                                                            prepend-inner-icon="mdi-clock-outline" clearable
                                                            @update:model-value="setNodeToolsWebSearchField('locationTimezone', $event)"
                                                            class="config-input" :readonly="isViewMode" />
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </template>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>

                    </div>

                    <!-- Tax Planning Agent Inspector Section
                         🔴 Deliberately SHORT. Every LLM/prompt/memory knob an ordinary agent exposes is owned by
                         tax.TaxPlanningSettingsV2 for this one, so the panel offers the two fields the Orchestrator
                         actually reads plus an optional settings pin — and says where the rest lives. -->
                    <div v-else-if="selectedNode.data.type === 'taxPlanningAgent'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-clipboard-text-clock" size="24" color="indigo" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-indigo mb-0">Tax Planning Agent</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">
                                แบบสอบถามวางแผนภาษี (Tax Planning V2) ในรูปแบบ Agent — ต่อเข้ากับ Orchestrator ที่ handle
                                <strong>agents</strong> เหมือน Agent ทั่วไป
                            </p>
                        </div>

                        <!-- Agent Name (Orchestrator ใช้เลือก agent) -->
                        <v-text-field v-model="nodeInspector.agentName" label="ชื่อ Agent"
                            variant="outlined" density="compact" hide-details="auto" class="mb-4"
                            prepend-inner-icon="mdi-tag-outline"
                            hint="ชื่อที่ Orchestrator ใช้อ้างถึง agent ตัวนี้"
                            persistent-hint
                            @update:model-value="updateNodeProperty('agentName', $event)"></v-text-field>

                        <!-- Agent Description (Orchestrator ใช้ตัดสินใจ) -->
                        <v-textarea v-model="nodeInspector.agentDescription" label="คำอธิบายงานที่ทำได้"
                            variant="outlined" density="compact" rows="4" auto-grow hide-details="auto" class="mb-4"
                            hint="Orchestrator อ่านข้อความนี้เพื่อตัดสินใจว่าจะส่งงานให้ agent ตัวนี้หรือไม่ — เขียนให้ครอบคลุมว่าดูแลเรื่องอะไรบ้าง"
                            persistent-hint
                            @update:model-value="updateNodeProperty('agentDescription', $event)"></v-textarea>

                        <!-- Guardrail bypass — the one behaviour switch this agent has -->
                        <v-card variant="outlined" class="pa-3 mb-4" rounded="lg">
                            <v-switch v-model="nodeInspector.bypassGuardrailWhenAlone"
                                color="primary" density="compact" hide-details inset
                                :readonly="isViewMode"
                                @update:model-value="updateNodeProperty('bypassGuardrailWhenAlone', $event)">
                                <template v-slot:label>
                                    <div class="ms-2">
                                        <div class="text-body-2 font-weight-medium">ข้าม Guardrail ของ Orchestrator</div>
                                        <div class="text-caption text-medium-emphasis">
                                            เฉพาะเทิร์นที่คำตอบของ agent ตัวนี้ถูกส่งออกไปตรง ๆ
                                        </div>
                                    </div>
                                </template>
                            </v-switch>

                            <v-divider class="my-3"></v-divider>

                            <div v-if="nodeInspector.bypassGuardrailWhenAlone === false"
                                class="text-caption text-warning d-flex">
                                <v-icon size="16" class="me-2 mt-1">mdi-alert-outline</v-icon>
                                <span>
                                    Guardrail จะตรวจและ<strong>เขียนคำตอบใหม่</strong>ทุกเทิร์น
                                    ทำให้การ์ดภาษี parse ไม่ได้และตัวเลขอาจเพี้ยน — เปิดไว้เพื่อ debug เท่านั้น
                                </span>
                            </div>
                            <div v-else class="text-caption text-medium-emphasis d-flex">
                                <v-icon size="16" class="me-2 mt-1">mdi-shield-check-outline</v-icon>
                                <span>
                                    เมื่อ Orchestrator ตั้ง Guardrail ไว้ — จะข้ามให้ทุกเทิร์นที่<strong>คำตอบของ agent นี้
                                    ถูกส่งออกไปตรง ๆ</strong> ไม่ว่าจะมี agent อื่นทำงานร่วมด้วยหรือไม่
                                    และบันทึก step <code>Bypassed</code> ไว้ในล็อก<br />
                                    ถ้าคำตอบถูกนำไปรวมกับ agent อื่นแล้วเขียนใหม่ Guardrail จะทำงานตามปกติ<br />
                                    ต้องเปิดสวิตช์ฝั่ง Orchestrator ด้วย จึงจะข้ามจริง
                                </span>
                            </div>
                        </v-card>

                        <!-- ══ Meta Document knowledge — pick by header ▸ category ═══════════════════════════════
                             🔴 UX: the inspector is a narrow column, so the two-level taxonomy is an accordion of
                             headers rather than a nested tree or a pair of linked dropdowns. Three properties make
                             it usable at this width:
                               · the SELECTION is visible without opening anything (chips at the top), because the
                                 thing an author gets wrong is forgetting what they already picked;
                               · one search box filters BOTH levels and auto-opens the headers that matched, so
                                 finding a category never costs more than typing part of its name;
                               · every header has a tri-state box, so "all of Tax" is one click and reads back as
                                 one chip instead of four. -->
                        <v-card variant="outlined" class="pa-0 mb-4 overflow-hidden" rounded="lg">
                            <div class="d-flex align-center pa-3">
                                <v-icon icon="mdi-bookshelf" size="20" color="indigo" class="me-3"></v-icon>
                                <div class="flex-grow-1">
                                    <div class="text-body-2 font-weight-medium">คลังความรู้ Meta Document</div>
                                    <div class="text-caption text-medium-emphasis">
                                        เลือกหมวดที่ agent ตัวนี้อ่านได้ — เลือกข้ามหัวข้อได้ทุกหัวข้อ
                                    </div>
                                    <!-- #746 -->
                                    <div class="text-caption text-medium-emphasis">
                                        ใช้เฉพาะเอกสารที่ปีภาษีตรงกับฟอร์ม (Tax Configuration) หรือไม่ระบุปี
                                    </div>
                                </div>
                                <v-switch v-model="nodeInspector.taxKnowledgeEnabled"
                                    color="indigo" density="compact" hide-details inset class="flex-grow-0 ms-2"
                                    :readonly="isViewMode"
                                    @update:model-value="updateNodeProperty('taxKnowledgeEnabled', $event)"></v-switch>
                            </div>

                            <v-expand-transition>
                                <div v-if="nodeInspector.taxKnowledgeEnabled">
                                    <v-divider></v-divider>

                                    <!-- The current selection, always visible without opening a panel. -->
                                    <div class="pa-3 pb-0">
                                        <div v-if="taxKnowledgeSelectionCount === 0"
                                            class="text-caption text-medium-emphasis d-flex align-start mb-3">
                                            <v-icon size="16" class="me-2">mdi-information-outline</v-icon>
                                            <span>ยังไม่ได้เลือกหมวดไหน — agent จะยังไม่เห็นคลังความรู้นี้</span>
                                        </div>
                                        <div v-else class="d-flex flex-wrap ga-1 mb-3">
                                            <v-chip v-for="chip in taxKnowledgeSelectedChips" :key="chip.key"
                                                size="small" color="indigo" variant="tonal" label
                                                :closable="!isViewMode"
                                                @click:close="removeTaxKnowledgeSelection(chip.header, chip.category)">
                                                <v-icon start size="14">{{ chip.category ? 'mdi-tag-outline' : 'mdi-folder-outline' }}</v-icon>
                                                {{ chip.label }}
                                            </v-chip>
                                        </div>
                                    </div>

                                    <div class="px-3">
                                        <v-text-field v-model="taxKnowledgeSearch"
                                            placeholder="ค้นหาหัวข้อหรือหมวด…"
                                            variant="outlined" density="compact" hide-details clearable
                                            prepend-inner-icon="mdi-magnify" class="mb-2"></v-text-field>
                                    </div>

                                    <!-- App Knowledge Center (A4): หัวข้อ/หมวดของ Center (ทะเบียน › catalog) — โหมดปิดไม่แสดง -->
                                    <v-alert v-if="kccTaxonomyNotice && !taxKnowledgeLoading"
                                        :type="kccTaxonomyNotice === 'kccTaxonomyCenterOnly' ? 'info' : 'warning'"
                                        variant="tonal" density="compact" class="mx-3 mb-3 text-caption">
                                        {{ $t(kccTaxonomyNotice) }}
                                    </v-alert>
                                    <div v-if="taxKnowledgeLoading" class="px-3 pb-3">
                                        <v-skeleton-loader type="list-item-two-line, list-item-two-line"></v-skeleton-loader>
                                    </div>
                                    <v-alert v-else-if="taxKnowledgeLoadError" type="warning" variant="tonal"
                                        density="compact" class="mx-3 mb-3">
                                        <div class="text-caption">
                                            โหลดรายการหัวข้อ/หมวดไม่สำเร็จ — ค่าที่เลือกไว้เดิมยังอยู่ครบ ไม่ถูกล้าง
                                            <v-btn size="x-small" variant="text" color="warning" class="ms-1"
                                                @click="loadTaxKnowledgeTaxonomy(true)">ลองอีกครั้ง</v-btn>
                                        </div>
                                    </v-alert>
                                    <div v-else-if="taxKnowledgeFilteredHeaders.length === 0"
                                        class="px-3 pb-3 text-caption text-medium-emphasis">
                                        ไม่พบหัวข้อหรือหมวดที่ตรงกับคำค้นนี้
                                    </div>

                                    <v-expansion-panels v-else v-model="taxKnowledgeOpenPanels" multiple flat
                                        class="tax-knowledge-panels">
                                        <v-expansion-panel v-for="h in taxKnowledgeFilteredHeaders" :key="h.title"
                                            :value="h.title" elevation="0">
                                            <v-expansion-panel-title class="px-3">
                                                <div class="d-flex align-center flex-grow-1" style="min-width:0">
                                                    <!-- 🔴 The wrapper is what stops the panel from toggling, and the
                                                         checkbox reports through @update:model-value — NOT @click. A
                                                         click handler on the control itself fires for both the input and
                                                         its label on some Vuetify builds, which toggles the header on
                                                         and straight back off with nothing on screen to explain it. -->
                                                    <div class="flex-grow-0 me-1 d-flex" @click.stop>
                                                        <v-checkbox-btn
                                                            :model-value="headerSelectState(h) === 'all'"
                                                            :indeterminate="headerSelectState(h) === 'some'"
                                                            color="indigo" density="compact"
                                                            :disabled="isViewMode"
                                                            @update:model-value="toggleTaxKnowledgeHeader(h)"></v-checkbox-btn>
                                                    </div>
                                                    <span class="text-body-2 text-truncate">{{ prettyTaxonomy(h.title) }}</span>
                                                    <v-chip v-if="headerSelectedCount(h) > 0" size="x-small"
                                                        color="indigo" variant="flat" label class="ms-2 flex-grow-0">
                                                        {{ headerSelectedCount(h) }}</v-chip>
                                                    <v-spacer></v-spacer>
                                                    <span class="text-caption text-medium-emphasis me-2 flex-grow-0">
                                                        {{ h.categories.length || '–' }}</span>
                                                </div>
                                            </v-expansion-panel-title>
                                            <v-expansion-panel-text class="tax-knowledge-panel-body">
                                                <div v-if="h.categories.length === 0"
                                                    class="text-caption text-medium-emphasis pb-2">
                                                    หัวข้อนี้ไม่มีหมวดย่อย — ติ๊กที่หัวข้อเพื่อใช้ทั้งหัวข้อ
                                                </div>
                                                <v-checkbox v-for="c in h.categories" :key="c.key"
                                                    :model-value="isTaxKnowledgeSelected(h.title, c.key)"
                                                    :label="prettyTaxonomy(c.key)"
                                                    color="indigo" density="compact" hide-details
                                                    class="tax-knowledge-cat"
                                                    :disabled="isViewMode || isTaxKnowledgeSelected(h.title, null)"
                                                    @update:model-value="toggleTaxKnowledgeCategory(h.title, c.key)"></v-checkbox>
                                            </v-expansion-panel-text>
                                        </v-expansion-panel>
                                    </v-expansion-panels>

                                    <v-divider></v-divider>
                                    <div class="d-flex align-center pa-2 px-3">
                                        <span class="text-caption text-medium-emphasis">
                                            เลือกไว้ {{ taxKnowledgeSelectionCount }} รายการ
                                        </span>
                                        <v-spacer></v-spacer>
                                        <v-btn size="x-small" variant="text"
                                            :disabled="isViewMode || taxKnowledgeSelectionCount === 0"
                                            @click="clearTaxKnowledgeSelections">ล้างทั้งหมด</v-btn>
                                    </div>
                                </div>
                            </v-expand-transition>
                        </v-card>

                        <v-alert type="info" variant="tonal" density="compact" class="mb-3">
                            <div class="text-body-2 font-weight-medium mb-1">การตั้งค่าอยู่ที่ส่วนกลาง</div>
                            <div class="text-caption">
                                โมเดล · prompt ทุกชั้น · หน่วยความจำ · quick reply · guardrail ในตัวของ agent
                                ตั้งที่ <strong>Tax Configuration V2 → Tax Planning Settings</strong>
                                ไม่ได้ตั้งที่ node นี้ เพื่อให้สิ่งที่ Tax Playground แสดงตรงกับที่ระบบใช้จริง<br />
                                ใช้<strong>เวอร์ชันที่ Active อยู่</strong> อ่านใหม่ทุกเทิร์น —
                                เปลี่ยนเวอร์ชันแล้วมีผลทันทีโดยไม่ต้องแก้ flow
                            </div>
                        </v-alert>

                        <v-alert type="success" variant="tonal" density="compact">
                            <div class="text-body-2 font-weight-medium mb-1">พฤติกรรมที่มีมาในตัว</div>
                            <div class="text-caption">
                                • <strong>จำสถานะฟอร์มข้ามเทิร์น</strong> — ระหว่างที่ลูกค้ากรอกอยู่ในบทสนทนานี้
                                agent จะขอรับเทิร์นเอง Orchestrator ไม่ต้องเดา คำตอบสั้น ๆ อย่าง "50,000" จึงไม่หลุด<br />
                                • <strong>ฟอร์มเป็นของลูกค้า ไม่ใช่ของห้องแชต</strong> — กลับมาวันหลังจะถาม
                                "ทำต่อ / เริ่มใหม่" ก่อน และระหว่างที่ยังไม่เลือก ลูกค้ายังคุยเรื่องอื่นได้ตามปกติ<br />
                                • <strong>ไม่ยึดบทสนทนา</strong> — กรอกจบแล้ว · ยังไม่ได้เริ่มในห้องนี้ ·
                                หรือระบบยังไม่มีปีภาษี/ชุดคำถามที่เปิดใช้งาน ทั้งสามกรณีส่งต่อ agent อื่นได้<br />
                                • <strong>มี guardrail ของตัวเอง</strong> — ห้ามตอบแทนลูกค้าหรือเดาค่าที่ไม่มีในฟอร์ม
                            </div>
                        </v-alert>
                    </div>

                    <!-- Tool Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'tool'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-wrench" size="24" color="amber" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-amber mb-0">Tool Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Configure tool capabilities for ReAct agents</p>
                        </div>

                        <!-- Tool Name -->
                        <v-text-field v-model="nodeInspector.toolName" label="Tool Name"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('toolName', $event)"
                            class="modern-input mb-4" :readonly="isViewMode"
                            prepend-inner-icon="mdi-label"
                            hint="Short, descriptive name for the tool" persistent-hint />

                        <!-- Tool Description -->
                        <v-textarea v-model="nodeInspector.toolDescription" label="Tool Description"
                            variant="outlined" density="compact" rows="3" auto-grow hide-details="auto"
                            @update:model-value="updateNodeProperty('toolDescription', $event)"
                            class="modern-input mb-4" :readonly="isViewMode"
                            placeholder="Describe what this tool does, when to use it, and what inputs it expects..."
                            hint="This helps the agent decide when to use this tool" persistent-hint />

                        <!-- Tool Type -->
                        <v-select v-model="nodeInspector.toolType" label="Tool Type"
                            :items="toolTypes" item-title="text" item-value="value"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('toolType', $event)"
                            class="modern-input mb-4" :readonly="isViewMode"
                            prepend-inner-icon="mdi-cog">
                            <template v-slot:item="{ props, item }">
                                <v-list-item v-bind="props">
                                    <template v-slot:prepend>
                                        <v-icon :color="item.raw.color">{{ item.raw.icon }}</v-icon>
                                    </template>
                                    <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                </v-list-item>
                            </template>
                        </v-select>

                        <!-- Master Tool Selection -->
                        <div v-if="nodeInspector.toolType === 'master'" class="master-tool-section mb-4">
                            <h6 class="section-title mb-3">Master Tool Selection</h6>
                            
                            <v-autocomplete v-model="nodeInspector.masterToolId" 
                                :items="availableAgentTools" 
                                item-title="name" 
                                item-value="id"
                                label="Select Tool" 
                                variant="outlined" 
                                density="compact" 
                                hide-details="auto"
                                @update:model-value="updateNodeProperty('masterToolId', $event)"
                                class="modern-input" 
                                :readonly="isViewMode"
                                prepend-inner-icon="mdi-toolbox"
                                placeholder="Choose a predefined tool">
                                <template v-slot:item="{ props, item }">
                                    <v-list-item v-bind="props">
                                        <template v-slot:prepend>
                                            <v-icon size="20" color="blue">mdi-wrench</v-icon>
                                        </template>
                                        <v-list-item-subtitle>{{ item.raw.description }}</v-list-item-subtitle>
                                    </v-list-item>
                                </template>
                            </v-autocomplete>
                        </div>

                        <!-- Custom API Configuration -->
                        <div v-if="nodeInspector.toolType === 'api'" class="api-config-section mb-4">
                            <h6 class="section-title mb-3">Custom API Configuration</h6>
                            
                            <!-- API Endpoint -->
                            <v-text-field v-model="nodeInspector.endpoint" label="API Endpoint"
                                variant="outlined" density="compact" hide-details="auto"
                                @update:model-value="updateNodeProperty('endpoint', $event)"
                                class="modern-input mb-3" :readonly="isViewMode"
                                prepend-inner-icon="mdi-link"
                                placeholder="https://api.example.com/endpoint" />

                            <!-- HTTP Method -->
                            <v-select v-model="nodeInspector.method" label="HTTP Method"
                                :items="['GET', 'POST', 'PUT', 'DELETE', 'PATCH']"
                                variant="outlined" density="compact" hide-details="auto"
                                @update:model-value="updateNodeProperty('method', $event)"
                                class="modern-input mb-3" :readonly="isViewMode"
                                prepend-inner-icon="mdi-arrow-right-bold" />

                            <!-- Headers Configuration -->
                            <div class="headers-section mb-3">
                                <div class="section-header mb-2 d-flex justify-space-between align-center">
                                    <h6 class="section-title mb-0">Request Headers</h6>
                                    <v-btn v-if="!isViewMode" size="small" color="primary" variant="tonal" 
                                        @click="addToolHeader">
                                        <v-icon size="16" class="mr-1">mdi-plus</v-icon>
                                        Add Header
                                    </v-btn>
                                </div>
                                <div v-if="nodeInspector.headers && nodeInspector.headers.length > 0" class="headers-list">
                                    <div v-for="(header, index) in nodeInspector.headers" :key="index" 
                                        class="d-flex gap-2 mb-2 align-center">
                                        <v-text-field v-model="header.key" label="Header Key" 
                                            variant="outlined" density="compact" hide-details="auto"
                                            class="modern-input" :readonly="isViewMode"
                                            placeholder="Content-Type" />
                                        <v-text-field v-model="header.value" label="Header Value" 
                                            variant="outlined" density="compact" hide-details="auto"
                                            class="modern-input" :readonly="isViewMode"
                                            placeholder="application/json" />
                                        <v-btn v-if="!isViewMode" icon size="small" color="error" variant="text" 
                                            @click="removeToolHeader(index)">
                                            <v-icon size="18">mdi-delete</v-icon>
                                        </v-btn>
                                    </div>
                                </div>
                                <v-alert v-else type="info" variant="tonal" density="compact" class="mt-2">
                                    <div class="text-caption">No headers configured. Click "Add Header" to add custom headers.</div>
                                </v-alert>
                            </div>

                            <!-- Request Body -->
                            <v-textarea v-model="nodeInspector.body" label="Request Body (JSON)"
                                variant="outlined" density="compact" rows="6" auto-grow hide-details="auto"
                                @update:model-value="updateNodeProperty('body', $event)"
                                class="modern-input" :readonly="isViewMode"
                                placeholder='{\n  "key": "value",\n  "data": "{{InputMessage}}"\n}'
                                hint="Use {{variables}} for dynamic values" persistent-hint />
                        </div>

                        <!-- Text Information Configuration -->
                        <div v-if="nodeInspector.toolType === 'text'" class="text-info-section mb-4">
                            <h6 class="section-title mb-3">Text Information Content</h6>
                            
                            <v-textarea v-model="nodeInspector.textContent" label="Information Text"
                                variant="outlined" density="compact" rows="8" auto-grow hide-details="auto"
                                @update:model-value="updateNodeProperty('textContent', $event)"
                                class="modern-input" :readonly="isViewMode"
                                placeholder="Enter the information text that will be provided to the agent when this tool is used..."
                                hint="This text will be returned as the tool's result">
                                <template v-slot:prepend-inner>
                                    <v-icon color="green">mdi-text-box</v-icon>
                                </template>
                            </v-textarea>

                            <v-alert type="success" variant="tonal" density="compact" class="mt-3">
                                <template v-slot:prepend>
                                    <v-icon size="small">mdi-lightbulb-on</v-icon>
                                </template>
                                <div class="text-caption">
                                    <strong>Use Cases:</strong><br>
                                    • Company policies and guidelines<br>
                                    • Product specifications or documentation<br>
                                    • FAQ responses<br>
                                    • Reference information that doesn't require API calls
                                </div>
                            </v-alert>
                        </div>

                        <!-- Info Alert -->
                        <v-alert type="info" variant="tonal" density="compact">
                            <template v-slot:prepend>
                                <v-icon size="small">mdi-information</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>Tool Usage:</strong><br>
                                Connect this tool to an Agent node's bottom handle. The agent will decide when to use this tool.
                            </div>
                        </v-alert>
                    </div>

                    <!-- Flow Tool Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'flowTool'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <v-icon icon="mdi-sitemap" size="24" color="indigo" class="me-3"></v-icon>
                                <h5 class="text-h6 font-weight-bold text-indigo mb-0">Flow Tool Configuration</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Create a custom tool with internal flow logic</p>
                        </div>

                        <!-- Flow Tool Name -->
                        <v-text-field v-model="nodeInspector.flowToolName" label="Flow Tool Name"
                            variant="outlined" density="compact" hide-details="auto"
                            @update:model-value="updateNodeProperty('flowToolName', $event)"
                            class="modern-input mb-4" :readonly="isViewMode"
                            prepend-inner-icon="mdi-label"
                            placeholder="e.g., Calculate Loan Interest"
                            hint="Name that helps Agent decide when to use this tool" persistent-hint />

                        <!-- Flow Tool Description -->
                        <v-textarea v-model="nodeInspector.flowToolDescription" label="Tool Description"
                            variant="outlined" density="compact" rows="3" auto-grow hide-details="auto"
                            @update:model-value="updateNodeProperty('flowToolDescription', $event)"
                            class="modern-input mb-4" :readonly="isViewMode"
                            placeholder="Describe what this tool does, when the Agent should use it, and what kind of input it expects..."
                            hint="This helps the Agent understand when and how to use this tool" persistent-hint />

                        <!-- Internal Flow Stats -->
                        <div class="internal-flow-stats mb-4">
                            <v-card variant="outlined" class="pa-3">
                                <div class="d-flex align-center justify-space-between">
                                    <div class="d-flex align-center">
                                        <v-icon color="indigo" class="mr-2">mdi-chart-timeline-variant</v-icon>
                                        <div>
                                            <div class="text-subtitle-2 font-weight-bold">Internal Flow</div>
                                            <div class="text-caption text-medium-emphasis">
                                                {{ getFlowToolNodeCount() }} nodes configured
                                            </div>
                                        </div>
                                    </div>
                                    <v-chip v-if="!isFlowToolValid()" color="warning" size="small" variant="tonal">
                                        <v-icon size="14" class="mr-1">mdi-alert</v-icon>
                                        Incomplete
                                    </v-chip>
                                    <v-chip v-else color="success" size="small" variant="tonal">
                                        <v-icon size="14" class="mr-1">mdi-check</v-icon>
                                        Ready
                                    </v-chip>
                                </div>
                            </v-card>
                        </div>

                        <!-- Edit Internal Flow Button -->
                        <v-btn v-if="!isViewMode" color="indigo" variant="elevated" block 
                            @click="openFlowToolEditor" class="mb-4">
                            <v-icon class="mr-2">mdi-pencil-box-multiple</v-icon>
                            Edit Internal Flow
                        </v-btn>
                        <v-btn v-else color="indigo" variant="outlined" block 
                            @click="openFlowToolEditor" class="mb-4">
                            <v-icon class="mr-2">mdi-eye</v-icon>
                            View Internal Flow
                        </v-btn>

                        <!-- Validation Warning -->
                        <v-alert v-if="!isFlowToolValid()" type="warning" variant="tonal" density="compact" class="mb-4">
                            <template v-slot:prepend>
                                <v-icon size="small">mdi-alert</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>Flow Incomplete:</strong><br>
                                The internal flow needs a complete path from Input to Output node.
                            </div>
                        </v-alert>

                        <!-- Info Alert -->
                        <v-alert type="info" variant="tonal" density="compact">
                            <template v-slot:prepend>
                                <v-icon size="small">mdi-information</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>How Flow Tool Works:</strong><br>
                                • Agent receives user input and decides to use this tool<br>
                                • Input text is passed to the internal flow<br>
                                • Internal flow processes the input<br>
                                • Output is returned to the Agent for final response
                            </div>
                        </v-alert>
                    </div>

                    <!-- Flow Input Node Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'flowInput'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <div class="hexagon-icon-small bg-success me-3">
                                    <v-icon icon="mdi-login-variant" size="20" color="white"></v-icon>
                                </div>
                                <h5 class="text-h6 font-weight-bold text-success mb-0">Flow Input</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Entry point for the internal flow</p>
                        </div>

                        <!-- Info Card -->
                        <v-card variant="tonal" color="success" class="mb-4">
                            <v-card-text class="pa-3">
                                <div class="d-flex align-center mb-2">
                                    <v-icon color="success" class="mr-2">mdi-information</v-icon>
                                    <span class="text-subtitle-2 font-weight-bold">Input Data</span>
                                </div>
                                <div class="text-caption">
                                    This node receives the input text when the Flow Tool is executed by an Agent. 
                                    The input is passed through the <code>{{InputMessage}}</code> variable.
                                </div>
                            </v-card-text>
                        </v-card>

                        <!-- Info Alert -->
                        <v-alert type="info" variant="tonal" density="compact">
                            <template v-slot:prepend>
                                <v-icon size="small">mdi-lightbulb</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>Tips:</strong><br>
                                • Connect the output handle to your processing nodes<br>
                                • This is a fixed node and cannot be deleted<br>
                                • Only one Flow Input allowed per internal flow
                            </div>
                        </v-alert>
                    </div>

                    <!-- Flow Output Node Inspector Section -->
                    <div v-else-if="selectedNode.data.type === 'flowOutput'" class="form-section">
                        <!-- Header Section -->
                        <div class="config-header mb-6">
                            <div class="d-flex align-center mb-2">
                                <div class="hexagon-icon-small bg-orange me-3">
                                    <v-icon icon="mdi-logout-variant" size="20" color="white"></v-icon>
                                </div>
                                <h5 class="text-h6 font-weight-bold text-orange mb-0">Flow Output</h5>
                            </div>
                            <p class="text-body-2 text-medium-emphasis mb-0">Exit point for the internal flow</p>
                        </div>

                        <!-- Info Card -->
                        <v-card variant="tonal" color="orange" class="mb-4">
                            <v-card-text class="pa-3">
                                <div class="d-flex align-center mb-2">
                                    <v-icon color="orange" class="mr-2">mdi-information</v-icon>
                                    <span class="text-subtitle-2 font-weight-bold">Output Data</span>
                                </div>
                                <div class="text-caption">
                                    This node captures the final output of the internal flow. 
                                    The result will be returned to the Agent that called this Flow Tool.
                                </div>
                            </v-card-text>
                        </v-card>

                        <!-- Info Alert -->
                        <v-alert type="info" variant="tonal" density="compact">
                            <template v-slot:prepend>
                                <v-icon size="small">mdi-lightbulb</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>Tips:</strong><br>
                                • Connect the input handle from your final processing node<br>
                                • This is a fixed node and cannot be deleted<br>
                                • Only one Flow Output allowed per internal flow<br>
                                • Ensure there's a valid path from Flow Input to this node
                            </div>
                        </v-alert>
                    </div>

                </v-form>
            </div>
        </div>
        <!-- Loading Dialog -->
        <v-dialog v-model="dialog" persistent width="300">
            <v-card color="#1976D2" dark>
                <v-card-text class="white--text">
                    Loading Please Wait...
                    <v-progress-linear indeterminate color="#FAA91B" class="mb-0"></v-progress-linear>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Save Confirmation Dialog -->
        <v-dialog v-model="saveDialog" max-width="500" persistent>
            <v-card class="save-dialog">
                <v-card-title class="dialog-header">
                    <div class="header-content">
                        <v-icon class="mr-3" color="white" size="28">mdi-content-save</v-icon>
                        <span class="text-h5 font-weight-bold">{{ isEditMode ? 'Update Flow' : 'Save Flow' }}</span>
                    </div>
                </v-card-title>

                <v-card-text class="dialog-content">
                    <div class="confirmation-content">
                        <!-- Flow Name Display -->
                        <div class="flow-name-display">
                            <v-icon class="mr-3" color="primary" size="32">mdi-chart-timeline-variant-shimmer</v-icon>
                            <div>
                                <div class="flow-name">{{ flowName || 'Untitled Flow' }}</div>
                                <div class="flow-subtitle">{{ isEditMode ? 'Update this flow?' : 'Save this flow?' }}
                                </div>
                            </div>
                        </div>

                        <!-- Warning for validation errors -->
                        <v-alert v-if="getValidationErrors().length > 0" type="warning" variant="tonal"
                            density="compact" class="mt-4">
                            <template v-slot:prepend>
                                <v-icon>mdi-alert</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>Warning:</strong> Flow has {{ getValidationErrors().length }} validation
                                issue(s).
                                <br>You can still save, but the flow may not work correctly.
                            </div>
                        </v-alert>

                        <!-- Version Comment (Only for Edit Mode) -->
                        <div v-if="isEditMode" class="mt-4">
                            <v-textarea
                                v-model="saveFlowData.versionComment"
                                label="Version Note (Optional)"
                                placeholder="Describe what changed in this version..."
                                variant="outlined"
                                density="comfortable"
                                rows="3"
                                hide-details="auto"
                                color="primary"
                                class="version-comment-field"
                                maxlength="500"
                                counter="500"
                            >
                                <template v-slot:prepend-inner>
                                    <v-icon color="grey-darken-1" size="small">mdi-note-text-outline</v-icon>
                                </template>
                            </v-textarea>
                        </div>
                    </div>
                </v-card-text>

                <v-card-actions class="dialog-actions">
                    <v-btn v-if="isEditMode" @click="generateFlowVersionNote" variant="outlined" color="primary"
                        :disabled="saving || generatingNote || !flowId" :loading="generatingNote"
                        style="padding: 0 15px; border-radius: 8px;">
                        <v-icon class="mr-2">mdi-auto-fix</v-icon>
                        Review
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn @click="closeSaveDialog" variant="outlined" class="mr-2" :disabled="saving"
                        style="padding: 0 15px; border-radius: 8px;">
                        Cancel
                    </v-btn>
                    <v-btn @click="confirmSave" :loading="saving" color="success" variant="elevated"
                        style="padding: 0 15px; border-radius: 8px;">
                        <v-icon class="mr-2">{{ isEditMode ? 'mdi-content-save-edit' : 'mdi-content-save' }}</v-icon>
                        {{ isEditMode ? 'Update Flow' : 'Save Flow' }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Load from Model Dialog -->
        <v-dialog v-model="loadFromModelDialog" max-width="600" persistent>
            <v-card class="load-model-dialog">
                <v-card-title class="dialog-header">
                    <div class="header-content">
                        <v-icon class="mr-3" color="white" size="28">mdi-download</v-icon>
                        <span class="text-h5 font-weight-bold">Load from Existing Model</span>
                    </div>
                </v-card-title>

                <v-card-text class="dialog-content">
                    <div class="load-model-content">
                        <!-- Model Selection -->
                        <div class="model-selection-section">
                            <div class="section-title mb-3">Select Model to Load</div>
                            <v-autocomplete v-model="selectedModelToLoad" :items="uniqueAiModelOptions"
                                item-title="searchableText" item-value="modelId" label="Select Model" variant="outlined"
                                density="comfortable" hide-details="auto" prepend-inner-icon="mdi-robot" class="mb-4"
                                placeholder="Choose a model to load configuration from"
                                :loading="loadingAvailableModels" clearable
                                :custom-filter="(value, query, item) => {
                                    if (!query) return true;
                                    const searchQuery = query.toLowerCase();
                                    const itemValue = item?.raw?.value?.toString().toLowerCase() || '';
                                    const itemName = item?.raw?.name?.toLowerCase() || '';
                                    return itemValue.includes(searchQuery) || itemName.includes(searchQuery);
                                }">
                                <template v-slot:item="{ props, item }">
                                    <v-list-item v-bind="props" class="model-option"
                                        :title="item.raw.value + ': ' + item.raw.name">
                                        <template v-slot:prepend>
                                            <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                                <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                            </div>
                                        </template>
                                    </v-list-item>
                                </template>

                                <template v-slot:selection="{ item }">
                                    <v-chip variant="text" size="medium"
                                        :text="item.raw.value + ': ' + item.raw.name"></v-chip>
                                </template>
                            </v-autocomplete>
                        </div>

                        <!-- Configuration Preview -->
                        <div v-if="selectedModelToLoad && modelConfigPreview" class="config-preview-section">
                            <div class="section-title mb-3">Configuration Preview</div>
                            <v-card variant="outlined" class="config-preview-card">
                                <v-card-text class="pa-3">
                                    <div class="preview-grid">
                                        <div class="preview-item">
                                            <span class="preview-label">Provider:</span>
                                            <span class="preview-value">{{ modelConfigPreview.provider }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Model:</span>
                                            <span class="preview-value">{{ (modelConfigPreview.model ||
                                                '').split('|')[0]
                                                }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Temperature:</span>
                                            <span class="preview-value">{{ modelConfigPreview.temperature }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Max Tokens:</span>
                                            <span class="preview-value">{{ modelConfigPreview.maxTokens }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Top P:</span>
                                            <span class="preview-value">{{ modelConfigPreview.topP }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Frequency Penalty:</span>
                                            <span class="preview-value">{{ modelConfigPreview.frequencyPenalty }}</span>
                                        </div>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </div>

                        <!-- Warning -->
                        <v-alert v-if="selectedModelToLoad" type="warning" variant="tonal" density="compact"
                            class="mt-4">
                            <template v-slot:prepend>
                                <v-icon>mdi-alert</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>Note:</strong> Loading configuration will overwrite current AI model settings in
                                this
                                node.
                            </div>
                        </v-alert>
                    </div>
                </v-card-text>

                <v-card-actions class="dialog-actions">
                    <v-btn @click="closeLoadFromModelDialog" variant="outlined" class="mr-2"
                        :disabled="loadingModelConfig">
                        Cancel
                    </v-btn>
                    <v-btn @click="confirmLoadFromModel" :loading="loadingModelConfig" :disabled="!selectedModelToLoad"
                        color="success" variant="elevated">
                        <v-icon class="mr-2">mdi-download</v-icon>
                        Load Configuration
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Smart Intent Load from Model Dialog -->
        <v-dialog v-model="smartIntentLoadFromModelDialog" max-width="600" persistent>
            <v-card class="load-model-dialog">
                <v-card-title class="dialog-header">
                    <div class="header-content">
                        <v-icon class="mr-3" color="white" size="28">mdi-download</v-icon>
                        <span class="text-h5 font-weight-bold">
                            Load Config for {{ getLoadConfigTitle() }}
                        </span>
                    </div>
                </v-card-title>

                <v-card-text class="dialog-content">
                    <div class="load-model-content">
                        <!-- Model Selection -->
                        <div class="model-selection-section">
                            <div class="section-title mb-3">Select Model to Load</div>
                            <v-autocomplete v-model="selectedSmartIntentModelToLoad" :items="uniqueAiModelOptions"
                                item-title="searchableText" item-value="modelId" label="Select Model" variant="outlined"
                                density="comfortable" hide-details="auto" prepend-inner-icon="mdi-robot" class="mb-4"
                                placeholder="Choose a model to load configuration from"
                                :loading="loadingAvailableModels" clearable
                                :custom-filter="(value, query, item) => {
                                    if (!query) return true;
                                    const searchQuery = query.toLowerCase();
                                    const itemValue = item?.raw?.value?.toString().toLowerCase() || '';
                                    const itemName = item?.raw?.name?.toLowerCase() || '';
                                    return itemValue.includes(searchQuery) || itemName.includes(searchQuery);
                                }">
                                <template v-slot:item="{ props, item }">
                                    <v-list-item v-bind="props" class="model-option"
                                        :title="item.raw.value + ': ' + item.raw.name">
                                        <template v-slot:prepend>
                                            <div class="model-icon-small mr-2" style="background-color: #3b82f6;">
                                                <v-icon size="14" color="white">{{ item.raw.icon }}</v-icon>
                                            </div>
                                        </template>
                                    </v-list-item>
                                </template>

                                <template v-slot:selection="{ item }">
                                    <v-chip variant="text" size="medium"
                                        :text="item.raw.value + ': ' + item.raw.name"></v-chip>
                                </template>
                            </v-autocomplete>
                        </div>

                        <!-- Configuration Preview -->
                        <div v-if="selectedSmartIntentModelToLoad && smartIntentModelConfigPreview" class="config-preview-section">
                            <div class="section-title mb-3">Configuration Preview</div>
                            <v-card variant="outlined" class="config-preview-card">
                                <v-card-text class="pa-3">
                                    <div class="preview-grid">
                                        <div class="preview-item">
                                            <span class="preview-label">Provider:</span>
                                            <span class="preview-value">{{ smartIntentModelConfigPreview.provider }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Model:</span>
                                            <span class="preview-value">{{ (smartIntentModelConfigPreview.model ||
                                                '').split('|')[0]
                                                }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Temperature:</span>
                                            <span class="preview-value">{{ smartIntentModelConfigPreview.temperature }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Max Tokens:</span>
                                            <span class="preview-value">{{ smartIntentModelConfigPreview.maxTokens }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Top P:</span>
                                            <span class="preview-value">{{ smartIntentModelConfigPreview.topP }}</span>
                                        </div>
                                        <div class="preview-item">
                                            <span class="preview-label">Frequency Penalty:</span>
                                            <span class="preview-value">{{ smartIntentModelConfigPreview.frequencyPenalty }}</span>
                                        </div>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </div>

                        <!-- Warning -->
                        <v-alert v-if="selectedSmartIntentModelToLoad" type="warning" variant="tonal" density="compact"
                            class="mt-4">
                            <template v-slot:prepend>
                                <v-icon>mdi-alert</v-icon>
                            </template>
                            <div class="text-caption">
                                <strong>Note:</strong> Loading configuration will overwrite current AI settings for {{ getLoadConfigTitle() }}.
                            </div>
                        </v-alert>
                    </div>
                </v-card-text>

                <v-card-actions class="dialog-actions">
                    <v-btn @click="closeSmartIntentLoadFromModelDialog" variant="outlined" class="mr-2"
                        :disabled="loadingSmartIntentModelConfig">
                        Cancel
                    </v-btn>
                    <v-btn @click="confirmSmartIntentLoadFromModel" :loading="loadingSmartIntentModelConfig" :disabled="!selectedSmartIntentModelToLoad"
                        color="success" variant="elevated">
                        <v-icon class="mr-2">mdi-download</v-icon>
                        Load Configuration
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Success Snackbar -->
        <v-snackbar v-model="showSnackbar" :timeout="3000" :color="snackBarColor" location="bottom center">
            <v-icon class="me-2">mdi-check-circle</v-icon>
            {{ snackbarMessage }}
        </v-snackbar>

        <!-- Success Dialog -->
        <v-dialog v-model="successDialog" max-width="500" persistent>
            <v-card class="success-dialog">
                <v-card-title class="success-dialog-header">
                    <div class="success-header-content">
                        <v-icon class="success-icon" size="32">mdi-check-circle</v-icon>
                        <span class="success-title">Success!</span>
                    </div>
                </v-card-title>

                <v-card-text class="success-dialog-content">
                    <div class="success-message">
                        {{ successMessage }}
                    </div>

                    <div class="success-details">
                        <div class="success-detail-item">
                            <v-icon size="16" class="mr-2">mdi-chart-timeline-variant</v-icon>
                            <span>Flow: {{ flowName }}</span>
                        </div>
                        <div class="success-detail-item">
                            <v-icon size="16" class="mr-2">mdi-clock-outline</v-icon>
                            <span>{{ new Date().toLocaleString() }}</span>
                        </div>
                    </div>
                </v-card-text>

                <v-card-actions class="success-dialog-actions">
                    <v-spacer></v-spacer>
                    <v-btn @click="successDialog = false" color="success" variant="elevated" class="success-ok-btn">
                        <v-icon class="mr-2">mdi-check</v-icon>
                        OK
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Confirmation Exit Dialog -->
        <v-dialog v-model="confirmExitDialog" persistent max-width="500">
            <v-card class="confirmation-dialog">
                <v-card-title class="dialog-header d-flex align-center">
                    <v-icon color="warning" size="28" class="mr-3">mdi-alert-circle</v-icon>
                    <div class="header-content">
                        <h3 class="text-h5 mb-0">Confirm Navigation</h3>
                    </div>
                </v-card-title>

                <v-card-text class="dialog-content pt-6">
                    <div class="confirmation-content">
                        <p class="text-body-1 mb-1">
                            Are you sure you want to leave?
                        </p>
                        <p class="text-body-1 mb-4">
                            Any unsaved changes will be lost.
                        </p>
                        
                        <v-alert type="warning" variant="tonal" class="mb-0">
                            <template v-slot:prepend>
                                <v-icon>mdi-content-save-alert</v-icon>
                            </template>
                            <strong>Unsaved Changes:</strong> Your current flow modifications have not been saved.
                        </v-alert>
                    </div>
                </v-card-text>

                <v-card-actions class="dialog-actions pa-6">
                    <v-btn @click="confirmExit" color="warning" variant="elevated" class="elevation-2">
                        <v-icon class="mr-2" style="transform: scaleX(-1);">mdi-exit-to-app</v-icon>
                        Leave Anyway
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn @click="cancelExit" variant="outlined" color="primary" class="mr-3">
                        Stay Here
                        <v-icon class="ml-2">mdi-arrow-right</v-icon>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { MarkerType, VueFlow } from '@vue-flow/core'
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import { ref, computed, toRef, reactive } from "vue";

// Import styles
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import '@vue-flow/controls/dist/style.css'

import FlowNode from '@/components/FlowComponents/FlowNode.vue'
import FlowEdge from '@/components/FlowComponents/FlowEdge.vue'
import NodePanel from '@/components/FlowComponents/NodePanel.vue'
import DebugControlBar from '@/components/FlowComponents/DebugControlBar.vue'
import DebugInspector from '@/components/FlowComponents/DebugInspector.vue'
import { taxConfigApi } from '@/services/taxConfigApi'
import { taxPlanningSettingsV2Api } from '@/services/taxPlanningSettingsV2Api'
// App Knowledge Center: picker โหมด Center (เอกสาร/graph/หัวข้อ▸หมวด ของ Center เท่านั้น · โหมดปิด ⇒ null ⇒ $call เดิม)
import { kccPickerSource, kccUnavailableReason } from '@/services/kccPickerSource'
// การ์ด Tools ของโหนด Agent — ตัวเลือก/ตัวประกอบ ToolsConfig (hosted tool web_search)
import {
    TOOL_TYPE_OPTIONS,
    TOOL_CHOICE_OPTIONS,
    SEARCH_CONTEXT_SIZE_OPTIONS,
    WEB_SEARCH_COUNTRY_OPTIONS,
    WEB_SEARCH_TIMEZONE_OPTIONS,
    createWebSearchForm,
    capAllowedDomains,
    buildToolsConfig,
    parseToolsConfig,
} from '@/utils/toolsConfig'
// ค่า default ในตัวของ prompt ทุกช่องบนโหนด Orchestrator — เติมลงโหนดตั้งแต่ตอนสร้าง (ดู applyNodeDefaults)
import { ORCHESTRATOR_PROMPT_DEFAULTS } from '@/utils/orchestratorPromptDefaults'
// style JSON เริ่มต้นของโหนด Button Style — ใช้ร่วมกับ flowUtils
// + id ของ handle บน (input ตัวที่สอง) ของ Contact Agent V2 — สงวนให้ Orchestrator `agents-bottom` เท่านั้น
//   (ดู Rule 3 ใน validateConnection, validateHandleTypes และ validateInputLimit)
import { BUTTON_STYLE_DEFAULTS, CONTACT_AGENT_V2_TOP_HANDLE } from '@/utils/flowUtils'
// ระดับสิทธิ์ขั้นต่ำของ intent ใน Advanced Router / Dynamic Router — dropdown ทุกชั้นใน inspector
// ขั้นบันไดโหลดจาก GET api/v1/AccessLevel (ตั้งได้ที่ /AccessLevelConfig, #914) · ยังไม่โหลด/พลาด ⇒ ค่าในตัว T0–T3
import { accessLevelState, ensureAccessLevelsLoaded, accessLevelOptionsIncluding } from '@/utils/accessLevelStore'
// เช่นเดียวกันสำหรับโหนด Mutual Fund — คีย์ของไฟล์นี้เป็นชื่อ const แบบ camelCase ไม่ใช่คีย์บน node.data
// (systemPrompt -> node.data.systemPrompt · extractionTemplate -> node.data.mutualFundExtractionTemplate)
// จึงต้องสะกดคู่กันทุกครั้ง
import {
    MUTUAL_FUND_PROMPT_DEFAULTS,
    PROMPT_DEFAULT_ROWS as MUTUAL_FUND_PROMPT_ROWS,
} from '@/utils/mutualFundPromptDefaults'
// และโหนด Agent — prompt สรุปคำตอบสุดท้าย (finalSummaryTemplate -> node.data.agentFinalSummaryTemplate)
import {
    AGENT_PROMPT_DEFAULTS,
    PROMPT_DEFAULT_ROWS as AGENT_PROMPT_ROWS,
} from '@/utils/agentPromptDefaults'
import SummaryMemoryParserSection from '@/components/FlowComponents/SummaryMemoryParserSection.vue'

export default {
    name: "Flow",
    inject: ["flagNav"],
    components: {
        VueFlow,
        Background,
        Controls,
        FlowNode,
        FlowEdge,
        NodePanel,
        DebugControlBar,
        DebugInspector,
        SummaryMemoryParserSection,
    },
    provide() {
        return {
            lockFlowCreatorStatus: computed(() => this.lockStatus),
            aiModelOptions: computed(() => this.aiModelOptions),
            flowEdges: computed(() => this.tempEdge),
            availableAgentTools: computed(() => this.availableAgentTools),
            // Debug mode provided state
            isDebugMode: computed(() => this.isDebugMode),
            debugNodeStates: computed(() => this.debugNodeStates),
            debugBreakpoints: computed(() => this.debugBreakpoints),
            toggleDebugBreakpoint: (nodeId) => this.toggleDebugBreakpoint(nodeId),
            debugEdgeStates: computed(() => this.debugEdgeStates),
            // Set Next Statement injections
            debugSetNextTargetId: computed(() => this.debugOverrideNextNodeId),
            debugHoveredNodeId: computed(() => this.debugHoveredNodeId),
            debugStatus: computed(() => this.debugStatus),
        };
    },
    data() {
        return {
            // #924 — what is half-typed in the Mutual Fund "Min similarity" box; null = show the node's value.
            mutualFundFuzzyMinSimilarityDraft: null,
            // #924 — the same for the "ห่างจากอันดับ 1" box.
            mutualFundFuzzyTopGapDraft: null,
            InputMessageTagText: '{{InputMessage}}',
            VoicePromptTagText: '{{PromptVoice}}',
            LanguageTagText: '{{Language}}',
            InsuranceTagText: '{{insurance}}',
            interactiveControls: true,
            // Wheel input device detection (macOS): 'mouse' => scroll zooms (original behavior),
            // 'trackpad' => two-finger scroll pans. Drives panOnScrollActive.
            wheelDevice: 'mouse',
            lastWheelTs: 0,
            unableToAccessModel: [],
            // Cache Scope Options
            cacheScopeOptions: [
                { label: 'Flow Level (All Users)', value: 'flow' },
                { label: 'User Level (Per User)', value: 'user' }
            ],
            // Edit mode properties
            isEditMode: false,
            isViewMode: false,
            editFlowId: null,
            // Save/Load UI state
            saveDialog: false,
            saveFormValid: false,
            saving: false,
            loading: false,
            successDialog: false,

            // ============================================
            // Debug Mode State
            // ============================================
            isDebugMode: false,
            debugStatus: 'ready', // ready, running, paused, completed, error, stopped
            debugSessionId: '',
            debugSteps: [],
            debugNodeStates: {}, // { nodeId: 'idle'|'running'|'success'|'error'|'paused' }
            debugEdgeStates: {}, // { edgeId: 'active'|'completed'|'inactive' }
            debugBreakpoints: new Set(),
            debugMessage: '',
            debugError: '',
            debugCurrentNodeId: '',
            debugPreviousNodeId: '',
            debugPausedNodeName: '',
            debugStepCount: 0,
            debugTotalDuration: 0,
            debugVariables: {},
            debugChangedVariables: new Set(),
            debugLastBotReply: '', // Last bot reply message from debug execution
            debugStreaming: false, // True while GPT tokens are streaming in (voice-mode debug)
            debugStreamingNodeId: null, // Node currently streaming tokens (resets the reply bubble per node)
            debugSseReader: null, // SSE reader reference for cleanup
            debugExitDialog: false,
            // Debug patching state (collected while paused, sent on resume)
            debugPatchVariables: {},    // { key: value } — variable edits to send on resume
            debugOverrideNextNodeId: '', // node ID to jump to on resume
            debugHoveredNodeId: '',      // node ID currently hovered (for set-next overlay)
            debugKeepMemory: false,      // When true, keep same userId across debug runs (retains chat history)
            debugVoiceMode: false,       // When true, simulate voice input (IsVoiceInput + TextForSpeechResponse)
            debugVoiceLang: 'th',        // Voice language code (sourceLangVoice) when voice mode is on
            debugProactiveMessage: '',   // Optional proactive message sent as ContinueQuestion
            debugUserId: '',             // Generated GUID userId for debug sessions
            debugCif: '',                // Optional simulated CIF (debug only) — resolves {CIFNo} fallback
            debugUseCif: false,          // When ON, use the typed CIF AS the identity (userId) instead of a generated GUID
            debugAccessLevel: 'T3',      // Simulated user access level T0–T3 for Advanced/Dynamic Router (DebugFlowRequest.AccessLevel)

            // Flow Tool Editor state
            flowToolEditorDialog: false,
            internalFlowNodes: [],
            internalFlowEdges: [],
            selectedInternalNode: null,
            // Saved main flow state (for swap technique)
            savedMainNodes: [],
            savedMainEdges: [],
            editingFlowToolNode: null, // Reference to the FlowTool node being edited
            // Node types to exclude from internal flow editor
            internalFlowExcludedTypes: ['messageInput', 'response', 'flowTool'],
            // Load from Model state
            loadFromModelDialog: false,
            selectedModelToLoad: null,
            availableModels: [],
            modelConfigPreview: null,
            loadingAvailableModels: false,
            loadingModelConfig: false,
            // Smart Intent Load Config properties (separate from AI Model)
            smartIntentLoadFromModelDialog: false,
            selectedSmartIntentModelToLoad: null,
            smartIntentModelConfigPreview: null,
            loadingSmartIntentModelConfig: false,
            currentLoadingSmartIntentIndex: null, // Track which intent is being loaded
            // Confirmation dialog properties
            confirmExitDialog: false,
            pendingNavigation: null,
            isNavigatingAway: false, // Flag to prevent double confirmation
            // Save form data
            saveFlowData: {
                name: '',
                versionComment: '',
                saveAsTemplate: false,
                createBackup: true,
                publishFlow: false
            },
            // Review/Summarize changes
            generatingNote: false,
            generatedHistoryNote: '',
            fieldDiffs: [],
            // Flow metadata
            flowId: null,
            flowName: 'Untitled Flow',
            flowCreatedAt: null,

            isDraggingNode: false,
            dialog: false,
            showSnackbar: false,
            snackBarColor: 'success',
            snackbarMessage: '',
            inspectorExpanded: false,
            selectedNode: null,
            selectedNodes: [], // Track multiple selected nodes

            // Node Search (Ctrl+F)
            nodeSearchOpen: false,
            nodeSearchQuery: '',
            nodeSearchResults: [],
            nodeSearchCurrentIndex: -1,

            // Undo/Redo system
            history: [], // Stack for storing flow states
            historyIndex: -1, // Current position in history
            maxHistorySize: 50, // Maximum number of undo states
            isPerformingUndoRedo: false, // Flag to prevent infinite loops
            propertyUpdateTimer: null, // Timer for debouncing property updates
            isDraggingNodes: false, // Flag to track node dragging
            dragStartPositions: {}, // Store initial positions before drag

            // Copy & Paste system
            copiedNodes: [], // Store copied nodes data (in-memory fallback)
            copiedEdges: [], // Store copied edges between selected nodes (in-memory fallback)
            pasteOffset: 100, // Offset for pasted nodes
            clipboardType: 'fusion-chatframework/flow-clipboard', // Signature for system-clipboard payloads

            DDVector: [],
            // App Knowledge Center (kccPickerSource): kccCenterMode = status.enabled ⇒ picker แสดงเฉพาะของ Center ·
            // kcc*Unavailable / kccTaxonomyNotice = ชื่อ i18n ของข้อความใต้ picker ('' = ไม่มีอะไรต้องบอก)
            kccCenterMode: false,
            kccDocsUnavailable: '',
            kccGraphsUnavailable: '',
            kccTaxonomyNotice: '',
            // Meta Document taxonomy for the Tax Planning Agent knowledge picker (lazy-loaded once per page).
            taxKnowledgeHeaderRows: [],
            taxKnowledgeCategoryRows: [],
            taxKnowledgeLoading: false,
            taxKnowledgeLoaded: false,
            taxKnowledgeLoadError: false,
            taxKnowledgeSearch: '',
            taxKnowledgeOpenPanels: [],
            _taxKnowledgePending: 0,

            // Mutual Fund card-config library for the Mutual Fund node's three card pickers
            // (lazy-loaded once per page, on the first time that inspector is opened).
            mutualFundCardConfigs: [],
            mutualFundCardConfigsLoading: false,
            mutualFundCardConfigsLoaded: false,

            // Meta Documents node picker — its own search/open-panel state (the taxonomy ROWS above are
            // shared with the Tax Planning Agent picker; they are node-independent and lazy-loaded once).
            metaDocSearch: '',
            metaDocOpenPanels: [],
            DDDocumentGroup: [], // Document Groups for Flow integration
            DDCommunityGraph: [], // Community Graphs for GraphRAG
            DDMetaCommunityGraph: [], // Meta Community Graphs for Meta GraphRAG
            // Title/Sub-Title Filter - Hardcoded master data (same as Document Management)
            titleOptions: [
                { id: 'Product', name: 'Product', value: 'Product' },
                { id: 'ServiceInfo', name: 'ServiceInfo', value: 'ServiceInfo' },
                { id: 'Financial_Transaction', name: 'Financial_Transaction', value: 'Financial_Transaction' },
                { id: 'Promotion', name: 'Promotion', value: 'Promotion' },
                { id: 'Contact_Agent', name: 'Contact_Agent', value: 'Contact_Agent' },
                { id: 'Family_Banking', name: 'Family_Banking', value: 'Family_Banking' },
                { id: 'Troubleshooting', name: 'Troubleshooting', value: 'Troubleshooting' },
                { id: 'Greeting', name: 'Greeting', value: 'Greeting' },
                { id: 'Non_Banking', name: 'Non_Banking', value: 'Non_Banking' },
                { id: 'Triage_Agent', name: 'Triage_Agent', value: 'Triage_Agent' },
            ],
            titleSubTitleMap: {
                'Product': ['Deposit', 'Loan', 'Insurance', 'Card', 'Calc'],
                'ServiceInfo': ['Service_Info', 'Page_Navigation', 'Branch_Navigation'],
                'Financial_Transaction': ['Transfer', 'TopUp', 'Paybill'],
                'Promotion': [],
                'Contact_Agent': [],
                'Family_Banking': [],
                'Troubleshooting': [],
                'Greeting': [],
                'Non_Banking': [],
                'Triage_Agent': [],
            },
            filterModeOptions: [
                { label: 'Include (keep only matching)', value: 'include' },
                { label: 'Exclude (remove matching)', value: 'exclude' }
            ],
            listDataIntegration: [],
            DDGroupReplace: [],
            DDProductGroup: [],
            DDProductGroupCollection: [], // Product Group Collections for Flow integration
            listDDModel: [],
            listDDIntentGroup: [],
            listMonitorLineName: [],
            listDDProvider: [],
            DDModelShow: [],
            // Tax Planning — Tax Year options (loaded when inspector opens / mode = pinned)
            taxPlanningYearOptions: [],
            // Tax Planning V2 — settings versions (`tax.TaxPlanningSettingsV2`). The V2 node keeps
            // no config of its own; it only pins which settings version to resolve
            // (`taxPlanningV2SettingsVersionId`, null = whichever version is active).
            taxPlanningV2SettingsVersions: [],
            taxPlanningV2SettingsVersionsLoading: false,
            taxPlanningV2SettingsVersionsLoaded: false,
            DDTimeZone: [],
            // Interest Optimizer data
            interestMasterOptions: [],
            loadingInterestMaster: false,
            ioValidationFields: [
                { label: 'TotalAmount', value: 'TotalAmount' },
                { label: 'AccountCode', value: 'AccountCode' },
                { label: 'CategoryFilter', value: 'CategoryFilter' },
                { label: 'NumDays', value: 'NumDays' },
                { label: 'MonthlyDeposit', value: 'MonthlyDeposit' },
                { label: 'DurationMonths', value: 'DurationMonths' },
            ],
            liValidationFields: [
                { label: 'LoanAmount', value: 'LoanAmount' },
                { label: 'LoanTermMonths', value: 'LoanTermMonths' },
                { label: 'MonthlyPayment', value: 'MonthlyPayment' },
                { label: 'LoanType', value: 'LoanType' },
                { label: 'LoanName', value: 'LoanName' },
                { label: 'OccupationID', value: 'OccupationID' },
                { label: 'MonthlyIncome', value: 'MonthlyIncome' },
            ],
            // Loan Interest Optimizer data
            loanInterestRateOptions: [],
            loadingLoanInterestRates: false,
            modelID: null,
            LLmsModel: {
                provider: null,
                model: null
            },
            // Document data properties (similar to IntentSessionDetail.vue)
            documentDataTable: [],
            documentDataHeaders: [
                {
                    title: "",
                    key: "selected",
                    align: "center",
                    sortable: false,
                    width: "10px",
                },
                {
                    title: "Text Original",
                    key: "textORG",
                    align: "start",
                    sortable: true,
                    width: "50%",
                },
                {
                    title: "Text English",
                    key: "textEN",
                    align: "start",
                    sortable: true,
                    width: "50%",
                }
            ],
            documentDataItemsPerPage: 10,
            documentDataTotalTable: 0,
            documentDataOptions: {},
            loadingDocumentDataTable: false,
            showDocumentDataTable: false,
            selectedDocumentData: [],
            documentDataExpanded: false, // Control expand/collapse state
            // Model configuration panels control
            modelConfigPanels: ['basic'], // Default to basic panel open
            intentaiModelConfigPanels: ['intentaibasic'], // Default to basic panel open
            orchestratorConfigPanels: ['model'], // Default to model panel open for Orchestrator

            // Prompt template properties
            showPromptTem: false,
            promptTemplateDialog: false,
            promptTemplates: [],
            promptTemplateGroups: [],
            groupPromptTemplate: null,
            showMenu: false,
            x: 0,
            y: 0,
            
            // Smart Intent Prompt Template properties
            smartIntentPromptTemplateDialog: false,
            smartIntentCurrentIndex: null,
            smartIntentGroupPromptTemplate: null,
            smartIntentTemplateSearchQuery: '',
            
            // Smart Intent Fallback Prompt Template properties
            smartIntentFallbackPromptTemplateDialog: false,
            smartIntentFallbackGroupPromptTemplate: null,
            smartIntentFallbackTemplateSearchQuery: '',
            
            dialogShowAll: false,
            templateSearchQuery: '',

            // IntentAI Prompt template properties
            intentaiPromptTemplateDialog: false,
            showIntentaiMenu: false,
            dialogShowAllIntentai: false,
            intentaiTemplateSearchQuery: '',
            intentaiGroupPromptTemplate: null,

            // JSON Condition UI state
            jsonConditionExpandedPanel: null,
            // Service Matcher (unified) UI state — rule expansion panels for the
            // Favorite / Service rule blocks. Reuses jsonConditionOperators below.
            serviceMatcherFavoritePanel: null,
            serviceMatcherServicePanel: null,
            jsonConditionOperators: [
                { title: '── Comparison ──', value: '_header_compare', props: { disabled: true } },
                { title: 'Equals (=)', value: 'equals' },
                { title: 'Not Equals (≠)', value: 'not_equals' },
                { title: 'Greater Than (>)', value: 'greater_than' },
                { title: 'Less Than (<)', value: 'less_than' },
                { title: 'Greater or Equal (≥)', value: 'greater_equal' },
                { title: 'Less or Equal (≤)', value: 'less_equal' },
                { title: '── String ──', value: '_header_string', props: { disabled: true } },
                { title: 'Contains', value: 'contains' },
                { title: 'Not Contains', value: 'not_contains' },
                { title: 'Starts With', value: 'starts_with' },
                { title: 'Ends With', value: 'ends_with' },
                { title: 'Regex Match', value: 'regex' },
                { title: '── Existence ──', value: '_header_existence', props: { disabled: true } },
                { title: 'Is Empty', value: 'is_empty' },
                { title: 'Is Not Empty', value: 'is_not_empty' },
                { title: 'Exists', value: 'exists' },
                { title: 'Not Exists', value: 'not_exists' },
                { title: '── Array ──', value: '_header_array', props: { disabled: true } },
                { title: 'Array Empty', value: 'array_empty' },
                { title: 'Array Not Empty', value: 'array_not_empty' },
                { title: 'Array Contains', value: 'array_contains' },
                { title: 'Array Length Min (≥)', value: 'array_length_min' },
                { title: 'Array Length Max (≤)', value: 'array_length_max' },
                { title: 'Array Length Equals (=)', value: 'array_length_equals' },
                { title: 'Array All Unique', value: 'array_all_unique' },
                { title: '── Boolean ──', value: '_header_boolean', props: { disabled: true } },
                { title: 'Is True', value: 'is_true' },
                { title: 'Is False', value: 'is_false' },
                { title: '── Schema ──', value: '_header_schema', props: { disabled: true } },
                { title: 'Type Is', value: 'type_is' },
                { title: 'One Of (enum)', value: 'one_of' },
                { title: 'Not One Of', value: 'not_one_of' },
                { title: 'Format (email/uri/uuid/date)', value: 'format' },
                { title: '── Cross-Field ──', value: '_header_crossfield', props: { disabled: true } },
                { title: 'Equals Field', value: 'equals_field' },
                { title: 'Greater Than Field', value: 'greater_than_field' },
                { title: 'Less Than Field', value: 'less_than_field' },
            ],

            // Fallback Return UI state (reserved for future use)

            nodeInspector: {
                label: '',
                color: '#1976D2',
                placeholderText: '',
                required: false,
                selectedModel: null,
                systemPrompt: '',
                temperature: null,
                maxTokens: null,
                // AI Model Configuration properties
                provider: null,
                model: null,
                useBaseModelPrompt: false,
                gender: 'male',
                typingSpeed: null,
                maxInputLength: 200000,
                streamResponse: false,
                // Cache settings
                enableCache: false,
                cacheTTLMinutes: 1440,
                cacheScope: 'flow',
                enableSemanticCache: false,
                similarityThreshold: 0.95,
                similarityMargin: 0.05,
                enableTime: null,
                timeZone: null,
                enableTransactionId: null,
                enableInputMessageReplace: false,
                enableVoicePromptReplace: false,
                enableLanguageReplace: false,
                jsonResponse: false,
                memoryTurn: null,
                memoryDuration: null,
                shortTermMemory: null,
                longTermMemory: false,
                isChainMassage: false,
                lenChainMassage: null,
                timeSecondChainMassage: null,
                topP: null,
                frequencyPenalty: null,
                presencePenalty: null,
                stopSequence: null,
                loadingBar: false,
                loadingBarDuration: 0,
                monitorLineUserId: null,
                monitorSocialID: null,
                showLineName: false,
                forbiddenWords: [''],
                nodeTimeout: 0,
                nodeMaxRetry: 0,
                queryRewriterTimeout: 0,
                queryRewriterMaxRetry: 0,
                queryRewriterEnableLanguageReplace: false,
                intentaiTimeout: 0,
                intentaiMaxRetry: 0,
                intentGroupName: '',
                intents: [],
                // Guardrail AI properties
                guardrailType: 'content_safety',
                guardrailModel: 'gpt-4',
                guardrailPrompt: '',
                allowAgentGuardrailBypass: true,
                safetyThreshold: 80,
                enableLogging: true,
                // API Trigger properties
                apiUrl: '',
                httpMethod: 'POST',
                requestBody: '',
                headers: '',
                textResponse: '',
                jsonValidate: '{\n   "Name":"Value"\n}',
                modelJsonValidator: null,
                useLHBService: false,
                listHeadersAPITrigger: [
                    {
                        key: '',
                        value: '',
                    },
                ],
                listValidation: [
                    {
                        key: '',
                        typeValidation: '',
                        numberOfCharacter: '',
                        regExp: '',
                        promptMessage: '',
                    },
                ],
                // Contact Agent properties
                contactAgentMessage: '',
                contactAgentMessageChinese: '',
                contactAgentMessageEnglish: '',
                botDisableTime: null,
                // Interest Optimizer properties
                ioAnswerModel: null,
                ioFallbackPrompt: '',
                ioParserModel: null,
                ioDocuments: [],
                ioAccountCodes: [],
                ioRequiredFields: [],
                // Loan Interest properties
                liAnswerModel: null,
                liFallbackPrompt: '',
                liRequiredFields: [],
                liParserModel: null,
                liDocuments: [],
                liInterestRates: [],
                // Interest Optimizer V2 properties
                ioV2MetaDocs: [],
                // Loan Interest V2 properties
                liV2MetaDocs: [],
                // Switch properties
                switchName: '',
                switchOutputs: [],
                // Condition properties
                conditionType: 'value_comparison',
                leftOperand: '',
                operator: 'equals',
                rightOperand: '',
                conditionExpression: '',
                trueLabel: 'True',
                falseLabel: 'False',
                description: '',
                minValue: 0,
                maxValue: 100,
                expectedValue: true,
                // Delay properties
                delayType: 'fixed',
                delayValue: 5,
                delayUnit: 'seconds',
                randomMin: 1,
                randomMax: 10,
                delayMessage: '',
                showProgress: true,
                delayExpression: '',
                // Documents properties
                document: [],
                textReplacement: [],
                topNChunks: 5,
                cosineSimilarity: 0.7,
                // Summary Memory (Embedding) properties
                summaryMemoryEnabled: false,
                summaryMemoryProvider: null,
                summaryMemoryModel: null,
                summaryMemoryApiVersion: null,
                summaryMemoryJsonRequest: null,
                summaryMemorySystemPrompt: '',
                summaryMemoryTemperature: 0,
                summaryMemoryMaxTokens: 500,
                summaryMemoryTopP: 1,
                summaryMemoryFrequencyPenalty: null,
                summaryMemoryPresencePenalty: null,
                summaryMemoryMemoryTurn: 0,
                summaryMemoryMemoryDuration: 60,
                summaryMemoryTimeout: 0,
                selectedDocumentData: [],
                // Database Integration properties
                tableName: null,
                sqlExtractionModel: null,
                promptSqlNoData: '',
                // Products properties
                productSourceType: 'product', // 'product' | 'group'
                productGroupCollectionId: null,
                productGroupCollectionName: '',
                productGroupList: [],
                productTopNChunks: 5,
                productCosineSimilarity: 0.7,
                // Knowledge API properties
                knowledgeApiUrl: '',
                knowledgeHttpMethod: 'GET',
                knowledgeRequestBody: '',
                useLHBService: false,
                listHeaders: [
                    {
                        key: '',
                        value: '',
                    },
                ],
                responseDataPath: '',
                // SharePoint properties
                sharepoint: {
                    siteUrl: '',
                    tenantId: '',
                    clientId: '',
                    clientSecret: '',
                    folderPath: '',
                    filename: ''
                },
                // Email properties
                emailProvider: 'mailgun',
                toEmail: '',
                ccEmails: [],
                bccEmails: [],
                subject: '',
                emailTemplate: 'custom',
                emailBody: '',
                isHtml: false,
                // Smart Intent properties
                smartIntentGroupName: '',
                smartIntents: null,
                smartIntentFallback: {
                        useAI: false,
                        provider: '',
                        model: '',
                        temperature: 0,
                        maxTokens: 150,
                        topP: 1.0,
                        presencePenalty: 0.0,
                        frequencyPenalty: 0.0,
                        streamingEnabled: true,
                        lineFlexMessage: false,
                        systemPrompt: '',
                        forbiddenWords: [],
                        nodeTimeout: 0,
                    },
                // Orchestrator properties
                orchestratorName: '',
                orchestratorModel: null,
                confidenceThreshold: 0.7,
                fallbackAgentId: null,
                // Orchestrator - configurable prompts / messages.
                // 🔴 เติมข้อความ default ลงโหนดตั้งแต่ตอนสร้าง เพื่อให้ operator เห็นและแก้ของจริงได้ทันที
                //    (backend ยังตีความ "ว่าง = ใช้ default" อยู่ ⇒ โหนดเก่าที่เว้นว่างไว้ไม่กระทบ)
                //    ผลข้างเคียงที่ตั้งใจ: ค่าถูก freeze ลงโหนด ณ วันที่สร้าง ไม่ตาม default ในโค้ดอีกต่อไป
                orchSingleSelectionTemplate: '',
                orchPipelineSelectionTemplate: '',
                orchVerifySelectionTemplate: '',
                orchAgentListItemTemplate: '',
                orchAgentNoToolsLabel: '',
                orchConversationStateTemplate: '',
                orchConversationStateFactTemplate: '',
                orchCompanionNote: '',
                orchSequentialContextTemplate: '',
                orchCheckerGoalTemplate: '',
                orchFallbackSubGoal: '',
                orchAggregationTemplate: '',
                orchStrategyHintParallel: '',
                orchStrategyHintPipeline: '',
                orchAggregationFallbackItemTemplate: '',
                orchGuardrailFormatSuffix: '',
                orchGuardrailUserTemplate: '',
                orchDirectLlmPrompt: '',
                orchBlockedMessage: '',
                orchDirectLlmEmptyMessage: '',
                orchDirectLlmErrorMessage: '',
                orchNoAgentSucceededMessage: '',
                // Agent properties
                agentName: '',
                agentDescription: '',
                bypassGuardrailWhenAlone: true,
                taxKnowledgeEnabled: false,
                taxKnowledgeSelections: [],
                agentModel: null,
                personaPrompt: '',
                agentFinalSummaryTemplate: '',
                maxIterations: 5,
                enableAgentToolDecision: false,
                useCustomAIModel: false,
                agentProvider: '',
                agentTemperature: 0,
                agentMaxTokens: null,
                agentTopP: 1,
                agentFrequencyPenalty: null,
                agentPresencePenalty: null,
                agentStopSequence: null,
                agentStreamResponse: false,
                agentJsonResponse: false,
                agentGender: 'male',
                agentTypingSpeed: null,
                agentMaxInputLength: 200000,
                agentMemoryTurn: null,
                agentMemoryDuration: null,
                agentShortTermMemory: 200,
                agentLongTermMemory: false,
                agentEnableTime: false,
                agentTimeZone: null,
                agentEnableTransactionId: false,
                agentEnableLanguageReplace: false,
                agentEnableInsurance: false,
                agentTimeout: 0,
                agentMaxRetry: 0,
                // Agent Knowledge Connections
                knowledgeConnections: {
                    documents: { nodeId: null, description: '' },
                    databaseIntegration: { nodeId: null, description: '' },
                    mutualFund: { nodeId: null, description: '' },
                    knowledgeApi: { nodeId: null, description: '' },
                    products: { nodeId: null, description: '' },
                    sharepoint: { nodeId: null, description: '' }
                },
                // Agent Hosted Tools (การ์ด Tools) — toolsConfig (JSON string) คือค่าที่เก็บลง node data จริง
                // ส่วน toolsType / toolsChoice / toolsWebSearch เป็น state ของฟอร์ม แตกมาจาก toolsConfig ตอน hydrate
                toolsConfig: null,
                toolsType: null,
                toolsChoice: null,
                toolsWebSearch: createWebSearchForm(),
                // Tool properties
                toolName: '',
                toolDescription: '',
                toolType: 'master',
                masterToolId: null,
                endpoint: '',
                method: 'GET',
                headers: [],
                body: '',
            },
            // Tool type options
            toolTypes: [
                { 
                    text: 'Master Tool', 
                    value: 'master',
                    icon: 'mdi-toolbox',
                    color: 'blue',
                    description: 'Select from predefined tools in Tool Management'
                },
                { 
                    text: 'Custom API Call', 
                    value: 'api',
                    icon: 'mdi-api',
                    color: 'orange',
                    description: 'Configure custom REST API endpoint'
                },
                { 
                    text: 'Text Information', 
                    value: 'text',
                    icon: 'mdi-text-box',
                    color: 'green',
                    description: 'Provide static text information to the agent'
                }
            ],
            // ตัวเลือกของการ์ด Tools (hosted tools ของโหนด Agent) — มาจาก @/utils/toolsConfig
            // ⚠️ ระวังอย่าสับสนกับ toolTypes ด้านบน ซึ่งเป็น tool type ของโหนด Tool (master/api/text) คนละเรื่องกัน
            toolTypeOptions: TOOL_TYPE_OPTIONS,
            toolChoiceOptions: TOOL_CHOICE_OPTIONS,
            webSearchContextOptions: SEARCH_CONTEXT_SIZE_OPTIONS,
            webSearchCountryOptions: WEB_SEARCH_COUNTRY_OPTIONS,
            webSearchTimezoneOptions: WEB_SEARCH_TIMEZONE_OPTIONS,
            // ค่า default ในตัวของโหนด Orchestrator (อ่านอย่างเดียว ไม่ถูก save ลง node) — เปิดให้ template ใช้เป็น placeholder/rows
            // Available Agent Tools (loaded from API)
            availableAgentTools: [],
            tempNode: [],
            tempEdge: [],
            uniqueAiModelOptions: [],
            aiModelOptions: [],
            DDTypeValidation: [
                {
                    name: "Phone",
                    value: "PV",
                },
                {
                    name: "Email",
                    value: "EV",
                },
                {
                    name: "Max Character",
                    value: "MCV",
                },
                {
                    name: "Regular Expression",
                    value: "RV",
                },
            ],

            // Variables list for JSON editor
            availableVariables: [
                {
                    key: '{CIFNo}',
                    label: '(LHB) CIF No',
                    description: 'Customer Identification File Number',
                    icon: 'mdi-account-card',
                    category: 'LHB'
                },
                {
                    key: '{Segment}',
                    label: '(LHB) Segment',
                    description: 'Customer Segment',
                    icon: 'mdi-account-group',
                    category: 'LHB'
                },
                {
                    key: '{WealthMembership}',
                    label: '(LHB) Wealth Membership',
                    description: 'Wealth Membership Level',
                    icon: 'mdi-diamond-stone',
                    category: 'LHB'
                },
                {
                    key: '{Screen}',
                    label: '(LHB) Screen',
                    description: 'Current Screen Name',
                    icon: 'mdi-monitor',
                    category: 'LHB'
                },
                {
                    key: '{Language}',
                    label: '(LHB) Language',
                    description: 'Current Language',
                    icon: 'mdi-translate',
                    category: 'LHB'
                },
                {
                    key: '{transactionID}',
                    label: 'Transaction ID',
                    description: 'Unique transaction identifier',
                    icon: 'mdi-identifier',
                    category: 'System'
                },
                {
                    key: '{channel}',
                    label: 'Channel',
                    description: 'Current communication channel',
                    icon: 'mdi-forum',
                    category: 'System'
                },
                {
                    key: '{channelID}',
                    label: 'Channel ID',
                    description: 'Channel identifier',
                    icon: 'mdi-pound',
                    category: 'System'
                },
                {
                    key: '{userChannel}',
                    label: 'User Channel',
                    description: 'User specific channel',
                    icon: 'mdi-account-circle',
                    category: 'User'
                },
                {
                    key: '{sessionID}',
                    label: 'Session ID',
                    description: 'User session identifier',
                    icon: 'mdi-clock-outline',
                    category: 'Session'
                }
            ],

            // Variables panel state
            variablesPanelExpanded: false,
            selectedVariableCategory: 'All',
        }
    },
    computed: {
        // Access Level options for Advanced/Dynamic Router intents — ladder ปัจจุบันจาก store (reactive หลังโหลด, #914)
        // dropdown ใน inspector ใช้ accessLevelOptionsIncluding(raw) เพื่อให้ค่าที่ไม่รู้จักยังแสดงอยู่
        accessLevelOptions() {
            return accessLevelState.options;
        },
        // ══ Mutual Fund — built-in prompt defaults, exposed to the inspector ════════════════════════════════
        // The node inspector shows these as the PLACEHOLDER of an empty prompt field (blank = built-in), and
        // uses the row counts as the textarea `rows` so the whole placeholder is visible — an empty textarea
        // cannot be scrolled, so a box shorter than its placeholder hides the rest of the text.
        mutualFundPromptDefaults() {
            return MUTUAL_FUND_PROMPT_DEFAULTS;
        },
        mutualFundPromptRows() {
            return MUTUAL_FUND_PROMPT_ROWS;
        },
        // ══ Agent — same arrangement for the final-summary prompt ══════════════════════════════════════════
        agentPromptDefaults() {
            return AGENT_PROMPT_DEFAULTS;
        },
        agentPromptRows() {
            return AGENT_PROMPT_ROWS;
        },
        // (mutualFundScopeChips / setMutualFundScopeChips are gone: the resolver no longer prints a fund list
        //  into the prompt, so there is no candidate scope to edit. node.data still carries the old key.)
        // App Knowledge Center: ข้อความตอน dropdown ว่าง — โหมด Center ที่ดึงรายการไม่ได้ ⇒ บอกเหตุผล · อื่น ๆ ⇒ ค่าตั้งต้น
        // ของ Vuetify ('$vuetify.noDataText' = เท่าเดิมทุกตัวอักษร)
        kccDocsNoDataText() {
            return this.kccDocsUnavailable ? this.$t(this.kccDocsUnavailable) : '$vuetify.noDataText';
        },
        kccGraphsNoDataText() {
            return this.kccGraphsUnavailable ? this.$t(this.kccGraphsUnavailable) : '$vuetify.noDataText';
        },
        // ══ Meta Document taxonomy, as a tree the picker can render ═════════════════════════════════════════
        // Built from the two flat lists the API returns. Inactive rows are dropped here rather than at each
        // reader, so a category an admin has deactivated cannot be newly picked — while one that was ALREADY
        // picked keeps showing as a chip (see taxKnowledgeSelectedChips), because silently dropping a saved
        // selection would change what an agent reads without anybody touching the flow.
        taxKnowledgeTree() {
            const bySlot = {};
            (this.taxKnowledgeCategoryRows || []).forEach(r => {
                if (!r || r.isActive === false) return;
                const parent = r.categoryTitle || r.CategoryTitle;
                const key = r.key || r.Key;
                if (!parent || !key) return;
                (bySlot[parent] = bySlot[parent] || []).push({
                    key: key,
                    code: r.code || r.Code || '',
                    order: r.displayOrder != null ? r.displayOrder : (r.DisplayOrder || 0),
                });
            });

            const headers = (this.taxKnowledgeHeaderRows || [])
                .filter(h => h && h.isActive !== false && (h.title || h.Title))
                .map(h => {
                    const title = h.title || h.Title;
                    const cats = (bySlot[title] || []).slice()
                        .sort((a, b) => (a.order - b.order) || a.key.localeCompare(b.key));
                    return {
                        title: title,
                        code: h.code || h.Code || '',
                        order: h.displayOrder != null ? h.displayOrder : (h.DisplayOrder || 0),
                        categories: cats,
                    };
                })
                .sort((a, b) => (a.order - b.order) || a.title.localeCompare(b.title));

            // A header the sub-category list references but the header list does not carry — keeps the picker
            // honest about data that already exists rather than hiding categories nobody could otherwise reach.
            const known = new Set(headers.map(h => h.title));
            Object.keys(bySlot).forEach(title => {
                if (known.has(title)) return;
                headers.push({
                    title: title, code: '', order: 9999,
                    categories: bySlot[title].slice().sort((a, b) => (a.order - b.order) || a.key.localeCompare(b.key)),
                });
            });
            return headers;
        },

        /// One search box over BOTH levels: a header matches by its own name, and it also survives when any of its
        /// categories match — with `categories` narrowed to the matches so the open panel shows only what was
        /// searched for. Matching is done on the pretty (space-separated) form too, so typing "income tax" finds
        /// Personal_Income_Tax.
        taxKnowledgeFilteredHeaders() {
            const q = (this.taxKnowledgeSearch || '').trim().toLowerCase();
            const tree = this.taxKnowledgeTree;
            if (!q) return tree;
            const hit = (v) => {
                const raw = (v || '').toLowerCase();
                return raw.indexOf(q) !== -1 || raw.replace(/_/g, ' ').indexOf(q) !== -1;
            };
            const out = [];
            tree.forEach(h => {
                if (hit(h.title) || hit(h.code)) { out.push(h); return; }
                const cats = h.categories.filter(c => hit(c.key) || hit(c.code));
                if (cats.length > 0) out.push(Object.assign({}, h, { categories: cats }));
            });
            return out;
        },

        taxKnowledgeSelectionCount() {
            const list = this.nodeInspector && this.nodeInspector.taxKnowledgeSelections;
            return Array.isArray(list) ? list.length : 0;
        },

        /// 🔴 Rendered from the SAVED selections, not from the tree, so a selection whose category has since been
        ///    deactivated or renamed still appears — labelled with what is stored. An author who cannot see a
        ///    saved value cannot remove it, and the agent would go on reading it forever.
        taxKnowledgeSelectedChips() {
            const list = (this.nodeInspector && this.nodeInspector.taxKnowledgeSelections) || [];
            if (!Array.isArray(list)) return [];
            return list.filter(s => s && s.header).map(s => {
                const cat = s.category || null;
                return {
                    key: s.header + ' ' + (cat || '*'),
                    header: s.header,
                    category: cat,
                    label: cat
                        ? this.prettyTaxonomy(s.header) + ' · ' + this.prettyTaxonomy(cat)
                        : this.prettyTaxonomy(s.header) + ' · ทั้งหัวข้อ',
                };
            });
        },

        // ══ Meta Documents node picker — same tree (taxKnowledgeTree is node-independent), its own
        //    search box and selection key (nodeInspector.metaDocSelections). Chips render from SAVED
        //    selections, not the tree, for the same reason as the tax picker: a deactivated/renamed
        //    category must stay visible and removable.
        metaDocFilteredHeaders() {
            const q = (this.metaDocSearch || '').trim().toLowerCase();
            const tree = this.taxKnowledgeTree;
            if (!q) return tree;
            const hit = (v) => {
                const raw = (v || '').toLowerCase();
                return raw.indexOf(q) !== -1 || raw.replace(/_/g, ' ').indexOf(q) !== -1;
            };
            const out = [];
            tree.forEach(h => {
                if (hit(h.title) || hit(h.code)) { out.push(h); return; }
                const cats = h.categories.filter(c => hit(c.key) || hit(c.code));
                if (cats.length > 0) out.push(Object.assign({}, h, { categories: cats }));
            });
            return out;
        },

        metaDocSelectionCount() {
            const list = this.nodeInspector && this.nodeInspector.metaDocSelections;
            return Array.isArray(list) ? list.length : 0;
        },

        metaDocSelectedChips() {
            const list = (this.nodeInspector && this.nodeInspector.metaDocSelections) || [];
            if (!Array.isArray(list)) return [];
            return list.filter(s => s && s.header).map(s => {
                const cat = s.category || null;
                return {
                    key: s.header + ' ' + (cat || '*'),
                    header: s.header,
                    category: cat,
                    label: cat
                        ? this.prettyTaxonomy(s.header) + ' · ' + this.prettyTaxonomy(cat)
                        : this.prettyTaxonomy(s.header) + ' · ทั้งหัวข้อ',
                };
            });
        },

        // Enable trackpad pan-on-scroll only on macOS, where Vue Flow also treats a
        // pinch (ctrlKey wheel event) as zoom. On other platforms this stays false so the
        // existing mouse-wheel zoom behavior is unchanged.
        isMacOs() {
            return typeof navigator !== 'undefined'
                && (navigator.userAgent || '').indexOf('Mac') >= 0;
        },
        // Pan-on-scroll is enabled only on macOS AND only while the current wheel input looks
        // like a trackpad. A physical mouse wheel keeps the original scroll-to-zoom behavior.
        panOnScrollActive() {
            return this.isMacOs && this.wheelDevice === 'trackpad';
        },
        // ── Tax Planning V2 — settings-version picker ────────────────────────────────
        // All config for the `taxPlanningV2` node lives in `tax.TaxPlanningSettingsV2`
        // (versioned). The node inspector only chooses WHICH version to resolve, so these
        // computeds shape the dropdown + the "currently using" summary card.
        taxPlanningV2ActiveSettingsVersion() {
            return (this.taxPlanningV2SettingsVersions || []).find(v => v.isActive) || null;
        },
        // First option is always the "auto" entry (value === null) and it is the default:
        // an empty pin means "resolve the active version at runtime".
        taxPlanningV2SettingsVersionOptions() {
            const active = this.taxPlanningV2ActiveSettingsVersion;
            const auto = {
                value: null,
                label: 'ใช้เวอร์ชันที่ใช้งานอยู่ (อัตโนมัติ)',
                subtitle: active
                    ? `ตอนนี้คือ ${active.versionCode}`
                    : 'ระบบจะเลือกเวอร์ชันที่ active ให้ตอนรัน',
                isActive: false
            };
            const versions = (this.taxPlanningV2SettingsVersions || []).map(v => ({
                value: v.id,
                label: v.versionCode,
                subtitle: [
                    v.description,
                    v.updatedBy ? `แก้ไขล่าสุดโดย ${v.updatedBy}` : ''
                ].filter(Boolean).join(' · '),
                isActive: v.isActive
            }));
            return [auto, ...versions];
        },
        // Normalize the raw node value: undefined / '' are treated the same as null (= auto).
        taxPlanningV2PinnedSettingsVersionId() {
            const raw = this.nodeInspector ? this.nodeInspector.taxPlanningV2SettingsVersionId : null;
            return (raw === undefined || raw === '') ? null : (raw ?? null);
        },
        taxPlanningV2EffectiveSettingsVersion() {
            const pinned = this.taxPlanningV2PinnedSettingsVersionId;
            if (pinned === null) return this.taxPlanningV2ActiveSettingsVersion;
            return (this.taxPlanningV2SettingsVersions || []).find(v => v.id === pinned) || null;
        },
        taxPlanningV2EffectiveVersionLabel() {
            const version = this.taxPlanningV2EffectiveSettingsVersion;
            if (version) return version.versionCode + (version.isActive ? ' (ใช้งานอยู่)' : '');
            if (this.taxPlanningV2SettingsVersionsLoading) return 'กำลังโหลด…';
            return 'เวอร์ชันที่ใช้งานอยู่';
        },
        // Pinned to a version that no longer exists → backend falls back to the active one.
        // Only warn once the list actually loaded, so a failed/pending fetch never cries wolf.
        taxPlanningV2PinnedVersionMissing() {
            const pinned = this.taxPlanningV2PinnedSettingsVersionId;
            if (pinned === null) return false;
            if (!this.taxPlanningV2SettingsVersionsLoaded) return false;
            return !(this.taxPlanningV2SettingsVersions || []).some(v => v.id === pinned);
        },
        hasConnecteddynamicRouter() {
            if (!this.selectedNode) return false;
            return this.tempEdge.some(e => {
                const sourceNode = this.tempNode.find(n => n.id === e.source);
                return e.target === this.selectedNode.id && sourceNode?.data?.type === 'dynamicRouter';
            });
        },
        // Derive set-next target display name for DebugControlBar
        debugSetNextTargetName() {
            if (!this.debugOverrideNextNodeId) return '';
            const node = this.tempNode.find(n => n.id === this.debugOverrideNextNodeId);
            return node?.data?.label || node?.data?.name || this.debugOverrideNextNodeId;
        },
        // uniqueAiModelOptions() {
        //     const seen = new Set();
        //     return this.aiModelOptions.filter(option => {
        //         const duplicate = seen.has(option.value);
        //         seen.add(option.value);
        //         return !duplicate;
        //     });
        // },
        
        uniqueProviderOptions() {
            // Extract unique providers from AI model options
            const providers = new Set();
            this.uniqueAiModelOptions.forEach(option => {
                if (option.value && option.value.includes('|')) {
                    const provider = option.value.split('|')[0];
                    providers.add(provider);
                } else if (option.name) {
                    // Fallback to extracting from name
                    const parts = option.name.split(' ');
                    if (parts.length > 0) {
                        providers.add(parts[0]);
                    }
                }
            });
            return Array.from(providers).sort();
        },
        
        filteredPromptTemplates() {

            if (this.groupPromptTemplate === null) {
                return [];
            }
            return this.DDPromptTemplate.filter(
                (item) => item.groupPromptTemplateID === this.groupPromptTemplate
            );
        },

        searchedTemplates() {
            if (!this.templateSearchQuery) {
                return this.filteredPromptTemplates;
            }

            const query = this.templateSearchQuery.toLowerCase();
            return this.filteredPromptTemplates.filter(template =>
                template.name.toLowerCase().includes(query) ||
                (template.prompt && template.prompt.toLowerCase().includes(query))
            );
        },

        // IntentAI Prompt Template computed properties
        filteredIntentaiPromptTemplates() {
            if (this.intentaiGroupPromptTemplate === null) {
                return [];
            }
            return this.DDPromptTemplate.filter(
                (item) => item.groupPromptTemplateID === this.intentaiGroupPromptTemplate
            );
        },

        searchedIntentaiTemplates() {
            if (!this.intentaiTemplateSearchQuery) {
                return this.filteredIntentaiPromptTemplates;
            }

            const query = this.intentaiTemplateSearchQuery.toLowerCase();
            return this.filteredIntentaiPromptTemplates.filter(template =>
                template.name.toLowerCase().includes(query) ||
                (template.prompt && template.prompt.toLowerCase().includes(query))
            );
        },

        // Smart Intent Prompt Template computed properties
        filteredSmartIntentPromptTemplates() {
            if (this.smartIntentGroupPromptTemplate === null) {
                return [];
            }
            return this.DDPromptTemplate.filter(
                (item) => item.groupPromptTemplateID === this.smartIntentGroupPromptTemplate
            );
        },

        smartIntentSearchedTemplates() {
            if (!this.smartIntentTemplateSearchQuery) {
                return this.filteredSmartIntentPromptTemplates;
            }

            const query = this.smartIntentTemplateSearchQuery.toLowerCase();
            return this.filteredSmartIntentPromptTemplates.filter(template =>
                template.name.toLowerCase().includes(query) ||
                (template.prompt && template.prompt.toLowerCase().includes(query))
            );
        },

        // Smart Intent Fallback Prompt Template computed properties
        filteredSmartIntentFallbackPromptTemplates() {
            if (this.smartIntentFallbackGroupPromptTemplate === null) {
                return [];
            }
            return this.DDPromptTemplate.filter(
                (item) => item.groupPromptTemplateID === this.smartIntentFallbackGroupPromptTemplate
            );
        },

        smartIntentFallbackSearchedTemplates() {
            if (!this.smartIntentFallbackTemplateSearchQuery) {
                return this.filteredSmartIntentFallbackPromptTemplates;
            }

            const query = this.smartIntentFallbackTemplateSearchQuery.toLowerCase();
            return this.filteredSmartIntentFallbackPromptTemplates.filter(template =>
                template.name.toLowerCase().includes(query) ||
                (template.prompt && template.prompt.toLowerCase().includes(query))
            );
        },

        // Filter variables by category
        filteredVariables() {
            if (this.selectedVariableCategory === 'All') {
                return this.availableVariables;
            }
            return this.availableVariables.filter(v => v.category === this.selectedVariableCategory);
        },

        // Get unique categories
        variableCategories() {
            const categories = ['All', ...new Set(this.availableVariables.map(v => v.category))];
            return categories;
        },
        lockStatus() {
            // ตรวจสอบว่า instance ของ vueFlow พร้อมใช้งานหรือไม่
            if (this.$refs.vueFlow) {
                // ปุ่ม lock จะควบคุมค่า nodesDraggable เราจึงใช้ค่านี้เป็นตัวชี้วัดสถานะ
                // ถ้า nodesDraggable เป็น false หรือ undefined ๝สดงว่า "Locked"
                const nodesDraggable = this.$refs.vueFlow.nodesDraggable;
                return nodesDraggable === false;
            }
            // ค่าเริ่มต้นฝ่อนที่ component จะ mount (unlocked)
            return false;
        },
        DDTimeZone() {
            return [
                {
                    value: "Asia/Bangkok",
                    name: "Thailand",
                },
                {
                    value: "Asia/Hong_Kong",
                    name: "Hong Kong",
                },
                {
                    value: "Asia/Tokyo",
                    name: "Japan",
                },
                {
                    value: "America/New_York",
                    name: "USA",
                },
                {
                    value: "Europe/London",
                    name: "England",
                },
                {
                    value: "Australia/Sydney",
                    name: "Australia",
                },
            ];
        },
        // Undo/Redo computed properties
        canUndo() {
            return this.historyIndex > 0;
        },
        canRedo() {
            return this.historyIndex < this.history.length - 1;
        }
    },
    watch: {
        // Watch for changes in document selection to fetch document data
        'nodeInspector.documentVectorName'(newValue) {
            if (newValue && this.nodeInspector.enableSpecificDataSelection) {
                this.getDocumentData();
            } else {
                this.resetDocumentDataTable();
            }
        },

        // Watch for model selection to show preview
        selectedModelToLoad(newValue) {
            if (newValue) {
                this.loadModelConfigPreview(newValue);
            } else {
                this.modelConfigPreview = null;
            }
        },

        // Watch for Smart Intent model selection to show preview
        selectedSmartIntentModelToLoad(newValue) {
            if (newValue) {
                this.loadSmartIntentModelConfigPreview(newValue);
            } else {
                this.smartIntentModelConfigPreview = null;
            }
        },

        // Watch for uniqueAiModelOptions to be loaded and filter messageInput models if needed
        uniqueAiModelOptions(newValue) {
            if (newValue && newValue.length > 0 && this.tempNode && this.tempNode.length > 0) {
                // Check if there are messageInput nodes that need filtering
                let hasUnfilteredMessageInput = this.tempNode.some(node =>
                    node.data &&
                    node.data.type === 'messageInput' &&
                    node.data.selectedModel &&
                    node.data.selectedModel.length > 0
                );

                if (hasUnfilteredMessageInput) {
                    console.log('AI Models loaded, applying filter to messageInput nodes');
                    this.filterMessageInputModels();
                }
            }
        },

        // Removed: No longer need to watch communityId - using hardcoded master data
    },
    methods: {
        // Classify a wheel event as coming from a physical mouse wheel or a trackpad.
        // Mouse wheels notch in multiples of 120 (wheelDeltaY) with no horizontal component;
        // trackpads emit pixel-granularity / fractional deltas and horizontal scroll.
        classifyWheelDevice(e) {
            // Line / page granularity is only produced by a classic mouse wheel (e.g. Firefox).
            if (e.deltaMode !== 0) return 'mouse';
            // Any horizontal component means a two-finger trackpad swipe.
            if (e.deltaX !== 0) return 'trackpad';
            // Chromium / WebKit expose wheelDeltaY; physical wheel notches are multiples of 120.
            const wdy = e.wheelDeltaY;
            if (typeof wdy === 'number' && wdy !== 0) {
                return (Math.abs(wdy) % 120 === 0) ? 'mouse' : 'trackpad';
            }
            // Fallback: fractional pixel deltas look like a trackpad.
            if (!Number.isInteger(e.deltaY)) return 'trackpad';
            return 'mouse';
        },
        // Capture-phase wheel handler (runs before Vue Flow's own handler). On macOS it decides
        // whether the current input is a trackpad (pan) or a mouse wheel (zoom) and flips
        // panOnScrollActive accordingly. The single event on which the mode flips is swallowed so
        // the user never sees one wrong-direction tick (a zoom blip when starting to pan, etc.).
        // No-op on non-macOS so mouse-wheel zoom there is completely untouched.
        onFlowWheelCapture(e) {
            if (!this.isMacOs) return;
            const now = (typeof performance !== 'undefined' && performance.now)
                ? performance.now() : Date.now();
            const idle = (now - this.lastWheelTs) > 150;
            this.lastWheelTs = now;
            const cls = this.classifyWheelDevice(e);
            let next = this.wheelDevice;
            if (idle) {
                // Start of a new gesture: trust this event's classification.
                next = cls;
            } else if (cls === 'trackpad') {
                // A definitive trackpad signal upgrades immediately; resist downgrading to
                // 'mouse' mid-gesture so a stray notch-like tick during a fast flick can't flip us.
                next = 'trackpad';
            }
            if (next !== this.wheelDevice) {
                this.wheelDevice = next; // takes effect from the NEXT wheel event
                // Drop the transitional event (except pinch, which must keep zooming) so the
                // outgoing mode doesn't act on it.
                if (!e.ctrlKey) {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                }
            }
        },
        navigateToModelDetail(options, value) {
            if (options && value) {
                const selectedModel = options.find(option => (option.value || option.modelDisplayID) === value);
                if (selectedModel && selectedModel.modelId) {
                    const routeData = this.$router.resolve({ path: '/GPTModelDetail', query: { id: selectedModel.modelId } });
                    window.open(routeData.href, '_blank');
                }
            }
        },
        navigateToModelDetailSql(options, value) {
            if (options && value) {
                const selectedModel = options.find(option => (option.value || option.modelDisplayID) === value);
                if (selectedModel && selectedModel.idEncrypt) {
                    const routeData = this.$router.resolve({ path: '/GPTModelDetail', query: { id: selectedModel.idEncrypt } });
                    window.open(routeData.href, '_blank');
                }
            }
        },
        // Knowledge Connection Methods
        getConnectedKnowledge() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'agent') {
                return [];
            }

            const knowledgeTypes = {
                'documents': {
                    key: 'documents',
                    label: 'Documents',
                    icon: 'mdi-file-document',
                    color: '#0369a1',
                    bgColor: '#dbeafe'
                },
                'databaseIntegration': {
                    key: 'databaseIntegration',
                    label: 'Database Integration',
                    icon: 'mdi-database',
                    color: '#16a34a',
                    bgColor: '#dcfce7'
                },
                'mutualFund': {
                    key: 'mutualFund',
                    label: 'Mutual Fund',
                    icon: 'mdi-chart-donut-variant',
                    color: '#7c3aed',
                    bgColor: '#ede9fe'
                },
                'apiknowledge': {
                    key: 'knowledgeApi',
                    label: 'Knowledge API',
                    icon: 'mdi-api',
                    color: '#ff6c37',
                    bgColor: '#ffecdb'
                },
                'products': {
                    key: 'products',
                    label: 'Products',
                    icon: 'mdi-package-variant',
                    color: '#d97706',
                    bgColor: '#fef3c7'
                },
                'sharepoint': {
                    key: 'sharepoint',
                    label: 'SharePoint',
                    icon: 'mdi-microsoft-sharepoint',
                    color: '#02787c',
                    bgColor: '#e6f7f8'
                },
                'taxPlanning': {
                    key: 'taxPlanning',
                    label: 'Tax Planning Form',
                    icon: 'mdi-clipboard-text-clock',
                    color: '#7c3aed',
                    bgColor: '#ede9fe'
                },
                'taxPlanningV2': {
                    key: 'taxPlanningV2',
                    label: 'Tax Planning Form V2',
                    icon: 'mdi-clipboard-text-clock',
                    color: '#0891b2',
                    bgColor: '#ccfbf1'
                },
                'metaDocuments': {
                    key: 'metaDocuments',
                    label: 'Meta Documents',
                    icon: 'mdi-bookshelf',
                    color: '#6366f1',
                    bgColor: '#e0e7ff'
                }
            };

            const connected = [];
            const agentId = this.selectedNode.id;

            // Find all edges connected to this agent's tools-bottom handle from knowledge sources
            const incomingEdges = this.tempEdge.filter(edge => {
                if (edge.source !== agentId || edge.sourceHandle !== 'tools-bottom') {
                    return false;
                }
                // Check if target is a knowledge node
                const targetNode = this.tempNode.find(n => n.id === edge.target);
                return targetNode && ['documents', 'dynamicKnowledge', 'databaseIntegration', 'mutualFund', 'apiknowledge', 'products', 'sharepoint', 'taxPlanning', 'taxPlanningV2', 'metaDocuments'].includes(targetNode.data.type);
            });

            // Define fixed display order for knowledge types
            const displayOrder = ['documents', 'dynamicKnowledge', 'databaseIntegration', 'mutualFund', 'apiknowledge', 'products', 'sharepoint', 'taxPlanning', 'taxPlanningV2', 'metaDocuments'];

            incomingEdges.forEach(edge => {
                const knowledgeNode = this.tempNode.find(n => n.id === edge.target);
                
                if (knowledgeNode && knowledgeNode.data && knowledgeNode.data.type) {
                    const knowledgeType = knowledgeTypes[knowledgeNode.data.type];
                    
                    if (knowledgeType) {
                        // Update the connection tracking in nodeInspector
                        if (!this.nodeInspector.knowledgeConnections[knowledgeType.key]) {
                            this.nodeInspector.knowledgeConnections[knowledgeType.key] = { nodeId: null, description: '' };
                        }
                        this.nodeInspector.knowledgeConnections[knowledgeType.key].nodeId = knowledgeNode.id;

                        connected.push({
                            ...knowledgeType,
                            nodeType: knowledgeNode.data.type, // Store original type for sorting
                            nodeId: knowledgeNode.id,
                            nodeName: knowledgeNode.data.label || knowledgeType.label,
                            description: this.nodeInspector.knowledgeConnections[knowledgeType.key].description || ''
                        });
                    }
                }
            });

            // Sort by fixed display order
            connected.sort((a, b) => {
                return displayOrder.indexOf(a.nodeType) - displayOrder.indexOf(b.nodeType);
            });

            return connected;
        },

        updateKnowledgeDescription(knowledgeType, description) {
            if (this.nodeInspector.knowledgeConnections && this.nodeInspector.knowledgeConnections[knowledgeType]) {
                this.nodeInspector.knowledgeConnections[knowledgeType].description = description;
                
                // Update in the actual node data
                if (this.selectedNode && this.selectedNode.data) {
                    if (!this.selectedNode.data.knowledgeConnections) {
                        this.selectedNode.data.knowledgeConnections = {};
                    }
                    if (!this.selectedNode.data.knowledgeConnections[knowledgeType]) {
                        this.selectedNode.data.knowledgeConnections[knowledgeType] = { nodeId: null, description: '' };
                    }
                    this.selectedNode.data.knowledgeConnections[knowledgeType].description = description;
                    
                    // Also update in tempNode array for proper persistence
                    const nodeIndex = this.tempNode.findIndex(n => n.id === this.selectedNode.id);
                    if (nodeIndex !== -1) {
                        if (!this.tempNode[nodeIndex].data.knowledgeConnections) {
                            this.tempNode[nodeIndex].data.knowledgeConnections = {};
                        }
                        if (!this.tempNode[nodeIndex].data.knowledgeConnections[knowledgeType]) {
                            this.tempNode[nodeIndex].data.knowledgeConnections[knowledgeType] = { nodeId: null, description: '' };
                        }
                        this.tempNode[nodeIndex].data.knowledgeConnections[knowledgeType].description = description;
                    }
                }

                // Save history for undo/redo
                this.saveStateToHistory(`Update ${knowledgeType} description`);
            }
        },


        // ===== Tools panel ของโหนด Agent (hosted tools) =====
        // ฟอร์มอยู่ใน nodeInspector อย่างเดียว ส่วนที่เขียนลง node data จริงคือ toolsConfig (JSON string)
        // toolsType / toolsChoice / toolsWebSearch เป็น UI state ล้วน ๆ แตกมาจาก toolsConfig ตอน hydrate

        // แตก toolsConfig ที่เก็บไว้ในโหนดกลับเป็นค่าเริ่มต้นของฟอร์ม (ใช้ตอน hydrate nodeInspector)
        buildNodeToolsInspectorState(rawToolsConfig) {
            const parsed = parseToolsConfig(rawToolsConfig);
            return {
                toolsConfig: rawToolsConfig || null,
                toolsType: parsed.toolType,
                toolsChoice: parsed.toolChoice,
                toolsWebSearch: parsed.webSearchForm,
            };
        },
        // ประกอบฟอร์มเป็น JSON แล้วเขียนลง node data — เรียกทุกครั้งที่แก้ช่องใดช่องหนึ่ง
        syncNodeToolsConfig() {
            const json = buildToolsConfig(this.nodeInspector.toolsType, this.nodeInspector.toolsChoice, this.nodeInspector.toolsWebSearch);
            this.nodeInspector.toolsConfig = json;
            this.updateNodeProperty('toolsConfig', json);
        },
        setNodeToolsType(value) {
            this.nodeInspector.toolsType = value || null;
            // เคลียร์ type = ไม่ใช้ tool แล้ว → ล้างค่าที่ตั้งไว้ทิ้ง (buildToolsConfig จะคืน null ให้เอง)
            if (!this.nodeInspector.toolsType) {
                this.nodeInspector.toolsChoice = null;
                this.nodeInspector.toolsWebSearch = createWebSearchForm();
            }
            this.syncNodeToolsConfig();
        },
        setNodeToolsChoice(value) {
            this.nodeInspector.toolsChoice = value || null;
            this.syncNodeToolsConfig();
        },
        setNodeToolsWebSearchField(field, value) {
            const form = this.nodeInspector.toolsWebSearch || createWebSearchForm();
            form[field] = field === 'allowedDomains' ? capAllowedDomains(value) : (value || null);
            // spread เป็น object ใหม่เสมอ เพื่อให้ Vue reactivity จับการเปลี่ยนแปลงได้
            this.nodeInspector.toolsWebSearch = { ...form };
            this.syncNodeToolsConfig();
        },
        // Knowledge placeholder and hint helper methods
        getKnowledgePlaceholder(key) {
            const placeholders = {
                documents: 'e.g., Use this for product manuals, FAQs, and policy documents...',
                databaseIntegration: 'e.g., Query customer data, order history, inventory levels...',
                knowledgeApi: 'e.g., Fetch real-time data from external APIs...',
                products: 'e.g., Search product catalog for recommendations and details...',
                sharepoint: 'e.g., Access company documents and shared files...'
            };
            return placeholders[key] || 'Describe when and how to use this knowledge source...';
        },

        getKnowledgeHint(key) {
            const hints = {
                documents: 'Helps agent decide when to search document knowledge base',
                databaseIntegration: 'Guides agent on database query scenarios',
                knowledgeApi: 'Instructs agent on when to call external APIs',
                products: 'Directs agent for product-related queries',
                sharepoint: 'Tells agent when to access SharePoint files'
            };
            return hints[key] || 'This description helps the agent understand when to use this source';
        },

        // Tool Header Management Methods
        addToolHeader() {
            if (!this.nodeInspector.headers) {
                this.nodeInspector.headers = [];
            }
            this.nodeInspector.headers.push({ key: '', value: '' });
            this.updateNodeProperty('headers', this.nodeInspector.headers);
        },
        removeToolHeader(index) {
            if (this.nodeInspector.headers && Array.isArray(this.nodeInspector.headers)) {
                this.nodeInspector.headers.splice(index, 1);
                this.updateNodeProperty('headers', this.nodeInspector.headers);
            }
        },

        // Flow Tool Methods
        getFlowToolNodeCount() {
            if (!this.selectedNode?.data?.internalNodes) return 0;
            // Exclude flowInput and flowOutput from count
            return this.selectedNode.data.internalNodes.filter(n => 
                n.data?.type !== 'flowInput' && n.data?.type !== 'flowOutput'
            ).length;
        },
        isFlowToolValid() {
            if (!this.selectedNode?.data?.internalNodes || !this.selectedNode?.data?.internalEdges) {
                return false;
            }
            
            // Check path from flowInput to flowOutput
            const pathValid = this.validateInternalFlow(
                this.selectedNode.data.internalNodes,
                this.selectedNode.data.internalEdges
            );
            if (!pathValid) {
                return false;
            }
            
            // Check if any internal node has warnings (except flowInput/flowOutput)
            const internalEdges = this.selectedNode.data.internalEdges || [];
            const internalNodesWithWarnings = this.selectedNode.data.internalNodes.filter(node => {
                // Skip flowInput and flowOutput nodes
                if (node.data?.type === 'flowInput' || node.data?.type === 'flowOutput') {
                    return false;
                }
                // Check if this node has warnings using validateSingleNode with internal edges
                return this.validateSingleNode(node, internalEdges);
            });
            
            return internalNodesWithWarnings.length === 0;
        },
        // Check if internal flow is complete (path valid + no node warnings)
        isInternalFlowComplete() {
            // Check path from flowInput to flowOutput
            if (!this.validateInternalFlow(this.tempNode, this.tempEdge)) {
                return false;
            }
            
            // Check if any internal node has warnings (except flowInput/flowOutput)
            // Use tempEdge explicitly since we're inside the internal flow editor
            const nodesWithWarnings = this.tempNode.filter(node => {
                // Skip flowInput and flowOutput nodes
                if (node.data?.type === 'flowInput' || node.data?.type === 'flowOutput') {
                    return false;
                }
                // Check if this node has warnings using validateSingleNode with current tempEdge
                return this.validateSingleNode(node, this.tempEdge);
            });
            
            return nodesWithWarnings.length === 0;
        },
        validateInternalFlow(nodes, edges) {
            if (!nodes || nodes.length === 0) return false;
            
            // Find flowInput and flowOutput nodes
            const inputNode = nodes.find(n => n.data?.type === 'flowInput');
            const outputNode = nodes.find(n => n.data?.type === 'flowOutput');
            
            if (!inputNode || !outputNode) return false;
            
            // Check if there's a path from flowInput to flowOutput using BFS
            const visited = new Set();
            const queue = [inputNode.id];
            
            while (queue.length > 0) {
                const currentId = queue.shift();
                if (currentId === outputNode.id) return true;
                if (visited.has(currentId)) continue;
                visited.add(currentId);
                
                // Find all edges where current node is source
                const outgoingEdges = edges.filter(e => e.source === currentId);
                outgoingEdges.forEach(edge => {
                    if (!visited.has(edge.target)) {
                        queue.push(edge.target);
                    }
                });
            }
            
            return false;
        },
        openFlowToolEditor() {
            // Save current main flow state
            this.savedMainNodes = [...this.tempNode];
            this.savedMainEdges = [...this.tempEdge];
            this.editingFlowToolNode = this.selectedNode;
            
            // Deselect node to close inspector (it doesn't make sense to show flow tool inspector)
            this.selectedNode = null;
            
            // Initialize internal flow
            this.initializeFlowToolEditor();
            
            // Swap to internal flow on main canvas
            this.tempNode = [...this.internalFlowNodes];
            this.tempEdge = [...this.internalFlowEdges];
            
            // Show editor mode
            this.flowToolEditorDialog = true;
            
            // Fit view to internal flow after swap (need delay for Vue Flow to render nodes)
            this.$nextTick(() => {
                setTimeout(() => {
                    if (this.$refs.vueFlow) {
                        this.$refs.vueFlow.fitView({ padding: 0.3, duration: 300 });
                    }
                }, 100);
            });
        },
        closeFlowToolEditor() {
            // Keep reference to the flow tool node before clearing
            const flowToolNodeId = this.editingFlowToolNode?.id;
            
            // Restore main flow
            this.tempNode = [...this.savedMainNodes];
            this.tempEdge = [...this.savedMainEdges];
            
            // Clear saved state
            this.savedMainNodes = [];
            this.savedMainEdges = [];
            this.editingFlowToolNode = null;
            
            // Hide editor mode
            this.flowToolEditorDialog = false;
            
            // Focus back to the flow tool node that was being edited and validate it
            this.$nextTick(() => {
                if (flowToolNodeId) {
                    // Find and select the flow tool node
                    const flowToolNode = this.tempNode.find(n => n.id === flowToolNodeId);
                    if (flowToolNode) {
                        // Validate the FlowTool node using the same logic as isFlowToolValid
                        this.validateFlowToolNode(flowToolNode);
                        
                        this.selectedNode = flowToolNode;
                        // Center view on the flow tool node
                        if (this.$refs.vueFlow) {
                            this.$refs.vueFlow.setCenter(
                                flowToolNode.position.x + 150,
                                flowToolNode.position.y + 75,
                                { zoom: 1, duration: 300 }
                            );
                        }
                    }
                }
            });
        },
        initializeFlowToolEditor() {
            // Use editingFlowToolNode since selectedNode is set to null before this is called
            const flowToolNode = this.editingFlowToolNode;
            
            // Initialize internal flow with default nodes if empty
            if (!flowToolNode.data.internalNodes || flowToolNode.data.internalNodes.length === 0) {
                const flowInputNode = {
                    id: `flow-input-${this.generateGuid()}`,
                    type: 'flow-node',
                    position: { x: 100, y: 300 },
                    data: {
                        type: 'flowInput',
                        label: 'Flow Input',
                        isFixed: true
                    }
                };
                const flowOutputNode = {
                    id: `flow-output-${this.generateGuid()}`,
                    type: 'flow-node',
                    position: { x: 1200, y: 300 },
                    data: {
                        type: 'flowOutput',
                        label: 'Flow Output',
                        isFixed: true
                    }
                };
                flowToolNode.data.internalNodes = [flowInputNode, flowOutputNode];
                flowToolNode.data.internalEdges = [];
            }
            
            // Set internal flow data for editor
            this.internalFlowNodes = [...flowToolNode.data.internalNodes];
            this.internalFlowEdges = [...flowToolNode.data.internalEdges];
        },
        saveFlowToolInternalFlow() {
            // Save current canvas state back to internal flow arrays
            this.internalFlowNodes = [...this.tempNode];
            this.internalFlowEdges = [...this.tempEdge];
            
            // Save internal flow back to node data
            this.editingFlowToolNode.data.internalNodes = [...this.internalFlowNodes];
            this.editingFlowToolNode.data.internalEdges = [...this.internalFlowEdges];
            
            // Update node count display
            const nodeCount = this.internalFlowNodes.filter(n => 
                n.data?.type !== 'flowInput' && n.data?.type !== 'flowOutput'
            ).length;
            this.editingFlowToolNode.data.internalNodeCount = nodeCount;
            
            // Note: validation will be done in closeFlowToolEditor after restoring main flow
            this.closeFlowToolEditor();
        },
        // Validate a FlowTool node and update its showWarning status
        validateFlowToolNode(flowToolNode) {
            if (!flowToolNode || flowToolNode.data?.type !== 'flowTool') return;
            
            let hasWarning = false;
            
            // Check if internal flow exists and has nodes
            if (!flowToolNode.data.internalNodes || flowToolNode.data.internalNodes.length === 0) {
                hasWarning = true;
            } else {
                // Check path from flowInput to flowOutput
                const pathValid = this.validateInternalFlow(
                    flowToolNode.data.internalNodes,
                    flowToolNode.data.internalEdges || []
                );
                if (!pathValid) {
                    hasWarning = true;
                }
                
                // Check if any internal node has warnings (except flowInput/flowOutput)
                if (!hasWarning) {
                    const internalEdges = flowToolNode.data.internalEdges || [];
                    const internalNodesWithWarnings = flowToolNode.data.internalNodes.filter(node => {
                        // Skip flowInput and flowOutput nodes
                        if (node.data?.type === 'flowInput' || node.data?.type === 'flowOutput') {
                            return false;
                        }
                        // Check if this node has warnings using validateSingleNode with internal edges
                        return this.validateSingleNode(node, internalEdges);
                    });
                    
                    if (internalNodesWithWarnings.length > 0) {
                        hasWarning = true;
                    }
                }
            }
            
            // Update the node's showWarning status
            flowToolNode.data.showWarning = hasWarning;
            
            // Also update in tempNode array
            const nodeIndex = this.tempNode.findIndex(n => n.id === flowToolNode.id);
            if (nodeIndex !== -1) {
                this.tempNode[nodeIndex].data.showWarning = hasWarning;
            }
        },

        // Internal Flow Editor Methods
        onInternalFlowDragStart(event, node) {
            event.dataTransfer.setData('application/reactflow', JSON.stringify(node));
            event.dataTransfer.effectAllowed = 'move';
        },
        onInternalFlowDragOver(event) {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'move';
        },
        onInternalFlowDrop(event) {
            event.preventDefault();
            const nodeData = event.dataTransfer.getData('application/reactflow');
            if (!nodeData) return;
            
            const node = JSON.parse(nodeData);
            this.addInternalFlowNode(node, event);
        },
        addInternalFlowNode(nodeOrTemplate, event = null) {
            // Support both: 
            // 1. NodePanel emit format: { id, type: 'flow-node', position, data }
            // 2. Template format from drag: { type, name, data, ... }
            
            let nodeData;
            if (nodeOrTemplate.type === 'flow-node') {
                // From NodePanel emit - already has proper structure
                nodeData = nodeOrTemplate.data;
            } else {
                // From template (drag or direct call)
                nodeData = nodeOrTemplate.data || nodeOrTemplate;
            }
            
            const nodeId = `internal-${this.generateGuid()}`;
            
            // Calculate position
            let position = nodeOrTemplate.position || { x: 300, y: 200 };
            if (event) {
                // Get canvas bounds for position calculation
                const canvas = document.querySelector('.internal-flow-canvas');
                if (canvas) {
                    const bounds = canvas.getBoundingClientRect();
                    position = {
                        x: event.clientX - bounds.left - 75,
                        y: event.clientY - bounds.top - 40
                    };
                }
            } else if (!nodeOrTemplate.position) {
                // Find a good position that doesn't overlap
                const existingPositions = this.internalFlowNodes.map(n => n.position);
                let x = 300;
                let y = 200;
                while (existingPositions.some(p => Math.abs(p.x - x) < 150 && Math.abs(p.y - y) < 100)) {
                    x += 50;
                    y += 30;
                }
                position = { x, y };
            }
            
            const newNode = {
                id: nodeId,
                type: 'flow-node',
                position: position,
                data: { ...nodeData }
            };
            
            this.internalFlowNodes.push(newNode);
        },
        addInternalFlowNodeDirect(nodeOrTemplate) {
            // Direct add to tempNode (used when editing internal flow on main canvas)
            let nodeData;
            if (nodeOrTemplate.type === 'flow-node') {
                nodeData = nodeOrTemplate.data;
            } else {
                nodeData = nodeOrTemplate.data || nodeOrTemplate;
            }
            
            const nodeId = `internal-${this.generateGuid()}`;
            
            // Find a good position that doesn't overlap
            const existingPositions = this.tempNode.map(n => n.position);
            let x = 300;
            let y = 200;
            while (existingPositions.some(p => Math.abs(p.x - x) < 150 && Math.abs(p.y - y) < 100)) {
                x += 50;
                y += 30;
            }
            
            const newNode = {
                id: nodeId,
                type: 'flow-node',
                position: { x, y },
                data: { ...nodeData }
            };
            
            this.tempNode.push(newNode);
        },
        deleteInternalFlowNode(nodeId) {
            // Don't allow deleting fixed nodes (flowInput, flowOutput)
            const node = this.internalFlowNodes.find(n => n.id === nodeId);
            if (node?.data?.isFixed) {
                this.showSnackbar = true;
                this.snackBarColor = 'warning';
                this.snackbarMessage = 'Cannot delete fixed entry/exit nodes';
                return;
            }
            
            // Remove node
            this.internalFlowNodes = this.internalFlowNodes.filter(n => n.id !== nodeId);
            // Remove connected edges
            this.internalFlowEdges = this.internalFlowEdges.filter(e => 
                e.source !== nodeId && e.target !== nodeId
            );
            
            if (this.selectedInternalNode?.id === nodeId) {
                this.selectedInternalNode = null;
            }
        },
        onInternalFlowNodeClick(event) {
            const node = event.node;
            this.selectedInternalNode = node;
        },
        onInternalFlowConnect(connection) {
            const newEdge = {
                id: `e-${this.generateGuid()}`,
                source: connection.source,
                target: connection.target,
                sourceHandle: connection.sourceHandle || 'output',
                targetHandle: connection.targetHandle || 'input',
                type: 'flow-edge',
                markerEnd: 'arrowclosed'
            };
            this.internalFlowEdges.push(newEdge);
        },

        updateTools(newTools) {
            // Reload tools from API to get fresh data
            this.loadAgentTools();
        },
        loadToolsFromStorage() {
            // Legacy method - now loads from API instead
            this.loadAgentTools();
        },
        loadAgentTools() {
            // Load Agent Tools from database via API
            this.$call.GetAgentTools(false, (res) => {
                if (res.status === 'successful' || res.status === 0) {
                    // Map API response to availableAgentTools format
                    // API returns: { id, name, description, apiEndpoint, httpMethod, headers, parameters }
                    const tools = res.data || [];
                    this.availableAgentTools = tools.map(tool => ({
                        id: tool.id || tool.ID,
                        name: tool.name || tool.Name,
                        description: tool.description || tool.Description || '',
                        apiEndpoint: tool.apiEndpoint || tool.ApiEndpoint,
                        httpMethod: tool.httpMethod || tool.HttpMethod,
                        headers: tool.headers || tool.Headers || [],
                        parameters: tool.parameters || tool.Parameters || []
                    }));
                    console.log('Loaded Agent Tools from API:', this.availableAgentTools.length);
                } else {
                    console.warn('Failed to load Agent Tools:', res.message);
                    // Fallback to empty array
                    this.availableAgentTools = [];
                }
            }, (err) => {
                console.error('Error loading Agent Tools:', err);
                this.availableAgentTools = [];
            });
        },
        copyToClipboard(text) {
            navigator.clipboard.writeText(text).then(() => {
                // Show success notification
                this.$toast?.success?.('Copied to clipboard!') || console.log('Copied:', text);
            }).catch(err => {
                console.error('Failed to copy:', err);
            });
        },
        async checkRouteMode() {
            const { id, mode, importData } = this.$route.query;
            const hasViewOnlyPermission = this.$pagePermission && this.$pagePermission.isViewOnly('FlowCreator');

            if (importData) {
                // Handle imported flow data
                await this.handleImportedData(importData);
                if (hasViewOnlyPermission) {
                    this.isViewMode = true;
                    this.isEditMode = false;
                }
            } else if (id) {
                // Handle edit/view mode
                this.editFlowId = id;
                this.isEditMode = mode === 'edit' && !hasViewOnlyPermission;
                this.isViewMode = mode === 'view' || hasViewOnlyPermission;

                // Load the flow data
                await this.loadFlowForEdit(id);

            } else if (hasViewOnlyPermission) {
                // New flow but view-only permission — force view mode to lock the canvas down.
                this.isViewMode = true;
                this.isEditMode = false;
            }
        },
        goBack() {
                // Use custom confirmation dialog if in edit mode with unsaved changes
            this.showExitConfirmation(() => {
                this.$router.push('/FlowList');
            });
        },
        generateGuid() {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                const r = Math.random() * 16 | 0;
                const v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        },

        // Undo/Redo System Methods
        saveStateToHistory(action = 'Unknown Action') {
            if (this.isPerformingUndoRedo) return;

            // Create a deep copy of current state
            const currentState = {
                nodes: JSON.parse(JSON.stringify(this.tempNode)),
                edges: JSON.parse(JSON.stringify(this.tempEdge)),
                action: action,
                timestamp: new Date().toISOString()
            };

            // Remove any history entries after current index (if we're not at the end)
            if (this.historyIndex < this.history.length - 1) {
                this.history = this.history.slice(0, this.historyIndex + 1);
            }

            // Add new state to history
            this.history.push(currentState);

            // Limit history size
            if (this.history.length > this.maxHistorySize) {
                this.history.shift();
            } else {
                this.historyIndex++;
            }
        },

        undo() {
            if (!this.canUndo) return;

            this.isPerformingUndoRedo = true;
            
            try {
                this.historyIndex--;
                const previousState = this.history[this.historyIndex];
                
                // Restore previous state
                this.tempNode = JSON.parse(JSON.stringify(previousState.nodes));
                this.tempEdge = JSON.parse(JSON.stringify(previousState.edges));
                
                // Clear selection when undoing
                this.selectedNode = null;
                this.selectedNodes = [];
                this.closeInspector();
                
                this.showSuccess(`Undone: ${previousState.action}`);
                
            } finally {
                this.isPerformingUndoRedo = false;
            }
        },

        redo() {
            if (!this.canRedo) return;

            this.isPerformingUndoRedo = true;
            
            try {
                this.historyIndex++;
                const nextState = this.history[this.historyIndex];
                
                // Restore next state
                this.tempNode = JSON.parse(JSON.stringify(nextState.nodes));
                this.tempEdge = JSON.parse(JSON.stringify(nextState.edges));
                
                // Clear selection when redoing
                this.selectedNode = null;
                this.selectedNodes = [];
                this.closeInspector();
                
                this.showSuccess(`Redone: ${nextState.action}`);
                
            } finally {
                this.isPerformingUndoRedo = false;
            }
        },

        initializeHistory() {
            // Save initial state
            this.saveStateToHistory('Initial State');
        },
        // Load GPT Models (async version)
        loadGPTModels() {
            return new Promise((resolve, reject) => {
                this.$call.DDGPTModel(
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            this.aiModelOptions = dataResponse.data.map(m => ({
                                name: m.name,
                                value: m.modelDisplayID,
                                modelId: m.id,
                                icon: 'mdi-robot',
                                color: '#10a37f',
                                searchableText: `${m.modelDisplayID}: ${m.name}` // สำหรับฝารค้นหา
                            }));
                            this.uniqueAiModelOptions = this.aiModelOptions.filter((option, index, self) =>
                                index === self.findIndex((t) => (
                                    t.value === option.value
                                ))
                            );
                        } else {
                            this.aiModelOptions = [];
                        }
                        resolve();
                    },
                    (err) => {
                        this.aiModelOptions = [];
                        reject(err);
                    }
                );
            });
        },
        // Load Model Management Data (async version)
        GetAllDDModelManagement() {
            // App Knowledge Center: ถามโหมด/รายการของ Center ขนานกับ $call (โหมดปิด ⇒ null — ไม่ยิงอะไรเพิ่มนอกจาก status)
            const kccVectorListPromise = kccPickerSource.vectorList();
            return new Promise((resolve, reject) => {
                let self = this;
                self.$call.GetAllDDModelManagement(
                    0,
                    (dataResponse) => {
                        if (dataResponse.status == 0) {
                            self.DDVector = (dataResponse.data.vectorDBName || []).map(item => ({
                                ...item,
                                displayName: item.typeName ? `${item.name} (${item.typeName})${item.docType === 'MetaDoc' ? ' [MetaDoc]' : ''}` : item.docType === 'MetaDoc' ? `${item.name} [MetaDoc]` : item.name
                            }));
                            self.listDataIntegration = dataResponse.data.dataIntegration;
                            self.DDGroupReplace = dataResponse.data.groupReplace;
                            self.DDProductGroup = dataResponse.data.productGroup;
                            self.listDDModel = dataResponse.data.modelData;
                            self.listDDIntentGroup = dataResponse.data.intentGroup;
                            self.listSqlExtract = dataResponse.data.modelPublih
                                .filter((e) => e.typeName == 'Bot')
                                .map(m => ({ ...m, searchableText: `${m.modelDisplayID}: ${m.name}` }));
                            self.listMonitorLineName = dataResponse.data.monitorSocialLine;
                            self.DDPromptTemplate = dataResponse.data.promptTemplate.listData;
                            self.DDGroupPromptTemplate = dataResponse.data.groupPromptTemplate;
                            self.listDDProvider = dataResponse.data.provider;
                        } else {
                            self.DDVector = [];
                            self.listDataIntegration = [];
                            self.listSqlExtract = [];
                            // self.listGuardRailModel = [];
                            self.DDGroupReplace = [];
                            self.DDProductGroup = [];
                            self.DDPromptTemplate = [];
                            self.listDDProvider = [];
                            self.listDDModel = [];
                        }
                        // โหมด Center ⇒ DDVector = เอกสารของ Center เท่านั้น (รายการอื่นจาก $call คงเดิม) · โหมดปิด ⇒ ไม่แตะ
                        self.kccApplyVectorList(kccVectorListPromise).then(() => resolve(), () => resolve());
                    },
                    (errorResource) => {
                        console.error(errorResource);
                        reject(errorResource);
                    }
                );
            });
        },
        // App Knowledge Center (AC-9 · A4/A19): โหมด Center ⇒ แทน DDVector ด้วย catalog ของ Runtime key (ของในเครื่องซ่อน —
        // ชื่อในเครื่องที่ flow บันทึกไว้จะขึ้น isDocumentMissing โดยตั้งใจ) · เปิดแต่ไม่มี Runtime key/Center ล้ม ⇒ []
        // + ข้อความ (ไม่ถอยไปของในเครื่อง) · โหมดปิด (list === null) ⇒ ไม่แตะ DDVector · kccPickerSource ไม่ throw
        async kccApplyVectorList(pending) {
            let list = null;
            try {
                list = await pending;
            } catch (e) {
                list = null;
            }
            this.kccCenterMode = list !== null;
            if (list === null) {
                this.kccDocsUnavailable = '';
                return;
            }
            this.DDVector = list;
            this.kccDocsUnavailable = kccUnavailableReason(list);
        },
        // Load Document Groups for Flow integration
        loadDocumentGroups() {
            return new Promise((resolve, reject) => {
                this.$call.GetDDDocumentGroup(
                    (response) => {
                        if (response.status === 0) {
                            this.DDDocumentGroup = response.data || [];
                        } else {
                            this.DDDocumentGroup = [];
                        }
                        resolve();
                    },
                    (error) => {
                        console.error('Failed to load document groups:', error);
                        this.DDDocumentGroup = [];
                        resolve(); // Don't reject, just continue with empty list
                    }
                );
            });
        },
        // Load Community Graphs for GraphRAG
        loadCommunityGraphs() {
            return new Promise((resolve, reject) => {
                this.$call.GetCommunityDropdown(
                    (response) => {
                        if (response.status === 0) {
                            this.DDCommunityGraph = response.data || [];
                        } else {
                            this.DDCommunityGraph = [];
                        }
                        resolve();
                    },
                    (error) => {
                        console.error('Failed to load community graphs:', error);
                        this.DDCommunityGraph = [];
                        resolve(); // Don't reject, just continue with empty list
                    }
                );
            });
        },
        // Load Meta Community Graphs for Meta GraphRAG
        // App Knowledge Center (D2): โหมด Center ⇒ graph ของ Center (`GET KnowledgeCenterClient/graphs`, id ของ Center —
        // flow ที่บันทึก id ของ Genie ไว้ Center จะตอบ deny ⇒ ไม่มีข้อมูล) · โหมดปิด (null) ⇒ $call เดิมด้านล่าง
        async loadMetaCommunityGraphs() {
            const kccGraphs = await kccPickerSource.graphs();
            if (kccGraphs !== null) {
                this.kccCenterMode = true;
                this.DDMetaCommunityGraph = kccGraphs;
                this.kccGraphsUnavailable = kccUnavailableReason(kccGraphs);
                this.ensureAllDynamicKnowledgeMetaCommunityDefaults();
                return;
            }
            this.kccGraphsUnavailable = '';
            return new Promise((resolve, reject) => {
                this.$call.GetMetaCommunityDropdown(
                    (response) => {
                        if (response.status === 0) {
                            this.DDMetaCommunityGraph = response.data || [];
                            this.ensureAllDynamicKnowledgeMetaCommunityDefaults();
                        } else {
                            this.DDMetaCommunityGraph = [];
                        }
                        resolve();
                    },
                    (error) => {
                        console.error('Failed to load meta community graphs:', error);
                        this.DDMetaCommunityGraph = [];
                        resolve(); // Don't reject, just continue with empty list
                    }
                );
            });
        },
        // Load Product Group Collections for Flow integration
        loadProductGroupCollections() {
            return new Promise((resolve, reject) => {
                this.$call.GetDDProductGroupCollection(
                    (response) => {
                        if (response.status === 0) {
                            this.DDProductGroupCollection = response.data || [];
                        } else {
                            this.DDProductGroupCollection = [];
                        }
                        resolve();
                    },
                    (error) => {
                        console.error('Failed to load product group collections:', error);
                        this.DDProductGroupCollection = [];
                        resolve(); // Don't reject, just continue with empty list
                    }
                );
            });
        },
        // Method to filter messageInput models after uniqueAiModelOptions is loaded
        filterMessageInputModels() {
            if (!this.uniqueAiModelOptions || this.uniqueAiModelOptions.length === 0) {
                return;
            }

            if (!this.tempNode || this.tempNode.length === 0) {
                return;
            }

            let messageInputNodes = this.tempNode.filter(n => n.data && n.data.type === 'messageInput');
            
            if (messageInputNodes.length > 0) {
                messageInputNodes.forEach(messageInputNode => {
                    if (messageInputNode.data.selectedModel && Array.isArray(messageInputNode.data.selectedModel) && messageInputNode.data.selectedModel.length > 0) {
                        this.unableToAccessModel = messageInputNode.data.selectedModel;
                        let tempUniqueModels = this.uniqueAiModelOptions.map(option => option.value);
                        this.unableToAccessModel = this.unableToAccessModel.filter(model => !tempUniqueModels.includes(model));
                        
                        // Update the specific node
                        let filteredModels = messageInputNode.data.selectedModel.filter(model => !this.unableToAccessModel.includes(model));
                        
                        // Update tempNode array
                        this.tempNode = this.tempNode.map(node => {
                            if (node.id === messageInputNode.id) {
                                return {
                                    ...node,
                                    data: {
                                        ...node.data,
                                        selectedModel: filteredModels
                                    }
                                };
                            }
                            return node;
                        });
                        
                        if (this.unableToAccessModel.length > 0) {
                            console.log(`Filtered out inaccessible models from node ${messageInputNode.id}: ${this.unableToAccessModel.join(', ')}`);
                        }
                    }
                });
            }
        },
        // Reload all models and data (useful for refresh actions)
        async reloadModels() {
            try {
                await Promise.all([
                    this.loadGPTModels(),
                    this.GetAllDDModelManagement()
                ]);
                this.showSuccess('Models reloaded successfully');
            } catch (error) {
                console.error('Failed to reload models:', error);
                this.showError('Failed to reload models. Please try again.');
            }
        },
        clearHistory() {
            this.history = [];
            this.historyIndex = -1;
            this.saveStateToHistory('Flow Saved');
            // Show info message about history being cleared
            this.showInfo('Undo/Redo history cleared');
            console.log('History cleared after save');
        },
        onSelectionChange({ nodes }) {
            // event นี้จะทำงานหลัง onSelectionDragEnd

            // เช็คว่าเพิ่งลาฝเสร็จ ๝ละมี node ที่ถูฝเลือฝ๝ค่ตัวเดียวหรือไม่
            if (this.wasJustDragSelecting && nodes.length === 1) {
                this.selectedNodes = []; // ยฝเลิฝฝารเลือฝ
            }

            // รีเซ็ตค่าธงเสมอ เพื่อให้ฝารคลิฝครั้งต่อไปทำงานปฝติ
            this.wasJustDragSelecting = false;

            // อัปเดต selectedNodes
            this.selectedNodes = nodes;

            // อัปเดต selectedNode สำหรับ inspector panel
            if (this.selectedNodes.length === 1) {
                this.selectedNode = this.selectedNodes[0];
            } else {
                this.selectedNode = null;
            }
        },
        onNodeClick({ node, event }) {
            // In debug mode: Ctrl+Click sets next statement
            if (this.isDebugMode) {
                if (this.debugStatus === 'paused' && event?.ctrlKey) {
                    this.onDebugSetNext(node.id);
                }
                return;
            }
            this.openNodeInspector(node);
        },
        onNodeMouseEnter({ node }) {
            if (this.isDebugMode && this.debugStatus === 'paused') {
                this.debugHoveredNodeId = node.id;
            }
        },
        onNodeMouseLeave() {
            this.debugHoveredNodeId = '';
        },
        openNodeInspector(node) {
            this.selectedNode = node;

            // The Meta Document taxonomy is needed by this one node type only, so it is fetched on first open
            // rather than on page load — two requests nobody else in the editor pays for.
            if (node.data && (node.data.type === 'taxPlanningAgent' || node.data.type === 'metaDocuments')) this.loadTaxKnowledgeTaxonomy();

            // Same reasoning for the Mutual Fund card-config library: one request, paid only by the node
            // type that owns the three pickers, and never on page load — this editor is heavy enough.
            if (node.data && node.data.type === 'mutualFund') this.loadMutualFundCardConfigs();

            // Default Base Prompt text
            const defaultBasePrompt = `Find the Intent from the message of users.
#Condition for Response
1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0`;

            // Initialize inspector values from node data
            this.nodeInspector = {
                label: node.data.label || '',
                customLabel: node.data.customLabel || '',
                color: node.data.color || '#1976D2',
                placeholderText: node.data.placeholderText || '',
                required: node.data.required || false,
                selectedModel: Array.isArray(node.data.selectedModel)
                    ? node.data.selectedModel
                    : (node.data.selectedModel ? [node.data.selectedModel] : []),
                systemPrompt: node.data.systemPrompt || '',
                temperature: node.data.temperature ?? null,
                maxTokens: node.data.maxTokens ?? null,
                // AI Model Configuration properties (matching ModelManagement.vue defaults)
                provider: node.data.provider || null,
                model: node.data.model || null,
                useBaseModelPrompt: node.data.useBaseModelPrompt !== undefined ? node.data.useBaseModelPrompt : false,
                gender: node.data.gender || 'male',
                typingSpeed: node.data.typingSpeed ?? null,
                maxInputLength: node.data.maxInputLength ?? 200000,
                streamResponse: node.data.streamResponse !== undefined ? node.data.streamResponse : false,
                bypassOnError: node.data.bypassOnError !== undefined ? node.data.bypassOnError : false,
                // Cache settings
                enableCache: node.data.enableCache !== undefined ? node.data.enableCache : false,
                cacheTTLMinutes: node.data.cacheTTLMinutes ?? 1440,
                cacheScope: node.data.cacheScope || 'flow',
                enableSemanticCache: node.data.enableSemanticCache !== undefined ? node.data.enableSemanticCache : false,
                similarityThreshold: node.data.similarityThreshold ?? 0.95,
                similarityMargin: node.data.similarityMargin ?? 0.05,
                jsonResponse: node.data.jsonResponse !== undefined ? node.data.jsonResponse : false,
                enableTime: node.data.enableTime ?? null,
                timeZone: node.data.timeZone || null,
                enableTransactionId: node.data.enableTransactionId ?? null,
                enableInputMessageReplace: node.data.enableInputMessageReplace !== undefined ? node.data.enableInputMessageReplace : false,
                enableVoicePromptReplace: node.data.enableVoicePromptReplace !== undefined ? node.data.enableVoicePromptReplace : false,
                enableLanguageReplace: node.data.enableLanguageReplace !== undefined ? node.data.enableLanguageReplace : false,
                memoryTurn: node.data.memoryTurn ?? null,
                memoryDuration: node.data.memoryDuration ?? null,
                shortTermMemory: node.data.shortTermMemory ?? null,
                longTermMemory: node.data.longTermMemory !== undefined ? node.data.longTermMemory : false,
                isChainMassage: node.data.isChainMassage !== undefined ? node.data.isChainMassage : false,
                lenChainMassage: node.data.lenChainMassage ?? null,
                timeSecondChainMassage: node.data.timeSecondChainMassage ?? null,
                topP: node.data.topP ?? null,
                frequencyPenalty: node.data.frequencyPenalty ?? null,
                presencePenalty: node.data.presencePenalty ?? null,
                stopSequence: node.data.stopSequence || null,
                loadingBar: node.data.loadingBar !== undefined ? node.data.loadingBar : false,
                loadingBarDuration: node.data.loadingBarDuration || 0,
                monitorLineUserId: node.data.monitorLineUserId || null,
                monitorSocialID: node.data.monitorSocialID || null,
                showLineName: node.data.showLineName !== undefined ? node.data.showLineName : false,
                forbiddenWords: node.data.forbiddenWords || [''],
                nodeTimeout: node.data.nodeTimeout ?? 0,
                nodeMaxRetry: node.data.nodeMaxRetry ?? 0,
                // LLM Enrich node properties (LLM keys + bypassOnError shared above)
                llmEnrichName: node.data.llmEnrichName || '',
                llmEnrichConnectorText: node.data.llmEnrichConnectorText || '',
                // Button Style node properties (style JSON per marker; blank = server default)
                buttonStyleName: node.data.buttonStyleName || '',
                productButtonStyle: node.data.productButtonStyle || '',
                serviceButtonStyle: node.data.serviceButtonStyle || '',
                // Tax Planning node properties (LLM keys shared above; these are tax-specific)
                taxPlanningYearMode: node.data.taxPlanningYearMode || 'active',
                taxPlanningYearId: node.data.taxPlanningYearId ?? null,
                taxPlanningAskWhenMultipleActive: node.data.taxPlanningAskWhenMultipleActive ?? true,
                taxPlanningFlowMode: node.data.taxPlanningFlowMode || 'hybrid',
                taxPlanningPersist: node.data.taxPlanningPersist ?? true,
                taxPlanningCompletionMessage: node.data.taxPlanningCompletionMessage || '',
                taxPlanningName: node.data.taxPlanningName || '',
                // 🆕 Tax Planning node-level split system prompts: Question (asking) + Answer (extracting)
                questionSystemPrompt: node.data.questionSystemPrompt || '',
                answerSystemPrompt: node.data.answerSystemPrompt || '',
                // 🆕 Optional separate model for question-phrasing steps only (falls back to main provider/model)
                taxPlanningUseQuestionModel: node.data.taxPlanningUseQuestionModel ?? false,
                taxPlanningQuestionProvider: node.data.taxPlanningQuestionProvider || null,
                taxPlanningQuestionModel: node.data.taxPlanningQuestionModel || null,
                // Tax Planning V2 node properties. All config (LLM / node system prompts / tax
                // year / form behaviour) moved to the versioned `tax.TaxPlanningSettingsV2` rows,
                // so the node only carries its label + which settings version it pins
                // (null = resolve the active version at runtime).
                taxPlanningV2Name: node.data.taxPlanningV2Name || '',
                taxPlanningV2SettingsVersionId: node.data.taxPlanningV2SettingsVersionId ?? null,
                queryRewriterTimeout: node.data.queryRewriterTimeout ?? 0,
                queryRewriterMaxRetry: node.data.queryRewriterMaxRetry ?? 0,
                intentaiTimeout: node.data.intentaiTimeout ?? 0,
                intentaiMaxRetry: node.data.intentaiMaxRetry ?? 0,
                // Processing mode for TTS nodes (ReformatTTS default: LLM, SSMLFormat default: RuleBased)
                processingMode: node.data.processingMode || (node.data.type === 'ssmlFormat' ? 'RuleBased' : 'LLM'),
                intentGroupName: node.data.intentGroupName || '',
                intents: node.data.type === 'intent'
                    ? (node.data.intents || [
                        {
                            id: `intent-${this.generateGuid()}`,
                            name: 'Intent Name',
                            priority: 0, // Default priority
                            prompt: '',
                            basePrompt: defaultBasePrompt, // Use default base prompt
                            enableBasePrompt: false, // Disable base prompt by default
                            messages: [
                                { id: `msg-${this.generateGuid()}`, text: '' }
                            ],
                            mainAction: { id: `main-${this.generateGuid()}`, label: 'Main Action' },
                            parallelActions: []
                        }
                    ])
                    : [],
                examples: node.data.examples || '',
                // Initialize actions and outputs for intent nodes
                actions: node.data.type === 'intent'
                    ? (node.data.actions || [
                        {
                            id: `action-${this.generateGuid()}`,
                            label: 'Action 1',
                            parallels: [
                                { id: `parallel-${this.generateGuid()}`, label: 'Parallel Action 1' }
                            ]
                        }
                    ])
                    : [],
                outputs: node.data.type === 'intent'
                    ? (node.data.outputs || [])
                    : [],
                // Smart Intent properties
                smartIntentGroupName: node.data.smartIntentGroupName || '',
                smartIntents: node.data.type === 'smartIntent'
                    ? (node.data.smartIntents || [
                        {
                            id: `smart-intent-${this.generateGuid()}`,
                            name: 'Smart Intent Name',
                            priority: 0,
                            prompt: '',
                            basePrompt: defaultBasePrompt,
                            enableBasePrompt: false,
                            useMemory: false,
                            messages: [
                                { id: `msg-${this.generateGuid()}`, text: '' }
                            ],
                            mainAction: {
                                id: `main-${this.generateGuid()}`,
                                label: 'Main Action',
                                useAI: true,
                                // Basic Information - match AI Model defaults
                                provider: null,
                                model: null,
                                apiVersion: null,
                                gender: 'male',
                                typingSpeed: null,
                                maxInputLength: 200000,
                                // Response Options - match AI Model defaults
                                streamResponse: false,
                                enableTime: null,
                                timeZone: null,
                                enableTransactionId: null,
                                // Prompt Replacement Flags
                                enableInputMessageReplace: false,
                                enableVoicePromptReplace: false,
                                enableLanguageReplace: false,
                                jsonResponse: false,
                                // AI Parameters - match AI Model defaults
                                temperature: null,
                                topP: null,
                                frequencyPenalty: null,
                                presencePenalty: null,
                                maxTokens: null,
                                stopSequence: null,
                                nodeTimeout: null,
                                nodeMaxRetry: null,
                                memoryTurn: null,
                                memoryDuration: null,
                                shortTermMemory: null,
                                longTermMemory: false,
                                // Multi-turn Input Configuration - match AI Model defaults
                                isChainMassage: false,
                                lenChainMassage: null,
                                timeSecondChainMassage: null,
                                // Forbidden Words - match AI Model defaults
                                forbiddenWords: [''],
                                // Knowledge Configuration
                                knowledgeType: '',
                                // Knowledge Sources Enable Flags
                                enableDocuments: false,
                                enableDatabaseIntegration: false,
                                enableProducts: false,
                                enableApiKnowledge: false,
                                // Documents properties
                                document: [],
                                topNChunks: 5,
                                cosineSimilarity: 0.5,
                                // Summary Memory (Embedding) properties
                                summaryMemoryEnabled: false,
                                summaryMemoryProvider: null,
                                summaryMemoryModel: null,
                                summaryMemoryApiVersion: null,
                                summaryMemoryJsonRequest: null,
                                summaryMemorySystemPrompt: '',
                                summaryMemoryTemperature: 0,
                                summaryMemoryMaxTokens: 500,
                                summaryMemoryTopP: 1,
                                summaryMemoryFrequencyPenalty: null,
                                summaryMemoryPresencePenalty: null,
                                summaryMemoryMemoryTurn: 0,
                                summaryMemoryMemoryDuration: 60,
                                summaryMemoryTimeout: 0,
                                // Database Integration properties
                                tableName: null,
                                sqlExtractionModel: null,
                                promptSqlNoData: '',
                                // Products properties
                                productSourceType: 'product',
                                productGroupCollectionId: null,
                                productGroupCollectionName: '',
                                productGroupList: [],
                                productTopNChunks: 5,
                                productCosineSimilarity: 0.5,
                                // API Knowledge properties
                                knowledgeApiUrl: '',
                                knowledgeHttpMethod: 'GET',
                                knowledgeResponseDataPath: ''
                            }
                        }
                    ])
                    : [],
                smartIntentFallback: node.data.type === 'smartIntent'
                    ? (node.data.smartIntentFallback || {
                        useAI: false,
                        // Basic Information - match AI Model defaults
                        provider: null,
                        model: null,
                        gender: 'male',
                        typingSpeed: null,
                        maxInputLength: 200000,
                        // Response Options - match AI Model defaults
                        streamResponse: false,
                        jsonResponse: false,
                        enableTime: null,
                        timeZone: null,
                        enableTransactionId: null,
                        // Prompt Replacement Flags
                        enableInputMessageReplace: false,
                        enableVoicePromptReplace: false,
                        enableLanguageReplace: false,
                        // AI Parameters - match AI Model defaults
                        temperature: null,
                        topP: null,
                        frequencyPenalty: null,
                        presencePenalty: null,
                        maxTokens: null,
                        stopSequence: null,
                        // Memory Settings - match AI Model defaults
                        memoryTurn: null,
                        memoryDuration: null,
                        shortTermMemory: null,
                        longTermMemory: false,
                        // Multi-turn Input Configuration - match AI Model defaults
                        isChainMassage: false,
                        lenChainMassage: null,
                        timeSecondChainMassage: null,
                        // Forbidden Words - match AI Model defaults
                        forbiddenWords: [''],
                        nodeTimeout: 0,
                    })
                    : {},
                // Guardrail AI properties
                guardrailType: node.data.guardrailType || 'content_safety',
                guardrailModel: node.data.guardrailModel || 'gpt-4',
                guardrailPrompt: node.data.guardrailPrompt || '',
                allowAgentGuardrailBypass: node.data.allowAgentGuardrailBypass ?? true,
                safetyThreshold: node.data.safetyThreshold || 80,
                enableLogging: node.data.enableLogging !== undefined ? node.data.enableLogging : true,
                // Intent AI properties
                intentaiModel: Array.isArray(node.data.intentaiModel)
                    ? (node.data.intentaiModel.length > 0 ? node.data.intentaiModel[0] : null)
                    : (node.data.intentaiModel || (Array.isArray(node.data.intentModel) ? (node.data.intentModel.length > 0 ? node.data.intentModel[0] : null) : node.data.intentModel) || null),
                intentAiModelApiVersion: node.data.intentAiModelApiVersion || null,
                intentaiProvider: node.data.intentaiProvider || node.data.provider || '',
                intentaiGender: node.data.intentaiGender || node.data.gender || '',
                intentaiTypingSpeed: node.data.intentaiTypingSpeed || node.data.typingSpeed || 0,
                intentaiMaxInputLength: node.data.intentaiMaxInputLength || node.data.maxInputLength || 4000,
                intentaiStreamResponse: node.data.intentaiStreamResponse !== undefined ? node.data.intentaiStreamResponse : (node.data.streamResponse !== undefined ? node.data.streamResponse : true),
                intentaiJsonResponse: node.data.intentaiJsonResponse !== undefined ? node.data.intentaiJsonResponse : (node.data.jsonResponse !== undefined ? node.data.jsonResponse : true),
                intentaiEnableTime: node.data.intentaiEnableTime !== undefined ? node.data.intentaiEnableTime : (node.data.enableTime !== undefined ? node.data.enableTime : false),
                intentaiTimeZone: node.data.intentaiTimeZone || node.data.timeZone || '',
                intentaiEnableTransactionId: node.data.intentaiEnableTransactionId !== undefined ? node.data.intentaiEnableTransactionId : (node.data.enableTransactionId !== undefined ? node.data.enableTransactionId : false),
                intentaiMemoryTurn: node.data.intentaiMemoryTurn || node.data.memoryTurn || null,
                intentaiMemoryDuration: node.data.intentaiMemoryDuration || node.data.memoryDuration || 60,
                intentaiShortTermMemory: node.data.intentaiShortTermMemory || node.data.shortTermMemory || 200,
                intentaiLongTermMemory: node.data.intentaiLongTermMemory !== undefined ? node.data.intentaiLongTermMemory : (node.data.longTermMemory !== undefined ? node.data.longTermMemory : false),
                intentaiIsChainMassage: node.data.intentaiIsChainMassage !== undefined ? node.data.intentaiIsChainMassage : (node.data.isChainMassage !== undefined ? node.data.isChainMassage : false),
                intentaiLenChainMassage: node.data.intentaiLenChainMassage || node.data.lenChainMassage || 5,
                intentaiTimeSecondChainMassage: node.data.intentaiTimeSecondChainMassage || node.data.timeSecondChainMassage || 30,
                intentaiTemperature: node.data.intentaiTemperature ?? node.data.temperature ?? 0,
                intentaiTopP: node.data.intentaiTopP || node.data.topP || 1.0,
                intentaiFrequencyPenalty: node.data.intentaiFrequencyPenalty || node.data.frequencyPenalty || 0,
                intentaiPresencePenalty: node.data.intentaiPresencePenalty || node.data.presencePenalty || 0,
                intentaiMaxTokens: node.data.intentaiMaxTokens || node.data.maxTokens || 1000,
                intentaiSystemPrompt: node.data.intentaiSystemPrompt || node.data.intentSystemPrompt || '',
                intentaiExamples: node.data.intentaiExamples || node.data.intentExamples || '',
                intentaiConfidence: node.data.intentaiConfidence || node.data.intentConfidence || 75,
                intentaiFallbackIntent: node.data.intentaiFallbackIntent || node.data.fallbackIntent || 'unknown',
                intentaiShowLineName: node.data.intentaiShowLineName !== undefined ? node.data.intentaiShowLineName : (node.data.showLineName !== undefined ? node.data.showLineName : false),
                intentaiLoadingBar: node.data.intentaiLoadingBar !== undefined ? node.data.intentaiLoadingBar : (node.data.loadingBar !== undefined ? node.data.loadingBar : false),
                intentaiLoadingBarDuration: node.data.intentaiLoadingBarDuration || node.data.loadingBarDuration || 0,
                intentaiMonitorLineUserId: node.data.intentaiMonitorLineUserId || node.data.monitorLineUserId || null,
                intentaiMonitorSocialID: node.data.intentaiMonitorSocialID || node.data.monitorSocialID || null,
                intentaiForbiddenWords: node.data.intentaiForbiddenWords || node.data.forbiddenWords || [''],
                // API Trigger properties
                apiUrl: node.data.apiUrl || '',
                httpMethod: node.data.httpMethod || 'POST',
                headers: node.data.headers || '',
                textResponse: node.data.textResponse || '',
                jsonValidate: node.data.jsonValidate || '{\n   "Name":"Value"\n}',
                modelJsonValidator: node.data.modelJsonValidator || null,
                useLHBService: node.data.useLHBService !== undefined ? node.data.useLHBService : false,
                listHeadersAPITrigger: node.data.listHeadersAPITrigger || [
                    {
                        key: '',
                        value: '',
                    },
                ],
                listValidation: node.data.listValidation || [
                    {
                        key: '',
                        typeValidation: '',
                        numberOfCharacter: '',
                        regExp: '',
                        promptMessage: '',
                    },
                ],
                // Contact Agent properties
                departmentName: node.data.departmentName || '',
                contactAgentMessage: node.data.contactAgentMessage || '',
                contactAgentMessageChinese: node.data.contactAgentMessageChinese || '',
                contactAgentMessageEnglish: node.data.contactAgentMessageEnglish || '',
                handoffMessage: node.data.handoffMessage || '',
                botDisableTime: node.data.botDisableTime || null,
                disableBot: node.data.disableBot !== undefined ? node.data.disableBot : true,
                // Switch properties
                switchName: node.data.switchName || '',
                switchOutputs: node.data.type === 'switch'
                    ? (node.data.switchOutputs || [
                        {
                            id: `switch-output-${this.generateGuid()}`, // Use GUID
                            name: 'Output 1',
                            condition: ''
                        },
                        {
                            id: `switch-output-${this.generateGuid()}`, // Use GUID
                            name: 'Output 2',
                            condition: ''
                        }
                    ])
                    : [],
                // Router properties
                routerName: node.data.routerName || '',
                routerPrompt: node.data.routerPrompt || '',
                confidenceThreshold: node.data.confidenceThreshold ?? 0.5,
                routerOutputs: node.data.type === 'router'
                    ? (node.data.routerOutputs || [
                        {
                            id: `router-output-${this.generateGuid()}`,
                            intentName: 'default',
                            label: 'Default Output'
                        }
                    ])
                    : [],
                // Advanced Router properties
                advancedRouterName: node.data.advancedRouterName || '',
                advancedRouterPrompt: node.data.advancedRouterPrompt || '',
                advancedRouterConfidenceThreshold: node.data.advancedRouterConfidenceThreshold ?? 0.5,
                advancedRouterMaxDepth: node.data.advancedRouterMaxDepth ?? 2,
                advancedRouterCustomSchemaEnabled: node.data.customSchemaEnabled ?? false,
                advancedRouterSchemaTemplateText: JSON.stringify(
                    node.data.customSchemaTemplate || this.getDefaultAdvancedRouterSchemaTemplate(),
                    null,
                    2
                ),
                advancedRouterIntentField: node.data.schemaFieldMapping?.intentField || 'capability_intent',
                advancedRouterConfidenceField: node.data.schemaFieldMapping?.confidenceField || 'confidence',
                advancedRouterSubIntentsField: node.data.schemaFieldMapping?.subIntentsField || 'intents',
                advancedRouterSchemaError: '',
                advancedRouterOutputs: node.data.type === 'advancedRouter'
                    ? (node.data.advancedRouterOutputs || [
                        {
                            id: `advrouter-output-${this.generateGuid()}`,
                            intentName: 'Category1',
                            label: 'Category 1',
                            subIntents: []
                        }
                    ])
                    : [],
                // LH Router properties (internal keys unchanged)
                dynamicRouterName: node.data.dynamicRouterName || '',
                dynamicRouterPrompt: node.data.dynamicRouterPrompt || '',
                dynamicRouterConfidenceThreshold: node.data.dynamicRouterConfidenceThreshold ?? 0.5,
                dynamicRouterMaxDepth: node.data.dynamicRouterMaxDepth ?? 2,
                dynamicRouterOutputs: node.data.type === 'dynamicRouter'
                    ? (node.data.dynamicRouterOutputs || [
                        {
                            id: `dynamicRouter-output-${this.generateGuid()}`,
                            intentName: 'Category1',
                            label: 'Category 1',
                            subIntents: []
                        }
                    ])
                    : [],
                // Condition properties
                conditionType: node.data.conditionType || 'value_comparison',
                leftOperand: node.data.leftOperand || '',
                operator: node.data.operator || 'equals',
                rightOperand: node.data.rightOperand || '',
                conditionExpression: node.data.conditionExpression || '',
                trueLabel: node.data.trueLabel || 'True',
                falseLabel: node.data.falseLabel || 'False',
                minValue: node.data.minValue || 0,
                maxValue: node.data.maxValue || 100,
                expectedValue: node.data.expectedValue !== undefined ? node.data.expectedValue : true,
                // JSON Condition properties
                jsonConditionName: node.data.jsonConditionName || 'Rule-Based',
                matchMode: node.data.matchMode || 'all',
                jsonConditions: node.data.type === 'jsonCondition'
                    ? (node.data.conditions && Array.isArray(node.data.conditions) && node.data.conditions.length > 0
                        ? JSON.parse(JSON.stringify(node.data.conditions))
                        : [{ id: `jc-${this.generateGuid()}`, jsonPath: '', operator: '', compareValue: '' }])
                    : [],
                matchLabel: node.data.matchLabel || 'Match',
                fallbackLabel: node.data.fallbackLabel || 'Fallback',
                // Service Fallback properties
                serviceType: node.data.serviceType || 'TRANSFER',
                // Primary (success) output
                // Two or more matches (Rule 1.2) — must stay identical to the backend
                // ServiceFallbackNodeProcessor.DefaultPrimaryMessage* constants.
                serviceFallbackPrimaryMessageTH: node.data.primaryMessageTH || '***กรุณาเลือกรายการที่ต้องการทำธุรกรรม และตรวจสอบข้อมูลก่อนทำรายการนะคะ',
                serviceFallbackPrimaryMessageEN: node.data.primaryMessageEN || '***Please select the transaction and review the details before confirming.',
                serviceFallbackPrimaryMessageCN: node.data.primaryMessageCN || '***請選擇欲進行交易的項目，並於操作前確認資料是否正確。',
                // Exactly one match (Rule 1.1)
                serviceFallbackPrimarySingleMessageTH: node.data.primarySingleMessageTH || '***กรุณาตรวจสอบข้อมูลธุรกรรมก่อนทำรายการนะคะ Genie กำลังพาไปหน้าทำรายการ',
                serviceFallbackPrimarySingleMessageEN: node.data.primarySingleMessageEN || '***Please review the details before confirming the transaction. I am taking you to the transaction screen.',
                serviceFallbackPrimarySingleMessageCN: node.data.primarySingleMessageCN || '***請在確認交易前先檢查詳細資料 我正在帶您前往交易畫面。',
                serviceFallbackPrimaryAllowEditOutput: node.data.primaryAllowEditOutput || false,
                serviceFallbackPrimaryOutputJson: node.data.primaryOutputJson || this.buildServiceFallbackPrimaryDefault(node.data.serviceType || 'TRANSFER'),
                // Fallback output (existing keys)
                serviceFallbackMessageTH: node.data.messageTH || 'ไม่พบรายชื่อนี้ในระบบ Genie กำลังพาไปหน้าทำรายการ กรุณาเลือกทำรายการด้วยตนเอง',
                serviceFallbackMessageEN: node.data.messageEN || "I can't find this name in the system. I'm taking you to the transaction screen—please select the transaction manually.",
                serviceFallbackMessageCN: node.data.messageCN || '系統中找不到此名稱。我正在帶您前往交易畫面，請您自行選擇交易項目。',
                serviceFallbackAllowEditOutput: node.data.allowEditOutput || false,
                serviceFallbackOutputJson: node.data.outputJson || JSON.stringify({
                    message: '{{auto_message}}',
                    action: 'callback',
                    navlist: [{
                        serviceType: node.data.serviceType || 'TRANSFER',
                        toType: 'N',
                        toValue: null,
                        accountNumber: null,
                        amount: 0
                    }]
                }, null, 2),
                // Delay properties
                delayType: node.data.delayType || 'fixed',
                delayValue: node.data.delayValue || 5,
                delayUnit: node.data.delayUnit || 'seconds',
                randomMin: node.data.randomMin || 1,
                randomMax: node.data.randomMax || 10,
                delayMessage: node.data.delayMessage || '',
                showProgress: node.data.showProgress !== undefined ? node.data.showProgress : true,
                delayExpression: node.data.delayExpression || '',
                // Documents properties
                documentSourceType: node.data.documentSourceType || 'doc', // 'doc' | 'group'
                documentGroupId: node.data.documentGroupId || null,
                documentGroupName: node.data.documentGroupName || '',
                document: node.data.document || [],
                selectedDocumentData: node.data.selectedDocumentData || [],
                enableSpecificDataSelection: node.data.enableSpecificDataSelection !== undefined ? node.data.enableSpecificDataSelection : false,
                documentVectorName: node.data.documentVectorName || '',
                textReplacement: node.data.textReplacement || [],
                topNChunks: node.data.topNChunks || 5,
                cosineSimilarity: node.data.cosineSimilarity || 0.7,
                // Summary Memory (Embedding) properties
                summaryMemoryEnabled: node.data.summaryMemoryEnabled ?? false,
                summaryMemoryProvider: node.data.summaryMemoryProvider || null,
                summaryMemoryModel: node.data.summaryMemoryModel || null,
                summaryMemoryApiVersion: node.data.summaryMemoryApiVersion || null,
                summaryMemoryJsonRequest: node.data.summaryMemoryJsonRequest || null,
                summaryMemorySystemPrompt: node.data.summaryMemorySystemPrompt || '',
                summaryMemoryTemperature: node.data.summaryMemoryTemperature ?? 0,
                summaryMemoryMaxTokens: node.data.summaryMemoryMaxTokens ?? 500,
                summaryMemoryTopP: node.data.summaryMemoryTopP ?? 1,
                summaryMemoryFrequencyPenalty: node.data.summaryMemoryFrequencyPenalty ?? null,
                summaryMemoryPresencePenalty: node.data.summaryMemoryPresencePenalty ?? null,
                summaryMemoryMemoryTurn: node.data.summaryMemoryMemoryTurn ?? 0,
                summaryMemoryMemoryDuration: node.data.summaryMemoryMemoryDuration ?? 60,
                summaryMemoryTimeout: node.data.summaryMemoryTimeout ?? 0,
                // Community Graph RAG properties
                communityId: node.data.communityId || null,
                communityTopGroups: node.data.communityTopGroups || 3,
                communityTopChunks: node.data.communityTopChunks || 5,
                communitySearchMethod: node.data.communitySearchMethod || 'topn',
                communityMinCosineScore: node.data.communityMinCosineScore || 0.7,
                // Meta Community Graph RAG properties
                metaCommunityId: node.data.metaCommunityId || null,
                metaCommunityTopGroups: node.data.metaCommunityTopGroups || 3,
                metaCommunityTopChunks: node.data.metaCommunityTopChunks || 5,
                metaCommunitySearchMethod: node.data.metaCommunitySearchMethod || 'topn',
                metaCommunityMinCosineScore: node.data.metaCommunityMinCosineScore || 0.7,
                // Topic/SubTopic Filter properties
                filterTopic: node.data.filterTopic || null,
                filterSubTopic: node.data.filterSubTopic || null,
                filterMode: node.data.filterMode || 'include',
                // Database Integration properties
                tableName: node.data.tableName || null,
                sqlExtractionModel: node.data.sqlExtractionModel || null,
                promptSqlNoData: node.data.promptSqlNoData || '',
                // Mutual Fund view lookup properties
                mutualFundLookupType: node.data.mutualFundLookupType || 'fundCode',
                mutualFundLookupValue: node.data.mutualFundLookupValue || '',
                mutualFundComparisonValues: node.data.mutualFundComparisonValues || '',
                mutualFundFactsheetConfigCode: node.data.mutualFundFactsheetConfigCode || '',
                mutualFundComparisonConfigCode: node.data.mutualFundComparisonConfigCode || '',
                mutualFundPerformanceConfigCode: node.data.mutualFundPerformanceConfigCode || '',
                // The picker cards (type14): the list the customer picks a fund from when the search
                // matched MORE THAN ONE — one config per mode. Blank is meaningful ("no buttons for
                // that case this turn"), so `|| ''`.
                mutualFundPickerConfigCode: node.data.mutualFundPickerConfigCode || '',
                mutualFundPickerCompareConfigCode: node.data.mutualFundPickerCompareConfigCode || '',
                mutualFundDemoMode: node.data.mutualFundDemoMode === true,
                // Mutual Fund resolver — caps.
                // 🔴 `??` and not `||`: the switches are booleans and the caps are numbers, so `false` and a
                //    deliberate small number have to survive. `||` would silently re-default them, and because
                //    openNodeInspector REPLACES nodeInspector wholesale on every node click, that reads as a
                //    switch that keeps flipping itself back on.
                mutualFundResolverEnabled: node.data.mutualFundResolverEnabled ?? false,
                mutualFundScopeTaxSavingFunds: node.data.mutualFundScopeTaxSavingFunds || '',
                mutualFundScopeRecommendedOnly: node.data.mutualFundScopeRecommendedOnly ?? false,
                mutualFundCandidateLimit: node.data.mutualFundCandidateLimit ?? 200,
                // How many funds the node pulls OUT OF THE DB in one turn — the whole-turn ceiling.
                mutualFundTopN: node.data.mutualFundTopN ?? 10,
                // Now means "how many funds go into the comparison card" — nothing else reads it,
                // and it is the ceiling of that ONE card, never of the search above it.
                mutualFundMaxSelect: node.data.mutualFundMaxSelect ?? 3,
                // #924 fuzzy search — absent = OFF (the same reading the backend makes), floor 0.78.
                mutualFundFuzzyEnabled: node.data.mutualFundFuzzyEnabled ?? false,
                mutualFundFuzzyMinSimilarity: node.data.mutualFundFuzzyMinSimilarity ?? 0.78,
                mutualFundFuzzyTopGap: node.data.mutualFundFuzzyTopGap ?? 0.1,
                // Resolver prompts. Blank means "use the built-in default" (the textarea shows it as
                // placeholder), so `|| ''` is right here — never seed the default text into the field.
                mutualFundExtractionTemplate: node.data.mutualFundExtractionTemplate || '',
                // 🔴 The three below have NO field in the inspector any more (the prompt carries no fund list),
                //    but they are still mapped and still saved so an older flow round-trips unchanged.
                mutualFundSelectionTemplate: node.data.mutualFundSelectionTemplate || '',
                mutualFundFundListItemTemplate: node.data.mutualFundFundListItemTemplate || '',
                mutualFundNoDescriptionLabel: node.data.mutualFundNoDescriptionLabel || '',
                // 🔴 apiVersion has never been in this literal, so the read-only "API Version" row (which is
                //    `v-if="nodeInspector.apiVersion"`) vanished the moment the inspector was reopened, even
                //    though node.data still held the value. It is only auto-filled by the model watcher at
                //    changeModel(), which writes both nodeInspector and node.data — mapping it back here is
                //    what makes the row survive a reopen.
                apiVersion: node.data.apiVersion || null,
                // Products properties
                productSourceType: node.data.productSourceType || 'product', // 'product' | 'group'
                productGroupCollectionId: node.data.productGroupCollectionId || null,
                productGroupCollectionName: node.data.productGroupCollectionName || '',
                productGroupList: node.data.productGroupList || [],
                productTopNChunks: node.data.productTopNChunks || 5,
                productCosineSimilarity: node.data.productCosineSimilarity || 0.7,
                // Knowledge API properties
                knowledgeApiUrl: node.data.knowledgeApiUrl || '',
                knowledgeHttpMethod: node.data.knowledgeHttpMethod || 'GET',
                listHeaders: node.data.listHeaders || [
                    {
                        key: '',
                        value: '',
                    },
                ],
                knowledgeRequestBody: node.data.knowledgeRequestBody || '',
                responseDataPath: node.data.responseDataPath || '',
                // API Request properties
                apiRequestUrl: node.data.apiRequestUrl || '',
                apiRequestMethod: node.data.apiRequestMethod || 'GET',
                apiRequestHeaders: node.data.apiRequestHeaders || [{ key: '', value: '' }],
                apiRequestBody: node.data.apiRequestBody || '',
                apiRequestResponsePath: node.data.apiRequestResponsePath || '',
                useResultAsMessage: node.data.useResultAsMessage || false,
                // Request timeout in seconds (apiRequest + serviceMatcher favorite lookup)
                timeout: node.data.timeout ?? 30,
                // Favorite Matcher properties (serviceType shared with Service Fallback above)
                // Service Matcher defaults this to '' (use the node's own API result);
                // Favorite Matcher keeps its legacy 'apiRequestResponse' default.
                favoritesSourceVariable: node.data.type === 'serviceMatcher'
                    ? (node.data.favoritesSourceVariable || '')
                    : (node.data.favoritesSourceVariable || 'apiRequestResponse'),
                outputVariable: node.data.outputVariable || 'userMessage',
                enableEmbedding: node.data.enableEmbedding ?? true,
                fuzzyThreshold: node.data.fuzzyThreshold ?? 0.85,
                embeddingThreshold: node.data.embeddingThreshold ?? 0.65,
                // Service Matcher properties (serviceType/outputVariable/embedding shared above)
                inputVariable: node.data.inputVariable || 'userMessage',
                activeOnly: node.data.activeOnly ?? true,
                dataIntegrationId: node.data.dataIntegrationId || null,
                // Favorite/Service Matcher result cap (0 = unlimited)
                maxResults: node.data.maxResults ?? 0,
                // Service Matcher — unified Favorite Lookup (API Request + Favorite Matcher + rules)
                enableFavoriteLookup: node.data.enableFavoriteLookup ?? false,
                matchStrategy: node.data.matchStrategy || 'favorite-first',
                favoriteEnableEmbedding: node.data.favoriteEnableEmbedding ?? true,
                favoriteKeywordFunnel: node.data.favoriteKeywordFunnel ?? true,
                favoriteFuzzyThreshold: node.data.favoriteFuzzyThreshold ?? 0.85,
                favoriteEmbeddingThreshold: node.data.favoriteEmbeddingThreshold ?? 0.65,
                favoriteMaxResults: node.data.favoriteMaxResults ?? 0,
                favoriteMatchMode: node.data.favoriteMatchMode || 'all',
                serviceMatchMode: node.data.serviceMatchMode || 'all',
                // Inspector-side copies of node.data.favoriteConditions / serviceConditions.
                // Deliberately named differently (same trick as jsonConditions <-> conditions)
                // so writing the node property does not replace the array these are bound to.
                serviceMatcherFavoriteRules: node.data.type === 'serviceMatcher'
                    ? this.cloneServiceMatcherRules(node.data.favoriteConditions)
                    : [],
                serviceMatcherServiceRules: node.data.type === 'serviceMatcher'
                    ? this.cloneServiceMatcherRules(node.data.serviceConditions)
                    : [],
                favoriteLabel: node.data.favoriteLabel || 'Favorite',
                serviceLabel: node.data.serviceLabel || 'Service',
                noMatchLabel: node.data.noMatchLabel || 'No Match',
                // SharePoint properties
                sharepoint: {
                    siteUrl: node.data.sharepoint?.siteUrl || '',
                    tenantId: node.data.sharepoint?.tenantId || '',
                    clientId: node.data.sharepoint?.clientId || '',
                    clientSecret: node.data.sharepoint?.clientSecret || '',
                    folderPath: node.data.sharepoint?.folderPath || '',
                    filename: node.data.sharepoint?.filename || ''
                },
                // Email properties
                emailProvider: node.data.emailProvider || 'mailgun',
                toEmail: node.data.toEmail || '',
                ccEmails: node.data.ccEmails || [],
                bccEmails: node.data.bccEmails || [],
                subject: node.data.subject || '',
                emailTemplate: node.data.emailTemplate || 'custom',
                emailBody: node.data.emailBody || '',
                isHtml: node.data.isHtml !== undefined ? node.data.isHtml : false,
                // Interest Optimizer properties
                ioAnswerModel: node.data.ioAnswerModel || null,
                ioFallbackPrompt: node.data.ioFallbackPrompt || '',
                ioParserModel: node.data.ioParserModel || null,
                ioDocuments: node.data.ioDocuments || [],
                ioAccountCodes: node.data.ioAccountCodes || [],
                ioRequiredFields: node.data.ioRequiredFields || [],
                enableLanguageReplace: node.data.enableLanguageReplace || false,
                enableVoicePromptReplace: node.data.enableVoicePromptReplace || false,
                // Loan Interest properties
                liAnswerModel: node.data.liAnswerModel || null,
                liFallbackPrompt: node.data.liFallbackPrompt || '',
                liRequiredFields: node.data.liRequiredFields || [],
                liParserModel: node.data.liParserModel || null,
                liDocuments: node.data.liDocuments || [],
                liInterestRates: node.data.liInterestRates || [],
                // Interest Optimizer V2 properties
                ioV2MetaDocs: node.data.ioV2MetaDocs || [],
                // Loan Interest V2 properties
                liV2MetaDocs: node.data.liV2MetaDocs || [],
                // Orchestrator properties
                orchestratorName: node.data.orchestratorName || '',
                confidenceThreshold: node.data.confidenceThreshold !== undefined ? node.data.confidenceThreshold : 0.7,
                guardrailPrompt: node.data.guardrailPrompt || '',
                allowAgentGuardrailBypass: node.data.allowAgentGuardrailBypass ?? true,
                enableMultiAgent: node.data.enableMultiAgent !== undefined ? node.data.enableMultiAgent : false,
                multiAgentStrategy: node.data.multiAgentStrategy || 'pipeline',
                // Orchestrator - configurable prompts / messages (blank = built-in default)
                orchSingleSelectionTemplate: node.data.orchSingleSelectionTemplate || '',
                orchPipelineSelectionTemplate: node.data.orchPipelineSelectionTemplate || '',
                orchVerifySelectionTemplate: node.data.orchVerifySelectionTemplate || '',
                orchAgentListItemTemplate: node.data.orchAgentListItemTemplate || '',
                orchAgentNoToolsLabel: node.data.orchAgentNoToolsLabel || '',
                orchConversationStateTemplate: node.data.orchConversationStateTemplate || '',
                orchConversationStateFactTemplate: node.data.orchConversationStateFactTemplate || '',
                orchCompanionNote: node.data.orchCompanionNote || '',
                orchSequentialContextTemplate: node.data.orchSequentialContextTemplate || '',
                orchCheckerGoalTemplate: node.data.orchCheckerGoalTemplate || '',
                orchFallbackSubGoal: node.data.orchFallbackSubGoal || '',
                orchAggregationTemplate: node.data.orchAggregationTemplate || '',
                orchStrategyHintParallel: node.data.orchStrategyHintParallel || '',
                orchStrategyHintPipeline: node.data.orchStrategyHintPipeline || '',
                orchAggregationFallbackItemTemplate: node.data.orchAggregationFallbackItemTemplate || '',
                orchGuardrailFormatSuffix: node.data.orchGuardrailFormatSuffix || '',
                orchGuardrailUserTemplate: node.data.orchGuardrailUserTemplate || '',
                orchDirectLlmPrompt: node.data.orchDirectLlmPrompt || '',
                orchBlockedMessage: node.data.orchBlockedMessage || '',
                orchDirectLlmEmptyMessage: node.data.orchDirectLlmEmptyMessage || '',
                orchDirectLlmErrorMessage: node.data.orchDirectLlmErrorMessage || '',
                orchNoAgentSucceededMessage: node.data.orchNoAgentSucceededMessage || '',
                // Orchestrator - extended fields
                gender: node.data.gender || 'male',
                typingSpeed: node.data.typingSpeed ?? null,
                maxInputLength: node.data.maxInputLength ?? 200000,
                frequencyPenalty: node.data.frequencyPenalty ?? null,
                presencePenalty: node.data.presencePenalty ?? null,
                stopSequence: node.data.stopSequence || null,
                enableCache: node.data.enableCache ?? false,
                cacheTTLMinutes: node.data.cacheTTLMinutes ?? 1440,
                cacheScope: node.data.cacheScope || 'flow',
                enableSemanticCache: node.data.enableSemanticCache ?? false,
                similarityThreshold: node.data.similarityThreshold ?? 0.95,
                showLineName: node.data.showLineName ?? false,
                loadingBar: node.data.loadingBar ?? false,
                loadingBarDuration: node.data.loadingBarDuration || 0,
                monitorLineUserId: node.data.monitorLineUserId || null,
                monitorSocialID: node.data.monitorSocialID || null,
                forbiddenWords: node.data.forbiddenWords || [''],
                enableTime: node.data.enableTime ?? null,
                timeZone: node.data.timeZone || null,
                enableTransactionId: node.data.enableTransactionId ?? null,
                enableInputMessageReplace: node.data.enableInputMessageReplace ?? false,
                enableVoicePromptReplace: node.data.enableVoicePromptReplace ?? false,
                enableLanguageReplace: node.data.enableLanguageReplace ?? false,
                // Agent properties
                agentName: node.data.agentName || '',
                agentDescription: node.data.agentDescription || '',
                // 🔴 Tax Planning Agent's third key. It MUST be listed in THIS object literal, not only in the
                //    per-type blocks further down: openNodeInspector REPLACES nodeInspector wholesale on every node
                //    click, so a key that is absent here is `undefined` no matter what node.data holds — the switch
                //    then renders OFF for a node whose data says true, and toggling it looks like a save that never
                //    sticks. (`?? true` and not `|| true`: false has to survive, that is the whole setting.)
                bypassGuardrailWhenAlone: node.data.bypassGuardrailWhenAlone ?? true,
                // Same rule as the switch above: a key absent from THIS literal is undefined on every node click,
                // whatever node.data holds. The array is also copied element by element — nodeInspector is edited
                // in place by the picker, and sharing the node's own array would mutate saved data with no save.
                taxKnowledgeEnabled: node.data.taxKnowledgeEnabled ?? false,
                taxKnowledgeSelections: Array.isArray(node.data.taxKnowledgeSelections)
                    ? node.data.taxKnowledgeSelections.map(x => ({ header: x.header, category: x.category ?? null }))
                    : [],
                // Meta Documents node picker — same copy-element-by-element rule as taxKnowledgeSelections
                // above: the picker edits nodeInspector in place, sharing the node's own array would mutate
                // saved data with no save.
                metaDocSelections: Array.isArray(node.data.metaDocSelections)
                    ? node.data.metaDocSelections.map(x => ({ header: x.header, category: x.category ?? null }))
                    : [],
                metaDocDescription: node.data.metaDocDescription || '',
                personaPrompt: node.data.personaPrompt || '',
                agentFinalSummaryTemplate: node.data.agentFinalSummaryTemplate || '',
                selectedTools: node.data.selectedTools || [],
                temperature: node.data.temperature !== undefined ? node.data.temperature : 0.7,
                enableAgentToolDecision: node.data.enableAgentToolDecision ?? false,
                useCustomAIModel: node.data.useCustomAIModel ?? false,
                agentProvider: node.data.agentProvider || '',
                agentModel: node.data.agentModel || null,
                agentTemperature: node.data.agentTemperature ?? 0,
                agentMaxTokens: node.data.agentMaxTokens ?? null,
                agentTopP: node.data.agentTopP ?? 1,
                agentFrequencyPenalty: node.data.agentFrequencyPenalty ?? null,
                agentPresencePenalty: node.data.agentPresencePenalty ?? null,
                agentStopSequence: node.data.agentStopSequence || null,
                agentStreamResponse: node.data.agentStreamResponse ?? false,
                agentJsonResponse: node.data.agentJsonResponse ?? false,
                agentGender: node.data.agentGender || 'male',
                agentTypingSpeed: node.data.agentTypingSpeed ?? null,
                agentMaxInputLength: node.data.agentMaxInputLength ?? 200000,
                agentMemoryTurn: node.data.agentMemoryTurn ?? null,
                agentMemoryDuration: node.data.agentMemoryDuration ?? null,
                agentShortTermMemory: node.data.agentShortTermMemory ?? 200,
                agentLongTermMemory: node.data.agentLongTermMemory ?? false,
                agentEnableTime: node.data.agentEnableTime ?? false,
                agentTimeZone: node.data.agentTimeZone || null,
                agentEnableTransactionId: node.data.agentEnableTransactionId ?? false,
                agentEnableLanguageReplace: node.data.agentEnableLanguageReplace ?? false,
                agentEnableInsurance: node.data.agentEnableInsurance ?? false,
                agentTimeout: node.data.agentTimeout ?? 0,
                agentMaxRetry: node.data.agentMaxRetry ?? 0,
                knowledgeConnections: node.data.knowledgeConnections || {
                    documents: { nodeId: null, description: '' },
                    databaseIntegration: { nodeId: null, description: '' },
                    mutualFund: { nodeId: null, description: '' },
                    knowledgeApi: { nodeId: null, description: '' },
                    products: { nodeId: null, description: '' },
                    sharepoint: { nodeId: null, description: '' }
                },
                // Agent hosted tools (การ์ด Tools) — toolsConfig เป็น key เดียวที่อยู่บน node data
                // ส่วน toolsType / toolsChoice / toolsWebSearch แตกออกมาใหม่ทุกครั้งที่คลิกโหนด
                ...this.buildNodeToolsInspectorState(node.data.toolsConfig),
                // Tool properties
                toolName: node.data.toolName || '',
                toolDescription: node.data.toolDescription || '',
                toolType: node.data.toolType || 'master',
                masterToolId: node.data.masterToolId || null,
                endpoint: node.data.endpoint || '',
                method: node.data.method || 'GET',
                headers: node.data.headers || [],
                body: node.data.body || '',
                // Flow Tool properties
                flowToolName: node.data.flowToolName || 'Flow Tool',
                flowToolDescription: node.data.flowToolDescription || '',
                // Query Rewriter properties
                queryRewriterMode: node.data.queryRewriterMode || 'continueQuestion',
                queryRewriterEnableLanguageReplace: node.data.queryRewriterEnableLanguageReplace ?? false,
                queryRewriterProvider: node.data.queryRewriterProvider || null,
                queryRewriterModel: node.data.queryRewriterModel || null,
                queryRewriterApiVersion: node.data.queryRewriterApiVersion || null,
                queryRewriterTemperature: node.data.queryRewriterTemperature ?? 0,
                queryRewriterMaxTokens: node.data.queryRewriterMaxTokens ?? 500,
                queryRewriterTopP: node.data.queryRewriterTopP ?? 1,
                queryRewriterFrequencyPenalty: node.data.queryRewriterFrequencyPenalty ?? null,
                queryRewriterPresencePenalty: node.data.queryRewriterPresencePenalty ?? null,
                queryRewriterSystemPrompt: node.data.queryRewriterSystemPrompt || '',
                queryRewriterMemoryTurn: node.data.queryRewriterMemoryTurn ?? 5,
                queryRewriterMemoryDuration: node.data.queryRewriterMemoryDuration ?? 60,
                // Response Options
                queryRewriterStreamResponse: node.data.queryRewriterStreamResponse ?? false,
                queryRewriterJsonResponse: node.data.queryRewriterJsonResponse ?? false,
                textContent: node.data.textContent || '',
                // Dynamic Knowledge properties
                useMetaCommunityGraphSubCat: node.data.useMetaCommunityGraphSubCat ?? node.data.dynamicKnowledgeUseMetaCommunityGraphSubCat ?? false,
                maxExplicitDocs: node.data.maxExplicitDocs ?? 10,
                // Summary Memory (Embedding) properties
                summaryMemoryEnabled: node.data.summaryMemoryEnabled ?? false,
                summaryMemoryProvider: node.data.summaryMemoryProvider || null,
                summaryMemoryModel: node.data.summaryMemoryModel || null,
                summaryMemoryApiVersion: node.data.summaryMemoryApiVersion || null,
                summaryMemoryJsonRequest: node.data.summaryMemoryJsonRequest || null,
                summaryMemorySystemPrompt: node.data.summaryMemorySystemPrompt || '',
                summaryMemoryTemperature: node.data.summaryMemoryTemperature ?? 0,
                summaryMemoryMaxTokens: node.data.summaryMemoryMaxTokens ?? 500,
                summaryMemoryTopP: node.data.summaryMemoryTopP ?? 1,
                summaryMemoryFrequencyPenalty: node.data.summaryMemoryFrequencyPenalty ?? null,
                summaryMemoryPresencePenalty: node.data.summaryMemoryPresencePenalty ?? null,
                summaryMemoryMemoryTurn: node.data.summaryMemoryMemoryTurn ?? 0,
                summaryMemoryMemoryDuration: node.data.summaryMemoryMemoryDuration ?? 60,
                summaryMemoryTimeout: node.data.summaryMemoryTimeout ?? 0,
            };

            if (node.data.type === 'dynamicKnowledge') {
                delete node.data.dynamicKnowledgeUseCommunityGraphCat;
                delete node.data.dynamicKnowledgeUseCommunityGraphSubCat;
                delete node.data.communityId;
                delete node.data.communityTopGroups;
                delete node.data.communityTopChunks;
                delete node.data.communitySearchMethod;
                delete node.data.communityMinCosineScore;

                delete this.nodeInspector.dynamicKnowledgeUseCommunityGraphCat;
                delete this.nodeInspector.dynamicKnowledgeUseCommunityGraphSubCat;
                delete this.nodeInspector.communityId;
                delete this.nodeInspector.communityTopGroups;
                delete this.nodeInspector.communityTopChunks;
                delete this.nodeInspector.communitySearchMethod;
                delete this.nodeInspector.communityMinCosineScore;

                // Migrate old property name to new one
                if (node.data.dynamicKnowledgeUseMetaCommunityGraphSubCat !== undefined) {
                    node.data.useMetaCommunityGraphSubCat = node.data.dynamicKnowledgeUseMetaCommunityGraphSubCat;
                    delete node.data.dynamicKnowledgeUseMetaCommunityGraphSubCat;
                }

                this.ensureDynamicKnowledgeMetaCommunityDefaults(node);
            }
            
            // Process smartIntent forbiddenWords to ensure empty item at the end (similar to AI model logic)
            if (node.data.type === 'smartIntent') {
                // Handle smartIntents array
                if (this.nodeInspector.smartIntents && this.nodeInspector.smartIntents.length > 0) {
                    this.nodeInspector.smartIntents.forEach(smartIntent => {
                        if (smartIntent.mainAction && smartIntent.mainAction.forbiddenWords) {
                            const lastItem = smartIntent.mainAction.forbiddenWords[smartIntent.mainAction.forbiddenWords.length - 1];
                            if (lastItem !== '') {
                                smartIntent.mainAction.forbiddenWords.push('');
                            }
                        } else if (smartIntent.mainAction) {
                            smartIntent.mainAction.forbiddenWords = [''];
                        }
                    });
                }
                
                // Handle smartIntentFallback forbiddenWords
                if (this.nodeInspector.smartIntentFallback && this.nodeInspector.smartIntentFallback.forbiddenWords) {
                    const lastItem = this.nodeInspector.smartIntentFallback.forbiddenWords[this.nodeInspector.smartIntentFallback.forbiddenWords.length - 1];
                    if (lastItem !== '') {
                        this.nodeInspector.smartIntentFallback.forbiddenWords.push('');
                    }
                } else if (this.nodeInspector.smartIntentFallback) {
                    this.nodeInspector.smartIntentFallback.forbiddenWords = [''];
                }
            }
            
            // Initialize provider dropdown for model nodes
            if (node.data.type === 'model' && this.nodeInspector.provider) {
                this.loadingModelConfig = true;
                this.changeProvider(this.nodeInspector.provider).then(() => {
                    this.loadingModelConfig = false;
                });
            }

            // Initialize provider dropdown + tax year list for taxPlanning (V1) nodes — V1 still
            // stores its LLM/tax config on the node itself, so this must keep working as-is.
            if (node.data.type === 'taxPlanning') {
                this.loadTaxPlanningYears();
                if (this.nodeInspector.provider) {
                    this.loadingModelConfig = true;
                    this.changeProvider(this.nodeInspector.provider).then(() => {
                        this.loadingModelConfig = false;
                    });
                }
            }

            // taxPlanningV2 keeps no LLM/tax config on the node anymore (it lives in the
            // versioned settings rows), so neither the provider/model dropdowns nor the tax-year
            // list are needed here — only the list of settings versions to pick from.
            if (node.data.type === 'taxPlanningV2') {
                this.loadTaxPlanningV2SettingsVersions();
            }

            // Initialize provider dropdown for intentAI nodes
            if (node.data.type === 'intentAI' && this.nodeInspector.intentaiProvider) {
                this.loadingModelConfig = true;
                this.changeProvider(this.nodeInspector.intentaiProvider).then(() => {
                    this.loadingModelConfig = false;
                });
            }

            // Initialize provider dropdown for orchestrator nodes
            if (node.data.type === 'orchestrator' && this.nodeInspector.provider) {
                this.loadingModelConfig = true;
                this.changeProvider(this.nodeInspector.provider).then(() => {
                    this.loadingModelConfig = false;
                });
            }
            // Initialize provider dropdown for reformatTTS nodes
            if (node.data.type === 'reformatTTS' && this.nodeInspector.provider) {
                this.loadingModelConfig = true;
                this.changeProvider(this.nodeInspector.provider).then(() => {
                    this.loadingModelConfig = false;
                });
            }
            // Initialize provider dropdown for ssmlFormat nodes
            if (node.data.type === 'ssmlFormat' && this.nodeInspector.provider) {
                this.loadingModelConfig = true;
                this.changeProvider(this.nodeInspector.provider).then(() => {
                    this.loadingModelConfig = false;
                });
            }
            // Initialize provider dropdown for queryRewriter nodes
            if (node.data.type === 'queryRewriter' && this.nodeInspector.queryRewriterProvider) {
                this.loadingModelConfig = true;
                this.changeProvider(this.nodeInspector.queryRewriterProvider).then(() => {
                    this.loadingModelConfig = false;
                });
            }

            // Initialize provider dropdown for agent nodes (custom AI model)
            if (node.data.type === 'agent' && this.nodeInspector.agentProvider && this.nodeInspector.useCustomAIModel) {
                this.loadingModelConfig = true;
                this.changeProvider(this.nodeInspector.agentProvider).then(() => {
                    this.loadingModelConfig = false;
                });
            }

            // Initialize provider dropdown for Summary Memory (retrieval and parser-context nodes)
            if (['documents', 'dynamicKnowledge', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2'].includes(node.data.type)
                && this.nodeInspector.summaryMemoryProvider) {
                this.loadingModelConfig = true;
                this.changeProvider(this.nodeInspector.summaryMemoryProvider).then(() => {
                    this.loadingModelConfig = false;
                });
            }

            // Show inspector as floating panel instead of dialog
            // this.inspectorDialog = true;

            // Set initial expand state for document data selection if enabled
            if (node.data.type === 'documents' && this.nodeInspector.enableSpecificDataSelection) {
                this.documentDataExpanded = true;
            } else {
                this.documentDataExpanded = false;
            }

            // Check for validation warnings when node is selected
            this.checkNodeValidation(node);
        },
        validateSingleNode(node, edges = null) {
            let hasWarning = false;
            // Use provided edges or fallback to tempEdge (for internal flow validation)
            const edgesToUse = edges || this.tempEdge;

            // Validation for messageInput node
            if (node.data.type === 'messageInput') {
                const selectedModel = node.data.selectedModel;
                if (!selectedModel || node.data.selectedModel === undefined || (Array.isArray(selectedModel) && selectedModel.length === 0)) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'model') {
                // Check if provider and model are selected
                if (!node.data.provider || !node.data.model) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'intent') {
                // เช็คว่า Intent มีฝารฝำหนด fallback intent หรือไม่
                let fallbackEdges = edgesToUse.filter(edge => edge.source === node.id && edge.sourceHandle === 'intent-fallback');

                // เช็คว่า Intent มีฝารต่อไปยัง node intentAI หรือไม่ จาฝ output-bottom
                let intentAIEdges = edgesToUse.filter(edge => edge.source === node.id && edge.sourceHandle === 'output-bottom');

                if (fallbackEdges.length === 0 || intentAIEdges.length === 0) {
                    hasWarning = true; // Missing fallback intent or intentAI connection
                }
            }

            if (node.data.type === 'smartIntent') {
                // เช็คว่า Smart Intent มีฝารฝำหนด fallback intent หรือไม่
                let fallbackEdges = edgesToUse.filter(edge => edge.source === node.id && edge.sourceHandle === 'smart-intent-fallback');

                // เช็คว่า Smart Intent มีฝารต่อไปยัง node intentAI หรือไม่ จาฝ output-bottom
                let intentAIEdges = edgesToUse.filter(edge => edge.source === node.id && edge.sourceHandle === 'output-bottom');

                if (fallbackEdges.length === 0 || intentAIEdges.length === 0) {
                    hasWarning = true; // Missing fallback intent or intentAI connection
                }

                // Validate Smart Intent knowledge sources
                if (node.data.smartIntents && Array.isArray(node.data.smartIntents)) {
                    node.data.smartIntents.forEach(smartIntent => {
                        if (smartIntent.mainAction && smartIntent.mainAction.useAI) {
                            // Validate Documents
                            if (smartIntent.mainAction.enableDocuments) {
                                if (!smartIntent.mainAction.document || (Array.isArray(smartIntent.mainAction.document) && smartIntent.mainAction.document.length === 0)) {
                                    hasWarning = true;
                                }
                            }

                            // Validate Database Integration
                            if (smartIntent.mainAction.enableDatabaseIntegration) {
                                if (!smartIntent.mainAction.tableName) {
                                    hasWarning = true;
                                }
                            }

                            // Validate Products
                            if (smartIntent.mainAction.enableProducts) {
                                if (smartIntent.mainAction.productSourceType === 'group') {
                                    // Group mode - check if collection is selected
                                    if (!smartIntent.mainAction.productGroupCollectionId) {
                                        hasWarning = true;
                                    }
                                } else {
                                    // Product mode - check if product groups are selected
                                    if (!smartIntent.mainAction.productGroupList || smartIntent.mainAction.productGroupList.length === 0) {
                                        hasWarning = true;
                                    }
                                }
                            }

                            // Validate API Knowledge
                            if (smartIntent.mainAction.enableApiKnowledge) {
                                if (!smartIntent.mainAction.knowledgeApiUrl || smartIntent.mainAction.knowledgeApiUrl.trim() === '') {
                                    hasWarning = true;
                                }
                            }
                        }
                    });
                }

                // Validate Smart Intent Fallback knowledge sources
                if (node.data.smartIntentFallback && node.data.smartIntentFallback.useAI) {
                    // Validate Documents in Fallback
                    if (node.data.smartIntentFallback.enableDocuments) {
                        if (!node.data.smartIntentFallback.document || (Array.isArray(node.data.smartIntentFallback.document) && node.data.smartIntentFallback.document.length === 0)) {
                            hasWarning = true;
                        }
                    }

                    // Validate Database Integration in Fallback
                    if (node.data.smartIntentFallback.enableDatabaseIntegration) {
                        if (!node.data.smartIntentFallback.tableName) {
                            hasWarning = true;
                        }
                    }

                    // Validate Products in Fallback
                    if (node.data.smartIntentFallback.enableProducts) {
                        if (node.data.smartIntentFallback.productSourceType === 'group') {
                            // Group mode - check if collection is selected
                            if (!node.data.smartIntentFallback.productGroupCollectionId) {
                                hasWarning = true;
                            }
                        } else {
                            // Product mode - check if product groups are selected
                            if (!node.data.smartIntentFallback.productGroupList || node.data.smartIntentFallback.productGroupList.length === 0) {
                                hasWarning = true;
                            }
                        }
                    }

                    // Validate API Knowledge in Fallback
                    if (node.data.smartIntentFallback.enableApiKnowledge) {
                        if (!node.data.smartIntentFallback.knowledgeApiUrl || node.data.smartIntentFallback.knowledgeApiUrl.trim() === '') {
                            hasWarning = true;
                        }
                    }
                }
            }

            if (node.data.type === 'intentAI') {
                // Check if provider and model are selected (use intentai properties only)
                if (!node.data.intentaiProvider || !node.data.intentaiModel) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'apiTrigger') {
                // Check if API URL is provided
                if (!node.data.apiUrl || node.data.apiUrl.trim() === '' || node.data.apiUrl === undefined) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'contactAgent' || node.data.type === 'contactAgentV2') {
                // Check if all required language messages are provided
                const thaiMessage = node.data.contactAgentMessage?.trim() || '';
                const chineseMessage = node.data.contactAgentMessageChinese?.trim() || '';
                const englishMessage = node.data.contactAgentMessageEnglish?.trim() || '';
                
                if (!node.data.botDisableTime || node.data.botDisableTime == 0 || 
                    thaiMessage === '' || chineseMessage === '' || englishMessage === '') {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'documents') {
                // Check if document source OR community graph is configured
                const hasCommunityGraph = (node.data.communityId && node.data.communityId > 0) || (node.data.metaCommunityId && node.data.metaCommunityId > 0);

                if (node.data.documentSourceType === 'group') {
                    // Group Doc mode - require group OR community graph
                    if (!node.data.documentGroupId && !hasCommunityGraph) {
                        hasWarning = true;
                    }
                } else {
                    // Document mode - require documents OR community graph
                    const hasDocuments = node.data.document && Array.isArray(node.data.document) && node.data.document.length > 0;
                    if (!hasDocuments && !hasCommunityGraph) {
                        hasWarning = true;
                    }
                }
            }

            if (node.data.type === 'databaseIntegration') {
                // Check if table name is provided
                if (!node.data.tableName) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'mutualFund') {
                // Two mutually exclusive ways this node finds a fund, so two different things to check.
                if (node.data.mutualFundResolverEnabled === true) {
                    // Resolver ON: the node asks an LLM which fund the customer meant, so it needs a
                    // provider AND a model. Without either the resolver call cannot be built at all and the
                    // turn answers with no card — exactly the silent nothing a badge is for.
                    const hasProvider = node.data.provider && String(node.data.provider).trim() !== '';
                    const hasModel = node.data.model && String(node.data.model).trim() !== '';
                    if (!hasProvider || !hasModel) {
                        hasWarning = true;
                    }
                } else {
                    // Resolver OFF: the node falls back to the legacy fixed values the admin typed.
                    const hasLookup = node.data.mutualFundLookupValue && node.data.mutualFundLookupValue.trim() !== '';
                    const hasComparison = node.data.mutualFundComparisonValues && node.data.mutualFundComparisonValues.trim() !== '';
                    if (!hasLookup && !hasComparison) {
                        hasWarning = true;
                    }
                }
                // 🔴 No card is rendered on EVERY turn any more. The turn splits in two, and each half has its
                //    own card set: a single fund draws factsheet (type8) + performance (type10), while a
                //    "compare these" turn that found 2+ funds draws ONLY the comparison card (type9) — the
                //    factsheet no longer tags along. So each of these two configs is the lead card of a whole
                //    outcome, and an empty one is a silent nothing for that outcome:
                //      · factsheet empty      → the single-fund turn (the common case) loses its main card
                //      · comparison empty     → a compare turn renders NO card at all, text only
                //    The Cards panel spells out which one is missing; the badge only says "look in here".
                //    (Performance is deliberately NOT checked: an empty one still leaves the factsheet.)
                const hasFactsheetConfig = node.data.mutualFundFactsheetConfigCode && node.data.mutualFundFactsheetConfigCode.trim() !== '';
                const hasComparisonConfig = node.data.mutualFundComparisonConfigCode && node.data.mutualFundComparisonConfigCode.trim() !== '';
                if (!hasFactsheetConfig || !hasComparisonConfig) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'apiknowledge') {
                // Check if Knowledge API URL is provided
                if (!node.data.knowledgeApiUrl || node.data.knowledgeApiUrl.trim() === '' || node.data.knowledgeApiUrl === undefined) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'apiRequest') {
                // Check if API Request URL is provided
                if (!node.data.apiRequestUrl || node.data.apiRequestUrl.trim() === '' || node.data.apiRequestUrl === undefined) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'products') {
                // Check if product source is configured
                if (node.data.productSourceType === 'group') {
                    // Group mode - check if collection is selected
                    if (!node.data.productGroupCollectionId) {
                        hasWarning = true;
                    }
                } else {
                    // Product mode - check if product groups are selected
                    if (!node.data.productGroupList || node.data.productGroupList.length === 0) {
                        hasWarning = true;
                    }
                }
            }

            // JSON Condition validation
            if (node.data.type === 'jsonCondition') {
                const conditions = node.data.conditions;
                if (!conditions || !Array.isArray(conditions) || conditions.length === 0) {
                    hasWarning = true;
                } else {
                    // Check each condition has at least jsonPath and operator
                    const hasIncomplete = conditions.some(c => !c.jsonPath || !c.jsonPath.trim() || !c.operator || !c.operator.trim());
                    if (hasIncomplete) {
                        hasWarning = true;
                    }
                }
            }

            // Service Matcher validation (unified Favorite Lookup mode only)
            if (node.data.type === 'serviceMatcher' && node.data.enableFavoriteLookup === true) {
                // With Favorite Lookup ON the node needs a favorites source: either its own
                // API call (apiRequestUrl) or an upstream context variable.
                const hasApiUrl = !!(node.data.apiRequestUrl && node.data.apiRequestUrl.trim() !== '');
                const hasSourceVar = !!(node.data.favoritesSourceVariable && node.data.favoritesSourceVariable.trim() !== '');
                if (!hasApiUrl && !hasSourceVar) {
                    hasWarning = true;
                }
            }

            // Fallback Return validation
            if (node.data.type === 'serviceFallback') {
                if (!node.data.serviceType || node.data.serviceType.trim() === '') {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'sharepoint') {
                // Check required fields for SharePoint node
                if (!node.data.sharepoint || 
                    !node.data.sharepoint.siteUrl || node.data.sharepoint.siteUrl.trim() === '' ||
                    !node.data.sharepoint.tenantId || node.data.sharepoint.tenantId.trim() === '' ||
                    !node.data.sharepoint.clientId || node.data.sharepoint.clientId.trim() === '' ||
                    !node.data.sharepoint.clientSecret || node.data.sharepoint.clientSecret.trim() === '' ||
                    !node.data.sharepoint.filename || node.data.sharepoint.filename.trim() === '') {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'email') {
                // Check required fields for Email node
                if (!node.data.toEmail || node.data.toEmail.trim() === '') {
                    hasWarning = true;
                } else {
                    // Validate email format
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(node.data.toEmail.trim())) {
                        hasWarning = true;
                    }
                }
                
                if (!node.data.subject || node.data.subject.trim() === '') {
                    hasWarning = true;
                }
                
                if (node.data.emailTemplate === 'custom' && (!node.data.emailBody || node.data.emailBody.trim() === '')) {
                    hasWarning = true;
                }

                if(!node.data.emailBody || node.data.emailBody.trim() === '' || node.data.emailBody.length > 10000) {
                    hasWarning = true;
                }
            }
            if (node.data.type === 'interestOptimizer') {
                if (!node.data.ioAnswerModel) {
                    hasWarning = true;
                }
                if (!node.data.ioParserModel) {
                    hasWarning = true;
                }
                if (!node.data.ioAccountCodes || node.data.ioAccountCodes.length === 0) {
                    hasWarning = true;
                }
            }
            if (node.data.type === 'loanInterest') {
                if (!node.data.liAnswerModel) {
                    hasWarning = true;
                }
                if (!node.data.liParserModel) {
                    hasWarning = true;
                }
                if (!node.data.liInterestRates || node.data.liInterestRates.length === 0) {
                    hasWarning = true;
                }
            }
            if (node.data.type === 'interestOptimizerV2') {
                if (!node.data.ioAnswerModel) {
                    hasWarning = true;
                }
                if (!node.data.ioParserModel) {
                    hasWarning = true;
                }
                if (!node.data.ioV2MetaDocs || node.data.ioV2MetaDocs.length === 0) {
                    hasWarning = true;
                }
            }
            if (node.data.type === 'loanInterestV2') {
                if (!node.data.liAnswerModel) {
                    hasWarning = true;
                }
                if (!node.data.liParserModel) {
                    hasWarning = true;
                }
                if (!node.data.liV2MetaDocs || node.data.liV2MetaDocs.length === 0) {
                    hasWarning = true;
                }
            }

            if (node.data.type === 'agent') {
                // When custom AI model is enabled, provider and model are required
                if (node.data.useCustomAIModel) {
                    if (!node.data.agentProvider || !node.data.agentModel) {
                        hasWarning = true;
                    }
                }
            }

            // Tax Planning Agent — the description is not cosmetic: it is the ONLY thing the Orchestrator's
            // selection prompt has to go on, so an empty one means this agent is effectively unreachable.
            if (node.data.type === 'taxPlanningAgent') {
                if (!node.data.agentName || !node.data.agentDescription) {
                    hasWarning = true;
                }
            }

            // Meta Documents — no selections means the node contributes nothing at runtime.
            if (node.data.type === 'metaDocuments') {
                const metaDocSelections = Array.isArray(node.data.metaDocSelections)
                    ? node.data.metaDocSelections.filter(s => s && s.header) : [];
                if (metaDocSelections.length === 0) {
                    hasWarning = true;
                }
            }

            // FlowTool validation - check if internal flow is complete
            if (node.data.type === 'flowTool') {
                // Check if internal flow exists and has nodes
                if (!node.data.internalNodes || node.data.internalNodes.length === 0) {
                    hasWarning = true;
                } else {
                    // Validate the internal flow structure (path from input to output)
                    const isValid = this.validateInternalFlow(
                        node.data.internalNodes,
                        node.data.internalEdges || []
                    );
                    if (!isValid) {
                        hasWarning = true;
                    }
                    
                    // Also check if any internal node has warnings
                    const internalEdges = node.data.internalEdges || [];
                    const internalNodesWithWarnings = node.data.internalNodes.filter(internalNode => {
                        // Skip flowInput and flowOutput nodes from validation
                        if (internalNode.data?.type === 'flowInput' || internalNode.data?.type === 'flowOutput') {
                            return false;
                        }
                        // Validate each internal node with internal edges
                        return this.validateSingleNode(internalNode, internalEdges);
                    });
                    
                    if (internalNodesWithWarnings.length > 0) {
                        hasWarning = true;
                    }
                }
            }

            return hasWarning;
        },
        checkNodeValidation(node) {
            const hasWarning = this.validateSingleNode(node);

            this.$nextTick(() => {
                // Update node data directly if warning state changes
                if (node.data.showWarning !== hasWarning) {
                    const nodeIndex = this.tempNode.findIndex(n => n.id === node.id);
                    if (nodeIndex !== -1) {
                        this.tempNode[nodeIndex].data.showWarning = hasWarning;
                    }
                }
            });
        },
        checkAffectedNodesValidation(nodeIds) {
            // Check validation only for specified nodes
            nodeIds.forEach(nodeId => {
                const node = this.tempNode.find(n => n.id === nodeId);
                if (!node) return;

                const hasWarning = this.validateSingleNode(node);

                // Update node data directly if warning state changes
                if (node.data.showWarning !== hasWarning) {
                    const nodeIndex = this.tempNode.findIndex(n => n.id === node.id);
                    if (nodeIndex !== -1) {
                        this.tempNode[nodeIndex].data.showWarning = hasWarning;
                    }
                }
            });
        },
        checkAllNodesValidation() {
            // Check validation for all nodes in the flow
            this.tempNode.forEach(node => {
                const hasWarning = this.validateSingleNode(node);

                // Update node data directly if warning state changes
                if (node.data.showWarning !== hasWarning) {
                    const nodeIndex = this.tempNode.findIndex(n => n.id === node.id);
                    if (nodeIndex !== -1) {
                        this.tempNode[nodeIndex].data.showWarning = hasWarning;
                    }
                }
            });
        },
        closeInspector() {
            this.selectedNode = null;
            this.inspectorExpanded = false;
        },
        clearSelection() {
            // Clear all node selections
            this.tempNode.forEach(node => {
                node.selected = false;
            });
            this.selectedNodes = [];
            this.selectedNode = null;
        },
        // Real-time update methods
        // Agent — older nodes (saved before agentFinalSummaryTemplate existed) open with the field blank and only
        // a placeholder to look at, and a placeholder cannot be copied. This drops the built-in text into the field
        // so the operator can edit from it instead of retyping it.
        loadAgentFinalSummaryDefault() {
            const text = AGENT_PROMPT_DEFAULTS.finalSummaryTemplate;
            this.nodeInspector.agentFinalSummaryTemplate = text;
            this.updateNodeProperty('agentFinalSummaryTemplate', text);
        },

        // #924 — the − / + buttons beside the Min similarity slider: one step = 0.01, kept inside the 0.60–1.00 the
        // backend clamps to. Rounded to two decimals so repeated clicks never drift into 0.7899999.
        stepMutualFundFuzzyMinSimilarity(direction) {
            const current = Number(this.nodeInspector.mutualFundFuzzyMinSimilarity);
            const start = Number.isFinite(current) ? current : 0.78;
            this.setMutualFundFuzzyMinSimilarity(start + direction * 0.01);
        },
        // One writer for the value: clamp to 0.60–1.00, round to 0.01, drop whatever was half-typed in the box.
        setMutualFundFuzzyMinSimilarity(value) {
            const next = Math.min(1, Math.max(0.6, Math.round(value * 100) / 100));
            this.mutualFundFuzzyMinSimilarityDraft = null;
            this.nodeInspector.mutualFundFuzzyMinSimilarity = next;
            this.updateNodeProperty('mutualFundFuzzyMinSimilarity', next);
        },
        // Typed characters: digits and a single dot only — a minus sign, "e" or "+" never reaches the box.
        onMutualFundFuzzyMinSimilarityKeypress(event) {
            const ch = event.key;
            const input = event.target;
            const value = String(input?.value ?? '');
            // What stays in the box once the key replaces the selection — select-all then "." must still work.
            const text = input && typeof input.selectionStart === 'number'
                ? value.slice(0, input.selectionStart) + value.slice(input.selectionEnd)
                : value;
            if (/^[0-9]$/.test(ch)) return;
            if (ch === '.' && !text.includes('.')) return;
            event.preventDefault();
        },
        // While typing the value stays a draft (so "0." or "0.7" can be typed on the way to 0.75). A paste is
        // cleaned the same way the keyboard is, and anything above 1 is capped at 1.00 at once.
        onMutualFundFuzzyMinSimilarityInput(raw) {
            let text = String(raw ?? '').replace(/[^0-9.]/g, '');
            const dot = text.indexOf('.');
            if (dot >= 0) text = text.slice(0, dot + 1) + text.slice(dot + 1).replace(/\./g, '').slice(0, 2);
            if (Number(text) > 1) text = '1.00';
            this.mutualFundFuzzyMinSimilarityDraft = text;
        },
        // Enter / leaving the box: a number is clamped into range (0.3 → 0.60); an empty or unreadable box goes
        // back to the value the node already had.
        commitMutualFundFuzzyMinSimilarity() {
            if (this.mutualFundFuzzyMinSimilarityDraft === null) return;
            const typed = parseFloat(this.mutualFundFuzzyMinSimilarityDraft);
            if (Number.isFinite(typed)) this.setMutualFundFuzzyMinSimilarity(typed);
            else this.mutualFundFuzzyMinSimilarityDraft = null;
        },
        // #924 — "ห่างจากอันดับ 1": the same − / + / typed box as Min similarity, inside the 0.00–0.40 the backend
        // clamps to. The keypress filter is shared (digits and one dot).
        stepMutualFundFuzzyTopGap(direction) {
            const current = Number(this.nodeInspector.mutualFundFuzzyTopGap);
            const start = Number.isFinite(current) ? current : 0.1;
            this.setMutualFundFuzzyTopGap(start + direction * 0.01);
        },
        setMutualFundFuzzyTopGap(value) {
            const next = Math.min(0.4, Math.max(0, Math.round(value * 100) / 100));
            this.mutualFundFuzzyTopGapDraft = null;
            this.nodeInspector.mutualFundFuzzyTopGap = next;
            this.updateNodeProperty('mutualFundFuzzyTopGap', next);
        },
        onMutualFundFuzzyTopGapInput(raw) {
            let text = String(raw ?? '').replace(/[^0-9.]/g, '');
            const dot = text.indexOf('.');
            if (dot >= 0) text = text.slice(0, dot + 1) + text.slice(dot + 1).replace(/\./g, '').slice(0, 2);
            if (Number(text) > 0.4) text = '0.40';
            this.mutualFundFuzzyTopGapDraft = text;
        },
        commitMutualFundFuzzyTopGap() {
            if (this.mutualFundFuzzyTopGapDraft === null) return;
            const typed = parseFloat(this.mutualFundFuzzyTopGapDraft);
            if (Number.isFinite(typed)) this.setMutualFundFuzzyTopGap(typed);
            else this.mutualFundFuzzyTopGapDraft = null;
        },
        updateNodeProperty(property, value) {
            if (!this.selectedNode) return;

            // Find the node in our tempNode array
            const nodeIndex = this.tempNode.findIndex(node => node.id === this.selectedNode.id);
            if (nodeIndex === -1) return;

            // Handle provider change
            // if (property === 'provider' || property === 'intentaiProvider') {
            //     // Skip if already loading or if the value hasn't changed
            //     if (this.loadingModelConfig || this.nodeInspector[property] === value) {
            //         console.log('Skipping provider change (already loading or unchanged):', value);
            //         return;
            //     }
            //     console.log('Changing provider to (updateNodeProperty):', value);
            //     this.loadingModelConfig = true;  // Set flag before calling
            //     this.changeProvider(value).finally(() => {
            //         this.loadingModelConfig = false;  // Reset flag after completion
            //     });
            // }

            // // Handle model change
            // if (property === 'model' || property === 'intentaiModel') {
            //     this.changeModel(value);
            // }

            // Update the node data immediately
            let updatedData;
            
            // Handle nested properties (e.g., sharepoint.siteUrl)
            if (property.includes('.')) {
                const [parentKey, childKey] = property.split('.');
                updatedData = {
                    ...this.tempNode[nodeIndex].data,
                    [parentKey]: {
                        ...this.tempNode[nodeIndex].data[parentKey],
                        [childKey]: value
                    }
                };
            } else {
                updatedData = {
                    ...this.tempNode[nodeIndex].data,
                    [property]: value
                };
            }

            // Replace the entire node to ensure reactivity
            this.tempNode = [
                ...this.tempNode.slice(0, nodeIndex),
                {
                    ...this.tempNode[nodeIndex],
                    data: updatedData
                },
                ...this.tempNode.slice(nodeIndex + 1)
            ];

            // Update selectedNode reference
            this.selectedNode = this.tempNode[nodeIndex];

            // Update nodeInspector to keep it in sync
            if (property.includes('.')) {
                const [parentKey, childKey] = property.split('.');
                if (!this.nodeInspector[parentKey]) {
                    this.nodeInspector[parentKey] = {};
                }
                this.nodeInspector[parentKey][childKey] = value;
            } else {
                this.nodeInspector[property] = value;
            }

            // Auto-assign apiVersion when model changes
            if (property === 'model') {
                this.$nextTick(() => {
                    const selectedModel = this.listDDModel.find(m => m.modelName === value);
                    if (selectedModel && this.nodeInspector.apiVersion !== selectedModel.apiVersion) {
                        this.nodeInspector.apiVersion = selectedModel.apiVersion;
                        if (this.selectedNode) {
                            this.selectedNode.data.apiVersion = selectedModel.apiVersion;
                            const nodeIndex = this.tempNode.findIndex(n => n.id === this.selectedNode.id);
                            if (nodeIndex !== -1) {
                                this.tempNode[nodeIndex].data.apiVersion = selectedModel.apiVersion;
                            }
                        }
                    }
                });
            }

            // Auto-assign intentAiModelApiVersion when intentaiModel changes
            if (property === 'intentaiModel') {
                this.$nextTick(() => {
                    const selectedModel = this.listDDModel.find(m => m.modelName === value);
                    if (selectedModel && this.nodeInspector.intentAiModelApiVersion !== selectedModel.apiVersion) {
                        this.nodeInspector.intentAiModelApiVersion = selectedModel.apiVersion;
                        if (this.selectedNode) {
                            this.selectedNode.data.intentAiModelApiVersion = selectedModel.apiVersion;
                            const nodeIndex = this.tempNode.findIndex(n => n.id === this.selectedNode.id);
                            if (nodeIndex !== -1) {
                                this.tempNode[nodeIndex].data.intentAiModelApiVersion = selectedModel.apiVersion;
                            }
                        }
                    }
                });
            }

            // Auto-assign queryRewriterApiVersion when queryRewriterModel changes
            if (property === 'queryRewriterModel') {
                this.$nextTick(() => {
                    const selectedModel = this.listDDModel.find(m => m.modelName === value);
                    if (selectedModel && this.nodeInspector.queryRewriterApiVersion !== selectedModel.apiVersion) {
                        this.nodeInspector.queryRewriterApiVersion = selectedModel.apiVersion;
                        if (this.selectedNode) {
                            this.selectedNode.data.queryRewriterApiVersion = selectedModel.apiVersion;
                            const nodeIndex = this.tempNode.findIndex(n => n.id === this.selectedNode.id);
                            if (nodeIndex !== -1) {
                                this.tempNode[nodeIndex].data.queryRewriterApiVersion = selectedModel.apiVersion;
                            }
                        }
                    }
                });
            }

            // Auto-assign summaryMemoryApiVersion when summaryMemoryModel changes (Summary Memory feature)
            if (property === 'summaryMemoryModel') {
                this.$nextTick(() => {
                    const selectedModel = this.listDDModel.find(m => m.modelName === value);
                    if (selectedModel && this.nodeInspector.summaryMemoryApiVersion !== selectedModel.apiVersion) {
                        this.nodeInspector.summaryMemoryApiVersion = selectedModel.apiVersion;
                        if (this.selectedNode) {
                            this.selectedNode.data.summaryMemoryApiVersion = selectedModel.apiVersion;
                            const nodeIndex = this.tempNode.findIndex(n => n.id === this.selectedNode.id);
                            if (nodeIndex !== -1) {
                                this.tempNode[nodeIndex].data.summaryMemoryApiVersion = selectedModel.apiVersion;
                            }
                        }
                    }
                });
            }

            this.checkNodeValidation(this.tempNode[nodeIndex]);

            // Debounce history saving for property updates
            this.debouncedSavePropertyChange(property);
        },

        // Handle document source type toggle change
        onDocumentSourceTypeChange(value) {
            this.updateNodeProperty('documentSourceType', value);

            // Clear the other type's selection when switching
            if (value === 'group') {
                // Clear document selection when switching to group
                this.updateNodeProperty('document', []);
                this.updateNodeProperty('selectedDocumentData', []);
                this.updateNodeProperty('enableSpecificDataSelection', false);
            } else {
                // Clear group selection when switching to doc
                this.updateNodeProperty('documentGroupId', null);
                this.updateNodeProperty('documentGroupName', '');
            }
        },

        // Handle document group selection change
        onDocumentGroupChange(groupId) {
            this.updateNodeProperty('documentGroupId', groupId);

            // Find the group name for display
            const group = this.DDDocumentGroup.find(g => g.id === groupId);
            this.updateNodeProperty('documentGroupName', group ? group.name : '');
        },

        // Handle community graph selection change (Documents node)
        onCommunityGraphChange(communityId) {
            this.applyCommunityGraphDefaults(communityId);
        },

        // Shared logic for setting community graph defaults
        applyCommunityGraphDefaults(communityId) {
            this.updateNodeProperty('communityId', communityId);

            // Set default values when community is selected
            if (communityId) {
                if (!this.nodeInspector.communityTopGroups) {
                    this.nodeInspector.communityTopGroups = 3;
                    this.updateNodeProperty('communityTopGroups', 3);
                }
                if (!this.nodeInspector.communityTopChunks) {
                    this.nodeInspector.communityTopChunks = 5;
                    this.updateNodeProperty('communityTopChunks', 5);
                }
                if (!this.nodeInspector.communitySearchMethod) {
                    this.nodeInspector.communitySearchMethod = 'topn';
                    this.updateNodeProperty('communitySearchMethod', 'topn');
                }
                if (!this.nodeInspector.communityMinCosineScore) {
                    this.nodeInspector.communityMinCosineScore = 0.7;
                    this.updateNodeProperty('communityMinCosineScore', 0.7);
                }
            }
        },

        // Validate community top groups
        validateCommunityTopGroups(event) {
            let value = parseInt(event.target.value) || 3;
            if (value < 1) value = 1;
            if (value > 100) value = 100;
            this.nodeInspector.communityTopGroups = value;
            this.updateNodeProperty('communityTopGroups', value);
        },

        // Validate community top chunks
        validateCommunityTopChunks(event) {
            let value = parseInt(event.target.value) || 5;
            if (value < 1) value = 1;
            if (value > 100) value = 100;
            this.nodeInspector.communityTopChunks = value;
            this.updateNodeProperty('communityTopChunks', value);
        },

        // Validate community min cosine score
        validateCommunityMinCosineScore(event) {
            let value = parseFloat(event.target.value) || 0.7;
            if (value < 0) value = 0;
            if (value > 1) value = 1;
            // Round to 2 decimal places
            value = Math.round(value * 100) / 100;
            this.nodeInspector.communityMinCosineScore = value;
            this.updateNodeProperty('communityMinCosineScore', value);
        },

        // Handle meta community graph selection change (Documents node)
        onMetaCommunityGraphChange(metaCommunityId) {
            this.applyMetaCommunityGraphDefaults(metaCommunityId);
        },

        // Shared logic for setting meta community graph defaults
        applyMetaCommunityGraphDefaults(metaCommunityId) {
            this.updateNodeProperty('metaCommunityId', metaCommunityId);

            // Set default values when meta community is selected
            if (metaCommunityId) {
                if (!this.nodeInspector.metaCommunityTopGroups) {
                    this.nodeInspector.metaCommunityTopGroups = 3;
                    this.updateNodeProperty('metaCommunityTopGroups', 3);
                }
                if (!this.nodeInspector.metaCommunityTopChunks) {
                    this.nodeInspector.metaCommunityTopChunks = 5;
                    this.updateNodeProperty('metaCommunityTopChunks', 5);
                }
                if (!this.nodeInspector.metaCommunitySearchMethod) {
                    this.nodeInspector.metaCommunitySearchMethod = 'topn';
                    this.updateNodeProperty('metaCommunitySearchMethod', 'topn');
                }
                if (!this.nodeInspector.metaCommunityMinCosineScore) {
                    this.nodeInspector.metaCommunityMinCosineScore = 0.7;
                    this.updateNodeProperty('metaCommunityMinCosineScore', 0.7);
                }
            }
        },

        ensureDynamicKnowledgeMetaCommunityDefaults(node = this.selectedNode) {
            if (!node || node.data?.type !== 'dynamicKnowledge') {
                return;
            }

            const defaultMetaCommunityId = node.data.metaCommunityId || this.DDMetaCommunityGraph?.[0]?.id || null;
            if (!defaultMetaCommunityId) {
                return;
            }

            const isSelectedNode = this.selectedNode?.id === node.id;

            if (isSelectedNode) {
                if (!node.data.metaCommunityId) {
                    node.data.metaCommunityId = defaultMetaCommunityId;
                }

                if (this.nodeInspector) {
                    this.nodeInspector.metaCommunityId = defaultMetaCommunityId;
                }

                this.applyMetaCommunityGraphDefaults(defaultMetaCommunityId);
                return;
            }

            const nodeIndex = this.tempNode.findIndex((tempNode) => tempNode.id === node.id);
            if (nodeIndex === -1) {
                return;
            }

            const currentNode = this.tempNode[nodeIndex];
            const updatedData = {
                ...currentNode.data,
                metaCommunityId: defaultMetaCommunityId,
                metaCommunityTopGroups: currentNode.data.metaCommunityTopGroups || 3,
                metaCommunityTopChunks: currentNode.data.metaCommunityTopChunks || 5,
                metaCommunitySearchMethod: currentNode.data.metaCommunitySearchMethod || 'topn',
                metaCommunityMinCosineScore: currentNode.data.metaCommunityMinCosineScore || 0.7,
            };

            this.tempNode = [
                ...this.tempNode.slice(0, nodeIndex),
                {
                    ...currentNode,
                    data: updatedData,
                },
                ...this.tempNode.slice(nodeIndex + 1),
            ];
        },

        ensureAllDynamicKnowledgeMetaCommunityDefaults() {
            if (!Array.isArray(this.tempNode) || !this.DDMetaCommunityGraph?.length) {
                return;
            }

            this.tempNode.forEach((node) => {
                if (node?.data?.type === 'dynamicKnowledge' && !node.data.metaCommunityId) {
                    this.ensureDynamicKnowledgeMetaCommunityDefaults(node);
                }
            });
        },

        // Validate meta community top groups
        validateMetaCommunityTopGroups(event) {
            let value = parseInt(event.target.value) || 3;
            if (value < 1) value = 1;
            if (value > 100) value = 100;
            this.nodeInspector.metaCommunityTopGroups = value;
            this.updateNodeProperty('metaCommunityTopGroups', value);
        },

        // Validate meta community top chunks
        validateMetaCommunityTopChunks(event) {
            let value = parseInt(event.target.value) || 5;
            if (value < 1) value = 1;
            if (value > 100) value = 100;
            this.nodeInspector.metaCommunityTopChunks = value;
            this.updateNodeProperty('metaCommunityTopChunks', value);
        },

        // Validate meta community min cosine score
        validateMetaCommunityMinCosineScore(event) {
            let value = parseFloat(event.target.value) || 0.7;
            if (value < 0) value = 0;
            if (value > 1) value = 1;
            value = Math.round(value * 100) / 100;
            this.nodeInspector.metaCommunityMinCosineScore = value;
            this.updateNodeProperty('metaCommunityMinCosineScore', value);
        },

        // Get available sub-titles based on selected title
        getAvailableSubTitles() {
            const selectedTitle = this.nodeInspector?.filterTopic;
            if (!selectedTitle) return [];
            const subTitles = this.titleSubTitleMap[selectedTitle] || [];
            return subTitles.map(st => ({ id: st, name: st, value: st }));
        },

        // Handle filter title change - clear sub-title when title changes
        onFilterTopicChange(value) {
            this.updateNodeProperty('filterTopic', value);
            // Clear sub-title when title changes (sub-titles depend on title)
            this.nodeInspector.filterSubTopic = null;
            this.updateNodeProperty('filterSubTopic', null);
        },

        // Handle product source type toggle change
        onProductSourceTypeChange(value) {
            this.updateNodeProperty('productSourceType', value);

            // Clear the other type's selection when switching
            if (value === 'group') {
                // Clear product group list when switching to group collection
                this.updateNodeProperty('productGroupList', []);
            } else {
                // Clear group collection selection when switching to product
                this.updateNodeProperty('productGroupCollectionId', null);
                this.updateNodeProperty('productGroupCollectionName', '');
            }
        },

        // Handle product group collection selection change
        onProductGroupCollectionChange(collectionId) {
            this.updateNodeProperty('productGroupCollectionId', collectionId);

            // Find the collection name for display
            const collection = this.DDProductGroupCollection.find(c => c.id === collectionId);
            this.updateNodeProperty('productGroupCollectionName', collection ? collection.name : '');
        },

        // Debounced method to save property changes to history
        debouncedSavePropertyChange(property) {
            // Clear existing timer
            if (this.propertyUpdateTimer) {
                clearTimeout(this.propertyUpdateTimer);
            }
            
            // Set new timer to save after 1 second of inactivity
            this.propertyUpdateTimer = setTimeout(() => {
                if (!this.isPerformingUndoRedo) {
                    this.saveStateToHistory(`Update ${property}`);
                }
            }, 1000);
        },
        updateValidatedNodeProperty(property, value, minValue = null, maxValue = null) {
            // Validate the value
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue)) {
                return; // Don't update if not a valid number
            }

            // Apply min/max constraints
            if (minValue !== null && validatedValue < minValue) {
                validatedValue = minValue;
            }
            if (maxValue !== null && validatedValue > maxValue) {
                validatedValue = maxValue;
            }

            // Round cosine similarity values to 4 decimal places
            if (property === 'cosineSimilarity' || property === 'productCosineSimilarity') {
                validatedValue = Math.round(validatedValue * 10000) / 10000;
            }

            // Update the node property with validated value
            this.updateNodeProperty(property, validatedValue);

            // Update the nodeInspector to reflect the validated value
            this.nodeInspector[property] = validatedValue;
        },

        // Validation methods for blur events
        validateMaxExplicitDocs(event) {
            let value = parseInt(event.target.value);
            if (isNaN(value) || value < 1) value = 10;
            this.nodeInspector.maxExplicitDocs = value;
            this.updateNodeProperty('maxExplicitDocs', value);
        },

        validateAndFixTopNChunks(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the property
            this.updateNodeProperty('topNChunks', validatedValue);
            this.nodeInspector.topNChunks = validatedValue;
        },

        validateAndFixCosineSimilarity(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the property
            this.updateNodeProperty('cosineSimilarity', validatedValue);
            this.nodeInspector.cosineSimilarity = validatedValue;
        },

        validateAndFixProductTopNChunks(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the property
            this.updateNodeProperty('productTopNChunks', validatedValue);
            this.nodeInspector.productTopNChunks = validatedValue;
        },

        validateAndFixProductCosineSimilarity(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the property
            this.updateNodeProperty('productCosineSimilarity', validatedValue);
            this.nodeInspector.productCosineSimilarity = validatedValue;
        },

        // Validation methods for Smart Intent fields
        validateAndFixSmartIntentTopNChunks(event, smartIntentIndex) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the nested property
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.topNChunks = validatedValue;
            this.updateSmartIntentProperty(smartIntentIndex, 'topNChunks', validatedValue);
        },

        validateAndFixSmartIntentCosineSimilarity(event, smartIntentIndex) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the nested property
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.cosineSimilarity = validatedValue;
            this.updateSmartIntentProperty(smartIntentIndex, 'cosineSimilarity', validatedValue);
        },

        validateAndFixSmartIntentProductTopNChunks(event, smartIntentIndex) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the nested property
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.productTopNChunks = validatedValue;
            this.updateSmartIntentProperty(smartIntentIndex, 'productTopNChunks', validatedValue);
        },

        validateAndFixSmartIntentProductCosineSimilarity(event, smartIntentIndex) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the nested property
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.productCosineSimilarity = validatedValue;
            this.updateSmartIntentProperty(smartIntentIndex, 'productCosineSimilarity', validatedValue);
        },

        // Validation methods for Smart Intent Fallback fields
        validateAndFixSmartIntentFallbackProductTopNChunks(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the nested property
            this.nodeInspector.smartIntentFallback.productTopNChunks = validatedValue;
            this.updateSmartIntentFallbackProperty('productTopNChunks', validatedValue);
        },

        validateAndFixSmartIntentFallbackProductCosineSimilarity(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the nested property
            this.nodeInspector.smartIntentFallback.productCosineSimilarity = validatedValue;
            this.updateSmartIntentFallbackProperty('productCosineSimilarity', validatedValue);
        },

        // Additional validation methods for Smart Intent Fallback Documents fields
        validateAndFixSmartIntentFallbackTopNChunks(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseInt(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue) || validatedValue < 1) {
                validatedValue = 1; // Default minimum value
            }

            // Update the nested property
            this.nodeInspector.smartIntentFallback.topNChunks = validatedValue;
            this.updateSmartIntentFallbackProperty('topNChunks', validatedValue);
        },

        validateAndFixSmartIntentFallbackCosineSimilarity(event) {
            const value = event.target.value;
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number and apply constraints
            if (isNaN(validatedValue) || validatedValue < 0.0001) {
                validatedValue = 0.0001; // Default minimum value
            }
            if (validatedValue > 1) {
                validatedValue = 1; // Default maximum value
            }

            // Round to 4 decimal places
            validatedValue = Math.round(validatedValue * 10000) / 10000;

            // Update the nested property
            this.nodeInspector.smartIntentFallback.cosineSimilarity = validatedValue;
            this.updateSmartIntentFallbackProperty('cosineSimilarity', validatedValue);
        },

        // Validation method specifically for Smart Intent properties
        updateValidatedSmartIntentProperty(smartIntentIndex, property, value, minValue = null, maxValue = null) {
            // Validate the value
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue)) {
                return; // Don't update if not a valid number
            }

            // Apply min/max constraints
            if (minValue !== null && validatedValue < minValue) {
                validatedValue = minValue;
            }
            if (maxValue !== null && validatedValue > maxValue) {
                validatedValue = maxValue;
            }

            // Round cosine similarity values to 4 decimal places
            if (property === 'cosineSimilarity' || property === 'productCosineSimilarity') {
                validatedValue = Math.round(validatedValue * 10000) / 10000;
            }

            // Update the smart intent property with validated value
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction[property] = validatedValue;
            
            // Call updateSmartIntentData to save the changes and trigger validation
            this.updateSmartIntentData();
        },

        // Validation method specifically for Smart Intent Fallback properties
        updateValidatedSmartIntentFallbackProperty(property, value, minValue = null, maxValue = null) {
            // Validate the value
            let validatedValue = value;

            // Convert to number if it's a string
            if (typeof value === 'string' && value !== '') {
                validatedValue = parseFloat(value);
            }

            // Check if it's a valid number
            if (isNaN(validatedValue)) {
                return; // Don't update if not a valid number
            }

            // Apply min/max constraints
            if (minValue !== null && validatedValue < minValue) {
                validatedValue = minValue;
            }
            if (maxValue !== null && validatedValue > maxValue) {
                validatedValue = maxValue;
            }

            // Round cosine similarity values to 4 decimal places
            if (property === 'cosineSimilarity' || property === 'productCosineSimilarity') {
                validatedValue = Math.round(validatedValue * 10000) / 10000;
            }

            // Update the smart intent fallback property with validated value
            this.nodeInspector.smartIntentFallback[property] = validatedValue;
            
            // Call updateSmartIntentFallbackData to save the changes and trigger validation
            this.updateSmartIntentFallbackData();
        },

        // Special validation for Bot Disable Time
        validateAndUpdateBotDisableTime(value) {
            // Allow empty value (bot remains active)
            if (!value || value === '') {
                this.updateNodeProperty('botDisableTime', '');
                this.nodeInspector.botDisableTime = '';
                return;
            }

            // Convert to number
            let numValue = parseFloat(value);

            // Check if it's a valid number
            if (isNaN(numValue)) {
                return; // Don't update if not a valid number
            }

            // Ensure minimum value is 1
            if (numValue < 1) {
                numValue = 1;
                // Show warning to user
                this.showWarning('Bot Disable Time must be at least 1 minute');
            }

            // Update with validated value
            this.updateNodeProperty('botDisableTime', numValue);
            this.nodeInspector.botDisableTime = numValue;
        },
        updateIntentData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'intent') return;

            this.updateNodeProperty('intents', this.nodeInspector.intents);
        },
        // API Trigger methods
        updateHeaderProperty(index, property, value) {
            if (!this.nodeInspector.listHeadersAPITrigger[index]) return;
            this.nodeInspector.listHeadersAPITrigger[index][property] = value;
            this.updateNodeProperty('listHeadersAPITrigger', this.nodeInspector.listHeadersAPITrigger);
        },
        updateValidationProperty(index, property, value) {
            if (!this.nodeInspector.listValidation[index]) return;
            this.nodeInspector.listValidation[index][property] = value;
            this.updateNodeProperty('listValidation', this.nodeInspector.listValidation);
        },
        addHeader() {
            if (!this.nodeInspector.listHeadersAPITrigger) {
                this.nodeInspector.listHeadersAPITrigger = [];
            }
            this.nodeInspector.listHeadersAPITrigger.push({
                key: '',
                value: '',
            });
            this.updateNodeProperty('listHeadersAPITrigger', this.nodeInspector.listHeadersAPITrigger);
        },
        addValidation() {
            if (!this.nodeInspector.listValidation) {
                this.nodeInspector.listValidation = [];
            }
            this.nodeInspector.listValidation.push({
                key: '',
                typeValidation: '',
                numberOfCharacter: '',
                regExp: '',
                promptMessage: '',
            });
            this.updateNodeProperty('listValidation', this.nodeInspector.listValidation);
        },
        deleteHeader(index) {
            if (this.nodeInspector.listHeadersAPITrigger && this.nodeInspector.listHeadersAPITrigger.length > 0) {
                this.nodeInspector.listHeadersAPITrigger.splice(index, 1);
                this.updateNodeProperty('listHeadersAPITrigger', this.nodeInspector.listHeadersAPITrigger);
            }
        },
        deleteValidation(index) {
            if (this.nodeInspector.listValidation && this.nodeInspector.listValidation.length > 0) {
                this.nodeInspector.listValidation.splice(index, 1);
                this.updateNodeProperty('listValidation', this.nodeInspector.listValidation);
            }
        },
        // Knowledge API Header methods
        updateKnowledgeAPIHeaderProperty(index, property, value) {
            if (!this.nodeInspector.listHeaders[index]) return;
            this.nodeInspector.listHeaders[index][property] = value;
            this.updateNodeProperty('listHeaders', this.nodeInspector.listHeaders);
        },
        addKnowledgeAPIHeader() {
            if (!this.nodeInspector.listHeaders) {
                this.nodeInspector.listHeaders = [];
            }
            this.nodeInspector.listHeaders.push({
                key: '',
                value: '',
            });
            this.updateNodeProperty('listHeaders', this.nodeInspector.listHeaders);
        },
        deleteKnowledgeAPIHeader(index) {
            if (this.nodeInspector.listHeaders && this.nodeInspector.listHeaders.length > 0) {
                this.nodeInspector.listHeaders.splice(index, 1);
                this.updateNodeProperty('listHeaders', this.nodeInspector.listHeaders);
            }
        },
        // API Request node header methods
        addApiRequestHeader() {
            if (!this.nodeInspector.apiRequestHeaders) {
                this.nodeInspector.apiRequestHeaders = [];
            }
            this.nodeInspector.apiRequestHeaders.push({ key: '', value: '' });
            this.updateNodeProperty('apiRequestHeaders', this.nodeInspector.apiRequestHeaders);
        },
        updateApiRequestHeaderProperty(index, property, value) {
            if (!this.nodeInspector.apiRequestHeaders[index]) return;
            this.nodeInspector.apiRequestHeaders[index][property] = value;
            this.updateNodeProperty('apiRequestHeaders', this.nodeInspector.apiRequestHeaders);
        },
        deleteApiRequestHeader(index) {
            if (this.nodeInspector.apiRequestHeaders && this.nodeInspector.apiRequestHeaders.length > 0) {
                this.nodeInspector.apiRequestHeaders.splice(index, 1);
                this.updateNodeProperty('apiRequestHeaders', this.nodeInspector.apiRequestHeaders);
            }
        },
        insertApiRequestVariable(variableKey) {
            const textareaRef = this.$refs.apiRequestJsonTextarea;
            if (!textareaRef) return;
            const textarea = textareaRef.$el?.querySelector('textarea');
            if (!textarea) return;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const currentValue = this.nodeInspector.apiRequestBody || '';
            const newValue = currentValue.substring(0, start) + `{${variableKey}}` + currentValue.substring(end);
            this.nodeInspector.apiRequestBody = newValue;
            this.updateNodeProperty('apiRequestBody', newValue);
            this.$nextTick(() => {
                const newPos = start + `{${variableKey}}`.length;
                textarea.setSelectionRange(newPos, newPos);
                textarea.focus();
            });
        },
        onApiRequestJsonDrop(event) {
            const variableKey = event.dataTransfer.getData('text/plain');
            if (!variableKey) return;
            this.insertApiRequestVariable(variableKey);
        },
        // Get category color
        getCategoryColor(category) {
            const colors = {
                'System': 'blue',
                'User': 'green',
                'Session': 'purple',
                'Input': 'orange',
                'Context': 'teal',
                'LHB': 'teal',
            };
            return colors[category] || 'grey';
        },
        // Utility methods for node type styling
        async loadTaxPlanningYears() {
            try {
                const years = await taxConfigApi.getYears(true);
                this.taxPlanningYearOptions = (Array.isArray(years) ? years : []).map(y => ({
                    yearID: y.yearID,
                    label: `${y.taxYearCode || y.yearID}${y.description ? ' — ' + y.description : ''}${y.isActive ? ' (active)' : ''}`
                }));
            } catch (e) {
                this.taxPlanningYearOptions = [];
            }
        },
        // Tax Planning V2 — load the settings versions for the node inspector dropdown.
        // Fail-soft: if the endpoint is unavailable the list stays empty and the node can
        // still keep/choose "ใช้เวอร์ชันที่ใช้งานอยู่ (อัตโนมัติ)" (value null).
        async loadTaxPlanningV2SettingsVersions() {
            if (this.taxPlanningV2SettingsVersionsLoading) return;
            this.taxPlanningV2SettingsVersionsLoading = true;
            try {
                const versions = await taxPlanningSettingsV2Api.listVersions();
                this.taxPlanningV2SettingsVersions = (Array.isArray(versions) ? versions : []).map(v => ({
                    id: v.id,
                    versionCode: v.versionCode || `#${v.id}`,
                    description: v.description || '',
                    isActive: !!v.isActive,
                    updatedBy: v.updatedBy || '',
                    updatedDate: v.updatedDate || null
                }));
                this.taxPlanningV2SettingsVersionsLoaded = true;
            } catch (e) {
                this.taxPlanningV2SettingsVersions = [];
                this.taxPlanningV2SettingsVersionsLoaded = false;
            } finally {
                this.taxPlanningV2SettingsVersionsLoading = false;
            }
        },
        // Open the Tax Configuration V2 admin page (which hosts the SETTINGS dialog) in a new
        // tab so unsaved flow edits on this canvas are not lost.
        openTaxPlanningV2Settings() {
            try {
                const target = this.$router.resolve({ name: 'TaxConfigurationV2' });
                window.open(target.href, '_blank', 'noopener');
            } catch (e) {
                window.open('/TaxConfigurationV2', '_blank', 'noopener');
            }
        },
        // ── Button Style node ─────────────────────────────────────────────────────────────────────────
        // Inspector helpers for the two style-JSON fields. Validation and preview mirror the server rule in
        // ButtonStyleRewriter: `value` = the agent's target first, then every other key of the JSON in order.
        buttonStyleJsonErrors(text) {
            if (!text || !String(text).trim()) return [];   // blank = server default
            try {
                const parsed = JSON.parse(text);
                if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
                    return ['ต้องเป็น JSON object เช่น {"fillColor":"#0B5FFF","cornerRadius":12}'];
                }
                return [];
            } catch (e) {
                return ['JSON ไม่ถูกต้อง — ปุ่มชนิดนี้จะถูกปล่อยไว้ตามเดิม: ' + e.message];
            }
        },
        buttonStylePreview(marker, label, target, text) {
            const key = marker === 'productbutton' ? 'productButtonStyle' : 'serviceButtonStyle';
            const source = (text && String(text).trim()) ? text : BUTTON_STYLE_DEFAULTS[key];
            let styled = target;
            try {
                const parsed = JSON.parse(source);
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    const out = { value: target };
                    for (const k of Object.keys(parsed)) {
                        if (k.toLowerCase() !== 'value') out[k] = parsed[k];
                    }
                    styled = JSON.stringify(out);
                }
            } catch (e) { /* invalid JSON → the server leaves the button as written */ }
            return `|${marker}|[${label}](${styled})`;
        },
        resetButtonStyle(key) {
            const value = BUTTON_STYLE_DEFAULTS[key];
            if (value === undefined) return;
            this.nodeInspector[key] = value;
            this.updateNodeProperty(key, value);
        },

        getNodeTypeColor(type) {
            const colors = {
                'messageInput': 'green',
                'model': 'blue',
                'llmEnrich': 'teal',
                'buttonStyle': 'purple',
                'taxPlanning': 'green',
                'taxPlanningV2': 'teal',
                'guardrailAI': 'green',
                'intentAI': 'purple',
                'intentClassifier': 'purple',
                'intent': 'purple',
                'smartIntent': 'purple',
                'router': 'green',
                'advancedRouter': 'purple',
                'dynamicRouter': 'teal',
                'queryRewriter': 'purple',
                'apiTrigger': 'blue',
                'apiRequest': 'indigo',
                'favoriteMatcher': 'teal',
                'serviceMatcher': 'cyan',
                'contactAgent': 'red',
                'contactAgentV2': 'red',
                'switch': 'red',
                'condition': 'green',
                'jsonCondition': 'teal',
                'serviceFallback': 'teal',
                'delay': 'blue',
                'documents': 'blue',
                'dynamicKnowledge': 'teal',
                'metaDocuments': 'indigo',
                'api': 'amber',
                'response': 'red',
                'databaseIntegration': 'green',
                'mutualFund': 'purple',
                'apiknowledge': 'indigo',
                'products': 'amber',
                'webhook': 'green',           
                'email': 'red',
                'sharepoint': 'green',
                'interestOptimizer': 'orange',
                'loanInterest': 'green',
                'interestOptimizerV2': 'orange',
                'loanInterestV2': 'green',
                'orchestrator': 'pink',
                'agent': 'teal',
                'tool': 'orange',
                'flowTool': 'indigo',
                'flowInput': 'green',
                'flowOutput': 'orange'
            };
            return colors[type] || 'grey';
        },
        getNodeTypeIcon(type) {
            const icons = {
                'messageInput': 'mdi-chat',
                'model': 'mdi-robot',
                'llmEnrich': 'mdi-text-box-plus',
                'buttonStyle': 'mdi-gesture-tap-button',
                'taxPlanning': 'mdi-clipboard-text-clock',
                'taxPlanningV2': 'mdi-clipboard-text-clock',
                'guardrailAI': 'mdi-shield-check',
                'intentAI': 'mdi-target-variant',
                'intentClassifier': 'mdi-sort-variant',
                'intent': 'mdi-crosshairs-question',
                'smartIntent': 'mdi-robot-confused',
                'router': 'mdi-router-wireless',
                'advancedRouter': 'mdi-source-branch',
                'dynamicRouter': 'mdi-file-document-multiple',
                'queryRewriter': 'mdi-message-text-clock-outline',
                'apiTrigger': 'mdi-rocket-launch',
                'apiRequest': 'mdi-api',
                'favoriteMatcher': 'mdi-account-search',
                'serviceMatcher': 'mdi-database-search',
                'contactAgent': 'mdi-account-supervisor',
                'contactAgentV2': 'mdi-account-supervisor',
                'interestOptimizer': 'mdi-calculator',
                'loanInterest': 'mdi-home-analytics',
                'interestOptimizerV2': 'mdi-calculator-variant',
                'loanInterestV2': 'mdi-home-analytics',
                'switch': 'mdi-arrow-decision',
                'condition': 'mdi-swap-horizontal-bold',
                'jsonCondition': 'mdi-code-json',
                'serviceFallback': 'mdi-swap-horizontal-variant',
                'delay': 'mdi-timer',
                'documents': 'mdi-file-document-multiple',
                'metaDocuments': 'mdi-bookshelf',
                'dynamicKnowledge': 'mdi-file-document-check',
                'api': 'mdi-api',
                'response': 'mdi-message-reply-text',
                'databaseIntegration': 'mdi-database-search',
                'mutualFund': 'mdi-chart-donut-variant',
                'apiknowledge': 'mdi-cloud',
                'products': 'mdi-package-variant',
                'webhook': 'mdi-webhook',
                'email': 'mdi-email',
                'sharepoint': 'mdi-microsoft-sharepoint',
                'orchestrator': 'mdi-hub',
                'agent': 'mdi-robot',
                'tool': 'mdi-wrench',
                'flowTool': 'mdi-sitemap',
                'flowInput': 'mdi-login-variant',
                'flowOutput': 'mdi-logout-variant'
            };
            return icons[type] || 'mdi-circle';
        },
        getNodeTypeLabel(type) {
            const labels = {
                'messageInput': 'Message Input',
                'model': 'AI Model',
                'llmEnrich': 'LLM Enrich',
                'buttonStyle': 'Button Style',
                'taxPlanning': 'Tax Planning Form',
                'taxPlanningV2': 'Tax Planning Form V2',
                'guardrailAI': 'Guardrail AI',
                'intentAI': 'Intent AI Model',
                'intentClassifier': 'Intent Classifier',
                'intent': 'Intent',
                'smartIntent': 'Smart Intent',
                'router': 'Router',
                'advancedRouter': 'Advanced Router',
                'dynamicRouter': 'Dynamic Router',
                'apiTrigger': 'API Trigger',
                'apiRequest': 'API Request',
                'favoriteMatcher': 'Favorite Matcher',
                'serviceMatcher': 'Service Matcher',
                'contactAgent': 'Contact Agent',
                'contactAgentV2': 'Contact Agent V2',
                'interestOptimizer': 'Interest Optimizer',
                'loanInterest': 'Loan Interest',
                'interestOptimizerV2': 'Interest Optimizer V2',
                'loanInterestV2': 'Loan Interest V2',
                'switch': 'Switch',
                'condition': 'Condition',
                'jsonCondition': 'JSON Condition',
                'serviceFallback': 'Service Fallback',
                'delay': 'Delay',
                'documents': 'Documents',
                'dynamicKnowledge': 'Dynamic Knowledge',
                'metaDocuments': 'Meta Documents',
                'databaseIntegration': 'Database Integration',
                'mutualFund': 'Mutual Fund',
                'apiknowledge': 'Knowledge API',
                'products': 'Products',
                'orchestrator': 'Orchestrator',
                'agent': 'Agent',
                'tool': 'Tool',
                'flowTool': 'Flow Tool',
                'flowInput': 'Flow Input',
                'flowOutput': 'Flow Output'
            };
            return labels[type] || type;
        },
        // Forbidden words management methods (ลอฝมาจาฝ ModelManagement.vue)
        isEnterForbidden(evt, index) {
            let charCode = evt.which ? evt.which : evt.keyCode;
            if (charCode === 13) {
                evt.preventDefault();
            } else {
                this.checkNewForbiddenWord(index);
                return true;
            }
        },
        checkNewForbiddenWord(index) {
            // Initialize forbiddenWords array if it doesn't exist
            if (!this.nodeInspector.forbiddenWords) {
                this.nodeInspector.forbiddenWords = [''];
            }

            // Add new empty word if we're at the last index
            if (this.nodeInspector.forbiddenWords.length === index + 1) {
                this.nodeInspector.forbiddenWords.push('');
                this.updateNodeProperty('forbiddenWords', this.nodeInspector.forbiddenWords);
            }
            return true;
        },
        checkDeleteForbiddenWord(index, value) {
            if (!this.nodeInspector.forbiddenWords) return;
            if (value === '' && this.nodeInspector.forbiddenWords.length > 1) {
                this.nodeInspector.forbiddenWords.splice(index, 1);
                this.updateNodeProperty('forbiddenWords', this.nodeInspector.forbiddenWords);
            }
        },
        updateForbiddenWordProperty(index, value) {
            // Initialize forbiddenWords array if it doesn't exist
            if (!this.nodeInspector.forbiddenWords) {
                this.nodeInspector.forbiddenWords = [''];
            }

            // Ensure the array has enough elements
            while (this.nodeInspector.forbiddenWords.length <= index) {
                this.nodeInspector.forbiddenWords.push('');
            }

            this.nodeInspector.forbiddenWords[index] = value;
            this.updateNodeProperty('forbiddenWords', this.nodeInspector.forbiddenWords);
        },
        removeForbiddenWordDirect(index) {
            if (!this.nodeInspector.forbiddenWords || this.nodeInspector.forbiddenWords.length <= 1) return;
            this.nodeInspector.forbiddenWords.splice(index, 1);
            this.updateNodeProperty('forbiddenWords', this.nodeInspector.forbiddenWords);
        },
        // เฝ็บ methods เดิมไว้เผื่อใช้ที่อื่น
        updateForbiddenWord(index, value) {
            this.updateForbiddenWordProperty(index, value);
            this.checkDeleteForbiddenWord(index, value);
        },
        removeForbiddenWord(index) {
            this.removeForbiddenWordDirect(index);
        },
        
        // Smart Intent Forbidden words management methods
        updateSmartIntentForbiddenWord(smartIntentIndex, index, value) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction) {
                smartIntent.mainAction = {};
            }
            if (!smartIntent.mainAction.forbiddenWords) {
                smartIntent.mainAction.forbiddenWords = [''];
            }
            
            // Ensure the array has enough elements
            while (smartIntent.mainAction.forbiddenWords.length <= index) {
                smartIntent.mainAction.forbiddenWords.push('');
            }

            smartIntent.mainAction.forbiddenWords[index] = value;
            this.updateSmartIntentData();
        },
        
        removeSmartIntentForbiddenWord(smartIntentIndex, index) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction?.forbiddenWords || smartIntent.mainAction.forbiddenWords.length <= 1) return;
            smartIntent.mainAction.forbiddenWords.splice(index, 1);
            this.updateSmartIntentData();
        },
        
        isEnterSmartIntentForbidden(evt, smartIntentIndex, index) {
            let charCode = evt.which ? evt.which : evt.keyCode;
            if (charCode === 13) {
                evt.preventDefault();
            } else {
                this.checkNewSmartIntentForbiddenWord(smartIntentIndex, index);
                return true;
            }
        },
        
        checkNewSmartIntentForbiddenWord(smartIntentIndex, index) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction) {
                smartIntent.mainAction = {};
            }
            if (!smartIntent.mainAction.forbiddenWords) {
                smartIntent.mainAction.forbiddenWords = [''];
            }

            // Add new empty word if we're at the last index
            if (smartIntent.mainAction.forbiddenWords.length === index + 1) {
                smartIntent.mainAction.forbiddenWords.push('');
                this.updateSmartIntentData();
            }
            return true;
        },
        
        checkDeleteSmartIntentForbiddenWord(smartIntentIndex, index, value) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction?.forbiddenWords) return;
            if (value === '' && smartIntent.mainAction.forbiddenWords.length > 1) {
                smartIntent.mainAction.forbiddenWords.splice(index, 1);
                this.updateSmartIntentData();
            }
        },
        
        updateSmartIntentForbiddenWordProperty(smartIntentIndex, index, value) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction) {
                smartIntent.mainAction = {};
            }
            if (!smartIntent.mainAction.forbiddenWords) {
                smartIntent.mainAction.forbiddenWords = [''];
            }

            // Ensure the array has enough elements
            while (smartIntent.mainAction.forbiddenWords.length <= index) {
                smartIntent.mainAction.forbiddenWords.push('');
            }

            smartIntent.mainAction.forbiddenWords[index] = value;
            
            // Add new empty word if we're typing in the last field and it's not empty
            if (smartIntent.mainAction.forbiddenWords.length === index + 1 && value !== '') {
                smartIntent.mainAction.forbiddenWords.push('');
            }
            
            this.updateSmartIntentData();
        },
        
        removeSmartIntentForbiddenWordDirect(smartIntentIndex, index) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction?.forbiddenWords || smartIntent.mainAction.forbiddenWords.length <= 1) return;
            smartIntent.mainAction.forbiddenWords.splice(index, 1);
            this.updateSmartIntentData();
        },
        
        // Smart Intent Fallback Forbidden words management methods
        updateSmartIntentFallbackForbiddenWord(index, value) {
            if (!this.nodeInspector.smartIntentFallback.forbiddenWords) {
                this.nodeInspector.smartIntentFallback.forbiddenWords = [''];
            }
            
            // Ensure the array has enough elements
            while (this.nodeInspector.smartIntentFallback.forbiddenWords.length <= index) {
                this.nodeInspector.smartIntentFallback.forbiddenWords.push('');
            }

            this.nodeInspector.smartIntentFallback.forbiddenWords[index] = value;
            this.updateSmartIntentFallbackData();
        },
        
        removeSmartIntentFallbackForbiddenWord(index) {
            if (!this.nodeInspector.smartIntentFallback.forbiddenWords || this.nodeInspector.smartIntentFallback.forbiddenWords.length <= 1) return;
            this.nodeInspector.smartIntentFallback.forbiddenWords.splice(index, 1);
            this.updateSmartIntentFallbackData();
        },
        
        isEnterSmartIntentFallbackForbidden(evt, index) {
            let charCode = evt.which ? evt.which : evt.keyCode;
            if (charCode === 13) {
                evt.preventDefault();
            } else {
                this.checkNewSmartIntentFallbackForbiddenWord(index);
                return true;
            }
        },
        
        checkNewSmartIntentFallbackForbiddenWord(index) {
            if (!this.nodeInspector.smartIntentFallback.forbiddenWords) {
                this.nodeInspector.smartIntentFallback.forbiddenWords = [''];
            }

            // Add new empty word if we're at the last index
            if (this.nodeInspector.smartIntentFallback.forbiddenWords.length === index + 1) {
                this.nodeInspector.smartIntentFallback.forbiddenWords.push('');
            }
        },
        
        checkDeleteSmartIntentFallbackForbiddenWord(index, value) {
            if (!value && this.nodeInspector.smartIntentFallback.forbiddenWords && this.nodeInspector.smartIntentFallback.forbiddenWords.length > 1) {
                this.removeSmartIntentFallbackForbiddenWord(index);
            }
        },
        
        // IntentAI Forbidden words management methods
        isEnterIntentaiForbidden(evt, index) {
            let charCode = evt.which ? evt.which : evt.keyCode;
            if (charCode === 13) {
                evt.preventDefault();
            } else {
                this.checkNewIntentaiForbiddenWord(index);
                return true;
            }
        },
        checkNewIntentaiForbiddenWord(index) {
            // Initialize intentaiForbiddenWords array if it doesn't exist
            if (!this.nodeInspector.intentaiForbiddenWords) {
                this.nodeInspector.intentaiForbiddenWords = [''];
            }

            // Add new empty word if we're at the last index
            if (this.nodeInspector.intentaiForbiddenWords.length === index + 1) {
                this.nodeInspector.intentaiForbiddenWords.push('');
                this.updateNodeProperty('intentaiForbiddenWords', this.nodeInspector.intentaiForbiddenWords);
            }
            return true;
        },
        checkDeleteIntentaiForbiddenWord(index, value) {
            if (!this.nodeInspector.intentaiForbiddenWords) return;
            if (value === '' && this.nodeInspector.intentaiForbiddenWords.length > 1) {
                this.nodeInspector.intentaiForbiddenWords.splice(index, 1);
                this.updateNodeProperty('intentaiForbiddenWords', this.nodeInspector.intentaiForbiddenWords);
            }
        },
        updateIntentaiForbiddenWordProperty(index, value) {
            // Initialize intentaiForbiddenWords array if it doesn't exist
            if (!this.nodeInspector.intentaiForbiddenWords) {
                this.nodeInspector.intentaiForbiddenWords = [''];
            }

            // Ensure the array has enough elements
            while (this.nodeInspector.intentaiForbiddenWords.length <= index) {
                this.nodeInspector.intentaiForbiddenWords.push('');
            }

            this.nodeInspector.intentaiForbiddenWords[index] = value;
            this.updateNodeProperty('intentaiForbiddenWords', this.nodeInspector.intentaiForbiddenWords);
        },
        removeIntentaiForbiddenWordDirect(index) {
            if (!this.nodeInspector.intentaiForbiddenWords || this.nodeInspector.intentaiForbiddenWords.length <= 1) return;
            this.nodeInspector.intentaiForbiddenWords.splice(index, 1);
            this.updateNodeProperty('intentaiForbiddenWords', this.nodeInspector.intentaiForbiddenWords);
        },
        // Provider and Model management methods
        async changeProvider(value) {
            return new Promise((resolve) => {
                if (!value) {
                    resolve();
                    return;
                }

                if (value == "Google" || value == "Anthropic" || value == "Azure") {
                    this.DDModelShow = this.listDDModel.filter(
                        (e) => e.provider == value
                    );
                } else {
                    this.DDModelShow = this.listDDModel.filter(
                        (e) => e.provider == value
                    );
                }

                // Clear model when provider changes (only if not loading from model config)
                if (!this.loadingModelConfig) {
                    // Check if this is intentAI node
                    if (this.selectedNode && this.selectedNode.data.type === 'intentAI') {
                        if (this.nodeInspector.intentaiModel) {
                            this.nodeInspector.intentaiModel = null;
                            this.updateNodeProperty('intentaiModel', null);
                        }
                    } else if (this.selectedNode && this.selectedNode.data.type === 'agent') {
                        // For agent nodes, clear agentModel
                        if (this.nodeInspector.agentModel) {
                            this.nodeInspector.agentModel = null;
                            this.updateNodeProperty('agentModel', null);
                        }
                    } else {
                        // For other node types
                        if (this.nodeInspector.model) {
                            this.nodeInspector.model = null;
                            this.updateNodeProperty('model', null);
                        }
                    }
                }

                // Give Vue time to update the dropdown
                this.$nextTick(() => {
                    resolve();
                });
            });
        },
        async changeModel(value) {
            if (!value) {
                return;
            }

            // Check if this is intentAI node and get the correct property
            const isIntentAI = this.selectedNode && this.selectedNode.data.type === 'intentAI';
            
            let ddm = this.listDDModel.find((e) => e.modelName == value);
            if (ddm) {
                // Update the correct property based on node type
                if (isIntentAI) {
                    this.nodeInspector.intentaiModel = value;
                } else {
                    this.nodeInspector.model = value;
                }
            }
        },
        toggleLockStatus() {
            if (this.$refs.vueFlow) {
                const flow = this.$refs.vueFlow;

                // Get current state - if nodesDraggable is undefined, default to true (unlocked)
                const isCurrentlyLocked = flow.nodesDraggable === false;
                const shouldLock = !isCurrentlyLocked; // Toggle the state
                // Apply new state
                flow.nodesDraggable = !shouldLock;
                flow.nodesConnectable = !shouldLock;
                flow.elementsSelectable = !shouldLock;
                flow.zoomOnScroll = !shouldLock;
                flow.zoomOnDoubleClick = !shouldLock;
                // flow.panOnDrag = !shouldLock ? [1, 2] : false;

                // Update interactiveControls to match lock status
                this.interactiveControls = !shouldLock;

                // Force Vue to re-render
                this.$forceUpdate();
            } else {
                console.warn('VueFlow ref not available');
            }
        },
        onEdgeMouseEnter({ edge: hoveredEdge }) {
            hoveredEdge.style = { strokeWidth: 1.6 };
        },
        onEdgeMouseLeave({ edge: hoveredEdge }) {
            hoveredEdge.style = { strokeWidth: 1.5 };
        },
        onEdgeClick({ edge, event }) {
            // Helper: get handle world position from node internals
            const getHandleCenter = (nodeId, handleId, handleType) => {
                if (!this.$refs.vueFlow) return null;
                const node = this.$refs.vueFlow.findNode(nodeId);
                if (!node) return null;
                const bounds = node.internals?.handleBounds?.[handleType];
                if (bounds && bounds.length > 0) {
                    const handle = (handleId ? bounds.find(h => h.id === handleId) : null) || bounds[0];
                    if (handle) {
                        return {
                            x: node.position.x + handle.x + handle.width / 2,
                            y: node.position.y + handle.y + handle.height / 2,
                        };
                    }
                }
                // Fallback to node center
                const w = node.dimensions?.width || 150;
                const h = node.dimensions?.height || 90;
                return { x: node.position.x + w / 2, y: node.position.y + h / 2 };
            };

            // Ctrl+Shift+Click (Windows) or Cmd+Shift+Click (Mac) → pan to source handle
            if ((event.ctrlKey || event.metaKey) && event.shiftKey) {
                event.preventDefault();
                const pos = getHandleCenter(edge.source, edge.sourceHandle, 'source');
                if (pos) {
                    const { zoom } = this.$refs.vueFlow.getViewport();
                    this.$refs.vueFlow.setCenter(pos.x, pos.y, { zoom, duration: 800 });
                }
            // Ctrl+Click (Windows) or Cmd+Click (Mac) → pan to target handle
            } else if (event.ctrlKey || event.metaKey) {
                event.preventDefault();
                const pos = getHandleCenter(edge.target, edge.targetHandle, 'target');
                if (pos) {
                    const { zoom } = this.$refs.vueFlow.getViewport();
                    this.$refs.vueFlow.setCenter(pos.x, pos.y, { zoom, duration: 800 });
                }
            }
        },

        // Handle clicking on the pane (background) to clear selection
        onPaneClick() {
            // Clear node selection and close inspector
            this.selectedNodes = [];
            this.selectedNode = null;
            this.closeInspector();
        },

        // Node drag event handlers for undo/redo
        onNodeDragStart({ nodes }) {
            this.isDraggingNodes = true;
            // Store initial positions of all selected nodes
            this.dragStartPositions = {};
            nodes.forEach(node => {
                this.dragStartPositions[node.id] = {
                    x: node.position.x,
                    y: node.position.y
                };
            });
        },

        onNodeDragStop({ nodes }) {
            this.isDraggingNodes = false;
            
            // Check if any node actually moved
            let hasMovement = false;
            for (const node of nodes) {
                const startPos = this.dragStartPositions[node.id];
                if (startPos && (Math.abs(startPos.x - node.position.x) > 1 || Math.abs(startPos.y - node.position.y) > 1)) {
                    hasMovement = true;
                    break;
                }
            }
            
            // Only save to history if there was actual movement
            if (hasMovement && !this.isPerformingUndoRedo) {
                const nodeCount = nodes.length;
                this.saveStateToHistory(`Move ${nodeCount} Node${nodeCount > 1 ? 's' : ''}`);
            }
            
            // Clear stored positions
            this.dragStartPositions = {};
        },
        // Optimized node changes to reduce re-renders & expensive work during drag
        onNodesChange(changes) {
            // Build index map once (O(n)) and reuse (O(1) lookups)
            const indexMap = new Map(this.tempNode.map((n, i) => [n.id, i]));

            let anySelectionChanged = false;

            changes.forEach(change => {
                if (change.type === 'position' && change.position) {
                    const nodeIndex = indexMap.get(change.id);
                    if (nodeIndex !== undefined) {
                        const node = this.tempNode[nodeIndex];
                        // Mutate position directly (cheaper than replacing whole node)
                        node.position.x = change.position.x;
                        node.position.y = change.position.y;
                    }
                } else if (change.type === 'remove') {
                    this.tempNode = this.tempNode.filter(node => node.id !== change.id);
                    this.tempEdge = this.tempEdge.filter(edge => edge.source !== change.id && edge.target !== change.id);
                } else if (change.type === 'select') {
                    const nodeIndex = indexMap.get(change.id);
                    if (nodeIndex !== undefined) {
                        this.tempNode[nodeIndex].selected = change.selected;
                        anySelectionChanged = true;
                    }
                }
            });

            if (anySelectionChanged) {
                this.selectedNodes = this.tempNode.filter(node => node.selected);
            }
        },
        onEdgesChange(changes) {
            // Collect affected node IDs from edge changes
            const affectedNodeIds = new Set();

            changes.forEach(change => {
                if (change.type === 'add') {
                    // Handle new edge
                    this.tempEdge.push(change.item);
                    affectedNodeIds.add(change.item.source);
                    affectedNodeIds.add(change.item.target);
                } else if (change.type === 'remove') {
                    // Find the edge before removing to get source/target
                    const edgeToRemove = this.tempEdge.find(edge => edge.id === change.id);
                    if (edgeToRemove) {
                        affectedNodeIds.add(edgeToRemove.source);
                        affectedNodeIds.add(edgeToRemove.target);
                    }
                    // Handle edge removal
                    this.tempEdge = this.tempEdge.filter(edge => edge.id !== change.id);
                }
            });

            // Check validation only for affected nodes
            if (affectedNodeIds.size > 0) {
                this.$nextTick(() => {
                    this.checkAffectedNodesValidation(Array.from(affectedNodeIds));
                });
            }
        },
        onConnect(connection) {

            // Validation rules for connections
            const validationResult = this.validateConnection(connection);
            if (!validationResult.isValid) {
                this.showWarning(validationResult.message);
                return;
            }

            // Check if edge already exists to prevent duplicates
            const existingEdge = this.tempEdge.find(edge =>
                edge.source === connection.source &&
                edge.target === connection.target &&
                edge.sourceHandle === connection.sourceHandle &&
                edge.targetHandle === connection.targetHandle
            );

            if (existingEdge) {
                console.warn('Edge already exists between these nodes and handles');
                this.showWarning('Connection already exists between these nodes');
                return;
            }

            // Create new edge
            const newEdge = {
                id: `e${connection.source}-${connection.target}-${this.generateGuid()}`,
                source: connection.source,
                target: connection.target,
                sourceHandle: connection.sourceHandle,
                targetHandle: connection.targetHandle,
                type: 'flow-edge',
                markerEnd: MarkerType.ArrowClosed,
                animated: false,
                showButton: false
            };

            this.tempEdge.push(newEdge);

            // Save state to history
            const sourceNode = this.tempNode.find(node => node.id === connection.source);
            const targetNode = this.tempNode.find(node => node.id === connection.target);
            const sourceName = sourceNode ? sourceNode.data.label || 'Node' : 'Node';
            const targetName = targetNode ? targetNode.data.label || 'Node' : 'Node';
            this.saveStateToHistory(`Connect ${sourceName} → ${targetName}`);

            // Check validation only for source and target nodes
            this.$nextTick(() => {
                this.checkAffectedNodesValidation([connection.source, connection.target]);
            });
        },
        validateConnection(connection) {
            const sourceNode = this.tempNode.find(node => node.id === connection.source);
            const targetNode = this.tempNode.find(node => node.id === connection.target);

            if (!sourceNode || !targetNode) {
                return {
                    isValid: false,
                    message: 'Source or target node not found'
                };
            }

            // Special validation for Agent connecting to Knowledge nodes (1 per type)
            // 'taxPlanningV2' is one-per-agent for a reason of its own: every instance reads the SAME form of the
            // SAME session, so a second one adds a duplicate block to the prompt and nothing else.
            // 'buttonStyle' is one-per-agent too: two style plugins under one agent would be two answers to
            // "which JSON goes into the parentheses" — and the second one would silently lose.
            const knowledgeNodeTypes = ['documents', 'databaseIntegration', 'mutualFund', 'apiknowledge', 'products', 'sharepoint', 'taxPlanningV2', 'metaDocuments', 'buttonStyle'];
            if (sourceNode.data.type === 'agent' && knowledgeNodeTypes.includes(targetNode.data.type)) {
                // Check if this agent already has a connection to this type of knowledge node
                const existingKnowledgeConnections = this.tempEdge.filter(edge => 
                    edge.source === connection.source && 
                    edge.sourceHandle === 'tools-bottom'
                );

                // Check each existing connection
                for (const existingEdge of existingKnowledgeConnections) {
                    if (existingEdge.target === connection.target) continue; // Skip same target
                    
                    const existingTargetNode = this.tempNode.find(n => n.id === existingEdge.target);
                    if (existingTargetNode && existingTargetNode.data.type === targetNode.data.type) {
                        const knowledgeLabels = {
                            'documents': 'Documents',
                            'databaseIntegration': 'Database Integration',
                            'mutualFund': 'Mutual Fund',
                            'apiknowledge': 'Knowledge API',
                            'products': 'Products',
                            'sharepoint': 'SharePoint',
                            'metaDocuments': 'Meta Documents',
                            'buttonStyle': 'Button Style'
                        };
                        return {
                            isValid: false,
                            message: `Agent can only connect to one ${knowledgeLabels[targetNode.data.type]} node. Disconnect the existing connection first.`
                        };
                    }
                }
            }

            // Rule 1: ห้ามเชื่อมต่อฝับตัวเอง
            if (connection.source === connection.target) {
                return {
                    isValid: false,
                    message: 'Cannot connect node to itself'
                };
            }

            // Rule 2: ห้ามลาฝจาฝ input handle
            if (connection.sourceHandle === 'input') {
                return {
                    isValid: false,
                    message: 'Cannot drag from input handle. Input handles can only receive connections.'
                };
            }

            // Rule 3: ห้ามลาฝไป output handle
            //
            // 🔴 This is an ALLOWLIST of target handle ids, not a test for the word "output" — anything not on
            //    it is assumed to be an output handle and refused with that message. So every genuine INPUT
            //    handle whose id is not plain 'input' has to be listed here, or it reports itself as an output
            //    and cannot be connected to at all. CONTACT_AGENT_V2_TOP_HANDLE is Contact Agent V2's second
            //    input (the top one, reserved for an Orchestrator) and belongs in exactly this set. Contact
            //    Agent (V1) has no top handle — it is deliberately not an Orchestrator agent.
            if (connection.targetHandle
                && !['input', 'plugin-input', CONTACT_AGENT_V2_TOP_HANDLE].includes(connection.targetHandle)) {
                return {
                    isValid: false,
                    message: 'Cannot connect to output handle. Output handles can only send connections.'
                };
            }

            // Rule 4: ตรวจสอบฝารเชื่อมต่อตาม node type
            const sourceNodeType = sourceNode.data.type;
            const targetNodeType = targetNode.data.type;

            // Rule 4a: Message Input node ต้องเป็น start node (ไม่รับ input)
            if (targetNodeType === 'messageInput') {
                return {
                    isValid: false,
                    message: 'Message Input node cannot receive connections as it is a start node'
                };
            }

            // Rule 4b: Response node ต้องเป็น end node (ไม่ส่ง output ต่อ)
            if (sourceNodeType === 'response') {
                return {
                    isValid: false,
                    message: 'Response node cannot send connections as it is an end node'
                };
            }

            // Rule 5: ตรวจสอบ handle types ที่เฉพาะเจาะจง
            const handleValidation = this.validateHandleTypes(sourceNode, targetNode, connection);
            if (!handleValidation.isValid) {
                return handleValidation;
            }

            // Rule 6: ห้ามสร้าง circular reference
            const circularValidation = this.checkCircularReference(connection);
            if (!circularValidation.isValid) {
                return circularValidation;
            }

            // Rule 7: จำฝัดจำนวน input connections
            const inputLimitValidation = this.validateInputLimit(sourceNode, targetNode, connection);
            if (!inputLimitValidation.isValid) {
                return inputLimitValidation;
            }

            // Rule 8: จำฝัดจำนวน output connections สำหรับ messageInput node
            const outputLimitValidation = this.validateOutputLimit(sourceNode, targetNode, connection);
            if (!outputLimitValidation.isValid) {
                return outputLimitValidation;
            }

            return { isValid: true };
        },
        // Validate specific handle types based on node types
        validateHandleTypes(sourceNode, targetNode, connection) {
            const sourceType = sourceNode.data.type;
            const targetType = targetNode.data.type;
            const sourceHandle = connection.sourceHandle;
            const targetHandle = connection.targetHandle;

            // ──────────────────────────────────────────────────────────────────────────────────────────
            //  Contact Agent V2's two input sides are not interchangeable, and BOTH are always on screen.
            //
            //    · TOP  ('input-top') — only an Orchestrator's `agents-bottom`. It means "I am one of the
            //      agents you may pick", and the engine reaches it through PluginNodes.
            //    · LEFT ('input')     — only the ordinary flow arriving (Router / Intent / AI Model / …).
            //      The engine walks into it as the next step on the path.
            //
            //  Nothing is hidden while dragging — the node keeps its shape under the cursor — so these two
            //  rules are what keeps the meanings apart. This half rejects everything-but-an-Orchestrator
            //  aimed at the top; the Orchestrator half lives in the orchestrator block further down.
            //  Contact Agent (V1) has only the left handle and is intentionally not an Orchestrator agent.
            // ──────────────────────────────────────────────────────────────────────────────────────────
            if (targetType === 'contactAgentV2' && targetHandle === CONTACT_AGENT_V2_TOP_HANDLE
                && sourceHandle !== 'agents-bottom') {
                return {
                    isValid: false,
                    message: 'Cannot connect here: the top handle of a Contact Agent V2 node is only for an Orchestrator. Use the left handle instead.'
                };
            }

            if (sourceType === 'messageInput') {
                const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'intent', 'smartIntent', 'advancedRouter', 'dynamicRouter', 'router', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'orchestrator', 'queryRewriter', 'jsonCondition', 'apiRequest', 'serviceMatcher', 'llmEnrich'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'Message Input node can only connect to AI Model, Intent Handler, or JSON Condition nodes'
                    };
                }

            }

            // Flow Input node validation (for internal flow) - similar to messageInput
            if (sourceType === 'flowInput') {
                const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'intent', 'smartIntent', 'advancedRouter', 'dynamicRouter', 'router', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'orchestrator', 'queryRewriter', 'apiTrigger', 'contactAgent', 'contactAgentV2', 'email', 'flowOutput', 'apiRequest', 'serviceMatcher', 'llmEnrich'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'Flow Input can only connect to AI Model, Intent, Router, API Trigger, Contact Agent, Email, or Flow Output nodes'
                    };
                }
            }

            // Flow Output node validation (for internal flow) - end node, cannot send
            if (sourceType === 'flowOutput') {
                return {
                    isValid: false,
                    message: 'Flow Output node cannot send connections as it is an end node'
                };
            }

            // Flow Output should not receive connections from start nodes
            if (targetType === 'flowOutput') {
                // Allow most processing nodes to connect to flowOutput
                const allowedSources = ['model', 'taxPlanning', 'taxPlanningV2', 'intent', 'smartIntent', 'advancedRouter', 'dynamicRouter', 'router', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'orchestrator', 'apiTrigger', 'contactAgent', 'contactAgentV2', 'email', 'flowInput', 'reformatTTS', 'ssmlFormat', 'aiVoice', 'queryRewriter', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'serviceMatcher'];
                if (!allowedSources.includes(sourceType)) {
                    return {
                        isValid: false,
                        message: `${this.getNodeTypeLabel(sourceType)} cannot connect to Flow Output`
                    };
                }
            }

            if (sourceType === 'api') {
                const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'intent', 'smartIntent', 'advancedRouter', 'dynamicRouter', 'router'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'API Call Input node can only connect to AI Model nodes or Intent Handler nodes'
                    };
                }

            }

            // Intent node handle validation
            if (sourceType === 'intent') {
                if (sourceHandle == 'output-bottom') {
                    const allowedTargets = ['intentAI'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Source model of Intent node can only connect to Intent AI nodes'
                        };
                    }
                }
                else {
                    const allowedTargets = ['intent', 'model', 'taxPlanning', 'taxPlanningV2', 'response', 'apiTrigger', 'contactAgent', 'contactAgentV2', 'email', 'smartIntent', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'queryRewriter', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Intent node can only connect to Intent, AI Model, API Trigger, Contact Agent, Query Rewriter, JSON Condition, Service Fallback, Orchestrator nodes or Response'
                        };
                    }
                }
            }

            // Smart Intent node handle validation
            if (sourceType === 'smartIntent') {
                if (sourceHandle == 'output-bottom') {
                    const allowedTargets = ['intentAI'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Source model of Intent node can only connect to Intent AI nodes'
                        };
                    }
                }
                else {
                    const allowedTargets = ['intent', 'model', 'taxPlanning', 'taxPlanningV2', 'response', 'apiTrigger', 'contactAgent', 'contactAgentV2', 'email', 'smartIntent', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'queryRewriter', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Smart Intent node can only connect to Intent, AI Model, API Trigger, Contact Agent, Query Rewriter, JSON Condition, Service Fallback, Orchestrator nodes or Response'
                        };
                    }
                }
            }

            // Router node handle validation
            if (sourceType === 'router') {
                if (sourceHandle == 'output-bottom') {
                    const allowedTargets = ['intentAI'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Source model of Router node can only connect to Intent AI nodes'
                        };
                    }
                }
                else {
                    const allowedTargets = ['intent', 'model', 'taxPlanning', 'taxPlanningV2', 'response', 'apiTrigger', 'contactAgent', 'contactAgentV2', 'email', 'smartIntent', 'advancedRouter', 'router', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'queryRewriter', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Router node can only connect to Intent, AI Model, API Trigger, Contact Agent, Router, Query Rewriter, JSON Condition, Service Fallback, Orchestrator nodes or Response'
                        };
                    }
                }
            }

            // Advanced Router node handle validation
            if (sourceType === 'advancedRouter') {
                if (sourceHandle == 'output-bottom') {
                    const allowedTargets = ['intentAI'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Source model of Advanced Router node can only connect to Intent AI nodes'
                        };
                    }
                }
                else {
                    const allowedTargets = ['intent', 'model', 'taxPlanning', 'taxPlanningV2', 'response', 'apiTrigger', 'contactAgent', 'contactAgentV2', 'email', 'smartIntent', 'router', 'advancedRouter', 'dynamicRouter', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'queryRewriter', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Advanced Router node can only connect to Intent, AI Model, API Trigger, Contact Agent, Router, Query Rewriter, Advanced Router, Dynamic Router, JSON Condition, Service Fallback, Orchestrator nodes or Response'
                        };
                    }
                }
            }

            // Dynamic Router node handle validation
            if (sourceType === 'dynamicRouter') {
                if (sourceHandle == 'output-bottom') {
                    const allowedTargets = ['intentAI'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Source model of Dynamic Router node can only connect to Intent AI nodes'
                        };
                    }
                }
                else {
                    const allowedTargets = ['intent', 'model', 'taxPlanning', 'taxPlanningV2', 'response', 'apiTrigger', 'contactAgent', 'contactAgentV2', 'email', 'smartIntent', 'router', 'advancedRouter', 'dynamicRouter', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'queryRewriter', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Dynamic Router node can only connect to Intent, AI Model, API Trigger, Contact Agent, Router, Query Rewriter, Advanced Router, Dynamic Router, JSON Condition, Service Fallback, Orchestrator nodes or Response'
                        };
                    }
                }
            }

            if(sourceType === 'interestOptimizer') {
                const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'response', 'contactAgent', 'contactAgentV2', 'apiTrigger', 'email', 'intent', 'smartIntent', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'queryRewriter', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: "Interest Optimizer node can't connect to this node type"
                    };
                }
            }

            if(sourceType === 'loanInterest') {
                const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'response', 'contactAgent', 'contactAgentV2', 'apiTrigger', 'email', 'intent', 'smartIntent', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'queryRewriter', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: "Loan Interest node can't connect to this node type"
                    };
                }

            }

            if(sourceType === 'interestOptimizerV2') {
                const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'response', 'contactAgent', 'contactAgentV2', 'apiTrigger', 'email', 'intent', 'smartIntent', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'queryRewriter', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: "Interest Optimizer V2 node can't connect to this node type"
                    };
                }
            }

            if(sourceType === 'loanInterestV2') {
                const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'response', 'contactAgent', 'contactAgentV2', 'apiTrigger', 'email', 'intent', 'smartIntent', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'queryRewriter', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: "Loan Interest V2 node can't connect to this node type"
                    };
                }
            }

            // AI Model node handle validation
            if (sourceType === 'model') {
                if (sourceHandle === 'output-bottom') {
                    // 'metaDocuments' is model+agent only — NOT added to the taxPlanning/taxPlanningV2
                    // parent lists below, because those processors do not consume it and a connection
                    // that silently does nothing is worse than a rejected drop.
                    const allowedTargets = ['databaseIntegration', 'mutualFund', 'documents', 'dynamicKnowledge', 'products', 'apiknowledge', 'sharepoint', 'taxPlanning', 'taxPlanningV2', 'metaDocuments'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'AI Model node knowledge souce can only connect to knowledge nodes;'
                        };
                    }
                } else {
                    // If not output-bottom, it can connect to allowed node types including TTS nodes
                    const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'intent', 'response', 'email', 'smartIntent', 'reformatTTS', 'ssmlFormat', 'queryRewriter', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'AI Model node can only connect to AI Model, Intent, Response, Quesry Rewriter, TTS nodes or Orchestrator'
                        };
                    }
                }
            }

            // Tax Planning node handle validation (parent that accepts knowledge plugins, mirror AI Model)
            if (sourceType === 'taxPlanning') {
                if (sourceHandle === 'output-bottom') {
                    const allowedTargets = ['databaseIntegration', 'mutualFund', 'documents', 'dynamicKnowledge', 'products', 'apiknowledge', 'sharepoint'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Tax Planning node knowledge source can only connect to knowledge nodes;'
                        };
                    }
                } else {
                    const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'intent', 'response', 'email', 'smartIntent', 'reformatTTS', 'ssmlFormat', 'queryRewriter', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Tax Planning node can only connect to AI Model, Intent, Response, Query Rewriter, TTS nodes or Orchestrator'
                        };
                    }
                }
            }

            // Tax Planning V2 node handle validation (parent that accepts knowledge plugins, mirror AI Model)
            if (sourceType === 'taxPlanningV2') {
                if (sourceHandle === 'output-bottom') {
                    const allowedTargets = ['databaseIntegration', 'mutualFund', 'documents', 'dynamicKnowledge', 'products', 'apiknowledge', 'sharepoint'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Tax Planning V2 node knowledge source can only connect to knowledge nodes;'
                        };
                    }
                } else {
                    const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'intent', 'response', 'email', 'smartIntent', 'reformatTTS', 'ssmlFormat', 'queryRewriter', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Tax Planning V2 node can only connect to AI Model, Intent, Response, Query Rewriter, TTS nodes or Orchestrator'
                        };
                    }
                }
            }

            if(sourceType === 'email') {
                const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'response', 'contactAgent', 'contactAgentV2', 'apiTrigger', 'email', 'intent', 'smartIntent', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: "Email node can't connect to this node type"
                    };
                }

            }

            // API Request node handle validation
            if (sourceType === 'apiRequest') {
                if (sourceHandle === 'output-bottom') {
                    // Bottom handle attaches plugin nodes (e.g. Favorite Matcher)
                    const allowedTargets = ['favoriteMatcher'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'API Request bottom handle can only connect to a Favorite Matcher plugin node'
                        };
                    }
                } else {
                    const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'intent', 'response', 'email', 'smartIntent', 'reformatTTS', 'ssmlFormat', 'queryRewriter', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiTrigger', 'contactAgent', 'contactAgentV2', 'apiRequest', 'serviceMatcher'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'API Request node can only connect to AI Model, Intent, Response, Query Rewriter, TTS nodes, Orchestrator, or similar processing nodes'
                        };
                    }
                }
            }

            // API Trigger node handle validation
            if (sourceType === 'apiTrigger') {
                const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'response', 'contactAgent', 'contactAgentV2', 'apiTrigger', 'smartIntent', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'API Trigger node can only connect to AI Model, Response, API Trigger, API Request, Contact Agent or Orchestrator nodes'
                    };
                }
            }
            if (sourceType === 'contactAgent' || sourceType === 'contactAgentV2') {
                const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'response', 'contactAgent', 'contactAgentV2', 'apiTrigger', 'intent', 'smartIntent', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'Contact Agent node can only connect to AI Model, Response, API Trigger, Contact Agent or Orchestrator nodes'
                    };
                }
            }

            // Condition node handle validation
            if (sourceType === 'condition') {
                const validHandles = ['output-true', 'output-false'];
                const isValidHandle = !sourceHandle || validHandles.includes(sourceHandle);

                if (!isValidHandle) {
                    return {
                        isValid: false,
                        message: 'Invalid output handle for Condition node'
                    };
                }
            }

            // JSON Condition node handle validation
            if (sourceType === 'jsonCondition') {
                const validHandles = ['output-match', 'output-fallback'];
                const isValidHandle = !sourceHandle || validHandles.includes(sourceHandle);

                if (!isValidHandle) {
                    return {
                        isValid: false,
                        message: 'Invalid output handle for JSON Condition node'
                    };
                }

                // Validate target types for JSON Condition outputs
                const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'intent', 'smartIntent', 'response', 'apiTrigger', 'contactAgent', 'contactAgentV2', 'email', 'orchestrator', 'flowOutput', 'queryRewriter', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'serviceFallback', 'llmEnrich', 'jsonCondition', 'delay', 'switch', 'condition', 'apiRequest', 'serviceMatcher'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'JSON Condition node can only connect to AI Model, Intent, Response, API Trigger, Contact Agent, Email, Service Fallback, Orchestrator or Flow Output nodes'
                    };
                }
            }

            // Service Fallback node handle validation
            if (sourceType === 'serviceFallback') {
                const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'intent', 'smartIntent', 'response', 'apiTrigger', 'contactAgent', 'contactAgentV2', 'email', 'orchestrator', 'flowOutput', 'queryRewriter', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'serviceFallback', 'llmEnrich', 'jsonCondition', 'delay', 'switch', 'condition', 'apiRequest', 'serviceMatcher'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'Service Fallback node can only connect to AI Model, Intent, Response, API Trigger, Contact Agent, Email, JSON Condition, Orchestrator or Flow Output nodes'
                    };
                }
            }

            // Service Matcher node handle validation (standalone: same downstream targets as Service Fallback)
            if (sourceType === 'serviceMatcher') {
                // 'output' is the legacy single handle (Enable Favorite Lookup = OFF);
                // the three branch handles appear when the toggle is ON.
                const validHandles = ['output', 'output-favorite', 'output-service', 'output-nomatch'];
                const isValidHandle = !sourceHandle || validHandles.includes(sourceHandle);

                if (!isValidHandle) {
                    return {
                        isValid: false,
                        message: 'Invalid output handle for Service Matcher node'
                    };
                }

                const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'intent', 'smartIntent', 'response', 'apiTrigger', 'contactAgent', 'contactAgentV2', 'email', 'orchestrator', 'flowOutput', 'queryRewriter', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'serviceFallback', 'llmEnrich', 'jsonCondition', 'delay', 'switch', 'condition', 'apiRequest', 'serviceMatcher'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'Service Matcher node can only connect to Service Fallback, AI Model, Intent, Response, JSON Condition, Orchestrator or Flow Output nodes'
                    };
                }
            }

            // LLM Enrich node handle validation (standalone flow-through, mirror Service Fallback)
            if (sourceType === 'llmEnrich') {
                const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'intent', 'smartIntent', 'response', 'apiTrigger', 'contactAgent', 'contactAgentV2', 'email', 'orchestrator', 'flowOutput', 'queryRewriter', 'interestOptimizer', 'loanInterest', 'interestOptimizerV2', 'loanInterestV2', 'serviceFallback', 'jsonCondition', 'delay', 'switch', 'condition', 'apiRequest', 'serviceMatcher', 'llmEnrich'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'LLM Enrich node can only connect to standard processing or response nodes'
                    };
                }
            }

            // Documents/Knowledge nodes should only connect to AI models, agents, or processing nodes
            if (['documents', 'dynamicKnowledge', 'databaseIntegration', 'mutualFund', 'products', 'apiknowledge', 'sharepoint'].includes(sourceType)) {
                const allowedTargets = ['model', 'taxPlanning', 'taxPlanningV2', 'agent'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: `${this.getNodeTypeLabel(sourceType)} can only connect to AI Model or Agent nodes`
                    };
                }
            }

            // Meta Documents as source — its own branch (not folded into the list above) because its
            // backend consumers are ONLY model and agent; taxPlanning/V2 do not read it.
            if (sourceType === 'metaDocuments') {
                const allowedTargets = ['model', 'agent'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: `${this.getNodeTypeLabel(sourceType)} can only connect to AI Model or Agent nodes`
                    };
                }
            }

            // Orchestrator node handle validation
            if (sourceType === 'orchestrator') {
                // agents-bottom handle can only connect to agent nodes.
                // 'taxPlanningAgent' is an agent KIND, not a different concept: same handle, same selection
                // prompt, same AgentExecutionAsync contract on the server (OrchestratorNodeProcessor.IsAgentNodeType).
                // 'contactAgentV2' hangs off the SAME handle DIRECTLY — no 'agent' node in between. It is not a
                // persona-with-tools, it is a terminal action the Orchestrator may choose ("hand this customer to
                // a human"), so it is offered to selection like any other agent and, when picked, runs its own
                // ProcessAsync (see OrchestratorNodeProcessor.RunContactAgentV2Async).
                // 🔴 'contactAgent' (V1) is deliberately NOT on this list — V1 is not an agent kind and stays an
                //    ordinary flow step only (rollback 2026-09-15). Use Contact Agent V2 under an Orchestrator.
                if (sourceHandle === 'agents-bottom') {
                    const allowedTargets = ['agent', 'taxPlanningAgent', 'contactAgentV2'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Orchestrator agents handle can only connect to Agent or Contact Agent V2 nodes'
                        };
                    }
                    // The other half of the Contact Agent V2 rule (see the block at the top of this method): an
                    // Orchestrator belongs on the TOP handle. Dropped on the left one it would read as an
                    // ordinary flow step — a completely different thing from "one of my agents".
                    if (targetType === 'contactAgentV2' && targetHandle !== CONTACT_AGENT_V2_TOP_HANDLE) {
                        return {
                            isValid: false,
                            message: 'Cannot connect here: an Orchestrator must connect to the TOP handle of a Contact Agent V2 node.'
                        };
                    }
                }
                // output handle (right side) can only connect to specific nodes
                else if (sourceHandle === 'output') {
                    const allowedTargets = ['intent', 'smartIntent', 'router', 'advancedRouter', 'model', 'taxPlanning', 'taxPlanningV2', 'loanInterest', 'interestOptimizer', 'interestOptimizerV2', 'loanInterestV2', 'contactAgent', 'contactAgentV2', 'apiTrigger', 'email', 'response', 'orchestrator', 'flowOutput', 'jsonCondition', 'serviceFallback', 'llmEnrich', 'apiRequest', 'serviceMatcher'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Orchestrator output can only connect to AI Model, Intent, Smart Intent, Router, TTS, Response, Email, API Trigger, Contact Agent, Interest nodes or Orchestrator'
                        };
                    }
                }
            }

            // Button Style node: a PLUGIN that hangs off an Agent's tools-bottom handle (like Meta Documents).
            // It restyles the |productbutton| / |servicebutton| targets in that agent's reply and has no output
            // of its own, so dragging from it may only land on an Agent.
            if (sourceType === 'buttonStyle') {
                const allowedTargets = ['agent'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'Button Style is a plugin — connect it under an Agent (tools handle)'
                    };
                }
            }

            // TTS node validations - can connect to response, model, or orchestrator
            if (sourceType === 'reformatTTS') {
                // Voice handle can connect to ssmlFormat, aiVoice, response (2-hop no-SSML), or orchestrator
                if (sourceHandle === 'Voice') {
                    const allowedTargets = ['ssmlFormat', 'aiVoice', 'response', 'orchestrator'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Reformat TTS Voice output can only connect to SSML Format, AI Voice, Response or Orchestrator nodes'
                        };
                    }
                }
                // Text handle can connect to response or orchestrator
                else if (sourceHandle === 'Text') {
                    const allowedTargets = ['response', 'orchestrator', 'model', 'taxPlanning', 'taxPlanningV2'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Reformat TTS Text output can only connect to Response, AI Model or Orchestrator nodes'
                        };
                    }
                }
            }

            if (sourceType === 'ssmlFormat') {
                const allowedTargets = ['aiVoice', 'response', 'orchestrator'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'SSML Format node can only connect to AI Voice, Response or Orchestrator nodes'
                    };
                }
            }

            if (sourceType === 'aiVoice') {
                const allowedTargets = ['response', 'orchestrator'];
                if (!allowedTargets.includes(targetType)) {
                    return {
                        isValid: false,
                        message: 'AI Voice node can only connect to Response or Orchestrator nodes'
                    };
                }
            }

            // Agent node handle validation
            if (sourceType === 'agent') {
                // Bottom handle for tools and knowledge sources
                if (sourceHandle === 'tools-bottom') {
                    // 'buttonStyle' is not knowledge or a tool: it post-processes the agent's reply
                    // (|productbutton| / |servicebutton| targets → style JSON) and hangs off the same handle.
                    const allowedTargets = ['tool', 'documents', 'dynamicKnowledge', 'databaseIntegration', 'mutualFund', 'apiknowledge', 'products', 'sharepoint', 'flowTool', 'taxPlanning', 'taxPlanningV2', 'metaDocuments', 'buttonStyle'];
                    if (!allowedTargets.includes(targetType)) {
                        return {
                            isValid: false,
                            message: 'Agent tools handle can only connect to Tool nodes, Knowledge source nodes, Flow Tool nodes or Button Style'
                        };
                    }
                }
            }

            // Tool node validation
            if (sourceType === 'tool') {
                return {
                    isValid: false,
                    message: 'Tool nodes cannot send connections. They can only receive connections from Agent nodes'
                };
            }

            // FlowTool node validation
            if (sourceType === 'flowTool') {
                return {
                    isValid: false,
                    message: 'Flow Tool nodes cannot send connections. They can only receive connections from Agent nodes'
                };
            }

            return { isValid: true };
        },

        // Check for circular references
        checkCircularReference(connection) {
            // Create a temporary edge list with the new connection
            const tempEdges = [...this.tempEdge, {
                source: connection.source,
                target: connection.target
            }];

            // Use DFS to detect cycles
            const visited = new Set();
            const recursionStack = new Set();

            const hasCycle = (nodeId) => {
                if (recursionStack.has(nodeId)) {
                    return true; // Cycle detected
                }
                if (visited.has(nodeId)) {
                    return false; // Already processed
                }

                visited.add(nodeId);
                recursionStack.add(nodeId);

                // Find all outgoing edges from this node
                const outgoingEdges = tempEdges.filter(edge => edge.source === nodeId);

                for (const edge of outgoingEdges) {
                    if (hasCycle(edge.target)) {
                        return true;
                    }
                }

                recursionStack.delete(nodeId);
                return false;
            };

            // Check for cycles starting from any node
            for (const node of this.tempNode) {
                if (!visited.has(node.id) && hasCycle(node.id)) {
                    return {
                        isValid: false,
                        message: 'This connection would create a circular reference in the flow'
                    };
                }
            }

            return { isValid: true };
        },
        // Validate input connection limits
        validateInputLimit(sourceNode, targetNode, connection) {

            const targetType = targetNode.data.type;

            // Count existing input connections to target node.
            // 🔴 Contact Agent V2 has TWO input handles — 'input' on the left for the ordinary flow, the top one
            //    for an Orchestrator — but it still takes ONE input (it is in singleInputNodes below). Counting
            //    only 'input' would let a Router edge sit on its left and an Orchestrator edge on its top at the
            //    same time: two different flows owning one node, and at runtime the Orchestrator would run it as
            //    an agent while the main path also walked into it.
            const inputHandleIds = targetType === 'contactAgentV2'
                ? ['input', CONTACT_AGENT_V2_TOP_HANDLE]
                : ['input'];
            const existingInputs = this.tempEdge.filter(edge =>
                edge.target === connection.target && inputHandleIds.includes(edge.targetHandle)
            ).length;

            if (targetType === 'documents' || targetType === 'products') {
                const existingModelBottomInputs = this.tempEdge.filter(edge => edge.source == sourceNode.id
                    && edge.targetNode.data.type === targetType
                    && edge.sourceHandle == 'output-bottom').length;

                // If there is already one connection from an AI Model output-bottom handle to documents node
                if (existingModelBottomInputs >= 1) {
                    return {
                        isValid: false,
                        message: `AI Model can only connect to one ${targetType === 'documents' ? 'Documents' : 'Products'} node from output-bottom handle`
                    };
                }
            }

            // Most nodes should only have one input connection.
            // 'apiRequest', 'serviceFallback' and 'serviceMatcher' are intentionally NOT listed:
            // each can be reached from multiple upstream branches (routing alternatives, not
            // concurrent inputs), so they accept multiple incoming connections. A Service Matcher
            // configured for one service type is typically shared by several router outcomes
            // (e.g. both a "transfer" and a "favorite transfer" intent land on the same matcher).
            // 'favoriteMatcher' stays limited on purpose: it is a plugin node attached under a
            // single apiRequest parent through the reserved 'output-bottom' handle.
            // 'documents' is intentionally NOT listed: like 'apiknowledge' it is a reusable
            // knowledge plugin — one Documents node may be attached to multiple parent nodes
            // (AI Model / Agent / etc.). Each parent gathers it independently from its
            // output-bottom at runtime, so multiple incoming connections are shared reuse,
            // not concurrent inputs.
            // 'llmEnrich' is intentionally NOT listed: same reasoning as apiRequest /
            // Service Fallback — it reads a single shared "userMessage" context variable
            // regardless of which upstream branch reached it, so multiple incoming edges
            // are routing alternatives, not concurrent inputs.
            const singleInputNodes = [
                'delay', 'apiTrigger', 'contactAgent', 'contactAgentV2',
                'guardrailAI', 'intentAI', 'databaseIntegration', 'products', 'favoriteMatcher'
            ];

            if (singleInputNodes.includes(targetType) && existingInputs >= 1) {
                return {
                    isValid: false,
                    message: `${this.getNodeTypeLabel(targetType)} node can only have one input connection`
                };
            }

            // Special cases for nodes that can have multiple inputs
            if (targetType === 'switch' && existingInputs >= 3) {
                return {
                    isValid: false,
                    message: 'Switch node can have maximum 3 input connections'
                };
            }

            if (targetType === 'condition' && existingInputs >= 2) {
                return {
                    isValid: false,
                    message: 'Condition node can have maximum 2 input connections'
                };
            }

            if (targetType === 'jsonCondition' && existingInputs >= 1) {
                return {
                    isValid: false,
                    message: 'JSON Condition node can have maximum 1 input connection'
                };
            }

            // Service Fallback can receive inputs from multiple upstream nodes.
            // The flow engine is a single-path traversal: whichever branch reaches this
            // node supplies the "userMessage" it consumes, so multiple incoming edges
            // are routing alternatives, not concurrent inputs — no limit needed.

            return { isValid: true };
        },
        // Validate output connection limits
        validateOutputLimit(sourceNode, targetNode, connection) {
            const sourceType = sourceNode.data.type;
            const sourceHandle = connection.sourceHandle;

            // Count existing output connections from source node
            const existingOutputs = this.tempEdge.filter(edge =>
                edge.source === connection.source
            ).length;

            // messageInput node can only have one output connection
            if (sourceType === 'messageInput' && existingOutputs >= 1) {
                return {
                    isValid: false,
                    message: 'Message Input node can only have one output connection'
                };
            }

            // API Request node - allow its main output handle to connect to multiple downstream nodes
            if (sourceType === 'apiRequest' && sourceHandle === 'output') {
                return { isValid: true };
            }

            // Intent node - limit each handle to one connection
            if (sourceType === 'intent') {
                // Check fallback handle
                if (sourceHandle === 'intent-fallback') {
                    const existingFallbackOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === 'intent-fallback'
                    ).length;

                    if (existingFallbackOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Intent fallback can only have one output connection'
                        };
                    }
                }
                // Check main action handles (format: {intent.id}-main)
                else if (sourceHandle && sourceHandle.endsWith('-main')) {
                    const existingMainOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    if (existingMainOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Each intent action can only have one output connection'
                        };
                    }
                }
                // Check parallel action handles (format: {intent.id}-{parallel.id})
                else if (sourceHandle && sourceHandle !== 'output-bottom' && sourceHandle !== 'input') {
                    const existingParallelOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    if (existingParallelOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Each intent action can only have one output connection'
                        };
                    }
                }
                else if (sourceHandle && sourceHandle == 'output-bottom') {
                    const existingBottomOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    if (existingBottomOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Intent can only have one Intent AI connection'
                        };
                    }
                }
            }
            if (sourceType === 'smartIntent') {
                // Check fallback handle
                if (sourceHandle === 'smart-intent-fallback') {
                    const existingFallbackOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === 'smart-intent-fallback'
                    ).length;

                    if (existingFallbackOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Smart Intent fallback can only have one output connection'
                        };
                    }
                }
                // Check main action handles (format: {intent.id}-main)
                else if (sourceHandle && sourceHandle.endsWith('main')) {
                    const existingMainOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    if (existingMainOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Each intent action can only have one output connection'
                        };
                    }
                }
                else if (sourceHandle && sourceHandle == 'output-bottom') {
                    const existingBottomOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    if (existingBottomOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Smart intent can only have one Intent AI connection'
                        };
                    }
                }
            }

            // Router node - limit each handle to one connection
            if (sourceType === 'router') {
                // Check fallback handle
                if (sourceHandle === 'router-fallback') {
                    const existingFallbackOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === 'router-fallback'
                    ).length;

                    if (existingFallbackOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Router fallback can only have one output connection'
                        };
                    }
                }
                // Check router output handles (format: router-{output.id})
                else if (sourceHandle && sourceHandle.startsWith('router-') && sourceHandle !== 'router-fallback') {
                    const existingRouterOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    if (existingRouterOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Each router output can only have one connection'
                        };
                    }
                }
                else if (sourceHandle && sourceHandle == 'output-bottom') {
                    const existingBottomOutputs = this.tempEdge.filter(edge =>
                        edge.source === connection.source && edge.sourceHandle === sourceHandle
                    ).length;

                    if (existingBottomOutputs >= 1) {
                        return {
                            isValid: false,
                            message: 'Router can only have one Intent AI connection'
                        };
                    }
                }
            }

            // General rule for all other source handles on the right side (Position.Right)
            // This covers: model, api, condition, switch, delay, webhook, email, apiTrigger, contactAgent, etc.
            if (sourceHandle && sourceHandle !== 'output-bottom' && sourceHandle !== 'input') {
                const existingHandleOutputs = this.tempEdge.filter(edge =>
                    edge.source === connection.source && edge.sourceHandle === sourceHandle
                ).length;

                // Check specific handle types that should be limited to one connection
                const limitedHandles = [
                    'output', // Standard output handle for most nodes
                    'output-success', // Success output for some nodes
                    'output-true', // Condition true output
                    'output-false', // Condition false output
                    'switch-default', // Switch default output
                    'output-match', // JSON Condition match output
                    'output-fallback', // JSON Condition fallback output
                    'output-favorite', // Service Matcher favorite-match branch
                    'output-service', // Service Matcher service-match branch
                    'output-nomatch' // Service Matcher no-match branch
                ];

                // Check if it's a switch output handle or other limited handles
                const isSwitchOutput = sourceHandle.startsWith('switch-output-');
                const isLimitedHandle = limitedHandles.includes(sourceHandle);

                if ((isSwitchOutput || isLimitedHandle) && existingHandleOutputs >= 1) {
                    let nodeName = this.getNodeTypeLabel(sourceType);
                    let handleName = sourceHandle;
                    
                    if (isSwitchOutput) {
                        handleName = 'switch output';
                    } else if (sourceHandle === 'output-true') {
                        handleName = 'true output';
                    } else if (sourceHandle === 'output-false') {
                        handleName = 'false output';
                    } else if (sourceHandle === 'switch-default') {
                        handleName = 'default output';
                    } else if (sourceHandle === 'output-match') {
                        handleName = 'match output';
                    } else if (sourceHandle === 'output-fallback') {
                        handleName = 'fallback output';
                    } else if (sourceHandle === 'output-favorite') {
                        handleName = 'favorite output';
                    } else if (sourceHandle === 'output-service') {
                        handleName = 'service output';
                    } else if (sourceHandle === 'output-nomatch') {
                        handleName = 'no match output';
                    } else if (sourceHandle === 'output' || sourceHandle === 'output-success') {
                        handleName = 'output';
                    }

                    return {
                        isValid: false,
                        message: `${nodeName} ${handleName} can only have one output connection`
                    };
                }
            }

            return { isValid: true };
        },
        // Helper function to apply default values to a node based on its type
        applyNodeDefaults(node) {
            const defaultBasePrompt = `Find the Intent from the message of users.
#Condition for Response
1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0`;

            // Apply comprehensive defaults based on openNodeInspector logic
            node.data = {
                ...node.data,
                // Common properties
                color: node.data.color || '#1976D2',
                placeholderText: node.data.placeholderText || '',
                required: node.data.required || false,
                selectedModel: Array.isArray(node.data.selectedModel)
                    ? node.data.selectedModel
                    : (node.data.selectedModel ? [node.data.selectedModel] : []),
                systemPrompt: node.data.systemPrompt || '',
                temperature: node.data.temperature || 0,
                maxTokens: node.data.maxTokens || null,
                
                // AI Model Configuration properties
                provider: node.data.provider || null,
                model: node.data.model || null,
                useBaseModelPrompt: node.data.useBaseModelPrompt !== undefined ? node.data.useBaseModelPrompt : false,
                gender: node.data.gender || 'male',
                typingSpeed: node.data.typingSpeed || null,
                maxInputLength: node.data.maxInputLength || 200000,
                streamResponse: node.data.streamResponse !== undefined ? node.data.streamResponse : false,
                jsonResponse: node.data.jsonResponse !== undefined ? node.data.jsonResponse : false,
                bypassOnError: node.data.bypassOnError !== undefined ? node.data.bypassOnError : false,
                enableTime: node.data.enableTime || null,
                timeZone: node.data.timeZone || null,
                memoryTurn: node.data.memoryTurn || null,
                memoryDuration: node.data.memoryDuration || null,
                shortTermMemory: node.data.shortTermMemory || null,
                longTermMemory: node.data.longTermMemory !== undefined ? node.data.longTermMemory : false,
                isChainMassage: node.data.isChainMassage !== undefined ? node.data.isChainMassage : false,
                lenChainMassage: node.data.lenChainMassage || null,
                timeSecondChainMassage: node.data.timeSecondChainMassage || null,
                topP: node.data.topP || null,
                frequencyPenalty: node.data.frequencyPenalty || null,
                presencePenalty: node.data.presencePenalty || null,
                stopSequence: node.data.stopSequence || null,
                loadingBar: node.data.loadingBar !== undefined ? node.data.loadingBar : false,
                loadingBarDuration: node.data.loadingBarDuration || 0,
                monitorLineUserId: node.data.monitorLineUserId || null,
                monitorSocialID: node.data.monitorSocialID || null,
                showLineName: node.data.showLineName !== undefined ? node.data.showLineName : false,
                forbiddenWords: node.data.forbiddenWords || [''],
                
                // Intent specific defaults
                intentGroupName: node.data.intentGroupName || '',
                examples: node.data.examples || '',
                
                // Intent AI properties with comprehensive defaults
                intentaiModel: Array.isArray(node.data.intentaiModel)
                    ? (node.data.intentaiModel.length > 0 ? node.data.intentaiModel[0] : null)
                    : (node.data.intentaiModel || (Array.isArray(node.data.intentModel) ? (node.data.intentModel.length > 0 ? node.data.intentModel[0] : null) : node.data.intentModel) || null),
                intentaiProvider: node.data.intentaiProvider || node.data.provider || '',
                intentaiGender: node.data.intentaiGender || node.data.gender || 'male',
                intentaiTypingSpeed: node.data.intentaiTypingSpeed || node.data.typingSpeed || 0,
                intentaiMaxInputLength: node.data.intentaiMaxInputLength || node.data.maxInputLength || 4000,
                intentaiStreamResponse: node.data.intentaiStreamResponse !== undefined ? node.data.intentaiStreamResponse : (node.data.streamResponse !== undefined ? node.data.streamResponse : true),
                intentaiJsonResponse: node.data.intentaiJsonResponse !== undefined ? node.data.intentaiJsonResponse : (node.data.jsonResponse !== undefined ? node.data.jsonResponse : true),
                intentaiEnableTime: node.data.intentaiEnableTime !== undefined ? node.data.intentaiEnableTime : (node.data.enableTime !== undefined ? node.data.enableTime : false),
                intentaiTimeZone: node.data.intentaiTimeZone || node.data.timeZone || '',
                intentaiMemoryTurn: node.data.intentaiMemoryTurn || node.data.memoryTurn || null,
                intentaiMemoryDuration: node.data.intentaiMemoryDuration || node.data.memoryDuration || 60,
                intentaiShortTermMemory: node.data.intentaiShortTermMemory || node.data.shortTermMemory || 3,
                intentaiLongTermMemory: node.data.intentaiLongTermMemory !== undefined ? node.data.intentaiLongTermMemory : (node.data.longTermMemory !== undefined ? node.data.longTermMemory : false),
                intentaiIsChainMassage: node.data.intentaiIsChainMassage !== undefined ? node.data.intentaiIsChainMassage : (node.data.isChainMassage !== undefined ? node.data.isChainMassage : false),
                intentaiLenChainMassage: node.data.intentaiLenChainMassage || node.data.lenChainMassage || 5,
                intentaiTimeSecondChainMassage: node.data.intentaiTimeSecondChainMassage || node.data.timeSecondChainMassage || 30,
                intentaiTemperature: node.data.intentaiTemperature ?? node.data.temperature ?? 0,
                intentaiTopP: node.data.intentaiTopP || node.data.topP || 1.0,
                intentaiFrequencyPenalty: node.data.intentaiFrequencyPenalty || node.data.frequencyPenalty || 0,
                intentaiPresencePenalty: node.data.intentaiPresencePenalty || node.data.presencePenalty || 0,
                intentaiMaxTokens: node.data.intentaiMaxTokens || node.data.maxTokens || 1000,
                intentaiSystemPrompt: node.data.intentaiSystemPrompt || node.data.intentSystemPrompt || '',
                intentaiExamples: node.data.intentaiExamples || node.data.intentExamples || '',
                intentaiConfidence: node.data.intentaiConfidence || node.data.intentConfidence || 75,
                intentaiFallbackIntent: node.data.intentaiFallbackIntent || node.data.fallbackIntent || 'unknown',
                intentaiShowLineName: node.data.intentaiShowLineName !== undefined ? node.data.intentaiShowLineName : (node.data.showLineName !== undefined ? node.data.showLineName : false),
                intentaiLoadingBar: node.data.intentaiLoadingBar !== undefined ? node.data.intentaiLoadingBar : (node.data.loadingBar !== undefined ? node.data.loadingBar : false),
                intentaiLoadingBarDuration: node.data.intentaiLoadingBarDuration || node.data.loadingBarDuration || 0,
                intentaiMonitorLineUserId: node.data.intentaiMonitorLineUserId || node.data.monitorLineUserId || null,
                intentaiMonitorSocialID: node.data.intentaiMonitorSocialID || node.data.monitorSocialID || null,
                intentaiForbiddenWords: node.data.intentaiForbiddenWords || node.data.forbiddenWords || [''],
                
                // Guardrail AI properties
                guardrailType: node.data.guardrailType || 'content_safety',
                guardrailModel: node.data.guardrailModel || 'gpt-4',
                guardrailPrompt: node.data.guardrailPrompt || '',
                allowAgentGuardrailBypass: node.data.allowAgentGuardrailBypass ?? true,
                safetyThreshold: node.data.safetyThreshold || 80,
                enableLogging: node.data.enableLogging !== undefined ? node.data.enableLogging : true,
                
                // API Trigger properties
                apiUrl: node.data.apiUrl || '',
                httpMethod: node.data.httpMethod || 'POST',
                headers: node.data.headers || '',
                textResponse: node.data.textResponse || '',
                jsonValidate: node.data.jsonValidate || '{\n   "Name":"Value"\n}',
                modelJsonValidator: node.data.modelJsonValidator || null,
                listHeadersAPITrigger: node.data.listHeadersAPITrigger || [{ key: '', value: '' }],
                listValidation: node.data.listValidation || [{ key: '', typeValidation: '', numberOfCharacter: '', regExp: '', promptMessage: '' }],
                
                // Contact Agent properties
                departmentName: node.data.departmentName || '',
                contactAgentMessage: node.data.contactAgentMessage || '',
                contactAgentMessageChinese: node.data.contactAgentMessageChinese || '',
                contactAgentMessageEnglish: node.data.contactAgentMessageEnglish || '',
                handoffMessage: node.data.handoffMessage || '',
                botDisableTime: node.data.botDisableTime || null,
                disableBot: node.data.disableBot !== undefined ? node.data.disableBot : true,
                
                // Switch properties
                switchName: node.data.switchName || '',
                
                // Condition properties
                conditionType: node.data.conditionType || 'value_comparison',
                leftOperand: node.data.leftOperand || '',
                operator: node.data.operator || 'equals',
                rightOperand: node.data.rightOperand || '',
                conditionExpression: node.data.conditionExpression || '',
                trueLabel: node.data.trueLabel || 'True',
                falseLabel: node.data.falseLabel || 'False',
                minValue: node.data.minValue || 0,
                maxValue: node.data.maxValue || 100,
                expectedValue: node.data.expectedValue !== undefined ? node.data.expectedValue : true,
                
                // JSON Condition properties
                jsonConditionName: node.data.jsonConditionName || 'Rule-Based',
                matchMode: node.data.matchMode || 'all',
                jsonConditions: node.data.type === 'jsonCondition'
                    ? (node.data.conditions && Array.isArray(node.data.conditions) && node.data.conditions.length > 0
                        ? JSON.parse(JSON.stringify(node.data.conditions))
                        : [{ id: `jc-${this.generateGuid()}`, jsonPath: '', operator: '', compareValue: '' }])
                    : [],
                matchLabel: node.data.matchLabel || 'Match',
                fallbackLabel: node.data.fallbackLabel || 'Fallback',

                // Fallback Return properties
                serviceType: node.data.serviceType || 'TRANSFER',
                serviceFallbackMessageTH: node.data.messageTH || 'ไม่พบรายชื่อนี้ในระบบ Genie กำลังพาไปหน้าทำรายการ กรุณาเลือกทำรายการด้วยตนเอง',
                serviceFallbackMessageEN: node.data.messageEN || "I can't find this name in the system. I'm taking you to the transaction screen—please select the transaction manually.",
                serviceFallbackMessageCN: node.data.messageCN || '系統中找不到此名稱。我正在帶您前往交易畫面，請您自行選擇交易項目。',
                serviceFallbackAllowEditOutput: node.data.allowEditOutput || false,
                serviceFallbackOutputJson: node.data.outputJson || JSON.stringify({
                    message: '{{auto_message}}',
                    action: 'callback',
                    navlist: [{
                        serviceType: node.data.serviceType || 'TRANSFER',
                        toType: 'N',
                        toValue: null,
                        accountNumber: null,
                        amount: 0
                    }]
                }, null, 2),

                // Delay properties
                delayType: node.data.delayType || 'fixed',
                delayValue: node.data.delayValue || 5,
                delayUnit: node.data.delayUnit || 'seconds',
                randomMin: node.data.randomMin || 1,
                randomMax: node.data.randomMax || 10,
                delayMessage: node.data.delayMessage || '',
                showProgress: node.data.showProgress !== undefined ? node.data.showProgress : true,
                delayExpression: node.data.delayExpression || '',
                
                // Documents properties
                documentSourceType: node.data.documentSourceType || 'doc', // 'doc' | 'group'
                documentGroupId: node.data.documentGroupId || null,
                documentGroupName: node.data.documentGroupName || '',
                document: node.data.document || [],
                selectedDocumentData: node.data.selectedDocumentData || [],
                enableSpecificDataSelection: node.data.enableSpecificDataSelection !== undefined ? node.data.enableSpecificDataSelection : false,
                documentVectorName: node.data.documentVectorName || '',
                textReplacement: node.data.textReplacement || [],
                topNChunks: node.data.topNChunks || 5,
                cosineSimilarity: node.data.cosineSimilarity || 0.7,
                // Summary Memory (Embedding) properties
                summaryMemoryEnabled: node.data.summaryMemoryEnabled ?? false,
                summaryMemoryProvider: node.data.summaryMemoryProvider || null,
                summaryMemoryModel: node.data.summaryMemoryModel || null,
                summaryMemoryApiVersion: node.data.summaryMemoryApiVersion || null,
                summaryMemoryJsonRequest: node.data.summaryMemoryJsonRequest || null,
                summaryMemorySystemPrompt: node.data.summaryMemorySystemPrompt || '',
                summaryMemoryTemperature: node.data.summaryMemoryTemperature ?? 0,
                summaryMemoryMaxTokens: node.data.summaryMemoryMaxTokens ?? 500,
                summaryMemoryTopP: node.data.summaryMemoryTopP ?? 1,
                summaryMemoryFrequencyPenalty: node.data.summaryMemoryFrequencyPenalty ?? null,
                summaryMemoryPresencePenalty: node.data.summaryMemoryPresencePenalty ?? null,
                summaryMemoryMemoryTurn: node.data.summaryMemoryMemoryTurn ?? 0,
                summaryMemoryMemoryDuration: node.data.summaryMemoryMemoryDuration ?? 60,
                summaryMemoryTimeout: node.data.summaryMemoryTimeout ?? 0,
                // Community Graph RAG properties
                communityId: node.data.communityId || null,
                communityTopGroups: node.data.communityTopGroups || 3,
                communityTopChunks: node.data.communityTopChunks || 5,
                communitySearchMethod: node.data.communitySearchMethod || 'topn',
                communityMinCosineScore: node.data.communityMinCosineScore || 0.7,
                // Meta Community Graph RAG properties
                metaCommunityId: node.data.metaCommunityId || null,
                metaCommunityTopGroups: node.data.metaCommunityTopGroups || 3,
                metaCommunityTopChunks: node.data.metaCommunityTopChunks || 5,
                metaCommunitySearchMethod: node.data.metaCommunitySearchMethod || 'topn',
                metaCommunityMinCosineScore: node.data.metaCommunityMinCosineScore || 0.7,
                // Topic/SubTopic Filter properties
                filterTopic: node.data.filterTopic || null,
                filterSubTopic: node.data.filterSubTopic || null,
                filterMode: node.data.filterMode || 'include',

                // Database Integration properties
                tableName: node.data.tableName || null,
                sqlExtractionModel: node.data.sqlExtractionModel || null,
                promptSqlNoData: node.data.promptSqlNoData || '',

                // Products properties
                productSourceType: node.data.productSourceType || 'product', // 'product' | 'group'
                productGroupCollectionId: node.data.productGroupCollectionId || null,
                productGroupCollectionName: node.data.productGroupCollectionName || '',
                productGroupList: node.data.productGroupList || [],
                productTopNChunks: node.data.productTopNChunks || 5,
                productCosineSimilarity: node.data.productCosineSimilarity || 0.7,

                // Knowledge API properties
                knowledgeApiUrl: node.data.knowledgeApiUrl || '',
                knowledgeHttpMethod: node.data.knowledgeHttpMethod || 'GET',
                listHeaders: node.data.listHeaders || [{ key: '', value: '' }],
                knowledgeRequestBody: node.data.knowledgeRequestBody || '',
                responseDataPath: node.data.responseDataPath || '',
                // API Request properties
                apiRequestUrl: node.data.apiRequestUrl || '',
                apiRequestMethod: node.data.apiRequestMethod || 'GET',
                apiRequestHeaders: node.data.apiRequestHeaders || [{ key: '', value: '' }],
                apiRequestBody: node.data.apiRequestBody || '',
                apiRequestResponsePath: node.data.apiRequestResponsePath || '',
                useResultAsMessage: node.data.useResultAsMessage || false,
                // Request timeout in seconds (apiRequest + serviceMatcher favorite lookup)
                timeout: node.data.timeout ?? 30,
                // Favorite Matcher properties (serviceType shared with Service Fallback above)
                // Service Matcher defaults this to '' (use the node's own API result);
                // Favorite Matcher keeps its legacy 'apiRequestResponse' default.
                favoritesSourceVariable: node.data.type === 'serviceMatcher'
                    ? (node.data.favoritesSourceVariable || '')
                    : (node.data.favoritesSourceVariable || 'apiRequestResponse'),
                outputVariable: node.data.outputVariable || 'userMessage',
                enableEmbedding: node.data.enableEmbedding ?? true,
                fuzzyThreshold: node.data.fuzzyThreshold ?? 0.85,
                embeddingThreshold: node.data.embeddingThreshold ?? 0.65,
                // Service Matcher properties (serviceType/outputVariable/embedding shared above)
                inputVariable: node.data.inputVariable || 'userMessage',
                activeOnly: node.data.activeOnly ?? true,
                dataIntegrationId: node.data.dataIntegrationId || null,
                maxResults: node.data.maxResults ?? 0,

                // Service Matcher — unified Favorite Lookup (API Request + Favorite Matcher + rules)
                enableFavoriteLookup: node.data.enableFavoriteLookup ?? false,
                matchStrategy: node.data.matchStrategy || 'favorite-first',
                favoriteEnableEmbedding: node.data.favoriteEnableEmbedding ?? true,
                favoriteKeywordFunnel: node.data.favoriteKeywordFunnel ?? true,
                favoriteFuzzyThreshold: node.data.favoriteFuzzyThreshold ?? 0.85,
                favoriteEmbeddingThreshold: node.data.favoriteEmbeddingThreshold ?? 0.65,
                favoriteMaxResults: node.data.favoriteMaxResults ?? 0,
                favoriteMatchMode: node.data.favoriteMatchMode || 'all',
                serviceMatchMode: node.data.serviceMatchMode || 'all',
                favoriteConditions: node.data.type === 'serviceMatcher'
                    ? this.cloneServiceMatcherRules(node.data.favoriteConditions)
                    : [],
                serviceConditions: node.data.type === 'serviceMatcher'
                    ? this.cloneServiceMatcherRules(node.data.serviceConditions)
                    : [],
                favoriteLabel: node.data.favoriteLabel || 'Favorite',
                serviceLabel: node.data.serviceLabel || 'Service',
                noMatchLabel: node.data.noMatchLabel || 'No Match',

                // SharePoint properties
                sharepoint: node.data.sharepoint || {
                    siteUrl: '',
                    tenantId: '',
                    clientId: '',
                    clientSecret: '',
                    folderPath: '',
                    filename: ''
                },
                
                // Email properties
                emailProvider: node.data.emailProvider || 'mailgun',
                toEmail: node.data.toEmail || '',
                ccEmails: node.data.ccEmails || [],
                bccEmails: node.data.bccEmails || [],
                subject: node.data.subject || '',
                emailTemplate: node.data.emailTemplate || 'custom',
                emailBody: node.data.emailBody || '',
                isHtml: node.data.isHtml !== undefined ? node.data.isHtml : false,
                
                // Interest Optimizer properties
                ioAnswerModel: node.data.ioAnswerModel || null,
                ioFallbackPrompt: node.data.ioFallbackPrompt || '',
                ioParserModel: node.data.ioParserModel || null,
                ioAccountCodes: node.data.ioAccountCodes || [],
                ioRequiredFields: node.data.ioRequiredFields || [],
                enableLanguageReplace: node.data.enableLanguageReplace || false,
                enableVoicePromptReplace: node.data.enableVoicePromptReplace || false,
                // Loan Interest properties
                liAnswerModel: node.data.liAnswerModel || null,
                liFallbackPrompt: node.data.liFallbackPrompt || '',
                liRequiredFields: node.data.liRequiredFields || [],
                liParserModel: node.data.liParserModel || null,
                liDocuments: node.data.liDocuments || [],
                liInterestRates: node.data.liInterestRates || []
            };

            // Type-specific defaults
            if (node.data.type === 'intent') {
                if (!node.data.intents || node.data.intents.length === 0) {
                    node.data.intents = [
                        {
                            id: `intent-${this.generateGuid()}`,
                            name: 'Intent Name',
                            priority: 0,
                            prompt: '',
                            basePrompt: defaultBasePrompt,
                            enableBasePrompt: false,
                            messages: [
                                { id: `msg-${this.generateGuid()}`, text: '' }
                            ],
                            mainAction: { id: `main-${this.generateGuid()}`, label: 'Main Action' },
                            parallelActions: []
                        }
                    ];
                }
                if (!node.data.actions || node.data.actions.length === 0) {
                    node.data.actions = [
                        {
                            id: `action-${this.generateGuid()}`,
                            label: 'Action 1',
                            parallels: [
                                { id: `parallel-${this.generateGuid()}`, label: 'Parallel Action 1' }
                            ]
                        }
                    ];
                }
                node.data.outputs = node.data.outputs || [];
            }

            // Tax Planning node defaults (LLM defaults applied via shared object above; tax keys here)
            if (node.data.type === 'taxPlanning') {
                node.data.taxPlanningYearMode = node.data.taxPlanningYearMode || 'active';
                node.data.taxPlanningYearId = node.data.taxPlanningYearId ?? null;
                node.data.taxPlanningAskWhenMultipleActive = node.data.taxPlanningAskWhenMultipleActive ?? true;
                node.data.taxPlanningFlowMode = node.data.taxPlanningFlowMode || 'hybrid';
                node.data.taxPlanningPersist = node.data.taxPlanningPersist ?? true;
                node.data.taxPlanningCompletionMessage = node.data.taxPlanningCompletionMessage || '';
                node.data.taxPlanningName = node.data.taxPlanningName || '';
                node.data.taxPlanningUseQuestionModel = node.data.taxPlanningUseQuestionModel ?? false;
                node.data.taxPlanningQuestionProvider = node.data.taxPlanningQuestionProvider || null;
                node.data.taxPlanningQuestionModel = node.data.taxPlanningQuestionModel || null;
            }

            // Tax Planning V2 node defaults. Config moved to the versioned settings rows
            // (`tax.TaxPlanningSettingsV2`) — only the label and the pinned settings version
            // stay on the node. `null` version = use whichever version is active.
            if (node.data.type === 'taxPlanningV2') {
                node.data.taxPlanningV2Name = node.data.taxPlanningV2Name || '';
                node.data.taxPlanningV2SettingsVersionId = node.data.taxPlanningV2SettingsVersionId ?? null;
            }

            // LLM Enrich node defaults (LLM defaults applied via shared object above)
            if (node.data.type === 'llmEnrich') {
                node.data.provider = node.data.provider || 'Azure';
                node.data.temperature = node.data.temperature ?? 0;
                node.data.topP = node.data.topP ?? 1;
                node.data.jsonResponse = node.data.jsonResponse ?? false;
                node.data.bypassOnError = node.data.bypassOnError ?? false;
                node.data.llmEnrichName = node.data.llmEnrichName || '';
                node.data.llmEnrichConnectorText = node.data.llmEnrichConnectorText || '';
            }

            // Button Style node defaults — the style JSON is seeded so the admin can SEE and edit it on the node
            // (a blank field falls back to the server's built-in default, which is the same text).
            if (node.data.type === 'buttonStyle') {
                node.data.buttonStyleName = node.data.buttonStyleName || '';
                node.data.productButtonStyle = node.data.productButtonStyle || BUTTON_STYLE_DEFAULTS.productButtonStyle;
                node.data.serviceButtonStyle = node.data.serviceButtonStyle || BUTTON_STYLE_DEFAULTS.serviceButtonStyle;
            }

            if (node.data.type === 'smartIntent') {
                if (!node.data.smartIntents || node.data.smartIntents.length === 0) {
                    node.data.smartIntents = [
                        {
                            id: `smart-intent-${this.generateGuid()}`,
                            name: 'Smart Intent Name',
                            priority: 0,
                            prompt: '',
                            basePrompt: defaultBasePrompt,
                            enableBasePrompt: false,
                            useMemory: false,
                            messages: [
                                { id: `msg-${this.generateGuid()}`, text: '' }
                            ],
                            mainAction: {
                                id: `main-${this.generateGuid()}`,
                                label: 'Action',
                                useAI: true,
                                provider: null,
                                model: null,
                                gender: 'male',
                                typingSpeed: null,
                                maxInputLength: 200000,
                                streamResponse: false,
                                jsonResponse: false,
                                enableTime: null,
                                timeZone: null,
                                enableTransactionId: null,
                                // Prompt Replacement Flags
                                enableInputMessageReplace: false,
                                enableVoicePromptReplace: false,
                                enableLanguageReplace: false,
                                temperature: null,
                                topP: null,
                                frequencyPenalty: null,
                                presencePenalty: null,
                                maxTokens: null,
                                stopSequence: null,
                                nodeTimeout: null,
                                memoryTurn: null,
                                memoryDuration: null,
                                shortTermMemory: null,
                                longTermMemory: false,
                                isChainMassage: false,
                                lenChainMassage: null,
                                timeSecondChainMassage: null,
                                forbiddenWords: [''],
                                // Knowledge Configuration
                                knowledgeType: '',
                                // Documents properties
                                document: [],
                                topNChunks: 5,
                                cosineSimilarity: 0.7,
                                // Database Integration properties
                                tableName: null,
                                sqlExtractionModel: null,
                                promptSqlNoData: '',
                                // Products properties
                                productSourceType: 'product',
                                productGroupCollectionId: null,
                                productGroupCollectionName: '',
                                productGroupList: [],
                                productTopNChunks: 5,
                                productCosineSimilarity: 0.7,
                                // API Knowledge properties
                                knowledgeApiUrl: '',
                                knowledgeHttpMethod: 'GET',
                                knowledgeResponseDataPath: ''
                            }
                        }
                    ];
                }
                if (!node.data.smartIntentFallback) {
                    node.data.smartIntentFallback = {
                        useAI: false,
                        provider: null,
                        model: null,
                        gender: 'male',
                        typingSpeed: null,
                        maxInputLength: 200000,
                        streamResponse: false,
                        jsonResponse: false,
                        enableTime: null,
                        timeZone: null,
                        enableTransactionId: null,
                        // Prompt Replacement Flags
                        enableInputMessageReplace: false,
                        enableVoicePromptReplace: false,
                        enableLanguageReplace: false,
                        temperature: null,
                        topP: null,
                        frequencyPenalty: null,
                        presencePenalty: null,
                        maxTokens: null,
                        stopSequence: null,
                        memoryTurn: null,
                        memoryDuration: null,
                        shortTermMemory: null,
                        longTermMemory: false,
                        isChainMassage: false,
                        lenChainMassage: null,
                        timeSecondChainMassage: null,
                        forbiddenWords: [''],
                        nodeTimeout: null
                    };
                }
                node.data.smartIntentGroupName = node.data.smartIntentGroupName || '';
            }

            if (node.data.type === 'switch') {
                if (!node.data.switchOutputs || node.data.switchOutputs.length === 0) {
                    node.data.switchOutputs = [
                        {
                            id: `switch-output-${this.generateGuid()}`,
                            name: 'Output 1',
                            condition: ''
                        },
                        {
                            id: `switch-output-${this.generateGuid()}`,
                            name: 'Output 2',
                            condition: ''
                        }
                    ];
                }
                node.data.switchName = node.data.switchName || 'Multi-Path';
            }

            if (node.data.type === 'router') {
                if (!node.data.routerOutputs || node.data.routerOutputs.length === 0) {
                    node.data.routerOutputs = [
                        {
                            id: `router-output-${this.generateGuid()}`,
                            intentName: 'default',
                            label: 'Default Output'
                        }
                    ];
                }
                node.data.routerName = node.data.routerName || 'New Router';
                node.data.routerPrompt = node.data.routerPrompt || '';
                node.data.confidenceThreshold = node.data.confidenceThreshold ?? 0.5;
            }

            if (node.data.type === 'advancedRouter') {
                if (!node.data.advancedRouterOutputs || node.data.advancedRouterOutputs.length === 0) {
                    node.data.advancedRouterOutputs = [
                        {
                            id: `advrouter-output-${this.generateGuid()}`,
                            intentName: 'Category1',
                            label: 'Category 1',
                            subIntents: []
                        }
                    ];
                }
                node.data.advancedRouterName = node.data.advancedRouterName || 'Advanced Router';
                node.data.advancedRouterPrompt = node.data.advancedRouterPrompt || '';
                node.data.advancedRouterConfidenceThreshold = node.data.advancedRouterConfidenceThreshold ?? 0.5;
                node.data.advancedRouterMaxDepth = node.data.advancedRouterMaxDepth ?? 2;
                node.data.customSchemaEnabled = node.data.customSchemaEnabled ?? false;
                node.data.customSchemaTemplate = node.data.customSchemaTemplate || this.getDefaultAdvancedRouterSchemaTemplate();
                node.data.schemaFieldMapping = node.data.schemaFieldMapping || {
                    intentField: 'capability_intent',
                    confidenceField: 'confidence',
                    subIntentsField: 'intents'
                };
            }

            if (node.data.type === 'dynamicRouter') {
                if (!node.data.dynamicRouterOutputs || node.data.dynamicRouterOutputs.length === 0) {
                    node.data.dynamicRouterOutputs = [
                        {
                            id: `dynamicRouter-output-${this.generateGuid()}`,
                            intentName: 'Category1',
                            label: 'Category 1',
                            subIntents: []
                        }
                    ];
                }
                node.data.dynamicRouterName = node.data.dynamicRouterName || 'Dynamic Router';
                node.data.dynamicRouterPrompt = node.data.dynamicRouterPrompt || '';
                node.data.dynamicRouterConfidenceThreshold = node.data.dynamicRouterConfidenceThreshold ?? 0.5;
                dynamicRouterMaxDepth: node.data.dynamicRouterMaxDepth ?? 2;
            }

            // Dynamic Knowledge node defaults
            if (node.data.type === 'dynamicKnowledge') {
                // Migrate old property name
                if (node.data.dynamicKnowledgeUseMetaCommunityGraphSubCat !== undefined) {
                    node.data.useMetaCommunityGraphSubCat = node.data.dynamicKnowledgeUseMetaCommunityGraphSubCat;
                    delete node.data.dynamicKnowledgeUseMetaCommunityGraphSubCat;
                }
                node.data.useMetaCommunityGraphSubCat = node.data.useMetaCommunityGraphSubCat ?? false;
                delete node.data.dynamicKnowledgeUseCommunityGraphCat;
                delete node.data.dynamicKnowledgeUseCommunityGraphSubCat;
                delete node.data.communityId;
                delete node.data.communityTopGroups;
                delete node.data.communityTopChunks;
                delete node.data.communitySearchMethod;
                delete node.data.communityMinCosineScore;
                node.data.topNChunks = node.data.topNChunks || 5;
                node.data.metaCommunityId = node.data.metaCommunityId || null;
                node.data.metaCommunityTopGroups = node.data.metaCommunityTopGroups || 3;
                node.data.metaCommunityTopChunks = node.data.metaCommunityTopChunks || 5;
                node.data.metaCommunitySearchMethod = node.data.metaCommunitySearchMethod || 'topn';
                node.data.metaCommunityMinCosineScore = node.data.metaCommunityMinCosineScore || 0.7;
            }

            // Orchestrator node defaults
            if (node.data.type === 'orchestrator') {
                node.data.orchestratorName = node.data.orchestratorName || '';
                node.data.provider = node.data.provider || '';
                node.data.model = node.data.model || '';
                node.data.temperature = node.data.temperature ?? 0;
                node.data.maxTokens = node.data.maxTokens ?? null;
                node.data.topP = node.data.topP ?? 1;
                node.data.streamResponse = node.data.streamResponse ?? false;
                node.data.jsonResponse = node.data.jsonResponse ?? false;
                node.data.memoryTurn = node.data.memoryTurn ?? null;
                node.data.memoryDuration = node.data.memoryDuration ?? null;
                node.data.shortTermMemory = node.data.shortTermMemory ?? 200;
                node.data.longTermMemory = node.data.longTermMemory ?? false;
                node.data.systemPrompt = node.data.systemPrompt || ORCHESTRATOR_PROMPT_DEFAULTS.systemPrompt;
                node.data.guardrailPrompt = node.data.guardrailPrompt || '';
                node.data.allowAgentGuardrailBypass = node.data.allowAgentGuardrailBypass ?? true;
                node.data.enableMultiAgent = node.data.enableMultiAgent || false;
                node.data.multiAgentStrategy = node.data.multiAgentStrategy || 'pipeline';
                // Orchestrator - configurable prompts / messages (blank = built-in default)
                node.data.orchSingleSelectionTemplate = node.data.orchSingleSelectionTemplate || ORCHESTRATOR_PROMPT_DEFAULTS.orchSingleSelectionTemplate;
                node.data.orchPipelineSelectionTemplate = node.data.orchPipelineSelectionTemplate || ORCHESTRATOR_PROMPT_DEFAULTS.orchPipelineSelectionTemplate;
                node.data.orchVerifySelectionTemplate = node.data.orchVerifySelectionTemplate || ORCHESTRATOR_PROMPT_DEFAULTS.orchVerifySelectionTemplate;
                node.data.orchAgentListItemTemplate = node.data.orchAgentListItemTemplate || ORCHESTRATOR_PROMPT_DEFAULTS.orchAgentListItemTemplate;
                node.data.orchAgentNoToolsLabel = node.data.orchAgentNoToolsLabel || ORCHESTRATOR_PROMPT_DEFAULTS.orchAgentNoToolsLabel;
                node.data.orchConversationStateTemplate = node.data.orchConversationStateTemplate || ORCHESTRATOR_PROMPT_DEFAULTS.orchConversationStateTemplate;
                node.data.orchConversationStateFactTemplate = node.data.orchConversationStateFactTemplate || ORCHESTRATOR_PROMPT_DEFAULTS.orchConversationStateFactTemplate;
                node.data.orchCompanionNote = node.data.orchCompanionNote || ORCHESTRATOR_PROMPT_DEFAULTS.orchCompanionNote;
                node.data.orchSequentialContextTemplate = node.data.orchSequentialContextTemplate || ORCHESTRATOR_PROMPT_DEFAULTS.orchSequentialContextTemplate;
                node.data.orchCheckerGoalTemplate = node.data.orchCheckerGoalTemplate || ORCHESTRATOR_PROMPT_DEFAULTS.orchCheckerGoalTemplate;
                node.data.orchFallbackSubGoal = node.data.orchFallbackSubGoal || ORCHESTRATOR_PROMPT_DEFAULTS.orchFallbackSubGoal;
                node.data.orchAggregationTemplate = node.data.orchAggregationTemplate || ORCHESTRATOR_PROMPT_DEFAULTS.orchAggregationTemplate;
                node.data.orchStrategyHintParallel = node.data.orchStrategyHintParallel || ORCHESTRATOR_PROMPT_DEFAULTS.orchStrategyHintParallel;
                node.data.orchStrategyHintPipeline = node.data.orchStrategyHintPipeline || ORCHESTRATOR_PROMPT_DEFAULTS.orchStrategyHintPipeline;
                node.data.orchAggregationFallbackItemTemplate = node.data.orchAggregationFallbackItemTemplate || ORCHESTRATOR_PROMPT_DEFAULTS.orchAggregationFallbackItemTemplate;
                node.data.orchGuardrailFormatSuffix = node.data.orchGuardrailFormatSuffix || ORCHESTRATOR_PROMPT_DEFAULTS.orchGuardrailFormatSuffix;
                node.data.orchGuardrailUserTemplate = node.data.orchGuardrailUserTemplate || ORCHESTRATOR_PROMPT_DEFAULTS.orchGuardrailUserTemplate;
                node.data.orchDirectLlmPrompt = node.data.orchDirectLlmPrompt || ORCHESTRATOR_PROMPT_DEFAULTS.orchDirectLlmPrompt;
                node.data.orchBlockedMessage = node.data.orchBlockedMessage || ORCHESTRATOR_PROMPT_DEFAULTS.orchBlockedMessage;
                node.data.orchDirectLlmEmptyMessage = node.data.orchDirectLlmEmptyMessage || ORCHESTRATOR_PROMPT_DEFAULTS.orchDirectLlmEmptyMessage;
                node.data.orchDirectLlmErrorMessage = node.data.orchDirectLlmErrorMessage || ORCHESTRATOR_PROMPT_DEFAULTS.orchDirectLlmErrorMessage;
                node.data.orchNoAgentSucceededMessage = node.data.orchNoAgentSucceededMessage || ORCHESTRATOR_PROMPT_DEFAULTS.orchNoAgentSucceededMessage;
                // Extended fields
                node.data.gender = node.data.gender || 'male';
                node.data.typingSpeed = node.data.typingSpeed ?? null;
                node.data.maxInputLength = node.data.maxInputLength ?? 200000;
                node.data.frequencyPenalty = node.data.frequencyPenalty ?? null;
                node.data.presencePenalty = node.data.presencePenalty ?? null;
                node.data.stopSequence = node.data.stopSequence || null;
                node.data.enableCache = node.data.enableCache ?? false;
                node.data.cacheTTLMinutes = node.data.cacheTTLMinutes ?? 1440;
                node.data.cacheScope = node.data.cacheScope || 'flow';
                node.data.enableSemanticCache = node.data.enableSemanticCache ?? false;
                node.data.similarityThreshold = node.data.similarityThreshold ?? 0.95;
                node.data.showLineName = node.data.showLineName ?? false;
                node.data.loadingBar = node.data.loadingBar ?? false;
                node.data.loadingBarDuration = node.data.loadingBarDuration || 0;
                node.data.monitorLineUserId = node.data.monitorLineUserId || null;
                node.data.monitorSocialID = node.data.monitorSocialID || null;
                node.data.forbiddenWords = node.data.forbiddenWords || [''];
                node.data.enableTime = node.data.enableTime ?? null;
                node.data.timeZone = node.data.timeZone || null;
                node.data.enableTransactionId = node.data.enableTransactionId ?? null;
                node.data.enableInputMessageReplace = node.data.enableInputMessageReplace ?? false;
                node.data.enableVoicePromptReplace = node.data.enableVoicePromptReplace ?? false;
                node.data.enableLanguageReplace = node.data.enableLanguageReplace ?? false;
            }

            // Tax Planning Agent defaults — three keys, matching flowUtils' expand case.
            if (node.data.type === 'taxPlanningAgent') {
                node.data.agentName = node.data.agentName || '';
                node.data.agentDescription = node.data.agentDescription || '';
                node.data.bypassGuardrailWhenAlone = node.data.bypassGuardrailWhenAlone ?? true;
                node.data.taxKnowledgeEnabled = node.data.taxKnowledgeEnabled ?? false;
                node.data.taxKnowledgeSelections = Array.isArray(node.data.taxKnowledgeSelections)
                    ? node.data.taxKnowledgeSelections : [];
            }

            // Meta Documents node defaults — also runs on copy/paste across versions, so a node
            // pasted from an older flow still gets every key the save whitelist expects.
            if (node.data.type === 'metaDocuments') {
                node.data.metaDocSelections = Array.isArray(node.data.metaDocSelections)
                    ? node.data.metaDocSelections : [];
                node.data.topNChunks = node.data.topNChunks || 5;
                node.data.metaDocDescription = node.data.metaDocDescription || '';
            }

            // Agent node defaults
            if (node.data.type === 'agent') {
                node.data.agentName = node.data.agentName || '';
                node.data.agentDescription = node.data.agentDescription || '';
                node.data.personaPrompt = node.data.personaPrompt || '';
                // Final-summary prompt: seed the built-in TEXT so a new node opens with it in the field (blank = built-in
                // on the backend either way). Only runs for NEW nodes — older nodes show it as a placeholder instead.
                node.data.agentFinalSummaryTemplate = node.data.agentFinalSummaryTemplate || AGENT_PROMPT_DEFAULTS.finalSummaryTemplate;
                node.data.selectedTools = node.data.selectedTools || [];
                node.data.enableAgentToolDecision = node.data.enableAgentToolDecision ?? false;
                node.data.useCustomAIModel = node.data.useCustomAIModel ?? false;
                node.data.agentProvider = node.data.agentProvider || '';
                node.data.agentModel = node.data.agentModel || null;
                node.data.agentTemperature = node.data.agentTemperature ?? 0;
                node.data.agentMaxTokens = node.data.agentMaxTokens ?? null;
                node.data.agentTopP = node.data.agentTopP ?? 1;
                node.data.agentFrequencyPenalty = node.data.agentFrequencyPenalty ?? null;
                node.data.agentPresencePenalty = node.data.agentPresencePenalty ?? null;
                node.data.agentStopSequence = node.data.agentStopSequence || null;
                node.data.agentStreamResponse = node.data.agentStreamResponse ?? false;
                node.data.agentJsonResponse = node.data.agentJsonResponse ?? false;
                node.data.agentGender = node.data.agentGender || 'male';
                node.data.agentTypingSpeed = node.data.agentTypingSpeed ?? null;
                node.data.agentMaxInputLength = node.data.agentMaxInputLength ?? 200000;
                node.data.agentMemoryTurn = node.data.agentMemoryTurn ?? null;
                node.data.agentMemoryDuration = node.data.agentMemoryDuration ?? null;
                node.data.agentShortTermMemory = node.data.agentShortTermMemory ?? 200;
                node.data.agentLongTermMemory = node.data.agentLongTermMemory ?? false;
                node.data.agentEnableTime = node.data.agentEnableTime ?? false;
                node.data.agentTimeZone = node.data.agentTimeZone || null;
                node.data.agentEnableTransactionId = node.data.agentEnableTransactionId ?? false;
                node.data.agentEnableLanguageReplace = node.data.agentEnableLanguageReplace ?? false;
                node.data.agentEnableInsurance = node.data.agentEnableInsurance ?? false;
                // Hosted tools — เฉพาะ toolsConfig เท่านั้นที่เป็นของ node data (form state ไม่ลงมาที่นี่)
                node.data.toolsConfig = node.data.toolsConfig || null;
                node.data.knowledgeConnections = node.data.knowledgeConnections || {
                    documents: { nodeId: null, description: '' },
                    databaseIntegration: { nodeId: null, description: '' },
                    knowledgeApi: { nodeId: null, description: '' },
                    products: { nodeId: null, description: '' },
                    sharepoint: { nodeId: null, description: '' }
                };
            }

            // ── Mutual Fund node defaults ───────────────────────────────────────────────────────────────
            // Runs on create AND on copy/paste/import, so a node carried over from an older flow still
            // gets every key the inspector and the save whitelist expect.
            //
            // 🔴 mutualFundResolverEnabled is seeded FALSE here on purpose. A node that predates the
            //    resolver has no such key, and turning it on for that node would change what an existing
            //    flow answers without anybody touching it. `true` comes from the palette instead, so only
            //    a node dragged out AFTER this feature shipped resolves the fund from the customer's text.
            if (node.data.type === 'mutualFund') {
                node.data.mutualFundLookupType = node.data.mutualFundLookupType || 'fundCode';
                node.data.mutualFundLookupValue = node.data.mutualFundLookupValue || '';
                node.data.mutualFundComparisonValues = node.data.mutualFundComparisonValues || '';
                node.data.mutualFundFactsheetConfigCode = node.data.mutualFundFactsheetConfigCode || '';
                node.data.mutualFundComparisonConfigCode = node.data.mutualFundComparisonConfigCode || '';
                node.data.mutualFundPerformanceConfigCode = node.data.mutualFundPerformanceConfigCode || '';
                // The picker cards (type14) — drawn when the search matched more than one fund, one per
                // mode. A node that predates them simply has no code selected, which is the same as
                // "that turn has no buttons".
                node.data.mutualFundPickerConfigCode = node.data.mutualFundPickerConfigCode || '';
                node.data.mutualFundPickerCompareConfigCode = node.data.mutualFundPickerCompareConfigCode || '';
                node.data.mutualFundDemoMode = node.data.mutualFundDemoMode ?? false;
                // Fund resolver — caps (`??`: a 0 an operator typed is a real value)
                node.data.mutualFundResolverEnabled = node.data.mutualFundResolverEnabled ?? false;
                // 🔴 The three scope/limit keys below are RETIRED — nothing reads them any more (the prompt
                //    carries no fund list to scope or cap) and the inspector has no field for them. They stay
                //    seeded here on purpose: this literal doubles as the save whitelist's source of truth, and
                //    dropping a key would strip it out of an older flow the first time it is re-saved.
                node.data.mutualFundScopeTaxSavingFunds = node.data.mutualFundScopeTaxSavingFunds || '';
                node.data.mutualFundScopeRecommendedOnly = node.data.mutualFundScopeRecommendedOnly ?? false;
                node.data.mutualFundCandidateLimit = node.data.mutualFundCandidateLimit ?? 200;
                // How many funds the search pulls back per turn (backend clamps 1..50).
                node.data.mutualFundTopN = node.data.mutualFundTopN ?? 10;
                // Still read — but it now caps how many funds go into the comparison card (type9).
                node.data.mutualFundMaxSelect = node.data.mutualFundMaxSelect ?? 3;
                // #924 fuzzy search. Seeded OFF: a node — new or carried over from an older flow — keeps the plain
                // LIKE search until the operator switches fuzzy on in the node panel.
                node.data.mutualFundFuzzyEnabled = node.data.mutualFundFuzzyEnabled ?? false;
                // Backend clamps 0.60..1.00; 1.00 = exact keys only (separators / Thai letters still normalised).
                node.data.mutualFundFuzzyMinSimilarity = node.data.mutualFundFuzzyMinSimilarity ?? 0.78;
                // Backend clamps 0.00..0.40: how far below the best fuzzy score a fund may be and still be offered.
                node.data.mutualFundFuzzyTopGap = node.data.mutualFundFuzzyTopGap ?? 0.1;
                // LLM settings — shared keys (no prefix) so changeProvider() and the apiVersion auto-fill work
                node.data.provider = node.data.provider ?? null;
                node.data.model = node.data.model ?? null;
                node.data.apiVersion = node.data.apiVersion ?? null;
                node.data.temperature = node.data.temperature ?? 0;
                node.data.topP = node.data.topP ?? 1;
                node.data.maxTokens = node.data.maxTokens ?? null;
                // Prompts — blank means "use the built-in", so `||` (not `??`) is what we want here.
                // The twin's keys are the CONSTANT names, not the node.data keys: spell both sides out.
                node.data.systemPrompt = node.data.systemPrompt || MUTUAL_FUND_PROMPT_DEFAULTS.systemPrompt;
                node.data.mutualFundExtractionTemplate = node.data.mutualFundExtractionTemplate || MUTUAL_FUND_PROMPT_DEFAULTS.extractionTemplate;
                // 🔴 Retired prompt keys — kept ONLY so a flow saved before the extractor change round-trips
                //    with its old text intact. `|| ''` and never a default: the twin no longer ships one, and
                //    writing `undefined` here would drop the key out of the next save payload entirely.
                node.data.mutualFundSelectionTemplate = node.data.mutualFundSelectionTemplate || '';
                node.data.mutualFundFundListItemTemplate = node.data.mutualFundFundListItemTemplate || '';
                node.data.mutualFundNoDescriptionLabel = node.data.mutualFundNoDescriptionLabel || '';
            }

            // Tool node defaults
            if (node.data.type === 'tool') {
                node.data.toolName = node.data.toolName || '';
                node.data.toolDescription = node.data.toolDescription || '';
                node.data.toolType = node.data.toolType || 'master';
                node.data.masterToolId = node.data.masterToolId || null;
                node.data.endpoint = node.data.endpoint || '';
                node.data.method = node.data.method || 'GET';
                node.data.headers = node.data.headers || [];
                node.data.body = node.data.body || '';
            }

            return node;
        },

        // Calculate center position of current viewport
        getCenterPosition() {
            try {
                // Get the VueFlow instance
                const vueFlow = this.$refs.vueFlow;
                if (!vueFlow) {
                    console.warn('VueFlow ref not found, using fallback position');
                    return { x: 400, y: 200 }; // Fallback
                }

                // Try multiple methods to get viewport information
                let viewport, dimensions;
                
                // Method 1: Direct access to VueFlow methods
                if (typeof vueFlow.getViewport === 'function') {
                    viewport = vueFlow.getViewport();
                }
                
                if (typeof vueFlow.getDimensions === 'function') {
                    dimensions = vueFlow.getDimensions();
                }

                // Method 2: Access through VueFlow store/state if direct methods don't work
                if (!viewport && vueFlow.viewport) {
                    viewport = vueFlow.viewport;
                }

                if (!dimensions && vueFlow.dimensions) {
                    dimensions = vueFlow.dimensions;
                }

                // Method 3: Access through VueFlow internal state
                if (!viewport || !dimensions) {
                    const flowContainer = vueFlow.$el || vueFlow.$refs?.wrapper || document.querySelector('.vue-flow');
                    if (flowContainer) {
                        const rect = flowContainer.getBoundingClientRect();
                        dimensions = dimensions || { width: rect.width, height: rect.height };
                        
                        // If we still don't have viewport, try to get it from the flow
                        if (!viewport) {
                            // Get transform from the flow viewport
                            const viewportElement = flowContainer.querySelector('.vue-flow__viewport');
                            if (viewportElement) {
                                const transform = viewportElement.style.transform;
                                const match = transform.match(/translate\(([^)]+)\) scale\(([^)]+)\)/);
                                if (match) {
                                    const [, translate, scale] = match;
                                    const [x, y] = translate.split(',').map(v => parseFloat(v.replace('px', '')));
                                    viewport = { x, y, zoom: parseFloat(scale) };
                                }
                            }
                        }
                    }
                }

                // If we still don't have complete viewport info, use fallback
                if (!viewport || !dimensions) {
                    return { x: 400, y: 200 };
                }

                
                // Calculate center position in flow coordinates
                const centerX = (-viewport.x + dimensions.width / 2) / viewport.zoom;
                const centerY = (-viewport.y + dimensions.height / 2) / viewport.zoom;
                
                // Default node size (approximate)
                const nodeWidth = 200;  // Average node width
                const nodeHeight = 100; // Average node height
                
                // Adjust position so that center of node is at calculated center
                const adjustedX = centerX - (nodeWidth / 2);
                const adjustedY = centerY - (nodeHeight / 2);
                
                return { x: adjustedX, y: adjustedY };
            } catch (error) {
                return { x: 400, y: 200 }; // Fallback
            }
        },

        // New methods for node management
        addNewNode(nodeData) {
            // Check if trying to add messageInput node when one already exists
            if (nodeData.data.type === 'messageInput') {
                const existingMessageInput = this.tempNode.find(node => node.data.type === 'messageInput');
                if (existingMessageInput) {
                    this.showError('Message Input node already exists. Only one Message Input node is allowed per flow.');
                    return;
                }
            }

            const newNode = {
                ...nodeData,
                id: this.generateGuid(),
                position: this.getCenterPosition() // Use dynamic center position
            };

            // Apply comprehensive defaults
            this.applyNodeDefaults(newNode);

            this.tempNode.push(newNode);

            // Save state to history
            this.saveStateToHistory(`Add ${nodeData.data.label} Node`);

            this.$nextTick(() => {
                const getTempNode = this.tempNode.find(node => node.id === newNode.id);
                if (getTempNode) {
                    this.checkNodeValidation(getTempNode);
                }
                
                // Focus and select the new node
                this.focusAndSelectNode(newNode.id);
            });
            // Show success message
            this.showSuccess(`Added ${nodeData.data.label} to flow`);
        },
        // Helper function to focus and select a newly added node
        focusAndSelectNode(nodeId) {
            this.$nextTick(() => {
                // Find the new node
                const newNode = this.tempNode.find(node => node.id === nodeId);
                if (newNode) {
                    // First, clear all existing selections by setting selected to false
                    this.tempNode.forEach(node => {
                        if (node.selected) {
                            node.selected = false;
                        }
                    });

                    // Clear edges selection too
                    this.tempEdge.forEach(edge => {
                        if (edge.selected) {
                            edge.selected = false;
                        }
                    });

                    // Force update the arrays to trigger reactivity
                    this.tempNode = [...this.tempNode];
                    this.tempEdge = [...this.tempEdge];

                    // Wait a bit then properly select the node
                    setTimeout(() => {
                        // Find the node again after array update
                        const nodeToSelect = this.tempNode.find(node => node.id === nodeId);
                        if (nodeToSelect) {
                            // Set the node as selected
                            nodeToSelect.selected = true;
                            
                            // Update the selection state
                            this.selectedNode = nodeToSelect;
                            this.selectedNodes = [nodeToSelect];
                            
                            // Force another reactivity update to trigger visual selection
                            this.tempNode = [...this.tempNode];
                            
                            // Trigger the selection change event manually to ensure VueFlow updates
                            this.$nextTick(() => {
                                // Call onNodeClick to ensure all selection logic is properly executed
                                this.onNodeClick({ node: nodeToSelect });
                                
                                // Focus the view on the new node with smooth animation
                                setTimeout(() => {
                                    if (this.$refs.vueFlow) {
                                        try {
                                            this.$refs.vueFlow.fitView({
                                                nodes: [nodeToSelect],
                                                padding: 0.3,
                                                duration: 500,
                                                minZoom: 0.8,
                                                maxZoom: 1.5
                                            });
                                        } catch (error) {
                                            console.warn('Failed to focus on node:', error);
                                        }
                                    }
                                }, 100);
                            });
                        }
                    }, 50);
                }
            });
        },
        handleDeleteNode(nodeId) {
            const nodeToDelete = this.tempNode.find(node => node.id === nodeId);
            const nodeName = nodeToDelete ? nodeToDelete.data.label || 'Node' : 'Node';

            // Remove the node
            this.tempNode = this.tempNode.filter(node => node.id !== nodeId);

            // Remove all edges connected to this node
            this.tempEdge = this.tempEdge.filter(edge =>
                edge.source !== nodeId && edge.target !== nodeId
            );

            // Save state to history
            this.saveStateToHistory(`Delete ${nodeName}`);

            // Show success message
            this.showSuccess('Node deleted successfully');

            // Close inspector panel if the deleted node was selected
            if (this.selectedNode && this.selectedNode.id === nodeId) {
                this.selectedNodes = [];
                this.selectedNode = null;
                this.closeInspector();
            }
        },
        handleKeyDown(event) {
            // Check for Undo shortcut (Ctrl+Z)
            if (event.ctrlKey && event.code === 'KeyZ' && !event.shiftKey && !this.isViewMode && !this.isDebugMode) {
                event.preventDefault();
                this.undo();
            }
            // Check for Redo shortcuts (Ctrl+Y or Ctrl+Shift+Z)
            else if ((event.ctrlKey && event.code === 'KeyY') || 
                     (event.ctrlKey && event.shiftKey && event.code === 'KeyZ') && !this.isViewMode && !this.isDebugMode) {
                event.preventDefault();
                this.redo();
            }
            // Check for Copy shortcut (Ctrl/Cmd+C)
            else if ((event.ctrlKey || event.metaKey) && event.code === 'KeyC' && !this.isViewMode && !this.isDebugMode && this.selectedNodes.length > 0) {
                // Let the browser copy text when the user is in an input/textarea/contenteditable.
                const activeEl = document.activeElement;
                const isEditable = activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.isContentEditable);
                if (isEditable) {
                    return;
                }
                event.preventDefault();
                this.handleCopySelectedNodes();
            }
            // NOTE: Paste (Ctrl/Cmd+V) is intentionally NOT handled here. It is handled by the
            // native 'paste' event listener (handleClipboardPaste, registered in mounted()), which
            // reads clipboardData directly — no permission prompt, works cross-tab/window/browser
            // and on Mac (Cmd+V). The "Paste" menu button uses handlePasteNodes() (clipboard.readText
            // with an in-memory fallback) since a button click fires no native paste event.
            // Check if Delete key is pressed and we're not in view mode
            else if (event.key === 'Delete' && !this.isViewMode && !this.isDebugMode && this.selectedNodes.length > 0) {
                event.preventDefault();
                this.handleDeleteSelectedNodes();
            }
            // Check if Ctrl+D is pressed for duplication (using event.code for keyboard layout independence)
            else if (event.ctrlKey && event.code === 'KeyD' && !this.isViewMode && !this.isDebugMode && this.selectedNodes.length > 0) {
                event.preventDefault();
                this.handleDuplicateSelectedNodes();
            }
            // Alternative: Also support Ctrl+Shift+D for duplication (fallback)
            else if (event.ctrlKey && event.shiftKey && event.code === 'KeyD' && !this.isViewMode && !this.isDebugMode && this.selectedNodes.length > 0) {
                event.preventDefault();
                this.handleDuplicateSelectedNodes();
            }
            // Fallback: Use event.key for older browsers or special cases
            else if (event.ctrlKey && event.key.toLowerCase() === 'd' && !this.isViewMode && !this.isDebugMode && this.selectedNodes.length > 0) {
                event.preventDefault();
                this.handleDuplicateSelectedNodes();
            }
            // Ctrl+F: Open node search (unless focus is in inspector input — then allow browser search)
            else if ((event.ctrlKey || event.metaKey) && (event.code === 'KeyF' || event.key.toLowerCase() === 'f')) {
                const activeEl = document.activeElement;
                const isInInspector = activeEl && activeEl.closest('.floating-inspector');
                const isInputField = activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.isContentEditable);
                if (isInInspector && isInputField) {
                    // Let browser native Ctrl+F handle it
                    return;
                }
                event.preventDefault();
                this.openNodeSearch();
            }
        },
        focusFlowContainer(event) {
            // Don't steal focus from input elements (e.g. debug control bar input)
            const tag = event?.target?.tagName?.toLowerCase();
            if (tag === 'input' || tag === 'textarea' || tag === 'select' || tag === 'option' || event?.target?.closest?.('.debug-bar-wrapper') || event?.target?.closest?.('.debug-control-bar') || event?.target?.closest?.('.debug-inspector')) {
                return;
            }
            // Ensure the flow container can receive keyboard events
            this.$nextTick(() => {
                if (this.$refs.flowContainer) {
                    this.$refs.flowContainer.focus();
                }
            });
        },

        // ── Node Search (Ctrl+F) ─────────────────────────────────────────────
        openNodeSearch() {
            this.nodeSearchOpen = true;
            this.$nextTick(() => {
                if (this.$refs.nodeSearchInput) {
                    this.$refs.nodeSearchInput.focus();
                    this.$refs.nodeSearchInput.select();
                }
            });
        },
        closeNodeSearch() {
            this.nodeSearchOpen = false;
            this.clearSearchHighlights();
            this.nodeSearchQuery = '';
            this.nodeSearchResults = [];
            this.nodeSearchCurrentIndex = -1;
        },
        // Build a human-readable display label from a node (mirrors what FlowNode.vue renders)
        getNodeDisplayLabel(node) {
            const d = node.data || {};
            const type = d.type || node.type || '';
            const fixed = {
                messageInput:    'Message Input',
                response:        'Response',
                intentAI:        'Intent AI',
                intent:          'Intent Router',
                router:          'Router',
                advancedRouter:  'Advanced Router',
                smartIntent:     'Smart Intent',
                condition:       'If (Condition)',
                jsonCondition:   d.jsonConditionName || 'Rule-Based',
                serviceFallback: 'Service Fallback',
                delay:           'Delay',
                switch:          'Switch',
                webhook:         'Webhook',
                email:           'Email',
                apiTrigger:      'API Trigger',
                contactAgent:    'Contact Agent',
                contactAgentV2:  'Contact Agent V2',
                documents:       'Documents',
                databaseIntegration: 'DB Integration',
                apiknowledge:    'API Knowledge',
                metaDocuments:   'Meta Documents',
                products:        'Products',
                interestOptimizer: 'Interest Optimizer',
                loanInterest:    'Loan Interest',
                interestOptimizerV2: 'Interest Optimizer V2',
                loanInterestV2:    'Loan Interest V2',
                sharepoint:      'SharePoint',
                aiVoice:         'AI Voice',
                reformatTTS:     'Reformat TTS',
                ssmlFormat:      'SSML Format',
                queryRewriter:   'Query Rewriter',
                flowInput:       d.label || 'Flow Input',
                flowOutput:      d.label || 'Flow Output',
            };
            if (fixed[type] !== undefined) return fixed[type];
            // Dynamic label nodes
            if (type === 'model')         return d.customLabel       || 'AI Model';
            if (type === 'orchestrator')  return d.orchestratorName  || 'Orchestrator';
            if (type === 'agent')         return d.agentName         || 'Agent';
            if (type === 'taxPlanningAgent') return d.agentName      || 'Tax Planning Agent';
            if (type === 'tool')          return d.toolName          || 'Tool';
            if (type === 'flowTool')      return d.flowToolName      || 'Flow Tool';
            if (type === 'buttonStyle')   return d.buttonStyleName   || 'Button Style';
            return d.label || d.name || type || '';
        },

        performNodeSearch() {
            const q = this.nodeSearchQuery.trim().toLowerCase();
            this.clearSearchHighlights();

            if (!q) {
                this.nodeSearchResults = [];
                this.nodeSearchCurrentIndex = -1;
                return;
            }

            const results = this.tempNode.filter(node => {
                const d = node.data || {};
                const displayLabel = this.getNodeDisplayLabel(node).toLowerCase();
                const type = (d.type || node.type || '').toLowerCase();
                // Extra dynamic names for extra coverage
                const extras = [
                    d.customLabel, d.orchestratorName, d.agentName, d.toolName,
                    d.flowToolName, d.jsonConditionName, d.label, d.name,
                ].filter(Boolean).join(' ').toLowerCase();
                return displayLabel.includes(q) || type.includes(q) || extras.includes(q);
            });

            this.nodeSearchResults = results;
            this.nodeSearchCurrentIndex = results.length > 0 ? 0 : -1;

            // Apply highlight classes via VueFlow internal nodes (reactive)
            results.forEach((node, idx) => {
                this.setNodeSearchClass(
                    node.id,
                    idx === 0 ? 'search-highlight search-highlight-current' : 'search-highlight'
                );
            });

            if (results.length > 0) {
                this.jumpToSearchNode(results[0]);
            }
        },
        navigateSearchResult(dir) {
            if (!this.nodeSearchResults.length) return;

            const prev = this.nodeSearchCurrentIndex;
            const total = this.nodeSearchResults.length;
            const next = ((prev + dir) % total + total) % total;

            // Remove -current from previous
            if (prev >= 0 && this.nodeSearchResults[prev]) {
                this.setNodeSearchClass(this.nodeSearchResults[prev].id, 'search-highlight');
            }

            // Set -current on next
            if (this.nodeSearchResults[next]) {
                this.setNodeSearchClass(this.nodeSearchResults[next].id, 'search-highlight search-highlight-current');
            }

            this.nodeSearchCurrentIndex = next;
            this.jumpToSearchNode(this.nodeSearchResults[next]);
        },
        jumpToSearchNode(node) {
            if (!this.$refs.vueFlow || !node) return;
            const w = node.dimensions?.width || 200;
            const h = node.dimensions?.height || 80;
            const cx = node.position.x + w / 2;
            const cy = node.position.y + h / 2;
            this.$refs.vueFlow.setCenter(cx, cy, { zoom: 1.4, duration: 400 });
        },
        // Set or clear search highlight class on a node via VueFlow reactive state
        setNodeSearchClass(nodeId, searchClass) {
            const vf = this.$refs.vueFlow;
            if (!vf) return;
            const n = vf.findNode(nodeId);
            if (!n) return;
            // Strip existing search classes (match both 'search-highlight' and 'search-highlight-current')
            let base = (n.class || '')
                .replace(/\bsearch-highlight-current\b/g, '')
                .replace(/\bsearch-highlight\b/g, '')
                .replace(/\s{2,}/g, ' ').trim();
            n.class = searchClass ? ((base ? base + ' ' : '') + searchClass).trim() : (base || undefined);
        },
        clearSearchHighlights() {
            this.nodeSearchResults.forEach(node => {
                this.setNodeSearchClass(node.id, null);
            });
        },
        // ────────────────────────────────────────────────────────────────────
        handleDeleteSelectedNodes() {
            if (this.selectedNodes.length === 0) {
                return;
            }

            // Collect all node IDs to delete
            const nodeIdsToDelete = this.selectedNodes.map(node => node.id);
            const nodeCount = nodeIdsToDelete.length;
            
            // Remove all selected nodes
            this.tempNode = this.tempNode.filter(node => !nodeIdsToDelete.includes(node.id));

            // Remove all edges connected to any of the deleted nodes
            this.tempEdge = this.tempEdge.filter(edge =>
                !nodeIdsToDelete.includes(edge.source) && !nodeIdsToDelete.includes(edge.target)
            );

            // Save state to history
            this.saveStateToHistory(`Delete ${nodeCount} Node${nodeCount > 1 ? 's' : ''}`);

            // Show success message
            this.showSuccess(`${nodeCount} node${nodeCount > 1 ? 's' : ''} deleted successfully`);

            // Clear selection and close inspector
            this.selectedNodes = [];
            this.selectedNode = null;
            this.closeInspector();
        },
        handleDuplicateSelectedNodes() {
            if (this.selectedNodes.length === 0) {
                return;
            }
            
            // Check if any selected node is messageInput type
            const hasMessageInputNode = this.selectedNodes.some(node => node.data.type === 'messageInput');
            if (hasMessageInputNode) {
                this.showError('Cannot duplicate Message Input node. Only one Message Input node is allowed per flow.');
                return;
            }
            
            console.log('Duplicating nodes:', this.selectedNodes);
            const duplicatedNodes = [];
            const oldToNewNodeIds = new Map(); // Map old node IDs to new node IDs
            const offsetX = 60; // Horizontal offset for duplicated nodes
            const offsetY = 60; // Vertical offset for duplicated nodes

            // Create duplicated nodes
            this.selectedNodes.forEach(originalNode => {
                const newNodeId = this.generateGuid();

                // Deep clone the node data to avoid reference issues
                let stgData = JSON.stringify(originalNode.data);
                stgData = stgData.replaceAll(originalNode.id, newNodeId);

                oldToNewNodeIds.set(originalNode.id, newNodeId);
                const clonedData = JSON.parse(stgData);
                
                // Update label if it exists
                if (clonedData.label) {
                    clonedData.label = clonedData.label.replace(' (Copy)', '') + ' (Copy)';
                }

                const duplicatedNode = {
                    ...originalNode,
                    id: newNodeId,
                    position: {
                        x: originalNode.position.x + offsetX,
                        y: originalNode.position.y + offsetY
                    },
                    selected: false, // Don't select the duplicated nodes initially
                    data: clonedData
                };

                duplicatedNodes.push(duplicatedNode);
            });

            // Add duplicated nodes to the flow
            this.tempNode.push(...duplicatedNodes);

            // Duplicate the edges that connect two selected nodes (remap to the new node ids,
            // keep the handles so router/switch/intent outputs stay wired up).
            const selectedNodeIds = this.selectedNodes.map(node => node.id);
            const duplicatedEdges = this.tempEdge
                .filter(edge => selectedNodeIds.includes(edge.source) && selectedNodeIds.includes(edge.target))
                .map(originalEdge => {
                    const newSource = oldToNewNodeIds.get(originalEdge.source);
                    const newTarget = oldToNewNodeIds.get(originalEdge.target);
                    return {
                        ...originalEdge,
                        id: `e${newSource}-${newTarget}-${this.generateGuid()}`,
                        source: newSource,
                        target: newTarget
                    };
                });
            if (duplicatedEdges.length) {
                this.tempEdge.push(...duplicatedEdges);
            }

            // Save state to history (after nodes + edges so undo restores both together)
            const nodeCount = duplicatedNodes.length;
            this.saveStateToHistory(`Duplicate ${nodeCount} Node${nodeCount > 1 ? 's' : ''}`);

            // Show success message
            this.showSuccess(`${nodeCount} node${nodeCount > 1 ? 's' : ''} duplicated successfully`);

            // Clear current selection first
            this.selectedNodes = [];
            this.selectedNode = null;

            // Select the duplicated nodes after a brief delay to ensure they're rendered
            this.$nextTick(() => {
                // Update the selected state of duplicated nodes
                duplicatedNodes.forEach(node => {
                    const nodeInFlow = this.tempNode.find(n => n.id === node.id);
                    if (nodeInFlow) {
                        nodeInFlow.selected = true;
                    }
                });
                
                this.selectedNodes = duplicatedNodes;
                
                // If only one node was duplicated, open it in inspector
                if (duplicatedNodes.length === 1) {
                    this.selectedNode = duplicatedNodes[0];
                    this.openNodeInspector(duplicatedNodes[0]);
                }
            });
        },
        // Build a self-contained clipboard payload from the current selection:
        // the selected nodes (with all their inner config) + the edges whose BOTH
        // endpoints are inside the selection (so relations travel with the nodes).
        buildClipboardPayload() {
            const selectedIds = new Set(this.selectedNodes.map(n => n.id));

            const nodes = this.selectedNodes.map(node => ({
                id: node.id,
                type: node.type || 'flow-node',
                position: { x: node.position.x, y: node.position.y },
                data: JSON.parse(JSON.stringify(node.data)) // deep clone, drop reactivity/refs
            }));

            const edges = this.tempEdge
                .filter(edge => selectedIds.has(edge.source) && selectedIds.has(edge.target))
                .map(edge => ({
                    id: edge.id,
                    source: edge.source,
                    target: edge.target,
                    sourceHandle: edge.sourceHandle ?? null,
                    targetHandle: edge.targetHandle ?? null,
                    type: edge.type || 'flow-edge',
                    markerEnd: edge.markerEnd ?? MarkerType.ArrowClosed,
                    animated: edge.animated ?? false
                }));

            return {
                __type: this.clipboardType,
                version: 1,
                app: 'Fusion.ChatFramework',
                kind: 'nodes',
                copiedAt: new Date().toISOString(),
                sourceFlowId: this.editFlowId || null,
                nodes,
                edges
            };
        },
        // Validate that an arbitrary parsed object is one of our flow-clipboard payloads.
        isFlowClipboardPayload(payload) {
            return !!payload
                && typeof payload === 'object'
                && payload.__type === this.clipboardType
                && Array.isArray(payload.nodes);
        },
        async handleCopySelectedNodes() {
            if (this.selectedNodes.length === 0) {
                return;
            }

            const payload = this.buildClipboardPayload();

            // Keep an in-memory copy as a fallback (same-tab paste when the clipboard API is blocked)
            this.copiedNodes = payload.nodes;
            this.copiedEdges = payload.edges;

            const nodeCount = payload.nodes.length;
            const suffix = nodeCount > 1 ? 's' : '';

            // Write to the real system clipboard so it can be pasted across flows / tabs / windows / browsers
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(JSON.stringify(payload));
                    this.showSuccess(`${nodeCount} node${suffix} copied to clipboard`);
                } else {
                    this.showSuccess(`${nodeCount} node${suffix} copied (same tab only — clipboard unavailable)`);
                }
            } catch (err) {
                console.warn('Clipboard write failed, using in-memory fallback:', err);
                this.showSuccess(`${nodeCount} node${suffix} copied (same tab only)`);
            }
        },
        // Entry point for the "Paste" menu button (keyboard Ctrl/Cmd+V goes through the native
        // 'paste' event instead). Reads the real system clipboard first (cross-flow / tab / window
        // / browser), and only falls back to the in-memory copy when the clipboard cannot be read.
        async handlePasteNodes(atScreenPos = null) {
            try {
                if (navigator.clipboard && navigator.clipboard.readText) {
                    const text = await navigator.clipboard.readText();
                    let payload = null;
                    if (text) {
                        try { payload = JSON.parse(text); } catch (_) { payload = null; }
                    }
                    if (this.isFlowClipboardPayload(payload)) {
                        this.pasteFlowPayload(payload, atScreenPos);
                    }
                    // Clipboard was readable but holds foreign content -> respect it, do nothing.
                    return;
                }
            } catch (err) {
                // No clipboard-read permission / insecure context -> fall back to in-memory copy.
                console.warn('Clipboard read failed, using in-memory fallback:', err);
            }

            if (this.copiedNodes && this.copiedNodes.length > 0) {
                this.pasteFlowPayload({
                    __type: this.clipboardType,
                    version: 1,
                    nodes: this.copiedNodes,
                    edges: this.copiedEdges || []
                }, atScreenPos);
            }
        },
        // Native "paste" event handler (Ctrl+V over the canvas in Chromium browsers).
        // Uses clipboardData synchronously, so it needs no clipboard-read permission prompt.
        handleClipboardPaste(event) {
            if (this.isViewMode || this.isDebugMode) {
                return;
            }
            // Don't hijack paste while the user is typing in an input/textarea/contenteditable.
            const activeEl = document.activeElement;
            const isEditable = activeEl && (
                activeEl.tagName === 'INPUT' ||
                activeEl.tagName === 'TEXTAREA' ||
                activeEl.isContentEditable
            );
            if (isEditable) {
                return;
            }

            const clipboardData = event.clipboardData || window.clipboardData;
            if (!clipboardData) {
                return;
            }
            const text = clipboardData.getData('text/plain');
            if (!text) {
                return;
            }
            let payload = null;
            try { payload = JSON.parse(text); } catch (_) { return; }
            if (!this.isFlowClipboardPayload(payload)) {
                return;
            }

            // It's our payload -> take over the paste.
            event.preventDefault();
            this.pasteFlowPayload(payload, this._lastClientPos);
        },
        // Resolve where to drop pasted nodes: at the cursor when we have a screen position
        // and Vue Flow can convert it, otherwise the viewport center.
        resolvePastePosition(atScreenPos) {
            try {
                const pos = atScreenPos || this._lastClientPos;
                const vueFlowInstance = this.$refs.vueFlow;
                if (pos && vueFlowInstance && typeof vueFlowInstance.screenToFlowPosition === 'function') {
                    const flowPos = vueFlowInstance.screenToFlowPosition({ x: pos.x, y: pos.y });
                    return { x: flowPos.x, y: flowPos.y };
                }
            } catch (err) {
                console.warn('screenToFlowPosition failed, falling back to center:', err);
            }
            return this.getCenterPosition();
        },
        // Core paste routine shared by every entry point (keyboard, menu, native paste,
        // in-memory fallback). Remaps all ids, recreates relations, and selects the result.
        pasteFlowPayload(payload, atScreenPos = null) {
            // Dedupe: Ctrl+V can fire both the keydown handler and the native 'paste' event in
            // some browsers — ignore a second paste that arrives within a short window.
            const now = Date.now();
            if (this._lastPasteTs && (now - this._lastPasteTs) < 250) {
                return false;
            }

            if (!this.isFlowClipboardPayload(payload) || payload.nodes.length === 0) {
                return false;
            }

            // Guard: only one Message Input node is allowed per flow.
            const hasMessageInputNode = payload.nodes.some(node => node?.data?.type === 'messageInput');
            if (hasMessageInputNode) {
                const existingMessageInput = this.tempNode.find(node => node.data.type === 'messageInput');
                if (existingMessageInput) {
                    this.showError('Cannot paste Message Input node. Only one Message Input node is allowed per flow.');
                    return false;
                }
            }

            // 1) Build a COMPLETE old->new id map first, so references between the pasted
            //    nodes (not just each node's own id) are remapped correctly.
            const oldToNewNodeIds = new Map();
            payload.nodes.forEach(node => oldToNewNodeIds.set(node.id, this.generateGuid()));

            // 2) Bounding box of the copied set (to preserve relative layout) + base position.
            let minX = Infinity, minY = Infinity;
            payload.nodes.forEach(node => {
                minX = Math.min(minX, node.position?.x ?? 0);
                minY = Math.min(minY, node.position?.y ?? 0);
            });
            if (!isFinite(minX)) minX = 0;
            if (!isFinite(minY)) minY = 0;
            const basePosition = this.resolvePastePosition(atScreenPos);

            // 3) Create the new nodes.
            const pastedNodes = payload.nodes.map(originalNode => {
                const newNodeId = oldToNewNodeIds.get(originalNode.id);

                // Deep clone data and remap EVERY referenced node id (own + siblings).
                // Internal handle ids (router-output-*, intent-*, ...) are independent guids
                // that are intentionally left untouched so edge handles still line up.
                let stgData = JSON.stringify(originalNode.data ?? {});
                oldToNewNodeIds.forEach((newId, oldId) => {
                    stgData = stgData.split(oldId).join(newId);
                });
                const clonedData = JSON.parse(stgData);
                if (clonedData.label) {
                    clonedData.label = clonedData.label.replace(' (Copy)', '') + ' (Copy)';
                }

                const relativeX = (originalNode.position?.x ?? 0) - minX;
                const relativeY = (originalNode.position?.y ?? 0) - minY;

                const newNode = {
                    id: newNodeId,
                    type: originalNode.type || 'flow-node',
                    position: { x: basePosition.x + relativeX, y: basePosition.y + relativeY },
                    selected: false,
                    data: clonedData
                };
                // Fill defaults that may be missing when pasting across versions
                // (only fills absent keys, never overwrites existing config).
                this.applyNodeDefaults(newNode);
                return newNode;
            });

            // 4) Recreate the relations between pasted nodes (remap source/target, keep handles).
            const pastedEdges = (payload.edges || [])
                .filter(edge => oldToNewNodeIds.has(edge.source) && oldToNewNodeIds.has(edge.target))
                .map(edge => {
                    const newSource = oldToNewNodeIds.get(edge.source);
                    const newTarget = oldToNewNodeIds.get(edge.target);
                    return {
                        id: `e${newSource}-${newTarget}-${this.generateGuid()}`,
                        source: newSource,
                        target: newTarget,
                        sourceHandle: edge.sourceHandle ?? null,
                        targetHandle: edge.targetHandle ?? null,
                        type: edge.type || 'flow-edge',
                        markerEnd: edge.markerEnd ?? MarkerType.ArrowClosed,
                        animated: edge.animated ?? false,
                        showButton: false
                    };
                });

            // 5) Commit to the flow.
            this.tempNode.push(...pastedNodes);
            if (pastedEdges.length) {
                this.tempEdge.push(...pastedEdges);
            }
            this._lastPasteTs = now;

            const nodeCount = pastedNodes.length;
            const suffix = nodeCount > 1 ? 's' : '';
            this.saveStateToHistory(`Paste ${nodeCount} Node${suffix}`);
            this.showSuccess(`${nodeCount} node${suffix} pasted successfully`);

            // 6) Select the freshly pasted nodes (open inspector when a single node was pasted).
            this.selectedNodes = [];
            this.selectedNode = null;
            this.$nextTick(() => {
                pastedNodes.forEach(node => {
                    const nodeInFlow = this.tempNode.find(n => n.id === node.id);
                    if (nodeInFlow) {
                        nodeInFlow.selected = true;
                    }
                });
                this.selectedNodes = pastedNodes;
                if (pastedNodes.length === 1) {
                    this.selectedNode = pastedNodes[0];
                    this.openNodeInspector(pastedNodes[0]);
                }
            });
            return true;
        },
        // Track the latest cursor position over the canvas for paste-at-cursor.
        // Stored on a plain (non-reactive) property to avoid reactivity churn on mousemove.
        trackCanvasMouse(event) {
            this._lastClientPos = { x: event.clientX, y: event.clientY };
        },
        handleRemoveOutgoingEdge(edgeId) {
            // Find edge details before removing for history
            const edgeToRemove = this.tempEdge.find(edge => edge.id === edgeId);
            
            // Remove the specific edge by ID
            this.tempEdge = this.tempEdge.filter(edge => edge.id !== edgeId);
            
            // Save to history
            if (edgeToRemove) {
                const sourceNode = this.tempNode.find(node => node.id === edgeToRemove.source);
                const targetNode = this.tempNode.find(node => node.id === edgeToRemove.target);
                const sourceName = sourceNode ? sourceNode.data.label || 'Node' : 'Node';
                const targetName = targetNode ? targetNode.data.label || 'Node' : 'Node';
                this.saveStateToHistory(`Disconnect ${sourceName} → ${targetName}`);
            } else {
                this.saveStateToHistory('Remove Connection');
            }
        },
        handleNodeDataUpdate(nodeId, newData) {
            const nodeIndex = this.tempNode.findIndex(node => node.id === nodeId);
            if (nodeIndex !== -1) {
                this.tempNode = [
                    ...this.tempNode.slice(0, nodeIndex),
                    {
                        ...this.tempNode[nodeIndex],
                        data: { ...this.tempNode[nodeIndex].data, ...newData }
                    },
                    ...this.tempNode.slice(nodeIndex + 1)
                ];
            }
        },
        onDrop(event) {
            event.preventDefault();
            const nodeData = event.dataTransfer.getData('application/reactflow');
            if (nodeData) {
                const nodeTemplate = JSON.parse(nodeData);

                // Get VueFlow instance
                const vueFlowInstance = this.$refs.vueFlow;
                if (vueFlowInstance) {
                    let position;

                    // Try to use screenToFlowPosition if available (newer versions)
                    if (typeof vueFlowInstance.screenToFlowPosition === 'function') {
                        const flowPosition = vueFlowInstance.screenToFlowPosition({
                            x: event.clientX,
                            y: event.clientY
                        });
                        position = {
                            x: flowPosition.x - 75,
                            y: flowPosition.y - 25
                        };
                    }
                    // Fallback to manual calculation
                    else {
                        const flowElement = vueFlowInstance.$el.querySelector('.vue-flow__viewport') || vueFlowInstance.$el;
                        const flowBounds = flowElement.getBoundingClientRect();

                        // Get current viewport transform
                        const viewport = vueFlowInstance.getViewport ? vueFlowInstance.getViewport() : { x: 0, y: 0, zoom: 1 };

                        // Calculate the position relative to the flow viewport
                        const relativeX = event.clientX - flowBounds.left;
                        const relativeY = event.clientY - flowBounds.top;

                        // Convert screen coordinates to flow coordinates accounting for zoom and pan
                        position = {
                            x: (relativeX - viewport.x) / viewport.zoom - 75,
                            y: (relativeY - viewport.y) / viewport.zoom - 25
                        };
                    }

                    const newNode = {
                        id: this.generateGuid(),
                        type: 'flow-node',
                        position: position,
                        data: { ...nodeTemplate.data }
                    };

                    // Check if trying to add messageInput node when one already exists
                    if (newNode.data.type === 'messageInput') {
                        const existingMessageInput = this.tempNode.find(node => node.data.type === 'messageInput');
                        if (existingMessageInput) {
                            this.showError('Message Input node already exists. Only one Message Input node is allowed per flow.');
                            return;
                        }
                    }

                    // Apply comprehensive defaults
                    this.applyNodeDefaults(newNode);

                    this.tempNode.push(newNode);
                    this.$nextTick(() => {
                        const getTempNode = this.tempNode.find(node => node.id === newNode.id);
                        if (getTempNode) {
                            this.checkNodeValidation(getTempNode);
                        }
                        
                        // Focus and select the new node
                        this.focusAndSelectNode(newNode.id);
                    });
                    // Show success message
                    this.showSuccess(`Added ${nodeTemplate.data.label} to flow`);
                } else {
                    console.error('VueFlow instance not available');
                }
            }
        },
        // Update SaveFlow method to use new targets structure
        async SaveFlow() {
            try {
                // this.dialog = true;

                // Check if there are any nodes in the flow has data.warningShow = true
                // if (this.tempNode.some(node => node.data.showWarning)) {
                //     this.showError('Please resolve all warnings before saving the flow');
                //     return;
                // }


                if (!this.tempNode || this.tempNode.length === 0) {
                    this.showError('Cannot save empty flow');
                    return;
                }

                // Convert to flat structure with targets
                const flatNodes = this.convertToFlatStructure();

                const flowData = {
                    id: this.flowId || null,
                    name: this.flowName || 'Untitled Flow',
                    versionComment: this.isEditMode ? (this.saveFlowData.versionComment || null) : null,
                    flowDetails: flatNodes
                };

                // Validate flow structure
                const validationResult = this.validateFlatFlow(flowData);
                if (!validationResult.isValid) {
                    this.showError(`Flow validation failed: ${validationResult.errors.join(', ')}`);
                    return;
                }

                // Choose the appropriate API method
                const apiMethod = this.flowId ? 'UpdateFlow' : 'CreateFlow';

                // Save to API
                const response = await this.saveFlowToAPI(flowData, apiMethod);

                if (response.success) {
                    // Update flow metadata
                    const oldFlowId = this.flowId;
                    const newFlowId = response.flowId || this.flowId;
                    
                    this.flowId = newFlowId;
                    this.flowName = flowData.name;
                    this.clearHistory();
                    this.showSuccess(this.isEditMode ? 'Flow updated successfully' : 'Flow saved successfully');
                    this.successDialog = true; // Show success dialog

                    // Update URL with new FlowID (especially important for UpdateFlow which creates new version)
                    if (oldFlowId !== newFlowId) {
                        // FlowID changed - update URL
                        this.$router.replace({ path: '/FlowCreator', query: { id: newFlowId } });
                    }
                    
                    this.isEditMode = true;
                    // Automatically hide success dialog after 2 seconds
                    setTimeout(() => {
                        this.successDialog = false;
                    }, 2000);
                } else {
                    throw new Error(response.message || 'Failed to save flow');
                }

            } catch (error) {
                console.error('Error saving flow:', error);
                this.showError(`Failed to save flow: ${error.message}`);
            } finally {
                this.dialog = false;
            }
        },

        // ============================================
        // DEBUG MODE METHODS
        // ============================================
        toggleDebugMode() {
            if (this.isDebugMode) {
                // Exiting debug mode
                this.exitDebugMode();
            } else {
                // Entering debug mode
                this.enterDebugMode();
            }
        },
        generateDebugUserId() {
            // Same GUID generation as Stream.vue GetUuidv4_Fun()
            return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
                (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
            );
        },
        enterDebugMode() {
            this.isDebugMode = true;
            this.debugStatus = 'ready';
            // Unselect any selected nodes and close inspector
            this.selectedNodes = [];
            this.selectedNode = null;
            this.tempNode.forEach(n => { n.selected = false; });
            this.closeInspector();
            this.debugSteps = [];
            this.debugNodeStates = {};
            this.debugEdgeStates = {};
            this.debugSessionId = '';
            this.debugMessage = '';
            this.debugError = '';
            this.debugCurrentNodeId = '';
            this.debugPreviousNodeId = '';
            this.debugPausedNodeName = '';
            this.debugStepCount = 0;
            this.debugTotalDuration = 0;
            this.debugVariables = {};
            this.debugChangedVariables = new Set();
            this.debugPatchVariables = {};
            this.debugOverrideNextNodeId = '';
            this.debugHoveredNodeId = '';
            // Generate initial debug userId
            this.debugUserId = this.generateDebugUserId();
            // Restore last simulated CIF + "ใช้ CIF" preference — reload each time debug is re-opened
            this.debugCif = localStorage.getItem('debug.lastCif') || '';
            this.debugUseCif = localStorage.getItem('debug.useCif') === 'true';
            // Mark all idle nodes
            this.tempNode.forEach(n => {
                this.debugNodeStates[n.id] = 'idle';
            });
            // Reset animated state on all edges
            this.tempEdge.forEach(e => { e.animated = false; });
        },
        exitDebugMode() {
            // Stop any running session first
            if (this.debugStatus === 'running' || this.debugStatus === 'paused') {
                this.stopDebugExecution();
            }
            this.isDebugMode = false;
            this.debugStatus = 'ready';
            this.debugSteps = [];
            this.debugNodeStates = {};
            this.debugEdgeStates = {};
            this.debugSessionId = '';
            this.debugCurrentNodeId = '';
            this.debugError = '';
            this.debugVariables = {};
            this.debugChangedVariables = new Set();
            this.debugLastBotReply = '';
            this.debugStreaming = false;
            this.debugStreamingNodeId = null;
            this.debugSseReader = null;
            this.debugPatchVariables = {};
            this.debugOverrideNextNodeId = '';
            this.debugHoveredNodeId = '';
            // Clear debug userId on exit
            this.debugUserId = '';
            this.debugKeepMemory = false;
            this.debugVoiceMode = false;
            this.debugVoiceLang = 'th';
            this.debugProactiveMessage = '';
            this.debugCif = '';
            this.debugUseCif = false;
            this.debugAccessLevel = 'T3';
            // Reset animated state on all edges
            this.tempEdge.forEach(e => { e.animated = false; });
        },
        onDebugKeepMemoryChange(val) {
            this.debugKeepMemory = val;
            if (val) {
                // Just turned ON: generate a fresh userId and lock it
                this.debugUserId = this.generateDebugUserId();
            }
            // If turned OFF: userId will be regenerated on next startDebugExecution
        },
        async startDebugExecution() {
            if (!this.debugMessage.trim()) return;
            if (this.debugStatus === 'running') return;

            // "ใช้ CIF" mode: the typed CIF IS the identity (mirrors production where context.UserId == CIF),
            // so it stays stable across sends and the Tax Planning form resumes correctly by CIF.
            const usingCif = this.debugUseCif && !!this.debugCif?.trim();
            // Regenerate userId if keepMemory is OFF — but not when using CIF (the CIF is the stable identity).
            if (!this.debugKeepMemory && !usingCif) {
                this.debugUserId = this.generateDebugUserId();
            }

            // Reset states
            this.debugStatus = 'running';
            this.debugSteps = [];
            this.debugStepCount = 0;
            this.debugTotalDuration = 0;
            this.debugError = '';
            this.debugCurrentNodeId = '';
            this.debugPreviousNodeId = '';
            this.debugPausedNodeName = '';
            this.debugVariables = {};
            this.debugChangedVariables = new Set();
            this.debugLastBotReply = '';
            this.debugStreaming = false;
            this.debugStreamingNodeId = null;
            this.debugPatchVariables = {};
            this.debugOverrideNextNodeId = '';
            this.debugHoveredNodeId = '';
            // Reset node and edge states
            const nodeStates = {};
            this.tempNode.forEach(n => { nodeStates[n.id] = 'idle'; });
            this.debugNodeStates = nodeStates;
            this.debugEdgeStates = {};
            // Reset animated state on all edges
            this.tempEdge.forEach(e => { e.animated = false; });
            const flatNodes = this.convertToFlatStructure();
            // Convert to backend format: position, data, targets as JSON strings
            const flowNodes = flatNodes.map(n => ({
                Id: n.id,
                NodeType: n.nodeType,
                Position: JSON.stringify(n.position),
                Data: JSON.stringify(n.data),
                Targets: JSON.stringify(n.targets),
            }));

            const request = {
                FlowNodes: flowNodes,
                Message: this.debugMessage.trim(),
                Breakpoints: Array.from(this.debugBreakpoints),
                Variables: {},
                DebugUserId: usingCif ? this.debugCif.trim() : this.debugUserId,
                VoiceMode: this.debugVoiceMode,
                VoiceLang: this.debugVoiceLang,
                ContinueQuestion: this.debugProactiveMessage?.trim() || null,
                Cif: usingCif ? this.debugCif.trim() : null,
                AccessLevel: this.debugAccessLevel,
            };
            // Remember debug identity preference + last simulated CIF
            localStorage.setItem('debug.useCif', this.debugUseCif ? 'true' : 'false');
            if (this.debugCif?.trim()) localStorage.setItem('debug.lastCif', this.debugCif.trim());

            try {
                const response = await this.$call.DebugFlowSSE(request);
                if (!response.ok) {
                    this.debugStatus = 'error';
                    this.debugError = `HTTP ${response.status}: ${response.statusText}`;
                    return;
                }
                // Process SSE stream
                const reader = response.body.getReader();
                this.debugSseReader = reader;
                const decoder = new TextDecoder();
                let buffer = '';

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    buffer += decoder.decode(value, { stream: true });

                    // Parse SSE events from buffer
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || ''; // Keep incomplete line in buffer
                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            const jsonStr = line.substring(6).trim();
                            if (jsonStr) {
                                try {
                                    const event = JSON.parse(jsonStr);
                                    this.handleDebugEvent(event);
                                } catch (e) {
                                    console.warn('Debug SSE parse error:', e, jsonStr);
                                }
                            }
                        }
                    }
                }
                // Stream ended
                if (this.debugStatus === 'running') {
                    this.debugStatus = 'completed';
                }
            } catch (err) {
                if (err.name === 'AbortError') {
                    this.debugStatus = 'stopped';
                } else {
                    console.error('Debug SSE error:', err);
                    this.debugStatus = 'error';
                    this.debugError = err.message;
                }
            }
        },
        handleDebugEvent(event) {
            const type = event.EventType || event.eventType;
            const nodeId = event.NodeId || event.nodeId;
            const nodeName = event.NodeName || event.nodeName;
            const nodeType = event.NodeType || event.nodeType;

            switch (type) {
                case 'session_start':
                    this.debugSessionId = event.SessionId || event.sessionId;
                    break;

                case 'node_start':
                    this.debugPreviousNodeId = this.debugCurrentNodeId;
                    this.debugCurrentNodeId = nodeId;
                    // Update node state
                    this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: 'running' };
                    // Prefer the exact output handle chosen by the previous step when multiple edges share the same target.
                    this.markExecutionEdge(this.debugPreviousNodeId, nodeId, 'active');
                    // Add step to timeline
                    this.debugSteps = [...this.debugSteps, {
                        eventType: type,
                        nodeId,
                        nodeName: nodeName || nodeType,
                        nodeType,
                        stepIndex: event.StepIndex || event.stepIndex,
                        startTime: event.StartTime || event.startTime,
                    }];
                    this.debugStepCount = event.StepIndex || event.stepIndex || this.debugStepCount;
                    // Auto-pan to current node
                    this.focusOnNode(nodeId);
                    break;

                // Voice-mode debug: GPT token chunks streamed as they arrive (mirrors ChatBotLHB voice).
                // Append each token so the reply bubble fills gradually.
                case 'node_token':
                    {
                        const token = event.GptReply || event.gptReply || '';
                        if (!token) break;
                        // New streaming node → start its reply fresh (bubble shows the latest node's reply, like node_complete).
                        if (this.debugStreamingNodeId !== nodeId) {
                            this.debugStreamingNodeId = nodeId;
                            this.debugLastBotReply = '';
                        }
                        this.debugStreaming = true;
                        this.debugLastBotReply += token;
                        // Append to the in-progress node_start step's gptReply
                        const idx = this.debugSteps.findLastIndex(s => s.nodeId === nodeId && s.eventType === 'node_start');
                        if (idx >= 0) {
                            const updated = [...this.debugSteps];
                            updated[idx] = { ...updated[idx], gptReply: (updated[idx].gptReply || '') + token };
                            this.debugSteps = updated;
                        }
                    }
                    break;

                case 'node_complete':
                    this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: 'success' };
                    this.markExecutionEdge(this.debugPreviousNodeId, nodeId, 'completed');
                    // Streaming for this node finished — reconcile with the authoritative full reply below.
                    this.debugStreaming = false;
                    this.debugStreamingNodeId = null;
                    // Track latest gptReply as it arrives
                    {
                        const gptReply = event.GptReply || event.gptReply;
                        if (gptReply) this.debugLastBotReply = gptReply;
                    }
                    // Merge into existing node_start step instead of adding new entry
                    {
                        const existingIdx = this.debugSteps.findLastIndex(s => s.nodeId === nodeId && s.eventType === 'node_start');
                        if (existingIdx >= 0) {
                            const updated = [...this.debugSteps];
                            updated[existingIdx] = {
                                ...updated[existingIdx],
                                eventType: type,
                                durationMs: event.DurationMs || event.durationMs,
                                output: event.Output || event.output,
                                outputPath: event.OutputPath || event.outputPath,
                                gptReply: event.GptReply || event.gptReply,
                                nextNodeId: event.NextNodeId || event.nextNodeId,
                                contextVariables: event.ContextVariables || event.contextVariables,
                                success: event.Success !== undefined ? event.Success : event.success,
                            };
                            this.debugSteps = updated;
                        } else {
                            this.debugSteps = [...this.debugSteps, {
                                eventType: type,
                                nodeId,
                                nodeName: nodeName || nodeType,
                                nodeType,
                                stepIndex: event.StepIndex || event.stepIndex,
                                durationMs: event.DurationMs || event.durationMs,
                                output: event.Output || event.output,
                                outputPath: event.OutputPath || event.outputPath,
                                gptReply: event.GptReply || event.gptReply,
                                nextNodeId: event.NextNodeId || event.nextNodeId,
                                contextVariables: event.ContextVariables || event.contextVariables,
                                success: event.Success !== undefined ? event.Success : event.success,
                            }];
                        }
                    }
                    // Update variables
                    if (event.ContextVariables || event.contextVariables) {
                        const vars = event.ContextVariables || event.contextVariables;
                        const prevVarKeys = Object.keys(this.debugVariables);
                        const changed = new Set();
                        for (const key of Object.keys(vars)) {
                            if (!prevVarKeys.includes(key) || JSON.stringify(this.debugVariables[key]) !== JSON.stringify(vars[key])) {
                                changed.add(key);
                            }
                        }
                        this.debugVariables = { ...vars };
                        this.debugChangedVariables = changed;
                    }
                    break;

                case 'node_error':
                    this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: 'error' };
                    // Merge into existing node_start step
                    {
                        const existingIdx = this.debugSteps.findLastIndex(s => s.nodeId === nodeId && s.eventType === 'node_start');
                        if (existingIdx >= 0) {
                            const updated = [...this.debugSteps];
                            updated[existingIdx] = {
                                ...updated[existingIdx],
                                eventType: type,
                                errorMessage: event.ErrorMessage || event.errorMessage,
                                success: false,
                            };
                            this.debugSteps = updated;
                        } else {
                            this.debugSteps = [...this.debugSteps, {
                                eventType: type,
                                nodeId,
                                nodeName: nodeName || nodeType,
                                nodeType,
                                stepIndex: event.StepIndex || event.stepIndex,
                                errorMessage: event.ErrorMessage || event.errorMessage,
                                success: false,
                            }];
                        }
                    }
                    break;

                case 'breakpoint_hit':
                    this.debugStatus = 'paused';
                    this.debugCurrentNodeId = nodeId;
                    this.debugPausedNodeName = nodeName || nodeType || '';
                    this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: 'paused' };
                    this.debugSteps = [...this.debugSteps, {
                        eventType: type,
                        nodeId,
                        nodeName: nodeName || nodeType,
                        nodeType,
                        stepIndex: event.StepIndex || event.stepIndex,
                    }];
                    this.focusOnNode(nodeId);
                    break;

                case 'agent_start':
                    // Agent sub-step inside orchestrator — add to timeline & highlight on canvas
                    {
                        const parentNodeId = event.ParentNodeId || event.parentNodeId;
                        const agentName = event.AgentName || event.agentName || nodeName;
                        // Highlight agent node on canvas (blue running glow)
                        this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: 'running' };
                        // Animate edge from orchestrator to this agent
                        this.markEdgeBetween(parentNodeId, nodeId, 'active');
                        this.debugSteps = [...this.debugSteps, {
                            eventType: type,
                            nodeId,
                            nodeName: agentName,
                            nodeType: 'agent',
                            parentNodeId,
                            agentName,
                            startTime: event.StartTime || event.startTime,
                            isSubStep: true,
                        }];
                        // Auto-pan to agent node
                        this.focusOnNode(nodeId);
                    }
                    break;

                case 'tool_start':
                    // Tool/knowledge node starting execution — highlight on canvas
                    {
                        const parentAgentId = event.ParentNodeId || event.parentNodeId;
                        // Set tool node to running state (blue glow)
                        this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: 'running' };
                        // Animate edge from agent to this tool node
                        if (parentAgentId) this.markEdgeBetween(parentAgentId, nodeId, 'active');
                    }
                    break;

                case 'tool_complete':
                    // Tool/knowledge node finished — mark success/error on canvas
                    {
                        const parentAgentId = event.ParentNodeId || event.parentNodeId;
                        const toolSuccess = event.Success !== undefined ? event.Success : event.success;
                        // Update tool node state
                        this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: toolSuccess ? 'success' : 'error' };
                        // Mark edge as completed
                        if (parentAgentId) this.markEdgeBetween(parentAgentId, nodeId, 'completed');
                    }
                    break;

                case 'agent_complete':
                    // Merge into existing agent_start entry & update canvas state
                    {
                        const agentName = event.AgentName || event.agentName || nodeName;
                        const agentSuccess = event.Success !== undefined ? event.Success : event.success;
                        const parentNodeId = event.ParentNodeId || event.parentNodeId;
                        const output = event.Output || event.output || {};

                        // Update agent node on canvas (green success / red error)
                        this.debugNodeStates = { ...this.debugNodeStates, [nodeId]: agentSuccess ? 'success' : 'error' };

                        // Mark edge orchestrator→agent as completed
                        if (parentNodeId) this.markEdgeBetween(parentNodeId, nodeId, 'completed');
                        const existingIdx = this.debugSteps.findLastIndex(s => s.nodeId === nodeId && s.eventType === 'agent_start' && s.agentName === agentName);
                        if (existingIdx >= 0) {
                            const updated = [...this.debugSteps];
                            updated[existingIdx] = {
                                ...updated[existingIdx],
                                eventType: type,
                                durationMs: event.DurationMs || event.durationMs,
                                success: event.Success !== undefined ? event.Success : event.success,
                                gptReply: event.GptReply || event.gptReply,
                                output: output,
                                errorMessage: event.ErrorMessage || event.errorMessage,
                            };
                            this.debugSteps = updated;
                        } else {
                            this.debugSteps = [...this.debugSteps, {
                                eventType: type,
                                nodeId,
                                nodeName: agentName,
                                nodeType: 'agent',
                                parentNodeId: event.ParentNodeId || event.parentNodeId,
                                agentName,
                                isSubStep: true,
                                durationMs: event.DurationMs || event.durationMs,
                                success: event.Success !== undefined ? event.Success : event.success,
                                gptReply: event.GptReply || event.gptReply,
                                output: output,
                                errorMessage: event.ErrorMessage || event.errorMessage,
                            }];
                        }
                    }
                    break;

                case 'flow_complete':
                    this.debugStatus = 'completed';
                    this.debugStreaming = false;
                    this.debugStreamingNodeId = null;
                    this.debugTotalDuration = event.TotalDurationMs || event.totalDurationMs || 0;
                    this.debugStepCount = event.TotalSteps || event.totalSteps || this.debugStepCount;
                    // Capture final reply from flow_complete's GptReply or last step's gptReply
                    {
                        const finalReply = event.GptReply || event.gptReply;
                        if (finalReply) {
                            this.debugLastBotReply = finalReply;
                        } else if (!this.debugLastBotReply) {
                            // Fallback: find last gptReply from steps
                            for (let i = this.debugSteps.length - 1; i >= 0; i--) {
                                if (this.debugSteps[i].gptReply) {
                                    this.debugLastBotReply = this.debugSteps[i].gptReply;
                                    break;
                                }
                            }
                        }
                    }
                    break;

                case 'flow_error':
                    this.debugStatus = 'error';
                    this.debugStreaming = false;
                    this.debugStreamingNodeId = null;
                    this.debugError = event.ErrorMessage || event.errorMessage || 'Unknown error';
                    break;

                case 'variables_patched':
                    // Update local variables view with the patched values from backend
                    if (event.ContextVariables || event.contextVariables) {
                        this.debugVariables = { ...(event.ContextVariables || event.contextVariables) };
                    }
                    break;

                case 'set_next_applied':
                    // Backend confirmed the node jump — reset all states and highlight target
                    {
                        const targetId = event.NextNodeId || event.nextNodeId;
                        // Reset all node states to idle (clear old success/running/paused visuals)
                        const resetStates = {};
                        this.tempNode.forEach(n => { resetStates[n.id] = 'idle'; });
                        if (targetId) {
                            resetStates[targetId] = 'running';
                        }
                        this.debugNodeStates = resetStates;
                        // Reset all edge animations
                        this.debugEdgeStates = {};
                        this.tempEdge.forEach(e => { e.animated = false; });
                        if (targetId) {
                            this.focusOnNode(targetId);
                        }
                    }
                    break;
            }
        },
        findDebugOutputPathForTransition(sourceNodeId, targetNodeId) {
            if (!sourceNodeId || !targetNodeId) return null;

            const previousStep = [...this.debugSteps].reverse().find(step =>
                step.nodeId === sourceNodeId &&
                step.eventType === 'node_complete' &&
                step.nextNodeId === targetNodeId &&
                step.outputPath
            );

            return previousStep?.outputPath || null;
        },
        markExecutionEdge(sourceNodeId, targetNodeId, state) {
            const outputPath = this.findDebugOutputPathForTransition(sourceNodeId, targetNodeId);
            this.markEdgeBetween(sourceNodeId, targetNodeId, state, outputPath);
        },
        markEdgeBetween(sourceNodeId, targetNodeId, state, sourceHandle = null) {
            // Prefer exact sourceHandle/outputPath matching so router-like nodes only animate the chosen branch.
            const newEdgeStates = { ...this.debugEdgeStates };

            let matchedExactEdge = false;
            this.tempEdge.forEach(edge => {
                const matchesNodePair = edge.target === targetNodeId && (!sourceNodeId || edge.source === sourceNodeId);
                const matchesHandle = !sourceHandle || edge.sourceHandle === sourceHandle;

                if (matchesNodePair && matchesHandle) {
                    newEdgeStates[edge.id] = state;
                    edge.animated = (state === 'active');
                    if (sourceHandle) {
                        matchedExactEdge = true;
                    }
                }
            });

            if (sourceHandle && !matchedExactEdge) {
                this.tempEdge.forEach(edge => {
                    if (edge.target === targetNodeId && (!sourceNodeId || edge.source === sourceNodeId)) {
                        newEdgeStates[edge.id] = state;
                        edge.animated = (state === 'active');
                    }
                });
            }

            this.debugEdgeStates = newEdgeStates;
        },
        async resumeDebugExecution() {
            if (!this.debugSessionId || this.debugStatus !== 'paused') return;
            this.debugStatus = 'running';
            this.debugPausedNodeName = '';
            this.debugHoveredNodeId = '';  // Clear hover overlay on resume
            // If breakpoint node was paused, reset it to running
            if (this.debugCurrentNodeId) {
                this.debugNodeStates = { ...this.debugNodeStates, [this.debugCurrentNodeId]: 'running' };
            }
            try {
                // Build resume payload with patches collected while paused
                const payload = {
                    Breakpoints: Array.from(this.debugBreakpoints),
                };

                // Include variable patches if any were edited
                if (Object.keys(this.debugPatchVariables).length > 0) {
                    payload.PatchVariables = { ...this.debugPatchVariables };
                }

                // Include node jump override if set
                if (this.debugOverrideNextNodeId) {
                    payload.OverrideNextNodeId = this.debugOverrideNextNodeId;
                }

                this.$call.DebugResume(this.debugSessionId, payload, () => {
                    // Resume acknowledged — clear patches
                    this.debugPatchVariables = {};
                    this.debugOverrideNextNodeId = '';
                }, (err) => {
                    console.error('Resume error:', err);
                });
            } catch (err) {
                console.error('Resume failed:', err);
            }
        },
        async stopDebugExecution() {
            if (!this.debugSessionId) {
                this.debugStatus = 'stopped';
                return;
            }
            try {
                // Cancel the SSE reader
                if (this.debugSseReader) {
                    try { this.debugSseReader.cancel(); } catch (e) { /* ignore */ }
                    this.debugSseReader = null;
                }
                this.$call.DebugStop(this.debugSessionId, () => {
                    this.debugStatus = 'stopped';
                }, (err) => {
                    console.error('Stop error:', err);
                    this.debugStatus = 'stopped';
                });
            } catch (err) {
                console.error('Stop failed:', err);
                this.debugStatus = 'stopped';
            }
        },
        toggleDebugBreakpoint(nodeId) {
            const newBps = new Set(this.debugBreakpoints);
            if (newBps.has(nodeId)) {
                newBps.delete(nodeId);
            } else {
                newBps.add(nodeId);
            }
            this.debugBreakpoints = newBps;
        },
        focusOnNode(nodeId) {
            if (!nodeId || !this.$refs.vueFlow) return;
            try {
                const node = this.tempNode.find(n => n.id === nodeId);
                if (node && node.position) {
                    const vf = this.$refs.vueFlow;
                    if (vf.setCenter) {
                        vf.setCenter(node.position.x + 60, node.position.y + 50, { duration: 400, zoom: 1.5 });
                    }
                }
            } catch (e) {
                console.warn('focusOnNode error:', e);
            }
        },
        // ---- Debug patching handlers (called from DebugInspector events) ----
        onDebugPatchVariable({ key, value }) {
            // Collect variable patches while paused — sent on resume
            this.debugPatchVariables = { ...this.debugPatchVariables, [key]: value };
        },
        onDebugSetNext(nodeId) {
            // Toggle: click same node again to cancel
            if (this.debugOverrideNextNodeId === nodeId) {
                this.debugOverrideNextNodeId = '';
                this.$toast?.info?.('Set Next Statement cancelled')
                    || console.log('[Debug] Set Next cancelled');
                return;
            }
            // Don't allow jumping to the currently paused node
            if (nodeId === this.debugCurrentNodeId) {
                this.$toast?.warning?.('Already paused at this node')
                    || console.log('[Debug] Already at this node');
                return;
            }
            // Set the node to jump to on next resume
            this.debugOverrideNextNodeId = nodeId;
            // Visual feedback: show which node is the jump target
            const node = this.tempNode.find(n => n.id === nodeId);
            const nodeName = node?.data?.label || node?.data?.name || nodeId;
            this.$toast?.info?.(`Set Next Statement → ${nodeName}`)
                || console.log(`[Debug] Override next node set to: ${nodeName}`);
        },
        onDebugCancelSetNext() {
            this.debugOverrideNextNodeId = '';
        },
        // ============================================
        // END DEBUG MODE METHODS
        // ============================================

        convertToFlatStructure() {
            const flatNodes = [];

            // First pass: create all nodes without targets
            this.tempNode.forEach(node => {
                const flatNode = {
                    id: node.id,
                    nodeType: node.data.type,
                    position: node.position,
                    data: this.extractNodeData(node),
                    targets: []
                };
                flatNodes.push(flatNode);
            });

            // Second pass: populate targets based on edges
            this.tempEdge.forEach(edge => {
                const sourceNode = flatNodes.find(node => node.id === edge.source);
                if (sourceNode) {
                    const targetInfo = {
                        nodeId: edge.target,
                        sourceHandle: edge.sourceHandle || null,
                        targetHandle: edge.targetHandle || null
                    };
                    sourceNode.targets.push(targetInfo);
                }
            });

            return flatNodes;
        },
        convertFromFlatStructure(flatNodes) {
            const flowNodes = [];
            const flowEdges = [];

            flatNodes.forEach(flatNode => {
                // Parse string data back to objects (handle both string and object for backward compatibility)
                const position = typeof flatNode.position === 'string'
                    ? JSON.parse(flatNode.position)
                    : flatNode.position;

                const data = typeof flatNode.data === 'string'
                    ? JSON.parse(flatNode.data)
                    : flatNode.data;

                const targets = typeof flatNode.targets === 'string'
                    ? JSON.parse(flatNode.targets)
                    : (flatNode.targets || []);

                // Create flow node
                const expandedData = this.expandNodeData(flatNode.nodeType, data);

                const flowNode = {
                    id: flatNode.id,
                    type: 'flow-node',
                    position: position,
                    data: {
                        type: flatNode.nodeType,
                        label: data.label,
                        ...expandedData
                    },
                    selected: false
                };
                flowNodes.push(flowNode);

                // Create edges from targets
                if (targets && Array.isArray(targets)) {
                    targets.forEach((target, index) => {
                        const edge = {
                            id: `e${flatNode.id}-${target.nodeId}-${this.generateGuid()}`,
                            source: flatNode.id,
                            target: target.nodeId,
                            sourceHandle: target.sourceHandle || null,
                            targetHandle: target.targetHandle || 'input',
                            type: 'flow-edge',
                            markerEnd: MarkerType.ArrowClosed,
                            animated: false,
                            showButton: false
                        };

                        flowEdges.push(edge);
                    });
                }
            });

            return { nodes: flowNodes, edges: flowEdges };
        },

        findRootNodes() {
            const allNodeIds = new Set(this.tempNode.map(node => node.id));
            const targetNodeIds = new Set();

            // Collect all target node IDs from edges
            this.tempEdge.forEach(edge => {
                targetNodeIds.add(edge.target);
            });

            // Root nodes are those that are not targets of any edge
            const rootNodes = Array.from(allNodeIds).filter(nodeId => !targetNodeIds.has(nodeId));

            return rootNodes;
        },

        // Recursively build nested node structure
        buildNestedNode(nodeId, nodeMap, processedNodes) {
            if (processedNodes.has(nodeId)) {
                return null; // Avoid circular references
            }

            processedNodes.add(nodeId);
            const node = nodeMap.get(nodeId);

            if (!node) {
                return null;
            }

            // Find all outgoing edges from this node
            const outgoingEdges = this.tempEdge.filter(edge => edge.source === nodeId);

            // Group edges by source handle for intent nodes with multiple outputs
            const edgeGroups = this.groupEdgesBySourceHandle(outgoingEdges, node);

            // Build target nodes
            node.target = edgeGroups.map(group => {
                const targetNodes = group.edges.map(edge => {
                    const targetNode = this.buildNestedNode(edge.target, nodeMap, new Set(processedNodes));
                    if (targetNode) {
                        // Add source information to target node
                        targetNode.source = group.sourceHandle ? `${nodeId}.${group.sourceHandle}` : nodeId;
                    }
                    return targetNode;
                }).filter(targetNode => targetNode !== null);

                return targetNodes;
            }).flat();

            return node;
        },
        // Group edges by source handle (for nodes with multiple outputs like intent, switch)
        groupEdgesBySourceHandle(edges, sourceNode) {
            if (sourceNode.nodeType === 'intent' || sourceNode.nodeType === 'switch') {
                // Group by source handle
                const groups = new Map();

                edges.forEach(edge => {
                    const sourceHandle = edge.sourceHandle || 'default';
                    if (!groups.has(sourceHandle)) {
                        groups.set(sourceHandle, {
                            sourceHandle: sourceHandle === 'default' ? null : sourceHandle,
                            edges: []
                        });
                    }
                    groups.get(sourceHandle).edges.push(edge);
                });

                return Array.from(groups.values());
            } else {
                // Single group for nodes with single output
                return [{
                    sourceHandle: null,
                    edges: edges
                }];
            }
        },
        // Extract node-specific data based on node type
        getSummaryMemoryData(data) {
            return {
                summaryMemoryEnabled: data.summaryMemoryEnabled ?? false,
                summaryMemoryProvider: data.summaryMemoryProvider || null,
                summaryMemoryModel: data.summaryMemoryModel || null,
                summaryMemoryApiVersion: data.summaryMemoryApiVersion || null,
                summaryMemoryJsonRequest: data.summaryMemoryJsonRequest || null,
                summaryMemorySystemPrompt: data.summaryMemorySystemPrompt || '',
                summaryMemoryTemperature: data.summaryMemoryTemperature ?? 0,
                summaryMemoryMaxTokens: data.summaryMemoryMaxTokens ?? 500,
                summaryMemoryTopP: data.summaryMemoryTopP ?? 1,
                summaryMemoryFrequencyPenalty: data.summaryMemoryFrequencyPenalty ?? null,
                summaryMemoryPresencePenalty: data.summaryMemoryPresencePenalty ?? null,
                summaryMemoryMemoryTurn: data.summaryMemoryMemoryTurn ?? 0,
                summaryMemoryMemoryDuration: data.summaryMemoryMemoryDuration ?? 60,
                summaryMemoryTimeout: data.summaryMemoryTimeout ?? 0,
            };
        },
        // Extract node-specific data based on node type
        extractNodeData(node) {
            const baseData = {
                // label: node.data.label
            };

            switch (node.data.type) {
                case 'messageInput':
                    return {
                        ...baseData,
                        modelDisplayId: [
                            ...(Array.isArray(node.data.selectedModel)
                                ? node.data.selectedModel
                                : (node.data.selectedModel ? [node.data.selectedModel] : [])),
                            
                        ],
                        unableToAccessModel: [
                            ...(Array.isArray(this.unableToAccessModel)
                                ? this.unableToAccessModel
                                : (this.unableToAccessModel ? [this.unableToAccessModel] : []))
                        ]
                    };

                case 'model':
                    return {
                        ...baseData,
                        // Custom label
                        customLabel: node.data.customLabel,
                        // Basic model configuration
                        provider: node.data.provider,
                        model: node.data.model,
                        apiVersion: node.data.apiVersion,
                        modelDisplayId: node.data.selectedModel,
                        systemPrompt: node.data.systemPrompt,
                        useBaseModelPrompt: node.data.useBaseModelPrompt,

                        // AI Parameters
                        temperature: node.data.temperature,
                        maxTokens: node.data.maxTokens,
                        topP: node.data.topP,
                        frequencyPenalty: node.data.frequencyPenalty,
                        presencePenalty: node.data.presencePenalty,
                        stopSequence: node.data.stopSequence,

                        // Behavior Settings
                        gender: node.data.gender,
                        typingSpeed: node.data.typingSpeed,
                        maxInputLength: node.data.maxInputLength,

                        // Response Options
                        streamResponse: node.data.streamResponse,
                        jsonResponse: node.data.jsonResponse,
                        bypassOnError: node.data.bypassOnError !== undefined ? node.data.bypassOnError : false,
                        enableTime: node.data.enableTime,
                        timeZone: node.data.timeZone,
                        enableTransactionId: node.data.enableTransactionId,

                        // Prompt Replacement Flags
                        enableInputMessageReplace: node.data.enableInputMessageReplace,
                        enableVoicePromptReplace: node.data.enableVoicePromptReplace,
                        enableLanguageReplace: node.data.enableLanguageReplace,

                        // Memory Settings — plain minutes; the backend resolves the window (MemoryWindow.Cutoff), no sign games
                        memoryTurn: node.data.memoryTurn,
                        memoryDuration: node.data.memoryDuration,
                        shortTermMemory: node.data.shortTermMemory,
                        longTermMemory: node.data.longTermMemory,
                        isChainMassage: node.data.isChainMassage,
                        lenChainMassage: node.data.lenChainMassage,
                        timeSecondChainMassage: node.data.timeSecondChainMassage,

                        // Line Configuration
                        loadingBar: node.data.loadingBar,
                        loadingBarDuration: node.data.loadingBarDuration,
                        showLineName: node.data.showLineName,
                        monitorLineUserId: node.data.monitorLineUserId,
                        monitorSocialID: node.data.monitorSocialID,
                        forbiddenWords: node.data.forbiddenWords,

                        // Cache Settings
                        enableCache: node.data.enableCache || false,
                        cacheTTLMinutes: node.data.cacheTTLMinutes || 1440,
                        cacheScope: node.data.cacheScope || 'flow',
                        enableSemanticCache: node.data.enableSemanticCache || false,
                        similarityThreshold: node.data.similarityThreshold || 0.95,
                        similarityMargin: node.data.similarityMargin || 0.05,

                        // Per-node timeout override
                        nodeTimeout: node.data.nodeTimeout || 0,
                        nodeMaxRetry: node.data.nodeMaxRetry || 0
                    };

                case 'taxPlanning':
                    return {
                        ...baseData,
                        // Node label
                        taxPlanningName: node.data.taxPlanningName,
                        // LLM configuration (same keys as AI Model)
                        provider: node.data.provider,
                        model: node.data.model,
                        apiVersion: node.data.apiVersion,
                        modelDisplayId: node.data.selectedModel,
                        systemPrompt: node.data.systemPrompt,
                        // 🆕 node-level split prompts (Question = asking, Answer = extracting)
                        questionSystemPrompt: node.data.questionSystemPrompt,
                        answerSystemPrompt: node.data.answerSystemPrompt,
                        // 🆕 Optional separate model for question-phrasing steps only (falls back to main provider/model)
                        taxPlanningUseQuestionModel: node.data.taxPlanningUseQuestionModel,
                        taxPlanningQuestionProvider: node.data.taxPlanningQuestionProvider,
                        taxPlanningQuestionModel: node.data.taxPlanningQuestionModel,
                        // 🆕 Use Base Model Prompt (node-level) — prepend MainModel prompt when phrasing every tax question.
                        // MUST be persisted here or the toggle is silently dropped on save and GetNodeData<bool> always reads false.
                        useBaseModelPrompt: node.data.useBaseModelPrompt,
                        // 🆕 Bot voice gender (ชาย=ครับ / หญิง=ค่ะ) — drives convertgender on the reply. MUST be persisted
                        // here or it is dropped on save and the backend defaults to "m", so the Female setting has no effect.
                        gender: node.data.gender,
                        // Prompt context flags (mirror AI Model node)
                        enableTime: node.data.enableTime,
                        timeZone: node.data.timeZone,
                        enableTransactionId: node.data.enableTransactionId,
                        // Prompt Replacement Flags
                        enableInputMessageReplace: node.data.enableInputMessageReplace,
                        enableVoicePromptReplace: node.data.enableVoicePromptReplace,
                        enableLanguageReplace: node.data.enableLanguageReplace,
                        temperature: node.data.temperature,
                        maxTokens: node.data.maxTokens,
                        topP: node.data.topP,
                        frequencyPenalty: node.data.frequencyPenalty,
                        presencePenalty: node.data.presencePenalty,
                        stopSequence: node.data.stopSequence,
                        memoryTurn: node.data.memoryTurn,
                        memoryDuration: node.data.memoryDuration,
                        streamResponse: node.data.streamResponse,
                        jsonResponse: node.data.jsonResponse,
                        bypassOnError: node.data.bypassOnError !== undefined ? node.data.bypassOnError : false,
                        nodeTimeout: node.data.nodeTimeout || 0,
                        nodeMaxRetry: node.data.nodeMaxRetry || 0,
                        // Tax-specific configuration
                        taxPlanningYearMode: node.data.taxPlanningYearMode,
                        taxPlanningYearId: node.data.taxPlanningYearId,
                        taxPlanningAskWhenMultipleActive: node.data.taxPlanningAskWhenMultipleActive,
                        taxPlanningFlowMode: node.data.taxPlanningFlowMode,
                        taxPlanningPersist: node.data.taxPlanningPersist,
                        taxPlanningCompletionMessage: node.data.taxPlanningCompletionMessage
                    };

                case 'taxPlanningV2':
                    return {
                        ...baseData,
                        // Node label
                        taxPlanningV2Name: node.data.taxPlanningV2Name,
                        // LLM configuration — SHARED keys, also used by model/llmEnrich/reformatTTS/…
                        // The V2 node no longer reads them at runtime (LLM config comes from the
                        // resolved settings version) but they must stay in this whitelist so legacy
                        // node data round-trips instead of being wiped on the next save.
                        provider: node.data.provider,
                        model: node.data.model,
                        apiVersion: node.data.apiVersion,
                        modelDisplayId: node.data.selectedModel,
                        systemPrompt: node.data.systemPrompt,
                        useBaseModelPrompt: node.data.useBaseModelPrompt,
                        gender: node.data.gender,
                        enableTime: node.data.enableTime,
                        timeZone: node.data.timeZone,
                        enableTransactionId: node.data.enableTransactionId,
                        temperature: node.data.temperature,
                        maxTokens: node.data.maxTokens,
                        topP: node.data.topP,
                        frequencyPenalty: node.data.frequencyPenalty,
                        presencePenalty: node.data.presencePenalty,
                        stopSequence: node.data.stopSequence,
                        memoryTurn: node.data.memoryTurn,
                        memoryDuration: node.data.memoryDuration,
                        streamResponse: node.data.streamResponse,
                        jsonResponse: node.data.jsonResponse,
                        bypassOnError: node.data.bypassOnError !== undefined ? node.data.bypassOnError : false,
                        nodeTimeout: node.data.nodeTimeout || 0,
                        nodeMaxRetry: node.data.nodeMaxRetry || 0,
                        // Tax-specific configuration (V2) now lives in `tax.TaxPlanningSettingsV2`
                        // as versioned rows. The only tax key left on the node is which settings
                        // version it pins (null = use the active version). This whitelist is the
                        // save payload — leaving the key out silently drops the user's choice.
                        taxPlanningV2SettingsVersionId: node.data.taxPlanningV2SettingsVersionId ?? null
                    };

                case 'intent':
                    return {
                        ...baseData,
                        intentGroupName: node.data.intentGroupName,
                        intents: node.data.intents || []
                    };

                case 'smartIntent':
                    return {
                        ...baseData,
                        smartIntentGroupName: node.data.smartIntentGroupName,
                        smartIntents: node.data.smartIntents || [],
                        smartIntentFallback: node.data.smartIntentFallback || {}
                    };

                case 'switch':
                    return {
                        ...baseData,
                        switchName: node.data.switchName,
                        switchOutputs: node.data.switchOutputs || []
                    };

                case 'router':
                    return {
                        ...baseData,
                        routerName: node.data.routerName,
                        routerPrompt: node.data.routerPrompt,
                        confidenceThreshold: node.data.confidenceThreshold,
                        routerOutputs: node.data.routerOutputs || []
                    };

                case 'advancedRouter':
                    return {
                        ...baseData,
                        advancedRouterName: node.data.advancedRouterName,
                        advancedRouterPrompt: node.data.advancedRouterPrompt,
                        advancedRouterConfidenceThreshold: node.data.advancedRouterConfidenceThreshold,
                        advancedRouterMaxDepth: node.data.advancedRouterMaxDepth,
                        advancedRouterOutputs: node.data.advancedRouterOutputs || [],
                        customSchemaEnabled: node.data.customSchemaEnabled ?? false,
                        customSchemaTemplate: node.data.customSchemaTemplate || this.getDefaultAdvancedRouterSchemaTemplate(),
                        schemaFieldMapping: node.data.schemaFieldMapping || {
                            intentField: 'capability_intent',
                            confidenceField: 'confidence',
                            subIntentsField: 'intents'
                        },
                        enableCache: node.data.enableCache || false,
                        cacheTTLMinutes: node.data.cacheTTLMinutes || 1440,
                        enableSemanticCache: node.data.enableSemanticCache || false,
                        similarityThreshold: node.data.similarityThreshold || 0.95,
                        similarityMargin: node.data.similarityMargin || 0.05,
                    };

                case 'dynamicRouter':
                    return {
                        ...baseData,
                        dynamicRouterName: node.data.dynamicRouterName,
                        dynamicRouterPrompt: node.data.dynamicRouterPrompt,
                        dynamicRouterConfidenceThreshold: node.data.dynamicRouterConfidenceThreshold,
                        dynamicRouterMaxDepth: node.data.dynamicRouterMaxDepth,
                        dynamicRouterOutputs: node.data.dynamicRouterOutputs || [],
                    };

                case 'dynamicKnowledge':
                    return {
                        ...baseData,
                        useMetaCommunityGraphSubCat: node.data.useMetaCommunityGraphSubCat ?? node.data.dynamicKnowledgeUseMetaCommunityGraphSubCat ?? false,
                        topNChunks: node.data.topNChunks || 5,
                        maxExplicitDocs: node.data.maxExplicitDocs ?? 10,
                        // Summary Memory (Embedding) properties
                summaryMemoryEnabled: node.data.summaryMemoryEnabled ?? false,
                summaryMemoryProvider: node.data.summaryMemoryProvider || null,
                summaryMemoryModel: node.data.summaryMemoryModel || null,
                summaryMemoryApiVersion: node.data.summaryMemoryApiVersion || null,
                summaryMemoryJsonRequest: node.data.summaryMemoryJsonRequest || null,
                summaryMemorySystemPrompt: node.data.summaryMemorySystemPrompt || '',
                summaryMemoryTemperature: node.data.summaryMemoryTemperature ?? 0,
                summaryMemoryMaxTokens: node.data.summaryMemoryMaxTokens ?? 500,
                summaryMemoryTopP: node.data.summaryMemoryTopP ?? 1,
                summaryMemoryFrequencyPenalty: node.data.summaryMemoryFrequencyPenalty ?? null,
                summaryMemoryPresencePenalty: node.data.summaryMemoryPresencePenalty ?? null,
                summaryMemoryMemoryTurn: node.data.summaryMemoryMemoryTurn ?? 0,
                summaryMemoryMemoryDuration: node.data.summaryMemoryMemoryDuration ?? 60,
                summaryMemoryTimeout: node.data.summaryMemoryTimeout ?? 0,
                        metaCommunityId: node.data.metaCommunityId || null,
                        metaCommunityTopGroups: node.data.metaCommunityTopGroups || 3,
                        metaCommunityTopChunks: node.data.metaCommunityTopChunks || 5,
                        metaCommunitySearchMethod: node.data.metaCommunitySearchMethod || 'topn',
                        metaCommunityMinCosineScore: node.data.metaCommunityMinCosineScore || 0.7,
                        nodeTimeout: node.data.nodeTimeout ?? 0,
                        nodeMaxRetry: node.data.nodeMaxRetry ?? 0,
                    };

                case 'condition':
                    return {
                        ...baseData,
                        conditionType: node.data.conditionType,
                        leftOperand: node.data.leftOperand,
                        operator: node.data.operator,
                        rightOperand: node.data.rightOperand,
                        conditionExpression: node.data.conditionExpression,
                        trueLabel: node.data.trueLabel,
                        falseLabel: node.data.falseLabel,
                        minValue: node.data.minValue,
                        maxValue: node.data.maxValue,
                        expectedValue: node.data.expectedValue
                    };

                case 'jsonCondition':
                    return {
                        ...baseData,
                        jsonConditionName: node.data.jsonConditionName,
                        matchMode: node.data.matchMode,
                        conditions: node.data.conditions || [],
                        matchLabel: node.data.matchLabel,
                        fallbackLabel: node.data.fallbackLabel
                    };

                case 'llmEnrich':
                    return {
                        ...baseData,
                        provider: node.data.provider,
                        model: node.data.model,
                        modelId: node.data.modelId,
                        apiVersion: node.data.apiVersion,
                        systemPrompt: node.data.systemPrompt,
                        temperature: node.data.temperature,
                        maxTokens: node.data.maxTokens,
                        topP: node.data.topP,
                        jsonResponse: node.data.jsonResponse,
                        llmEnrichName: node.data.llmEnrichName,
                        llmEnrichConnectorText: node.data.llmEnrichConnectorText,
                        bypassOnError: node.data.bypassOnError !== undefined ? node.data.bypassOnError : false
                    };

                case 'buttonStyle':
                    return {
                        ...baseData,
                        buttonStyleName: node.data.buttonStyleName,
                        productButtonStyle: node.data.productButtonStyle,
                        serviceButtonStyle: node.data.serviceButtonStyle
                    };

                case 'serviceFallback':
                    return {
                        ...baseData,
                        serviceType: node.data.serviceType,
                        // Primary (success) path — matched navlist. Kept in the saved payload so
                        // custom messages / templates survive a save instead of silently
                        // reverting to the backend defaults.
                        primaryMessageTH: node.data.primaryMessageTH,
                        primaryMessageEN: node.data.primaryMessageEN,
                        primaryMessageCN: node.data.primaryMessageCN,
                        primarySingleMessageTH: node.data.primarySingleMessageTH,
                        primarySingleMessageEN: node.data.primarySingleMessageEN,
                        primarySingleMessageCN: node.data.primarySingleMessageCN,
                        primaryAllowEditOutput: node.data.primaryAllowEditOutput,
                        primaryOutputJson: node.data.primaryOutputJson,
                        // Fallback (no-match) path
                        messageTH: node.data.messageTH,
                        messageEN: node.data.messageEN,
                        messageCN: node.data.messageCN,
                        allowEditOutput: node.data.allowEditOutput,
                        outputJson: node.data.outputJson
                    };

                case 'delay':
                    return {
                        ...baseData,
                        delayType: node.data.delayType,
                        delayValue: node.data.delayValue,
                        delayUnit: node.data.delayUnit,
                        randomMin: node.data.randomMin,
                        randomMax: node.data.randomMax,
                        delayMessage: node.data.delayMessage,
                        showProgress: node.data.showProgress,
                        delayExpression: node.data.delayExpression
                    };

                case 'apiTrigger':
                    return {
                        ...baseData,
                        apiUrl: node.data.apiUrl,
                        httpMethod: node.data.httpMethod || 'GET',
                        textResponse: node.data.textResponse,
                        jsonValidate: node.data.jsonValidate,
                        modelJsonValidator: node.data.modelJsonValidator,
                        listHeadersAPITrigger: node.data.listHeadersAPITrigger,
                        listValidation: node.data.listValidation,
                        useLHBService: node.data.useLHBService || false
                    };

                case 'contactAgent':
                // 🔴 V2 saves the same 4 keys — `default:` below returns {} so a missing case silently drops them.
                case 'contactAgentV2':
                    return {
                        ...baseData,
                        contactAgentMessage: node.data.contactAgentMessage,
                        contactAgentMessageChinese: node.data.contactAgentMessageChinese,
                        contactAgentMessageEnglish: node.data.contactAgentMessageEnglish,
                        botDisableTime: node.data.botDisableTime,
                    };

                case 'guardrailAI':
                    return {
                        ...baseData,
                        guardrailModel: node.data.guardrailModel,
                        guardrailPrompt: node.data.guardrailPrompt,
                        allowAgentGuardrailBypass: node.data.allowAgentGuardrailBypass,
                        safetyThreshold: node.data.safetyThreshold,
                        enableLogging: node.data.enableLogging
                    };

                case 'intentAI':
                    return {
                        ...baseData,
                        // Basic model configuration
                        intentaiProvider: node.data.intentaiProvider,
                        intentaiModel: node.data.intentaiModel,
                        intentAiModelApiVersion: node.data.intentAiModelApiVersion,
                        intentaiSystemPrompt: node.data.intentaiSystemPrompt,
                        intentaiExamples: node.data.intentaiExamples,
                        intentaiConfidence: node.data.intentaiConfidence,
                        intentaiFallbackIntent: node.data.intentaiFallbackIntent,

                        // AI Parameters
                        intentaiTemperature: node.data.intentaiTemperature,
                        intentaiMaxTokens: node.data.intentaiMaxTokens,
                        intentaiTopP: node.data.intentaiTopP,
                        intentaiFrequencyPenalty: node.data.intentaiFrequencyPenalty,
                        intentaiPresencePenalty: node.data.intentaiPresencePenalty,
                        intentaiStopSequence: node.data.intentaiStopSequence,

                        // Behavior Settings
                        intentaiGender: node.data.intentaiGender,
                        intentaiTypingSpeed: node.data.intentaiTypingSpeed,
                        intentaiMaxInputLength: node.data.intentaiMaxInputLength,

                        // Response Options
                        intentaiStreamResponse: node.data.intentaiStreamResponse,
                        intentaiEnableTime: node.data.intentaiEnableTime,
                        intentaiTimeZone: node.data.intentaiTimeZone,
                        intentaiEnableTransactionId: node.data.intentaiEnableTransactionId,
                        intentaiJsonResponse: node.data.intentaiJsonResponse,

                        // Memory Settings — plain minutes; the backend resolves the window (MemoryWindow.Cutoff), no sign games
                        intentaiMemoryTurn: node.data.intentaiMemoryTurn,
                        intentaiMemoryDuration: node.data.intentaiMemoryDuration,
                        intentaiShortTermMemory: node.data.intentaiShortTermMemory,
                        intentaiLongTermMemory: node.data.intentaiLongTermMemory,
                        intentaiIsChainMassage: node.data.intentaiIsChainMassage,
                        intentaiLenChainMassage: node.data.intentaiLenChainMassage,
                        intentaiTimeSecondChainMassage: node.data.intentaiTimeSecondChainMassage,

                        // Line Configuration
                        intentaiShowLineName: node.data.intentaiShowLineName,
                        intentaiLoadingBar: node.data.intentaiLoadingBar,
                        intentaiLoadingBarDuration: node.data.intentaiLoadingBarDuration,
                        intentaiMonitorLineUserId: node.data.intentaiMonitorLineUserId,
                        intentaiMonitorSocialID: node.data.intentaiMonitorSocialID,
                        intentaiForbiddenWords: node.data.intentaiForbiddenWords,

                        // Per-node timeout override
                        intentaiTimeout: node.data.intentaiTimeout || 0,
                        intentaiMaxRetry: node.data.intentaiMaxRetry || 0
                    };

                case 'documents':
                    return {
                        ...baseData,
                        documentSourceType: node.data.documentSourceType || 'doc',
                        documentGroupId: node.data.documentGroupId || null,
                        documentGroupName: node.data.documentGroupName || '',
                        document: node.data.document,
                        textReplacement: node.data.textReplacement,
                        topNChunks: node.data.topNChunks,
                        cosineSimilarity: node.data.cosineSimilarity,
                        // Summary Memory (Embedding) properties
                summaryMemoryEnabled: node.data.summaryMemoryEnabled ?? false,
                summaryMemoryProvider: node.data.summaryMemoryProvider || null,
                summaryMemoryModel: node.data.summaryMemoryModel || null,
                summaryMemoryApiVersion: node.data.summaryMemoryApiVersion || null,
                summaryMemoryJsonRequest: node.data.summaryMemoryJsonRequest || null,
                summaryMemorySystemPrompt: node.data.summaryMemorySystemPrompt || '',
                summaryMemoryTemperature: node.data.summaryMemoryTemperature ?? 0,
                summaryMemoryMaxTokens: node.data.summaryMemoryMaxTokens ?? 500,
                summaryMemoryTopP: node.data.summaryMemoryTopP ?? 1,
                summaryMemoryFrequencyPenalty: node.data.summaryMemoryFrequencyPenalty ?? null,
                summaryMemoryPresencePenalty: node.data.summaryMemoryPresencePenalty ?? null,
                summaryMemoryMemoryTurn: node.data.summaryMemoryMemoryTurn ?? 0,
                summaryMemoryMemoryDuration: node.data.summaryMemoryMemoryDuration ?? 60,
                summaryMemoryTimeout: node.data.summaryMemoryTimeout ?? 0,
                        selectedDocumentData: node.data.selectedDocumentData || [],
                        enableSpecificDataSelection: node.data.enableSpecificDataSelection || false,
                        documentVectorName: '',
                        communityId: node.data.communityId || null,
                        communityTopGroups: node.data.communityTopGroups || 3,
                        communityTopChunks: node.data.communityTopChunks || 5,
                        communitySearchMethod: node.data.communitySearchMethod || 'topn',
                        communityMinCosineScore: node.data.communityMinCosineScore || 0.7,
                        metaCommunityId: node.data.metaCommunityId || null,
                        metaCommunityTopGroups: node.data.metaCommunityTopGroups || 3,
                        metaCommunityTopChunks: node.data.metaCommunityTopChunks || 5,
                        metaCommunitySearchMethod: node.data.metaCommunitySearchMethod || 'topn',
                        metaCommunityMinCosineScore: node.data.metaCommunityMinCosineScore || 0.7,
                        // Topic/SubTopic Filter properties
                        filterTopic: node.data.filterTopic || null,
                        filterSubTopic: node.data.filterSubTopic || null,
                        filterMode: node.data.filterMode || 'include'
                    };

                case 'databaseIntegration':
                    return {
                        ...baseData,
                        tableName: node.data.tableName,
                        sqlExtractionModel: node.data.sqlExtractionModel,
                        promptSqlNoData: node.data.promptSqlNoData,
                        nodeTimeout: node.data.nodeTimeout ?? 0,
                        nodeMaxRetry: node.data.nodeMaxRetry ?? 0
                    };

                // 🔴 This is a WHITELIST, not a spread: a key missing from the object below is dropped from the
                //    UpdateFlow payload and is gone from the flow after the next save — silently, with the
                //    inspector still showing the value until the page is reloaded. Every key the mutualFund
                //    inspector binds with updateNodeProperty() has to appear here.
                case 'mutualFund':
                    return {
                        ...baseData,
                        mutualFundLookupType: node.data.mutualFundLookupType || 'fundCode',
                        mutualFundLookupValue: node.data.mutualFundLookupValue || '',
                        mutualFundComparisonValues: node.data.mutualFundComparisonValues || '',
                        mutualFundFactsheetConfigCode: node.data.mutualFundFactsheetConfigCode || '',
                        mutualFundComparisonConfigCode: node.data.mutualFundComparisonConfigCode || '',
                        mutualFundPerformanceConfigCode: node.data.mutualFundPerformanceConfigCode || '',
                        mutualFundPickerConfigCode: node.data.mutualFundPickerConfigCode || '',
                        mutualFundPickerCompareConfigCode: node.data.mutualFundPickerCompareConfigCode || '',
                        mutualFundDemoMode: node.data.mutualFundDemoMode === true,
                        // Fund resolver — caps. `??` keeps a false switch and a 0 the operator typed.
                        // The scope/limit trio no longer has a field in the inspector and nothing reads it at
                        // run time, but it is still written back so an older flow survives a re-save (A6).
                        mutualFundResolverEnabled: node.data.mutualFundResolverEnabled ?? false,
                        mutualFundScopeTaxSavingFunds: node.data.mutualFundScopeTaxSavingFunds || '',
                        mutualFundScopeRecommendedOnly: node.data.mutualFundScopeRecommendedOnly ?? false,
                        mutualFundCandidateLimit: node.data.mutualFundCandidateLimit ?? 200,
                        mutualFundTopN: node.data.mutualFundTopN ?? 10,
                        mutualFundMaxSelect: node.data.mutualFundMaxSelect ?? 3,
                        // #924 fuzzy search (`??`: a false switch must survive the save)
                        mutualFundFuzzyEnabled: node.data.mutualFundFuzzyEnabled ?? false,
                        mutualFundFuzzyMinSimilarity: node.data.mutualFundFuzzyMinSimilarity ?? 0.78,
                        mutualFundFuzzyTopGap: node.data.mutualFundFuzzyTopGap ?? 0.1,
                        // Resolver prompts. Saved raw (no `|| default`): blank is a meaningful value here — it
                        // is how an operator says "go back to the built-in", and the backend's ResolvePrompt
                        // treats blank/whitespace as exactly that.
                        systemPrompt: node.data.systemPrompt,
                        mutualFundExtractionTemplate: node.data.mutualFundExtractionTemplate,
                        // Retired, still persisted so a flow saved before the extractor change round-trips.
                        mutualFundSelectionTemplate: node.data.mutualFundSelectionTemplate,
                        mutualFundFundListItemTemplate: node.data.mutualFundFundListItemTemplate,
                        mutualFundNoDescriptionLabel: node.data.mutualFundNoDescriptionLabel,
                        // LLM settings — shared (unprefixed) keys, same set llmEnrich saves, so changeProvider()
                        // and the apiVersion auto-fill keep working for this node for free.
                        provider: node.data.provider,
                        model: node.data.model,
                        apiVersion: node.data.apiVersion,
                        temperature: node.data.temperature,
                        topP: node.data.topP,
                        maxTokens: node.data.maxTokens
                    };

                case 'products':
                    return {
                        ...baseData,
                        productSourceType: node.data.productSourceType || 'product',
                        productGroupCollectionId: node.data.productGroupCollectionId || null,
                        productGroupCollectionName: node.data.productGroupCollectionName || '',
                        productGroupList: Array.isArray(node.data.productGroupList)
                            ? node.data.productGroupList.map(item => ({
                                id: item.id,
                                name: item.name
                            }))
                            : [],
                        productTopNChunks: node.data.productTopNChunks,
                        productCosineSimilarity: node.data.productCosineSimilarity
                    };

                case 'apiknowledge':
                    return {
                        ...baseData,
                        knowledgeApiUrl: node.data.knowledgeApiUrl,
                        knowledgeHttpMethod: node.data.knowledgeHttpMethod || 'GET',
                        knowledgeRequestBody: node.data.knowledgeRequestBody,
                        responseDataPath: node.data.responseDataPath,
                        useLHBService: node.data.useLHBService || false,
                        listHeaders: node.data.listHeaders || [],
                    };

                case 'apiRequest':
                    return {
                        ...baseData,
                        apiRequestUrl: node.data.apiRequestUrl,
                        apiRequestMethod: node.data.apiRequestMethod || 'GET',
                        apiRequestHeaders: node.data.apiRequestHeaders || [],
                        apiRequestBody: node.data.apiRequestBody || '',
                        apiRequestResponsePath: node.data.apiRequestResponsePath || '',
                        useLHBService: node.data.useLHBService || false,
                        useResultAsMessage: node.data.useResultAsMessage || false,
                        timeout: node.data.timeout || 30,
                    };

                case 'favoriteMatcher':
                    return {
                        ...baseData,
                        serviceType: node.data.serviceType || 'TRANSFER',
                        favoritesSourceVariable: node.data.favoritesSourceVariable || 'apiRequestResponse',
                        outputVariable: node.data.outputVariable || 'userMessage',
                        enableEmbedding: node.data.enableEmbedding ?? true,
                        fuzzyThreshold: node.data.fuzzyThreshold ?? 0.85,
                        embeddingThreshold: node.data.embeddingThreshold ?? 0.65,
                        maxResults: node.data.maxResults ?? 0,
                    };

                case 'serviceMatcher':
                    return {
                        ...baseData,
                        serviceType: node.data.serviceType || 'TRANSFER',
                        dataIntegrationId: node.data.dataIntegrationId || null,
                        inputVariable: node.data.inputVariable || 'userMessage',
                        outputVariable: node.data.outputVariable || 'userMessage',
                        activeOnly: node.data.activeOnly ?? true,
                        enableEmbedding: node.data.enableEmbedding ?? false,
                        fuzzyThreshold: node.data.fuzzyThreshold ?? 0.85,
                        embeddingThreshold: node.data.embeddingThreshold ?? 0.65,
                        maxResults: node.data.maxResults ?? 0,
                        // ── Unified Favorite Lookup (master toggle; false = legacy behaviour) ──
                        enableFavoriteLookup: node.data.enableFavoriteLookup ?? false,
                        matchStrategy: node.data.matchStrategy || 'favorite-first',
                        // API Request parity (same keys as the apiRequest node)
                        apiRequestUrl: node.data.apiRequestUrl || '',
                        apiRequestMethod: node.data.apiRequestMethod || 'GET',
                        apiRequestHeaders: node.data.apiRequestHeaders || [],
                        apiRequestBody: node.data.apiRequestBody || '',
                        apiRequestResponsePath: node.data.apiRequestResponsePath || '',
                        timeout: node.data.timeout ?? 30,
                        useLHBService: node.data.useLHBService ?? false,
                        // Favorite matching
                        favoritesSourceVariable: node.data.favoritesSourceVariable || '',
                        favoriteEnableEmbedding: node.data.favoriteEnableEmbedding ?? true,
                        favoriteKeywordFunnel: node.data.favoriteKeywordFunnel ?? true,
                        favoriteFuzzyThreshold: node.data.favoriteFuzzyThreshold ?? 0.85,
                        favoriteEmbeddingThreshold: node.data.favoriteEmbeddingThreshold ?? 0.65,
                        favoriteMaxResults: node.data.favoriteMaxResults ?? 0,
                        // Branch decision rules
                        favoriteConditions: node.data.favoriteConditions || [],
                        favoriteMatchMode: node.data.favoriteMatchMode || 'all',
                        serviceConditions: node.data.serviceConditions || [],
                        serviceMatchMode: node.data.serviceMatchMode || 'all',
                        // Output handle labels
                        favoriteLabel: node.data.favoriteLabel || 'Favorite',
                        serviceLabel: node.data.serviceLabel || 'Service',
                        noMatchLabel: node.data.noMatchLabel || 'No Match',
                    };

                case 'email':
                    return {
                        ...baseData,
                        emailProvider: node.data.emailProvider || 'mailgun',
                        toEmail: node.data.toEmail || '',
                        ccEmails: node.data.ccEmails || [],
                        bccEmails: node.data.bccEmails || [],
                        subject: node.data.subject || '',
                        emailTemplate: node.data.emailTemplate || 'custom',
                        emailBody: node.data.emailBody || '',
                        isHtml: node.data.isHtml !== undefined ? node.data.isHtml : false
                    };
                case 'sharepoint':
                    return {
                        ...baseData,
                        sharepoint: {
                            siteUrl: node.data.sharepoint?.siteUrl || '',
                            tenantId: node.data.sharepoint?.tenantId || '',
                            clientId: node.data.sharepoint?.clientId || '',
                            clientSecret: node.data.sharepoint?.clientSecret || '',
                            folderPath: node.data.sharepoint?.folderPath || '',
                            filename: node.data.sharepoint?.filename || ''
                        }
                    };

                case 'interestOptimizer':
                    return {
                        ...baseData,
                        useBaseModelPrompt: node.data.useBaseModelPrompt,
                        enableLanguageReplace: node.data.enableLanguageReplace || false,
                        enableVoicePromptReplace: node.data.enableVoicePromptReplace || false,
                        enableTransactionId: node.data.enableTransactionId || false,
                        ioAnswerModel: node.data.ioAnswerModel || null,
                        ioFallbackPrompt: node.data.ioFallbackPrompt || '',
                        ioParserModel: node.data.ioParserModel || null,
                        ioDocuments: node.data.ioDocuments || [],
                        ioAccountCodes: node.data.ioAccountCodes || [],
                        ioRequiredFields: node.data.ioRequiredFields || [],
                        ...this.getSummaryMemoryData(node.data),
                        nodeTimeout: node.data.nodeTimeout ?? 0,
                        nodeMaxRetry: node.data.nodeMaxRetry ?? 0
                    };

                case 'loanInterest':
                    return {
                        ...baseData,
                        useBaseModelPrompt: node.data.useBaseModelPrompt,
                        enableLanguageReplace: node.data.enableLanguageReplace || false,
                        enableVoicePromptReplace: node.data.enableVoicePromptReplace || false,
                        enableTransactionId: node.data.enableTransactionId || false,
                        liAnswerModel: node.data.liAnswerModel || null,
                        liFallbackPrompt: node.data.liFallbackPrompt || '',
                        liRequiredFields: node.data.liRequiredFields || [],
                        liParserModel: node.data.liParserModel || null,
                        liDocuments: node.data.liDocuments || [],
                        liInterestRates: node.data.liInterestRates || [],
                        ...this.getSummaryMemoryData(node.data),
                        nodeTimeout: node.data.nodeTimeout ?? 0,
                        nodeMaxRetry: node.data.nodeMaxRetry ?? 0
                    };

                case 'interestOptimizerV2':
                    return {
                        ...baseData,
                        useBaseModelPrompt: node.data.useBaseModelPrompt,
                        enableLanguageReplace: node.data.enableLanguageReplace || false,
                        enableVoicePromptReplace: node.data.enableVoicePromptReplace || false,
                        enableTransactionId: node.data.enableTransactionId || false,
                        ioAnswerModel: node.data.ioAnswerModel || null,
                        ioFallbackPrompt: node.data.ioFallbackPrompt || '',
                        ioParserModel: node.data.ioParserModel || null,
                        ioDocuments: node.data.ioDocuments || [],
                        ioV2MetaDocs: node.data.ioV2MetaDocs || [],
                        ioRequiredFields: node.data.ioRequiredFields || [],
                        ...this.getSummaryMemoryData(node.data),
                        nodeTimeout: node.data.nodeTimeout ?? 0,
                        nodeMaxRetry: node.data.nodeMaxRetry ?? 0
                    };

                case 'loanInterestV2':
                    return {
                        ...baseData,
                        useBaseModelPrompt: node.data.useBaseModelPrompt,
                        enableLanguageReplace: node.data.enableLanguageReplace || false,
                        enableVoicePromptReplace: node.data.enableVoicePromptReplace || false,
                        enableTransactionId: node.data.enableTransactionId || false,
                        liAnswerModel: node.data.liAnswerModel || null,
                        liFallbackPrompt: node.data.liFallbackPrompt || '',
                        liRequiredFields: node.data.liRequiredFields || [],
                        liParserModel: node.data.liParserModel || null,
                        liDocuments: node.data.liDocuments || [],
                        liV2MetaDocs: node.data.liV2MetaDocs || [],
                        ...this.getSummaryMemoryData(node.data),
                        nodeTimeout: node.data.nodeTimeout ?? 0,
                        nodeMaxRetry: node.data.nodeMaxRetry ?? 0
                    };
                case 'reformatTTS':
                    return {
                        ...baseData,
                        provider: node.data.provider,
                        model: node.data.model,
                        temperature: node.data.temperature,
                        maxTokens: node.data.maxTokens,
                        systemPrompt: node.data.systemPrompt,
                        streamResponse: node.data.streamResponse,
                        jsonResponse: node.data.jsonResponse,
                        enableCache: node.data.enableCache,
                        cacheTTLMinutes: node.data.cacheTTLMinutes,
                        enableSemanticCache: node.data.enableSemanticCache || false,
                        similarityThreshold: node.data.similarityThreshold || 0.95,
                        similarityMargin: node.data.similarityMargin || 0.05,
                        processingMode: node.data.processingMode,
                        enableTransactionId: node.data.enableTransactionId || false,
                        enableLanguageReplace: node.data.enableLanguageReplace || false,
                        nodeTimeout: node.data.nodeTimeout || 0,
                        nodeMaxRetry: node.data.nodeMaxRetry || 0
                    };

                case 'ssmlFormat':
                    return {
                        ...baseData,
                        provider: node.data.provider,
                        model: node.data.model,
                        temperature: node.data.temperature,
                        maxTokens: node.data.maxTokens,
                        systemPrompt: node.data.systemPrompt,
                        streamResponse: node.data.streamResponse,
                        jsonResponse: node.data.jsonResponse,
                        enableCache: node.data.enableCache,
                        cacheTTLMinutes: node.data.cacheTTLMinutes,
                        enableSemanticCache: node.data.enableSemanticCache || false,
                        similarityThreshold: node.data.similarityThreshold || 0.95,
                        similarityMargin: node.data.similarityMargin || 0.05,
                        processingMode: node.data.processingMode,
                        enableTransactionId: node.data.enableTransactionId || false,
                        nodeTimeout: node.data.nodeTimeout || 0,
                        nodeMaxRetry: node.data.nodeMaxRetry || 0
                    };

                case 'queryRewriter':
                    return {
                        ...baseData,
                        queryRewriterMode: node.data.queryRewriterMode,
                        queryRewriterEnableLanguageReplace: node.data.queryRewriterEnableLanguageReplace ?? false,
                        queryRewriterProvider: node.data.queryRewriterProvider,
                        queryRewriterModel: node.data.queryRewriterModel,
                        queryRewriterApiVersion: node.data.queryRewriterApiVersion,
                        queryRewriterTemperature: node.data.queryRewriterTemperature,
                        queryRewriterMaxTokens: node.data.queryRewriterMaxTokens,
                        queryRewriterTopP: node.data.queryRewriterTopP,
                        queryRewriterFrequencyPenalty: node.data.queryRewriterFrequencyPenalty,
                        queryRewriterPresencePenalty: node.data.queryRewriterPresencePenalty,
                        queryRewriterSystemPrompt: node.data.queryRewriterSystemPrompt,
                        queryRewriterMemoryTurn: node.data.queryRewriterMemoryTurn,
                        queryRewriterMemoryDuration: node.data.queryRewriterMemoryDuration,
                        // Response Options
                        queryRewriterStreamResponse: node.data.queryRewriterStreamResponse,
                        queryRewriterJsonResponse: node.data.queryRewriterJsonResponse,
                        // Per-node timeout override
                        queryRewriterTimeout: node.data.queryRewriterTimeout || 0,
                        queryRewriterMaxRetry: node.data.queryRewriterMaxRetry || 0
                    };

                case 'orchestrator':
                    return {
                        ...baseData,
                        orchestratorName: node.data.orchestratorName,
                        // Model configuration
                        provider: node.data.provider,
                        model: node.data.model,
                        temperature: node.data.temperature,
                        maxTokens: node.data.maxTokens,
                        topP: node.data.topP,
                        // Response Options
                        streamResponse: node.data.streamResponse,
                        jsonResponse: node.data.jsonResponse,
                        // Memory Settings
                        memoryTurn: node.data.memoryTurn,
                        memoryDuration: node.data.memoryDuration,
                        shortTermMemory: node.data.shortTermMemory,
                        longTermMemory: node.data.longTermMemory,
                        // Prompts
                        systemPrompt: node.data.systemPrompt,
                        guardrailPrompt: node.data.guardrailPrompt,
                        allowAgentGuardrailBypass: node.data.allowAgentGuardrailBypass,
                        // Configurable prompts / messages (blank = built-in default)
                        orchSingleSelectionTemplate: node.data.orchSingleSelectionTemplate,
                        orchPipelineSelectionTemplate: node.data.orchPipelineSelectionTemplate,
                        orchVerifySelectionTemplate: node.data.orchVerifySelectionTemplate,
                        orchAgentListItemTemplate: node.data.orchAgentListItemTemplate,
                        orchAgentNoToolsLabel: node.data.orchAgentNoToolsLabel,
                        orchConversationStateTemplate: node.data.orchConversationStateTemplate,
                        orchConversationStateFactTemplate: node.data.orchConversationStateFactTemplate,
                        orchCompanionNote: node.data.orchCompanionNote,
                        orchSequentialContextTemplate: node.data.orchSequentialContextTemplate,
                        orchCheckerGoalTemplate: node.data.orchCheckerGoalTemplate,
                        orchFallbackSubGoal: node.data.orchFallbackSubGoal,
                        orchAggregationTemplate: node.data.orchAggregationTemplate,
                        orchStrategyHintParallel: node.data.orchStrategyHintParallel,
                        orchStrategyHintPipeline: node.data.orchStrategyHintPipeline,
                        orchAggregationFallbackItemTemplate: node.data.orchAggregationFallbackItemTemplate,
                        orchGuardrailFormatSuffix: node.data.orchGuardrailFormatSuffix,
                        orchGuardrailUserTemplate: node.data.orchGuardrailUserTemplate,
                        orchDirectLlmPrompt: node.data.orchDirectLlmPrompt,
                        orchBlockedMessage: node.data.orchBlockedMessage,
                        orchDirectLlmEmptyMessage: node.data.orchDirectLlmEmptyMessage,
                        orchDirectLlmErrorMessage: node.data.orchDirectLlmErrorMessage,
                        orchNoAgentSucceededMessage: node.data.orchNoAgentSucceededMessage,
                        // Threshold
                        confidenceThreshold: node.data.confidenceThreshold,
                        // Multi-Agent flag
                        enableMultiAgent: node.data.enableMultiAgent,
                        multiAgentStrategy: node.data.multiAgentStrategy,
                        // Extended fields
                        gender: node.data.gender,
                        typingSpeed: node.data.typingSpeed,
                        maxInputLength: node.data.maxInputLength,
                        frequencyPenalty: node.data.frequencyPenalty,
                        presencePenalty: node.data.presencePenalty,
                        stopSequence: node.data.stopSequence,
                        enableCache: node.data.enableCache,
                        cacheTTLMinutes: node.data.cacheTTLMinutes,
                        cacheScope: node.data.cacheScope,
                        enableSemanticCache: node.data.enableSemanticCache,
                        similarityThreshold: node.data.similarityThreshold,
                        showLineName: node.data.showLineName,
                        loadingBar: node.data.loadingBar,
                        loadingBarDuration: node.data.loadingBarDuration,
                        monitorLineUserId: node.data.monitorLineUserId,
                        monitorSocialID: node.data.monitorSocialID,
                        forbiddenWords: node.data.forbiddenWords,
                        enableTime: node.data.enableTime,
                        timeZone: node.data.timeZone,
                        enableTransactionId: node.data.enableTransactionId,
                        enableInputMessageReplace: node.data.enableInputMessageReplace,
                        enableVoicePromptReplace: node.data.enableVoicePromptReplace,
                        enableLanguageReplace: node.data.enableLanguageReplace,
                        // Per-node timeout override
                        nodeTimeout: node.data.nodeTimeout || 0,
                        nodeMaxRetry: node.data.nodeMaxRetry || 0
                    };

                case 'agent':
                    return {
                        ...baseData,
                        agentName: node.data.agentName,
                        agentDescription: node.data.agentDescription,
                        personaPrompt: node.data.personaPrompt,
                        // Saved raw (no `|| default`): blank is how an operator says "go back to the built-in".
                        agentFinalSummaryTemplate: node.data.agentFinalSummaryTemplate,
                        useBaseModelPrompt: node.data.useBaseModelPrompt,
                        selectedTools: node.data.selectedTools || [],
                        enableAgentToolDecision: node.data.enableAgentToolDecision,
                        useCustomAIModel: node.data.useCustomAIModel,
                        agentProvider: node.data.agentProvider,
                        agentModel: node.data.agentModel,
                        agentTemperature: node.data.agentTemperature,
                        agentMaxTokens: node.data.agentMaxTokens,
                        agentTopP: node.data.agentTopP,
                        agentFrequencyPenalty: node.data.agentFrequencyPenalty,
                        agentPresencePenalty: node.data.agentPresencePenalty,
                        agentStopSequence: node.data.agentStopSequence,
                        agentStreamResponse: node.data.agentStreamResponse,
                        agentJsonResponse: node.data.agentJsonResponse,
                        agentGender: node.data.agentGender,
                        agentTypingSpeed: node.data.agentTypingSpeed,
                        agentMaxInputLength: node.data.agentMaxInputLength,
                        agentMemoryTurn: node.data.agentMemoryTurn,
                        agentMemoryDuration: node.data.agentMemoryDuration,
                        agentShortTermMemory: node.data.agentShortTermMemory,
                        agentLongTermMemory: node.data.agentLongTermMemory,
                        agentEnableTime: node.data.agentEnableTime,
                        agentTimeZone: node.data.agentTimeZone,
                        agentEnableTransactionId: node.data.agentEnableTransactionId,
                        agentEnableLanguageReplace: node.data.agentEnableLanguageReplace,
                        agentEnableInsurance: node.data.agentEnableInsurance,
                        agentTimeout: node.data.agentTimeout || 0,
                        agentMaxRetry: node.data.agentMaxRetry || 0,
                        // Hosted tools config (JSON string หรือ null) — ลืมบรรทัดนี้ = ค่าหายเงียบตอน save
                        toolsConfig: node.data.toolsConfig,
                        knowledgeSources: node.data.knowledgeSources || {
                            documents: { nodeId: null, description: '' },
                            databaseIntegration: { nodeId: null, description: '' },
                            mutualFund: { nodeId: null, description: '' },
                            knowledgeApi: { nodeId: null, description: '' },
                            products: { nodeId: null, description: '' },
                            sharepoint: { nodeId: null, description: '' }
                        }
                    };

                // 🔴 Tax Planning Agent — THREE keys, and all three have to be listed here. `default:` returns
                //    `baseData` (an empty object), so a type missing from this switch is saved with NO data at all:
                //    the agent would come back nameless and description-less, and the Orchestrator selects agents
                //    from exactly those two fields — i.e. it would become unreachable, silently, on the first save.
                //    Everything else about this agent (model, prompts, memory, guardrail) lives in
                //    tax.TaxPlanningSettingsV2 and must NOT be added here.
                case 'taxPlanningAgent':
                    return {
                        ...baseData,
                        agentName: node.data.agentName,
                        agentDescription: node.data.agentDescription,
                        bypassGuardrailWhenAlone: node.data.bypassGuardrailWhenAlone ?? true,
                        // Knowledge picker. `taxKnowledgeSelections` is the whole payload the backend will read:
                        // [{ header, category }] where category === null means "the whole header, including
                        // categories added to it later". Normalised on the way out so a half-built entry from an
                        // interrupted click can never be persisted.
                        taxKnowledgeEnabled: node.data.taxKnowledgeEnabled ?? false,
                        taxKnowledgeSelections: (Array.isArray(node.data.taxKnowledgeSelections)
                            ? node.data.taxKnowledgeSelections : [])
                            .filter(x => x && x.header)
                            .map(x => ({ header: x.header, category: x.category ?? null }))
                    };

                case 'metaDocuments':
                    return {
                        ...baseData,
                        // The whole payload the backend reads: [{ header, category }] where
                        // category === null means "the whole header, including categories added later".
                        // Normalised on the way out so a half-built entry can never be persisted.
                        metaDocSelections: (Array.isArray(node.data.metaDocSelections)
                            ? node.data.metaDocSelections : [])
                            .filter(x => x && x.header)
                            .map(x => ({ header: x.header, category: x.category ?? null })),
                        topNChunks: node.data.topNChunks || 5,
                        metaDocDescription: node.data.metaDocDescription || ''
                    };

                case 'tool':
                    return {
                        ...baseData,
                        toolName: node.data.toolName,
                        toolDescription: node.data.toolDescription,
                        toolType: node.data.toolType,
                        masterToolId: node.data.masterToolId,
                        endpoint: node.data.endpoint,
                        method: node.data.method,
                        headers: node.data.headers || [],
                        body: node.data.body,
                        textContent: node.data.textContent
                    };

                case 'flowTool':
                    // Optimize internal nodes - remove redundant 'type: flow-node' as it's always the same
                    const optimizedInternalNodes = (node.data.internalNodes || []).map(n => ({
                        id: n.id,
                        position: n.position,
                        data: n.data
                    }));
                    // Optimize internal edges - remove redundant 'type' and 'markerEnd' as they are constants
                    const optimizedInternalEdges = (node.data.internalEdges || []).map(e => ({
                        id: e.id,
                        source: e.source,
                        target: e.target,
                        sourceHandle: e.sourceHandle !== 'output' ? e.sourceHandle : undefined,
                        targetHandle: e.targetHandle !== 'input' ? e.targetHandle : undefined
                    }));
                    return {
                        ...baseData,
                        flowToolName: node.data.flowToolName || 'Flow Tool',
                        flowToolDescription: node.data.flowToolDescription || '',
                        internalNodes: optimizedInternalNodes,
                        internalEdges: optimizedInternalEdges,
                        internalNodeCount: node.data.internalNodeCount || 0,
                        showWarning: node.data.showWarning || false
                    };

                default:
                    return baseData;
            }
        },

        // Validate nested flow structure
        validateNestedFlow(flowData) {
            const errors = [];

            // Check for required flow properties
            if (!flowData.name || flowData.name.trim() === '') {
                errors.push('Flow name is required');
            }

            // Check for at least one root node
            if (!flowData.nodes || flowData.nodes.length === 0) {
                errors.push('Flow must contain at least one root node');
            }

            // Validate nested structure
            const allNodeIds = new Set();
            const validateNode = (node, path = '') => {
                if (!node || !node.id) {
                    errors.push(`Node at path ${path} is missing ID`);
                    return;
                }

                if (allNodeIds.has(node.id)) {
                    errors.push(`Duplicate node ID found: ${node.id} at path ${path}`);
                    return;
                }

                allNodeIds.add(node.id);

                if (!node.nodeType) {
                    errors.push(`Node ${node.id} is missing nodeType`);
                }

                // Validate node-specific data
                // this.validateNodeData(node, errors);

                // Recursively validate target nodes
                if (node.target && Array.isArray(node.target)) {
                    node.target.forEach((targetNode, index) => {
                        validateNode(targetNode, `${path}.target[${index}]`);
                    });
                }
            };

            flowData.nodes.forEach((rootNode, index) => {
                validateNode(rootNode, `nodes[${index}]`);
            });

            return {
                isValid: errors.length === 0,
                errors
            };
        },

        // Validate specific node data
        validateNodeData(node, errors) {
            switch (node.nodeType) {
                case 'messageInput':
                    const messageInputModels = Array.isArray(node.data.modelDisplayId)
                        ? node.data.modelDisplayId
                        : (node.data.modelDisplayId ? [node.data.modelDisplayId] : []);
                    if (!messageInputModels.length) {
                        errors.push(`Message Input node (${node.id}) requires at least one selected model`);
                    }
                    break;
                case 'model':
                    if (!node.data.provider || !node.data.model) {
                        errors.push(`AI Model node (${node.id}) requires a selected model`);
                    }
                    break;
                
                case 'intentAI':
                    if (!node.data.intentaiModel || !node.data.intentaiProvider) {
                        errors.push(`Intent AI node (${node.id}) requires a model and provider`);
                    }
                    break;

                case 'intent':
                    if (!node.data.intents || node.data.intents.length === 0) {
                        errors.push(`Intent node (${node.id}) requires at least one intent`);
                    }
                    break;

                case 'switch':
                    if (!node.data.switchOutputs || node.data.switchOutputs.length < 2) {
                        errors.push(`Switch node (${node.id}) requires at least two output paths`);
                    }
                    break;

                case 'apiTrigger':
                    if (!node.data.apiUrl) {
                        errors.push(`API Trigger node (${node.id}) requires API URL`);
                    }
                    break;

                case 'apiknowledge':
                    if (!node.data.knowledgeApiUrl) {
                        errors.push(`Knowledge API node (${node.id}) requires API URL`);
                    }
                    break;

                case 'email':
                    if (!node.data.toEmail || node.data.toEmail.trim() === '') {
                        errors.push(`Email node (${node.id}) requires a recipient email address`);
                    } else {
                        // Basic email validation
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!emailRegex.test(node.data.toEmail.trim())) {
                            errors.push(`Email node (${node.id}) has invalid recipient email format`);
                        }
                    }
                    
                    if (!node.data.subject || node.data.subject.trim() === '') {
                        errors.push(`Email node (${node.id}) requires a subject`);
                    }
                    
                    if (node.data.emailTemplate === 'custom' && (!node.data.emailBody || node.data.emailBody.trim() === '')) {
                        errors.push(`Email node (${node.id}) requires email content when using custom template`);
                    }
                    break;

                case 'sharepoint':
                    if (!node.data.sharepoint || 
                        !node.data.sharepoint.siteUrl || node.data.sharepoint.siteUrl.trim() === '' ||
                        !node.data.sharepoint.tenantId || node.data.sharepoint.tenantId.trim() === '' ||
                        !node.data.sharepoint.clientId || node.data.sharepoint.clientId.trim() === '' ||
                        !node.data.sharepoint.clientSecret || node.data.sharepoint.clientSecret.trim() === '' ||
                        !node.data.sharepoint.folderPath || node.data.sharepoint.folderPath.trim() === '' ||
                        !node.data.sharepoint.filename || node.data.sharepoint.filename.trim() === '') {
                        errors.push(`SharePoint node (${node.id}) requires all configuration fields: Site URL, Tenant ID, Client ID, Client Secret, Folder Path, and Filename`);
                    }
                    break;
            }
        },
        // Expand node data back to full structure
        expandNodeData(nodeType, data) {
            const expanded = { ...data };

            switch (nodeType) {
                case 'messageInput':
                    expanded.selectedModel = Array.isArray(data.modelDisplayId)
                        ? data.modelDisplayId
                        : (data.modelDisplayId ? [data.modelDisplayId] : []);
                    break;
                case 'model':
                    // Basic model configuration
                    expanded.selectedModel = data.modelDisplayId;
                    expanded.provider = data.provider || null;
                    expanded.model = data.model || null;
                    expanded.apiVersion = data.apiVersion || null;
                    expanded.systemPrompt = data.systemPrompt || '';
                    expanded.useBaseModelPrompt = data.useBaseModelPrompt !== undefined ? data.useBaseModelPrompt : false;

                    // AI Parameters
                    expanded.temperature = data.temperature ?? null;
                    expanded.maxTokens = data.maxTokens ?? null;
                    expanded.topP = data.topP ?? null;
                    expanded.frequencyPenalty = data.frequencyPenalty ?? null;
                    expanded.presencePenalty = data.presencePenalty ?? null;
                    expanded.stopSequence = data.stopSequence || null;

                    // Behavior Settings
                    expanded.gender = data.gender || 'male';
                    expanded.typingSpeed = data.typingSpeed ?? null;
                    expanded.maxInputLength = data.maxInputLength ?? 200000;

                    // Response Options
                    expanded.streamResponse = data.streamResponse !== undefined ? data.streamResponse : false;
                    expanded.jsonResponse = data.jsonResponse !== undefined ? data.jsonResponse : false;
                    expanded.enableTime = data.enableTime ?? false;
                    expanded.timeZone = data.timeZone || null;
                    expanded.enableTransactionId = data.enableTransactionId ?? false;

                    // Memory Settings (convert memoryDuration from negative like ModelManagement.vue)
                    expanded.memoryTurn = data.memoryTurn ?? null;
                    expanded.memoryDuration = data.memoryDuration < 0
                        ? Math.abs(data.memoryDuration)
                        : data.memoryDuration;
                    expanded.shortTermMemory = data.shortTermMemory ?? null;
                    expanded.longTermMemory = data.longTermMemory !== undefined ? data.longTermMemory : false;
                    expanded.isChainMassage = data.isChainMassage !== undefined ? data.isChainMassage : false;
                    expanded.lenChainMassage = data.lenChainMassage ?? null;
                    expanded.timeSecondChainMassage = data.timeSecondChainMassage ?? null;

                    // Line Configuration
                    expanded.loadingBar = data.loadingBar !== undefined ? data.loadingBar : false;
                    expanded.loadingBarDuration = data.loadingBarDuration ?? 0;
                    expanded.showLineName = data.showLineName !== undefined ? data.showLineName : false;
                    expanded.monitorLineUserId = data.monitorLineUserId || null;
                    expanded.monitorSocialID = data.monitorSocialID || null;
                    expanded.forbiddenWords = data.forbiddenWords || [''];
                    expanded.nodeTimeout = data.nodeTimeout ?? 0;
                     expanded.nodeMaxRetry = data.nodeMaxRetry ?? 0;
                    break;
                case 'taxPlanning':
                    // Node label
                    expanded.taxPlanningName = data.taxPlanningName || '';
                    // LLM configuration (same keys as AI Model)
                    expanded.selectedModel = data.modelDisplayId;
                    expanded.provider = data.provider || null;
                    expanded.model = data.model || null;
                    expanded.apiVersion = data.apiVersion || null;
                    expanded.systemPrompt = data.systemPrompt || '';
                    expanded.temperature = data.temperature ?? null;
                    expanded.maxTokens = data.maxTokens ?? null;
                    expanded.topP = data.topP ?? null;
                    expanded.frequencyPenalty = data.frequencyPenalty ?? null;
                    expanded.presencePenalty = data.presencePenalty ?? null;
                    expanded.stopSequence = data.stopSequence || null;
                    expanded.memoryTurn = data.memoryTurn ?? null;
                    expanded.memoryDuration = data.memoryDuration < 0
                        ? Math.abs(data.memoryDuration)
                        : data.memoryDuration;
                    expanded.streamResponse = data.streamResponse !== undefined ? data.streamResponse : false;
                    expanded.jsonResponse = data.jsonResponse !== undefined ? data.jsonResponse : false;
                    expanded.bypassOnError = data.bypassOnError !== undefined ? data.bypassOnError : false;
                    expanded.nodeTimeout = data.nodeTimeout ?? 0;
                    expanded.nodeMaxRetry = data.nodeMaxRetry ?? 0;
                    // Tax-specific configuration
                    expanded.taxPlanningYearMode = data.taxPlanningYearMode || 'active';
                    expanded.taxPlanningYearId = data.taxPlanningYearId ?? null;
                    expanded.taxPlanningAskWhenMultipleActive = data.taxPlanningAskWhenMultipleActive ?? true;
                    expanded.taxPlanningFlowMode = data.taxPlanningFlowMode || 'hybrid';
                    expanded.taxPlanningPersist = data.taxPlanningPersist ?? true;
                    expanded.taxPlanningCompletionMessage = data.taxPlanningCompletionMessage || '';
                    // 🆕 Optional separate model for question-phrasing steps only (falls back to main provider/model)
                    expanded.taxPlanningUseQuestionModel = data.taxPlanningUseQuestionModel ?? false;
                    expanded.taxPlanningQuestionProvider = data.taxPlanningQuestionProvider || null;
                    expanded.taxPlanningQuestionModel = data.taxPlanningQuestionModel || null;
                    break;
                case 'taxPlanningV2':
                    // Node label
                    expanded.taxPlanningV2Name = data.taxPlanningV2Name || '';
                    // LLM configuration — SHARED keys (same as the AI Model node). Kept for
                    // round-tripping legacy node data only; the V2 node reads its LLM config from
                    // the resolved settings version now.
                    expanded.selectedModel = data.modelDisplayId;
                    expanded.provider = data.provider || null;
                    expanded.model = data.model || null;
                    expanded.apiVersion = data.apiVersion || null;
                    expanded.systemPrompt = data.systemPrompt || '';
                    expanded.useBaseModelPrompt = data.useBaseModelPrompt !== undefined ? data.useBaseModelPrompt : false;
                    expanded.gender = data.gender || 'male';
                    expanded.temperature = data.temperature ?? null;
                    expanded.maxTokens = data.maxTokens ?? null;
                    expanded.topP = data.topP ?? null;
                    expanded.frequencyPenalty = data.frequencyPenalty ?? null;
                    expanded.presencePenalty = data.presencePenalty ?? null;
                    expanded.stopSequence = data.stopSequence || null;
                    expanded.memoryTurn = data.memoryTurn ?? null;
                    expanded.memoryDuration = data.memoryDuration < 0
                        ? Math.abs(data.memoryDuration)
                        : data.memoryDuration;
                    expanded.streamResponse = data.streamResponse !== undefined ? data.streamResponse : false;
                    expanded.jsonResponse = data.jsonResponse !== undefined ? data.jsonResponse : false;
                    expanded.bypassOnError = data.bypassOnError !== undefined ? data.bypassOnError : false;
                    expanded.enableTime = data.enableTime ?? false;
                    expanded.timeZone = data.timeZone || null;
                    expanded.nodeTimeout = data.nodeTimeout ?? 0;
                    expanded.nodeMaxRetry = data.nodeMaxRetry ?? 0;
                    // Tax-specific configuration (V2) moved to the versioned settings rows
                    // (`tax.TaxPlanningSettingsV2`). Only the pinned settings version stays on the
                    // node — `null` means "use whichever version is active".
                    expanded.taxPlanningV2SettingsVersionId = data.taxPlanningV2SettingsVersionId ?? null;
                    break;
                case 'llmEnrich':
                    expanded.provider = data.provider || null;
                    expanded.model = data.model || null;
                    expanded.modelId = data.modelId ?? null;
                    expanded.apiVersion = data.apiVersion || null;
                    expanded.systemPrompt = data.systemPrompt || '';
                    expanded.temperature = data.temperature ?? 0;
                    expanded.maxTokens = data.maxTokens ?? null;
                    expanded.topP = data.topP ?? 1;
                    expanded.jsonResponse = data.jsonResponse !== undefined ? data.jsonResponse : false;
                    expanded.llmEnrichName = data.llmEnrichName || '';
                    expanded.llmEnrichConnectorText = data.llmEnrichConnectorText || '';
                    expanded.bypassOnError = data.bypassOnError !== undefined ? data.bypassOnError : false;
                    break;
                case 'buttonStyle':
                    expanded.buttonStyleName = data.buttonStyleName || '';
                    expanded.productButtonStyle = data.productButtonStyle || '';
                    expanded.serviceButtonStyle = data.serviceButtonStyle || '';
                    break;
                case 'apiRequest':
                    expanded.apiRequestUrl = data.apiRequestUrl || '';
                    expanded.apiRequestMethod = data.apiRequestMethod || 'GET';
                    expanded.apiRequestHeaders = data.apiRequestHeaders || [{ key: '', value: '' }];
                    expanded.apiRequestBody = data.apiRequestBody || '';
                    expanded.apiRequestResponsePath = data.apiRequestResponsePath || '';
                    expanded.useLHBService = data.useLHBService ?? false;
                    expanded.useResultAsMessage = data.useResultAsMessage ?? false;
                    expanded.timeout = data.timeout ?? 30;
                    break;
                case 'favoriteMatcher':
                    expanded.serviceType = data.serviceType || 'TRANSFER';
                    expanded.favoritesSourceVariable = data.favoritesSourceVariable || 'apiRequestResponse';
                    expanded.outputVariable = data.outputVariable || 'userMessage';
                    expanded.enableEmbedding = data.enableEmbedding ?? true;
                    expanded.fuzzyThreshold = data.fuzzyThreshold ?? 0.85;
                    expanded.embeddingThreshold = data.embeddingThreshold ?? 0.65;
                    expanded.maxResults = data.maxResults ?? 0;
                    break;
                case 'serviceMatcher':
                    expanded.serviceType = data.serviceType || 'TRANSFER';
                    expanded.dataIntegrationId = data.dataIntegrationId || null;
                    expanded.inputVariable = data.inputVariable || 'userMessage';
                    expanded.outputVariable = data.outputVariable || 'userMessage';
                    expanded.activeOnly = data.activeOnly ?? true;
                    expanded.enableEmbedding = data.enableEmbedding ?? false;
                    expanded.fuzzyThreshold = data.fuzzyThreshold ?? 0.85;
                    expanded.embeddingThreshold = data.embeddingThreshold ?? 0.65;
                    expanded.maxResults = data.maxResults ?? 0;
                    // ── Unified Favorite Lookup (master toggle; false = legacy behaviour) ──
                    expanded.enableFavoriteLookup = data.enableFavoriteLookup ?? false;
                    expanded.matchStrategy = data.matchStrategy || 'favorite-first';
                    // API Request parity
                    expanded.apiRequestUrl = data.apiRequestUrl || '';
                    expanded.apiRequestMethod = data.apiRequestMethod || 'GET';
                    expanded.apiRequestHeaders = this.parseServiceMatcherHeaders(data.apiRequestHeaders);
                    expanded.apiRequestBody = data.apiRequestBody || '';
                    expanded.apiRequestResponsePath = data.apiRequestResponsePath || '';
                    expanded.timeout = data.timeout ?? 30;
                    expanded.useLHBService = data.useLHBService ?? false;
                    // Favorite matching
                    expanded.favoritesSourceVariable = data.favoritesSourceVariable || '';
                    expanded.favoriteEnableEmbedding = data.favoriteEnableEmbedding ?? true;
                    expanded.favoriteKeywordFunnel = data.favoriteKeywordFunnel ?? true;
                    expanded.favoriteFuzzyThreshold = data.favoriteFuzzyThreshold ?? 0.85;
                    expanded.favoriteEmbeddingThreshold = data.favoriteEmbeddingThreshold ?? 0.65;
                    expanded.favoriteMaxResults = data.favoriteMaxResults ?? 0;
                    // Branch decision rules
                    expanded.favoriteConditions = this.cloneServiceMatcherRules(data.favoriteConditions);
                    expanded.favoriteMatchMode = data.favoriteMatchMode || 'all';
                    expanded.serviceConditions = this.cloneServiceMatcherRules(data.serviceConditions);
                    expanded.serviceMatchMode = data.serviceMatchMode || 'all';
                    // Output handle labels
                    expanded.favoriteLabel = data.favoriteLabel || 'Favorite';
                    expanded.serviceLabel = data.serviceLabel || 'Service';
                    expanded.noMatchLabel = data.noMatchLabel || 'No Match';
                    break;
                case 'intentAI':
                    // Basic intentAI configuration
                    expanded.intentaiProvider = data.intentaiProvider || data.provider || null;
                    // Handle intentaiModel - extract single value from array if needed
                    expanded.intentaiModel = Array.isArray(data.intentaiModel)
                        ? (data.intentaiModel.length > 0 ? data.intentaiModel[0] : null)
                        : (data.intentaiModel || data.model || null);
                    expanded.intentAiModelApiVersion = data.intentAiModelApiVersion || null;
                    expanded.intentaiSystemPrompt = data.intentaiSystemPrompt || data.intentSystemPrompt || '';
                    expanded.intentaiExamples = data.intentaiExamples || data.intentExamples || '';
                    expanded.intentaiConfidence = data.intentaiConfidence ?? data.intentConfidence ?? 75;
                    expanded.intentaiFallbackIntent = data.intentaiFallbackIntent || data.fallbackIntent || 'unknown';

                    // AI Parameters
                    expanded.intentaiTemperature = data.intentaiTemperature ?? data.temperature ?? null;
                    expanded.intentaiMaxTokens = data.intentaiMaxTokens ?? data.maxTokens ?? null;
                    expanded.intentaiTopP = data.intentaiTopP ?? data.topP ?? null;
                    expanded.intentaiFrequencyPenalty = data.intentaiFrequencyPenalty ?? data.frequencyPenalty ?? null;
                    expanded.intentaiPresencePenalty = data.intentaiPresencePenalty ?? data.presencePenalty ?? null;
                    expanded.intentaiStopSequence = data.intentaiStopSequence || data.stopSequence || null;

                    // Behavior Settings
                    expanded.intentaiGender = data.intentaiGender || data.gender || 'male';
                    expanded.intentaiTypingSpeed = data.intentaiTypingSpeed ?? data.typingSpeed ?? null;
                    expanded.intentaiMaxInputLength = data.intentaiMaxInputLength ?? data.maxInputLength ?? 200000;

                    // Response Options
                    expanded.intentaiStreamResponse = data.intentaiStreamResponse !== undefined ? data.intentaiStreamResponse : (data.streamResponse !== undefined ? data.streamResponse : false);
                    expanded.intentaiJsonResponse = data.intentaiJsonResponse !== undefined ? data.intentaiJsonResponse : (data.jsonResponse !== undefined ? data.jsonResponse : false);
                    expanded.intentaiEnableTime = data.intentaiEnableTime !== undefined ? data.intentaiEnableTime : (data.enableTime ?? false);
                    expanded.intentaiTimeZone = data.intentaiTimeZone || data.timeZone || null;
                    expanded.intentaiEnableTransactionId = data.intentaiEnableTransactionId !== undefined ? data.intentaiEnableTransactionId : (data.enableTransactionId ?? false);

                    // Memory Settings (convert memoryDuration from negative like ModelManagement.vue)
                    expanded.intentaiMemoryTurn = data.intentaiMemoryTurn ?? data.memoryTurn ?? null;
                    expanded.intentaiMemoryDuration = data.intentaiMemoryDuration < 0
                        ? Math.abs(data.intentaiMemoryDuration)
                        : (data.memoryDuration < 0 ? Math.abs(data.memoryDuration) : data.intentaiMemoryDuration ?? data.memoryDuration);
                    expanded.intentaiShortTermMemory = data.intentaiShortTermMemory ?? data.shortTermMemory ?? null;
                    expanded.intentaiLongTermMemory = data.intentaiLongTermMemory !== undefined ? data.intentaiLongTermMemory : (data.longTermMemory !== undefined ? data.longTermMemory : false);
                    expanded.intentaiIsChainMassage = data.intentaiIsChainMassage !== undefined ? data.intentaiIsChainMassage : (data.isChainMassage !== undefined ? data.isChainMassage : false);
                    expanded.intentaiLenChainMassage = data.intentaiLenChainMassage || data.lenChainMassage || null;
                    expanded.intentaiTimeSecondChainMassage = data.intentaiTimeSecondChainMassage || data.timeSecondChainMassage || null;

                    // Line Configuration
                    expanded.intentaiShowLineName = data.intentaiShowLineName !== undefined ? data.intentaiShowLineName : (data.showLineName !== undefined ? data.showLineName : false);
                    expanded.intentaiLoadingBar = data.intentaiLoadingBar !== undefined ? data.intentaiLoadingBar : (data.loadingBar !== undefined ? data.loadingBar : false);
                    expanded.intentaiLoadingBarDuration = data.intentaiLoadingBarDuration || data.loadingBarDuration || 0;
                    expanded.intentaiMonitorLineUserId = data.intentaiMonitorLineUserId || data.monitorLineUserId || null;
                    expanded.intentaiMonitorSocialID = data.intentaiMonitorSocialID || data.monitorSocialID || null;
                    expanded.intentaiForbiddenWords = data.intentaiForbiddenWords || data.forbiddenWords || [''];
                    expanded.intentaiTimeout = data.intentaiTimeout ?? 0;
                    expanded.intentaiMaxRetry = data.intentaiMaxRetry ?? 0;
                    break;
                case 'intent':
                    // Ensure required arrays exist
                    expanded.intents = data.intents || [];
                    expanded.actions = data.actions || [];
                    expanded.outputs = data.outputs || [];
                    break;
                case 'smartIntent':
                    // Ensure all smartIntent properties are properly loaded
                    expanded.smartIntentGroupName = data.smartIntentGroupName || '';
                    expanded.smartIntents = data.smartIntents || [];
                    
                    // Process each smartIntent to ensure forbiddenWords have empty item at the end
                    if (expanded.smartIntents && expanded.smartIntents.length > 0) {
                        expanded.smartIntents.forEach(smartIntent => {
                            if (smartIntent.mainAction) {
                                // Ensure apiVersion is restored
                                if (smartIntent.mainAction.apiVersion === undefined) {
                                    smartIntent.mainAction.apiVersion = null;
                                }
                                
                                // Ensure forbiddenWords array exists and has empty item at the end
                                if (smartIntent.mainAction.forbiddenWords && Array.isArray(smartIntent.mainAction.forbiddenWords)) {
                                    // Only add empty item if the last item is not already empty
                                    const lastItem = smartIntent.mainAction.forbiddenWords[smartIntent.mainAction.forbiddenWords.length - 1];
                                    if (lastItem !== '') {
                                        smartIntent.mainAction.forbiddenWords.push('');
                                    }
                                } else {
                                    smartIntent.mainAction.forbiddenWords = [''];
                                }
                            }
                        });
                    }
                    
                    expanded.smartIntentFallback = data.smartIntentFallback || {
                        useAI: false,
                        provider: null,
                        model: null,
                        apiVersion: null,
                        gender: 'male',
                        typingSpeed: null,
                        maxInputLength: 200000,
                        streamResponse: false,
                        jsonResponse: false,
                        enableTime: null,
                        timeZone: null,
                        enableTransactionId: null,
                        temperature: null,
                        topP: null,
                        frequencyPenalty: null,
                        presencePenalty: null,
                        maxTokens: null,
                        stopSequence: null,
                        memoryTurn: null,
                        memoryDuration: null,
                        shortTermMemory: null,
                        longTermMemory: false,
                        isChainMassage: false,
                        lenChainMassage: null,
                        timeSecondChainMassage: null,
                        forbiddenWords: [''],
                        nodeTimeout: null
                    };
                    
                    // Ensure fallback forbiddenWords has empty item at the end
                    if (expanded.smartIntentFallback.forbiddenWords && Array.isArray(expanded.smartIntentFallback.forbiddenWords)) {
                        const lastItem = expanded.smartIntentFallback.forbiddenWords[expanded.smartIntentFallback.forbiddenWords.length - 1];
                        if (lastItem !== '') {
                            expanded.smartIntentFallback.forbiddenWords.push('');
                        }
                    } else {
                        expanded.smartIntentFallback.forbiddenWords = [''];
                    }
                    break;
                case 'contactAgent':
                case 'contactAgentV2':
                    // Ensure all contact agent properties are properly loaded
                    expanded.departmentName = data.departmentName || '';
                    expanded.contactAgentMessage = data.contactAgentMessage || '';
                    expanded.contactAgentMessageChinese = data.contactAgentMessageChinese || '';
                    expanded.contactAgentMessageEnglish = data.contactAgentMessageEnglish || '';
                    expanded.handoffMessage = data.handoffMessage || '';
                    expanded.botDisableTime = data.botDisableTime || null;
                    expanded.disableBot = data.disableBot !== undefined ? data.disableBot : true;
                    break;
                case 'apiTrigger':
                    // Ensure all API trigger properties are properly loaded
                    expanded.apiUrl = data.apiUrl || '';
                    expanded.httpMethod = data.httpMethod || 'GET';
                    expanded.headers = data.headers || '';
                    expanded.textResponse = data.textResponse || '';
                    expanded.jsonValidate = data.jsonValidate || '{\n   "Name":"Value"\n}';
                    expanded.modelJsonValidator = data.modelJsonValidator || null;
                    expanded.listHeadersAPITrigger = data.listHeadersAPITrigger || [
                        {
                            key: '',
                            value: '',
                        },
                    ];
                    expanded.listValidation = data.listValidation || [
                        {
                            key: '',
                            typeValidation: '',
                            numberOfCharacter: '',
                            regExp: '',
                            promptMessage: '',
                        },
                    ];
                    break;
                case 'products':
                    // Ensure all products node properties are properly loaded
                    expanded.productSourceType = data.productSourceType || 'product';
                    expanded.productGroupCollectionId = data.productGroupCollectionId || null;
                    expanded.productGroupCollectionName = data.productGroupCollectionName || '';
                    expanded.productGroupList = data.productGroupList || [];
                    expanded.productTopNChunks = data.productTopNChunks || 5;
                    expanded.productCosineSimilarity = data.productCosineSimilarity || 0.7;
                    break;
                case 'documents':
                    // Ensure all documents node properties are properly loaded
                    expanded.document = data.document || [];
                    expanded.textReplacement = data.textReplacement || [];
                    expanded.topNChunks = data.topNChunks || 5;
                    expanded.cosineSimilarity = data.cosineSimilarity || 0.7;
                    expanded.selectedDocumentData = data.selectedDocumentData || [];
                    expanded.enableSpecificDataSelection = data.enableSpecificDataSelection || false;
                    expanded.documentVectorName = '';
                    // Community Graph RAG properties
                    expanded.communityId = data.communityId || null;
                    expanded.communityTopGroups = data.communityTopGroups || 3;
                    expanded.communityTopChunks = data.communityTopChunks || 5;
                    expanded.communitySearchMethod = data.communitySearchMethod || 'topn';
                    expanded.communityMinCosineScore = data.communityMinCosineScore || 0.7;
                    // Meta Community Graph RAG properties
                    expanded.metaCommunityId = data.metaCommunityId || null;
                    expanded.metaCommunityTopGroups = data.metaCommunityTopGroups || 3;
                    expanded.metaCommunityTopChunks = data.metaCommunityTopChunks || 5;
                    expanded.metaCommunitySearchMethod = data.metaCommunitySearchMethod || 'topn';
                    expanded.metaCommunityMinCosineScore = data.metaCommunityMinCosineScore || 0.7;
                    // Topic/SubTopic Filter properties
                    expanded.filterTopic = data.filterTopic || null;
                    expanded.filterSubTopic = data.filterSubTopic || null;
                    expanded.filterMode = data.filterMode || 'include';
                    break;
                case 'apiknowledge':
                    // Ensure all apiknowledge node properties are properly loaded
                    expanded.knowledgeApiUrl = data.knowledgeApiUrl || '';
                    expanded.knowledgeHttpMethod = data.knowledgeHttpMethod || 'GET';
                    expanded.knowledgeRequestBody = data.knowledgeRequestBody || '';
                    expanded.responseDataPath = data.responseDataPath || 'data';
                    expanded.useLHBService = data.useLHBService || false;
                    expanded.listHeaders = data.listHeaders || [];
                    expanded.description = data.description || '';
                    break;
                case 'email':
                    // Ensure all email node properties are properly loaded
                    expanded.emailProvider = data.emailProvider || 'mailgun';
                    expanded.toEmail = data.toEmail || '';
                    expanded.ccEmails = data.ccEmails || [];
                    expanded.bccEmails = data.bccEmails || [];
                    expanded.subject = data.subject || '';
                    expanded.emailTemplate = data.emailTemplate || 'custom';
                    expanded.emailBody = data.emailBody || '';
                    expanded.isHtml = data.isHtml !== undefined ? data.isHtml : false;
                    break;
                case 'sharepoint':
                    // Ensure all SharePoint node properties are properly loaded
                    expanded.sharepoint = data.sharepoint || {
                        siteUrl: '',
                        tenantId: '',
                        clientId: '',
                        clientSecret: '',
                        folderPath: '',
                        filename: ''
                    };
                    break;
                case 'interestOptimizer':
                    // Ensure all Interest Optimizer properties are properly loaded
                    expanded.useBaseModelPrompt = data.useBaseModelPrompt !== undefined ? data.useBaseModelPrompt : false;
                    expanded.enableLanguageReplace = data.enableLanguageReplace ?? false;
                    expanded.enableVoicePromptReplace = data.enableVoicePromptReplace ?? false;
                    expanded.enableTransactionId = data.enableTransactionId ?? false;
                    expanded.ioAnswerModel = data.ioAnswerModel || '';
                    expanded.ioFallbackPrompt = data.ioFallbackPrompt || '';
                    expanded.ioParserModel = data.ioParserModel || '';
                    expanded.ioDocuments = data.ioDocuments || [];
                    expanded.ioAccountCodes = data.ioAccountCodes || [];
                    expanded.ioRequiredFields = data.ioRequiredFields || [];
                    Object.assign(expanded, this.getSummaryMemoryData(data));
                    expanded.nodeTimeout = data.nodeTimeout ?? 0;
                     expanded.nodeMaxRetry = data.nodeMaxRetry ?? 0;
                    break;

                case 'loanInterest':
                    // Ensure all Loan Interest properties are properly loaded
                    expanded.useBaseModelPrompt = data.useBaseModelPrompt !== undefined ? data.useBaseModelPrompt : false;
                    expanded.enableLanguageReplace = data.enableLanguageReplace !== undefined ? data.enableLanguageReplace : false;
                    expanded.enableVoicePromptReplace = data.enableVoicePromptReplace ?? false;
                    expanded.enableTransactionId = data.enableTransactionId ?? false;
                    expanded.liAnswerModel = data.liAnswerModel || '';
                    expanded.liFallbackPrompt = data.liFallbackPrompt || '';
                    expanded.liRequiredFields = data.liRequiredFields || [];
                    expanded.liParserModel = data.liParserModel || '';
                    expanded.liDocuments = data.liDocuments || [];
                    expanded.liInterestRates = data.liInterestRates || [];
                    Object.assign(expanded, this.getSummaryMemoryData(data));
                    expanded.nodeTimeout = data.nodeTimeout ?? 0;
                    expanded.nodeMaxRetry = data.nodeMaxRetry ?? 0;
                    break;

                case 'interestOptimizerV2':
                    expanded.useBaseModelPrompt = data.useBaseModelPrompt !== undefined ? data.useBaseModelPrompt : false;
                    expanded.enableLanguageReplace = data.enableLanguageReplace ?? false;
                    expanded.enableVoicePromptReplace = data.enableVoicePromptReplace ?? false;
                    expanded.enableTransactionId = data.enableTransactionId ?? false;
                    expanded.ioAnswerModel = data.ioAnswerModel || '';
                    expanded.ioFallbackPrompt = data.ioFallbackPrompt || '';
                    expanded.ioParserModel = data.ioParserModel || '';
                    expanded.ioDocuments = data.ioDocuments || [];
                    expanded.ioV2MetaDocs = data.ioV2MetaDocs || [];
                    expanded.ioRequiredFields = data.ioRequiredFields || [];
                    Object.assign(expanded, this.getSummaryMemoryData(data));
                    expanded.nodeTimeout = data.nodeTimeout ?? 0;
                    expanded.nodeMaxRetry = data.nodeMaxRetry ?? 0;
                    break;

                case 'loanInterestV2':
                    expanded.useBaseModelPrompt = data.useBaseModelPrompt !== undefined ? data.useBaseModelPrompt : false;
                    expanded.enableLanguageReplace = data.enableLanguageReplace !== undefined ? data.enableLanguageReplace : false;
                    expanded.enableVoicePromptReplace = data.enableVoicePromptReplace ?? false;
                    expanded.enableTransactionId = data.enableTransactionId ?? false;
                    expanded.liAnswerModel = data.liAnswerModel || '';
                    expanded.liFallbackPrompt = data.liFallbackPrompt || '';
                    expanded.liRequiredFields = data.liRequiredFields || [];
                    expanded.liParserModel = data.liParserModel || '';
                    expanded.liDocuments = data.liDocuments || [];
                    expanded.liV2MetaDocs = data.liV2MetaDocs || [];
                    Object.assign(expanded, this.getSummaryMemoryData(data));
                    expanded.nodeTimeout = data.nodeTimeout ?? 0;
                    expanded.nodeMaxRetry = data.nodeMaxRetry ?? 0;
                    break;

                case 'orchestrator':
                    expanded.orchestratorName = data.orchestratorName || '';
                    // Model configuration
                    expanded.provider = data.provider || '';
                    expanded.model = data.model || '';
                    expanded.temperature = data.temperature ?? 0;
                    expanded.maxTokens = data.maxTokens ?? null;
                    expanded.topP = data.topP ?? 1;
                    // Response Options
                    expanded.streamResponse = data.streamResponse ?? false;
                    expanded.jsonResponse = data.jsonResponse ?? false;
                    // Memory Settings
                    expanded.memoryTurn = data.memoryTurn ?? null;
                    expanded.memoryDuration = data.memoryDuration ?? null;
                    expanded.shortTermMemory = data.shortTermMemory ?? 200;
                    expanded.longTermMemory = data.longTermMemory ?? false;
                    // Prompts
                    expanded.systemPrompt = data.systemPrompt || '';
                    expanded.guardrailPrompt = data.guardrailPrompt || '';
                    expanded.allowAgentGuardrailBypass = data.allowAgentGuardrailBypass ?? true;
                    // Configurable prompts / messages (blank = built-in default)
                    expanded.orchSingleSelectionTemplate = data.orchSingleSelectionTemplate || '';
                    expanded.orchPipelineSelectionTemplate = data.orchPipelineSelectionTemplate || '';
                    expanded.orchVerifySelectionTemplate = data.orchVerifySelectionTemplate || '';
                    expanded.orchAgentListItemTemplate = data.orchAgentListItemTemplate || '';
                    expanded.orchAgentNoToolsLabel = data.orchAgentNoToolsLabel || '';
                    expanded.orchConversationStateTemplate = data.orchConversationStateTemplate || '';
                    expanded.orchConversationStateFactTemplate = data.orchConversationStateFactTemplate || '';
                    expanded.orchCompanionNote = data.orchCompanionNote || '';
                    expanded.orchSequentialContextTemplate = data.orchSequentialContextTemplate || '';
                    expanded.orchCheckerGoalTemplate = data.orchCheckerGoalTemplate || '';
                    expanded.orchFallbackSubGoal = data.orchFallbackSubGoal || '';
                    expanded.orchAggregationTemplate = data.orchAggregationTemplate || '';
                    expanded.orchStrategyHintParallel = data.orchStrategyHintParallel || '';
                    expanded.orchStrategyHintPipeline = data.orchStrategyHintPipeline || '';
                    expanded.orchAggregationFallbackItemTemplate = data.orchAggregationFallbackItemTemplate || '';
                    expanded.orchGuardrailFormatSuffix = data.orchGuardrailFormatSuffix || '';
                    expanded.orchGuardrailUserTemplate = data.orchGuardrailUserTemplate || '';
                    expanded.orchDirectLlmPrompt = data.orchDirectLlmPrompt || '';
                    expanded.orchBlockedMessage = data.orchBlockedMessage || '';
                    expanded.orchDirectLlmEmptyMessage = data.orchDirectLlmEmptyMessage || '';
                    expanded.orchDirectLlmErrorMessage = data.orchDirectLlmErrorMessage || '';
                    expanded.orchNoAgentSucceededMessage = data.orchNoAgentSucceededMessage || '';
                    // Multi-Agent flag
                    expanded.enableMultiAgent = data.enableMultiAgent ?? false;
                    expanded.multiAgentStrategy = data.multiAgentStrategy || 'pipeline';
                    // Extended fields
                    expanded.gender = data.gender || 'male';
                    expanded.typingSpeed = data.typingSpeed ?? null;
                    expanded.maxInputLength = data.maxInputLength ?? 200000;
                    expanded.frequencyPenalty = data.frequencyPenalty ?? null;
                    expanded.presencePenalty = data.presencePenalty ?? null;
                    expanded.stopSequence = data.stopSequence || null;
                    expanded.enableCache = data.enableCache ?? false;
                    expanded.cacheTTLMinutes = data.cacheTTLMinutes ?? 1440;
                    expanded.cacheScope = data.cacheScope || 'flow';
                    expanded.enableSemanticCache = data.enableSemanticCache ?? false;
                    expanded.similarityThreshold = data.similarityThreshold ?? 0.95;
                    expanded.showLineName = data.showLineName ?? false;
                    expanded.loadingBar = data.loadingBar ?? false;
                    expanded.loadingBarDuration = data.loadingBarDuration || 0;
                    expanded.monitorLineUserId = data.monitorLineUserId || null;
                    expanded.monitorSocialID = data.monitorSocialID || null;
                    expanded.forbiddenWords = data.forbiddenWords || [''];
                    expanded.enableTime = data.enableTime ?? null;
                    expanded.timeZone = data.timeZone || null;
                    expanded.enableTransactionId = data.enableTransactionId ?? null;
                    expanded.enableInputMessageReplace = data.enableInputMessageReplace ?? false;
                    expanded.enableVoicePromptReplace = data.enableVoicePromptReplace ?? false;
                    expanded.enableLanguageReplace = data.enableLanguageReplace ?? false;
                    expanded.nodeTimeout = data.nodeTimeout ?? 0;
                     expanded.nodeMaxRetry = data.nodeMaxRetry ?? 0;
                    break;

                // Twin of the flowUtils case — three keys, nothing from the central settings.
                case 'taxPlanningAgent':
                    expanded.agentName = data.agentName || '';
                    expanded.agentDescription = data.agentDescription || '';
                    expanded.bypassGuardrailWhenAlone = data.bypassGuardrailWhenAlone ?? true;
                    expanded.taxKnowledgeEnabled = data.taxKnowledgeEnabled ?? false;
                    expanded.taxKnowledgeSelections = Array.isArray(data.taxKnowledgeSelections)
                        ? data.taxKnowledgeSelections : [];
                    break;
                case 'metaDocuments':
                    expanded.metaDocSelections = Array.isArray(data.metaDocSelections)
                        ? data.metaDocSelections : [];
                    expanded.topNChunks = data.topNChunks || 5;
                    expanded.metaDocDescription = data.metaDocDescription || '';
                    break;
                case 'agent':
                    expanded.agentName = data.agentName || '';
                    expanded.agentDescription = data.agentDescription || '';
                    expanded.personaPrompt = data.personaPrompt || '';
                    expanded.agentFinalSummaryTemplate = data.agentFinalSummaryTemplate || '';
                    expanded.selectedTools = data.selectedTools || [];
                    expanded.enableAgentToolDecision = data.enableAgentToolDecision ?? false;
                    expanded.useCustomAIModel = data.useCustomAIModel ?? false;
                    expanded.agentProvider = data.agentProvider || '';
                    expanded.agentModel = data.agentModel || null;
                    expanded.agentTemperature = data.agentTemperature ?? 0;
                    expanded.agentMaxTokens = data.agentMaxTokens ?? null;
                    expanded.agentTopP = data.agentTopP ?? 1;
                    expanded.agentFrequencyPenalty = data.agentFrequencyPenalty ?? null;
                    expanded.agentPresencePenalty = data.agentPresencePenalty ?? null;
                    expanded.agentStopSequence = data.agentStopSequence || null;
                    expanded.agentStreamResponse = data.agentStreamResponse ?? false;
                    expanded.agentJsonResponse = data.agentJsonResponse ?? false;
                    expanded.agentGender = data.agentGender || 'male';
                    expanded.agentTypingSpeed = data.agentTypingSpeed ?? null;
                    expanded.agentMaxInputLength = data.agentMaxInputLength ?? 200000;
                    expanded.agentMemoryTurn = data.agentMemoryTurn ?? null;
                    expanded.agentMemoryDuration = data.agentMemoryDuration ?? null;
                    expanded.agentShortTermMemory = data.agentShortTermMemory ?? 200;
                    expanded.agentLongTermMemory = data.agentLongTermMemory ?? false;
                    expanded.agentEnableTime = data.agentEnableTime ?? false;
                    expanded.agentTimeZone = data.agentTimeZone || null;
                    expanded.agentEnableTransactionId = data.agentEnableTransactionId ?? false;
                    expanded.agentEnableLanguageReplace = data.agentEnableLanguageReplace ?? false;
                    expanded.agentEnableInsurance = data.agentEnableInsurance ?? false;
                    expanded.agentTimeout = data.agentTimeout ?? 0;
                    expanded.agentMaxRetry = data.agentMaxRetry ?? 0;
                    // Hosted tools — twin ของ flowUtils.expandNodeData
                    expanded.toolsConfig = data.toolsConfig || null;
                    expanded.knowledgeConnections = data.knowledgeConnections || {
                        documents: { nodeId: null, description: '' },
                        databaseIntegration: { nodeId: null, description: '' },
                        knowledgeApi: { nodeId: null, description: '' },
                        products: { nodeId: null, description: '' },
                        sharepoint: { nodeId: null, description: '' }
                    };
                    break;

                case 'tool':
                    expanded.toolName = data.toolName || '';
                    expanded.toolDescription = data.toolDescription || '';
                    expanded.toolType = data.toolType || 'master';
                    expanded.masterToolId = data.masterToolId || null;
                    expanded.endpoint = data.endpoint || '';
                    expanded.method = data.method || 'GET';
                    expanded.headers = data.headers || [];
                    expanded.body = data.body || '';
                    expanded.textContent = data.textContent || '';
                    break;

                case 'router':
                    // Ensure all Router node properties are properly loaded
                    expanded.routerName = data.routerName || '';
                    expanded.routerPrompt = data.routerPrompt || '';
                    expanded.confidenceThreshold = data.confidenceThreshold ?? 0.5;
                    expanded.routerOutputs = data.routerOutputs || [];
                    break;

                case 'advancedRouter':
                    // Ensure all Advanced Router node properties are properly loaded
                    expanded.advancedRouterName = data.advancedRouterName || '';
                    expanded.advancedRouterPrompt = data.advancedRouterPrompt || '';
                    expanded.advancedRouterConfidenceThreshold = data.advancedRouterConfidenceThreshold ?? 0.5;
                    expanded.advancedRouterMaxDepth = data.advancedRouterMaxDepth ?? 2;
                    expanded.advancedRouterOutputs = data.advancedRouterOutputs || [];
                    expanded.advancedRouterCustomSchemaEnabled = data.customSchemaEnabled ?? false;
                    expanded.advancedRouterSchemaTemplateText = JSON.stringify(
                        data.customSchemaTemplate || this.getDefaultAdvancedRouterSchemaTemplate(),
                        null,
                        2
                    );
                    expanded.advancedRouterIntentField = data.schemaFieldMapping?.intentField || 'capability_intent';
                    expanded.advancedRouterConfidenceField = data.schemaFieldMapping?.confidenceField || 'confidence';
                    expanded.advancedRouterSubIntentsField = data.schemaFieldMapping?.subIntentsField || 'intents';
                    expanded.advancedRouterSchemaError = '';
                    expanded.enableCache = data.enableCache ?? false;
                    expanded.cacheTTLMinutes = data.cacheTTLMinutes ?? 1440;
                    expanded.enableSemanticCache = data.enableSemanticCache ?? false;
                    expanded.similarityThreshold = data.similarityThreshold ?? 0.95;
                    expanded.similarityMargin = data.similarityMargin ?? 0.05;
                    break;

                case 'dynamicRouter':
                    expanded.dynamicRouterName = data.dynamicRouterName || '';
                    expanded.dynamicRouterPrompt = data.dynamicRouterPrompt || '';
                    expanded.dynamicRouterConfidenceThreshold = data.dynamicRouterConfidenceThreshold ?? 0.5;
                    expanded.dynamicRouterMaxDepth = data.dynamicRouterMaxDepth ?? 2;
                    expanded.dynamicRouterOutputs = data.dynamicRouterOutputs || [];
                    break;

                case 'dynamicKnowledge':
                    expanded.useMetaCommunityGraphSubCat = data.useMetaCommunityGraphSubCat ?? false;
                    expanded.topNChunks = data.topNChunks || 5;
                    expanded.maxExplicitDocs = data.maxExplicitDocs ?? 10;
                    expanded.metaCommunityId = data.metaCommunityId || null;
                    expanded.metaCommunityTopGroups = data.metaCommunityTopGroups || 3;
                    expanded.metaCommunityTopChunks = data.metaCommunityTopChunks || 5;
                    expanded.metaCommunitySearchMethod = data.metaCommunitySearchMethod || 'topn';
                    expanded.metaCommunityMinCosineScore = data.metaCommunityMinCosineScore || 0.7;
                    expanded.nodeTimeout = data.nodeTimeout ?? 0;
                    expanded.nodeMaxRetry = data.nodeMaxRetry ?? 0;
                    break;

                case 'reformatTTS':
                    // TTS Reformat node properties
                    expanded.provider = data.provider || null;
                    expanded.model = data.model || null;
                    expanded.temperature = data.temperature ?? null;
                    expanded.maxTokens = data.maxTokens ?? null;
                    expanded.systemPrompt = data.systemPrompt || '';
                    expanded.streamResponse = data.streamResponse ?? false;
                    expanded.jsonResponse = data.jsonResponse ?? false;
                    expanded.enableCache = data.enableCache ?? false;
                    expanded.cacheTTLMinutes = data.cacheTTLMinutes ?? 60;
                    expanded.enableSemanticCache = data.enableSemanticCache ?? false;
                    expanded.similarityThreshold = data.similarityThreshold ?? 0.95;
                    expanded.similarityMargin = data.similarityMargin ?? 0.05;
                    expanded.processingMode = data.processingMode || 'LLM';
                    expanded.enableTransactionId = data.enableTransactionId ?? false;
                    expanded.enableLanguageReplace = data.enableLanguageReplace ?? false;
                    expanded.nodeTimeout = data.nodeTimeout ?? 0;
                    expanded.nodeMaxRetry = data.nodeMaxRetry ?? 0;
                    break;

                case 'ssmlFormat':
                    // SSML Format node properties
                    expanded.provider = data.provider || null;
                    expanded.model = data.model || null;
                    expanded.temperature = data.temperature ?? null;
                    expanded.maxTokens = data.maxTokens ?? null;
                    expanded.systemPrompt = data.systemPrompt || '';
                    expanded.streamResponse = data.streamResponse ?? false;
                    expanded.jsonResponse = data.jsonResponse ?? false;
                    expanded.enableCache = data.enableCache ?? false;
                    expanded.cacheTTLMinutes = data.cacheTTLMinutes ?? 60;
                    expanded.enableSemanticCache = data.enableSemanticCache ?? false;
                    expanded.similarityThreshold = data.similarityThreshold ?? 0.95;
                    expanded.similarityMargin = data.similarityMargin ?? 0.05;
                    expanded.processingMode = data.processingMode || 'RuleBased';
                    expanded.enableTransactionId = data.enableTransactionId ?? false;
                    expanded.nodeTimeout = data.nodeTimeout ?? 0;
                    expanded.nodeMaxRetry = data.nodeMaxRetry ?? 0;
                    break;

                case 'queryRewriter':
                    // Query Rewriter node properties
                    expanded.queryRewriterMode = data.queryRewriterMode || 'single';
                    expanded.queryRewriterEnableLanguageReplace = data.queryRewriterEnableLanguageReplace ?? false;
                    expanded.queryRewriterProvider = data.queryRewriterProvider || null;
                    expanded.queryRewriterModel = data.queryRewriterModel || null;
                    expanded.queryRewriterApiVersion = data.queryRewriterApiVersion || null;
                    expanded.queryRewriterTemperature = data.queryRewriterTemperature ?? null;
                    expanded.queryRewriterMaxTokens = data.queryRewriterMaxTokens ?? null;
                    expanded.queryRewriterTopP = data.queryRewriterTopP ?? null;
                    expanded.queryRewriterFrequencyPenalty = data.queryRewriterFrequencyPenalty ?? null;
                    expanded.queryRewriterPresencePenalty = data.queryRewriterPresencePenalty ?? null;
                    expanded.queryRewriterSystemPrompt = data.queryRewriterSystemPrompt || '';
                    expanded.queryRewriterMemoryTurn = data.queryRewriterMemoryTurn ?? null;
                    expanded.queryRewriterMemoryDuration = data.queryRewriterMemoryDuration ?? null;
                    // Response Options
                    expanded.queryRewriterStreamResponse = data.queryRewriterStreamResponse ?? false;
                    expanded.queryRewriterJsonResponse = data.queryRewriterJsonResponse ?? false;
                    expanded.queryRewriterTimeout = data.queryRewriterTimeout ?? 0;
                    expanded.queryRewriterMaxRetry = data.queryRewriterMaxRetry ?? 0;
                    break;

                case 'flowTool':
                    // Flow Tool node properties
                    expanded.flowToolName = data.flowToolName || 'Flow Tool';
                    expanded.flowToolDescription = data.flowToolDescription || '';
                    // Restore full internal nodes format (add back 'type: flow-node')
                    expanded.internalNodes = (data.internalNodes || []).map(n => ({
                        id: n.id,
                        type: 'flow-node', // Restore constant type
                        position: n.position,
                        data: n.data
                    }));
                    // Restore full internal edges format (add back 'type' and 'markerEnd')
                    expanded.internalEdges = (data.internalEdges || []).map(e => ({
                        id: e.id,
                        source: e.source,
                        target: e.target,
                        sourceHandle: e.sourceHandle || 'output', // Restore default
                        targetHandle: e.targetHandle || 'input',  // Restore default
                        type: 'flow-edge',   // Restore constant type
                        markerEnd: 'arrowclosed' // Restore constant marker
                    }));
                    expanded.internalNodeCount = data.internalNodeCount || 0;
                    expanded.showWarning = data.showWarning ?? false;
                    break;
            }
            return expanded;
        },
        validateFlatFlow(flowData) {
            const errors = [];

            // Check for required flow properties
            if (!flowData.name || flowData.name.trim() === '') {
                errors.push('Flow name is required');
            }

            // Check for at least one node
            if (!flowData.flowDetails || flowData.flowDetails.length === 0) {
                errors.push('Flow must contain at least one node');
            }

            // Validate nodes
            const nodeIds = new Set();
            flowData.flowDetails.forEach((node, index) => {
                if (!node.id) {
                    errors.push(`Node ${index + 1} is missing ID`);
                }

                if (nodeIds.has(node.id)) {
                    errors.push(`Duplicate node ID found: ${node.id}`);
                }
                nodeIds.add(node.id);

                if (!node.nodeType) {
                    errors.push(`Node ${node.id} is missing nodeType`);
                }

                if (!node.position) {
                    errors.push(`Node ${node.id} is missing position`);
                }

                // Validate node-specific data
                // this.validateNodeData(node, errors);
            });

            // Validate target references
            const allNodeIds = new Set(flowData.flowDetails.map(node => node.id));
            flowData.flowDetails.forEach(node => {
                if (node.targets && Array.isArray(node.targets)) {
                    node.targets.forEach((target, targetIndex) => {
                        if (!target.nodeId) {
                            errors.push(`Node ${node.id} target ${targetIndex + 1} is missing nodeId`);
                        } else if (!allNodeIds.has(target.nodeId)) {
                            errors.push(`Node ${node.id} references non-existent target node: ${target.nodeId}`);
                        }
                    });
                }
            });

            return {
                isValid: errors.length === 0,
                errors
            };
        },

        // API call to save flow
        async saveFlowToAPI(flowData, apiMethod = 'CreateFlow') {
            return new Promise((resolve, reject) => {
                const apiFlowData = {
                    ...flowData,
                    flowDetails: flowData.flowDetails.map(node => ({
                        ...node,
                        position: JSON.stringify(node.position),
                        data: JSON.stringify(node.data),
                        targets: JSON.stringify(node.targets)
                    }))
                };
                apiFlowData.flowId = this.flowId;
                this.$call[apiMethod](
                    apiFlowData,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            resolve({
                                success: true,
                                flowId: dataResponse.data?.newFlowId || dataResponse.data?.flowId || dataResponse.data?.id,
                                message: dataResponse.message
                            });
                        } else {
                            resolve({
                                success: false,
                                message: dataResponse.message || 'Unknown error occurred'
                            });
                        }
                    },
                    (errorResponse) => {
                        reject(new Error(errorResponse.message || 'API call failed'));
                    }
                );
            });
        },

        // Validate flow structure
        validateFlow(flowData) {
            const errors = [];

            // Check for required flow properties
            if (!flowData.name || flowData.name.trim() === '') {
                errors.push('Flow name is required');
            }

            // Check for at least one node
            if (!flowData.nodes || flowData.nodes.length === 0) {
                errors.push('Flow must contain at least one node');
            }

            // Validate nodes
            flowData.nodes.forEach((node, index) => {
                if (!node.id) {
                    errors.push(`Node ${index + 1} is missing ID`);
                }
                if (!node.data || !node.data.type) {
                    errors.push(`Node ${index + 1} is missing type`);
                }
                if (!node.position) {
                    errors.push(`Node ${index + 1} is missing position`);
                }

                // Validate specific node types
                switch (node.data.type) {
                    case 'model':
                        if (!node.data.selectedModel) {
                            errors.push(`AI Model node (${node.id}) requires a selected model`);
                        }
                        break;
                    case 'intent':
                        if (!node.data.intents || node.data.intents.length === 0) {
                            errors.push(`Intent node (${node.id}) requires at least one intent`);
                        }
                        break;
                    case 'switch':
                        if (!node.data.switchOutputs || node.data.switchOutputs.length < 2) {
                            errors.push(`Switch node (${node.id}) requires at least two output paths`);
                        }
                        break;
                    case 'apiTrigger':
                        if (!node.data.apiUrl) {
                            errors.push(`API Trigger node (${node.id}) requires API URL`);
                        }
                        break;
                }
            });

            // Validate edges
            flowData.edges.forEach((edge, index) => {
                if (!edge.source || !edge.target) {
                    errors.push(`Edge ${index + 1} is missing source or target`);
                }

                // Check if source and target nodes exist
                const sourceExists = flowData.nodes.some(node => node.id === edge.source);
                const targetExists = flowData.nodes.some(node => node.id === edge.target);

                if (!sourceExists) {
                    errors.push(`Edge ${index + 1} references non-existent source node: ${edge.source}`);
                }
                if (!targetExists) {
                    errors.push(`Edge ${index + 1} references non-existent target node: ${edge.target}`);
                }
            });

            return {
                isValid: errors.length === 0,
                errors
            };
        },

        // Quick save function for auto-save
        async quickSave() {
            if (this.flowId) {
                // Only auto-save if flow already exists
                await this.SaveFlow();
            }
        },

        // Show success message
        showSuccess(message) {
            this.snackbarMessage = message;
            this.snackBarColor = 'success';
            this.showSnackbar = true;
        },

        // Show error message
        showError(message) {
            this.snackbarMessage = message;
            this.snackBarColor = 'error';
            this.showSnackbar = true;
            console.error(message);
        },

        // Show warning message
        showWarning(message) {
            this.snackbarMessage = message;
            this.snackBarColor = 'warning';
            this.showSnackbar = true;
        },

        // Show info message
        showInfo(message) {
            this.snackbarMessage = message;
            this.snackBarColor = 'info';
            this.showSnackbar = true;
        },
        // Load flow data for editing
        async loadFlowForEdit(flowId) {
            try {
                this.dialog = true;

                const response = await this.loadFlowFromAPI(flowId);

                if (response.success) {
                    const flowData = response.data;
                    // Convert flow data to editor format
                    if (flowData.flowDetails && Array.isArray(flowData.flowDetails)) {
                        const { nodes, edges } = this.convertFromFlatStructure(flowData.flowDetails);
                        this.tempNode = nodes;
                        this.tempEdge = edges;
                    } else {
                        // Fallback for old format
                        this.tempNode = flowData.nodes || [];
                        this.tempEdge = flowData.edges || [];
                    }
                    let messageInputTemp = this.tempNode.find(n => n.data && n.data.type === 'messageInput');
                    if (messageInputTemp) {
                        // Only filter models if uniqueAiModelOptions is available (to prevent losing models on reload)
                        if (this.uniqueAiModelOptions && this.uniqueAiModelOptions.length > 0) {
                            this.unableToAccessModel = messageInputTemp.data.selectedModel;
                            let tempUniqueModels = this.uniqueAiModelOptions.map(option => option.value);
                            this.unableToAccessModel = this.unableToAccessModel.filter(model => !tempUniqueModels.includes(model));
                            this.tempNode = this.tempNode.map(node => {
                                if (node.data && node.data.type === 'messageInput') {
                                    let filteredModels = node.data.selectedModel.filter(model => !this.unableToAccessModel.includes(model));
                                    return {
                                        ...node,
                                        data: {
                                            ...node.data,
                                            selectedModel: filteredModels
                                        }
                                    };
                                }
                                return node;
                            });
                        } else {
                            // If uniqueAiModelOptions is not loaded yet, keep original selectedModel values
                            // They will be filtered later when models are loaded
                            console.warn('AI Model options not loaded yet, keeping original selectedModel values');
                        }
                    }
                    // Update flow metadata
                    this.flowId = flowData.id;
                    this.flowName = flowData.name;
                    this.flowCreatedAt = flowData.createDate;

                    this.showSuccess(`Flow "${flowData.name}" loaded successfully`);

                    // Fit view after loading nodes
                    this.$nextTick(() => {
                        // Check validation for all nodes after loading
                        this.checkAllNodesValidation();

                        setTimeout(() => {
                            // Fit view to show all nodes if in edit or view mode
                            if ((this.isEditMode || this.isViewMode) && this.$refs.vueFlow && this.tempNode.length > 0) {
                                try {
                                    this.$refs.vueFlow.fitView({
                                        padding: 0.2,
                                        minZoom: 0.5,
                                        maxZoom: 1.5,
                                        duration: 800,
                                        includeHiddenNodes: false
                                    });
                                } catch (error) {
                                    console.warn('Failed to fit view:', error);
                                }
                            }
                        }, 200);
                    });

                } else {
                    throw new Error(response.message || 'Failed to load flow');
                }

            } catch (error) {
                console.error('Error loading flow for edit:', error);
                this.showError(`Failed to load flow: ${error.message}`);

                // Redirect back to flow list on error
                this.$router.push('/FlowList');
            } finally {
                this.dialog = false;
                this.$nextTick(() => {
                    setTimeout(() => {
                        // Allow time for UI to stabilize
                    }, 300);
                });
            }
        },
        async handleImportedData(importDataString) {
            try {
                const flowData = JSON.parse(importDataString);

                // Validate imported data
                if (!flowData.name || !flowData.flowDetails) {
                    throw new Error('Invalid flow structure');
                }

                // Convert imported data to editor format
                if (flowData.flowDetails && Array.isArray(flowData.flowDetails)) {
                    const { nodes, edges } = this.convertFromFlatStructure(flowData.flowDetails);
                    this.tempNode = nodes;
                    this.tempEdge = edges;
                } else {
                    this.tempNode = flowData.nodes || [];
                    this.tempEdge = flowData.edges || [];
                }

                // Set flow metadata
                this.flowId = null; // New flow from import
                this.flowName = `${flowData.name} (Imported)`;
                this.isEditMode = true; // Allow editing of imported flow

                this.showSuccess(`Flow "${flowData.name}" imported successfully`);

                // Clear the import data from URL
                this.$router.replace({ path: '/FlowCreator' });

                this.$nextTick(() => {
                    setTimeout(() => {
                        // Allow time for UI to stabilize
                    }, 300);
                });
                this.$nextTick(() => {
                    setTimeout(() => {
                        // Fit view after importing nodes
                        if (this.$refs.vueFlow && this.tempNode.length > 0) {
                            try {
                                this.$refs.vueFlow.fitView({
                                    padding: 0.1,
                                    minZoom: 0.5,
                                    maxZoom: 1.5,
                                    duration: 800,
                                    includeHiddenNodes: false
                                });
                            } catch (error) {
                                console.warn('Failed to fit view after import:', error);
                            }
                        }
                    }, 200);
                });

            } catch (error) {
                console.error('Error handling imported data:', error);
                this.showError('Failed to import flow data');
                this.$router.push('/FlowList');
            }
        },
        // Set view-only mode
        setViewOnlyMode() {
            this.$nextTick(() => {
                if (this.$refs.vueFlow) {
                    const flow = this.$refs.vueFlow;
                    // Lock all interactions
                    flow.nodesDraggable = false;
                    flow.nodesConnectable = false;
                    flow.elementsSelectable = false;
                    this.interactiveControls = false;
                }
            });
        },

        // Load flow with nested structure support
        async LoadFlow(flowId) {
            try {
                this.dialog = true;

                const response = await this.loadFlowFromAPI(flowId);

                if (response.success) {
                    const flowData = response.data;

                    // Check if it's flat structure (version 2.0+) or old nested structure
                    if (flowData.metadata?.version && parseFloat(flowData.metadata.version) >= 2.0) {
                        // Check if it uses targets or source format
                        const firstNode = flowData.nodes[0];
                        if (firstNode && firstNode.targets !== undefined) {
                            // New targets format
                            const { nodes, edges } = this.convertFromFlatStructure(flowData.nodes);
                            this.tempNode = nodes;
                            this.tempEdge = edges;
                        } else if (firstNode && firstNode.source !== undefined) {
                            // Old source format - convert to new format
                            const { nodes, edges } = this.convertFromSourceFormat(flowData.nodes);
                            this.tempNode = nodes;
                            this.tempEdge = edges;
                        } else {
                            throw new Error('Invalid flow data format');
                        }
                    } else {
                        // Load old nested structure directly (backward compatibility)
                        this.tempNode = flowData.nodes.map(node => ({
                            ...node,
                            selected: false
                        }));
                        this.tempEdge = flowData.edges || [];
                    }

                    // Update flow metadata
                    this.flowId = flowData.id;
                    this.flowName = flowData.name;
                    this.flowCreatedAt = flowData.metadata?.createdAt;

                    // Restore viewport if available
                    if (flowData.metadata?.viewport && this.$refs.vueFlow) {
                        setTimeout(() => {
                            this.$refs.vueFlow.setViewport(flowData.metadata.viewport);
                        }, 300);
                    }

                    // Check validation for all nodes after loading
                    this.$nextTick(() => {
                        this.checkAllNodesValidation();
                    });

                    this.showSuccess('Flow loaded successfully');
                } else {
                    throw new Error(response.message || 'Failed to load flow');
                }

            } catch (error) {
                console.error('Error loading flow:', error);
                this.showError(`Failed to load flow: ${error.message}`);
            } finally {
                this.dialog = false;
            }
        },
        async loadFlowFromAPI(flowId) {
            return new Promise((resolve, reject) => {
                this.$call.GetFlowById(
                    flowId,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            resolve({
                                success: true,
                                data: dataResponse.data
                            });
                        } else {
                            resolve({
                                success: false,
                                message: dataResponse.message || 'Failed to load flow'
                            });
                        }
                    },
                    (errorResponse) => {
                        reject(new Error(errorResponse.message || 'API call failed'));
                    }
                );
            });
        },
        // Helper method to convert old source format to new targets format
        convertFromSourceFormat(sourceNodes) {
            const flowNodes = [];
            const flowEdges = [];

            sourceNodes.forEach(sourceNode => {
                // Create flow node
                const flowNode = {
                    id: sourceNode.id,
                    type: 'flow-node',
                    position: sourceNode.position,
                    data: {
                        type: sourceNode.nodeType,
                        label: sourceNode.data.label,
                        ...this.expandNodeData(sourceNode.nodeType, sourceNode.data)
                    },
                    selected: false
                };

                flowNodes.push(flowNode);

                // Create edge if source exists
                if (sourceNode.source) {
                    const edge = {
                        id: `e${sourceNode.source}-${sourceNode.id}-${Date.now()}`,
                        source: sourceNode.source,
                        target: sourceNode.id,
                        sourceHandle: sourceNode.sourceHandle || null,
                        targetHandle: 'input',
                        type: 'flow-edge',
                        markerEnd: 'arrowclosed',
                        animated: false,
                        showButton: false
                    };

                    flowEdges.push(edge);
                }
            });

            return { nodes: flowNodes, edges: flowEdges };
        },


        clearFlow() {
            this.tempNode = [];
            this.tempEdge = [];
            
            // Save state to history
            this.saveStateToHistory('Clear Flow');
            
            this.showSuccess('Flow cleared');
        },
        // Methods for managing intent group, intents, and their actions
        addInspectorIntent() {
            if (!this.nodeInspector.intents) {
                this.nodeInspector.intents = [];
            }
            const defaultBasePrompt = `Find the Intent from the message of users.
#Condition for Response
1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0`;

            const newIntent = {
                id: `intent-${this.generateGuid()}`, // Use GUID
                name: `Intent Name ${this.nodeInspector.intents.length + 1}`,
                priority: 0, // Default priority
                prompt: '',
                basePrompt: defaultBasePrompt, // Use default base prompt
                enableBasePrompt: false, // Disable base prompt by default
                messages: [
                    { id: `msg-${this.generateGuid()}`, text: '' } // Use GUID
                ],
                mainAction: {
                    id: `main-${this.generateGuid()}`, // Use GUID
                    label: 'Main Action'
                },
                parallelActions: []
            };
            this.nodeInspector.intents.push(newIntent);
            this.updateIntentData();
        },
        removeInspectorIntent(index) {
            if (this.nodeInspector.intents && this.nodeInspector.intents.length > 1) {
                const intentToRemove = this.nodeInspector.intents[index];

                // Collect all source handles related to this intent (main action + parallel actions)
                const sourceHandlesToRemove = [];

                // Add main action source handle
                sourceHandlesToRemove.push(`${intentToRemove.id}-main`);

                // Add parallel actions source handles
                if (intentToRemove.parallelActions) {
                    intentToRemove.parallelActions.forEach(parallel => {
                        sourceHandlesToRemove.push(`${intentToRemove.id}-${parallel.id}`);
                    });
                }

                // Remove all edges connected to this intent's actions
                this.tempEdge = this.tempEdge.filter(edge =>
                    !(edge.source === this.selectedNode.id && sourceHandlesToRemove.includes(edge.sourceHandle))
                );

                // Remove the intent
                this.nodeInspector.intents.splice(index, 1);
                this.updateIntentData();
            }
        },
        addInspectorParallelAction(intentIndex) {
            const intent = this.nodeInspector.intents[intentIndex];
            if (!intent.parallelActions) {
                intent.parallelActions = [];
            }
            const newParallel = {
                id: `parallel-${this.generateGuid()}`,
                label: `Parallel Action ${intent.parallelActions.length + 1}`
            };
            intent.parallelActions.push(newParallel);
            this.updateIntentData();
        },
        removeInspectorParallelAction(intentIndex, parallelIndex) {
            const intent = this.nodeInspector.intents[intentIndex];
            if (intent.parallelActions && intent.parallelActions.length > parallelIndex) {
                const parallelToRemove = intent.parallelActions[parallelIndex];
                const sourceHandleToRemove = `${intent.id}-${parallelToRemove.id}`;

                // Remove edges connected to this parallel action
                this.tempEdge = this.tempEdge.filter(edge =>
                    !(edge.source === this.selectedNode.id && edge.sourceHandle === sourceHandleToRemove)
                );

                // Remove the parallel action
                intent.parallelActions.splice(parallelIndex, 1);
                this.updateIntentData();
            }
        },
        addInspectorIntentMessage(intentIndex) {
            const intent = this.nodeInspector.intents[intentIndex];
            if (!intent.messages) {
                intent.messages = [];
            }
            const newMessage = {
                id: `msg-${this.generateGuid()}`,
                text: ''
            };
            intent.messages.push(newMessage);
            this.updateIntentData();
        },
        removeInspectorIntentMessage(intentIndex, messageIndex) {
            const intent = this.nodeInspector.intents[intentIndex];
            if (intent.messages && intent.messages.length > 1) {
                intent.messages.splice(messageIndex, 1);
                this.updateIntentData();
            }
        },
        convertIntentsToActions(intents) {
            const actions = [];
            intents.forEach((intent, intentIndex) => {
                // Add main action for each intent
                actions.push({
                    id: `${intent.id}-main`,
                    label: `${intent.mainAction.label}`,
                    type: 'main',
                    intentId: intent.id,
                });

                // Add parallel actions for each intent
                intent.parallelActions.forEach((parallel, parallelIndex) => {
                    actions.push({
                        id: `${intent.id}-${parallel.id}`,
                        label: `${parallel.label}`,
                        type: 'parallel',
                        intentId: intent.id,
                    });
                });
            });
            return actions;
        },
        convertIntentsToOutputs(intents) {
            const outputs = [];
            intents.forEach((intent, intentIndex) => {
                // Add main action output for each intent
                outputs.push({
                    id: `${intent.id}-main`,
                    label: `${intent.mainAction.label}`,
                    type: 'main',
                    intentId: intent.id,
                });

                // Add parallel outputs for each intent
                intent.parallelActions.forEach((parallel, parallelIndex) => {
                    outputs.push({
                        id: `${intent.id}-${parallel.id}`,
                        label: `${parallel.label}`,
                        type: 'parallel',
                        intentId: intent.id,
                    });
                });
            });
            return outputs;
        },
        // Methods for managing intent actions and parallels (Legacy - keep for compatibility)
        addInspectorAction() {
            if (!this.nodeInspector.actions) {
                this.nodeInspector.actions = [];
            }
            const newAction = {
                id: `action-${this.generateGuid()}`,
                label: `Action ${this.nodeInspector.actions.length + 1}`,
                parallels: [
                    { id: `parallel-${this.generateGuid()}`, label: 'Parallel Action 1' }
                ]
            };
            this.nodeInspector.actions.push(newAction);
        },
        removeInspectorAction(index) {
            if (this.nodeInspector.actions && this.nodeInspector.actions.length > 1) {
                this.nodeInspector.actions.splice(index, 1);
            }
        },
        addInspectorParallel(actionIndex) {
            if (!this.nodeInspector.actions[actionIndex].parallels) {
                this.nodeInspector.actions[actionIndex].parallels = [];
            }
            const action = this.nodeInspector.actions[actionIndex];
            const newParallel = {
                id: `parallel-${this.generateGuid()}`,
                label: `Parallel Action ${action.parallels.length + 1}`
            };
            action.parallels.push(newParallel);
        },
        removeInspectorParallel(actionIndex, parallelIndex) {
            const action = this.nodeInspector.actions[actionIndex];
            if (action.parallels && action.parallels.length > 1) {
                action.parallels.splice(parallelIndex, 1);
            }
        },

        // Switch methods
        addInspectorSwitchOutput() {
            if (!this.nodeInspector.switchOutputs) {
                this.nodeInspector.switchOutputs = [];
            }
            const newOutput = {
                id: `switch-output-${this.generateGuid()}`,
                name: `Output ${this.nodeInspector.switchOutputs.length + 1}`,
                condition: '',
                description: '',
                // Simple condition builder properties
                conditionMode: 'simple',
                conditionType: 'text_contains',
                sourceField: 'input.text',
                searchValue: '',
                compareValue: '',
                caseSensitive: false,
                operator: 'gt',
                expectedValue: true,
                regexPattern: '',
                regexFlags: '',
                lengthOperator: 'gt',
                lengthValue: 0,
                lengthValueMax: 100
            };
            this.nodeInspector.switchOutputs.push(newOutput);
            this.updateSwitchData();
        },
        removeInspectorSwitchOutput(index) {
            if (this.nodeInspector.switchOutputs && this.nodeInspector.switchOutputs.length > 2) {
                this.nodeInspector.switchOutputs.splice(index, 1);
                this.updateSwitchData();
            }
        },
        updateSwitchData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'switch') return;

            this.updateNodeProperty('switchOutputs', this.nodeInspector.switchOutputs);
            this.updateNodeProperty('switchName', this.nodeInspector.switchName);
        },

        // JSON Condition methods
        updateJsonConditionData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'jsonCondition') return;

            this.updateNodeProperty('jsonConditionName', this.nodeInspector.jsonConditionName);
            this.updateNodeProperty('matchMode', this.nodeInspector.matchMode);
            this.updateNodeProperty('conditions', JSON.parse(JSON.stringify(this.nodeInspector.jsonConditions || [])));
            this.updateNodeProperty('matchLabel', this.nodeInspector.matchLabel);
            this.updateNodeProperty('fallbackLabel', this.nodeInspector.fallbackLabel);
        },
        addJsonConditionRule() {
            if (!this.nodeInspector.jsonConditions) {
                this.nodeInspector.jsonConditions = [];
            }
            this.nodeInspector.jsonConditions.push({
                id: `jc-${this.generateGuid()}`,
                jsonPath: '',
                operator: '',
                compareValue: ''
            });
            // Auto-expand the newly added panel
            this.jsonConditionExpandedPanel = this.nodeInspector.jsonConditions.length - 1;
            this.updateJsonConditionData();
        },
        removeJsonConditionRule(index) {
            if (this.nodeInspector.jsonConditions && this.nodeInspector.jsonConditions.length > 1) {
                this.nodeInspector.jsonConditions.splice(index, 1);
                this.updateJsonConditionData();
            }
        },
        getJsonConditionOperatorLabel(op) {
            const labels = {
                'equals': '=', 'not_equals': '\u2260', 'contains': '\u2283', 'not_contains': '\u2285',
                'starts_with': 'starts', 'ends_with': 'ends',
                'greater_than': '>', 'less_than': '<', 'greater_equal': '\u2265', 'less_equal': '\u2264',
                'is_empty': 'empty', 'is_not_empty': '\u2260 empty',
                'array_empty': '[] empty', 'array_not_empty': '[] \u2260 empty', 'array_contains': '[] \u2283',
                'array_length_min': '[] \u2265', 'array_length_max': '[] \u2264', 'array_length_equals': '[] =',
                'array_all_unique': '[] unique',
                'exists': '\u2203', 'not_exists': '\u2204',
                'is_true': '= true', 'is_false': '= false',
                'regex': '~ regex',
                'type_is': 'type:', 'one_of': '\u2208', 'not_one_of': '\u2209', 'format': 'fmt:',
                'equals_field': '= field', 'greater_than_field': '> field', 'less_than_field': '< field'
            };
            return labels[op] || op || '?';
        },

        // ── Service Matcher (unified) rule methods ────────────────────────────────
        // Both rule blocks (Favorite / Service) share the jsonCondition operator list
        // and label helper; only the backing node property differs.
        // kind = 'favorite' | 'service'
        // node.data property that is persisted by extractNodeData
        serviceMatcherRuleKey(kind) {
            return kind === 'service' ? 'serviceConditions' : 'favoriteConditions';
        },
        // nodeInspector property the inspector UI is bound to (kept separate from the
        // node property so updateNodeProperty() cannot replace the bound array)
        serviceMatcherInspectorRuleKey(kind) {
            return kind === 'service' ? 'serviceMatcherServiceRules' : 'serviceMatcherFavoriteRules';
        },
        // Deep-clone a rule array coming from node.data / the API, falling back to the
        // documented default rule (match_count > 0) when it is missing or unusable.
        cloneServiceMatcherRules(raw) {
            let list = raw;
            if (typeof list === 'string') {
                try {
                    list = JSON.parse(list);
                } catch (e) {
                    list = null;
                }
            }
            if (Array.isArray(list) && list.length > 0) {
                return JSON.parse(JSON.stringify(list));
            }
            return [this.buildServiceMatcherDefaultRule()];
        },
        buildServiceMatcherDefaultRule() {
            return {
                id: `sm-${this.generateGuid()}`,
                jsonPath: 'match_count',
                operator: 'greater_than',
                compareValue: '0'
            };
        },
        // apiRequestHeaders may come back from the API as a JSON string; normalise to an array.
        parseServiceMatcherHeaders(raw) {
            let list = raw;
            if (typeof list === 'string') {
                try {
                    list = JSON.parse(list);
                } catch (e) {
                    list = null;
                }
            }
            if (Array.isArray(list)) {
                return JSON.parse(JSON.stringify(list));
            }
            return [{ key: '', value: '' }];
        },
        updateServiceMatcherRules(kind) {
            if (!this.selectedNode || this.selectedNode.data.type !== 'serviceMatcher') return;

            const inspectorKey = this.serviceMatcherInspectorRuleKey(kind);
            this.updateNodeProperty(
                this.serviceMatcherRuleKey(kind),
                JSON.parse(JSON.stringify(this.nodeInspector[inspectorKey] || []))
            );
        },
        addServiceMatcherRule(kind) {
            const inspectorKey = this.serviceMatcherInspectorRuleKey(kind);
            if (!Array.isArray(this.nodeInspector[inspectorKey])) {
                this.nodeInspector[inspectorKey] = [];
            }
            this.nodeInspector[inspectorKey].push({
                id: `sm-${this.generateGuid()}`,
                jsonPath: '',
                operator: '',
                compareValue: ''
            });
            // Auto-expand the newly added panel
            const panelIndex = this.nodeInspector[inspectorKey].length - 1;
            if (kind === 'service') {
                this.serviceMatcherServicePanel = panelIndex;
            } else {
                this.serviceMatcherFavoritePanel = panelIndex;
            }
            this.updateServiceMatcherRules(kind);
        },
        removeServiceMatcherRule(kind, index) {
            const inspectorKey = this.serviceMatcherInspectorRuleKey(kind);
            // Always keep at least one rule so the branch stays configurable.
            if (Array.isArray(this.nodeInspector[inspectorKey]) && this.nodeInspector[inspectorKey].length > 1) {
                this.nodeInspector[inspectorKey].splice(index, 1);
                this.updateServiceMatcherRules(kind);
            }
        },

        // Fallback Return methods
        // Build the default primary (success) Output JSON template for a service type.
        // The navlist[0] item is a per-match template: at runtime the node expands it once per
        // entry in the incoming "matches" array, substituting {{field}} placeholders
        // (favorite_id -> toValue, thumbnail -> thubmnail, favorite_name, amount).
        // TRANSFER ships this template; other types start empty (admin fills in).
        buildServiceFallbackPrimaryDefault(serviceType) {
            const type = (serviceType || 'TRANSFER').toUpperCase();
            // TOPUP / BILL_PAYMENT add reference fields (ref1 = phone / account / smart-card
            // S-N typed by the user; ref2..ref4 reserved). TRANSFER carries "accountNumber"
            // from account_no instead — set only for bank destinations (toType "B"); PromptPay
            // ("P") and generic ("N") resolve to null.
            if (type === 'TOPUP' || type === 'BILL_PAYMENT') {
                return JSON.stringify({
                    message: '{{auto_message}}',
                    action: 'callback',
                    navlist: [
                        {
                            serviceType: serviceType || 'TRANSFER',
                            toType: '{{to_type}}',
                            toValue: '{{favorite_id}}',
                            ref1: '{{ref1}}',
                            ref2: '{{ref2}}',
                            ref3: '{{ref3}}',
                            ref4: '{{ref4}}',
                            amount: '{{amount}}',
                            favorite_name: '{{favorite_name}}'
                        }
                    ]
                }, null, 2);
            }
            return JSON.stringify({
                message: '{{auto_message}}',
                action: 'callback',
                navlist: [
                    {
                        serviceType: serviceType || 'TRANSFER',
                        toType: '{{to_type}}',
                        toValue: '{{favorite_id}}',
                        accountNumber: '{{account_no}}',
                        amount: '{{amount}}',
                        thubmnail: '{{thumbnail}}',
                        favorite_name: '{{favorite_name}}'
                    }
                ]
            }, null, 2);
        },
        // Build the default fallback (no-match) Output JSON for a service type. The navlist item
        // mirrors the per-type shape of buildServiceFallbackPrimaryDefault(): TOPUP /
        // BILL_PAYMENT carry ref1..ref4, TRANSFER carries accountNumber. Kept in sync with the
        // backend ServiceFallbackNodeProcessor.BuildDefaultResponse().
        buildServiceFallbackNoMatchDefault(serviceType) {
            // Nothing matched, so the item only says there is no destination. Reference fields
            // (ref1..ref4) and accountNumber belong to a matched destination — leaving them out
            // keeps the same shape the navigation prompts emit. {{amount}} is substituted at
            // runtime from the matcher envelope and drops out when the user said no amount;
            // toValue: null drops out the same way.
            const item = {
                serviceType: serviceType || 'TRANSFER',
                toType: 'N',
                toValue: null,
                amount: '{{amount}}',
            };
            return JSON.stringify({ message: '{{auto_message}}', action: 'callback', navlist: [item] }, null, 2);
        },
        updateServiceFallbackData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'serviceFallback') return;

            this.updateNodeProperty('serviceType', this.nodeInspector.serviceType);
            // Primary (success) output
            this.updateNodeProperty('primaryMessageTH', this.nodeInspector.serviceFallbackPrimaryMessageTH);
            this.updateNodeProperty('primaryMessageEN', this.nodeInspector.serviceFallbackPrimaryMessageEN);
            this.updateNodeProperty('primaryMessageCN', this.nodeInspector.serviceFallbackPrimaryMessageCN);
            this.updateNodeProperty('primarySingleMessageTH', this.nodeInspector.serviceFallbackPrimarySingleMessageTH);
            this.updateNodeProperty('primarySingleMessageEN', this.nodeInspector.serviceFallbackPrimarySingleMessageEN);
            this.updateNodeProperty('primarySingleMessageCN', this.nodeInspector.serviceFallbackPrimarySingleMessageCN);
            this.updateNodeProperty('primaryAllowEditOutput', this.nodeInspector.serviceFallbackPrimaryAllowEditOutput);
            this.updateNodeProperty('primaryOutputJson', this.nodeInspector.serviceFallbackPrimaryOutputJson);
            // Fallback output
            this.updateNodeProperty('messageTH', this.nodeInspector.serviceFallbackMessageTH);
            this.updateNodeProperty('messageEN', this.nodeInspector.serviceFallbackMessageEN);
            this.updateNodeProperty('messageCN', this.nodeInspector.serviceFallbackMessageCN);
            this.updateNodeProperty('allowEditOutput', this.nodeInspector.serviceFallbackAllowEditOutput);
            this.updateNodeProperty('outputJson', this.nodeInspector.serviceFallbackOutputJson);
        },
        onServiceTypeChange(newType) {
            this.nodeInspector.serviceType = newType;
            // Auto-regenerate primary output JSON if editing is not allowed
            if (!this.nodeInspector.serviceFallbackPrimaryAllowEditOutput) {
                this.nodeInspector.serviceFallbackPrimaryOutputJson = this.buildServiceFallbackPrimaryDefault(newType);
            } else {
                // Update serviceType inside the existing primary JSON
                try {
                    let obj = JSON.parse(this.nodeInspector.serviceFallbackPrimaryOutputJson);
                    if (obj.navlist && obj.navlist.length > 0) {
                        obj.navlist.forEach(item => { item.serviceType = newType; });
                    }
                    this.nodeInspector.serviceFallbackPrimaryOutputJson = JSON.stringify(obj, null, 2);
                } catch (e) { /* keep as-is if parse fails */ }
            }
            // Auto-regenerate fallback output JSON if editing is not allowed
            if (!this.nodeInspector.serviceFallbackAllowEditOutput) {
                this.nodeInspector.serviceFallbackOutputJson = this.buildServiceFallbackNoMatchDefault(newType);
            } else {
                // Update serviceType inside the existing fallback JSON
                try {
                    let obj = JSON.parse(this.nodeInspector.serviceFallbackOutputJson);
                    if (obj.navlist && obj.navlist.length > 0) {
                        obj.navlist[0].serviceType = newType;
                    }
                    this.nodeInspector.serviceFallbackOutputJson = JSON.stringify(obj, null, 2);
                } catch (e) { /* keep as-is if parse fails */ }
            }
            this.updateServiceFallbackData();
        },

        // Router methods
        addRouterOutput() {
            if (!this.nodeInspector.routerOutputs) {
                this.nodeInspector.routerOutputs = [];
            }
            const newOutput = {
                id: `router-output-${this.generateGuid()}`,
                intentName: `intent_${this.nodeInspector.routerOutputs.length + 1}`,
                label: `Output ${this.nodeInspector.routerOutputs.length + 1}`
            };
            this.nodeInspector.routerOutputs.push(newOutput);
            this.updateRouterData();
        },
        removeRouterOutput(index) {
            if (this.nodeInspector.routerOutputs && this.nodeInspector.routerOutputs.length > 1) {
                this.nodeInspector.routerOutputs.splice(index, 1);
                this.updateRouterData();
            }
        },
        updateRouterData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'router') return;

            this.updateNodeProperty('routerOutputs', this.nodeInspector.routerOutputs);
            this.updateNodeProperty('routerName', this.nodeInspector.routerName);
            this.updateNodeProperty('routerPrompt', this.nodeInspector.routerPrompt);
            this.updateNodeProperty('confidenceThreshold', this.nodeInspector.confidenceThreshold);
        },

        // Access Level dropdown items ของ intent (Advanced/Dynamic Router ทุกชั้น): ladder ปัจจุบัน + แถว
        // "<raw> (ไม่รู้จัก — ระบบถือเป็นระดับสูงสุด)" เมื่อค่าที่ตั้งไว้ไม่อยู่ใน ladder (เช่น ระดับถูกลบ) — ไม่หายเงียบ
        accessLevelOptionsIncluding(raw) {
            return accessLevelOptionsIncluding(raw);
        },

        // Advanced Router Methods
        addAdvancedRouterOutput() {
            if (!this.nodeInspector.advancedRouterOutputs) {
                this.nodeInspector.advancedRouterOutputs = [];
            }
            const newOutput = {
                id: `advrouter-output-${this.generateGuid()}`,
                intentName: `intent_${this.nodeInspector.advancedRouterOutputs.length + 1}`,
                label: `Output ${this.nodeInspector.advancedRouterOutputs.length + 1}`,
                subIntents: []
            };
            this.nodeInspector.advancedRouterOutputs.push(newOutput);
            this.updateAdvancedRouterData();
        },
        removeAdvancedRouterOutput(index) {
            if (this.nodeInspector.advancedRouterOutputs && this.nodeInspector.advancedRouterOutputs.length > 1) {
                const outputToRemove = this.nodeInspector.advancedRouterOutputs[index];
                
                // Remove all edges connected to this output and its children recursively
                if (outputToRemove) {
                    this.removeEdgesForIntent(outputToRemove);
                }
                
                this.nodeInspector.advancedRouterOutputs.splice(index, 1);
                this.updateAdvancedRouterData();
            }
        },
        addAdvancedRouterSubIntent(outputIndex, subIndex = null, subSubIndex = null) {
            const outputs = this.nodeInspector.advancedRouterOutputs;
            if (!outputs || !outputs[outputIndex]) return;

            const newSubIntent = {
                id: `advrouter-output-${this.generateGuid()}`,
                intentName: '',
                label: '',
                subIntents: []
            };

            if (subSubIndex !== null && subIndex !== null) {
                // Adding Level 4 sub-intent
                if (!outputs[outputIndex].subIntents[subIndex].subIntents[subSubIndex].subIntents) {
                    outputs[outputIndex].subIntents[subIndex].subIntents[subSubIndex].subIntents = [];
                }
                outputs[outputIndex].subIntents[subIndex].subIntents[subSubIndex].subIntents.push(newSubIntent);
            } else if (subIndex !== null) {
                // Adding Level 3 sub-intent
                if (!outputs[outputIndex].subIntents[subIndex].subIntents) {
                    outputs[outputIndex].subIntents[subIndex].subIntents = [];
                }
                outputs[outputIndex].subIntents[subIndex].subIntents.push(newSubIntent);
            } else {
                // Adding Level 2 sub-intent
                if (!outputs[outputIndex].subIntents) {
                    outputs[outputIndex].subIntents = [];
                }
                outputs[outputIndex].subIntents.push(newSubIntent);
            }
            this.updateAdvancedRouterData();
        },
        removeAdvancedRouterSubIntent(outputIndex, subIndex, subSubIndex = null, l4Index = null) {
            const outputs = this.nodeInspector.advancedRouterOutputs;
            if (!outputs || !outputs[outputIndex]) return;

            let intentToRemove = null;

            if (l4Index !== null && subSubIndex !== null) {
                // Removing Level 4 sub-intent
                intentToRemove = outputs[outputIndex].subIntents[subIndex].subIntents[subSubIndex].subIntents[l4Index];
                outputs[outputIndex].subIntents[subIndex].subIntents[subSubIndex].subIntents.splice(l4Index, 1);
            } else if (subSubIndex !== null) {
                // Removing Level 3 sub-intent
                intentToRemove = outputs[outputIndex].subIntents[subIndex].subIntents[subSubIndex];
                outputs[outputIndex].subIntents[subIndex].subIntents.splice(subSubIndex, 1);
            } else {
                // Removing Level 2 sub-intent
                intentToRemove = outputs[outputIndex].subIntents[subIndex];
                outputs[outputIndex].subIntents.splice(subIndex, 1);
            }

            // Remove all edges connected to this intent and its children recursively
            if (intentToRemove) {
                this.removeEdgesForIntent(intentToRemove);
            }

            this.updateAdvancedRouterData();
        },
        
        // Helper method to recursively remove edges for an intent and all its children
        removeEdgesForIntent(intent) {
            if (!intent || !this.selectedNode) return;

            const handleId = `advrouter-${intent.id}`;
            
            // Remove edges from this intent's handle
            this.tempEdge = this.tempEdge.filter(edge => 
                !(edge.source === this.selectedNode.id && edge.sourceHandle === handleId)
            );

            // Recursively remove edges for all children
            if (intent.subIntents && intent.subIntents.length > 0) {
                intent.subIntents.forEach(subIntent => {
                    this.removeEdgesForIntent(subIntent);
                });
            }
        },
        updateAdvancedRouterData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'advancedRouter') return;

            this.updateNodeProperty('advancedRouterOutputs', this.nodeInspector.advancedRouterOutputs);
            this.updateNodeProperty('advancedRouterName', this.nodeInspector.advancedRouterName);
            this.updateNodeProperty('advancedRouterPrompt', this.nodeInspector.advancedRouterPrompt);
            this.updateNodeProperty('advancedRouterConfidenceThreshold', this.nodeInspector.advancedRouterConfidenceThreshold);
            this.updateNodeProperty('advancedRouterMaxDepth', this.nodeInspector.advancedRouterMaxDepth);

            const customSchemaEnabled = this.nodeInspector.advancedRouterCustomSchemaEnabled === true;
            this.updateNodeProperty('customSchemaEnabled', customSchemaEnabled);

            const schemaFieldMapping = {
                intentField: this.nodeInspector.advancedRouterIntentField || 'capability_intent',
                confidenceField: this.nodeInspector.advancedRouterConfidenceField || 'confidence',
                subIntentsField: this.nodeInspector.advancedRouterSubIntentsField || 'intents'
            };
            this.updateNodeProperty('schemaFieldMapping', schemaFieldMapping);

            const parsedSchema = this.parseAdvancedRouterSchemaTemplate(this.nodeInspector.advancedRouterSchemaTemplateText);
            if (parsedSchema) {
                this.nodeInspector.advancedRouterSchemaError = '';
                this.updateNodeProperty('customSchemaTemplate', parsedSchema);
            } else {
                this.nodeInspector.advancedRouterSchemaError = 'Invalid JSON schema format. Please provide a valid JSON object.';
            }
        },

        resetAdvancedRouterSchemaTemplate() {
            const defaultSchema = this.getDefaultAdvancedRouterSchemaTemplate();
            this.nodeInspector.advancedRouterSchemaTemplateText = JSON.stringify(defaultSchema, null, 2);
            this.nodeInspector.advancedRouterSchemaError = '';
            this.updateAdvancedRouterData();
        },

        parseAdvancedRouterSchemaTemplate(schemaText) {
            if (!schemaText || typeof schemaText !== 'string') {
                return null;
            }

            try {
                const parsed = JSON.parse(schemaText);
                return parsed && typeof parsed === 'object' ? parsed : null;
            } catch (error) {
                return null;
            }
        },

        getDefaultAdvancedRouterSchemaTemplate() {
            return {
                type: 'OBJECT',
                properties: {
                    intents: {
                        type: 'ARRAY',
                        items: {
                            type: 'OBJECT',
                            properties: {
                                capability_intent: { type: 'STRING' },
                                confidence: { type: 'NUMBER' },
                                intents: {
                                    type: 'ARRAY',
                                    items: {
                                        type: 'OBJECT',
                                        properties: {
                                            capability_intent: { type: 'STRING' },
                                            confidence: { type: 'NUMBER' },
                                            intents: {
                                                type: 'ARRAY',
                                                items: {}
                                            }
                                        },
                                        required: ['capability_intent', 'confidence', 'intents']
                                    }
                                }
                            },
                            required: ['capability_intent', 'confidence', 'intents']
                        }
                    }
                },
                required: ['intents']
            };
        },

        // Dynamic Router Methods
        adddynamicRouterOutput() {
            if (!this.nodeInspector.dynamicRouterOutputs) {
                this.nodeInspector.dynamicRouterOutputs = [];
            }
            const newOutput = {
                id: `dynamicRouter-output-${this.generateGuid()}`,
                intentName: `intent_${this.nodeInspector.dynamicRouterOutputs.length + 1}`,
                label: `Output ${this.nodeInspector.dynamicRouterOutputs.length + 1}`,
                subIntents: []
            };
            this.nodeInspector.dynamicRouterOutputs.push(newOutput);
            this.updatedynamicRouterData();
        },
        removedynamicRouterOutput(index) {
            if (this.nodeInspector.dynamicRouterOutputs && this.nodeInspector.dynamicRouterOutputs.length > 1) {
                const outputToRemove = this.nodeInspector.dynamicRouterOutputs[index];
                
                if (outputToRemove) {
                    this.removeEdgesFordynamicRouterIntent(outputToRemove);
                }
                
                this.nodeInspector.dynamicRouterOutputs.splice(index, 1);
                this.updatedynamicRouterData();
            }
        },
        adddynamicRouterSubIntent(outputIndex, subIndex = null, subSubIndex = null) {
            const outputs = this.nodeInspector.dynamicRouterOutputs;
            if (!outputs || !outputs[outputIndex]) return;

            const newSubIntent = {
                id: `dynamicRouter-output-${this.generateGuid()}`,
                intentName: '',
                label: '',
                subIntents: []
            };

            if (subSubIndex !== null && subIndex !== null) {
                if (!outputs[outputIndex].subIntents[subIndex].subIntents[subSubIndex].subIntents) {
                    outputs[outputIndex].subIntents[subIndex].subIntents[subSubIndex].subIntents = [];
                }
                outputs[outputIndex].subIntents[subIndex].subIntents[subSubIndex].subIntents.push(newSubIntent);
            } else if (subIndex !== null) {
                if (!outputs[outputIndex].subIntents[subIndex].subIntents) {
                    outputs[outputIndex].subIntents[subIndex].subIntents = [];
                }
                outputs[outputIndex].subIntents[subIndex].subIntents.push(newSubIntent);
            } else {
                if (!outputs[outputIndex].subIntents) {
                    outputs[outputIndex].subIntents = [];
                }
                outputs[outputIndex].subIntents.push(newSubIntent);
            }
            this.updatedynamicRouterData();
        },
        removedynamicRouterSubIntent(outputIndex, subIndex, subSubIndex = null, l4Index = null) {
            const outputs = this.nodeInspector.dynamicRouterOutputs;
            if (!outputs || !outputs[outputIndex]) return;

            let intentToRemove = null;

            if (l4Index !== null && subSubIndex !== null) {
                intentToRemove = outputs[outputIndex].subIntents[subIndex].subIntents[subSubIndex].subIntents[l4Index];
                outputs[outputIndex].subIntents[subIndex].subIntents[subSubIndex].subIntents.splice(l4Index, 1);
            } else if (subSubIndex !== null) {
                intentToRemove = outputs[outputIndex].subIntents[subIndex].subIntents[subSubIndex];
                outputs[outputIndex].subIntents[subIndex].subIntents.splice(subSubIndex, 1);
            } else {
                intentToRemove = outputs[outputIndex].subIntents[subIndex];
                outputs[outputIndex].subIntents.splice(subIndex, 1);
            }

            if (intentToRemove) {
                this.removeEdgesFordynamicRouterIntent(intentToRemove);
            }

            this.updatedynamicRouterData();
        },
        removeEdgesFordynamicRouterIntent(intent) {
            if (!intent || !this.selectedNode) return;

            const handleId = `dynamicRouter-${intent.id}`;
            
            this.tempEdge = this.tempEdge.filter(edge => 
                !(edge.source === this.selectedNode.id && edge.sourceHandle === handleId)
            );

            if (intent.subIntents && intent.subIntents.length > 0) {
                intent.subIntents.forEach(subIntent => {
                    this.removeEdgesFordynamicRouterIntent(subIntent);
                });
            }
        },
        updatedynamicRouterData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'dynamicRouter') return;

            this.updateNodeProperty('dynamicRouterOutputs', this.nodeInspector.dynamicRouterOutputs);
            this.updateNodeProperty('dynamicRouterName', this.nodeInspector.dynamicRouterName);
            this.updateNodeProperty('dynamicRouterPrompt', this.nodeInspector.dynamicRouterPrompt);
            this.updateNodeProperty('dynamicRouterConfidenceThreshold', this.nodeInspector.dynamicRouterConfidenceThreshold);
            this.updateNodeProperty('dynamicRouterMaxDepth', this.nodeInspector.dynamicRouterMaxDepth);
        },

        // Smart Intent Methods
        addInspectorSmartIntent() {
            if (!this.nodeInspector.smartIntents) {
                this.nodeInspector.smartIntents = [];
            }
            const defaultBasePrompt = `Find the Intent from the message of users.
#Condition for Response
1. If the user asks a question related to or similar in meaning to the #Group Intent below. You must response 1. Otherwise, 0`;

            const newSmartIntent = {
                id: `smart-intent-${this.generateGuid()}`,
                name: `Smart Intent ${this.nodeInspector.smartIntents.length + 1}`,
                priority: 0,
                prompt: '',
                basePrompt: defaultBasePrompt,
                enableBasePrompt: false,
                useMemory: false,
                messages: [
                    { id: `msg-${this.generateGuid()}`, text: '' }
                ],
                mainAction: {
                    id: `main-${this.generateGuid()}`,
                    label: 'Action',
                    useAI: false,
                    // Basic Information - match AI Model defaults
                    provider: null,
                    model: null,
                    gender: 'male',
                    typingSpeed: null,
                    maxInputLength: 200000,
                    // Response Options - match AI Model defaults
                    streamResponse: false,
                    jsonResponse: false,
                    enableTime: false,
                    timeZone: null,
                    enableTransactionId: false,
                    // Prompt Replacement Flags
                    enableInputMessageReplace: false,
                    enableVoicePromptReplace: false,
                    enableLanguageReplace: false,
                    // AI Parameters - match AI Model defaults
                    temperature: null,
                    topP: null,
                    frequencyPenalty: null,
                    presencePenalty: null,
                    maxTokens: null,
                    stopSequence: null,
                    nodeTimeout: null,
                    nodeMaxRetry: null,
                    streamingEnabled: true,
                    // Line Configuration - match AI Model defaults
                    lineFlexMessage: false,
                    // System Prompt - match AI Model defaults
                    systemPrompt: '',
                    // Forbidden Words - match AI Model defaults
                    forbiddenWords: [''],
                    // Memory Settings - match AI Model defaults
                    memoryTurn: null,
                    memoryDuration: null,
                    shortTermMemory: null,
                    longTermMemory: false,
                    // Multi-turn Input Configuration - match AI Model defaults
                    isChainMassage: false,
                    lenChainMassage: null,
                    timeSecondChainMassage: null,
                    // Knowledge Configuration
                    knowledgeType: '',
                    // Knowledge Sources Enable Flags
                    enableDocuments: false,
                    enableDatabaseIntegration: false,
                    enableProducts: false,
                    enableApiKnowledge: false,
                    // Documents properties
                    document: [],
                    topNChunks: 5,
                    cosineSimilarity: 0.5,
                    // Database Integration properties
                    tableName: null,
                    sqlExtractionModel: null,
                    promptSqlNoData: '',
                    // Products properties
                    productSourceType: 'product',
                    productGroupCollectionId: null,
                    productGroupCollectionName: '',
                    productGroupList: [],
                    productTopNChunks: 5,
                    productCosineSimilarity: 0.5,
                    // API Knowledge properties
                    knowledgeApiUrl: '',
                    knowledgeHttpMethod: 'GET',
                    listHeaders: [],
                    knowledgeRequestBody: '',
                    knowledgeResponseDataPath: ''
                }
            };
            this.nodeInspector.smartIntents.push(newSmartIntent);
            this.updateSmartIntentData();
        },

        removeInspectorSmartIntent(index) {
            if (this.nodeInspector.smartIntents && this.nodeInspector.smartIntents.length > 1) {
                const smartIntentToRemove = this.nodeInspector.smartIntents[index];

                // Collect source handles related to this smart intent
                const sourcesToRemove = [];
                sourcesToRemove.push(`${smartIntentToRemove.id}-main`);

                // Remove edges connected to these source handles
                this.tempEdge = this.tempEdge.filter(edge => 
                    !sourcesToRemove.includes(edge.sourceHandle)
                );

                // Remove the smart intent
                this.nodeInspector.smartIntents.splice(index, 1);
                this.updateSmartIntentData();
            }
        },

        addInspectorSmartIntentMessage(smartIntentIndex) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            const newMessage = {
                id: `msg-${this.generateGuid()}`,
                text: ''
            };
            smartIntent.messages.push(newMessage);
            this.updateSmartIntentData();
        },

        removeInspectorSmartIntentMessage(smartIntentIndex, messageIndex) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (smartIntent.messages && smartIntent.messages.length > 1) {
                smartIntent.messages.splice(messageIndex, 1);
                this.updateSmartIntentData();
            }
        },

        updateSmartIntentData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'smartIntent') return;
            
            this.updateNodeProperty('smartIntents', this.nodeInspector.smartIntents);
            this.updateNodeProperty('smartIntentGroupName', this.nodeInspector.smartIntentGroupName);
            
            // Trigger validation check for real-time feedback
            this.checkNodeValidation(this.selectedNode);
        },
        
        updateSmartIntentProviderChange(smartIntentIndex, newProvider) {
            // Clear model when provider changes
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.model = null;
            this.nodeInspector.smartIntents[smartIntentIndex].mainAction.apiVersion = null;
            this.updateSmartIntentData();
        },

        updateSmartIntentModelChange(smartIntentIndex, newModel) {
            // Auto-assign apiVersion when model changes
            this.$nextTick(() => {
                const selectedModel = this.listDDModel.find(m => m.modelName === newModel);
                if (selectedModel) {
                    this.nodeInspector.smartIntents[smartIntentIndex].mainAction.apiVersion = selectedModel.apiVersion;
                }
                this.updateSmartIntentData();
            });
        },

        updateSmartIntentFallbackData() {
            if (!this.selectedNode || this.selectedNode.data.type !== 'smartIntent') return;
            
            // Initialize fallback if it doesn't exist
            if (!this.nodeInspector.smartIntentFallback) {
                this.nodeInspector.smartIntentFallback = {
                    useAI: false,
                    provider: '',
                    model: '',
                    apiVersion: null,
                    // Basic Information
                    gender: 'male',
                    typingSpeed: null,
                    maxInputLength: 200000,
                    // Response Options
                    streamingEnabled: false,
                    enableTime: false,
                    timeZone: null,
                    enableTransactionId: false,
                    // AI Parameters
                    temperature: 0,
                    topP: 1.0,
                    frequencyPenalty: 0.0,
                    presencePenalty: 0.0,
                    maxTokens: 150,
                    stopSequence: '',
                    // Memory Settings
                    memoryTurn: null,
                    memoryDuration: null,
                    shortTermMemory: null,
                    longTermMemory: false,
                    // Multi-turn Input Configuration
                    isChainMassage: false,
                    lenChainMassage: null,
                    timeSecondChainMassage: null,
                    // Line Configuration
                    showLineName: false,
                    loadingBar: false,
                    loadingBarDuration: 0,
                    monitorLineUserId: null,
                    monitorSocialID: null,
                    lineFlexMessage: false,
                    // System Prompt
                    systemPrompt: '',
                    // Forbidden Words
                    forbiddenWords: [''],
                    nodeTimeout: 0
                };
            }
            
            this.updateNodeProperty('smartIntentFallback', this.nodeInspector.smartIntentFallback);
            
            // Trigger validation check for real-time feedback
            this.checkNodeValidation(this.selectedNode);
        },
        
        updateSmartIntentFallbackProviderChange(newProvider) {
            // Clear model when provider changes
            this.nodeInspector.smartIntentFallback.model = '';
            this.nodeInspector.smartIntentFallback.apiVersion = null;
            this.updateSmartIntentFallbackData();
        },

        updateSmartIntentFallbackModelChange(newModel) {
            // Auto-assign apiVersion when model changes
            this.$nextTick(() => {
                const selectedModel = this.listDDModel.find(m => m.modelName === newModel);
                if (selectedModel) {
                    this.nodeInspector.smartIntentFallback.apiVersion = selectedModel.apiVersion;
                }
                this.updateSmartIntentFallbackData();
            });
        },

        // Tax Planning: dedicated provider/model change handlers for the optional
        // "question phrasing" model override — deliberately does NOT call changeProvider(),
        // which mutates the shared DDModelShow/nodeInspector.model state used by the main
        // Provider*/Model* fields on this same node.
        updateTaxPlanningQuestionProviderChange(newProvider) {
            this.nodeInspector.taxPlanningQuestionModel = null;
            this.updateNodeProperty('taxPlanningQuestionProvider', newProvider);
            this.updateNodeProperty('taxPlanningQuestionModel', null);
        },

        updateTaxPlanningQuestionModelChange(newModel) {
            this.updateNodeProperty('taxPlanningQuestionModel', newModel);
        },

        // Smart Intent Knowledge API Header Management Methods
        addSmartIntentKnowledgeAPIHeader(smartIntentIndex) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (!smartIntent.mainAction.listHeaders) {
                smartIntent.mainAction.listHeaders = [];
            }
            
            const newHeader = {
                id: `header-${this.generateGuid()}`,
                key: '',
                value: ''
            };
            
            smartIntent.mainAction.listHeaders.push(newHeader);
            this.updateSmartIntentData();
        },

        removeSmartIntentKnowledgeAPIHeader(smartIntentIndex, headerIndex) {
            const smartIntent = this.nodeInspector.smartIntents[smartIntentIndex];
            if (smartIntent.mainAction.listHeaders && smartIntent.mainAction.listHeaders.length > 0) {
                smartIntent.mainAction.listHeaders.splice(headerIndex, 1);
                this.updateSmartIntentData();
            }
        },

        // Smart Intent Fallback Knowledge API Header Management Methods
        addSmartIntentFallbackKnowledgeAPIHeader() {
            if (!this.nodeInspector.smartIntentFallback.listHeaders) {
                this.nodeInspector.smartIntentFallback.listHeaders = [];
            }
            
            const newHeader = {
                id: `header-${this.generateGuid()}`,
                key: '',
                value: ''
            };
            
            this.nodeInspector.smartIntentFallback.listHeaders.push(newHeader);
            this.updateSmartIntentFallbackData();
        },

        removeSmartIntentFallbackKnowledgeAPIHeader(headerIndex) {
            if (this.nodeInspector.smartIntentFallback.listHeaders && this.nodeInspector.smartIntentFallback.listHeaders.length > 0) {
                this.nodeInspector.smartIntentFallback.listHeaders.splice(headerIndex, 1);
                this.updateSmartIntentFallbackData();
            }
        },

        getModelsByProvider(provider) {
            if (!provider) return [];
            
            // Filter models by the specific provider
            // Check both providerName and provider fields for compatibility
            return this.listDDModel.filter(
                model => model.providerName === provider || model.provider === provider
            );
        },
        // Document data methods
        getDocumentData() {
            let self = this;
            // App Knowledge Center (D4): โหมด Center ไม่ใช้ Specific Data Selection (ข้อความ chunk ฝังใน flow JSON = ข้าม
            // Policy ของ Center) ⇒ ไม่ดึงตาราง chunk ในเครื่อง
            if (self.kccCenterMode) {
                self.resetDocumentDataTable();
                return;
            }
            // Only fetch data if specific data selection is enabled
            if (!self.nodeInspector.enableSpecificDataSelection) {
                self.resetDocumentDataTable();
                return;
            }

            if (self.nodeInspector.documentVectorName == "" || self.nodeInspector.documentVectorName == null) {
                return;
            }

            this.loadingDocumentDataTable = true;
            self.resetDocumentDataTable();

            let temp = {
                page: 1,
                perPage: 1000,
                name: self.nodeInspector.documentVectorName,
                sortDesc: false,
                sortBy: null,
            };

            self.$call.GetTableVector(
                temp,
                (dataResponse) => {
                    if (dataResponse.status == 0) {
                        self.documentDataTable = dataResponse.data.table.data || [];
                        self.documentDataTotalTable = dataResponse.data.table.total || 0;
                        self.showDocumentDataTable = true;

                        // Initialize selection state for each item
                        self.documentDataTable.forEach((element) => {
                            element.selected = false;
                            // Check if item is already selected
                            if (self.nodeInspector.selectedDocumentData) {
                                const alreadySelected = self.nodeInspector.selectedDocumentData.find(
                                    (selected) => selected.id == element.id
                                );
                                if (alreadySelected) {
                                    element.selected = true;
                                }
                            }
                        });
                    } else {
                        self.documentDataTable = [];
                        self.documentDataTotalTable = 0;
                        self.showDocumentDataTable = false;
                    }
                    self.loadingDocumentDataTable = false;
                },
                (errorResource) => {
                    console.error('Error fetching document data:', errorResource);
                    self.documentDataTable = [];
                    self.documentDataTotalTable = 0;
                    self.showDocumentDataTable = false;
                    self.loadingDocumentDataTable = false;
                }
            );
        },
        selectDocumentData(flag, id, textORG) {
            let self = this;

            if (!self.nodeInspector.selectedDocumentData) {
                self.nodeInspector.selectedDocumentData = [];
            }

            if (flag) {
                // Add to selection
                self.nodeInspector.selectedDocumentData.push({
                    id: id,
                    textORG: textORG
                });

                // Update node property
                self.updateNodeProperty('selectedDocumentData', self.nodeInspector.selectedDocumentData);
            } else {
                // Remove from selection
                if (self.nodeInspector.selectedDocumentData.findIndex(e => e.id == id) > -1) {
                    self.nodeInspector.selectedDocumentData.splice(
                        self.nodeInspector.selectedDocumentData.findIndex(e => e.id == id),
                        1
                    );
                    self.updateNodeProperty('selectedDocumentData', self.nodeInspector.selectedDocumentData);
                }
            }

            // Update UI selection state
            self.documentDataTable.forEach((element) => {
                if (element.id == id) {
                    element.selected = flag;
                }
            });
        },
        deleteSelectedDocumentData(id) {
            let self = this;
            if (self.nodeInspector.selectedDocumentData && self.nodeInspector.selectedDocumentData.length > 0) {
                // Remove from selected data
                self.nodeInspector.selectedDocumentData = self.nodeInspector.selectedDocumentData.filter(item => item.id !== id);
                self.updateNodeProperty('selectedDocumentData', self.nodeInspector.selectedDocumentData);

                // Update table selection state
                self.documentDataTable.forEach((element) => {
                    if (element.id == id) {
                        element.selected = false;
                    }
                });
            }
        },
        onSpecificDataSelectionChange(enabled) {
            let self = this;

            // Update node property
            self.updateNodeProperty('enableSpecificDataSelection', enabled);

            if (enabled) {
                // When enabled, auto-expand and clear document vector selection
                self.documentDataExpanded = true;

                // Clear document vector selection
                self.nodeInspector.documentVectorName = '';
                self.updateNodeProperty('documentVectorName', '');

                // Reset document data table
                self.resetDocumentDataTable();
            } else {
                // When disabled, reset document data and clear selection
                self.resetDocumentDataTable();
                if (self.nodeInspector.selectedDocumentData) {
                    self.nodeInspector.selectedDocumentData = [];
                    self.updateNodeProperty('selectedDocumentData', []);
                }
                self.documentDataExpanded = false;
            }
        },
        isDocumentMissing(docName) {
            return !this.DDVector.some(v => v.name === docName);
        },
        resetDocumentDataAndSelection() {
            // Reset table data
            this.resetDocumentDataTable();

            // Reset document vector selection and clear selected data if necessary
            if (this.nodeInspector.documentVectorName) {
                this.nodeInspector.documentVectorName = '';
                this.updateNodeProperty('documentVectorName', '');
            }

            // Clear selected document data if necessary
            if (this.nodeInspector.selectedDocumentData && this.nodeInspector.selectedDocumentData.length > 0) {
                this.nodeInspector.selectedDocumentData = [];
                this.updateNodeProperty('selectedDocumentData', []);
            }
        },
        resetDocumentDataTable() {
            this.documentDataTable = [];
            this.documentDataTotalTable = 0;
            this.showDocumentDataTable = false;
        },
        formatText(text) {
            if (!text) return '';
            return text.replace(/ /g, "&nbsp;");
        },
        generateConditionPreview(output) {
            if (!output.conditionType) return '';

            const source = output.sourceField || 'input.text';

            switch (output.conditionType) {
                case 'text_contains':
                    const containsMethod = output.caseSensitive ? 'includes' : 'toLowerCase().includes';
                    const searchValue = output.caseSensitive ? `'${output.searchValue}'` : `'${(output.searchValue || '').toLowerCase()}'`;
                    return `${source}.${containsMethod}(${searchValue})`;

                case 'text_equals':
                    const equalsMethod = output.caseSensitive ? '===' : 'toLowerCase() ===';
                    const compareValue = output.caseSensitive ? `'${output.compareValue}'` : `'${(output.compareValue || '').toLowerCase()}'`;
                    return `${source}.${equalsMethod} ${compareValue}`;

                case 'number_comparison':
                    const operators = { gt: '>', lt: '<', eq: '===', gte: '>=', lte: '<=', ne: '!==' };
                    const op = operators[output.operator] || '>';
                    return `${source} ${op} ${output.compareValue || 0}`;

                case 'boolean_check':
                    return `${source} === ${output.expectedValue}`;

                case 'regex_match':
                    const flags = output.regexFlags ? `'${output.regexFlags}'` : '';
                    return `new RegExp('${output.regexPattern}'${flags ? ', ' + flags : ''}).test(${source})`;

                case 'length_check':
                    const lengthOp = { gt: '>', lt: '<', eq: '===', between: 'between' }[output.lengthOperator] || '>';
                    if (output.lengthOperator === 'between') {
                        return `${source}.length >= ${output.lengthValue || 0} && ${source}.length <= ${output.lengthValueMax || 100}`;
                    }
                    return `${source}.length ${lengthOp} ${output.lengthValue || 0}`;

                default:
                    return '';
            }
        },
        insertConditionHelper(outputIndex, helperText) {
            if (!this.nodeInspector.switchOutputs[outputIndex]) return;

            const currentCondition = this.nodeInspector.switchOutputs[outputIndex].condition || '';
            const newCondition = currentCondition + (currentCondition ? ' && ' : '') + helperText;

            this.nodeInspector.switchOutputs[outputIndex].condition = newCondition;
            this.updateSwitchData();
        },


        // Save/Load UI Methods
        openSaveDialog() {
            this.saveDialog = true;
        },

        closeSaveDialog() {
            this.saveDialog = false;
            this.saveFlowData.versionComment = ''; // Reset version comment
        },

        // Load from Model methods
        async openLoadFromModelDialog() {
            this.loadFromModelDialog = true;
        },

        async openSmartIntentLoadFromModelDialog(smartIntentIndex = null) {
            this.currentLoadingSmartIntentIndex = smartIntentIndex; // Store which intent/fallback is being loaded
            this.smartIntentLoadFromModelDialog = true;
        },

        closeLoadFromModelDialog() {
            this.loadFromModelDialog = false;
            this.selectedModelToLoad = null;
            this.modelConfigPreview = null;
        },

        closeSmartIntentLoadFromModelDialog() {
            this.smartIntentLoadFromModelDialog = false;
            this.selectedSmartIntentModelToLoad = null;
            this.smartIntentModelConfigPreview = null;
            this.currentLoadingSmartIntentIndex = null;
        },

        async confirmLoadFromModel() {
            if (!this.selectedModelToLoad) return;

            this.loadingModelConfig = true;
            try {
                // Call API to get model configuration
                this.$call.GetGPTModelById(
                    this.selectedModelToLoad,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            const modelConfig = dataResponse.data;
                            // Check if this is intentAI node
                            const isIntentAI = this.selectedNode && this.selectedNode.data.type === 'intentAI';
                            
                            // Update provider first and trigger model dropdown update
                            if (modelConfig.provider) {
                                if (isIntentAI) {
                                    this.nodeInspector.intentaiProvider = modelConfig.provider;
                                } else {
                                    this.nodeInspector.provider = modelConfig.provider;
                                }
                                // Wait for provider change to complete before setting model
                                this.changeProvider(modelConfig.provider).then(() => {
                                    // Now set the model after dropdown is populated
                                    this.$nextTick(() => {
                                        // Try to find exact match first
                                        let modelValue = modelConfig.model;

                                        // Check if model exists in dropdown
                                        const modelExists = this.DDModelShow.find(m => m.modelName === modelValue);
                                        if (!modelExists && modelValue && modelValue.includes('|')) {
                                            // If not found and contains |, try the part after |
                                            const modelParts = modelValue.split('|');
                                            modelValue = modelParts[modelParts.length - 1];
                                        }

                                        // Set the model value
                                        if (isIntentAI) {
                                            this.nodeInspector.intentaiModel = modelValue;
                                        } else {
                                            this.nodeInspector.model = modelValue;
                                        }

                                        // Continue with other properties
                                        this.loadOtherProperties(modelConfig);
                                    });
                                });
                            } else {
                                // No provider, just load other properties
                                this.loadOtherProperties(modelConfig);
                            }
                        } else {
                            this.showError('Failed to load model configuration');
                            this.loadingModelConfig = false;
                        }
                    },
                    (error) => {
                        console.error('API Error:', error);
                        this.showError('Error loading model configuration');
                        this.loadingModelConfig = false;
                    }
                );
            } catch (error) {
                console.error('Exception:', error);
                this.showError('Error loading model configuration');
                this.loadingModelConfig = false;
            }
        },

        loadOtherProperties(modelConfig) {
            console.log('Loaded model configuration:', modelConfig);
            // Check if this is intentAI node
            const isIntentAI = this.selectedNode && this.selectedNode.data.type === 'intentAI';
            // Load configuration into nodeInspector with proper reactive updates
            if (isIntentAI) {
                this.nodeInspector.intentaiGender = (modelConfig.gender === "m" ? 'male' : 'female') || 'male';
                this.nodeInspector.intentaiTemperature = modelConfig.temperature ?? null;
                this.nodeInspector.intentaiMaxTokens = modelConfig.max_tokens ?? null;
                this.nodeInspector.intentaiTopP = modelConfig.top_p ?? null;
                this.nodeInspector.intentaiFrequencyPenalty = modelConfig.frequency_penalty ?? null;
                this.nodeInspector.intentaiPresencePenalty = modelConfig.presence_penalty ?? null;
                this.nodeInspector.intentaiSystemPrompt = modelConfig.promptTemplate || '';
                this.nodeInspector.intentaiTypingSpeed = modelConfig.typingSpeed ?? null;
                this.nodeInspector.intentaiMaxInputLength = modelConfig.maximumChatInput ?? 200000;
                this.nodeInspector.intentaiStreamResponse = modelConfig.isStream !== undefined ? modelConfig.isStream : false;
                this.nodeInspector.intentaiJsonResponse = modelConfig.jsonResponse !== undefined ? modelConfig.jsonResponse : false;
                this.nodeInspector.intentaiEnableTime = modelConfig.timeZone ? true : false;
                this.nodeInspector.intentaiTimeZone = modelConfig.timeZone || null;
                this.nodeInspector.intentaiMemoryTurn = modelConfig.historyCount ?? null;
                this.nodeInspector.intentaiMemoryDuration = modelConfig.historyMinute < 0
                    ? Math.abs(modelConfig.historyMinute)
                    : modelConfig.historyMinute;
                this.nodeInspector.intentaiShortTermMemory = modelConfig.len_ShortMemory ?? null;
                this.nodeInspector.intentaiLongTermMemory = modelConfig.isLongTerm_Memory !== undefined ? modelConfig.isLongTerm_Memory : false;
                this.nodeInspector.intentaiIsChainMassage = modelConfig.isChainMassage !== undefined ? modelConfig.isChainMassage : false;
                this.nodeInspector.intentaiLenChainMassage = modelConfig.lenChainMassage ?? null;
                this.nodeInspector.intentaiTimeSecondChainMassage = modelConfig.timeSecondChainMassage ?? null;
                this.nodeInspector.intentaiShowLineName = modelConfig.isShowNameUserMonitor !== undefined ? modelConfig.isShowNameUserMonitor : false;
                this.nodeInspector.intentaiLoadingBar = modelConfig.loadingBarFlag !== undefined ? modelConfig.loadingBarFlag : false;
                this.nodeInspector.intentaiLoadingBarDuration = modelConfig.loadingBarDuration ?? 0;
                this.nodeInspector.intentaiMonitorLineUserId = modelConfig.monitorLineUserID || null;
                this.nodeInspector.intentaiMonitorSocialID = modelConfig.monitorSocialID || null;
                this.nodeInspector.intentaiStopSequence = modelConfig.stop || null;
                // Handle forbidden words for intentAI
                if (modelConfig.forbiddenWord) {
                    try {
                        this.nodeInspector.intentaiForbiddenWords = JSON.parse(modelConfig.forbiddenWord);
                        this.nodeInspector.intentaiForbiddenWords.push('');
                    } catch (e) {
                        this.nodeInspector.intentaiForbiddenWords = [''];
                    }
                } else {
                    this.nodeInspector.intentaiForbiddenWords = [''];
                }
            } else {
                this.nodeInspector.gender = (modelConfig.gender === "m" ? 'male' : 'female') || 'male';
                this.nodeInspector.temperature = modelConfig.temperature ?? null;
                this.nodeInspector.maxTokens = modelConfig.max_tokens ?? null;
                this.nodeInspector.topP = modelConfig.top_p ?? null;
                this.nodeInspector.frequencyPenalty = modelConfig.frequency_penalty ?? null;
                this.nodeInspector.presencePenalty = modelConfig.presence_penalty ?? null;
                this.nodeInspector.systemPrompt = modelConfig.promptTemplate || '';
                this.nodeInspector.typingSpeed = modelConfig.typingSpeed ?? null;
                this.nodeInspector.maxInputLength = modelConfig.maximumChatInput ?? 200000;
                this.nodeInspector.streamResponse = modelConfig.isStream !== undefined ? modelConfig.isStream : false;
                this.nodeInspector.jsonResponse = false;
                this.nodeInspector.enableTime = modelConfig.timeZone ? true : false;
                this.nodeInspector.timeZone = modelConfig.timeZone || null;
                this.nodeInspector.memoryTurn = modelConfig.historyCount ?? null;
                this.nodeInspector.memoryDuration = modelConfig.historyMinute < 0
                    ? Math.abs(modelConfig.historyMinute)
                    : modelConfig.historyMinute;
                this.nodeInspector.shortTermMemory = modelConfig.len_ShortMemory ?? null;
                this.nodeInspector.longTermMemory = modelConfig.isLongTerm_Memory !== undefined ? modelConfig.isLongTerm_Memory : false;
                this.nodeInspector.isChainMassage = modelConfig.isChainMassage !== undefined ? modelConfig.isChainMassage : false;
                this.nodeInspector.lenChainMassage = modelConfig.lenChainMassage ?? null;
                this.nodeInspector.timeSecondChainMassage = modelConfig.timeSecondChainMassage ?? null;
                this.nodeInspector.showLineName = modelConfig.isShowNameUserMonitor !== undefined ? modelConfig.isShowNameUserMonitor : false;
                this.nodeInspector.loadingBar = modelConfig.loadingBarFlag !== undefined ? modelConfig.loadingBarFlag : false;
                this.nodeInspector.loadingBarDuration = modelConfig.loadingBarDuration ?? 0;
                this.nodeInspector.monitorLineUserId = modelConfig.monitorLineUserID || null;
                this.nodeInspector.monitorSocialID = modelConfig.monitorSocialID || null;
                this.nodeInspector.stopSequence = modelConfig.stop || null;
                // Handle forbidden words for AI Model
                if (modelConfig.forbiddenWord) {
                    try {
                        this.nodeInspector.forbiddenWords = JSON.parse(modelConfig.forbiddenWord);
                        this.nodeInspector.forbiddenWords.push('');
                    } catch (e) {
                        this.nodeInspector.forbiddenWords = [''];
                    }
                } else {
                    this.nodeInspector.forbiddenWords = [''];
                }
            }
            
            // Common properties for both node types
            this.nodeInspector.loadingBar = modelConfig.loadingBarFlag !== undefined ? modelConfig.loadingBarFlag : false;
            this.nodeInspector.loadingBarDuration = modelConfig.loadingBarDuration ?? 0;
            this.nodeInspector.showLineName = modelConfig.isShowNameUserMonitor !== undefined ? modelConfig.isShowNameUserMonitor : false;
            this.nodeInspector.monitorLineUserId = modelConfig.monitorLineUserID || null;
            this.nodeInspector.monitorSocialID = modelConfig.monitorSocialID || null;
            

            // Handle forbidden words
            if (modelConfig.forbiddenWord) {
                try {
                    this.nodeInspector.forbiddenWords = JSON.parse(modelConfig.forbiddenWord);
                    this.nodeInspector.forbiddenWords.push('');
                } catch (e) {
                    this.nodeInspector.forbiddenWords = [''];
                }
            } else {
                this.nodeInspector.forbiddenWords = [''];
            }

            // Update node data immediately and force UI refresh
            this.updateAllNodeProperties();
            this.$forceUpdate();

            this.closeLoadFromModelDialog();
            this.showSuccess(`Successfully loaded configuration from ${modelConfig.modelName || 'selected model'}`);
            this.loadingModelConfig = false;
        },

        updateAllNodeProperties() {
            if (!this.selectedNode) return;

            // Update all properties to sync with the node data
            Object.keys(this.nodeInspector).forEach(key => {
                this.updateNodeProperty(key, this.nodeInspector[key]);
            });

            // Force Vue to update the UI
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },

        getModelTypeColor(provider) {
            const colors = {
                'OpenAI': '#10a37f',
                'Anthropic': '#d97757',
                'Google': '#4285f4',
                'Azure': '#0078d4',
                'AWS': '#ff9900'
            };
            return colors[provider] || '#666666';
        },

        // Smart Intent Config Loading Methods
        async loadSmartIntentModelConfigPreview(modelId = null) {
            const targetModelId = modelId || this.selectedSmartIntentModelToLoad;
            if (!targetModelId) {
                this.smartIntentModelConfigPreview = null;
                return;
            }

            this.loadingSmartIntentModelConfig = true;
            try {
                // Use the same method as AI Model node
                this.$call.GetGPTModelById(
                    targetModelId,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            const modelConfig = dataResponse.data;
                            // Map to preview format like AI Model node
                            this.smartIntentModelConfigPreview = {
                                provider: modelConfig.provider || 'N/A',
                                model: modelConfig.model || 'N/A',
                                temperature: modelConfig.temperature ?? 'N/A',
                                maxTokens: modelConfig.max_tokens ?? 'N/A',
                                topP: modelConfig.top_p ?? 'N/A',
                                frequencyPenalty: modelConfig.frequency_penalty ?? 'N/A',
                                presencePenalty: modelConfig.presence_penalty ?? 'N/A',
                                systemPrompt: modelConfig.promptTemplate ? (modelConfig.promptTemplate.length > 100 ? modelConfig.promptTemplate.substring(0, 100) + '...' : modelConfig.promptTemplate) : 'N/A',
                                // Keep original data for applying config
                                originalData: modelConfig
                            };
                        } else {
                            this.showError('Failed to load model configuration: ' + dataResponse.message);
                            this.smartIntentModelConfigPreview = null;
                        }
                        this.loadingSmartIntentModelConfig = false;
                    },
                    (error) => {
                        console.error('Error loading Smart Intent model config:', error);
                        this.showError('Failed to load model configuration. Please try again.');
                        this.smartIntentModelConfigPreview = null;
                        this.loadingSmartIntentModelConfig = false;
                    }
                );
            } catch (error) {
                console.error('Error loading Smart Intent model config:', error);
                this.showError('Failed to load model configuration. Please try again.');
                this.smartIntentModelConfigPreview = null;
                this.loadingSmartIntentModelConfig = false;
            }
        },

        confirmSmartIntentLoadFromModel() {
            if (!this.smartIntentModelConfigPreview || !this.smartIntentModelConfigPreview.originalData) {
                this.showError('No model configuration to load');
                return;
            }

            const modelConfig = this.smartIntentModelConfigPreview.originalData;

            // Apply configuration based on current selection
            if (this.currentLoadingSmartIntentIndex === 'fallback') {
                // Apply to Fallback directly (fallback doesn't have mainAction)
                this.applyConfigToTarget(modelConfig, this.nodeInspector.smartIntentFallback);
                this.showSuccess(`Successfully loaded configuration from ${modelConfig.modelName || 'selected model'} to Fallback`);
            } else if (this.currentLoadingSmartIntentIndex !== null) {
                // Apply to specific Smart Intent's mainAction
                const smartIntent = this.nodeInspector.smartIntents[this.currentLoadingSmartIntentIndex];
                if (smartIntent && smartIntent.mainAction) {
                    this.applyConfigToTarget(modelConfig, smartIntent.mainAction);
                    this.showSuccess(`Successfully loaded configuration from ${modelConfig.modelName || 'selected model'} to ${smartIntent.name || 'Smart Intent'}`);
                }
            } else {
                this.showError('No target selected for configuration loading');
                return;
            }

            // Force update nodeInspector to sync with data changes
            this.updateSmartIntentData();
            this.updateSmartIntentFallbackData();
            
            // Ensure selectedNode.data is synced with nodeInspector
            if (this.selectedNode && this.selectedNode.data) {
                this.selectedNode.data.smartIntents = JSON.parse(JSON.stringify(this.nodeInspector.smartIntents));
                this.selectedNode.data.smartIntentFallback = JSON.parse(JSON.stringify(this.nodeInspector.smartIntentFallback));
            }
            
            // Force re-sync nodeInspector from selectedNode.data
            this.syncNodeInspectorFromSelectedNode();

            // Update node data and force UI refresh
            this.updateSmartIntentNodeProperties();
            this.$forceUpdate();

            this.closeSmartIntentLoadFromModelDialog();
            this.smartIntentLoadFromModelDialog = false;
            this.loadingSmartIntentModelConfig = false;
        },

        applyConfigToTarget(modelConfig, target) {
            // Basic AI Parameters
            target.provider = modelConfig.provider || null;
            target.model = modelConfig.model || null;
            
            // Auto-assign apiVersion from listDDModel based on the loaded model
            if (modelConfig.model) {
                const selectedModel = this.listDDModel.find(m => m.modelName === modelConfig.model);
                if (selectedModel) {
                    target.apiVersion = selectedModel.apiVersion;
                }
            }
            
            target.systemPrompt = modelConfig.promptTemplate || '';
            target.temperature = modelConfig.temperature ?? null;
            target.maxTokens = modelConfig.max_tokens ?? null;
            target.topP = modelConfig.top_p ?? null;
            target.presencePenalty = modelConfig.presence_penalty ?? null;
            target.frequencyPenalty = modelConfig.frequency_penalty ?? null;
            target.streamingEnabled = modelConfig.isStream !== undefined ? modelConfig.isStream : true;
            target.lineFlexMessage = modelConfig.lineFlexMessage !== undefined ? modelConfig.lineFlexMessage : false;
            
            // Basic Information fields that are specific to Smart Intent mainAction
            if (target.hasOwnProperty('typingSpeed') || target.hasOwnProperty('maxInputLength')) {
                target.typingSpeed = modelConfig.typingSpeed ?? null;
                target.maxInputLength = modelConfig.maximumChatInput ?? 200000;
            }
            
            // Response Options fields that are specific to Smart Intent mainAction
            if (target.hasOwnProperty('enableTime') || target.hasOwnProperty('timeZone')) {
                target.enableTime = modelConfig.timeZone ? true : false;
                target.timeZone = modelConfig.timeZone || null;
            }
            
            // Memory Settings fields that are specific to Smart Intent mainAction
            if (target.hasOwnProperty('memoryTurn') || target.hasOwnProperty('memoryDuration') || target.hasOwnProperty('shortTermMemory') || target.hasOwnProperty('longTermMemory')) {
                target.memoryTurn = modelConfig.historyCount ?? null;
                target.memoryDuration = modelConfig.historyMinute ? Math.abs(modelConfig.historyMinute) : null;
                target.shortTermMemory = modelConfig.len_ShortMemory ?? null;
                target.longTermMemory = modelConfig.isLongTerm_Memory !== undefined ? modelConfig.isLongTerm_Memory : false;
            }
            
            // Multi-turn Input Configuration fields that are specific to Smart Intent mainAction
            if (target.hasOwnProperty('isChainMassage') || target.hasOwnProperty('lenChainMassage') || target.hasOwnProperty('timeSecondChainMassage')) {
                target.isChainMassage = modelConfig.isChainMassage !== undefined ? modelConfig.isChainMassage : false;
                target.lenChainMassage = modelConfig.lenChainMassage ?? null;
                target.timeSecondChainMassage = modelConfig.timeSecondChainMassage ?? null;
            }
            
            target.streamResponse = modelConfig.isStream !== undefined ? modelConfig.isStream : false;
            target.jsonResponse = modelConfig.jsonResponse !== undefined ? modelConfig.jsonResponse : false;
            target.stopSequence = modelConfig.stop || null;
            
            // Advanced configuration - map from AI Model node format
            target.loadingBar = modelConfig.loadingBarFlag !== undefined ? modelConfig.loadingBarFlag : false;
            target.loadingBarDuration = modelConfig.loadingBarDuration || null;
            target.showLineName = modelConfig.isShowNameUserMonitor !== undefined ? modelConfig.isShowNameUserMonitor : false;
            target.monitorLineUserId = modelConfig.monitorLineUserID || null;
            target.monitorSocialID = modelConfig.monitorSocialID || null;
            
            // Set forbidden words if available
            if (modelConfig.forbiddenWord) {
                try {
                    target.forbiddenWords = JSON.parse(modelConfig.forbiddenWord) || [''];
                    target.forbiddenWords.push('');
                } catch (e) {
                    target.forbiddenWords = [''];
                }
            } else {
                target.forbiddenWords = [''];
            }
            
            // Ensure forbidden words is always an array
            if (!Array.isArray(target.forbiddenWords)) {
                target.forbiddenWords = [''];
            }
        },

        updateSmartIntentNodeProperties() {
            if (!this.selectedNode || this.selectedNode.type !== 'smartIntent') return;

            // Update Smart Intent properties to sync with the node data
            if (this.selectedNode.data.smartIntents) {
                this.selectedNode.data.smartIntents = [...this.nodeInspector.smartIntents];
            }
            if (this.selectedNode.data.smartIntentFallback) {
                this.selectedNode.data.smartIntentFallback = { ...this.nodeInspector.smartIntentFallback };
            }

            // Force Vue to update the UI
            this.$nextTick(() => {
                this.$forceUpdate();
            });
        },

        getLoadConfigTitle() {
            if (this.currentLoadingSmartIntentIndex === 'fallback') {
                return 'Fallback';
            } else if (this.currentLoadingSmartIntentIndex !== null) {
                const smartIntent = this.nodeInspector.smartIntents[this.currentLoadingSmartIntentIndex];
                return smartIntent ? (smartIntent.name || `Smart Intent ${this.currentLoadingSmartIntentIndex + 1}`) : 'Smart Intent';
            }
            return 'Smart Intent';
        },

        syncNodeInspectorFromSelectedNode() {
            if (!this.selectedNode) return;
            
            // Sync Smart Intent data back to nodeInspector
            if (this.selectedNode.data.type === 'smartIntent') {
                if (this.selectedNode.data.smartIntents) {
                    this.nodeInspector.smartIntents = JSON.parse(JSON.stringify(this.selectedNode.data.smartIntents));
                    
                    // Ensure each smart intent has all required mainAction properties
                    this.nodeInspector.smartIntents.forEach(smartIntent => {
                        if (!smartIntent.mainAction) {
                            smartIntent.mainAction = {};
                        }
                        // Set defaults for missing properties
                        const defaults = {
                            useAI: false,
                            provider: null,
                            model: null,
                            // Basic Information
                            typingSpeed: null,
                            maxInputLength: 200000,
                            // Response Options
                            enableTime: false,
                            timeZone: null,
                            enableTransactionId: false,
                            // AI Parameters
                            temperature: null,
                            topP: null,
                            frequencyPenalty: null,
                            presencePenalty: null,
                            maxTokens: null,
                            stopSequence: null,
                            nodeTimeout: null,
                            nodeMaxRetry: null,
                            streamingEnabled: true,
                            // Line Configuration
                            lineFlexMessage: false,
                            // System Prompt
                            systemPrompt: '',
                            // Forbidden Words
                            forbiddenWords: [''],
                            // Memory Settings
                            memoryTurn: null,
                            memoryDuration: null,
                            shortTermMemory: null,
                            longTermMemory: false,
                            // Multi-turn Input Configuration
                            isChainMassage: false,
                            lenChainMassage: null,
                            timeSecondChainMassage: null,
                            // Knowledge Configuration
                            knowledgeType: '',
                            // Knowledge Sources Enable Flags
                            enableDocuments: false,
                            enableDatabaseIntegration: false,
                            enableProducts: false,
                            enableApiKnowledge: false,
                            // Documents properties
                            document: [],
                            topNChunks: 5,
                            cosineSimilarity: 0.5,
                            // Database Integration properties
                            tableName: null,
                            sqlExtractionModel: null,
                            promptSqlNoData: '',
                            // Products properties
                            productSourceType: 'product',
                            productGroupCollectionId: null,
                            productGroupCollectionName: '',
                            productGroupList: [],
                            productTopNChunks: 5,
                            productCosineSimilarity: 0.5,
                            // API Knowledge properties
                            knowledgeApiUrl: '',
                            knowledgeHttpMethod: 'GET',
                            listHeaders: [],
                            knowledgeRequestBody: '',
                            knowledgeResponseDataPath: ''
                        };
                        Object.keys(defaults).forEach(key => {
                            if (smartIntent.mainAction[key] === undefined) {
                                smartIntent.mainAction[key] = defaults[key];
                            }
                        });
                        // Ensure forbiddenWords is always an array
                        if (!Array.isArray(smartIntent.mainAction.forbiddenWords)) {
                            smartIntent.mainAction.forbiddenWords = [''];
                        }
                    });
                }
                
                if (this.selectedNode.data.smartIntentFallback) {
                    this.nodeInspector.smartIntentFallback = JSON.parse(JSON.stringify(this.selectedNode.data.smartIntentFallback));
                    
                    // Ensure fallback has all required properties
                    const fallbackDefaults = {
                        useAI: false,
                        provider: '',
                        model: '',
                        temperature: 0,
                        maxTokens: 150,
                        topP: 1.0,
                        presencePenalty: 0.0,
                        frequencyPenalty: 0.0,
                        streamingEnabled: true,
                        lineFlexMessage: false,
                        systemPrompt: '',
                        forbiddenWords: [''],
                        // Additional fields from AI Model node
                        typingSpeed: 50,
                        maxInputLength: 2000,
                        responseOption: 'text',
                        enableMemory: false,
                        memorySize: 10,
                        stopSequence: '',
                        seedValue: null,
                        enableSafetyFilter: false,
                        enableContentFilter: false
                    };
                    Object.keys(fallbackDefaults).forEach(key => {
                        if (this.nodeInspector.smartIntentFallback[key] === undefined) {
                            this.nodeInspector.smartIntentFallback[key] = fallbackDefaults[key];
                        }
                    });
                    // Ensure forbiddenWords is always an array
                    if (!Array.isArray(this.nodeInspector.smartIntentFallback.forbiddenWords)) {
                        this.nodeInspector.smartIntentFallback.forbiddenWords = [''];
                    }
                }
                
                if (this.selectedNode.data.smartIntentGroupName) {
                    this.nodeInspector.smartIntentGroupName = this.selectedNode.data.smartIntentGroupName;
                }
            }

            // Sync Orchestrator data back to nodeInspector
            if (this.selectedNode.data.type === 'orchestrator') {
                this.nodeInspector.orchestratorName = this.selectedNode.data.orchestratorName || '';
                // Model configuration
                this.nodeInspector.provider = this.selectedNode.data.provider || '';
                this.nodeInspector.model = this.selectedNode.data.model || '';
                this.nodeInspector.temperature = this.selectedNode.data.temperature ?? 0;
                this.nodeInspector.maxTokens = this.selectedNode.data.maxTokens ?? null;
                this.nodeInspector.topP = this.selectedNode.data.topP ?? 1;
                // Response Options
                this.nodeInspector.streamResponse = this.selectedNode.data.streamResponse ?? false;
                this.nodeInspector.jsonResponse = this.selectedNode.data.jsonResponse ?? false;
                // Memory Settings
                this.nodeInspector.memoryTurn = this.selectedNode.data.memoryTurn ?? null;
                this.nodeInspector.memoryDuration = this.selectedNode.data.memoryDuration ?? null;
                this.nodeInspector.shortTermMemory = this.selectedNode.data.shortTermMemory ?? 200;
                this.nodeInspector.longTermMemory = this.selectedNode.data.longTermMemory ?? false;
                // Prompts
                this.nodeInspector.systemPrompt = this.selectedNode.data.systemPrompt || '';
                this.nodeInspector.guardrailPrompt = this.selectedNode.data.guardrailPrompt || '';
                this.nodeInspector.allowAgentGuardrailBypass = this.selectedNode.data.allowAgentGuardrailBypass ?? true;
                // Configurable prompts / messages (blank = built-in default)
                this.nodeInspector.orchSingleSelectionTemplate = this.selectedNode.data.orchSingleSelectionTemplate || '';
                this.nodeInspector.orchPipelineSelectionTemplate = this.selectedNode.data.orchPipelineSelectionTemplate || '';
                this.nodeInspector.orchVerifySelectionTemplate = this.selectedNode.data.orchVerifySelectionTemplate || '';
                this.nodeInspector.orchAgentListItemTemplate = this.selectedNode.data.orchAgentListItemTemplate || '';
                this.nodeInspector.orchAgentNoToolsLabel = this.selectedNode.data.orchAgentNoToolsLabel || '';
                this.nodeInspector.orchConversationStateTemplate = this.selectedNode.data.orchConversationStateTemplate || '';
                this.nodeInspector.orchConversationStateFactTemplate = this.selectedNode.data.orchConversationStateFactTemplate || '';
                this.nodeInspector.orchCompanionNote = this.selectedNode.data.orchCompanionNote || '';
                this.nodeInspector.orchSequentialContextTemplate = this.selectedNode.data.orchSequentialContextTemplate || '';
                this.nodeInspector.orchCheckerGoalTemplate = this.selectedNode.data.orchCheckerGoalTemplate || '';
                this.nodeInspector.orchFallbackSubGoal = this.selectedNode.data.orchFallbackSubGoal || '';
                this.nodeInspector.orchAggregationTemplate = this.selectedNode.data.orchAggregationTemplate || '';
                this.nodeInspector.orchStrategyHintParallel = this.selectedNode.data.orchStrategyHintParallel || '';
                this.nodeInspector.orchStrategyHintPipeline = this.selectedNode.data.orchStrategyHintPipeline || '';
                this.nodeInspector.orchAggregationFallbackItemTemplate = this.selectedNode.data.orchAggregationFallbackItemTemplate || '';
                this.nodeInspector.orchGuardrailFormatSuffix = this.selectedNode.data.orchGuardrailFormatSuffix || '';
                this.nodeInspector.orchGuardrailUserTemplate = this.selectedNode.data.orchGuardrailUserTemplate || '';
                this.nodeInspector.orchDirectLlmPrompt = this.selectedNode.data.orchDirectLlmPrompt || '';
                this.nodeInspector.orchBlockedMessage = this.selectedNode.data.orchBlockedMessage || '';
                this.nodeInspector.orchDirectLlmEmptyMessage = this.selectedNode.data.orchDirectLlmEmptyMessage || '';
                this.nodeInspector.orchDirectLlmErrorMessage = this.selectedNode.data.orchDirectLlmErrorMessage || '';
                this.nodeInspector.orchNoAgentSucceededMessage = this.selectedNode.data.orchNoAgentSucceededMessage || '';
                // Extended fields
                this.nodeInspector.gender = this.selectedNode.data.gender || 'male';
                this.nodeInspector.typingSpeed = this.selectedNode.data.typingSpeed ?? null;
                this.nodeInspector.maxInputLength = this.selectedNode.data.maxInputLength ?? 200000;
                this.nodeInspector.frequencyPenalty = this.selectedNode.data.frequencyPenalty ?? null;
                this.nodeInspector.presencePenalty = this.selectedNode.data.presencePenalty ?? null;
                this.nodeInspector.stopSequence = this.selectedNode.data.stopSequence || null;
                this.nodeInspector.enableCache = this.selectedNode.data.enableCache ?? false;
                this.nodeInspector.cacheTTLMinutes = this.selectedNode.data.cacheTTLMinutes ?? 1440;
                this.nodeInspector.cacheScope = this.selectedNode.data.cacheScope || 'flow';
                this.nodeInspector.enableSemanticCache = this.selectedNode.data.enableSemanticCache ?? false;
                this.nodeInspector.similarityThreshold = this.selectedNode.data.similarityThreshold ?? 0.95;
                this.nodeInspector.showLineName = this.selectedNode.data.showLineName ?? false;
                this.nodeInspector.loadingBar = this.selectedNode.data.loadingBar ?? false;
                this.nodeInspector.loadingBarDuration = this.selectedNode.data.loadingBarDuration || 0;
                this.nodeInspector.monitorLineUserId = this.selectedNode.data.monitorLineUserId || null;
                this.nodeInspector.monitorSocialID = this.selectedNode.data.monitorSocialID || null;
                this.nodeInspector.forbiddenWords = this.selectedNode.data.forbiddenWords || [''];
                this.nodeInspector.enableTime = this.selectedNode.data.enableTime ?? null;
                this.nodeInspector.timeZone = this.selectedNode.data.timeZone || null;
                this.nodeInspector.enableTransactionId = this.selectedNode.data.enableTransactionId ?? null;
                this.nodeInspector.enableInputMessageReplace = this.selectedNode.data.enableInputMessageReplace ?? false;
                this.nodeInspector.enableVoicePromptReplace = this.selectedNode.data.enableVoicePromptReplace ?? false;
                this.nodeInspector.enableLanguageReplace = this.selectedNode.data.enableLanguageReplace ?? false;
            }

            // Sync Agent data back to nodeInspector
            if (this.selectedNode.data.type === 'agent') {
                this.nodeInspector.agentName = this.selectedNode.data.agentName || '';
                this.nodeInspector.agentDescription = this.selectedNode.data.agentDescription || '';
                this.nodeInspector.personaPrompt = this.selectedNode.data.personaPrompt || '';
                this.nodeInspector.agentFinalSummaryTemplate = this.selectedNode.data.agentFinalSummaryTemplate || '';
                this.nodeInspector.selectedTools = this.selectedNode.data.selectedTools ? 
                    JSON.parse(JSON.stringify(this.selectedNode.data.selectedTools)) : [];
                this.nodeInspector.useCustomAIModel = this.selectedNode.data.useCustomAIModel ?? false;
                this.nodeInspector.agentProvider = this.selectedNode.data.agentProvider || '';
                this.nodeInspector.agentModel = this.selectedNode.data.agentModel || null;
                this.nodeInspector.agentTemperature = this.selectedNode.data.agentTemperature ?? 0;
                this.nodeInspector.agentMaxTokens = this.selectedNode.data.agentMaxTokens ?? null;
                this.nodeInspector.agentTopP = this.selectedNode.data.agentTopP ?? 1;
                this.nodeInspector.agentFrequencyPenalty = this.selectedNode.data.agentFrequencyPenalty ?? null;
                this.nodeInspector.agentPresencePenalty = this.selectedNode.data.agentPresencePenalty ?? null;
                this.nodeInspector.agentStopSequence = this.selectedNode.data.agentStopSequence || null;
                this.nodeInspector.agentStreamResponse = this.selectedNode.data.agentStreamResponse ?? false;
                this.nodeInspector.agentJsonResponse = this.selectedNode.data.agentJsonResponse ?? false;
                this.nodeInspector.agentGender = this.selectedNode.data.agentGender || 'male';
                this.nodeInspector.agentTypingSpeed = this.selectedNode.data.agentTypingSpeed ?? null;
                this.nodeInspector.agentMaxInputLength = this.selectedNode.data.agentMaxInputLength ?? 200000;
                this.nodeInspector.agentMemoryTurn = this.selectedNode.data.agentMemoryTurn ?? null;
                this.nodeInspector.agentMemoryDuration = this.selectedNode.data.agentMemoryDuration ?? null;
                this.nodeInspector.agentShortTermMemory = this.selectedNode.data.agentShortTermMemory ?? 200;
                this.nodeInspector.agentLongTermMemory = this.selectedNode.data.agentLongTermMemory ?? false;
                this.nodeInspector.agentEnableTime = this.selectedNode.data.agentEnableTime ?? false;
                this.nodeInspector.agentTimeZone = this.selectedNode.data.agentTimeZone || null;
                this.nodeInspector.agentEnableTransactionId = this.selectedNode.data.agentEnableTransactionId ?? false;
                // Hosted tools — derive ฟอร์มใหม่จาก toolsConfig เสมอ (ไม่เขียน form state กลับลง node data)
                Object.assign(this.nodeInspector, this.buildNodeToolsInspectorState(this.selectedNode.data.toolsConfig));
            }

            // Sync Tax Planning Agent data back to nodeInspector.
            // 🔴 NOT the node-click path — this function has exactly one caller (the Smart Intent "load config from
            //    model" dialog). Clicking a node goes through openNodeInspector, which REBUILDS nodeInspector from
            //    one object literal, and that literal is where a missing key actually costs you the value. This
            //    block only keeps the three keys from being dropped on that one dialog's re-sync.
            if (this.selectedNode.data.type === 'taxPlanningAgent') {
                this.nodeInspector.agentName = this.selectedNode.data.agentName || '';
                this.nodeInspector.agentDescription = this.selectedNode.data.agentDescription || '';
                this.nodeInspector.bypassGuardrailWhenAlone = this.selectedNode.data.bypassGuardrailWhenAlone ?? true;
                this.nodeInspector.taxKnowledgeEnabled = this.selectedNode.data.taxKnowledgeEnabled ?? false;
                this.nodeInspector.taxKnowledgeSelections = Array.isArray(this.selectedNode.data.taxKnowledgeSelections)
                    ? this.selectedNode.data.taxKnowledgeSelections.map(x => ({ header: x.header, category: x.category ?? null }))
                    : [];
            }
        },

        // ══ Meta Document taxonomy for the Tax Planning Agent's knowledge picker ═══════════════════════════
        // 🔴 Loaded from the SAME two endpoints the Meta Document admin screen manages, never from a hardcoded
        //    list: MetaDocCategory is the header level (MetaDocPrefixMapping Level="Category") and
        //    MetaDocSubCategory is the category level (Level="Type", joined by CategoryTitle). A copy of the tree
        //    kept in this file would go stale the first time somebody adds a category and would silently offer a
        //    picker that cannot express what the documents are actually filed under.
        loadTaxKnowledgeTaxonomy(force) {
            if (this.taxKnowledgeLoading) return;
            if (!force && this.taxKnowledgeLoaded) return;

            const self = this;
            self.taxKnowledgeLoading = true;
            self.taxKnowledgeLoadError = false;

            const done = () => {
                if (self._taxKnowledgePending > 0) return;
                self.taxKnowledgeLoading = false;
                self.taxKnowledgeLoaded = !self.taxKnowledgeLoadError;
            };

            // App Knowledge Center (A4): โหมด Center ⇒ ทะเบียนหัวข้อ/หมวดของ Center (passthrough · อ่านไม่ได้ ⇒ สร้างจาก
            // catalog: หัวข้อ = documentCategory, หมวด = title) · โหมดปิด (null) ⇒ $call เดิมสองตัวด้านล่างทุกบรรทัด
            kccPickerSource.metaDocTaxonomy().then((kccTaxonomy) => {
                if (kccTaxonomy !== null) {
                    self.kccCenterMode = true;
                    self.taxKnowledgeHeaderRows = kccTaxonomy.headers;
                    self.taxKnowledgeCategoryRows = kccTaxonomy.categories;
                    // reason ≠ '' = ดึงไม่ได้ (ไม่มี Runtime key / Center ล้ม) ⇒ ถือเป็นโหลดไม่สำเร็จ ให้กด "ลองอีกครั้ง" ได้
                    self.taxKnowledgeLoadError = !!kccTaxonomy.reason;
                    self.kccTaxonomyNotice = kccTaxonomy.reason
                        || (kccTaxonomy.source === 'catalog' ? 'kccTaxonomyFromCatalog' : 'kccTaxonomyCenterOnly');
                    self._taxKnowledgePending = 0;
                    done();
                    return;
                }
                self.kccTaxonomyNotice = '';
                self._taxKnowledgePending = 2;

                self.$call.GetMetaDocCategories(
                    (res) => {
                        self.taxKnowledgeHeaderRows = (res && res.status === 0 && Array.isArray(res.data)) ? res.data : [];
                        self._taxKnowledgePending--; done();
                    },
                    () => {
                        self.taxKnowledgeHeaderRows = [];
                        self.taxKnowledgeLoadError = true;
                        self._taxKnowledgePending--; done();
                    }
                );

                self.$call.GetMetaDocSubCategories(
                    (res) => {
                        self.taxKnowledgeCategoryRows = (res && res.status === 0 && Array.isArray(res.data)) ? res.data : [];
                        self._taxKnowledgePending--; done();
                    },
                    () => {
                        self.taxKnowledgeCategoryRows = [];
                        self.taxKnowledgeLoadError = true;
                        self._taxKnowledgePending--; done();
                    }
                );
            }).catch((e) => {
                // kccPickerSource ไม่ throw — กันไว้ไม่ให้ skeleton ค้างถ้า callback ข้างบนพังเอง
                console.error('loadTaxKnowledgeTaxonomy failed:', e);
                self.taxKnowledgeLoadError = true;
                self._taxKnowledgePending = 0;
                done();
            });
        },

        // ══ Mutual Fund card configs for the node inspector's three card pickers ═══════════════════════════
        /// Fetched on the first open of a Mutual Fund inspector, never on page load. A failure leaves the list
        /// empty and `…Loaded` false so the next open retries — call.js already alerts, so no second popup here.
        loadMutualFundCardConfigs() {
            if (this.mutualFundCardConfigsLoading || this.mutualFundCardConfigsLoaded) return;
            this.mutualFundCardConfigsLoading = true;
            const self = this;
            self.$call.GetMutualFundCardConfigs(
                (res) => {
                    self.mutualFundCardConfigsLoading = false;
                    const items = res && res.status === 0 && res.data && Array.isArray(res.data.items) ? res.data.items : [];
                    self.mutualFundCardConfigs = items;
                    self.mutualFundCardConfigsLoaded = true;
                },
                () => {
                    self.mutualFundCardConfigsLoading = false;
                    self.mutualFundCardConfigs = [];
                }
            );
        },

        /// Picker options for ONE card type. The stored value is `configCode` — the business key the node
        /// resolves at run time — while the label falls back to the code so a config saved with a blank name
        /// is still pickable instead of rendering as an empty row.
        mutualFundConfigOptions(cardType) {
            return (this.mutualFundCardConfigs || [])
                .filter(row => row && row.isActive !== false && String(row.cardType || '').toUpperCase() === cardType)
                .map(row => ({ title: String(row.configName || '').trim() || row.configCode, value: row.configCode }));
        },

        /// Codes are stored with underscores (Personal_Income_Tax). Shown with spaces — the underscore is a
        /// storage detail and reading it in a checkbox label is needless friction.
        prettyTaxonomy(code) {
            return (code || '').replace(/_/g, ' ');
        },

        taxKnowledgeSelections() {
            const list = this.nodeInspector && this.nodeInspector.taxKnowledgeSelections;
            return Array.isArray(list) ? list : [];
        },

        isTaxKnowledgeSelected(header, category) {
            return this.taxKnowledgeSelections().some(s =>
                s && s.header === header && (s.category || null) === (category || null));
        },

        /// 🔴 Every mutation goes through here so there is exactly ONE path from a click to the node's data.
        ///    A method that edited nodeInspector directly would look identical on screen and never save.
        commitTaxKnowledgeSelections(next) {
            const clean = (next || []).filter(s => s && s.header);
            this.nodeInspector.taxKnowledgeSelections = clean;
            this.updateNodeProperty('taxKnowledgeSelections', clean);
        },

        toggleTaxKnowledgeCategory(header, category) {
            const current = this.taxKnowledgeSelections();
            // A whole-header selection swallows its categories, so an individual tick under one is a no-op
            // rather than a contradictory pair of entries.
            if (this.isTaxKnowledgeSelected(header, null)) return;
            this.commitTaxKnowledgeSelections(
                this.isTaxKnowledgeSelected(header, category)
                    ? current.filter(s => !(s.header === header && (s.category || null) === category))
                    : current.concat([{ header: header, category: category }])
            );
        },

        /// Tri-state: nothing → whole header; anything → clear the header entirely. "Whole header" is stored as a
        /// single {header, category:null} entry rather than one entry per category, so a category added later is
        /// included automatically — which is what an author ticking a header means.
        toggleTaxKnowledgeHeader(h) {
            if (this.isViewMode) return;
            const state = this.headerSelectState(h);
            const withoutHeader = this.taxKnowledgeSelections().filter(s => s.header !== h.title);
            this.commitTaxKnowledgeSelections(
                state === 'none' ? withoutHeader.concat([{ header: h.title, category: null }]) : withoutHeader
            );
        },

        removeTaxKnowledgeSelection(header, category) {
            this.commitTaxKnowledgeSelections(this.taxKnowledgeSelections().filter(s =>
                !(s.header === header && (s.category || null) === (category || null))));
        },

        clearTaxKnowledgeSelections() {
            this.commitTaxKnowledgeSelections([]);
        },

        headerSelectedCount(h) {
            if (this.isTaxKnowledgeSelected(h.title, null)) return h.categories.length || 1;
            return this.taxKnowledgeSelections().filter(s => s.header === h.title && s.category).length;
        },

        headerSelectState(h) {
            if (this.isTaxKnowledgeSelected(h.title, null)) return 'all';
            const picked = this.headerSelectedCount(h);
            if (picked === 0) return 'none';
            return (h.categories.length > 0 && picked >= h.categories.length) ? 'all' : 'some';
        },

        // ══ Meta Documents node picker — mirrors the taxKnowledge* methods above, bound to
        //    nodeInspector.metaDocSelections. Same invariants: every mutation goes through
        //    commitMetaDocSelections (the ONLY path that persists via updateNodeProperty);
        //    whole-header is stored as one {header, category: null} entry that swallows its
        //    categories and auto-includes categories added later.
        metaDocSelectionsList() {
            const list = this.nodeInspector && this.nodeInspector.metaDocSelections;
            return Array.isArray(list) ? list : [];
        },

        isMetaDocSelected(header, category) {
            return this.metaDocSelectionsList().some(s =>
                s && s.header === header && (s.category || null) === (category || null));
        },

        commitMetaDocSelections(next) {
            const clean = (next || []).filter(s => s && s.header);
            this.nodeInspector.metaDocSelections = clean;
            this.updateNodeProperty('metaDocSelections', clean);
        },

        /// What the CHECKBOX shows, which is not the same question as what is STORED: a whole-header
        /// entry ({header, category:null}) means every category is in scope, so every category reads
        /// as ticked. Storage stays one entry — see toggleMetaDocHeader for why that matters.
        isMetaDocCategoryChecked(header, category) {
            return this.isMetaDocSelected(header, null) || this.isMetaDocSelected(header, category);
        },

        /// Takes the header OBJECT, not its title, because unticking a category under a whole-header
        /// selection has to know the header's full category list to expand it.
        toggleMetaDocCategory(h, category) {
            if (this.isViewMode) return;
            const current = this.metaDocSelectionsList();

            // Unticking one category while the WHOLE header is selected: rewrite the single
            // {header, category:null} entry as explicit per-category entries minus this one.
            // The author narrowed the scope deliberately, and the thing they give up by doing so
            // is exactly the header entry's "categories added later are included too" property.
            if (this.isMetaDocSelected(h.title, null)) {
                const narrowed = (h.categories || [])
                    .filter(c => c.key !== category)
                    .map(c => ({ header: h.title, category: c.key }));
                this.commitMetaDocSelections(
                    current.filter(s => s.header !== h.title).concat(narrowed)
                );
                return;
            }

            this.commitMetaDocSelections(
                this.isMetaDocSelected(h.title, category)
                    ? current.filter(s => !(s.header === h.title && (s.category || null) === category))
                    : current.concat([{ header: h.title, category: category }])
            );
        },

        toggleMetaDocHeader(h) {
            if (this.isViewMode) return;
            const state = this.metaDocHeaderSelectState(h);
            const withoutHeader = this.metaDocSelectionsList().filter(s => s.header !== h.title);
            this.commitMetaDocSelections(
                state === 'none' ? withoutHeader.concat([{ header: h.title, category: null }]) : withoutHeader
            );
        },

        removeMetaDocSelection(header, category) {
            this.commitMetaDocSelections(this.metaDocSelectionsList().filter(s =>
                !(s.header === header && (s.category || null) === (category || null))));
        },

        clearMetaDocSelections() {
            this.commitMetaDocSelections([]);
        },

        metaDocHeaderSelectedCount(h) {
            if (this.isMetaDocSelected(h.title, null)) return h.categories.length || 1;
            return this.metaDocSelectionsList().filter(s => s.header === h.title && s.category).length;
        },

        metaDocHeaderSelectState(h) {
            if (this.isMetaDocSelected(h.title, null)) return 'all';
            const picked = this.metaDocHeaderSelectedCount(h);
            if (picked === 0) return 'none';
            return (h.categories.length > 0 && picked >= h.categories.length) ? 'all' : 'some';
        },

        async loadModelConfigPreview(modelId) {
            try {
                this.$call.GetGPTModelById(
                    modelId,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            const modelConfig = dataResponse.data;
                            this.modelConfigPreview = {
                                provider: modelConfig.provider || 'N/A',
                                model: modelConfig.model || 'N/A',
                                temperature: modelConfig.temperature ?? 'N/A',
                                maxTokens: modelConfig.max_tokens ?? 'N/A',
                                topP: modelConfig.top_p ?? 'N/A',
                                frequencyPenalty: modelConfig.frequency_penalty ?? 'N/A',
                                presencePenalty: modelConfig.presence_penalty ?? 'N/A',
                                systemPrompt: modelConfig.promptTemplate ? (modelConfig.promptTemplate.length > 100 ? modelConfig.promptTemplate.substring(0, 100) + '...' : modelConfig.promptTemplate) : 'N/A'
                            };
                        }
                    },
                    (error) => {
                        console.error('Error loading model config preview:', error);
                    }
                );
            } catch (error) {
                console.error('Error loading model config preview:', error);
            }
        },

        async generateFlowVersionNote() {
            // Only allow for existing flows (updates)
            if (!this.flowId) {
                this.$swal.fire({
                    icon: 'info',
                    title: 'Not Available',
                    text: 'Version note generation is only available when updating existing flows.',
                    showConfirmButton: true,
                    confirmButtonColor: '#1976d2',
                });
                return;
            }

            this.generatingNote = true;

            try {
                // Prepare the flow data similar to SaveFlow
                // This represents the NEW data (what the user is about to save)
                const flatNodes = this.convertToFlatStructure();
                
                // Convert to API format (stringify position, data, targets)
                const apiFlowDetails = flatNodes.map(node => ({
                    ...node,
                    position: JSON.stringify(node.position),
                    data: JSON.stringify(node.data),
                    targets: JSON.stringify(node.targets)
                }));
                
                const request = {
                    ID: this.flowId,
                    Name: this.flowName,
                    VersionComment: this.saveFlowData.versionComment || '',
                    FlowDetails: apiFlowDetails
                };

                await this.$call.SummarizeFlowChanges(
                    request,
                    this.flowId,
                    (dataResponse) => {
                        if (dataResponse.status === 0) {
                            this.generatedHistoryNote = dataResponse.data.historyNote || '';
                            this.fieldDiffs = dataResponse.data.diff || {};

                            if (this.generatedHistoryNote) {
                                this.saveFlowData.versionComment = this.generatedHistoryNote;
                            }

                            this.$swal.fire({
                                icon: 'success',
                                title: 'Note Generated',
                                text: 'Version note has been generated successfully!',
                                showConfirmButton: false,
                                timer: 1500,
                            });
                        } else {
                            this.$swal.fire({
                                icon: 'error',
                                title: 'Generation Failed',
                                text: dataResponse.message || 'Failed to generate version note.',
                                showConfirmButton: true,
                                confirmButtonColor: '#d33',
                            });
                        }
                        this.generatingNote = false;
                    },
                    (errorResource) => {
                        this.$swal.fire({
                            icon: 'error',
                            title: 'Error',
                            text: 'An error occurred while generating the version note.',
                            showConfirmButton: true,
                            confirmButtonColor: '#d33',
                        });
                        this.generatingNote = false;
                    }
                );
            } catch (error) {
                this.generatingNote = false;
                this.$swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'An unexpected error occurred.',
                    showConfirmButton: true,
                    confirmButtonColor: '#d33',
                });
            }
        },

        async confirmSave() {
            this.saving = true;
            try {
                // Call the existing SaveFlow method
                await this.SaveFlow();

                this.closeSaveDialog();

            } catch (error) {
                console.error('Error saving flow:', error);
            } finally {
                this.saving = false;
            }
        },

        async performSave() {
            if (!this.saveFormValid) return;

            this.saving = true;
            try {
                // Update flow metadata
                this.flowName = this.saveFlowData.name;

                // Call the existing SaveFlow method
                await this.SaveFlow();

                this.closeSaveDialog();

            } catch (error) {
                console.error('Error saving flow:', error);
            } finally {
                this.saving = false;
            }
        },

        // Update exportFlow to use targets structure
        exportFlow() {
            try {
                const flatNodes = this.convertToFlatStructure();

                const flowData = {
                    name: this.flowName,
                    nodes: flatNodes,
                    metadata: {
                        exportedAt: new Date().toISOString(),
                        nodeCount: this.tempNode.length,
                        edgeCount: this.tempEdge.length,
                    }
                };

                const dataStr = JSON.stringify(flowData, null, 2);
                const dataBlob = new Blob([dataStr], { type: 'application/json' });

                const link = document.createElement('a');
                link.href = URL.createObjectURL(dataBlob);
                link.download = `Flow_Config_${this.flowName.replace(/\s+/g, '_')}.json`;
                link.click();

                this.showSuccess('Flow exported successfully');
            } catch (error) {
                this.showError('Failed to export flow');
            }
        },
        navigateToCompare() {
            if (!this.flowId) return;
            this.$router.push({
                name: 'FlowCompare',
                query: {
                    rightId: this.flowId,
                    mode: 'version',
                },
            });
        },

        // Update importFlow to handle targets structure
        importFlow() {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = '.json';
            input.onchange = (e) => {
                const file = e.target.files[0];
                if (!file) return;

                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const flowData = JSON.parse(e.target.result);
                        
                        // Validate flow data structure
                        if (!flowData.nodes) {
                            throw new Error('Invalid flow file format');
                        }

                        // Check format type
                        const firstNode = flowData.nodes[0];
                        let nodes, edges;
                        if (firstNode && firstNode.targets !== undefined) {
                            // New targets format
                            const result = this.convertFromFlatStructure(flowData.nodes);
                            nodes = result.nodes;
                            edges = result.edges;
                        } else if (firstNode && firstNode.source !== undefined) {
                            // Old source format
                            const result = this.convertFromSourceFormat(flowData.nodes);
                            nodes = result.nodes;
                            edges = result.edges;
                        } else if (flowData.edges) {
                            // Very old format with separate edges
                            nodes = flowData.nodes;
                            edges = flowData.edges || [];
                        } else {
                            throw new Error('Unsupported flow file format');
                        }

                        // Generate new IDs for all nodes and update references
                        const idMap = new Map();
                        nodes.forEach(node => {
                            const oldId = node.id;
                            const newId = this.generateGuid();
                            idMap.set(oldId, newId);
                            node.id = newId;
                        });

                        // Update edge sources and targets
                        edges.forEach(edge => {
                            if (idMap.has(edge.source)) {
                                edge.source = idMap.get(edge.source);
                            }
                            if (idMap.has(edge.target)) {
                                edge.target = idMap.get(edge.target);
                            }
                        });

                        // Update node data targets if they exist
                        nodes.forEach(node => {
                            if (node.data && node.data.targets && Array.isArray(node.data.targets)) {
                                node.data.targets = node.data.targets.map(targetId => 
                                    idMap.has(targetId) ? idMap.get(targetId) : targetId
                                );
                            }
                            // Also check for any other references in node data
                            if (node.data) {
                                const dataStr = JSON.stringify(node.data);
                                const updatedDataStr = dataStr.replace(/("target":\s*"[^"]*")/g, (match, targetPart) => {
                                    const targetId = targetPart.match(/"target":\s*"([^"]*)"/)[1];
                                    if (idMap.has(targetId)) {
                                        return `"target":"${idMap.get(targetId)}"`;
                                    }
                                    return match;
                                });
                                if (dataStr !== updatedDataStr) {
                                    node.data = JSON.parse(updatedDataStr);
                                }
                            }
                        });

                        this.tempNode = nodes;
                        this.tempEdge = edges;

                        this.flowName = flowData.name || 'Imported Flow';

                        this.showSuccess('Flow imported successfully');
                        this.$nextTick(() => {
                            // Check validation for all nodes after importing
                            this.checkAllNodesValidation();

                            setTimeout(() => {
                                // Fit view after importing nodes
                                if (this.$refs.vueFlow && this.tempNode.length > 0) {
                                    try {
                                        this.$refs.vueFlow.fitView({
                                            padding: 0.1,
                                            minZoom: 0.5,
                                            maxZoom: 1.5,
                                            duration: 800,
                                            includeHiddenNodes: false
                                        });
                                    } catch (error) {
                                        console.warn('Failed to fit view after import:', error);
                                    }
                                }
                            }, 200);
                        });

                    } catch (error) {
                        this.showError('Failed to import flow: Invalid file format');
                    }
                };
                reader.readAsText(file);
            };
            input.click();
        },

        // Drag and Drop Methods for JSON Editor
        onDragStart(event, text) {
            event.dataTransfer.setData("text/plain", text);
            event.dataTransfer.effectAllowed = "copy";
        },

        onJsonDragOver(event) {
            event.preventDefault();
            event.stopPropagation();

            const ta = this.getTextareaElement(event.target);
            if (ta) ta.classList.add("drag-over");
        },

        onJsonDragLeave(event) {
            event.stopPropagation();

            const ta = this.getTextareaElement(event.target);
            if (ta) ta.classList.remove("drag-over");
        },

        onJsonDrop(event) {
            event.preventDefault();
            event.stopPropagation(); // ป้องฝันฝารทำงานซ้ำซ้อน

            const text = event.dataTransfer.getData("text/plain");
            if (!text) return;

            // หา textarea element ที่๝ท้จริง
            const ta = this.getTextareaElement(event.target);
            if (!ta) return;

            ta.classList.remove("drag-over");

            // คำนวณตำ๝หน่งฝาร๝ทรฝข้อความจาฝตำ๝หน่งของเมาส์
            const caretPosition = this.getCaretPositionFromPoint(ta, event.clientX, event.clientY);

            // ๝ทรฝข้อความที่ตำ๝หน่งที่คำนวณได้
            const value = ta.value || "";
            const newValue = value.slice(0, caretPosition) + text + value.slice(caretPosition);

            // อัพเดต textarea ๝ละ model
            ta.value = newValue;
            this.nodeInspector.jsonValidate = newValue;
            this.updateNodeProperty("jsonValidate", newValue);

            // เลื่อน caret หลังข้อความที่๝ทรฝ
            const newPos = caretPosition + text.length;
            this.$nextTick(() => {
                ta.setSelectionRange(newPos, newPos);
                ta.focus();
            });
        },
        getCaretPositionFromPoint(textarea, clientX, clientY) {
            // สำหรับเบราว์เซอร์สมัยใหม่ที่รองรับ caretPositionFromPoint/caretRangeFromPoint
            if (document.caretPositionFromPoint) {
                const position = document.caretPositionFromPoint(clientX, clientY);
                if (position && position.offsetNode === textarea.firstChild) {
                    return position.offset;
                }
            } else if (document.caretRangeFromPoint) {
                const range = document.caretRangeFromPoint(clientX, clientY);
                if (range && range.startContainer === textarea.firstChild) {
                    return range.startOffset;
                }
            }

            // ถ้าไม่สามารถคำนวณตำ๝หน่งได้ ให้ใช้ตำ๝หน่งปัจจุบัน หรือท้ายข้อความ
            return textarea.selectionStart || textarea.value.length;
        },

        // หา <textarea> จริงๆ ใน v-textarea
        getTextareaElement(target) {
            if (target.tagName === "TEXTAREA") return target;
            const container = target.closest(".json-textarea-container");
            return container && container.querySelector("textarea");
        },

        // Click to insert variable
        insertVariable(text) {
            const textarea = this.$refs.jsonTextarea?.$el?.querySelector('textarea');

            if (textarea) {
                const start = textarea.selectionStart || 0;
                const end = textarea.selectionEnd || 0;
                const currentValue = textarea.value || '';

                const newValue =
                    currentValue.substring(0, start) +
                    text +
                    currentValue.substring(end);

                // อัพเดต model
                this.nodeInspector.jsonValidate = newValue;
                this.updateNodeProperty('jsonValidate', newValue);

                // ตั้งตำ๝หน่ง cursor
                this.$nextTick(() => {
                    textarea.setSelectionRange(start + text.length, start + text.length);
                    textarea.focus();
                });
            }
        },

        // Click to insert variable for Knowledge API
        insertKnowledgeVariable(text) {
            const textarea = this.$refs.knowledgeJsonTextarea?.$el?.querySelector('textarea');

            if (textarea) {
                const start = textarea.selectionStart || 0;
                const end = textarea.selectionEnd || 0;
                const currentValue = textarea.value || '';

                const newValue =
                    currentValue.substring(0, start) +
                    text +
                    currentValue.substring(end);

                // อัพเดต model
                this.nodeInspector.knowledgeRequestBody = newValue;
                this.updateNodeProperty('knowledgeRequestBody', newValue);

                // ตั้งตำ๝หน่ง cursor
                this.$nextTick(() => {
                    textarea.setSelectionRange(start + text.length, start + text.length);
                    textarea.focus();
                });
            }
        },

        // Drag and drop for Knowledge API JSON
        onKnowledgeJsonDrop(event) {
            event.preventDefault();
            event.stopPropagation();

            const text = event.dataTransfer.getData("text/plain");
            if (!text) return;

            // หา textarea element ที่แท้จริง
            const ta = this.getTextareaElement(event.target);
            if (!ta) return;

            ta.classList.remove("drag-over");

            // คำนวณตำแหน่งการแทรกข้อความจากตำแหน่งของเมาส์
            const caretPosition = this.getCaretPositionFromPoint(ta, event.clientX, event.clientY);

            // แทรกข้อความที่ตำแหน่งที่คำนวณได้
            const value = ta.value || "";
            const newValue = value.slice(0, caretPosition) + text + value.slice(caretPosition);

            // อัพเดต textarea และ model
            ta.value = newValue;
            this.nodeInspector.knowledgeRequestBody = newValue;
            this.updateNodeProperty("knowledgeRequestBody", newValue);

            // เลื่อน caret หลังข้อความที่แทรก
            const newPos = caretPosition + text.length;
            this.$nextTick(() => {
                ta.setSelectionRange(newPos, newPos);
                ta.focus();
            });
        },

        getValidationErrors() {
            const errors = [];

            if (!this.tempNode.length) {
                errors.push('Flow must contain at least one node');
            }

            const isolatedNodes = this.tempNode.filter(node => node.data.showWarning != false);
            
            if (isolatedNodes.length) {
                isolatedNodes.forEach(element => {
                    errors.push(`${element.id} isolated node found`);
                });
            }

            return errors;
        },

        formatDate(dateString) {
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

            if (diffDays === 0) return 'Today';
            if (diffDays === 1) return 'Yesterday';
            if (diffDays < 7) return `${diffDays} days ago`;

            return date.toLocaleDateString();
        },

        handleBeforeUnload(event) {
            // Skip browser confirmation if we're already navigating away via custom dialog
            if (this.isNavigatingAway) {
                return;
            }
            
            // Show warning for page refresh, close tab, or navigate away from the page
            if (!this.isViewMode) {
                const message = 'Are you sure you want to leave? Any unsaved changes will be lost.';
                
                // Modern browsers - this will trigger the browser's confirmation dialog
                event.preventDefault();
                event.returnValue = message;
                
                // For older browsers
                return message;
            }
        },

        // Custom confirmation dialog methods
        showExitConfirmation(callback) {
            if (!this.isViewMode && (this.canUndo || this.canRedo)) {
                this.pendingNavigation = callback;
                this.confirmExitDialog = true;
                return false; // Prevent immediate navigation
            } else {
                callback(); // Proceed with navigation if in view mode
                return true;
            }
        },

        confirmExit() {
            this.confirmExitDialog = false;
            this.isNavigatingAway = true; // Set flag to prevent browser confirmation
            
            if (this.pendingNavigation) {
                // Add a small delay to ensure the flag is set before navigation
                this.$nextTick(() => {
                    this.pendingNavigation();
                    this.pendingNavigation = null;
                });
            }
        },

        cancelExit() {
            this.confirmExitDialog = false;
            this.pendingNavigation = null;
        },

        // Prompt Template Methods
        showPromptTemplate() {
            this.promptTemplateDialog = true;
        },
        
        showSmartIntentPromptTemplate(smartIntentIndex) {
            this.smartIntentCurrentIndex = smartIntentIndex;
            // Reset any existing group selection and search query for fresh start
            this.smartIntentGroupPromptTemplate = null;
            this.smartIntentTemplateSearchQuery = '';
            this.smartIntentPromptTemplateDialog = true;
        },

        setSelectSmartIntentPromptTemplate(template) {
            if (template && template.prompt && this.smartIntentCurrentIndex !== null) {
                const smartIntent = this.nodeInspector.smartIntents[this.smartIntentCurrentIndex];
                if (smartIntent && smartIntent.mainAction) {
                    // Insert template content into Smart Intent system prompt
                    const currentPrompt = smartIntent.mainAction.systemPrompt || '';
                    const templatePrompt = template.prompt;

                    // Add template content (could append or replace based on preference)
                    smartIntent.mainAction.systemPrompt = currentPrompt + (currentPrompt ? '\n\n' : '') + templatePrompt;
                    this.updateSmartIntentData();
                }

                // Close template dialog
                this.smartIntentPromptTemplateDialog = false;
                this.smartIntentCurrentIndex = null; // Reset
            }
        },

        showSmartIntentFallbackPromptTemplate() {
            // Reset any existing group selection and search query for fresh start
            this.smartIntentFallbackGroupPromptTemplate = null;
            this.smartIntentFallbackTemplateSearchQuery = '';
            this.smartIntentFallbackPromptTemplateDialog = true;
        },

        setSelectSmartIntentFallbackPromptTemplate(template) {
            if (template && template.prompt) {
                // Insert template content into Smart Intent Fallback system prompt
                const currentPrompt = this.nodeInspector.smartIntentFallback.systemPrompt || '';
                const templatePrompt = template.prompt;

                // Add template content (could append or replace based on preference)
                this.nodeInspector.smartIntentFallback.systemPrompt = currentPrompt + (currentPrompt ? '\n\n' : '') + templatePrompt;
                this.updateSmartIntentFallbackData();

                // Close template dialog
                this.smartIntentFallbackPromptTemplateDialog = false;
            }
        },

        setSelectPromptTemplate(template) {
            if (template && template.prompt) {
                // Only for regular AI Model nodes
                const currentPrompt = this.nodeInspector.systemPrompt || '';
                const templatePrompt = template.prompt;

                // Add template content (could append or replace based on preference)
                this.nodeInspector.systemPrompt = currentPrompt + (currentPrompt ? '\n\n' : '') + templatePrompt;
                this.updateNodeProperty('systemPrompt', this.nodeInspector.systemPrompt);

                // Close template dialog
                this.promptTemplateDialog = false;
            }
        },
        
        truncatePrompt(prompt) {
            if (!prompt) return '';
            return prompt.length > 100 ? prompt.substring(0, 100) + '...' : prompt;
        },

        onContextMenu(event) {
            if (this.filteredPromptTemplates.length > 0) {
                event.preventDefault();
                this.x = event.clientX;
                this.y = event.clientY;
                this.showMenu = true;
            }
        },

        // IntentAI Prompt Template Methods
        showIntentaiPromptTemplate() {
            this.intentaiPromptTemplateDialog = true;
        },

        setSelectIntentaiPromptTemplate(template) {
            if (template && template.prompt) {
                // Insert template content into intentAI system prompt
                const currentPrompt = this.nodeInspector.intentaiSystemPrompt || '';
                const templatePrompt = template.prompt;

                // Add template content (could append or replace based on preference)
                this.nodeInspector.intentaiSystemPrompt = currentPrompt + (currentPrompt ? '\n\n' : '') + templatePrompt;
                this.updateNodeProperty('intentaiSystemPrompt', this.nodeInspector.intentaiSystemPrompt);

                // Close template dialog
                this.intentaiPromptTemplateDialog = false;
            }
        },

        onIntentaiContextMenu(event) {
            if (this.filteredIntentaiPromptTemplates.length > 0) {
                event.preventDefault();
                this.x = event.clientX;
                this.y = event.clientY;
                this.showIntentaiMenu = true;
            }
        },

        // Input validation functions
        isNumberpositive: function (evt) {
            evt = evt || window.event;
            let charCode = evt.which ? evt.which : evt.keyCode;
            // Block minus sign (45) and any non-digit characters, but allow control keys (0-31)
            if ((charCode > 31 && (charCode < 48 || charCode > 57)) || charCode === 45) {
                evt.preventDefault();
            } else {
                return true;
            }
        },

        maxNumberpositive: function (evt) {
            evt = evt || window.event;
            let charCode = evt.which ? evt.which : evt.keyCode;

            // อนุฝาตเฉพาะตัวเลข 0-9 เท่านั้น (บล็อฝเครื่องหมายลบ๝ละตัวอื่นๆ)
            if (charCode < 48 || charCode > 57) {
                evt.preventDefault();
                return false;
            }

            return true;
        },

        // Function สำหรับตรวจสอบ๝ละปรับค่าเมื่อ unfocus หรือฝด enter
        validateAndCorrectValue: function (evt, min = 1, max = 200000) {
            let value = parseInt(evt.target.value) || 0;

            if (value < min) {
                evt.target.value = min;
            } else if (value > max) {
                evt.target.value = max;
            }

            // อัปเดตค่าในโมเดล
            const fieldName = evt.target.getAttribute('data-field');
            if (fieldName && this.nodeInspector[fieldName] !== undefined) {
                this.nodeInspector[fieldName] = parseInt(evt.target.value);
                this.updateNodeProperty(fieldName, parseInt(evt.target.value));
            }
        },

        // Email list management methods
        addEmailToList(listType) {
            if(listType == 'ccEmails') {
                if (!this.nodeInspector.ccEmails) {
                    this.nodeInspector.ccEmails = [];
                }
                this.nodeInspector.ccEmails.push('');
            }
            else {
                if (!this.nodeInspector.bccEmails) {
                    this.nodeInspector.bccEmails = [];
                }
                this.nodeInspector.bccEmails.push('');
            }
            this.updateEmailList(listType);
        },

        removeEmailFromList(listType, index) {
            if (this.nodeInspector[listType]) {
                this.nodeInspector[listType].splice(index, 1);
                this.updateEmailList(listType);
            }
        },

        updateEmailList(listType) {
            if(listType == 'ccEmails') {
                if (!this.nodeInspector.ccEmails) {
                    this.nodeInspector.ccEmails = [];
                    this.updateNodeProperty(listType, []);
                    return;
                }
                const filteredEmails = this.nodeInspector.ccEmails.filter(email => email && email.trim() !== '');
                this.nodeInspector.ccEmails = [...this.nodeInspector.ccEmails]; // trigger reactivity
                this.updateNodeProperty(listType, this.nodeInspector.ccEmails);
            }
            else {
                if (!this.nodeInspector.bccEmails) {
                    this.nodeInspector.bccEmails = [];
                    this.updateNodeProperty(listType, []);
                    return;
                }
                const filteredEmails = this.nodeInspector.bccEmails.filter(email => email && email.trim() !== '');
                this.nodeInspector.bccEmails = [...this.nodeInspector.bccEmails]; // trigger reactivity
                this.updateNodeProperty(listType, this.nodeInspector.bccEmails);
            }
        },

        // Interest Optimizer Methods
        async loadAccountInfoDropdown() {
            try {
                let self = this;
                self.loadingInterestMaster = true;
                await self.$call.GetAccountInfoDropdownOptions(
                    (dataResponse) => {
                    if (dataResponse.status == 0) {
                        self.interestMasterOptions = dataResponse.data;
                    } else {
                        self.interestMasterOptions = [];
                    }
                    self.loadingInterestMaster = false;
                    },
                    (errorResource) => {
                    self.loadingInterestMaster = false;
                    console.error('Error loading AccountInfo dropdown:', errorResource);
                    self.$swal.fire({
                        icon: 'error',
                        title: 'ข้อผิดพลาด',
                        text: 'ไม่สามารถโหลดข้อมูล Account Code ได้',
                    });
                    }
                );
            } catch (error) {
                console.error('Error loading account info:', error);
                this.interestMasterOptions = [];
            } finally {
                this.loadingInterestMaster = false;
            }
        },

        removeAccountCode(accountCode) {
            if (this.nodeInspector.ioAccountCodes) {
                const index = this.nodeInspector.ioAccountCodes.indexOf(accountCode);
                if (index > -1) {
                    this.nodeInspector.ioAccountCodes.splice(index, 1);
                    this.updateNodeProperty('ioAccountCodes', this.nodeInspector.ioAccountCodes);
                }
            }
        },

        removeLoanInterestRate(interestRateID) {
            if (this.nodeInspector.liInterestRates) {
                const index = this.nodeInspector.liInterestRates.indexOf(interestRateID);
                if (index > -1) {
                    this.nodeInspector.liInterestRates.splice(index, 1);
                    this.updateNodeProperty('liInterestRates', this.nodeInspector.liInterestRates);
                }
            }
        },

        async loadLoanInterestRates() {
            this.loadingLoanInterestRates = true;
            try {
                await new Promise((resolve, reject) => {
                    this.$call.GetInterestRates(
                        (response) => {
                            if (response && response.data) {
                                // Filter only Home Loan type and active interest rates
                                this.loanInterestRateOptions = response.data
                                    .filter(rate => rate.isActive)
                                    .map(rate => ({
                                        interestRateID: rate.interestRateID,
                                        interestRateName: rate.interestRateName,
                                        interestRateType: rate.interestRateType,
                                        categoryID: rate.categoryID,
                                        categoryName: rate.categoryName || 'N/A',
                                        effectiveStartDate: rate.effectiveStartDate,
                                        effectiveEndDate: rate.effectiveEndDate
                                    }));
                            }
                            resolve(response);
                        },
                        (error) => {
                            console.error('Error loading loan interest rates:', error);
                            this.$swal({
                                title: 'Error!',
                                text: 'Failed to load loan interest rates',
                                icon: 'error',
                                confirmButtonColor: '#d33'
                            });
                            reject(error);
                        }
                    );
                });
            } catch (error) {
                console.error('Error in loadLoanInterestRates:', error);
            } finally {
                this.loadingLoanInterestRates = false;
            }
        }
    },
    async mounted() {
        this.loadToolsFromStorage();
        // ขั้นบันได Access Level (#914) — โหลดเบื้องหลังไม่รอ (store ไม่ reject / ไม่เด้ง popup; พลาด ⇒ ค่าในตัว T0–T3)
        ensureAccessLevelsLoaded();

        const { id, importData } = this.$route.query;
        const shouldShowInitialLoader = Boolean(id || importData);

        if (shouldShowInitialLoader) {
            this.dialog = true;
            await this.$nextTick();
        }

        const coreDataPromise = Promise.all([
            this.loadGPTModels(),
            this.GetAllDDModelManagement(),
            this.loadDocumentGroups(), // Load document groups for Flow integration
            this.loadProductGroupCollections(), // Load product group collections for Flow integration
            this.loadCommunityGraphs(), // Load community graphs for GraphRAG
            this.loadMetaCommunityGraphs(), // Load meta community graphs for Meta GraphRAG
        ]).catch((error) => {
            console.warn('Failed to load models:', error);
            this.showError('Failed to load AI models. Please refresh the page.');
        });

        const supplementalDataPromise = Promise.allSettled([
            this.loadAccountInfoDropdown(),
            this.loadLoanInterestRates()
        ]);

        await this.checkRouteMode();
        await coreDataPromise;
        await supplementalDataPromise;
        this.ensureAllDynamicKnowledgeMetaCommunityDefaults();

        // Initialize history after loading
        this.initializeHistory();

        // Check validation for all nodes after loading
        this.$nextTick(() => {
            this.checkAllNodesValidation();
        });

        this.$nextTick(() => {
            if (this.$refs.vueFlow) {
                const flow = this.$refs.vueFlow;
                // Set default unlocked state
                flow.nodesDraggable = true;
                flow.nodesConnectable = true;

                // flow.elementsSelectable = true;
                // flow.zoomOnScroll = true;
                // flow.zoomOnDoubleClick = true;
                // flow.panOnDrag = true;

                // Ensure interactive controls are enabled by default (unlocked state)
                this.interactiveControls = true;
                if (this.isViewMode) {
                    this.setViewOnlyMode();
                }
            }

            // Focus the flow container so it can receive keyboard events
            if (this.$refs.flowContainer) {
                this.$refs.flowContainer.focus();
            }
        });
        
        window.addEventListener('beforeunload', this.handleBeforeUnload);

        // Global Ctrl+F listener (capture phase to intercept before browser search)
        this._globalKeyHandler = (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.code === 'KeyF' || e.key?.toLowerCase() === 'f')) {
                // If focus is on an input/textarea inside the inspector, allow browser native search
                const activeEl = document.activeElement;
                const isInInspector = activeEl && activeEl.closest('.floating-inspector');
                const isInputField = activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.isContentEditable);
                if (isInInspector && isInputField) {
                    return; // Let browser handle Ctrl+F
                }
                e.preventDefault();
                e.stopPropagation();
                this.openNodeSearch();
            }
            if (e.key === 'Escape' && this.nodeSearchOpen) {
                e.preventDefault();
                this.closeNodeSearch();
            }
        };
        window.addEventListener('keydown', this._globalKeyHandler, true);

        // Native clipboard paste (Ctrl/Cmd+V over the canvas): primary cross-tab/window/browser
        // paste path in Chromium — reads clipboardData without a permission prompt.
        window.addEventListener('paste', this.handleClipboardPaste);
    },
    beforeRouteLeave(to, from, next) {
        if (!this.isViewMode && !this.isNavigatingAway) {
            // Use custom confirmation dialog instead of browser confirm
            this.showExitConfirmation(() => {
                this.isNavigatingAway = true; // Set flag to prevent handleBeforeUnload from triggering
                next();
            });
        } else {
            next();
        }
    },

    beforeUnmount() {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);

        if (this._globalKeyHandler) {
            window.removeEventListener('keydown', this._globalKeyHandler, true);
        }

        window.removeEventListener('paste', this.handleClipboardPaste);

        // Clear property update timer
        if (this.propertyUpdateTimer) {
            clearTimeout(this.propertyUpdateTimer);
        }
    }
}
</script>

<style scoped>
/* ══ Tax Planning Agent — Meta Document knowledge picker ═══════════════════════════════════════════════
   The inspector column is narrow, so the accordion is stripped of Vuetify's default card chrome: no
   elevation, no rounded corners, no min-height on the rows. What is left is a dense list that reads as one
   block instead of a stack of cards, which is what makes a two-level taxonomy scannable at this width. */
.tax-knowledge-panels {
    border-radius: 0;
}
.tax-knowledge-panels .v-expansion-panel {
    background: transparent;
}
.tax-knowledge-panels .v-expansion-panel::after {
    display: none;                 /* Vuetify's inter-panel divider — the header rows already read as rows */
}
.tax-knowledge-panels .v-expansion-panel-title {
    min-height: 40px;
    padding-top: 2px;
    padding-bottom: 2px;
}
.tax-knowledge-panels .v-expansion-panel-title--active {
    min-height: 40px;
}
.tax-knowledge-panel-body :deep(.v-expansion-panel-text__wrapper) {
    padding: 0 12px 8px 40px;      /* indented under the header's own checkbox, so the hierarchy is visible */
}
.tax-knowledge-cat :deep(.v-label) {
    font-size: 0.8125rem;
    opacity: 1;
}
.tax-knowledge-cat :deep(.v-selection-control) {
    min-height: 28px;
}

/* Strategy Info Popover */
.strategy-info-card {
    border: 1px solid rgba(0, 0, 0, 0.08);
}

.strategy-section {
    padding: 16px 18px;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.02);
}

.strategy-diagram-wrapper {
    background: white;
    border-radius: 8px;
    padding: 16px 14px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    overflow: hidden;
}

.strategy-diagram {
    width: 100%;
    height: auto;
    display: block;
}

.card2 {
    height: 100%;
    border-radius: 16px !important;
    margin-bottom: 2rem;
    padding-top: 1rem;
    position: relative;
}

.flow-container {
    height: 100%;
    width: 100%;
    border: 1px solid rgba(0, 0, 0, 0.1);
    overflow: hidden;
    border-radius: 12px;
    outline: none; /* Remove focus outline */
    position: relative; /* Required for overlay positioning */
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.flow-container.flow-debug-active {
    border-color: transparent;
}

.flow-container.flow-debug-active::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 3px solid #ff9900;
    border-radius: 12px;
    pointer-events: none;
    z-index: 9999;
    box-shadow: inset 0 0 6px rgba(255, 152, 0, 0.8);
}

.basic-flow {
    height: 100%;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    position: relative;
}

/* Header improvements */
:deep(.v-toolbar) {
    border-radius: 12px !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12) !important;
}

:deep(.vue-flow__edge) {
    stroke-width: 2;
}

:deep(.vue-flow__edge-path) {
    stroke: #1976d2;
}

:deep(.vue-flow__edge-text) {
    font-size: 12px;
    font-weight: 500;
    fill: #1976d2;
}

:deep(.vue-flow__background) {
    background: transparent !important;
}

:deep(.vue-flow__controls) {
    bottom: 5px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 3px;
    gap: 4px;
}

:deep(.vue-flow__controls-button) {
    background: transparent;
    border: none;
    border-radius: 8px;
    margin: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

:deep(.vue-flow__controls-button:hover) {
    background: rgba(59, 130, 246, 0.1);
    transform: scale(1.05);
}

:deep(.vue-flow__controls-button:active) {
    transform: scale(0.95);
}

/* Enhanced edge hover effects */
:deep(.edge-hovered .vue-flow__edge-path) {
    transition: all 0.3s ease;
    stroke: #ffc400 !important;
    filter: drop-shadow(0 0 5px #ffc400) drop-shadow(0 0 10px #ffc400);
}

/* Selected edge highlight */
:deep(.vue-flow__edge.selected .vue-flow__edge-path) {
    stroke: #f59e0b !important;
    stroke-width: 3.5px !important;
    transition: stroke 0.2s ease, stroke-width 0.2s ease;
}

/* Node selection effects */
/* :deep(.vue-flow__node.selected) {
        box-shadow: 0 0 0 2px #3b82f6, 0 0 0 4px rgba(59, 130, 246, 0.2);
    } */

/* Responsive design */
@media (max-width: 768px) {
    .flow-container {
        height: 400px;
        margin: 0.5rem;
    }

    :deep(.vue-flow__controls) {
        bottom: 10px;
        left: 10px;
    }
}

/* Loading state */
.flow-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    background: rgba(255, 255, 255, 0.9);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
}

.custom-controls-demo {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 10;
    background: white;
    padding: 10px 15px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    gap: 15px;
}

/* Model Selector Styles */
.model-option {
    padding: 8px 0;
}

.model-icon-small {
    width: 20px;
    height: 20px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Floating Inspector Panel */
.floating-inspector {
    position: absolute;
    top: 25px;
    right: 10px;
    width: 420px;
    max-height: calc(100vh - 80px);
    background: rgba(255, 255, 255, 0.5);
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 32px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    display: flex;
    flex-direction: column;
    transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    border: 1px solid rgba(0, 0, 0, 0.06);
    backdrop-filter: blur(10px);
    animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1);
    overflow: hidden;
}

.floating-inspector.inspector-expanded {
    position: absolute;
    top: 20px;
    left: 40%;
    right: 20px;
    bottom: 0px;
    width: auto;
    max-height: none;
    z-index: 1001;
    border-radius: 24px;
}

.inspector-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 28px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 20px 20px 0 0;
    position: relative;
    overflow: hidden;
}

.inspector-header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    pointer-events: none;
}

.inspector-title {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 1.1rem;
    z-index: 1;
    position: relative;
}

.inspector-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    z-index: 1;
    position: relative;
}

.inspector-controls .v-btn {
    background: rgba(255, 255, 255, 0.15) !important;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    transition: all 0.2s ease !important;
}

.inspector-controls .v-btn:hover {
    background: rgba(255, 255, 255, 0.25) !important;
    transform: scale(1.05);
}

.inspector-content {
    flex: 1;
    overflow-y: auto;
    padding: 0px;
    background-color: white;
}

/* Form Sections */
.section-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.add-btn {
    height: 36px !important;
    font-size: 0.8rem !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    text-transform: none !important;
    letter-spacing: 0.3px !important;
}

.header-chip {
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 1px !important;
    border-radius: 16px !important;
    padding: 12px 20px !important;
    min-height: 48px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    background: linear-gradient(135deg, var(--v-theme-primary) 0%, var(--v-theme-primary-darken-1) 100%) !important;
    border: 2px solid rgba(255, 255, 255, 0.2) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    position: relative;
    overflow: hidden;
    color: white !important;
}

.header-chip::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
}

.header-chip:hover::before {
    left: 100%;
}

.header-chip:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2) !important;
}

.header-chip .v-icon {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

/* Color-specific gradients for different node types */
.header-chip.bg-blue {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
}

.header-chip.bg-green {
    background: linear-gradient(135deg, #10b981 0%, #047857 100%) !important;
}

.header-chip.bg-teal {
    background: linear-gradient(135deg, #14b8a6 0%, #0f766e 100%) !important;
}

.header-chip.bg-orange {
    background: linear-gradient(135deg, #f97316 0%, #ea580c 100%) !important;
}

.header-chip.bg-purple {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
}

.header-chip.bg-red {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
}

.header-chip.bg-indigo {
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%) !important;
}

.header-chip.bg-amber {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%) !important;
}

.header-chip.bg-pink {
    background: linear-gradient(135deg, #ec4899 0%, #db2777 100%) !important;
}

.header-chip.bg-cyan {
    background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%) !important;
}

/* Node Type Badge */
.node-type-badge {
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
    padding: 8px 0;
}

.node-type-badge .v-chip {
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-radius: 12px;
}

/* Modern Input Styles */
.modern-input :deep(.v-field) {
    border-radius: 14px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
}

.modern-input :deep(.v-field--focused) {
    transform: translateY(-1px);
}

.modern-input :deep(.v-field__outline) {
    --v-field-border-opacity: 0.15;
}

.modern-input :deep(.v-field--focused .v-field__outline) {
    --v-field-border-opacity: 1;
    color: rgb(102, 126, 234) !important;
    --v-field-border-width: 2px;
}

.modern-input :deep(.v-label) {
    font-weight: 500;
}
/* Modern Switch */
.modern-switch :deep(.v-switch__track) {
    border-radius: 20px !important;
    background: #e5e7eb !important;
    transition: all 0.3s ease !important;
}

.modern-switch :deep(.v-switch--inset .v-switch__track) {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
}

.modern-switch :deep(.v-switch__thumb) {
    border-radius: 50% !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.modern-switch :deep(.v-switch__thumb):hover {
    transform: scale(1.1);
}

/* Slider Styles */
.slider-container {
    padding: 16px 0;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.slider-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 12px;
    display: block;
}

.modern-slider :deep(.v-slider-track__fill) {
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%) !important;
}

.modern-slider :deep(.v-slider-thumb__surface) {
    background: white !important;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
    border: 3px solid #667eea !important;
}

.modern-slider :deep(.v-slider-thumb__surface):hover {
    transform: scale(1.2);
}

/* Intent Cards */
.intents-section {
    padding-right: 4px;
}

.intent-card-inner {
    border-radius: 16px !important;
    border: 1px solid rgba(0, 0, 0, 0.06) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
    overflow: hidden;
}

.intent-card-inner:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08) !important;
    transform: translateY(-2px);
}

.intent-card-inner :deep(.v-card-text) {
    background: transparent !important;
}

/* Scrollbar Styles */
.inspector-content::-webkit-scrollbar,
.intents-section::-webkit-scrollbar {
    width: 6px;
}

.inspector-content::-webkit-scrollbar-track,
.intents-section::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
}

.inspector-content::-webkit-scrollbar-thumb,
.intents-section::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
}

.inspector-content::-webkit-scrollbar-thumb:hover,
.intents-section::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
}

/* Animation for smooth transitions */
@keyframes slideInRight {
    from {
        transform: translateX(100%) scale(0.9);
        opacity: 0;
    }

    to {
        transform: translateX(0) scale(1);
        opacity: 1;
    }
}

/* Add button animations */
.add-btn {
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.add-btn:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3) !important;
}

/* Improved scrollbar */
.inspector-content::-webkit-scrollbar,
.intents-section::-webkit-scrollbar {
    width: 8px;
}

.inspector-content::-webkit-scrollbar-track,
.intents-section::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.03);
    border-radius: 4px;
}

.inspector-content::-webkit-scrollbar-thumb,
.intents-section::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 4px;
    border: 2px solid transparent;
    background-clip: content-box;
}

.inspector-content::-webkit-scrollbar-thumb:hover,
.intents-section::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
    background-clip: content-box;
}

/* Mobile Responsive for Inspector */
@media (max-width: 768px) {
    .floating-inspector {
        width: calc(100vw - 20px);
        right: 10px;
        left: 10px;
        width: auto;
    }

    .floating-inspector.inspector-expanded {
        width: calc(100vw - 20px);
        left: 10px;
        right: 10px;
        top: 10px;
        bottom: 10px;
    }

    .inspector-header {
        padding: 20px;
    }

    .inspector-content {
        padding: 20px;
    }
}

/* Intent expansion panel styling */
.intent-expansion {
    border-radius: 8px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
}

.intent-expansion .v-expansion-panel {
    border-radius: 8px !important;
}

.intent-expansion .v-expansion-panel-title {
    padding: 16px 20px !important;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
    min-height: 56px !important;
}

.intent-expansion .v-expansion-panel-title:hover {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%) !important;
}

.intent-expansion .v-expansion-panel-text {
    background: #ffffff !important;
}

.intent-content {
    background: #ffffff;
    border-radius: 0 0 8px 8px;
}

/* Smart Intent card styling */
.smart-intent-card {
    background: #fcfaf8 !important;
    border: 1px solid rgba(37, 99, 235, 0.1) !important;
    border-radius: 12px !important;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.08) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    overflow: hidden !important;
}

.smart-intent-card:hover {
    border-color: rgba(37, 99, 235, 0.2) !important;
    box-shadow: 0 8px 24px rgba(37, 99, 235, 0.12) !important;
    transform: translateY(-2px) !important;
}

.smart-intent-card .v-card-title {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
    color: white !important;
    padding: 16px 20px !important;
    font-size: 1.1rem !important;
    font-weight: 600 !important;
    letter-spacing: 0.5px !important;
}

.smart-intent-card .v-card-text {
    padding: 20px !important;
    background: #ffffff !important;
}

/* Smart Intent expansion panel styling */
.smart-intent-expansion {
    padding: 0 !important;
    border-radius: 8px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
}

.smart-intent-expansion .v-expansion-panel {
    border-radius: 8px !important;
}

.smart-intent-expansion .v-expansion-panel-title {
    padding: 16px 20px !important;
    background: #e8f3ff !important;
    border-bottom: 1px solid #4e46e528 !important;
    min-height: 56px !important;
}

.smart-intent-expansion .v-expansion-panel-title:hover {
    background: #c7e1ff !important;
}

.smart-intent-expansion .v-expansion-panel-text {
    background: #ffffff !important;
}
.smart-intent-expansion :deep(.v-expansion-panel-text__wrapper) {
    padding: 0 5px 0 5px !important;
}

.smart-intent-content {
    background: #ffffff;
    border-radius: 0 0 8px 8px;
}

/* Fallback AI Config expansion panel styling */
.fallback-ai-config-expansion :deep(.v-expansion-panel-text .v-expansion-panel-text__wrapper) {
    padding: 0 5px 0 5px !important;
}
.fallback-ai-config-expansion {
    padding: 0 !important;
    border-radius: 8px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(255, 152, 0, 0.12) !important;
    margin-top: 8px !important;
}

.fallback-ai-config-expansion .v-expansion-panel {
    border-radius: 8px !important;
    border: 1px solid rgba(255, 152, 0, 0.2) !important;
}

.fallback-ai-config-title {
    padding: 16px 20px !important;
    background: linear-gradient(135deg, #fff3e0, #ffe0b2) !important;
    border-bottom: 1px solid rgba(255, 152, 0, 0.3) !important;
    min-height: 56px !important;
    transition: all 0.3s ease !important;
}

.fallback-ai-config-title:hover {
    background: linear-gradient(135deg, #ffe0b2, #ffcc80) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 152, 0, 0.15) !important;
}

.fallback-ai-config-expansion .v-expansion-panel-text {
    background: #ffffff !important;
    border-radius: 0 0 8px 8px !important;
}

.fallback-ai-config-expansion :deep(.v-expansion-panel-text__wrapper) {
    padding: 16px 20px !important;
}

/* AI Main Config expansion panel styling */
.ai-main-config-expansion {
    padding: 0 !important;
    border-radius: 8px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(33, 150, 243, 0.12) !important;
    margin-top: 8px !important;
}

.ai-main-config-expansion .v-expansion-panel {
    border-radius: 8px !important;
    border: 1px solid rgba(33, 150, 243, 0.2) !important;
}
.ai-main-config-expansion :deep(.v-expansion-panel .v-expansion-panel-text .v-expansion-panel-text__wrapper) {
    padding: 0 5px 0 5px !important;
}

.ai-config-title {
    padding: 16px 20px !important;
    background: linear-gradient(135deg, #e3f2fd, #bbdefb) !important;
    border-bottom: 1px solid rgba(33, 150, 243, 0.3) !important;
    min-height: 56px !important;
    transition: all 0.3s ease !important;
}

.ai-config-title:hover {
    background: linear-gradient(135deg, #bbdefb, #90caf9) !important;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.15) !important;
}

.ai-main-config-expansion .v-expansion-panel-text {
    background: #ffffff !important;
    border-radius: 0 0 8px 8px !important;
}

.ai-main-config-expansion :deep(.v-expansion-panel-text__wrapper) {
    padding: 16px 20px !important;
}

/* Switch output expansion panel styling */
.switch-output-expansion {
    border-radius: 8px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
}

.switch-output-expansion .v-expansion-panel {
    border-radius: 8px !important;
}

.switch-output-expansion .v-expansion-panel-title {
    padding: 16px 20px !important;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06) !important;
    min-height: 56px !important;
}

.switch-output-expansion .v-expansion-panel-title:hover {
    background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%) !important;
}

.switch-output-expansion .v-expansion-panel-text {
    background: #ffffff !important;
}

.switch-output-content {
    background: #ffffff;
    border-radius: 0 0 8px 8px;
}

.switch-outputs-section {
    max-height: 500px;
    padding-right: 4px;
}

.switch-output-card {
    border-radius: 16px !important;
    border: 1px solid rgba(0, 0, 0, 0.06) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
    overflow: hidden;
}

.switch-output-card:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08) !important;
    transform: translateY(-2px);
}

.switch-output-card .v-card-text {
    background: transparent !important;
}

/* Selection box styling */
:deep(.vue-flow__selection) {
    background: rgba(102, 126, 234, 0.08);
    border: 1.5px solid #667eea;
    border-radius: 4px;
}

:deep(.vue-flow__nodesselection-rect) {
    background: transparent;
    border: none;
    box-shadow: none;
}

/* Multi-Selection Indicator */
.floating-multi-selection {
    position: absolute;
    top: 30px;
    right: 20px;
    z-index: 1000;
    max-width: 280px;
}

.multi-selection-card {
    border-radius: 12px !important;
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.multi-selection-card .v-card-text {
    background: transparent;
}

/* Condition Configuration Styles */
.condition-builder {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.06);
}

.row-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.condition-builder .modern-input {
    background: white;
    border-radius: 8px;
}

.condition-builder .modern-input :deep(.v-field) {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.output-labels-section {
    background:none;
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(168, 85, 247, 0.1);
}

.output-labels-section .section-title {
    font-weight: 600;
}

.gap-2 {
    gap: 8px;
}

.d-flex.gap-2 {
    display: flex;
}

/* Condition type specific styling */
.condition-builder.text-contains {
    background: linear-gradient(135deg, #fef3e2 0%, #fed7aa 100%);
    border-color: rgba(251, 146, 60, 0.2);
}

.condition-builder.number-range {
    background: linear-gradient(135deg, #ecfccb 0%, #d9f99d 100%);
    border-color: rgba(132, 204, 22, 0.2);
}

.condition-builder.boolean-check {
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    border-color: rgba(59, 130, 246, 0.2);
}

.condition-builder.custom-expression {
    background: linear-gradient(135deg, #fdf2f8 0%, #fce7f3 100%);
    border-color: rgba(236, 72, 153, 0.2);
}

/* Delay Configuration Styles */
.delay-config-section {
    background: linear-gradient(135deg, #fef3f4 0%, #fde2e4 100%);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(244, 63, 94, 0.1);
}

.delay-config-section .modern-input {
    background: white;
    border-radius: 8px;
}

.delay-config-section .modern-input :deep(.v-field) {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.duration-preview {
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    border-radius: 12px;
    padding: 12px;
    border: 1px solid rgba(14, 165, 233, 0.1);
}

.duration-preview .v-alert {
    background: transparent !important;
}

/* Delay type specific styling */
.delay-config-section.fixed-delay {
    background: linear-gradient(135deg, #ecfccb 0%, #d9f99d 100%);
    border-color: rgba(132, 204, 22, 0.2);
}

.delay-config-section.random-delay {
    background: linear-gradient(135deg, #fef3e2 0%, #fed7aa 100%);
    border-color: rgba(251, 146, 60, 0.2);
}

.delay-config-section.variable-delay {
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    border-color: rgba(59, 130, 246, 0.2);
}

/* Switch Condition Builder Styles */
.condition-builder-section {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(0, 0, 0, 0.06);
}

.condition-section-title {
    color: #475569;
    font-weight: 600;
    font-size: 0.875rem;
    margin: 0;
}

.condition-mode-toggle {
    border-radius: 8px !important;
}

.condition-mode-toggle .v-btn {
    border-radius: 6px !important;
    text-transform: none !important;
    font-weight: 500 !important;
}

.simple-condition-builder,
.advanced-condition-builder {
    background: white;
    border-radius: 10px;
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.04);
    margin-top: 12px;
}

.condition-fields {
    background: linear-gradient(135deg, #fefcfb 0%, #fef7f0 100%);
    border-radius: 8px;
    padding: 12px;
    border: 1px solid rgba(251, 146, 60, 0.1);
    margin-top: 8px;
}

.condition-preview {
    background: rgba(59, 130, 246, 0.1) !important;
    color: #1e40af !important;
    padding: 4px 8px !important;
    border-radius: 4px !important;
    font-family: 'Fira Code', monospace !important;
    font-size: 0.75rem !important;
    word-break: break-all;
}

.condition-hints {
    background: linear-gradient(135deg, #fefcfb 0%, #fef7f0 100%);
    border-radius: 8px;
    padding: 8px;
    border: 1px solid rgba(251, 146, 60, 0.1);
}

.hint-chip {
    cursor: pointer !important;
    transition: all 0.2s ease !important;
    font-family: 'Fira Code', monospace !important;
    font-size: 0.75rem !important;
}

.hint-chip:hover {
    background: rgba(59, 130, 246, 0.1) !important;
    color: #1e40af !important;
}

.gap-2 {
    gap: 8px;
}

/* Switch output specific styling */
.switch-output-expansion .condition-builder-section {
    margin-bottom: 0;
}

.switch-output-expansion .condition-fields .modern-input {
    background: white;
    border-radius: 6px;
}

.switch-output-expansion .condition-fields .modern-input :deep(.v-field) {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* Toolbar Styles */
.flow-toolbar {
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1000;
    width: auto;
    max-width: 90%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;
    padding: 16px 24px;
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-radius: 16px;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 20px;
}

.flow-status-chip {
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 1px !important;
    border-radius: 12px !important;
    padding: 8px 16px !important;
    min-height: 40px !important;
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.2) !important;
}

.flow-info {
    display: flex;
    flex-direction: row;
    gap: 4px;
}

.confirmation-content {
    padding: 8px 0;
}

.flow-name-display {
    display: flex;
    align-items: center;
    padding: 20px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    margin-bottom: 20px;
}

.flow-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin-top: 4px;
}

.stats-summary {
    display: flex;
    justify-content: space-around;
    padding: 16px;
    background: white;
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    margin-bottom: 16px;
}

.stat-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
}

.stat-item .v-icon {
    opacity: 0.8;
}

.flow-name {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a202c;
    margin: 0;
}

.flow-status {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    color: #f59e0b;
    font-weight: 500;
}

.flow-status.saved {
    color: green;
}

.toolbar-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-top: 3px;
    padding: 0px 12px;
}

.btn-text {
    margin-left: 4px;
    font-size: 0.8rem;
}

.lock-btn {
    border-radius: 10px !important;
    text-transform: none !important;
    font-weight: 500 !important;
    transition: all 0.2s ease !important;
}

.more-actions-btn {
    border-radius: 50% !important;
    background: transparent !important;
}

/* Dialog Styles */
.save-dialog,
.load-dialog,
.load-model-dialog {
    border-radius: 20px !important;
    overflow: hidden;
}

.dialog-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 24px !important;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.dialog-header .header-content {
    flex: 1;
}

.dialog-header .text-h5 {
    font-size: 1.25rem !important;
}

.header-content {
    display: flex;
    align-items: center;
}

.dialog-content {
    padding: 28px !important;
    background: #fafafa;
}

.dialog-actions {
    padding: 20px 28px !important;
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

/* Header Actions */
.header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.load-model-btn {
    transition: all 0.2s ease;
}

.load-model-btn:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

/* Load Model Dialog Specific */
.load-model-content {
    min-height: 200px;
}

.model-selection-section {
    margin-bottom: 20px;
}

.model-list-item {
    padding: 12px 16px;
    border-radius: 8px;
    margin: 4px 0;
}

.model-list-item:hover {
    background-color: #f8fafc;
}

.config-preview-section {
    margin-top: 20px;
}

.config-preview-card {
    border: 1px solid #e5e7eb;
    border-radius: 8px;
}

.preview-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
}

.preview-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f3f4f6;
}

.preview-item:last-child {
    border-bottom: none;
}

.preview-label {
    font-weight: 500;
    color: #6b7280;
    font-size: 0.875rem;
}

.preview-value {
    font-weight: 600;
    color: #374151;
    font-size: 0.875rem;
    text-align: right;
}

/* Form Sections */
.form-section {
    background: white;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.section-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
}

/* Stats Grid */
.stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

.stat-card {
    border-radius: 8px !important;
}

.stat-number {
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1;
}

.stat-label {
    font-size: 0.75rem;
    opacity: 0.8;
    margin-top: 4px;
}

/* Flow List */
.search-section {
    padding: 0 4px;
}

.flow-list {
    max-height: 400px;
    overflow-y: auto;
    padding: 4px;
}

.flow-item {
    margin-bottom: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    border-radius: 12px !important;
}

.flow-item:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    transform: translateY(-1px);
}

.flow-item.selected {
    border-color: #667eea !important;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2) !important;
}

.flow-icon {
    background: rgba(102, 126, 234, 0.1);
    border-radius: 8px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.flow-title {
    font-size: 1rem;
    font-weight: 600;
    color: #1a202c;
}

.flow-meta {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 2px;
}

.flow-description {
    font-size: 0.85rem;
    color: #4b5563;
    line-height: 1.4;
    margin-top: 8px;
}

.flow-stats {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.flow-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.no-flows {
    text-align: center;
    padding: 40px 20px;
}

/* Tags Section */
.tags-section {
    margin-top: 8px;
}

/* Alert Styles */
.alert-title {
    font-weight: 600;
    margin-bottom: 4px;
}

/* Modern Input Overrides */
.modern-input :deep(.v-field) {
    border-radius: 10px !important;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
}

/* Responsive Design */
@media (max-width: 768px) {
    .flow-toolbar {
        flex-direction: column;
        gap: 12px;
        padding: 12px 16px;
    }

    .toolbar-left {
        width: 100%;
        justify-content: center;
    }

    .toolbar-actions {
        width: 100%;
        justify-content: center;
    }

    .stats-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .btn-text {
        display: none;
    }
}

@media (max-width: 480px) {
    .stats-grid {
        grid-template-columns: 1fr;
    }

    .flow-stats {
        flex-direction: row;
        gap: 8px;
    }
}

/* Scrollbar for flow list */
.flow-list::-webkit-scrollbar {
    width: 6px;
}

.flow-list::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
}

.flow-list::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.3);
    border-radius: 3px;
}

.flow-list::-webkit-scrollbar-thumb:hover {
    background: rgba(102, 126, 234, 0.5);
}

.floating-back-btn {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 2000;
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08) !important;
    border-radius: 12px !important;
    border: 1px solid rgba(255, 255, 255, 0.2);
    height: 44px !important;
    padding: 0 16px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    overflow: hidden;
}

.floating-back-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent);
    transition: left 0.5s ease;
}

.floating-back-btn:hover::before {
    left: 100%;
}

.floating-back-btn:hover {
    background: rgba(255, 255, 255, 1) !important;
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.floating-back-btn:active {
    transform: translateY(-1px) scale(0.98);
}

.floating-back-btn .v-icon {
    margin-right: 8px;
    transition: transform 0.2s ease;
}

.floating-back-btn:hover .v-icon {
    transform: translateX(-2px);
}

.back-text {
    font-size: 0.875rem;
    font-weight: 600;
    color: #667eea;
    letter-spacing: 0.3px;
    transition: color 0.2s ease;
}

.floating-back-btn:hover .back-text {
    color: #5a67d8;
}

.view-mode-btn {
    background-color: rgb(255, 224, 121);
    height: 40px;
    color: rgb(186, 109, 9);
    border-radius: 8px;
}

/* API Trigger Styles */
.api-section {
    background: rgba(248, 249, 250, 0.5);
    border-radius: 12px;
    padding: 20px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    margin-bottom: 16px;
}

.section-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
}

.section-title {
    font-weight: 600;
    font-size: 1rem;
    color: #1976d2;
    margin-bottom: 12px;
}

.header-row,
.validation-row {
    background: white;
    border-radius: 8px;
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    margin-bottom: 8px;
}

.headers-section,
.validation-section {
    background: rgba(248, 249, 250, 0.3);
    border-radius: 8px;
    padding: 12px;
    border: 1px solid rgba(0, 0, 0, 0.04);
}

.add-btn {
    margin-top: 8px;
    border: 1px dashed #1976d2 !important;
    background: rgba(25, 118, 210, 0.04) !important;
    color: #1976d2 !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
}

.add-btn:hover {
    background: rgba(25, 118, 210, 0.08) !important;
    border-color: #1976d2 !important;
}

/* Responsive Design for API Trigger */
@media (max-width: 768px) {
    .api-section {
        padding: 16px;
    }

    .header-row,
    .validation-row {
        padding: 8px;
    }

    .section-title {
        font-size: 0.9rem;
    }

    .headers-section,
    .validation-section {
        padding: 8px;
    }
}

/* Enhanced JSON Editor Container */
.json-editor-container {
    background: white;
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

/* Variables Panel Header */
.json-variables-header {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding: 12px 16px;
}

.variables-header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.variables-title {
    display: flex;
    align-items: center;
    font-weight: 600;
    color: #374151;
    flex: 1;
    justify-content: space-between;
}

.category-filter-btn {
    height: 28px !important;
    min-width: auto !important;
    padding: 0 8px !important;
    border-radius: 6px !important;
    font-size: 0.75rem !important;
    color: #6b7280 !important;
    transition: all 0.2s ease !important;
}

.category-filter-btn.filter-active {
    background: rgba(79, 70, 229, 0.1) !important;
    color: #4f46e5 !important;
}

.category-filter-btn:hover {
    background: rgba(0, 0, 0, 0.04) !important;
}

.expand-btn {
    height: 28px !important;
    width: 28px !important;
    border-radius: 6px !important;
    color: #6b7280 !important;
}

.expand-btn:hover {
    background: rgba(0, 0, 0, 0.04) !important;
    color: #374151 !important;
}

/* Category Menu */
.category-menu {
    border-radius: 8px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
    border: 1px solid rgba(0, 0, 0, 0.06) !important;
    min-width: 120px;
}

.category-menu .v-list-item {
    min-height: 36px !important;
    padding: 0 12px !important;
    font-size: 0.875rem !important;
}

.category-menu .v-list-item:hover {
    background: rgba(79, 70, 229, 0.04) !important;
}

.category-menu .v-list-item--active {
    background: rgba(79, 70, 229, 0.1) !important;
    color: #4f46e5 !important;
}

/* Variables Content */
.json-variables-content {
    padding: 16px;
    background: #fafbfc;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

/* Compact Grid Layout */
.variables-quick-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(210px, 210px));
    gap: 8px;
    max-height: 160px;
    overflow-y: auto;
    padding: 2px;
}

.variable-chip-compact {
    height: 32px !important;
    padding: 0 8px !important;
    border-radius: 8px !important;
    cursor: grab !important;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    font-size: 0.75rem !important;
    border: 1px solid rgba(0, 0, 0, 0.06) !important;
    user-select: none;
}

.variable-chip-compact:hover {
    transform: translateY(-1px) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    border-color: rgba(0, 0, 0, 0.1) !important;
}

.variable-chip-compact:active {
    cursor: grabbing !important;
    transform: scale(0.98) !important;
}

.variable-label {
    flex: 1;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0 4px;
}

.drag-handle {
    opacity: 0.4;
    transition: opacity 0.2s ease;
    position: absolute;
    right: 12px;
}

.variable-chip-compact:hover .drag-handle {
    opacity: 0.8;
}

.variable-key {
    font-size: 0.75rem !important;
    color: #6b7280 !important;
    font-family: 'Fira Code', monospace !important;
}

.variable-actions {
    display: flex;
    align-items: center;
    gap: 4px;
}

.insert-btn {
    opacity: 0;
    transition: opacity 0.2s ease;
}

.variable-list-item:hover .insert-btn {
    opacity: 1;
}

/* JSON Textarea Container */
.json-textarea-container {
    position: relative;
}

.modern-json-editor {
    border-radius: 0 !important;
    background: white !important;
}

.modern-json-editor :deep(.v-field) {
    border-radius: 0 !important;
    font-family: 'Fira Code', 'Fira Mono', Consolas, Menlo, Courier, monospace !important;
    font-size: 13px !important;
    line-height: 1.5 !important;
    border: none !important;
    box-shadow: none !important;
}

.modern-json-editor :deep(.v-field__outline) {
    display: none !important;
}

.modern-json-editor :deep(textarea) {
    border: 2px solid transparent !important;
    transition: all 0.2s ease !important;
    background: white !important;
    min-height: 120px !important;
}

.modern-json-editor :deep(textarea:focus) {
    box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1) !important;
}

.modern-json-editor :deep(textarea.drag-over) {
    border-color: #10b981 !important;
    background-color: rgba(16, 185, 129, 0.05) !important;
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1) !important;
}

.modern-json-editor :deep(textarea:hover) {
    cursor: text;
}

.modern-json-editor :deep(textarea.drag-over) {
    cursor: copy !important;
}

/* Insert Helper */
.insert-helper {
    position: absolute;
    bottom: 8px;
    right: 12px;
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    pointer-events: none;
}

.helper-text {
    font-size: 0.7rem;
    color: #6b7280;
    font-weight: 500;
}

/* Time Field Container */
.time-field-container {
    max-width: 300px;
}

.time-unit {
    font-size: 0.875rem;
    font-weight: 500;
}

.info-icon {
    opacity: 0.7;
    transition: opacity 0.2s ease;
}

.info-icon:hover {
    opacity: 1;
}

/* Animation for smooth transitions */
.v-enter-active,
.v-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

/* Scrollbar Styling */
.variables-quick-grid::-webkit-scrollbar {
    width: 4px;
}

.variables-quick-grid::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.04);
    border-radius: 2px;
}

.variables-quick-grid::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
}

.variables-quick-grid::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
}

/* New CSS classes to replace inline styles */
.flow-main-container {
    height: 100%;
    width: 100%;
    position: relative;
}

.flow-container-fluid {
    height: 100%;
    width: 100%;
    padding: 10px;
}

.card2 {
    margin-top: 1rem;
    padding: 0;
}

.flow-name-input {
    width: 250px;
    min-width: 180px;
}

.flow-actions {
    display: flex;
    gap: 4px;
    align-items: center;
    flex-shrink: 0;
}

/* Undo/Redo Container */
.undo-redo-container {
    position: absolute;
    top: 21px;
    left: 130px;
    display: flex;
    align-items: center;
    gap: 4px;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 10px;
    padding: 4px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    z-index: 1201;
    backdrop-filter: blur(10px);
}

.undo-btn, .redo-btn {
    height: 32px !important;
    width: 32px !important;
    min-width: 32px !important;
    border-radius: 8px !important;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.undo-btn:hover:not(:disabled), .redo-btn:hover:not(:disabled) {
    background: rgba(25, 118, 210, 0.08) !important;
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.15) !important;
}

.undo-btn:disabled, .redo-btn:disabled {
    opacity: 0.4 !important;
    cursor: not-allowed !important;
}

.undo-btn:active:not(:disabled), .redo-btn:active:not(:disabled) {
    transform: scale(0.95);
}

.undo-btn .v-icon, .redo-btn .v-icon {
    transition: all 0.2s ease;
}

.undo-btn:hover:not(:disabled) .v-icon, .redo-btn:hover:not(:disabled) .v-icon {
    color: #1976d2 !important;
}

.menu-list {
    border-radius: 15px;
    box-shadow: 3px 4px 10px rgba(0, 0, 0, 0.2);
}

.menu-divider {
    border: none;
    border-bottom: 1px solid #d5d5d5;
    margin: 6px 8px;
}

.save-btn {
    height: 40px;
    width: 100px;
    border-radius: 12px;
}

.node-panel {
    max-height: 100%;
}

/* Animation for smooth transitions */
.v-enter-active,
.v-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

/* Priority Number Styling */
.priority-number {
    font-size: 0.8rem;
    color: #7018ff;
    font-weight: 500;
    background: rgba(219, 176, 255, 0.349);
    padding: 2px 6px;
    border-radius: 4px;
    min-width: 20px;
    text-align: center;
}

/* Success Dialog Styles */
.success-dialog {
    border-radius: 20px !important;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
}

.success-dialog-header {
    background: linear-gradient(135deg, #10b943 0%, #047834 100%);
    color: white;
    padding: 24px !important;
    text-align: center;
    border-bottom: none;
}

.success-header-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.success-icon {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    padding: 12px;
    animation: successPulse 2s infinite;
}

@keyframes successPulse {
    0% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
    }

    50% {
        transform: scale(1.05);
        box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
    }

    100% {
        transform: scale(1);
        box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
    }
}

.success-title {
    font-size: 1.5rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.success-dialog-content {
    padding: 32px 24px !important;
    text-align: center;
    background: #fafafa;
}

.success-message {
    font-size: 1.1rem;
    font-weight: 600;
    color: #047857;
    margin-bottom: 24px;
    line-height: 1.5;
}

.success-details {
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: white;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid rgba(16, 185, 129, 0.1);
}

.success-detail-item {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    color: #374151;
    font-weight: 500;
}

.success-detail-item .v-icon {
    color: #10b981;
}

.success-dialog-actions {
    padding: 20px 24px !important;
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.success-ok-btn {
    height: 44px !important;
    padding: 0 24px !important;
    border-radius: 12px !important;
    font-weight: 600 !important;
    text-transform: none !important;
    background: linear-gradient(135deg, #10b943 0%, #047834 100%);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.success-ok-btn:hover {
    transform: translateY(-2px) !important;
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4) !important;
}

.success-ok-btn:active {
    transform: translateY(0) !important;
}

/* Animation for dialog entrance */
.v-dialog-transition-enter-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-dialog-transition-enter-from {
    transform: scale(0.8);
    opacity: 0;
}

/* Mobile responsive */
@media (max-width: 480px) {
    .success-dialog {
        margin: 16px;
    }

    .success-dialog-header {
        padding: 20px !important;
    }

    .success-dialog-content {
        padding: 24px 20px !important;
    }

    .success-title {
        font-size: 1.25rem;
    }

    .success-message {
        font-size: 1rem;
    }

    .success-details {
        padding: 12px;
    }

    .success-detail-item {
        font-size: 0.8rem;
    }
}

/* Confirmation Dialog Styles */
.confirmation-dialog {
    border-radius: 16px !important;
    overflow: hidden;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2) !important;
}

.confirmation-dialog .dialog-header {
    background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
    padding: 24px !important;
    border-bottom: 1px solid rgba(255, 183, 77, 0.2);
}

.confirmation-dialog .header-content {
    flex: 1;
}

.confirmation-dialog .text-h5 {
    color: #e65100;
    font-weight: 600;
}

.confirmation-dialog .dialog-content {
    padding: 24px !important;
    background: #fafafa;
}

.confirmation-content {
    text-align: center;
}

.confirmation-content .text-body-1 {
    color: #424242;
    font-size: 16px;
    line-height: 1.5;
    margin-bottom: 16px;
}

.confirmation-dialog .dialog-actions {
    background: white;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    padding: 20px 24px !important;
}

.confirmation-dialog .v-btn {
    border-radius: 8px;
    text-transform: none;
    font-weight: 500;
    padding: 0 20px;
    height: 40px;
}

.confirmation-dialog .v-btn--outlined {
    border-width: 2px;
}

.confirmation-dialog .v-btn--elevated {
    box-shadow: 0 4px 12px rgba(255, 152, 0, 0.3) !important;
}

.confirmation-dialog .v-btn--elevated:hover {
    box-shadow: 0 6px 16px rgba(255, 152, 0, 0.4) !important;
    transform: translateY(-1px);
}

.confirmation-dialog .v-btn--outlined:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2);
}

/* Alert styling in confirmation */
.confirmation-dialog .v-alert {
    border-radius: 12px;
    background: rgba(255, 193, 7, 0.1) !important;
    border: 1px solid rgba(255, 193, 7, 0.3);
}

.confirmation-dialog .v-alert .v-alert__content {
    color: #e65100;
    font-size: 14px;
}

/* Animation for dialog entrance */
.v-dialog-transition-enter-active .confirmation-dialog {
    animation: confirmationSlideIn 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

@keyframes confirmationSlideIn {
    from {
        opacity: 0;
        transform: translateY(-30px) scale(0.95);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

/* Mobile responsive for confirmation dialog */
@media (max-width: 480px) {
    .confirmation-dialog .dialog-header {
        padding: 20px !important;
    }
    
    .confirmation-dialog .dialog-content {
        padding: 20px !important;
    }
    
    .confirmation-dialog .dialog-actions {
        padding: 16px !important;
        flex-direction: column-reverse;
        gap: 12px;
    }
    
    .confirmation-dialog .v-btn {
        width: 100%;
        margin: 0 !important;
    }
    
    .confirmation-dialog .v-spacer {
        display: none;
    }
}

/* ── Node Search Highlight ─────────────────────────────────────── */
:deep(.vue-flow__node.search-highlight) {
    /* outline: 5px solid #f59e0b;
    outline-offset: 0px; */
    border-radius: 12px;
    background-color: rgba(245, 158, 11, 0.2);
    transition: outline 0.15s ease, background-color 0.15s ease;
}

:deep(.vue-flow__node.search-highlight-current) {
    /* outline: 3px solid #3b82f6!important; */
    /* outline-offset: 4px; */
    border-radius: 12px;
    background-color: rgba(59, 130, 246, 0.10);
    box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.20);
    animation: searchNodePulse 1.2s ease-in-out 1;
}

@keyframes searchNodePulse {
    0%   { box-shadow: 0 0 0 0px rgba(59, 130, 246, 0.45); }
    50%  { box-shadow: 0 0 0 10px rgba(59, 130, 246, 0.15); }
    100% { box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.20); }
}

/* Node Search Panel */
.node-search-panel {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 1000;
    background: #1e1e2e;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35), 0 2px 8px rgba(0, 0, 0, 0.2);
    overflow: hidden;
}

.node-search-inner {
    display: flex;
    align-items: center;
    padding: 5px 6px;
    gap: 2px;
    min-width: 280px;
}

.node-search-icon-left {
    color: rgba(255, 255, 255, 0.45);
    flex-shrink: 0;
    margin-right: 2px;
}

.node-search-input {
    background: transparent;
    border: none;
    outline: none;
    color: #e2e8f0;
    font-size: 13px;
    flex: 1;
    min-width: 0;
    padding: 2px 4px;
    font-family: inherit;
    caret-color: #60a5fa;
}

.node-search-input::placeholder {
    color: rgba(255, 255, 255, 0.30);
}

.node-search-count {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.50);
    white-space: nowrap;
    padding: 0 4px;
    flex-shrink: 0;
}

.node-search-count.no-results {
    color: #f87171;
}

.node-search-nav-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.55);
    border-radius: 4px;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s;
}

.node-search-nav-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.10);
    color: #e2e8f0;
}

.node-search-nav-btn:disabled {
    opacity: 0.3;
    cursor: default;
}

.node-search-close-btn {
    margin-left: 2px;
}

/* Transition */
.node-search-slide-enter-active,
.node-search-slide-leave-active {
    transition: opacity 0.15s ease, transform 0.15s ease;
}

.node-search-slide-enter-from,
.node-search-slide-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}

/* Invalid connection state */
:deep(.vue-flow__handle.invalid-connection) {
    background-color: #ef4444 !important;
    border-color: #dc2626 !important;
    cursor: not-allowed !important;
}

/* Valid connection state */
:deep(.vue-flow__handle.valid-connection) {
    background-color: #10b981 !important;
    border-color: #059669 !important;
    box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3) !important;
}

/* Connection preview line */
:deep(.vue-flow__connection-line) {
    stroke: #6366f1;
    stroke-width: 2;
    stroke-dasharray: 5, 5;
    animation: dash 1s linear infinite;
}

@keyframes dash {
    to {
        stroke-dashoffset: -10;
    }
}

/* Invalid connection preview */
:deep(.vue-flow__connection-line.invalid) {
    stroke: #ef4444;
    stroke-dasharray: 3, 3;
}

/* Success animation for valid connections */
@keyframes connectionSuccess {
    0% {
        stroke-width: 2;
    }

    50% {
        stroke-width: 4;
    }

    100% {
        stroke-width: 2;
    }
}

:deep(.vue-flow__edge.connection-success .vue-flow__edge-path) {
    animation: connectionSuccess 0.5s ease-in-out;
}

/* Selected Document Data Styles */
.selected-document-data {
    background: rgba(25, 118, 210, 0.04);
    border-radius: 12px;
    padding: 16px;
    border: 1px solid rgba(25, 118, 210, 0.1);
}

.selected-document-data-chip {
    margin: 2px !important;
    font-weight: 500 !important;
    display: inline-flex !important;
    align-items: center !important;
    max-width: 250px !important;
}

.selected-document-data-chip .chip-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
}

.selected-document-data-chip :deep(.v-chip__content) {
    display: flex !important;
    align-items: center !important;
    max-width: 100% !important;
    overflow: hidden !important;
}

.selected-document-data-chip :deep(.v-chip__close) {
    flex-shrink: 0 !important;
    margin-left: 4px !important;
    order: 2 !important;
}

.selected-document-data-chip :deep(.v-chip__content > span) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
}

.custom-divider {
    border: none;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    margin: 0px 0px 20px 0px;
    width: 100%;
}

/* Enhanced Configuration Panel Styles */
.config-header {
    background: linear-gradient(135deg, #f8f9ff 0%, #e3f2fd 100%);
    border-radius: 16px;
    padding: 24px;
    border: 1px solid rgba(25, 118, 210, 0.1);
    margin-bottom: 24px;
}

.config-panels {
    border: none !important;
    gap: 16px;
}

.config-panels :deep(.v-expansion-panel::after) {
    display: none !important;
}

.panel-item {
    border-radius: 16px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06) !important;
    border: 1px solid rgba(0, 0, 0, 0.08) !important;
    transition: all 0.3s ease;
}


.panel-title-custom {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding: 0 24px !important;
}

.panel-icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #1976D2, #1565C0);
    box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
}

.panel-title {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.2;
}

.panel-subtitle {
    font-size: 13px;
    color: #666;
    margin-top: 2px;
    line-height: 1.3;
}

.panel-content-wrapper {
    background: #ffffff;
}

.panel-content {
    background: #ffffff;
}

.config-section {
    position: relative;
}

.section-label {
    font-size: 14px;
    font-weight: 600;
    color: #1976D2;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 16px;
    position: relative;
    padding-left: 12px;
}

.section-label::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 16px;
    background: linear-gradient(135deg, #1976D2, #1565C0);
    border-radius: 2px;
}

.model-config-header {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 8px;
}

.model-config-header .section-label {
    flex: 1 1 auto;
}

.model-config-edit-btn {
    height: 28px !important;
    min-width: 60px !important;
    width: auto !important;
    padding: 0 12px !important;
    border-radius: 6px !important;
    flex: 0 0 auto;
    text-transform: none !important;
    font-size: 13px !important;
    font-weight: 600 !important;
}

.sql-extraction-edit-btn {
    height: 36px !important;
    min-width: 72px !important;
    width: auto !important;
    padding: 0 16px !important;
    border-radius: 8px !important;
    flex: 0 0 auto;
    text-transform: none !important;
    font-size: 15px !important;
    font-weight: 600 !important;
}


.model-config-edit-btn .v-icon,
.sql-extraction-edit-btn .v-icon {
    display: inline-flex !important;
    margin-right: 6px;
    width: auto !important;
}

.model-config-edit-btn :deep(.v-btn__content),
.sql-extraction-edit-btn :deep(.v-btn__content) {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.model-config-edit-btn :deep(.v-btn__content)::after,
.sql-extraction-edit-btn :deep(.v-btn__content)::after {
    content: 'Edit';
}

.model-config-edit-btn::after,
.sql-extraction-edit-btn::after {
    content: none !important;
}

.config-section > .section-label.mb-3:has(+ .model-config-edit-btn) {
    display: block;
    padding-right: 96px;
}

.config-section > .section-label.mb-3 + .model-config-edit-btn {
    position: absolute;
    top: -3px;
    right: 0;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    margin-left: 0;
    margin-bottom: 8px !important;
}

.modern-input.mb-3:has(+ .sql-extraction-edit-btn) {
    display: block !important;
    width: calc(100% - 88px) !important;
    max-width: calc(100% - 88px) !important;
    margin-right: 8px !important;
    margin-bottom: 12px !important;
}

.modern-input.mb-3 + .sql-extraction-edit-btn {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    float: right;
    margin-top: -52px !important;
    margin-bottom: 16px !important;
}

.sql-extraction-model-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
}

.sql-extraction-model-row .modern-input {
    flex: 1 1 auto;
    min-width: 0;
    margin-bottom: 0 !important;
}

.sql-extraction-model-row .sql-extraction-edit-btn {
    flex: 0 0 auto;
    height: 40px !important;
    min-height: 40px !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    float: none;
}

.sql-extraction-edit-btn {
    margin-top: 2px;
}

/* #924 — Min similarity stepper: − · box · +, sized to sit beside the slider rather than tower over it. */
.mf-sim-stepper {
    gap: 4px;
}

.mf-sim-btn {
    width: 22px !important;
    height: 22px !important;
    min-width: 22px !important;
}

.mf-sim-btn :deep(.v-icon) {
    font-size: 14px;
}

.mf-sim-input {
    width: 54px;
    flex: 0 0 54px;
}

.mf-sim-input :deep(.v-field) {
    --v-input-control-height: 26px;
    font-size: 13px;
}

.mf-sim-input :deep(.v-field__input) {
    min-height: 26px;
    padding: 2px 4px;
    text-align: center;
    font-variant-numeric: tabular-nums;
}

.mf-view-code {
    background: rgba(0, 0, 0, .06);
    padding: 1px 6px;
    border-radius: 4px;
    font-size: .85em;
}

.mf-view-link {
    color: rgb(var(--v-theme-primary));
    font-weight: 600;
}

.config-input {
    margin-bottom: 0px;
}

.config-input :deep(.v-field) {
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
}

.config-input :deep(.v-field:hover) {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.config-input :deep(.v-field--focused) {
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

/* Enhanced Option Cards */
.option-cards-row {
    gap: 16px;
}

.option-card {
    border-radius: 16px !important;
    transition: all 0.3s ease;
    border: 2px solid rgba(0, 0, 0, 0.08) !important;
    background: linear-gradient(135deg, #ffffff 0%, #fafafa 100%);
}

.option-card:hover {
    border-color: rgba(25, 118, 210, 0.3) !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
    transform: translateY(-2px);
}

.option-card.option-active {
    border-color: rgba(25, 118, 210, 0.6) !important;
    background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
    box-shadow: 0 4px 16px rgba(25, 118, 210, 0.2) !important;
}

.option-title {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.2;
}

.option-description {
    font-size: 13px;
    color: #666;
    margin-top: 4px;
    line-height: 1.4;
}

/* Memory Toggle Card */
.memory-toggle-card {
    border-radius: 12px !important;
    transition: all 0.3s ease;
}

.memory-toggle-card.toggle-active {
    border-color: rgba(76, 175, 80, 0.5) !important;
    background: linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%);
}

.memory-label {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
}

.memory-description {
    font-size: 12px;
    color: #666;
    margin-top: 2px;
}

/* Switch Option Compact Styles */
.switch-option-compact {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    background: #ffffff;
    transition: all 0.3s ease;
}

.switch-content-row {
    min-width: 0;
    flex: 1 1 auto;
}

.switch-copy-block {
    min-width: 0;
    flex: 1 1 auto;
}

.switch-option-compact :deep(.v-switch) {
    flex: 0 0 auto;
    margin-left: 12px;
}

.switch-option-compact:hover {
    border-color: rgba(25, 118, 210, 0.3);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.switch-active-bg {
    background: linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.02) 100%) !important;
    border-color: rgba(25, 118, 210, 0.3) !important;
}

/* Code block styles for prompt replacement flags */
.code-block-wrapper {
    margin-bottom: 8px;
}

.code-block {
    background: #f5f5f5;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 0px 8px;
    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
}

.code-text {
    font-size: 13px;
    color: #7c3aed;
    font-weight: 500;
    background: transparent;
    user-select: all;
}

.copy-btn {
    opacity: 0.6;
    transition: opacity 0.2s ease;
}

.copy-btn:hover {
    opacity: 1;
}

/* Compact inline switch styles */
.switch-option-inline {
    display: inline-block;
}

.switch-active-bg-compact {
    background: linear-gradient(135deg, rgba(94, 53, 177, 0.08) 0%, rgba(94, 53, 177, 0.04) 100%) !important;
    border: 1px solid rgba(94, 53, 177, 0.2) !important;
    border-radius: 8px !important;
}

.switch-title-compact {
    font-size: 12px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.1;
    white-space: nowrap;
}

.switch-description-compact {
    font-size: 10px;
    color: #666;
    line-height: 1.2;
    white-space: nowrap;
}

/* Action buttons container responsive layout */
.action-buttons-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.base-prompt-toggle-section {
    width: 100%;
}

.base-prompt-toggle-section .v-card {
    border: 1px solid rgba(58, 183, 73, 0.2);
    transition: all 0.2s ease;
}

.base-prompt-toggle-section .v-card:hover {
    border-color: rgba(35, 140, 49, 0.4);
    box-shadow: 0 2px 8px rgba(58, 183, 77, 0.1);
}

.load-model-button-wrapper {
    display: flex;
    justify-content: flex-end;
}

/* Responsive adjustments for action buttons */
@media (max-width: 600px) {
    .load-model-button-wrapper {
        justify-content: stretch;
    }
    
    .load-model-btn {
        width: 100%;
    }
    
    .switch-title-compact {
        font-size: 11px;
    }
    
    .switch-description-compact {
        font-size: 9px;
    }
    
    .toggle-text-container {
        min-width: 120px;
    }
}

@media (max-width: 400px) {
    .base-prompt-toggle-wrapper .switch-option-inline > div {
        flex-direction: column;
        align-items: flex-start;
        padding: 8px 12px;
    }
    
    .toggle-text-container {
        margin: 4px 0;
        min-width: auto;
    }
    
    .switch-title-compact,
    .switch-description-compact {
        white-space: normal;
    }
}

.switch-title {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
    line-height: 1.2;
}

.switch-description {
    font-size: 11.5px;
    color: #666;
    margin-top: 3px;
    line-height: 1.3;
    overflow-wrap: anywhere;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .config-header {
        padding: 16px;
        margin-bottom: 16px;
    }

    .panel-content {
        padding: 16px !important;
    }

    .panel-title-custom {
        padding: 0 16px !important;
    }

    .option-cards-row {
        gap: 12px;
    }

    /* Smart Intent responsive styles */
    .smart-intent-card .v-card-title {
        padding: 12px 16px !important;
        font-size: 1rem !important;
    }

    .smart-intent-card .v-card-text {
        padding: 16px !important;
    }

    .smart-intent-expansion .v-expansion-panel-title {
        padding: 12px 16px !important;
        min-height: 48px !important;
    }
}

/* Line Configuration Specific Styles */
.line-option-card {
    border-radius: 12px !important;
    transition: all 0.3s ease;
}

.line-option-card.option-active {
    border-color: rgba(76, 175, 80, 0.5) !important;
    background: linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%);
}

/* System Prompt Specific Styles */
.prompt-section {
    position: relative;
}

.prompt-card {
    border-radius: 16px !important;
    overflow: hidden;
    border: 2px solid rgba(0, 0, 0, 0.08) !important;
    transition: all 0.3s ease;
}

.prompt-card:hover {
    border-color: rgba(25, 118, 210, 0.3) !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12) !important;
}

.prompt-textarea :deep(.v-field) {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
}

.prompt-textarea :deep(.v-field__field) {
    padding: 0px !important;
}

.prompt-textarea :deep(.v-field__input) {
    font-family: 'Roboto Mono', monospace !important;
    font-size: 14px !important;
    line-height: 1.6 !important;
}

.tips-card {
    border-radius: 12px !important;
    border: 1px solid rgba(96, 125, 139, 0.2) !important;
}

.tips-card ul {
    margin: 0;
    padding-left: 16px;
}

.tips-card li {
    margin-bottom: 4px;
}

.tips-card li:last-child {
    margin-bottom: 0;
}

/* Prompt Template Styles */
.prompt-template-container {
    padding: 0.5rem 0;
}

.prompt-editor-card {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    transition: all 0.2s ease;
}

.prompt-editor-card:hover {
    border-color: rgba(25, 118, 210, 0.5);
    box-shadow: 0 1px 3px rgba(227, 6, 6, 0.05);
}

.prompt-textarea {
    font-family: 'Roboto Mono', monospace;
    line-height: 1.5;
    border: none !important;
    box-shadow: none !important;
    padding: 0px 5px !important;
    font-size: 14px;
    min-height: 300px !important;
}

.prompt-editor-card {
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    box-shadow: none !important;
    transition: all 0.2s ease;
    overflow: hidden;
}

.prompt-editor-card:hover {
    border-color: rgba(25, 118, 210, 0.5) !important;
}

.prompt-templates-card {
    border-radius: 8px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    transition: all 0.3s ease;
}

.template-list-item {
    border-radius: 4px;
    margin-bottom: 2px;
    transition: all 0.2s ease;
}

.template-list-item:hover {
    background-color: rgba(25, 118, 210, 0.04);
}

/* Selection Info Styles */
.selection-info {
    position: absolute;
    bottom: 20px;
    left: 160px;
    z-index: 1000;
    display: flex;
    align-items: flex-start;
    pointer-events: none;
}

.selection-info .v-chip {
    pointer-events: auto;
    margin: 0;
}

.prompt-template-list {
    max-height: 500px;
    overflow-y: auto;
}

.template-list-all {
    max-height: 400px;
    overflow-y: auto;
}

/* Knowledge Sources Styles */
.knowledge-sources-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

/* SharePoint Configuration Subsection Styles */
.subsection {
    background: rgba(248, 250, 252, 0.6);
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
    transition: all 0.2s ease;
}

/* .subsection:hover {
    background: rgba(248, 250, 252, 0.8);
    border-color: rgba(25, 118, 210, 0.2);
} */

.subsection-header {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding-bottom: 8px;
}

.subsection-title {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a1a;
    letter-spacing: 0.025em;
}

.subsection .modern-input:last-child {
    margin-bottom: 0 !important;
}

/* Color coding for different subsections */
.subsection:nth-child(2) {
    background: rgba(25, 118, 210, 0.02);
    border-color: rgba(25, 118, 210, 0.1);
}

.subsection:nth-child(3) {
    background: rgba(76, 175, 80, 0.02);
    border-color: rgba(76, 175, 80, 0.1);
}

.subsection:nth-child(4) {
    background: rgba(33, 150, 243, 0.02);
    border-color: rgba(33, 150, 243, 0.1);
}

.knowledge-source-card {
    background-color: white;
    transition: all 0.2s ease;
}

.knowledge-source-card .border {
    border: 1px solid #e0e0e0 !important;
    transition: border-color 0.2s ease, background-color 0.2s ease;
}

.knowledge-source-card .border:hover {
    border-color: #1976d2 !important;
}

.knowledge-source-frame {
    display: flex; 
    align-items: center; 
    justify-content: space-between;
    flex-direction: column;
    padding: 5px;
}
.knowledge-source-title {
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 5px;
}
.knowledge-source-config {
    width: 100%;
    padding: 5px;
}

/* Knowledge Connection Card Styles */
.knowledge-connection-card {
    border-radius: 12px !important;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    transition: all 0.2s ease;
    background: white;
}

.knowledge-connection-card:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
}

.knowledge-card-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.knowledge-connection-card .modern-input :deep(.v-field) {
    background: #fafafa;
    border-radius: 8px;
}

.knowledge-connection-card .modern-input :deep(.v-field:hover) {
    background: #f5f5f5;
}

.knowledge-connection-card .modern-input :deep(.v-field--focused) {
    background: white;
}

/* ========================================
   FLOW TOOL EDITOR STYLES
   ======================================== */

/* Hexagon icon for inspector headers */
.hexagon-icon-small {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
}

.hexagon-icon-small.bg-success {
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
}

.hexagon-icon-small.bg-orange {
    background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
}

/* Header bar for internal flow editing mode */
.flow-tool-editor-header {
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;
    background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%);
    color: white;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(79, 70, 229, 0.4);
    min-width: 500px;
    gap: 24px;
}

/* Header transition */
.flow-tool-header-enter-active,
.flow-tool-header-leave-active {
    transition: all 0.3s ease;
}

.flow-tool-header-enter-from,
.flow-tool-header-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
}

/* Help overlay in center */
.flow-tool-help-center {
    position: absolute;
    top: 80px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 50;
    pointer-events: none;
}

.flow-tool-help-center .help-alert {
    max-width: 500px;
}

/* Internal Flow Edge Styles - Match indigo handle color */
.flow-tool-editing :deep(.vue-flow__edge-path) {
    stroke: #6366f1 !important;
}

.flow-tool-editing :deep(.vue-flow__connectionline path) {
    stroke: #6366f1 !important;
}

/* Legacy overlay styles (kept for reference) */
.flow-tool-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100;
    display: flex;
    flex-direction: column;
}

.flow-tool-overlay-backdrop {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(4px);
}

.flow-tool-overlay-content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
    border: 2px solid #4f46e5;
    border-radius: 12px;
    margin: 8px;
    background: white;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(79, 70, 229, 0.2);
}

.flow-tool-overlay-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 16px;
    background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%);
    color: white;
    flex-shrink: 0;
}

.flow-tool-overlay-body {
    display: flex;
    flex: 1;
    overflow: hidden;
    background: #f8fafc;
}

.flow-tool-overlay-panel {
    width: 320px;
    flex-shrink: 0;
    overflow: hidden;
    background: white;
    border-right: 1px solid #e2e8f0;
}

.flow-tool-overlay-canvas {
    flex: 1;
    position: relative;
    background: #fafafa;
}

.flow-tool-overlay-inspector {
    width: 260px;
    flex-shrink: 0;
    border-left: 1px solid #e2e8f0;
    background: white;
}

/* Transition */
.flow-tool-overlay-enter-active,
.flow-tool-overlay-leave-active {
    transition: all 0.3s ease;
}

.flow-tool-overlay-enter-from,
.flow-tool-overlay-leave-to {
    opacity: 0;
    transform: scale(0.95);
}

/* Legacy dialog styles (keep for backward compatibility) */
.flow-tool-editor-card {
    display: flex;
    flex-direction: column;
    height: 100vh;
}

.flow-tool-editor-content {
    flex: 1;
    overflow: hidden;
}

.flow-tool-editor-container {
    display: flex;
    height: 100%;
    background: #f5f5f5;
}

.flow-tool-node-panel {
    width: 280px;
    flex-shrink: 0;
    border-right: 1px solid #e0e0e0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.flow-tool-node-list {
    flex: 1;
    overflow-y: auto;
    max-height: calc(100vh - 120px);
}

.flow-tool-draggable-node {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: white;
    cursor: grab;
    transition: all 0.2s ease;
}

.flow-tool-draggable-node:hover {
    border-color: #4f46e5;
    background: #f0f0ff;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(79, 70, 229, 0.15);
}

.flow-tool-draggable-node:active {
    cursor: grabbing;
}

.flow-tool-canvas {
    flex: 1;
    position: relative;
    background: #fafafa;
}

.internal-flow-canvas {
    width: 100%;
    height: 100%;
}

.flow-tool-help-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 5;
    pointer-events: none;
    max-width: 400px;
}

.flow-tool-inspector {
    width: 300px;
    flex-shrink: 0;
    border-left: 1px solid #e0e0e0;
    overflow: hidden;
}

/* Flow Input/Output Node Styles (for internal flow) */
.flow-input-node,
.flow-output-node {
    background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
    border: 3px solid #4f46e5;
    border-radius: 12px;
    padding: 12px 20px;
    min-width: 120px;
    text-align: center;
}

.flow-input-node {
    border-color: #22c55e;
    background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
}

.flow-output-node {
    border-color: #ef4444;
    background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
}

.flow-input-node .node-title,
.flow-output-node .node-title {
    font-size: 14px;
    font-weight: 600;
}

.flow-input-node .node-subtitle,
.flow-output-node .node-subtitle {
    font-size: 11px;
    color: #666;
}

/* ============================================
   DEBUG MODE STYLES 
   ============================================ */
.debug-toggle-btn {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 500;
}

.debug-toggle-btn .btn-text {
    margin-left: 4px;
}
</style>
